import { getToken as getAuthToken } from '../lib/auth.js';
import {
  acknowledgeMlsWelcomes,
  createMlsGroup,
  fetchLatestMlsKeyPackage,
  fetchMlsMessages,
  getGroupEpoch,
  getGroupSnapshot,
  getJoinBundle,
  getMlsDeviceIdentity,
  mintMlsSelfWelcome,
  probeMlsBootstrap,
  publishMlsKeyPackage,
  reissueMlsWelcomes,
  resetConversationMlsGroup,
  resetConversationMlsGroupCache,
  sendMlsMessage,
  uploadMlsWelcome,
} from './api.js';
import { isWorkerBytesOnlyEnabled } from './mls/flags.js';
import { clearBackupRestoreFlag, isBackupRestoreFlagSet, scheduleMlsBackup } from './mlsBackup.js';
import {
  addMemberFromKeyPackageBytes,
  createGroup,
  createIdentity,
  decodeWelcomeCiphersuite,
  decryptMessage,
  encryptMessage,
  getSupportedCiphersuites,
  initMls,
  buildBootstrapProbeEnvelope as runtimeBuildBootstrapProbeEnvelope,
  shutdownMlsWorker,
  debugDumpRuntimeState as workerDebugDumpRuntimeState,
  debugGroupRuntimeState as workerDebugGroupRuntimeState,
  debugIdentityState as workerDebugIdentityState,
  dropRuntimeGroupsByGroupId as workerDropRuntimeGroupsByGroupId,
  exportGroupKey as workerExportGroupKey,
  exportKeyPackage as workerExportKeyPackage,
  getIdentityHealth as workerGetIdentityHealth,
  getKeyPackageHealth as workerGetKeyPackageHealth,
  joinGroup as workerJoinGroup,
  listGroups as workerListGroups,
  previewIdentityKeyPackage,
  removeRuntimeGroup as workerRemoveRuntimeGroup,
  rotateKeyPackage as workerRotateKeyPackage,
} from './mlsClient.js';
import { getMlsLoginNonce } from './mlsLoginNonce.js';
import { keyPackagePublishCoordinator } from './keyPackagePublishCoordinator.js';
import { extractApiErrorCode, shouldBlockJoinBundleRecovery } from './mlsRecoveryError.js';
import { welcomeRecoveryCoordinator } from './welcomeRecoveryCoordinator.js';
import {
  loadPendingQueueFromStorage,
  makePendingQueueKey,
  persistPendingQueueToStorage,
} from './mlsPendingStorage';
import { normalizeSuiteId } from './mlsShared.js';
import {
  deriveGroupSyncState,
  getIdentityCandidates,
  getNormalizedMembers,
  hasJoinedGroupHistory,
  hasCurrentIdentityMembership,
  isRuntimeBehindSnapshot,
} from './conversationSyncState.js';
import { deleteItem, getItem, setItem } from './storage';
import { bytesToBase64 } from './utils.js';

const HYBRID_MLS_CIPHERSUITE = 0x004d;
const CIPHERSUITE_LABELS = new Map([
  [0x0001, 'MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519'],
  [0x0002, 'MLS_128_DHKEMP256_AES128GCM_SHA256_P256'],
  [0x0003, 'MLS_128_DHKEMX25519_CHACHA20POLY1305_SHA256_Ed25519'],
  [0x0004, 'MLS_256_DHKEMX448_AES256GCM_SHA512_Ed448'],
  [0x0005, 'MLS_256_DHKEMP521_AES256GCM_SHA512_P521'],
  [0x0006, 'MLS_256_DHKEMX448_CHACHA20POLY1305_SHA512_Ed448'],
  [0x0007, 'MLS_256_DHKEMP384_AES256GCM_SHA384_P384'],
  [HYBRID_MLS_CIPHERSUITE, 'MLS_256_XWING_CHACHA20POLY1305_SHA256_Ed25519'],
]);

const describeCiphersuite = (value) => {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return CIPHERSUITE_LABELS.get(numeric & 0xffff) || null;
};

const normalizeTriStateBoolean = (value) =>
  value === true ? true : value === false ? false : null;

const normalizeKeyPackageProvenance = (value) => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
};

const getPendingEnvelopeIdentifiers = (payload = {}) => {
  const serverMsgId = payload?.server_msg_id ?? payload?.serverMsgId ?? null;
  const serverSeq = payload?.server_seq ?? payload?.serverSeq ?? null;
  const clientMsgId = payload?.client_msg_id ?? payload?.clientMsgId ?? null;
  return {
    serverMsgId: serverMsgId != null ? String(serverMsgId) : null,
    serverSeq: serverSeq != null ? String(serverSeq) : null,
    clientMsgId: clientMsgId != null ? String(clientMsgId) : null,
  };
};

const pendingIdentifiersMatch = (a = {}, b = {}) => {
  if (a.serverMsgId && b.serverMsgId && a.serverMsgId === b.serverMsgId) return true;
  if (a.serverSeq && b.serverSeq && a.serverSeq === b.serverSeq) return true;
  if (a.clientMsgId && b.clientMsgId && a.clientMsgId === b.clientMsgId) return true;
  return false;
};

const getPendingQueueKeyForEntry = (entry) => {
  if (!entry) return null;
  const identifiers = getPendingEnvelopeIdentifiers(entry?.envelope || entry);
  if (identifiers.serverMsgId) return `smid:${identifiers.serverMsgId}`;
  if (identifiers.serverSeq) return `seq:${identifiers.serverSeq}`;
  if (identifiers.clientMsgId) return `client:${identifiers.clientMsgId}`;
  return null;
};

const isMlsTraceEnabled = () => {
  try {
    const root = typeof globalThis !== 'undefined' ? globalThis : null;
    const win = root?.window || null;
    const forced =
      root?.__MLS_TRACE__ === true || win?.__MLS_TRACE__ === true || win?.__MLS_DEBUG__ === true;
    if (forced) return true;
    const host = win?.location?.hostname || root?.location?.hostname || '';
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  } catch {
    return false;
  }
};

const appendMlsTraceEvent = (scope, event) => {
  try {
    const root = typeof globalThis !== 'undefined' ? globalThis : null;
    if (!root) return;
    if (!Array.isArray(root.__MLS_TRACE_EVENTS__)) root.__MLS_TRACE_EVENTS__ = [];
    const list = root.__MLS_TRACE_EVENTS__;
    list.push({ scope, ...event });
    if (list.length > 500) list.splice(0, list.length - 500);
  } catch {}
};

const traceMlsSession = (stage, data = {}) => {
  if (!isMlsTraceEnabled()) return;
  try {
    const event = {
      stage,
      at: 'sessionManagerMLS.js',
      ts: new Date().toISOString(),
      ...data,
    };
    appendMlsTraceEvent('sessionManagerMLS', event);
    try {
      if (typeof window !== 'undefined' && window?.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent('mls-trace', { detail: { scope: 'sessionManagerMLS', ...event } }),
        );
      }
    } catch {}
    console.log('[MLS TRACE][sessionManagerMLS]', event);
  } catch {}
};

const knownMlsConversations = new Map();
const hydratedEpochsByGroup = new Map();
const conversationSnapshotFingerprints = new Map();
const historyDerivedKeyCache = new Map();
const globalActiveJoinBundleTargets = new Map();
const DEFAULT_MLS_PROVIDER = 'openmls_libcrux_crypto';
const MLS_WORKER_BYTES_ONLY = isWorkerBytesOnlyEnabled();
const BOOTSTRAP_PROBE_DEBOUNCE_MS = 30_000;
const GROUP_SNAPSHOT_STALE_AFTER_MS = 60 * 60 * 1000; // force refresh of snapshots older than 60 minutes
const JOIN_BUNDLE_RESPONSE_CACHE_TTL_MS = 4_000;
const HISTORY_DERIVED_KEY_PREFIX = 'mls-history-derived-key:';
const HISTORY_DERIVED_KEY_LABEL = 'history-key-v1';
const HISTORY_DERIVED_KEY_LENGTH = 32;

const sleep = (ms) =>
  ms > 0 ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();

const awaitWithTimeout = async (work, timeoutMs, timeoutValue = null) => {
  const ms = Number(timeoutMs);
  const promise =
    typeof work === 'function' ? Promise.resolve().then(() => work()) : Promise.resolve(work);
  if (!Number.isFinite(ms) || ms <= 0) {
    return promise;
  }
  let timerId = null;
  const timeoutToken = { __mlsTimeout: true };
  try {
    const result = await Promise.race([
      promise,
      new Promise((resolve) => {
        timerId = setTimeout(() => resolve(timeoutToken), ms);
      }),
    ]);
    return result === timeoutToken ? timeoutValue : result;
  } finally {
    if (timerId) clearTimeout(timerId);
  }
};

const historyDerivedKeyStorageKey = (groupId, epoch) =>
  `${HISTORY_DERIVED_KEY_PREFIX}${groupId}:${epoch}`;

const loadHistoryDerivedKey = async (storageKey) => {
  if (!storageKey) return null;
  let localValue = null;
  try {
    localValue = localStorage.getItem(storageKey);
  } catch {}
  let storedValue = null;
  try {
    storedValue = await getItem(storageKey);
  } catch {}
  const selected = localValue || storedValue || null;
  if (selected) {
    if (!localValue) {
      try {
        localStorage.setItem(storageKey, selected);
      } catch {}
    }
    if (!storedValue) {
      try {
        await setItem(storageKey, selected);
      } catch {}
    }
  }
  return selected || null;
};

const storeHistoryDerivedKey = async (storageKey, keyB64) => {
  if (!storageKey || !keyB64) return;
  try {
    localStorage.setItem(storageKey, keyB64);
  } catch {}
  try {
    await setItem(storageKey, keyB64);
  } catch {}
};

const KEY_PACKAGE_RETRY_DELAYS_MS = [0, 500, 1000, 2000];
const KEY_PACKAGE_DEFER_DURATION_MS = 60_000;
const KEY_PACKAGE_MIN_POST_INTERVAL_MS = 60_000;
// Treat key packages as stale after a bounded TTL regardless of cache flags
const KEY_PACKAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Prevent thrashing: allow one forced republish per window per suite
const KEY_PACKAGE_REPUBLISH_BACKOFF_MS = 30_000;
const FORCE_JOIN_BUNDLE_DEBOUNCE_MS = 12_000;
const FORCE_JOIN_BUNDLE_PUBLISH_DEBOUNCE_MS = 60_000;
const FORCE_JOIN_PREFLIGHT_TIMEOUT_MS = 8_000;
const FORCE_JOIN_FETCH_TIMEOUT_MS = 12_000;
const JOIN_BUNDLE_GROUP_DEBOUNCE_MS = 4_000;
const JOIN_BUNDLE_NO_WELCOME_REISSUE_COOLDOWN_MS = 10_000;
const RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS = 20_000;
const RUNTIME_BOOTSTRAP_RETRY_COOLDOWN_MS = 4_000;
const ACKED_WELCOME_IDS_MAX_PER_GROUP = 200;
const BOOTSTRAP_CONVERGENCE_WINDOW_MS = 20_000;
const ACTIVE_JOIN_BUNDLE_TARGET_TTL_MS = BOOTSTRAP_CONVERGENCE_WINDOW_MS;
const ACKED_WELCOME_IDS_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const GROUP_PIPELINE_STAGE_TIMEOUT_MS = 60_000;
const GROUP_PIPELINE_HARD_RELEASE_MS = 180_000;
const RUNTIME_SYNC_TASK_TIMEOUT_MS = 45_000;
const RUNTIME_SYNC_STAGE_FAST_TIMEOUT_MS = RUNTIME_SYNC_TASK_TIMEOUT_MS;
const SNAPSHOT_APPLY_OBSERVE_TIMEOUT_MS = 1_500;
const ENSURE_GROUP_WAIT_TIMEOUT_MS = 8_000;
const JOIN_GROUP_WORKER_TIMEOUT_MS = 12_000;
const PENDING_APPLY_FLIGHT_TIMEOUT_MS = 30_000;
const REHYDRATE_RETRY_BACKOFF_MS = 30_000;
const REHYDRATE_MISSING_KEY_BACKOFF_MS = 90_000;

const KEY_PACKAGE_REF_LABEL = new TextEncoder().encode('MLS 1.0 KeyPackage Reference');

const encodeVarint = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  let length = 1;
  let prefix = 0x00;
  if (numeric <= 0x3f) {
    length = 1;
    prefix = 0x00;
  } else if (numeric <= 0x3fff) {
    length = 2;
    prefix = 0x40;
  } else if (numeric <= 0x3fff_ffff) {
    length = 4;
    prefix = 0x80;
  } else {
    length = 8;
    prefix = 0xc0;
  }
  const out = new Uint8Array(length);
  out[0] = prefix;
  let remaining = BigInt(numeric);
  for (let i = length - 1; i >= 0; i -= 1) {
    out[i] |= Number(remaining & 0xffn);
    remaining >>= 8n;
  }
  return out;
};

const decodeVarint = (bytes, offset) => {
  if (!(bytes instanceof Uint8Array)) return null;
  const start = offset ?? 0;
  if (start < 0 || start >= bytes.length) return null;
  const first = bytes[start];
  const prefix = first >> 6;
  const length = prefix === 0 ? 1 : prefix === 1 ? 2 : prefix === 2 ? 4 : 8;
  if (start + length > bytes.length) return null;
  let value = BigInt(first & 0x3f);
  for (let i = 1; i < length; i += 1) {
    value = (value << 8n) | BigInt(bytes[start + i]);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return { value: numeric, length };
};

const parseKeyPackageCiphersuite = (bytes) => {
  if (!(bytes instanceof Uint8Array) || bytes.length < 4) return null;
  return (bytes[2] << 8) | bytes[3];
};

const getSuiteHashAlgorithm = (suite) => {
  const id = Number(suite);
  if (!Number.isFinite(id)) return 'SHA-256';
  switch (id & 0xffff) {
    case 0x0004:
    case 0x0005:
    case 0x0006:
      return 'SHA-512';
    case 0x0007:
      return 'SHA-384';
    default:
      return 'SHA-256';
  }
};

const concatBytes = (...chunks) => {
  const parts = chunks.filter((chunk) => chunk && chunk.length);
  if (!parts.length) return new Uint8Array();
  const total = parts.reduce((acc, part) => acc + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};

async function computeKeyPackageRefHexFromB64(b64, suiteHint = null) {
  try {
    if (!b64 || typeof b64 !== 'string') return null;
    const bytes = decodeBase64ToBytes(b64);
    if (!bytes || !bytes.length) return null;
    const suite = parseKeyPackageCiphersuite(bytes) ?? suiteHint ?? null;
    const labelLen = encodeVarint(KEY_PACKAGE_REF_LABEL.length);
    const valueLen = encodeVarint(bytes.length);
    if (!labelLen || !valueLen) return null;
    const input = concatBytes(labelLen, KEY_PACKAGE_REF_LABEL, valueLen, bytes);
    const subtle =
      (globalThis.crypto && globalThis.crypto.subtle) ||
      (globalThis.window && window.crypto && window.crypto.subtle);
    if (!subtle || typeof subtle.digest !== 'function') return null;
    const digest = await subtle.digest(getSuiteHashAlgorithm(suite), input);
    const view = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < view.length; i += 1) {
      hex += view[i].toString(16).padStart(2, '0');
    }
    return hex;
  } catch {
    return null;
  }
}

function toHex(uint8) {
  if (!uint8) return null;
  let hex = '';
  for (let i = 0; i < uint8.length; i += 1) {
    hex += uint8[i].toString(16).padStart(2, '0');
  }
  return hex;
}

function extractWelcomeRefHexesFromB64(b64) {
  try {
    const bytes = decodeBase64ToBytes(b64);
    if (!bytes || bytes.length < 4) return [];
    let offset = 0;
    if (bytes.length >= 6) {
      const wireFormat = (bytes[2] << 8) | bytes[3];
      if (wireFormat === 3) {
        offset = 4;
      }
    }
    if (bytes.length < offset + 2) return [];
    offset += 2;
    const secretsInfo = decodeVarint(bytes, offset);
    if (!secretsInfo) return [];
    offset += secretsInfo.length;
    const secretsEnd = offset + secretsInfo.value;
    if (secretsEnd > bytes.length) return [];
    const refs = [];
    while (offset < secretsEnd) {
      const refInfo = decodeVarint(bytes, offset);
      if (!refInfo) return [];
      offset += refInfo.length;
      if (offset + refInfo.value > secretsEnd) return [];
      const ref = bytes.subarray(offset, offset + refInfo.value);
      const hex = toHex(ref);
      offset += refInfo.value;
      const kemInfo = decodeVarint(bytes, offset);
      if (!kemInfo) return [];
      offset += kemInfo.length + kemInfo.value;
      const ctInfo = decodeVarint(bytes, offset);
      if (!ctInfo) return [];
      offset += ctInfo.length + ctInfo.value;
      if (hex) refs.push(hex);
    }
    return refs;
  } catch {
    return [];
  }
}

function extractWelcomeRefHexFromB64(b64) {
  const refs = extractWelcomeRefHexesFromB64(b64);
  return refs[0] || null;
}

function welcomeContainsKeyPackageRefHex(b64, expectedRef) {
  const normalizedExpected = normalizeHexRefValue(expectedRef);
  if (!normalizedExpected) return false;
  return extractWelcomeRefHexesFromB64(b64).includes(normalizedExpected);
}

function resolveMatchingWelcomeRefHex(b64, preferredRefs = []) {
  const refs = extractWelcomeRefHexesFromB64(b64);
  if (!refs.length) return null;
  const normalizedPreferred = Array.isArray(preferredRefs)
    ? preferredRefs.map((value) => normalizeHexRefValue(value)).filter(Boolean)
    : [];
  const matched = normalizedPreferred.find((value) => refs.includes(value));
  return matched || refs[0] || null;
}

async function computeSha256Hex(bytes) {
  try {
    if (!(bytes instanceof Uint8Array) || !bytes.length) return null;
    const subtle =
      (globalThis.crypto && globalThis.crypto.subtle) ||
      (globalThis.window && window.crypto && window.crypto.subtle);
    if (!subtle || typeof subtle.digest !== 'function') return null;
    const digest = await subtle.digest('SHA-256', bytes);
    const view = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < view.length; i += 1) {
      hex += view[i].toString(16).padStart(2, '0');
    }
    return hex;
  } catch {
    return null;
  }
}

async function computeSha256HexFromBase64(b64) {
  try {
    if (!b64 || typeof b64 !== 'string') return null;
    const bytes = decodeBase64ToBytes(b64);
    if (!(bytes instanceof Uint8Array) || !bytes.length) return null;
    return computeSha256Hex(bytes);
  } catch {
    return null;
  }
}

const coerceProbeEnvelopeB64 = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value instanceof Uint8Array || ArrayBuffer.isView(value)) {
    const view =
      value instanceof Uint8Array
        ? value
        : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    return bytesToBase64(view);
  }

  if (value instanceof ArrayBuffer) {
    return bytesToBase64(new Uint8Array(value));
  }

  if (typeof value === 'object') {
    const nested =
      value.envelopeB64 ??
      value.envelope_b64 ??
      value.probeEnvelopeB64 ??
      value.probe_envelope_b64 ??
      value.publicMessageB64 ??
      value.public_message_b64 ??
      value.bytes ??
      value.buffer ??
      value.envelopeBytes ??
      value.envelope_bytes ??
      null;
    if (nested) {
      return coerceProbeEnvelopeB64(nested);
    }
  }

  return null;
};

const coerceTimestampMs = (value) => {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value >= 1e11) return Math.floor(value);
    return Math.floor(value * 1000);
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      if (numeric >= 1e11) return Math.floor(numeric);
      return Math.floor(numeric * 1000);
    }
    const parsed = Date.parse(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const coerceNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const normalizeHexRefValue = (value) => {
  if (value == null) {
    return null;
  }
  try {
    const str = String(value).trim().toLowerCase();
    if (!str || !/^[0-9a-f]+$/.test(str)) {
      return null;
    }
    return str;
  } catch {
    return null;
  }
};

const extractKeyPackageMetadata = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const root = payload.key_package || payload.keyPackage || payload;
  const pickFirst = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate != null) {
        return candidate;
      }
    }
    return null;
  };

  const clientCandidate = pickFirst(
    root?.client_id,
    root?.clientId,
    root?.client?.client_id,
    root?.client?.clientId,
    root?.client?.id,
    root?.client?.device_id,
    payload.client_id,
    payload.clientId,
    payload.client?.client_id,
    payload.client?.clientId,
    payload.client?.id,
    payload.client?.device_id,
  );
  const identityCandidate = pickFirst(
    root?.identity_id,
    root?.identityId,
    root?.identity?.identity_id,
    root?.identity?.client_id,
    root?.identity?.clientId,
    root?.identity?.id,
    payload.identity_id,
    payload.identityId,
    payload.identity?.identity_id,
    payload.identity?.client_id,
    payload.identity?.clientId,
    payload.identity?.id,
  );
  const ciphersuiteCandidate = root?.ciphersuite ?? payload.ciphersuite ?? null;
  const statusCandidate = root?.status ?? payload.status ?? null;
  const ttlCandidates = [
    root?.ttl_seconds,
    root?.ttlSeconds,
    payload.ttl_seconds,
    payload.ttlSeconds,
  ];
  const expiresCandidate =
    root?.expires_at ?? root?.expiresAt ?? payload.expires_at ?? payload.expiresAt ?? null;
  const keyPackageRefCandidate = pickFirst(
    root?.key_package_ref,
    root?.keyPackageRef,
    root?.ref,
    payload.key_package_ref,
    payload.keyPackageRef,
    payload.ref,
  );
  const keyPackageB64Candidate = pickFirst(
    root?.key_package_b64,
    root?.keyPackageB64,
    payload.key_package_b64,
    payload.keyPackageB64,
  );

  let ttlSeconds = null;
  for (const candidate of ttlCandidates) {
    const numeric = coerceNumber(candidate);
    if (numeric != null) {
      ttlSeconds = numeric;
      break;
    }
  }

  const expiresAt = coerceTimestampMs(expiresCandidate);

  let ttlMs = null;
  if (ttlSeconds != null && Number.isFinite(ttlSeconds)) {
    ttlMs = Math.max(0, ttlSeconds * 1000);
  }

  const normalizeId = (value) => {
    if (value == null) {
      return null;
    }
    try {
      const str = String(value).trim();
      return str ? str : null;
    } catch {
      return null;
    }
  };

  const normalizedClientId = normalizeId(clientCandidate);
  const normalizedIdentityId = normalizeId(identityCandidate);
  const resolvedIdentityId = normalizedIdentityId ?? normalizedClientId ?? null;
  const mintedClientId = normalizedClientId ?? null;
  const normalizedRef = normalizeHexRefValue(keyPackageRefCandidate);
  const normalizedKeyPackageB64 = normalizeId(keyPackageB64Candidate);

  return {
    identityId: resolvedIdentityId,
    clientId: mintedClientId,
    ciphersuite: ciphersuiteCandidate ?? null,
    status: typeof statusCandidate === 'string' ? statusCandidate.toLowerCase() : null,
    ttlMs,
    expiresAt,
    ref: normalizedRef,
    keyPackageB64: normalizedKeyPackageB64,
  };
};

const debugLog = (...args) => {
  if (typeof globalThis === 'undefined') return;
  if (globalThis.__MLS_DEBUG__ !== true) return;
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug(...args);
  }
};

const normalizeRuntimeGroups = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object' || typeof value === 'function') {
    const iterator = value[Symbol.iterator];
    if (typeof iterator === 'function') {
      return Array.from(value).filter(Boolean);
    }
  }
  return [];
};

const normalizeConversationId = (conversation) => {
  if (!conversation) return null;
  const { id, conversationId, conversation_id: conversationIdSnake, meta } = conversation;
  if (id != null) return String(id);
  if (conversationId != null) return String(conversationId);
  if (conversationIdSnake != null) return String(conversationIdSnake);
  if (meta?.conversation_id != null) return String(meta.conversation_id);
  return null;
};

const resolveConversationProtocol = (conversation) => {
  if (!conversation) return null;
  const protocolCandidates = [
    conversation.e2ee_protocol,
    conversation.e2eeProtocol,
    conversation.protocol,
    conversation.meta?.e2ee_protocol,
    conversation.meta?.e2eeProtocol,
    conversation.meta?.protocol,
  ];

  for (const value of protocolCandidates) {
    if (!value) continue;
    try {
      const normalized = String(value).trim();
      if (normalized) {
        return normalized.toLowerCase();
      }
    } catch {}
  }

  return null;
};

const resolveConversationMlsGroupId = (conversation) => {
  if (!conversation) return null;
  const candidates = [
    conversation.mls_group_id,
    conversation.mlsGroupId,
    conversation.meta?.mls_group_id,
    conversation.meta?.mlsGroupId,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    try {
      const normalized = String(value);
      if (normalized.trim()) {
        return normalized;
      }
    } catch {}
  }
  return null;
};

export function isMLSConversation(conv) {
  if (!conv) return false;
  const protocol = resolveConversationProtocol(conv);
  const hasGroupId = resolveConversationMlsGroupId(conv);
  const enabled = conv.e2ee_enabled === true || conv.e2eeEnabled === true;
  const isMls = Boolean(hasGroupId || protocol === 'mls' || (enabled && protocol === 'mls'));
  if (isMls) {
    const conversationId = normalizeConversationId(conv);
    if (conversationId != null) {
      const existing = knownMlsConversations.get(conversationId);
      const nextMeta = {
        ...((existing && existing.meta) || conv.meta || {}),
      };
      if (hasGroupId) {
        nextMeta.mls_group_id = hasGroupId;
      }
      knownMlsConversations.set(conversationId, {
        ...existing,
        ...conv,
        meta: nextMeta,
        mls_group_id: hasGroupId || existing?.mls_group_id || existing?.mlsGroupId || null,
        mlsGroupId: hasGroupId || existing?.mlsGroupId || existing?.mls_group_id || null,
        e2ee_protocol: conv.e2ee_protocol ?? conv.e2eeProtocol ?? existing?.e2ee_protocol ?? 'mls',
        e2ee_enabled: conv.e2ee_enabled ?? conv.e2eeEnabled ?? existing?.e2ee_enabled ?? true,
      });
    }
  }
  return isMls;
}

export const getKnownMLSConversation = (conversationId) => {
  if (conversationId == null) return null;
  const key = String(conversationId);
  return knownMlsConversations.get(key) || null;
};

const normalizeUserId = (value) => {
  if (value == null) return null;
  try {
    const normalized = String(value).trim();
    return normalized || null;
  } catch {
    return null;
  }
};

const collectConversationMemberIds = (conversation, fallbackUserId) => {
  const ids = new Set();
  const push = (value) => {
    const normalized = normalizeUserId(value);
    if (normalized) {
      ids.add(normalized);
    }
  };

  if (fallbackUserId != null) {
    push(fallbackUserId);
  }

  if (conversation) {
    const participantList = Array.isArray(conversation.participants)
      ? conversation.participants
      : [];
    participantList.forEach((participant) => {
      if (!participant) return;
      push(participant.id);
      push(participant.user_id);
      push(participant.userId);
    });

    const meta = conversation.meta || {};
    [
      conversation.peer_user_id,
      conversation.peerUserId,
      conversation.creator_id,
      conversation.creatorId,
      conversation.owner_id,
      conversation.ownerId,
      meta.peer_user_id,
      meta.peerUserId,
      meta.creator_id,
      meta.creatorId,
    ].forEach(push);

    const listCandidates = [
      conversation.member_ids,
      conversation.memberIds,
      conversation.participant_ids,
      conversation.participantIds,
      meta.member_ids,
      meta.memberIds,
      meta.participant_ids,
      meta.participantIds,
    ];

    listCandidates.forEach((list) => {
      if (!list) return;
      const normalizedList = Array.isArray(list) ? list : [list];
      normalizedList.forEach(push);
    });
  }

  return Array.from(ids);
};

const persistKnownConversationGroupId = (conversationId, groupId) => {
  const normalizedConversationId = normalizeUserId(conversationId);
  const normalizedGroupId = normalizeUserId(groupId);
  if (!normalizedConversationId || !normalizedGroupId) {
    return;
  }

  const existing = knownMlsConversations.get(normalizedConversationId);
  const baseConversation = existing || {
    id: normalizedConversationId,
    conversation_id: normalizedConversationId,
    e2ee_protocol: 'mls',
    e2ee_enabled: true,
    meta: {},
  };

  const nextMeta = {
    ...(baseConversation.meta || {}),
    mls_group_id: normalizedGroupId,
  };

  knownMlsConversations.set(normalizedConversationId, {
    ...baseConversation,
    mlsGroupId: normalizedGroupId,
    mls_group_id: normalizedGroupId,
    meta: nextMeta,
  });
};

const clearKnownConversationGroupId = (conversationId) => {
  const normalizedConversationId = normalizeUserId(conversationId);
  if (!normalizedConversationId) {
    return;
  }

  const existing = knownMlsConversations.get(normalizedConversationId);
  if (!existing) {
    return;
  }

  const nextMeta = { ...(existing.meta || {}) };
  delete nextMeta.mls_group_id;
  delete nextMeta.mlsGroupId;
  delete nextMeta.groupId;
  delete nextMeta.group_id;

  knownMlsConversations.set(normalizedConversationId, {
    ...existing,
    mlsGroupId: null,
    mls_group_id: null,
    meta: nextMeta,
  });
};

const shouldAcceptAuthoritativeSnapshotReset = ({
  existing = null,
  incomingEpoch = null,
  snapshotGroupHandle = null,
  snapshotRatchetTreeB64 = null,
  snapshotWelcomeB64 = null,
  normalizedPendingWelcomes = null,
  snapshotIdentityId = null,
} = {}) => {
  const existingEpoch = ensureEpoch(
    existing?.epoch ?? existing?.current_epoch ?? existing?.currentEpoch ?? null,
  );
  const nextEpoch = ensureEpoch(incomingEpoch);

  if (
    !Number.isFinite(existingEpoch) ||
    !Number.isFinite(nextEpoch) ||
    nextEpoch >= existingEpoch
  ) {
    return false;
  }

  const existingHandle = ensureString(
    existing?.groupHandle ?? existing?.runtimeHandle ?? existing?.runtime_handle ?? null,
  );
  const nextHandle = ensureString(snapshotGroupHandle);
  const handleChanged = Boolean(existingHandle && nextHandle && existingHandle !== nextHandle);

  const existingTree = pickBase64String(
    existing?.ratchetTreeB64,
    existing?.ratchetTree,
    existing?.ratchet_tree_b64,
    existing?.ratchet_tree,
  );
  const treeChanged =
    Boolean(existingTree && snapshotRatchetTreeB64) && existingTree !== snapshotRatchetTreeB64;

  const hasIncomingWelcome = Boolean(snapshotWelcomeB64);
  const hasIncomingPendingWelcomes = Array.isArray(normalizedPendingWelcomes)
    ? normalizedPendingWelcomes.length > 0
    : false;

  const existingIdentityId = ensureString(existing?.identityId ?? existing?.identity_id ?? null);
  const nextIdentityId = ensureString(snapshotIdentityId);
  const identityChanged = Boolean(
    existingIdentityId && nextIdentityId && existingIdentityId !== nextIdentityId,
  );

  return Boolean(
    handleChanged ||
      treeChanged ||
      hasIncomingWelcome ||
      hasIncomingPendingWelcomes ||
      identityChanged,
  );
};

const findKnownConversationByGroupId = (groupId) => {
  const normalizedGroupId = normalizeUserId(groupId);
  if (!normalizedGroupId) {
    return null;
  }

  for (const conversation of knownMlsConversations.values()) {
    if (!conversation) continue;
    const persistedGroupId =
      conversation.mls_group_id ??
      conversation.mlsGroupId ??
      conversation.meta?.mls_group_id ??
      null;
    if (persistedGroupId != null && String(persistedGroupId) === normalizedGroupId) {
      return conversation;
    }
  }

  return null;
};

const ensureArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.filter((entry) => entry != null && entry !== '');
};

const normalizeMemberId = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    try {
      const normalized = String(value).trim();
      return normalized ? normalized : null;
    } catch {
      return null;
    }
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const candidate = normalizeMemberId(entry);
      if (candidate) {
        return candidate;
      }
    }
    return null;
  }

  if (typeof value === 'object') {
    const candidates = [
      value.client_id,
      value.clientId,
      value.identity_id,
      value.identityId,
      value.member_id,
      value.memberId,
      value.member_client_id,
      value.memberClientId,
      value.id,
    ];
    for (const candidate of candidates) {
      const normalized = normalizeMemberId(candidate);
      if (normalized) {
        return normalized;
      }
    }
    if (value.client && typeof value.client === 'object') {
      const normalized = normalizeMemberId(value.client);
      if (normalized) {
        return normalized;
      }
    }
    if (value.identity && typeof value.identity === 'object') {
      const normalized = normalizeMemberId(value.identity);
      if (normalized) {
        return normalized;
      }
    }
  }

  return null;
};

const normalizeMemberList = (list, { returnOriginalWhenClean = false } = {}) => {
  if (!Array.isArray(list)) {
    return returnOriginalWhenClean ? list : [];
  }

  const output = [];
  const seen = new Set();
  let changed = false;

  for (const entry of list) {
    const memberId = normalizeMemberId(entry);
    if (!memberId) {
      changed = true;
      continue;
    }

    if (seen.has(memberId)) {
      changed = true;
      continue;
    }

    seen.add(memberId);
    output.push(memberId);
    if (typeof entry !== 'string' || String(entry).trim() !== memberId) {
      changed = true;
    }
  }

  if (!changed && output.length === list.length && returnOriginalWhenClean) {
    return list;
  }

  return output;
};

const resolveRuntimeHandle = (entry) => {
  if (!entry || typeof entry !== 'object') return null;
  return entry.runtimeHandle ?? entry.runtime_handle ?? null;
};

const containsObjectMembers = (members) =>
  Array.isArray(members) && members.some((member) => typeof member !== 'string');

const ensureEpoch = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.trunc(numeric));
};

const ensureString = (value) => {
  if (value == null) {
    return null;
  }

  try {
    const normalized = String(value);
    const trimmed = normalized.trim();
    return trimmed ? trimmed : null;
  } catch {
    return null;
  }
};

const normalizeUserIdList = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const output = [];
  const seen = new Set();

  for (const entry of value) {
    if (entry == null) continue;
    let normalized = null;
    if (typeof entry === 'number' && Number.isFinite(entry)) {
      normalized = Math.trunc(entry);
    } else {
      const text = ensureString(entry);
      if (!text) continue;
      if (/^\d+$/.test(text)) {
        normalized = Number(text);
      } else {
        normalized = text;
      }
    }

    const dedupeKey = typeof normalized === 'number' ? String(normalized) : normalized;
    if (!dedupeKey || seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    output.push(normalized);
  }

  return output;
};

const normalizeEpochOrNull = (value) => {
  if (value == null) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return ensureEpoch(numeric);
};

const pickDeviceIdentityMetadata = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return {
      deviceId: null,
      clientId: null,
      credentialPublicKeyB64: null,
      signaturePrivateKeyRef: null,
    };
  }

  const deviceId = ensureString(
    payload.device_id ??
      payload.deviceId ??
      payload.id ??
      payload.identity_id ??
      payload.identityId ??
      payload.client_id ??
      payload.clientId ??
      null,
  );
  const clientId = ensureString(
    payload.client_id ?? payload.clientId ?? payload.identity_id ?? null,
  );
  const credentialPublicKeyB64 = pickBase64String(
    payload.credential_public_key_b64,
    payload.credentialPublicKeyB64,
    payload.credential_public_key,
    payload.credentialPublicKey,
    payload.signature_public_key_b64,
    payload.signaturePublicKeyB64,
    payload.public_key_b64,
    payload.publicKeyB64,
    null,
  );

  const keyRefIdentity = ensureString(clientId ?? deviceId ?? null);
  const signaturePrivateKeyRef = keyRefIdentity ? `identity-signing:${keyRefIdentity}` : null;

  return {
    deviceId: deviceId ?? null,
    clientId: clientId ?? null,
    credentialPublicKeyB64: credentialPublicKeyB64 ?? null,
    signaturePrivateKeyRef,
  };
};

const buildPendingHandshakeQueue = (entries, fallbackGroupId = null) => {
  if (!Array.isArray(entries) || !entries.length) {
    return [];
  }

  const normalizedFallbackGroupId = ensureString(fallbackGroupId);
  return entries
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const groupId = ensureString(entry.group_id ?? entry.groupId ?? normalizedFallbackGroupId);
      if (!groupId) return null;

      const epoch = normalizeEpochOrNull(entry.epoch ?? entry.current_epoch ?? entry.currentEpoch);
      const seqNo = normalizeEpochOrNull(entry.seq_no ?? entry.seqNo ?? index + 1);
      const ciphertextHandshake = pickBase64String(
        entry.ciphertext_handshake_b64,
        entry.ciphertextHandshakeB64,
        entry.welcome_b64,
        entry.welcomeB64,
        entry.envelope_b64,
        entry.envelopeB64,
        entry.ciphertext_b64,
        entry.ciphertextB64,
        entry.ciphertext,
        entry.welcome,
      );

      if (!ciphertextHandshake) return null;

      const handshakeType = ensureString(
        entry.handshake_type ??
          entry.handshakeType ??
          (entry.welcome_b64 || entry.welcomeB64 ? 'welcome' : 'commit'),
      );

      return {
        group_id: groupId,
        groupId,
        seq_no: seqNo,
        seqNo,
        epoch,
        current_epoch: epoch,
        currentEpoch: epoch,
        handshake_type: handshakeType ?? null,
        handshakeType: handshakeType ?? null,
        ciphertext_handshake_b64: ciphertextHandshake,
        ciphertextHandshakeB64: ciphertextHandshake,
        welcome_id: ensureString(entry.welcome_id ?? entry.welcomeId ?? null),
      };
    })
    .filter(Boolean);
};

const serializeGroupStateSnapshot = (entry = {}) => {
  const payload = {
    v: 1,
    group_id: ensureString(entry.groupId ?? entry.group_id ?? null),
    group_handle: ensureString(entry.groupHandle ?? entry.group_handle ?? null),
    epoch: normalizeEpochOrNull(entry.epoch ?? entry.current_epoch ?? entry.currentEpoch),
    ciphersuite: normalizeSuiteId(entry.ciphersuite ?? null),
    ratchet_tree_b64: pickBase64String(
      entry.ratchetTreeB64,
      entry.ratchet_tree_b64,
      entry.ratchetTree,
      entry.ratchet_tree,
      null,
    ),
    runtime_handle: ensureString(entry.runtimeHandle ?? entry.runtime_handle ?? null),
    members: normalizeMemberList(entry.members ?? []),
    pending_handshake_queue: buildPendingHandshakeQueue(
      Array.isArray(entry.pending_handshake_queue)
        ? entry.pending_handshake_queue
        : Array.isArray(entry.pendingHandshakeQueue)
          ? entry.pendingHandshakeQueue
          : Array.isArray(entry.pending_welcomes)
            ? entry.pending_welcomes
            : Array.isArray(entry.pendingWelcomes)
              ? entry.pendingWelcomes
              : [],
      entry.groupId ?? entry.group_id ?? null,
    ),
  };

  try {
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
      return Buffer.from(encoded).toString('base64');
    }
    let binary = '';
    for (let i = 0; i < encoded.length; i += 1) {
      binary += String.fromCharCode(encoded[i]);
    }
    if (typeof btoa === 'function') {
      return btoa(binary);
    }
  } catch {}

  return null;
};

const normalizePendingWelcomesFromSnapshot = (
  snapshot,
  { conversationId = null, groupId = null, groupHandle = null } = {},
) => {
  if (!snapshot) {
    return [];
  }

  const candidates = Array.isArray(snapshot.pending_welcomes)
    ? snapshot.pending_welcomes
    : Array.isArray(snapshot.pendingWelcomes)
      ? snapshot.pendingWelcomes
      : [];

  if (!candidates.length) {
    return [];
  }

  const fallbackGroupId =
    ensureString(groupId) ??
    ensureString(snapshot.group_id) ??
    ensureString(snapshot.groupId) ??
    null;

  const fallbackGroupHandle =
    ensureString(groupHandle) ??
    ensureString(snapshot.group_handle) ??
    ensureString(snapshot.groupHandle) ??
    fallbackGroupId ??
    null;

  const resolvedConversationId =
    ensureString(conversationId) ??
    ensureString(snapshot.conversation_id) ??
    ensureString(snapshot.conversationId) ??
    null;

  return candidates
    .map((raw) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const welcomeId = ensureString(raw.welcome_id ?? raw.welcomeId ?? raw.id ?? null);
      const welcomeB64 = ensureString(
        raw.welcome_b64 ?? raw.welcomeB64 ?? (typeof raw.welcome === 'string' ? raw.welcome : null),
      );
      const pendingGroupId =
        ensureString(raw.group_id ?? raw.groupId ?? fallbackGroupId ?? fallbackGroupHandle) ?? null;
      const ratchetTreeB64 = ensureString(
        raw.ratchet_tree_b64 ??
          raw.ratchetTreeB64 ??
          raw.ratchet_tree ??
          raw.ratchetTree ??
          snapshot.ratchet_tree_b64 ??
          snapshot.ratchetTree ??
          snapshot.ratchet_tree ??
          null,
      );

      if (!welcomeB64 || !pendingGroupId) {
        return null;
      }

      const epochCandidate =
        raw.epoch ??
        raw.current_epoch ??
        raw.currentEpoch ??
        snapshot.epoch ??
        snapshot.current_epoch ??
        snapshot.currentEpoch ??
        null;
      const numericEpoch = Number(epochCandidate);
      const normalizedEpoch = Number.isFinite(numericEpoch) ? ensureEpoch(numericEpoch) : null;

      const identityCandidate = ensureString(
        raw.identity_id ??
          raw.identityId ??
          raw.client_id ??
          raw.clientId ??
          raw.to_client_id ??
          raw.toClientId ??
          raw.target_client_id ??
          raw.targetClientId ??
          null,
      );

      const normalized = { ...raw };
      normalized.welcome_id = welcomeId ?? normalized.welcome_id ?? null;
      normalized.welcomeId = normalized.welcomeId ?? normalized.welcome_id ?? null;
      normalized.welcome_b64 = welcomeB64;
      normalized.welcomeB64 = welcomeB64;
      normalized.group_id = pendingGroupId;
      normalized.groupId = pendingGroupId;
      normalized.ratchet_tree_b64 = ratchetTreeB64 ?? normalized.ratchet_tree_b64 ?? null;
      normalized.ratchetTreeB64 = normalized.ratchetTreeB64 ?? normalized.ratchet_tree_b64 ?? null;
      normalized.conversation_id =
        ensureString(raw.conversation_id ?? raw.conversationId) ?? resolvedConversationId ?? null;
      normalized.conversationId = normalized.conversationId ?? normalized.conversation_id ?? null;

      if (normalizedEpoch != null) {
        normalized.epoch = normalizedEpoch;
        normalized.current_epoch = normalizedEpoch;
        normalized.currentEpoch = normalizedEpoch;
      }

      if (identityCandidate) {
        normalized.identity_id = identityCandidate;
        normalized.identityId = identityCandidate;
        normalized.client_id = identityCandidate;
        normalized.clientId = identityCandidate;
        normalized.to_client_id = identityCandidate;
        normalized.toClientId = identityCandidate;
      }

      return normalized;
    })
    .filter(Boolean);
};

const getPendingWelcomesList = (entry) => {
  if (!entry || typeof entry !== 'object') return [];
  if (Array.isArray(entry.pending_welcomes)) return entry.pending_welcomes;
  if (Array.isArray(entry.pendingWelcomes)) return entry.pendingWelcomes;
  return [];
};

const getPendingMessageType = (entry) => {
  const explicitMessageType =
    ensureString(
      entry?.msg_type ??
        entry?.msgType ??
        entry?.handshake_type ??
        entry?.handshakeType ??
        entry?.message_type ??
        entry?.messageType ??
        null,
    ) ?? null;
  if (explicitMessageType) {
    return explicitMessageType.toLowerCase();
  }
  const rawType =
    ensureString(entry?.type ?? entry?.envelope_type ?? entry?.envelopeType ?? null) ?? null;
  return rawType ? rawType.toLowerCase() : null;
};

const isPendingWelcomeMessage = (entry) => {
  const normalizedType = getPendingMessageType(entry);
  if (normalizedType === 'welcome') return true;
  return Boolean(entry?.welcome_b64 || entry?.welcomeB64 || entry?.welcome);
};

const isPendingApplicationMessage = (entry) => {
  const normalizedType = getPendingMessageType(entry);
  if (normalizedType === 'application') return true;
  const contentType = ensureString(entry?.content_type ?? entry?.contentType ?? null);
  if (contentType && contentType.toLowerCase() !== 'application/mls') {
    return true;
  }
  return false;
};

const isPendingHandshakeMessage = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (isPendingWelcomeMessage(entry)) return false;
  if (isPendingApplicationMessage(entry)) return false;

  const explicitMessageType =
    ensureString(
      entry?.msg_type ?? entry?.msgType ?? entry?.handshake_type ?? entry?.handshakeType ?? null,
    ) ?? null;
  const normalizedExplicitType = explicitMessageType ? explicitMessageType.toLowerCase() : null;
  if (
    normalizedExplicitType === 'commit' ||
    normalizedExplicitType === 'proposal' ||
    normalizedExplicitType === 'handshake'
  ) {
    return true;
  }

  const normalizedType = getPendingMessageType(entry);
  if (
    normalizedType === 'commit' ||
    normalizedType === 'proposal' ||
    normalizedType === 'handshake'
  ) {
    return true;
  }

  const contentType = ensureString(entry?.content_type ?? entry?.contentType ?? null);
  if (contentType && contentType.toLowerCase() === 'application/mls') {
    return true;
  }

  const clientMsgId = ensureString(entry?.client_msg_id ?? entry?.clientMsgId ?? null);
  if (clientMsgId) {
    const normalizedClientMsgId = clientMsgId.toLowerCase();
    if (
      normalizedClientMsgId.startsWith('commit-') ||
      normalizedClientMsgId.startsWith('proposal-')
    ) {
      return true;
    }
  }

  return false;
};

const getPendingMessageCiphertextB64 = (entry) =>
  pickBase64String(
    entry?.envelope_b64,
    entry?.envelopeB64,
    entry?.ciphertext_b64,
    entry?.ciphertextB64,
    entry?.ciphertext,
    null,
  );

const resolveConversationUpdatedAt = (conversation) => {
  if (!conversation) return null;
  const candidates = [
    conversation.updated_at,
    conversation.updatedAt,
    conversation.meta?.updated_at,
    conversation.meta?.updatedAt,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;
    try {
      const normalized = String(candidate).trim();
      if (normalized) {
        return normalized;
      }
    } catch {}
  }

  return null;
};

const resolveConversationEpochMetadata = (conversation) => {
  if (!conversation) return null;

  const parseEpochCandidate = (value) => {
    if (value == null) return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return ensureEpoch(numeric);
    }
    try {
      const normalized = String(value).trim();
      return normalized || null;
    } catch {
      return null;
    }
  };

  const candidates = [
    conversation.mls_epoch,
    conversation.mlsEpoch,
    conversation.meta?.mls_epoch,
    conversation.meta?.mlsEpoch,
    conversation.current_epoch,
    conversation.currentEpoch,
    conversation.meta?.current_epoch,
    conversation.meta?.currentEpoch,
  ];

  for (const candidate of candidates) {
    const parsed = parseEpochCandidate(candidate);
    if (parsed != null) {
      return parsed;
    }
  }

  return null;
};

const computeConversationSnapshotFingerprint = (conversation) => {
  if (!conversation) return null;
  const groupId = resolveConversationMlsGroupId(conversation);
  const updatedAt = resolveConversationUpdatedAt(conversation);
  const epoch = resolveConversationEpochMetadata(conversation);

  if (!groupId && !updatedAt && epoch == null) {
    return null;
  }

  return {
    groupId: groupId || null,
    updatedAt: updatedAt || null,
    epoch: epoch ?? null,
  };
};

const trackConversationSnapshotFingerprint = (conversationId, conversation) => {
  if (!conversationId) {
    return false;
  }

  const fingerprint = computeConversationSnapshotFingerprint(conversation);
  const existing = conversationSnapshotFingerprints.get(conversationId) || null;

  if (!fingerprint) {
    if (existing) {
      conversationSnapshotFingerprints.delete(conversationId);
      return true;
    }
    return false;
  }

  if (!existing) {
    conversationSnapshotFingerprints.set(conversationId, fingerprint);
    return false;
  }

  const changed =
    existing.groupId !== fingerprint.groupId ||
    existing.updatedAt !== fingerprint.updatedAt ||
    existing.epoch !== fingerprint.epoch;

  if (changed) {
    conversationSnapshotFingerprints.set(conversationId, fingerprint);
  }

  return changed;
};

const normalizeSuiteList = (value) => {
  if (!value) return [];

  const suites = [];
  const pushSuite = (candidate) => {
    const normalized = normalizeSuiteId(candidate);
    if (normalized == null) return;
    if (!suites.includes(normalized)) {
      suites.push(normalized);
    }
  };

  if (typeof value[Symbol.iterator] === 'function') {
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of value) {
        pushSuite(entry);
      }
      return suites;
    } catch {}
  }

  pushSuite(value);
  return suites;
};

const pushMlsTelemetry = () => {};

const parseCursor = (value) => {
  if (value == null) return null;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.trunc(numeric));
};

const MAX_TRACKED_SERVER_SEQ_GAPS = 1024;

const normalizeProcessedServerSeqs = (
  value,
  { baseCursor = null, maxItems = MAX_TRACKED_SERVER_SEQ_GAPS } = {},
) => {
  const minimumCursor = parseCursor(baseCursor) ?? 0;
  const candidates = Array.isArray(value) ? value : value == null ? [] : [value];
  const seen = new Set();
  const normalized = [];

  candidates.forEach((candidate) => {
    const parsed = parseCursor(candidate);
    if (parsed == null || parsed <= minimumCursor || seen.has(parsed)) {
      return;
    }
    seen.add(parsed);
    normalized.push(parsed);
  });

  normalized.sort((a, b) => a - b);
  if (normalized.length > maxItems) {
    return normalized.slice(0, maxItems);
  }
  return normalized;
};

const buildProcessedServerSeqState = (entry, serverSeq = null) => {
  const contiguousCursor = parseCursor(
    entry?.lastServerSeq ?? entry?.last_server_seq ?? entry?.lastServerSequence ?? null,
  );
  const baseCursor = contiguousCursor ?? 0;
  const existingProcessed = normalizeProcessedServerSeqs(
    entry?.processedServerSeqs ??
      entry?.processed_server_seqs ??
      entry?.processedServerSequences ??
      [],
    {
      baseCursor,
    },
  );
  const nextSeq = parseCursor(serverSeq);
  const pendingSet = new Set(existingProcessed);

  if (nextSeq != null && nextSeq > baseCursor) {
    pendingSet.add(nextSeq);
  }

  let nextCursor = baseCursor;
  while (pendingSet.has(nextCursor + 1)) {
    pendingSet.delete(nextCursor + 1);
    nextCursor += 1;
  }

  const pending = Array.from(pendingSet).sort((a, b) => a - b);
  const normalizedPending = normalizeProcessedServerSeqs(pending, {
    baseCursor: nextCursor,
  });

  return {
    lastServerSeq: nextCursor,
    last_server_seq: nextCursor,
    processedServerSeqs: normalizedPending,
    processed_server_seqs: normalizedPending,
  };
};

const isServerSeqAlreadyProcessed = (entry, serverSeq = null) => {
  const parsedSeq = parseCursor(serverSeq);
  if (parsedSeq == null) return false;
  const contiguousCursor = parseCursor(
    entry?.lastServerSeq ?? entry?.last_server_seq ?? entry?.lastServerSequence ?? null,
  );
  if (contiguousCursor != null && parsedSeq <= contiguousCursor) {
    return true;
  }
  const processed = normalizeProcessedServerSeqs(
    entry?.processedServerSeqs ??
      entry?.processed_server_seqs ??
      entry?.processedServerSequences ??
      [],
    {
      baseCursor: contiguousCursor ?? 0,
    },
  );
  return processed.includes(parsedSeq);
};

// Cursor MUST be based on server_seq (per-epoch ordering), not server_msg_id.
const pickServerSeqCursor = (msg) =>
  parseCursor(
    msg?.server_seq ??
      msg?.serverSeq ??
      msg?.server_sequence ??
      msg?.serverSequence ??
      msg?.meta?.server_seq ??
      msg?.meta?.serverSeq ??
      null,
  );

const resolvePreservedLastServerSeq = (groups, { groupId = null, groupHandle = null } = {}) => {
  const normalizedGroupId = groupId != null ? String(groupId) : null;
  const normalizedGroupHandle = groupHandle != null ? String(groupHandle) : null;
  const collection = groups && typeof groups === 'object' ? groups : {};

  if (normalizedGroupId && collection[normalizedGroupId]) {
    const parsed = parseCursor(
      collection[normalizedGroupId]?.lastServerSeq ??
        collection[normalizedGroupId]?.last_server_seq ??
        collection[normalizedGroupId]?.lastServerSequence ??
        null,
    );
    if (parsed != null) return parsed;
  }

  const values = Object.values(collection);
  for (const candidate of values) {
    if (!candidate || typeof candidate !== 'object') continue;
    const candidateGroupId = candidate.groupId != null ? String(candidate.groupId) : null;
    const candidateGroupHandle =
      candidate.groupHandle != null ? String(candidate.groupHandle) : null;
    const matchById = normalizedGroupId && candidateGroupId === normalizedGroupId;
    const matchByHandle = normalizedGroupHandle && candidateGroupHandle === normalizedGroupHandle;
    if (!matchById && !matchByHandle) continue;
    const parsed = parseCursor(
      candidate.lastServerSeq ?? candidate.last_server_seq ?? candidate.lastServerSequence ?? null,
    );
    if (parsed != null) return parsed;
  }

  return null;
};

const resolvePreservedProcessedServerSeqs = (
  groups,
  { groupId = null, groupHandle = null, baseCursor = null } = {},
) => {
  const normalizedGroupId = groupId != null ? String(groupId) : null;
  const normalizedGroupHandle = groupHandle != null ? String(groupHandle) : null;
  const collection = groups && typeof groups === 'object' ? groups : {};
  const normalizeEntryProcessed = (entry) =>
    normalizeProcessedServerSeqs(
      entry?.processedServerSeqs ??
        entry?.processed_server_seqs ??
        entry?.processedServerSequences ??
        [],
      {
        baseCursor:
          baseCursor ??
          parseCursor(
            entry?.lastServerSeq ?? entry?.last_server_seq ?? entry?.lastServerSequence ?? null,
          ) ??
          0,
      },
    );

  if (normalizedGroupId && collection[normalizedGroupId]) {
    return normalizeEntryProcessed(collection[normalizedGroupId]);
  }

  const values = Object.values(collection);
  for (const candidate of values) {
    if (!candidate || typeof candidate !== 'object') continue;
    const candidateGroupId = candidate.groupId != null ? String(candidate.groupId) : null;
    const candidateGroupHandle =
      candidate.groupHandle != null ? String(candidate.groupHandle) : null;
    const matchById = normalizedGroupId && candidateGroupId === normalizedGroupId;
    const matchByHandle = normalizedGroupHandle && candidateGroupHandle === normalizedGroupHandle;
    if (!matchById && !matchByHandle) continue;
    return normalizeEntryProcessed(candidate);
  }

  return [];
};

const resolvePreservedMembers = (
  groups,
  { groupId = null, groupHandle = null, identityId = null } = {},
) => {
  const normalizedGroupId = groupId != null ? String(groupId) : null;
  const normalizedGroupHandle = groupHandle != null ? String(groupHandle) : null;
  const collection = groups && typeof groups === 'object' ? groups : {};

  let preserved = [];

  if (normalizedGroupId && collection[normalizedGroupId]) {
    preserved = normalizeMemberList(collection[normalizedGroupId]?.members);
  }

  if (!preserved.length) {
    const values = Object.values(collection);
    for (const candidate of values) {
      if (!candidate || typeof candidate !== 'object') continue;
      const candidateGroupId = candidate.groupId != null ? String(candidate.groupId) : null;
      const candidateGroupHandle =
        candidate.groupHandle != null ? String(candidate.groupHandle) : null;
      const matchById = normalizedGroupId && candidateGroupId === normalizedGroupId;
      const matchByHandle =
        normalizedGroupHandle &&
        (candidateGroupHandle === normalizedGroupHandle ||
          candidateGroupId === normalizedGroupHandle);
      if (!matchById && !matchByHandle) continue;
      preserved = normalizeMemberList(candidate.members);
      if (preserved.length) break;
    }
  }

  const normalizedIdentity = normalizeMemberId(identityId);
  if (normalizedIdentity && !preserved.includes(normalizedIdentity)) {
    preserved.push(normalizedIdentity);
  }

  return preserved;
};

const storageKeys = {
  identity: (userId) => `mls:identity:${userId}`,
  deviceIdentity: (userId) => `mls:device-identity:${userId}`,
  groups: (userId) => `mls:groups:${userId}`,
  keyPackages: (userId) => `mls:key-packages:${userId}`,
  keyPackagesByRef: (userId) => `mls:key-packages-by-ref:${userId}`,
  clientIds: (userId) => `mls:client-ids:${userId}`,
  ackedWelcomes: (userId) => `mls:acked-welcomes:${userId}`,
};

// Stable client identity persistence across reloads
const stableKeys = {
  identityId: (userId) => `mls:stable-client:${userId}`,
  seed: (userId) => `mls:stable-seed:${userId}`,
  loginNonce: (userId) => `mls:stable-login:${userId}`,
};

const readStableValue = async (key) => {
  if (!key) return null;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      const value = ls.getItem(key);
      if (value != null && String(value).trim()) return String(value);
    } catch {}
  }
  try {
    const stored = await getItem(key);
    if (stored != null && String(stored).trim()) return String(stored);
  } catch {}
  return null;
};

const writeStableValue = async (key, value) => {
  if (!key) return;
  const ls = getLocalStorageSafe();
  if (ls) {
    try {
      if (value != null) {
        ls.setItem(key, String(value));
      } else {
        ls.removeItem(key);
      }
    } catch {}
  }
  try {
    if (value != null) {
      await setItem(key, String(value));
    } else {
      await deleteItem(key);
    }
  } catch {}
};

const getLocalStorageSafe = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const WAITING_RATCHET_TREE_REASON = 'missing_snapshot_ratchet_tree';
const DEFAULT_MLS_CIPHERSUITE = HYBRID_MLS_CIPHERSUITE;

const coerceClientId = (value) => {
  if (value == null) return null;
  try {
    const stringified = String(value);
    return stringified.trim() ? stringified : null;
  } catch {
    return null;
  }
};

const normalizeLoginNonce = (value) => {
  if (value == null) return null;
  const stringValue = String(value).trim();
  return stringValue ? stringValue : null;
};

const gatherClientIds = (source) => {
  const output = new Set();
  if (!source) return output;

  const pushValue = (value) => {
    const normalized = coerceClientId(value);
    if (normalized) {
      output.add(normalized);
    }
  };

  if (Array.isArray(source)) {
    source.forEach(pushValue);
    return output;
  }

  pushValue(source);
  return output;
};

function toBase64(bytes) {
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    return Buffer.from(bytes).toString('base64');
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  if (typeof btoa === 'function') {
    return btoa(binary);
  }
  return globalThis?.Buffer?.from ? globalThis.Buffer.from(bytes).toString('base64') : binary;
}

const coerceUint8Array = (value) => {
  if (!value) return null;
  if (value instanceof Uint8Array) return value;
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (value && typeof value === 'object') {
    if (typeof value.as_bytes === 'function') {
      try {
        const bytes = value.as_bytes();
        return coerceUint8Array(bytes);
      } catch {}
    }
    if (typeof value.asBytes === 'function') {
      try {
        const bytes = value.asBytes();
        return coerceUint8Array(bytes);
      } catch {}
    }
    if (Array.isArray(value?.bytes)) {
      try {
        return coerceUint8Array(value.bytes);
      } catch {}
    }
  }
  return null;
};

const coerceBase64String = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value && typeof value === 'object' && typeof value.base64 === 'string') {
    const trimmed = value.base64.trim();
    return trimmed || null;
  }

  const bytes = coerceUint8Array(value);
  if (bytes) {
    try {
      return toBase64(bytes);
    } catch {}
  }

  return null;
};

const pickBase64String = (...candidates) => {
  for (const candidate of candidates) {
    const normalized = coerceBase64String(candidate);
    if (normalized) {
      return normalized;
    }
  }
  return null;
};

const assertBase64Payload = (welcomeB64, ratchetTreeB64) => {
  if (typeof welcomeB64 !== 'string') {
    throw new Error('welcomeB64 must be base64');
  }
  if (ratchetTreeB64 && typeof ratchetTreeB64 !== 'string') {
    throw new Error('ratchetTreeB64 must be base64 or null');
  }
};

function generateSeed(length = 32) {
  let source = null;
  if (
    typeof globalThis !== 'undefined' &&
    globalThis.crypto &&
    typeof globalThis.crypto.getRandomValues === 'function'
  ) {
    source = new Uint8Array(length);
    globalThis.crypto.getRandomValues(source);
  } else {
    try {
      // eslint-disable-next-line global-require
      const { randomBytes } = require('crypto');
      source = randomBytes(length);
    } catch {
      source = new Uint8Array(length);
      for (let i = 0; i < length; i += 1) {
        source[i] = Math.floor(Math.random() * 256);
      }
    }
  }

  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source);
  return toBase64(bytes);
}

const isCanonicalMlsClientId = (value) => {
  if (!value) return false;
  if (typeof value !== 'string') return false;
  return value.startsWith('mls-client-') && value.length > 'mls-client-'.length + 8;
};

const generateMlsClientId = () => {
  try {
    if (
      typeof globalThis !== 'undefined' &&
      globalThis.crypto &&
      typeof globalThis.crypto.randomUUID === 'function'
    ) {
      return `mls-client-${globalThis.crypto.randomUUID()}`;
    }
  } catch {}
  return `mls-client-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const isDevBuild =
  typeof process !== 'undefined' &&
  process != null &&
  process.env &&
  process.env.NODE_ENV !== 'production';

const decodeBase64ToBytes = (value) => {
  if (typeof value !== 'string' || value === '') return null;
  try {
    if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
      const buffer = Buffer.from(value, 'base64');
      return buffer instanceof Uint8Array ? new Uint8Array(buffer) : Uint8Array.from(buffer);
    }
  } catch {}

  try {
    const globalAtob =
      typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function'
        ? (input) => globalThis.atob(input)
        : null;
    const fallbackAtob = typeof atob === 'function' ? (input) => atob(input) : null;
    const decodeFn = globalAtob || fallbackAtob;
    if (!decodeFn) return null;
    const binary = decodeFn(value);
    if (typeof binary !== 'string') return null;
    const output = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      output[i] = binary.charCodeAt(i);
    }
    return output;
  } catch {}

  return null;
};

const normalizeMintedSelfWelcome = (input) => {
  if (!input) return null;

  let wrapper = null;
  let payload = input;
  if (payload && typeof payload === 'object' && payload.welcome != null) {
    wrapper = payload;
    payload = payload.welcome;
  }

  const collectSource = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed || null;
  };

  const sourceCandidates = [];
  if (wrapper && typeof wrapper === 'object') {
    sourceCandidates.push(collectSource(wrapper.path));
    sourceCandidates.push(collectSource(wrapper.source));
    sourceCandidates.push(collectSource(wrapper.transportPath));
    sourceCandidates.push(collectSource(wrapper.welcomeSource));
  }
  if (typeof input === 'object' && input) {
    sourceCandidates.push(collectSource(input.path));
    sourceCandidates.push(collectSource(input.source));
    sourceCandidates.push(collectSource(input.transportPath));
    sourceCandidates.push(collectSource(input.welcomeSource));
  }
  if (
    payload &&
    typeof payload === 'object' &&
    !ArrayBuffer.isView(payload) &&
    !(payload instanceof ArrayBuffer)
  ) {
    sourceCandidates.push(collectSource(payload.path));
    sourceCandidates.push(collectSource(payload.source));
    sourceCandidates.push(collectSource(payload.transportPath));
    sourceCandidates.push(collectSource(payload.welcomeSource));
  }

  const resolvedSource =
    sourceCandidates.find((entry) => typeof entry === 'string' && entry) || null;

  const normalized = {
    welcomeBuffer: null,
    welcomeB64: null,
    ratchetTreeBuffer: null,
    ratchetTreeB64: null,
    groupId: null,
    groupHandle: null,
    epoch: null,
    path: resolvedSource,
    welcomeSource: resolvedSource,
  };

  const assignBuffer = (value) => {
    if (!value) return;
    if (value instanceof ArrayBuffer) {
      normalized.welcomeBuffer = value;
      return;
    }
    if (ArrayBuffer.isView(value) && value.buffer) {
      normalized.welcomeBuffer = value.buffer.slice(
        value.byteOffset,
        value.byteOffset + value.byteLength,
      );
    }
  };

  const assignWelcomeB64 = (value) => {
    if (typeof value === 'string' && value) {
      normalized.welcomeB64 = value;
    }
  };

  const assignRatchetTreeBuffer = (value) => {
    if (!value) return;
    if (value instanceof ArrayBuffer) {
      normalized.ratchetTreeBuffer = value;
      return;
    }
    if (ArrayBuffer.isView(value) && value.buffer) {
      normalized.ratchetTreeBuffer = value.buffer.slice(
        value.byteOffset,
        value.byteOffset + value.byteLength,
      );
    }
  };

  const assignRatchetTreeB64 = (value) => {
    if (typeof value === 'string' && value) {
      normalized.ratchetTreeB64 = value;
    }
  };

  if (payload instanceof ArrayBuffer || ArrayBuffer.isView(payload)) {
    assignBuffer(payload);
    return normalized;
  }

  if (typeof payload === 'string') {
    assignWelcomeB64(payload);
    return normalized;
  }

  if (!payload || typeof payload !== 'object') {
    return normalized;
  }

  assignBuffer(payload.welcome);
  assignBuffer(payload.welcome_buffer);
  assignBuffer(payload.welcomeBuffer);

  assignWelcomeB64(payload.welcome_b64);
  assignWelcomeB64(payload.welcomeB64);
  assignWelcomeB64(payload.welcome);

  assignRatchetTreeBuffer(payload.ratchet_tree_buffer);
  assignRatchetTreeBuffer(payload.ratchetTreeBuffer);
  assignRatchetTreeBuffer(payload.ratchet_tree);
  assignRatchetTreeBuffer(payload.ratchetTree);

  assignRatchetTreeB64(payload.ratchet_tree_b64);
  assignRatchetTreeB64(payload.ratchetTreeB64);
  assignRatchetTreeB64(payload.ratchet_tree);
  assignRatchetTreeB64(payload.ratchetTree);

  normalized.groupId = payload.group_id || payload.groupId || null;
  normalized.groupHandle = payload.group_handle || payload.groupHandle || null;
  const epochCandidate = payload.epoch || payload.current_epoch || payload.currentEpoch || null;
  if (epochCandidate != null) {
    const numeric = Number(epochCandidate);
    normalized.epoch = Number.isNaN(numeric) ? epochCandidate : numeric;
  }

  return normalized;
};

const toArrayBuffer = (value) => {
  if (!value) return null;
  if (value instanceof ArrayBuffer) {
    return value;
  }
  if (ArrayBuffer.isView(value)) {
    return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
  }
  return null;
};

export async function joinWithSelfWelcome(groupId, deviceIdentityId, token, options = {}) {
  if (!groupId) {
    throw new Error('groupId is required to join MLS self welcome');
  }
  if (!deviceIdentityId) {
    throw new Error('deviceIdentityId is required to join MLS self welcome');
  }

  const {
    conversationId,
    preferredCiphersuite: preferredOption,
    supportedCiphersuites: providedSupportedSuites,
  } = options || {};

  let supportedSuites = normalizeSuiteList(providedSupportedSuites);
  if (supportedSuites.length === 0) {
    try {
      const runtimeSupported = await getSupportedCiphersuites();
      supportedSuites = normalizeSuiteList(runtimeSupported);
    } catch (error) {
      try {
        console.warn(
          '[MLS] Failed to resolve supported ciphersuites before minting self welcome',
          error,
        );
      } catch {}
      supportedSuites = [];
    }
  }

  let preferredSuite = normalizeSuiteId(preferredOption);
  if (preferredSuite == null && supportedSuites.length > 0) {
    [preferredSuite] = supportedSuites;
  }
  if (preferredSuite != null && !supportedSuites.includes(preferredSuite)) {
    supportedSuites.push(preferredSuite);
  }

  const minted = await mintMlsSelfWelcome({
    groupId,
    conversationId,
    identityId: deviceIdentityId,
    token,
    preferredCiphersuite: preferredSuite,
    supportedCiphersuites: supportedSuites,
  });

  const normalized = normalizeMintedSelfWelcome(minted);
  if (!normalized) {
    return null;
  }

  const mintedSourceCandidates = [];
  if (normalized?.welcomeSource) mintedSourceCandidates.push(normalized.welcomeSource);
  if (normalized?.path) mintedSourceCandidates.push(normalized.path);
  if (minted && typeof minted === 'object') {
    mintedSourceCandidates.push(minted.path);
    mintedSourceCandidates.push(minted.welcomeSource);
    mintedSourceCandidates.push(minted.transportPath);
  }

  const welcomeSource =
    mintedSourceCandidates.find((value) => typeof value === 'string' && value) || null;

  let welcomeBuffer = toArrayBuffer(normalized.welcomeBuffer);
  let welcomeB64 =
    typeof normalized.welcomeB64 === 'string' && normalized.welcomeB64
      ? normalized.welcomeB64
      : null;
  if (!welcomeBuffer && welcomeB64) {
    const decoded = decodeBase64ToBytes(welcomeB64);
    if (decoded) {
      welcomeBuffer = toArrayBuffer(decoded);
    }
  }

  let ratchetTreeBuffer = toArrayBuffer(normalized.ratchetTreeBuffer);
  let ratchetTreeB64 =
    typeof normalized.ratchetTreeB64 === 'string' && normalized.ratchetTreeB64
      ? normalized.ratchetTreeB64
      : null;
  if (ratchetTreeB64) {
    const decodedTree = decodeBase64ToBytes(ratchetTreeB64);
    if (decodedTree) {
      ratchetTreeBuffer = toArrayBuffer(decodedTree);
    }
  }

  if (!welcomeB64 && welcomeBuffer) {
    try {
      welcomeB64 = toBase64(new Uint8Array(welcomeBuffer));
    } catch {}
  }

  if (!ratchetTreeB64 && ratchetTreeBuffer) {
    try {
      ratchetTreeB64 = toBase64(new Uint8Array(ratchetTreeBuffer));
    } catch {}
  }

  if (welcomeBuffer && typeof console?.debug === 'function') {
    try {
      const previewSource = new Uint8Array(welcomeBuffer);
      if (previewSource.length > 0) {
        const headHex = Array.from(previewSource.slice(0, Math.min(4, previewSource.length)))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join(' ');
        if (headHex) {
          console.debug('[MLS] welcome head:', headHex);
        }
      }
    } catch {}
  }

  let welcomeSuite = null;
  let welcomeSuiteError = null;
  let suiteBytes = null;
  if (welcomeBuffer) {
    try {
      suiteBytes = new Uint8Array(welcomeBuffer);
    } catch {}
  }
  if (!suiteBytes && welcomeB64) {
    suiteBytes = decodeBase64ToBytes(welcomeB64);
  }

  if (suiteBytes instanceof Uint8Array && suiteBytes.length > 0) {
    let decoded = null;
    try {
      decoded = await Promise.resolve(decodeWelcomeCiphersuite(suiteBytes));
    } catch {
      decoded = null;
    }
    if (decoded && Number.isFinite(decoded.ciphersuite)) {
      welcomeSuite = decoded.ciphersuite;
    } else if (decoded?.error) {
      welcomeSuiteError = decoded.error;
    }
  }

  if (welcomeSuiteError) {
    console.warn('[MLS] Failed to decode welcome ciphersuite for minted self welcome', {
      groupId,
      error: welcomeSuiteError,
    });
  }

  return {
    welcomeBuffer: welcomeBuffer || null,
    welcomeB64: welcomeB64 || null,
    ratchetTreeBuffer: ratchetTreeBuffer || null,
    ratchetTreeB64: ratchetTreeB64 || null,
    groupId: normalized.groupId || groupId || null,
    groupHandle: normalized.groupHandle || null,
    epoch: normalized.epoch ?? null,
    welcomeSource,
    welcomeSuite: Number.isFinite(welcomeSuite) ? welcomeSuite : null,
  };
}

const looksLikeTextBytes = (bytes) => {
  if (!(bytes instanceof Uint8Array) || bytes.length === 0) return false;
  let printableCount = 0;
  let nullCount = 0;
  const { length } = bytes;
  for (let i = 0; i < length; i += 1) {
    const value = bytes[i];
    if (value === 0) {
      nullCount += 1;
      continue;
    }
    if ((value >= 0x20 && value <= 0x7e) || value === 0x09 || value === 0x0a || value === 0x0d) {
      printableCount += 1;
    }
  }

  if (nullCount / length > 0.05) {
    return false;
  }

  return printableCount / length >= 0.85;
};

const createEventEmitter = () => {
  const registry = new Map();

  const off = (eventName, handler) => {
    if (!eventName || typeof handler !== 'function') return;
    const listeners = registry.get(eventName);
    if (!listeners) return;
    listeners.delete(handler);
    if (listeners.size === 0) {
      registry.delete(eventName);
    }
  };

  const on = (eventName, handler) => {
    if (!eventName || typeof handler !== 'function') return () => {};
    let listeners = registry.get(eventName);
    if (!listeners) {
      listeners = new Set();
      registry.set(eventName, listeners);
    }
    listeners.add(handler);
    return () => off(eventName, handler);
  };

  const emit = (eventName, payload) => {
    if (!eventName) return;
    const listeners = registry.get(eventName);
    if (!listeners || listeners.size === 0) return;
    Array.from(listeners).forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.warn('[MLS] event listener failed', { eventName, error });
      }
    });
  };

  return { on, off, emit };
};

export class SessionManagerMLS {
  #bootstrapProbeDebounce = new Map();
  #bootstrapProbeInflight = new Set();

  constructor(userId, options = {}) {
    this.userId = userId;
    this.sessionGeneration = 1;
    this._flushPendingInFlightByGroup = new Map();
    this._flushAllPendingInFlight = null;
    this.identityName = options.identityName || `user-${userId}`;
    this.pendingQueueStorageKey = makePendingQueueKey(this.userId);
    this.pendingMlsQueue = new Map();
    this.pendingQueueLoadPromise = null;
    // In server-managed mode, the backend/sidecar mints and delivers welcomes.
    // When enabled, the client should proxy a recipients list instead of raw welcomes.
    this.serverManagedWelcomes = Boolean(options.serverManaged);
    const normalizedPreferred = normalizeSuiteId(options.preferredCiphersuite);
    this.preferredCiphersuiteExplicit = normalizedPreferred != null;
    this.wantPQ = Boolean(options.wantPQ);
    if (this.preferredCiphersuiteExplicit) {
      this.preferredCiphersuite = normalizedPreferred;
    } else if (this.wantPQ) {
      this.preferredCiphersuite = HYBRID_MLS_CIPHERSUITE;
    } else {
      this.preferredCiphersuite = null;
    }
    this.initWantPQ = null;
    this.identityKey = storageKeys.identity(userId);
    this.deviceIdentityKey = storageKeys.deviceIdentity(userId);
    this.groupsKey = storageKeys.groups(userId);
    this.keyPackagesKey = storageKeys.keyPackages(userId);
    this.keyPackagesByRefKey = storageKeys.keyPackagesByRef(userId);
    this.clientIdsKey = storageKeys.clientIds(userId);
    this.ackedWelcomesKey = storageKeys.ackedWelcomes(userId);
    this.initPromise = null;
    this.identityPromise = null;
    this.groupEnsureTasks = new Map();
    this.groupEnsureTaskMetadata = new WeakMap();
    this.groupEnsureTaskSequence = 0;
    this.serverEnsureTasks = new Map();
    this.recoveryTasks = new Map();
    this.warmRuntimePromise = null;
    this.events = createEventEmitter();
    this._readyGroups = new Set();
    this.groupRecovery = new Map();
    this.identityId = null;
    this.conversationGroups = new Map();
    this.missingRatchetTreeGroups = new Set();
    this.groupEnsureRetryTimers = new Map();
    this.groupEnsureRetryBackoff = new Map();
    this.pendingWelcomeCommitTasks = new Map();
    this.bootstrapRecoveryTimers = new Map();
    this.runtimeSupportedCiphersuites = [];
    this.runtimeSupportedSuiteLabels = [];
    this.runtimeProvider = DEFAULT_MLS_PROVIDER;

    this.joinGroupFlights = new Map();
    this.joinGroupRetryState = new Map();
    this.keyPackageStatusCache = new Map();
    this.keyPackagesByRef = new Map();
    this._keyPackageCacheLoaded = false;
    this._keyPackageCacheLoadPromise = null;
    this._keyPackageCachePersistScheduled = false;
    this._keyPackagesByRefLoaded = false;
    this._keyPackagesByRefLoadPromise = null;
    this._keyPackagesByRefPersistScheduled = false;
    this.identityResetAttemptsByGroup = new Map();

    this.clientIdsBySuite = new Map();
    this._clientIdsLoaded = false;
    this.serverDeviceIdentity = null;
    this.serverDeviceIdentityPromise = null;
    // One-shot bypass to prevent server identity override during forced local rotation recovery.
    this._ignoreServerDeviceIdentityOnce = false;
    // Correlate messages#pending fetches with subsequent join logs
    this._pendingFetchCorrelation = new Map();
    // Dedupe concurrent and bursty messages#pending fetches per group/since cursor.
    this._pendingFetchFlights = new Map();
    this._pendingFetchRecent = new Map();
    // Single-flight guard for pending apply per group.
    this._pendingApplyFlights = new Map();
    this._pendingApplyFlightStartedAt = new Map();
    // Independent cursor for welcome-only sync scans.
    // Do not reuse group lastServerSeq here because that cursor is tied to
    // decrypt/consumption, and welcome sync can otherwise loop on the same commit frames.
    this._pendingWelcomeCursorByGroup = new Map();
    // Correlate snapshot syncWelcomes with subsequent join logs
    this._snapshotFetchCorrelation = new Map();
    // Avoid spamming join_bundle retries when a welcome is missing
    this._forceJoinBundleDebounce = new Map();
    // Avoid repeating forced key-package publish before join_bundle.
    this._forceJoinBundlePublishDebounce = new Map();
    // Dedupe concurrent join_bundle calls for the same group/device/key package.
    this._joinBundleFlights = new Map();
    // Reuse very recent join_bundle responses to avoid short-lived duplicate retries.
    this._joinBundleRecentResponses = new Map();
    // Hard debounce for any join_bundle network call per group to prevent storms.
    this._joinBundleCallDebounceByGroup = new Map();
    // Backoff repeated already-member/no-welcome join_bundle retries per group/device/key package.
    this._joinBundleNoWelcomeCooldownByFlight = new Map();
    // Keep one authoritative join_bundle key package target per group/identity while
    // a welcome is still being fetched/staged so later retries cannot rotate refs mid-flow.
    // This registry must be shared across manager instances because multiple hooks/controllers
    // can coexist for the same user during one live convergence window.
    this._activeJoinBundleTargetByGroupIdentity = globalActiveJoinBundleTargets;
    // Ensure join_bundle recovery paths still hit server `/pending` at least once.
    this._forceJoinPreflightActiveByGroup = new Set();
    this._forceJoinPendingPreflightAtByGroup = new Map();
    this._protectedWelcomeTargetDriftByIdentity = new Map();
    this._conversationCreateBootstrapByIdentity = new Map();
    // Track groups with pending welcomes to avoid redundant reissues
    this._pendingWelcomesByGroup = new Map();
    // Local short-lived dedupe while welcome ack is in-flight.
    this._pendingWelcomeAckByGroup = new Map();
    this._ackFlights = new Set();
    this._ackedWelcomeIdsByGroup = new Map();
    this._ackedWelcomeTimestampsByGroup = new Map();
    this._ackedWelcomeIdsLoaded = false;
    this._ackedWelcomeIdsLoadPromise = null;
    this._ackedWelcomeIdsPersistScheduled = false;
    this._waitReadyFlights = new Map();
    this.runtimeSyncTasks = new Map();
    this.runtimeSyncTaskStartedAt = new Map();
    this.runtimeBootstrapTasks = new Map();
    this.runtimeBootstrapTaskStartedAt = new Map();
    this.runtimeBootstrapCooldownByGroup = new Map();
    // Single source-of-truth pipeline per group to prevent concurrent state churn.
    this.groupPipelineTails = new Map();

    this._clientIdsLoadPromise = null;
    this._clientIdsPersistScheduled = false;

    this._groupsNormalized = false;
    this.workerRecoveryPromise = null;
    this.identityRecord = null;
    this.identityClientId = null;
    this.buildBootstrapProbeEnvelope =
      typeof options.buildBootstrapProbeEnvelope === 'function'
        ? options.buildBootstrapProbeEnvelope
        : async ({
            identityId: providedIdentityId,
            conversationId: providedConversationId,
            groupId: providedGroupId,
            senderClientId: providedSenderClientId,
            clientMsgId: providedClientMsgId,
            ciphersuite: providedCiphersuite,
            epoch: providedEpoch,
            payload: providedPayload,
            timestamp: providedTimestamp,
          } = {}) => {
            const normalizedGroupId = ensureString(providedGroupId);
            const normalizedIdentityId =
              ensureString(providedIdentityId) ??
              ensureString(this.identityRecord?.identityId) ??
              ensureString(this.identityId);
            const normalizedSenderClientId = ensureString(providedSenderClientId);
            const normalizedClientMsgId = ensureString(providedClientMsgId);

            if (
              !normalizedGroupId ||
              !normalizedIdentityId ||
              !normalizedSenderClientId ||
              !normalizedClientMsgId
            ) {
              return null;
            }

            const normalizedConversationId = ensureString(providedConversationId);
            const normalizedCiphersuite =
              providedCiphersuite == null ? null : normalizeSuiteId(providedCiphersuite);
            const normalizedEpoch = providedEpoch == null ? null : ensureEpoch(providedEpoch);
            const normalizedTimestamp =
              typeof providedTimestamp === 'number' && Number.isFinite(providedTimestamp)
                ? Math.floor(providedTimestamp)
                : Date.now();
            const normalizedPayload =
              providedPayload &&
              typeof providedPayload === 'object' &&
              !Array.isArray(providedPayload)
                ? { ...providedPayload }
                : undefined;

            const envelopeB64 = await runtimeBuildBootstrapProbeEnvelope({
              identityId: normalizedIdentityId,
              groupId: normalizedGroupId,
              senderClientId: normalizedSenderClientId,
              clientMsgId: normalizedClientMsgId,
              ciphersuite: normalizedCiphersuite ?? undefined,
              epoch: normalizedEpoch ?? undefined,
              conversationId: normalizedConversationId ?? undefined,
              payload: normalizedPayload,
              timestamp: normalizedTimestamp,
            });

            return envelopeB64 ?? null;
          };
  }

  #showSmallToast(message, timeoutMs = 1500) {
    try {
      const doc = globalThis?.document;
      if (!doc || !doc.body) return;
      const toast = doc.createElement('div');
      toast.textContent = String(message || '').trim() || 'Working…';
      toast.setAttribute('role', 'status');
      toast.style.position = 'fixed';
      toast.style.left = '50%';
      toast.style.bottom = '24px';
      toast.style.transform = 'translateX(-50%)';
      toast.style.background = 'rgba(0,0,0,0.85)';
      toast.style.color = '#fff';
      toast.style.fontSize = '14px';
      toast.style.lineHeight = '18px';
      toast.style.padding = '8px 12px';
      toast.style.borderRadius = '8px';
      toast.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
      toast.style.zIndex = '2147483647';
      toast.style.pointerEvents = 'none';
      doc.body.appendChild(toast);
      setTimeout(
        () => {
          try {
            toast.remove();
          } catch {}
        },
        Math.max(500, timeoutMs | 0),
      );
    } catch {}
  }

  // Bump this whenever the session is logically replaced (logout/login/reload) so stale async
  // operations can no-op safely.
  _bumpGeneration(reason = 'unknown') {
    this.sessionGeneration = Number(this.sessionGeneration || 0) + 1;
    try {
      this.events?.emit?.('mls:generation-bumped', { reason, gen: this.sessionGeneration });
    } catch {}
    return this.sessionGeneration;
  }

  _guardGen(gen) {
    return gen === this.sessionGeneration;
  }

  // Persist pending queue immediately (internal helper used by flushing).
  async _persistPendingQueueNow() {
    try {
      await persistPendingQueueToStorage(this.pendingQueueStorageKey, this.pendingMlsQueue);
    } catch {}
  }

  /**
   * Flush pending queue entries for a single group.
   * deps is injected so UI/hook can pass send/decrypt helpers without importing UI modules here.
   *
   * deps expected:
   *  - sendEncryptedMessage(outboundPayload)
   *  - decryptEnvelope(envelopePayload)
   *  - budget (optional number)
   */
  async flushPendingQueueForGroup({
    groupId,
    conversationId = null,
    reason = 'manual',
    source = 'unknown',
    deps = {},
  } = {}) {
    const normalizedGroupId = groupId == null ? null : String(groupId).trim();
    if (!normalizedGroupId) return;

    // Single-flight per group
    const existing = this._flushPendingInFlightByGroup?.get(normalizedGroupId);
    if (existing) return existing;

    const gen = this.sessionGeneration;

    const task = (async () => {
      await this.ensurePendingQueueLoaded?.();
      if (!this._guardGen(gen)) return;

      const listFn = typeof this.listPendingEntries === 'function' ? this.listPendingEntries : null;
      const removeFn =
        typeof this.removePendingEntry === 'function' ? this.removePendingEntry : null;
      if (!listFn || !removeFn) return;

      const entries = listFn.call(this, normalizedGroupId) || [];
      // Only flush when runtime is ready and group is not in recovery.
      try {
        if (typeof this.groupInfo === 'function') {
          const info = await this.groupInfo({ groupId: normalizedGroupId }).catch(() => null);
          if (!this._guardGen(gen)) return;
          if (!this.#isGroupReady(info) || info?.needsRecovery) {
            return;
          }
        }
      } catch {
        return;
      }
      if (!Array.isArray(entries) || entries.length === 0) return;

      const sendEncryptedMessage = deps?.sendEncryptedMessage;
      const decryptEnvelope = deps?.decryptEnvelope;

      // Avoid long blocks on the main thread.
      const budget = Number.isFinite(deps?.budget) ? Math.max(1, deps.budget) : 25;
      let processed = 0;

      for (const entry of entries) {
        if (!this._guardGen(gen)) return;
        if (!entry) continue;

        try {
          // Outbound resend entries
          if (entry?.outbound) {
            if (typeof sendEncryptedMessage !== 'function') {
              // no sender available; keep entry
              continue;
            }

            try {
              await sendEncryptedMessage(entry?.outbound);
              // Only remove on success.
              removeFn.call(this, normalizedGroupId, entry);
            } catch {
              // Keep outbound entry for retry; stop to avoid tight loops.
              break;
            }
          } else {
            // Inbound decrypt entries
            if (typeof decryptEnvelope === 'function') {
              const result = await decryptEnvelope(entry?.envelope ?? entry);
              const stillPending = Boolean(
                result?.pendingRecovery ||
                  result?.pendingRuntime ||
                  result?.pendingGenerationTooOld,
              );
              if (!stillPending) {
                removeFn.call(this, normalizedGroupId, entry);
              }
            }
          }
        } catch {
          // Stop on first failure to avoid tight loops.
          break;
        }

        processed += 1;
        if (processed >= budget) break;
      }

      await this._persistPendingQueueNow();
      try {
        this.events?.emit?.('mls:pending-queue-flushed', {
          groupId: normalizedGroupId,
          conversationId: conversationId ?? null,
          reason,
          source,
        });
      } catch {}
    })().finally(() => {
      try {
        if (this._flushPendingInFlightByGroup?.get(normalizedGroupId) === task) {
          this._flushPendingInFlightByGroup.delete(normalizedGroupId);
        }
      } catch {}
    });

    try {
      this._flushPendingInFlightByGroup?.set(normalizedGroupId, task);
    } catch {}

    return task;
  }

  async flushAllPendingQueues({ reason = 'manual', source = 'unknown', deps = {} } = {}) {
    if (this._flushAllPendingInFlight) return this._flushAllPendingInFlight;

    const gen = this.sessionGeneration;

    const task = (async () => {
      await this.ensurePendingQueueLoaded?.();
      if (!this._guardGen(gen)) return;

      const listGroupsFn =
        typeof this.listPendingGroupIds === 'function' ? this.listPendingGroupIds : null;
      if (!listGroupsFn) return;

      const groupIds = listGroupsFn.call(this) || [];
      for (const gid of groupIds) {
        if (!this._guardGen(gen)) return;
        await this.flushPendingQueueForGroup({
          groupId: gid,
          reason,
          source,
          deps,
        });
      }
    })().finally(() => {
      if (this._flushAllPendingInFlight === task) this._flushAllPendingInFlight = null;
    });

    this._flushAllPendingInFlight = task;
    return task;
  }

  // UI-safe "nudge" entrypoint (prevents UI calling convergeGroupState directly)
  async nudgeConverge({
    groupId,
    conversationId = null,
    reason = 'nudge',
    source = 'unknown',
  } = {}) {
    try {
      if (typeof this.convergeGroupState === 'function') {
        return await this.convergeGroupState({
          groupId,
          conversationId,
          reason: `${reason}:${source}`,
        });
      }
    } catch {}
    return null;
  }

  #normalizeIdentityId(value) {
    if (value == null) return null;
    try {
      const str = String(value).trim();
      return str ? str : null;
    } catch {
      return null;
    }
  }

  #resolveIdentityName(nameHint, identityId) {
    const normalizedId = this.#normalizeIdentityId(identityId);
    if (normalizedId && isCanonicalMlsClientId(normalizedId)) {
      return normalizedId;
    }
    if (nameHint != null) {
      const trimmed = String(nameHint).trim();
      if (trimmed) return trimmed;
    }
    return this.identityName || `user-${this.userId}`;
  }

  #normalizeIdentityClientIds(clientIds, identityId = null) {
    const normalized = {};
    const normalizedIdentityId = this.#normalizeIdentityId(identityId);
    if (!clientIds || typeof clientIds !== 'object') {
      return { normalized, changed: Boolean(clientIds) };
    }

    const entries = Object.entries(clientIds);
    const visitedKeys = new Set();
    let changed = false;

    for (const [rawSuite, rawValue] of entries) {
      let suite = normalizeSuiteId(rawSuite);
      if (suite == null && typeof rawSuite === 'string' && rawSuite.startsWith('#')) {
        suite = normalizeSuiteId(rawSuite.slice(1));
      }

      const minted = this.#normalizeIdentityId(rawValue);
      if (suite == null || !minted) {
        changed = true;
        continue;
      }

      const key = String(suite);
      visitedKeys.add(key);
      normalized[key] = minted;

      if (key !== rawSuite || minted !== rawValue) {
        changed = true;
      }
    }

    if (entries.length !== visitedKeys.size) {
      changed = true;
    }

    if (Object.keys(normalized).length !== visitedKeys.size) {
      changed = true;
    }

    return { normalized, changed };
  }

  #normalizeIdentityRecord(identity) {
    if (!identity || typeof identity !== 'object') {
      return { record: identity ?? null, changed: false };
    }

    const normalizedIdentityId = this.#normalizeIdentityId(
      identity.identityId ?? identity.identity_id ?? identity.identity ?? null,
    );
    const normalizedClientId = this.#normalizeIdentityId(
      identity.clientId ?? identity.client_id ?? null,
    );
    const mintedClientId = normalizedClientId ?? null;
    const normalizedLoginNonce = normalizeLoginNonce(
      identity.loginNonce ?? identity.login_nonce ?? null,
    );
    const normalizedDeviceId = this.#normalizeIdentityId(
      identity.deviceId ??
        identity.device_id ??
        identity.identityId ??
        identity.identity_id ??
        null,
    );
    const identityName =
      ensureString(identity.identityName ?? identity.identity_name ?? null) || null;
    const credentialPublicKeyB64 = pickBase64String(
      identity.credentialPublicKeyB64,
      identity.credential_public_key_b64,
      identity.credentialPublicKey,
      identity.credential_public_key,
      identity.signature_public_key_b64,
      identity.signaturePublicKeyB64,
      identity.public_key_b64,
      identity.publicKeyB64,
      null,
    );
    const signaturePrivateKeySeedB64 = pickBase64String(
      identity.signature_private_key_seed_b64,
      identity.signaturePrivateKeySeedB64,
      identity.signing_private_key_b64,
      identity.signingPrivateKeyB64,
      identity.seed,
      null,
    );
    const signaturePrivateKeyRef = ensureString(
      identity.signature_private_key_ref ??
        identity.signaturePrivateKeyRef ??
        (normalizedDeviceId ? `identity-signing:${normalizedDeviceId}` : null),
    );
    const publishedKeyPackageRef = normalizeHexRefValue(
      identity.publishedKeyPackageRef ?? identity.published_key_package_ref ?? null,
    );
    const publishedAt = coerceTimestampMs(identity.publishedAt ?? identity.published_at ?? null);
    const privateMaterialReady = normalizeTriStateBoolean(
      identity.privateMaterialReady ?? identity.private_material_ready,
    );
    const privateMaterialVerifiedAt = coerceTimestampMs(
      identity.privateMaterialVerifiedAt ?? identity.private_material_verified_at ?? null,
    );
    const keyPackageHealthCode =
      ensureString(identity.keyPackageHealthCode ?? identity.key_package_health_code ?? null) ||
      null;
    const keyPackageProvenance = normalizeKeyPackageProvenance(
      identity.keyPackageProvenance ?? identity.key_package_provenance ?? null,
    );
    const welcomeStageReady = normalizeTriStateBoolean(
      identity.welcomeStageReady ?? identity.welcome_stage_ready,
    );
    const welcomeStageReason =
      ensureString(identity.welcomeStageReason ?? identity.welcome_stage_reason ?? null) || null;

    const { normalized: normalizedClientIds, changed: clientIdsChanged } =
      this.#normalizeIdentityClientIds(identity.clientIds, normalizedIdentityId);

    const normalizedSuite = normalizeSuiteId(identity.ciphersuite ?? null);
    if (normalizedSuite != null && mintedClientId) {
      const suiteKey = String(normalizedSuite);
      if (normalizedClientIds[suiteKey] !== mintedClientId) {
        normalizedClientIds[suiteKey] = mintedClientId;
      }
    }

    const nextIdentity = {
      ...identity,
      identityId: identity.identityId ?? normalizedIdentityId ?? identity.identity_id ?? null,
      clientId: mintedClientId ?? null,
      client_id: mintedClientId ?? null,
      deviceId: normalizedDeviceId ?? null,
      device_id: normalizedDeviceId ?? null,
      identityName,
      identity_name: identityName,
      credentialPublicKeyB64: credentialPublicKeyB64 ?? null,
      credential_public_key_b64: credentialPublicKeyB64 ?? null,
      signaturePrivateKeySeedB64: signaturePrivateKeySeedB64 ?? null,
      signature_private_key_seed_b64: signaturePrivateKeySeedB64 ?? null,
      signaturePrivateKeyRef: signaturePrivateKeyRef ?? null,
      signature_private_key_ref: signaturePrivateKeyRef ?? null,
      publishedKeyPackageRef: publishedKeyPackageRef ?? null,
      published_key_package_ref: publishedKeyPackageRef ?? null,
      publishedAt: publishedAt ?? null,
      published_at: publishedAt ?? null,
      privateMaterialReady,
      private_material_ready: privateMaterialReady,
      privateMaterialVerifiedAt: privateMaterialVerifiedAt ?? null,
      private_material_verified_at: privateMaterialVerifiedAt ?? null,
      keyPackageHealthCode,
      key_package_health_code: keyPackageHealthCode,
      keyPackageProvenance,
      key_package_provenance: keyPackageProvenance,
      welcomeStageReady,
      welcome_stage_ready: welcomeStageReady,
      welcomeStageReason,
      welcome_stage_reason: welcomeStageReason,
      loginNonce: normalizedLoginNonce ?? null,
      login_nonce: normalizedLoginNonce ?? null,
      clientIds: normalizedClientIds,
    };

    const changed =
      clientIdsChanged ||
      (identity.loginNonce ?? identity.login_nonce ?? null) !== (normalizedLoginNonce ?? null) ||
      (identity.clientId ?? identity.client_id ?? null) !== (mintedClientId ?? null) ||
      (identity.deviceId ?? identity.device_id ?? null) !== (normalizedDeviceId ?? null) ||
      (identity.identityName ?? identity.identity_name ?? null) !== (identityName ?? null) ||
      (identity.credentialPublicKeyB64 ?? identity.credential_public_key_b64 ?? null) !==
        (credentialPublicKeyB64 ?? null) ||
      (identity.signaturePrivateKeySeedB64 ?? identity.signature_private_key_seed_b64 ?? null) !==
        (signaturePrivateKeySeedB64 ?? null) ||
      (identity.signaturePrivateKeyRef ?? identity.signature_private_key_ref ?? null) !==
        (signaturePrivateKeyRef ?? null) ||
      (identity.publishedKeyPackageRef ?? identity.published_key_package_ref ?? null) !==
        (publishedKeyPackageRef ?? null) ||
      (identity.publishedAt ?? identity.published_at ?? null) !== (publishedAt ?? null) ||
      (identity.privateMaterialReady ?? identity.private_material_ready ?? null) !==
        privateMaterialReady ||
      (identity.privateMaterialVerifiedAt ?? identity.private_material_verified_at ?? null) !==
        (privateMaterialVerifiedAt ?? null) ||
      (identity.keyPackageHealthCode ?? identity.key_package_health_code ?? null) !==
        (keyPackageHealthCode ?? null) ||
      (identity.keyPackageProvenance ?? identity.key_package_provenance ?? null) !==
        (keyPackageProvenance ?? null) ||
      (identity.welcomeStageReady ?? identity.welcome_stage_ready ?? null) !== welcomeStageReady ||
      (identity.welcomeStageReason ?? identity.welcome_stage_reason ?? null) !==
        (welcomeStageReason ?? null) ||
      (identity.clientIds === undefined && Object.keys(normalizedClientIds).length > 0);

    return { record: nextIdentity, changed };
  }

  #setIdentityState(identity) {
    if (!identity || typeof identity !== 'object') {
      return;
    }

    if (!this._clientIdsLoaded && !this._clientIdsLoadPromise) {
      this.#ensureClientIdCacheLoaded().catch(() => {});
    }

    const { record: normalizedIdentity } = this.#normalizeIdentityRecord(identity);
    const effectiveIdentity = normalizedIdentity || identity;

    if (effectiveIdentity?.clientIds && typeof effectiveIdentity.clientIds === 'object') {
      for (const [suiteKey, minted] of Object.entries(effectiveIdentity.clientIds)) {
        const numericSuite = normalizeSuiteId(suiteKey);
        const normalizedClientId = this.#normalizeIdentityId(minted);
        if (numericSuite != null && normalizedClientId) {
          this.#adoptClientIdForSuite(numericSuite, normalizedClientId);
        }
      }
    }

    const previousIdentity = this.identityRecord;
    const previousClientId = this.identityClientId;
    const previousIdentityId = this.#normalizeIdentityId(
      previousIdentity?.identityId ??
        previousIdentity?.identity_id ??
        previousIdentity?.identity ??
        null,
    );
    const previousSuite = normalizeSuiteId(previousIdentity?.ciphersuite ?? null);

    const normalizedIdentityId = this.#normalizeIdentityId(
      effectiveIdentity.identityId ?? effectiveIdentity.identity_id ?? effectiveIdentity.identity,
    );
    if (normalizedIdentityId) {
      this.identityId = normalizedIdentityId;
    }

    const normalizedSuite = normalizeSuiteId(effectiveIdentity.ciphersuite ?? null);
    const normalizedClientId = this.#normalizeIdentityId(
      effectiveIdentity.clientId ?? effectiveIdentity.client_id ?? null,
    );

    let resolvedClientId = normalizedClientId ?? null;

    const isIdentityReplacement =
      Boolean(previousIdentityId) &&
      Boolean(normalizedIdentityId) &&
      normalizedIdentityId !== previousIdentityId;

    if (isIdentityReplacement) {
      this.#clearClientIdCacheForSuite(previousSuite);
      this.#clearClientIdCacheForSuite(normalizedSuite);
      this.#clearKeyPackageCacheForSuite(previousSuite);
      this.#clearKeyPackageCacheForSuite(normalizedSuite);
      resolvedClientId = normalizedClientId ?? null;
    }

    if (normalizedSuite != null) {
      if (resolvedClientId) {
        this.#adoptClientIdForSuite(normalizedSuite, resolvedClientId);
      } else if (!isIdentityReplacement) {
        const cachedClientId = this.#getClientIdForSuite(normalizedSuite);
        if (cachedClientId) {
          resolvedClientId = cachedClientId;
        }
      }
    }

    if (!resolvedClientId && normalizedIdentityId && isCanonicalMlsClientId(normalizedIdentityId)) {
      resolvedClientId = normalizedIdentityId;
      if (normalizedSuite != null) {
        this.#adoptClientIdForSuite(normalizedSuite, resolvedClientId);
      }
    }

    if (effectiveIdentity?.clientIds && typeof effectiveIdentity.clientIds === 'object') {
      const suiteKey = String(normalizedSuite ?? '');
      if (normalizedSuite != null && resolvedClientId) {
        if (effectiveIdentity.clientIds[suiteKey] !== resolvedClientId) {
          effectiveIdentity.clientIds = {
            ...effectiveIdentity.clientIds,
            [suiteKey]: resolvedClientId,
          };
        }
      }
    }

    this.identityRecord = effectiveIdentity;
    this.identityClientId = resolvedClientId ?? null;

    if (
      resolvedClientId &&
      resolvedClientId !== previousClientId &&
      typeof console !== 'undefined' &&
      typeof console.info === 'function'
    ) {
      try {
        debugLog('[MLS] Active MLS client id updated', { clientId: resolvedClientId });
      } catch {}
    }
  }

  #normalizeSuiteKey(value) {
    const numeric = normalizeSuiteId(value);
    if (numeric != null) {
      return `#${numeric}`;
    }
    if (value == null) {
      return '';
    }
    try {
      return String(value);
    } catch {
      return '';
    }
  }

  #suiteKeysMatch(a, b) {
    return this.#normalizeSuiteKey(a) === this.#normalizeSuiteKey(b);
  }

  async #ensureKeyPackageCacheLoaded() {
    if (this._keyPackageCacheLoaded) {
      return;
    }

    if (this._keyPackageCacheLoadPromise) {
      await this._keyPackageCacheLoadPromise;
      return;
    }

    this._keyPackageCacheLoadPromise = (async () => {
      try {
        const stored = await getItem(this.keyPackagesKey);
        if (stored && typeof stored === 'object') {
          Object.entries(stored).forEach(([key, value]) => {
            const normalized = this.#normalizeStoredKeyPackageCacheEntry(value);
            if (normalized) {
              this.keyPackageStatusCache.set(key, normalized);
            }
          });
        }
      } catch (error) {
        debugLog('[MLS] Failed to load key package cache from storage', { error });
      } finally {
        this._keyPackageCacheLoaded = true;
        this._keyPackageCacheLoadPromise = null;
      }
    })();

    await this._keyPackageCacheLoadPromise;
  }

  #normalizeStoredKeyPackageByRefEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const kpRef = normalizeHexRefValue(
      entry.kp_ref ?? entry.kpRef ?? entry.key_package_ref ?? entry.keyPackageRef ?? entry.ref,
    );
    if (!kpRef) {
      return null;
    }

    const statusCandidate = ensureString(entry.status ?? null);
    const status = statusCandidate ? statusCandidate.toLowerCase() : null;
    const keyPackageB64 = pickBase64String(
      entry.key_package_b64,
      entry.keyPackageB64,
      entry.key_package,
      entry.keyPackage,
      null,
    );

    const normalized = {
      kp_ref: kpRef,
      key_package_ref: kpRef,
      ref: kpRef,
      status: status === 'consumed' ? 'consumed' : 'unused',
      identity_id: this.#normalizeIdentityId(entry.identity_id ?? entry.identityId ?? null),
      client_id: this.#normalizeIdentityId(entry.client_id ?? entry.clientId ?? null),
      ciphersuite: normalizeSuiteId(entry.ciphersuite ?? null),
      key_package_b64: keyPackageB64 ?? null,
      init_private_key_ref:
        ensureString(entry.init_private_key_ref ?? entry.initPrivateKeyRef ?? null) || null,
      hpke_private_key_ref:
        ensureString(entry.hpke_private_key_ref ?? entry.hpkePrivateKeyRef ?? null) || null,
      keypackage_bundle_ref:
        ensureString(entry.keypackage_bundle_ref ?? entry.keypackageBundleRef ?? null) || null,
      published_at: coerceTimestampMs(entry.published_at ?? entry.publishedAt ?? null) || null,
      verified_local_at:
        coerceTimestampMs(entry.verified_local_at ?? entry.verifiedLocalAt ?? null) || null,
      private_material_ready: normalizeTriStateBoolean(
        entry.private_material_ready ?? entry.privateMaterialReady,
      ),
      key_package_handle_alive: normalizeTriStateBoolean(
        entry.key_package_handle_alive ?? entry.keyPackageHandleAlive,
      ),
      key_package_exportable: normalizeTriStateBoolean(
        entry.key_package_exportable ?? entry.keyPackageExportable,
      ),
      key_package_health_code:
        ensureString(entry.key_package_health_code ?? entry.keyPackageHealthCode ?? null) || null,
      key_package_provenance:
        normalizeKeyPackageProvenance(
          entry.key_package_provenance ?? entry.keyPackageProvenance ?? null,
        ) || null,
      welcome_stage_ready: normalizeTriStateBoolean(
        entry.welcome_stage_ready ?? entry.welcomeStageReady,
      ),
      welcome_stage_reason:
        ensureString(entry.welcome_stage_reason ?? entry.welcomeStageReason ?? null) || null,
      consumed_by_welcome: normalizeTriStateBoolean(
        entry.consumed_by_welcome ?? entry.consumedByWelcome,
      ),
      consumed_at: coerceTimestampMs(entry.consumed_at ?? entry.consumedAt ?? null) || null,
      used_group_id: ensureString(entry.used_group_id ?? entry.usedGroupId ?? null) || null,
      used_epoch: normalizeEpochOrNull(entry.used_epoch ?? entry.usedEpoch ?? null),
      updated_at:
        coerceTimestampMs(entry.updated_at ?? entry.updatedAt ?? Date.now()) || Date.now(),
      created_at:
        coerceTimestampMs(entry.created_at ?? entry.createdAt ?? Date.now()) || Date.now(),
    };

    return normalized;
  }

  #serializeKeyPackageByRefEntry(entry) {
    const normalized = this.#normalizeStoredKeyPackageByRefEntry(entry);
    if (!normalized) {
      return null;
    }
    return { ...normalized };
  }

  async #ensureKeyPackagesByRefLoaded() {
    if (this._keyPackagesByRefLoaded) {
      return;
    }

    if (this._keyPackagesByRefLoadPromise) {
      await this._keyPackagesByRefLoadPromise;
      return;
    }

    this._keyPackagesByRefLoadPromise = (async () => {
      try {
        const stored = await getItem(this.keyPackagesByRefKey);
        if (stored && typeof stored === 'object') {
          Object.entries(stored).forEach(([rawRef, rawEntry]) => {
            const entry = this.#normalizeStoredKeyPackageByRefEntry({
              ...(rawEntry && typeof rawEntry === 'object' ? rawEntry : {}),
              kp_ref: rawRef,
            });
            if (entry?.kp_ref) {
              this.keyPackagesByRef.set(entry.kp_ref, entry);
            }
          });
        }
      } catch (error) {
        debugLog('[MLS] Failed to load key package ref cache from storage', { error });
      } finally {
        this._keyPackagesByRefLoaded = true;
        this._keyPackagesByRefLoadPromise = null;
      }
    })();

    await this._keyPackagesByRefLoadPromise;
  }

  async #persistKeyPackagesByRef() {
    const payload = {};
    this.keyPackagesByRef.forEach((entry, kpRef) => {
      const serialized = this.#serializeKeyPackageByRefEntry({ ...entry, kp_ref: kpRef });
      if (serialized) {
        payload[kpRef] = serialized;
      }
    });
    await setItem(this.keyPackagesByRefKey, payload);
  }

  #schedulePersistKeyPackagesByRef() {
    if (this._keyPackagesByRefPersistScheduled) {
      return;
    }
    this._keyPackagesByRefPersistScheduled = true;
    Promise.resolve()
      .then(() => this.#persistKeyPackagesByRef())
      .catch((error) => {
        debugLog('[MLS] Failed to persist key package ref cache', { error });
      })
      .finally(() => {
        this._keyPackagesByRefPersistScheduled = false;
      });
  }

  async #trackKeyPackageByRef({
    kpRef,
    identityId,
    clientId,
    ciphersuite,
    keyPackageB64,
    status = 'unused',
    publishedAt = null,
    verifiedLocalAt = null,
    privateMaterialReady = null,
    keyPackageHandleAlive = null,
    keyPackageExportable = null,
    keyPackageHealthCode = null,
    keyPackageProvenance = null,
    welcomeStageReady = null,
    welcomeStageReason = null,
    consumedByWelcome = null,
    consumedAt = null,
    usedGroupId = null,
    usedEpoch = null,
  } = {}) {
    const normalizedRef = normalizeHexRefValue(kpRef);
    if (!normalizedRef) {
      return null;
    }

    await this.#ensureKeyPackagesByRefLoaded();

    const now = Date.now();
    const previous = this.keyPackagesByRef.get(normalizedRef) || null;
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ?? previous?.identity_id ?? null,
    );
    const normalizedClientId = this.#normalizeIdentityId(
      clientId ?? previous?.client_id ?? normalizedIdentityId ?? null,
    );
    const normalizedSuite = normalizeSuiteId(ciphersuite ?? previous?.ciphersuite ?? null);
    const normalizedKeyPackageB64 = pickBase64String(
      keyPackageB64,
      previous?.key_package_b64,
      null,
    );
    const normalizedStatus = String(status || previous?.status || 'unused').toLowerCase();
    const keystoreIdentity = normalizedIdentityId ?? normalizedClientId ?? 'unknown';
    const keystoreSuite = normalizedSuite ?? 'unknown';

    const next = {
      kp_ref: normalizedRef,
      key_package_ref: normalizedRef,
      ref: normalizedRef,
      identity_id: normalizedIdentityId ?? null,
      client_id: normalizedClientId ?? null,
      ciphersuite: normalizedSuite ?? null,
      key_package_b64: normalizedKeyPackageB64 ?? null,
      status: normalizedStatus === 'consumed' ? 'consumed' : 'unused',
      init_private_key_ref:
        previous?.init_private_key_ref ??
        `keystore:${keystoreIdentity}:${keystoreSuite}:init-private`,
      hpke_private_key_ref:
        previous?.hpke_private_key_ref ??
        `keystore:${keystoreIdentity}:${keystoreSuite}:hpke-private`,
      keypackage_bundle_ref:
        previous?.keypackage_bundle_ref ?? `keystore:${keystoreIdentity}:${keystoreSuite}:bundle`,
      published_at:
        coerceTimestampMs(publishedAt ?? previous?.published_at ?? null) ??
        previous?.published_at ??
        null,
      verified_local_at:
        coerceTimestampMs(verifiedLocalAt ?? previous?.verified_local_at ?? null) ??
        previous?.verified_local_at ??
        null,
      private_material_ready:
        normalizeTriStateBoolean(privateMaterialReady) ??
        normalizeTriStateBoolean(previous?.private_material_ready) ??
        null,
      key_package_handle_alive:
        normalizeTriStateBoolean(keyPackageHandleAlive) ??
        normalizeTriStateBoolean(previous?.key_package_handle_alive) ??
        null,
      key_package_exportable:
        normalizeTriStateBoolean(keyPackageExportable) ??
        normalizeTriStateBoolean(previous?.key_package_exportable) ??
        null,
      key_package_health_code:
        ensureString(keyPackageHealthCode ?? previous?.key_package_health_code ?? null) || null,
      key_package_provenance:
        normalizeKeyPackageProvenance(
          keyPackageProvenance ?? previous?.key_package_provenance ?? null,
        ) || null,
      welcome_stage_ready:
        normalizeTriStateBoolean(welcomeStageReady) ??
        normalizeTriStateBoolean(previous?.welcome_stage_ready) ??
        null,
      welcome_stage_reason:
        ensureString(welcomeStageReason ?? previous?.welcome_stage_reason ?? null) || null,
      consumed_by_welcome:
        normalizeTriStateBoolean(consumedByWelcome) ??
        normalizeTriStateBoolean(previous?.consumed_by_welcome) ??
        null,
      consumed_at:
        coerceTimestampMs(consumedAt ?? previous?.consumed_at ?? null) ??
        previous?.consumed_at ??
        null,
      used_group_id: ensureString(usedGroupId ?? previous?.used_group_id ?? null) || null,
      used_epoch: normalizeEpochOrNull(usedEpoch ?? previous?.used_epoch ?? null),
      created_at: previous?.created_at ?? now,
      updated_at: now,
    };

    this.keyPackagesByRef.set(normalizedRef, next);
    this.#schedulePersistKeyPackagesByRef();
    return next;
  }

  async #markKeyPackageRefConsumed(kpRef, { groupId = null, epoch = null } = {}) {
    const normalizedRef = normalizeHexRefValue(kpRef);
    if (!normalizedRef) {
      return null;
    }
    const tracked = await this.#trackKeyPackageByRef({
      kpRef: normalizedRef,
      status: 'consumed',
      consumedByWelcome: true,
      consumedAt: Date.now(),
      usedGroupId: groupId,
      usedEpoch: epoch,
    });

    let updatedAny = false;
    for (const [cacheKey, entry] of this.keyPackageStatusCache.entries()) {
      if (!entry || entry.ref !== normalizedRef) {
        continue;
      }
      this.keyPackageStatusCache.set(cacheKey, {
        ...entry,
        status: 'consumed',
        consumedAt: Date.now(),
        consumedByWelcome: true,
        usedGroupId: ensureString(groupId) || entry.usedGroupId || null,
        usedEpoch: normalizeEpochOrNull(epoch ?? entry.usedEpoch ?? null),
      });
      updatedAny = true;
    }
    if (updatedAny) {
      this.#schedulePersistKeyPackageCache();
    }
    return tracked;
  }

  async #registerIssuedWelcomeTargetRef(
    kpRef,
    { identityId = null, clientId = null, ciphersuite = null, groupId = null, epoch = null } = {},
  ) {
    const normalizedRef = normalizeHexRefValue(kpRef);
    if (!normalizedRef) {
      return null;
    }

    const trackedEntry = await this.#getTrackedKeyPackageRefEntry(normalizedRef);
    const locallyConsumed =
      trackedEntry?.status === 'consumed' || trackedEntry?.consumed_by_welcome === true;

    return this.#trackKeyPackageByRef({
      kpRef: normalizedRef,
      identityId,
      clientId,
      ciphersuite,
      status: trackedEntry?.status === 'consumed' ? 'consumed' : (trackedEntry?.status ?? 'unused'),
      consumedByWelcome: locallyConsumed
        ? true
        : normalizeTriStateBoolean(trackedEntry?.consumed_by_welcome),
      consumedAt: locallyConsumed ? (trackedEntry?.consumed_at ?? null) : null,
      usedGroupId: locallyConsumed
        ? ensureString(groupId ?? trackedEntry?.used_group_id ?? null) || null
        : ensureString(trackedEntry?.used_group_id ?? null) || null,
      usedEpoch: locallyConsumed
        ? normalizeEpochOrNull(epoch ?? trackedEntry?.used_epoch ?? null)
        : normalizeEpochOrNull(trackedEntry?.used_epoch ?? null),
    });
  }

  async #persistKeyPackageLifecycle({
    identityId = null,
    clientId = null,
    ciphersuite = null,
    kpRef = null,
    keyPackageB64 = null,
    status = 'unused',
    publishedAt = null,
    verifiedLocalAt = null,
    privateMaterialReady = null,
    keyPackageHandleAlive = null,
    keyPackageExportable = null,
    keyPackageHealthCode = null,
    keyPackageProvenance = null,
    welcomeStageReady = null,
    welcomeStageReason = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    const normalizedClientId = this.#normalizeIdentityId(clientId ?? null);
    const normalizedSuite = normalizeSuiteId(ciphersuite ?? null);
    const normalizedRef = normalizeHexRefValue(kpRef);
    const normalizedPublishedAt = coerceTimestampMs(publishedAt ?? null);
    const normalizedVerifiedLocalAt = coerceTimestampMs(verifiedLocalAt ?? null);
    const normalizedHealthCode = ensureString(keyPackageHealthCode ?? null) || null;
    const normalizedKeyPackageB64 = pickBase64String(keyPackageB64, null);
    const unresolvedWelcomeTarget = normalizedIdentityId
      ? await this.#findUnresolvedWelcomeTargetForIdentity(normalizedIdentityId).catch(() => null)
      : null;
    const unresolvedTargetRef = normalizeHexRefValue(unresolvedWelcomeTarget?.targetRef ?? null);
    const preserveWelcomeTargetAuthority =
      Boolean(unresolvedTargetRef) &&
      Boolean(normalizedRef) &&
      unresolvedTargetRef !== normalizedRef &&
      status !== 'consumed';
    const authoritativeRefForStorage = preserveWelcomeTargetAuthority
      ? unresolvedTargetRef
      : normalizedRef;

    const cacheTargets = new Set([normalizedClientId, normalizedIdentityId].filter(Boolean));
    cacheTargets.forEach((cacheId) => {
      if (!cacheId || normalizedSuite == null) return;
      this.#updateKeyPackageCache(cacheId, normalizedSuite, {
        status,
        ref: authoritativeRefForStorage ?? undefined,
        lastPublishedAt: normalizedPublishedAt ?? undefined,
        publishedAt: normalizedPublishedAt ?? undefined,
        verifiedLocalAt: normalizedVerifiedLocalAt ?? undefined,
        privateMaterialReady: normalizeTriStateBoolean(privateMaterialReady) ?? undefined,
        keyPackageHandleAlive: normalizeTriStateBoolean(keyPackageHandleAlive) ?? undefined,
        keyPackageExportable: normalizeTriStateBoolean(keyPackageExportable) ?? undefined,
        keyPackageHealthCode: normalizedHealthCode ?? undefined,
        keyPackageProvenance:
          normalizeKeyPackageProvenance(keyPackageProvenance ?? null) ?? undefined,
        welcomeStageReady: normalizeTriStateBoolean(welcomeStageReady) ?? undefined,
        welcomeStageReason: ensureString(welcomeStageReason ?? null) || undefined,
      });
    });

    if (normalizedSuite != null) {
      if (status === 'consumed') {
        this.#clearPublishedKeyPackageMirror({
          identityId: normalizedIdentityId,
          clientId: normalizedClientId,
          ciphersuite: normalizedSuite,
        });
      } else if (normalizedKeyPackageB64 && !preserveWelcomeTargetAuthority) {
        this.#persistPublishedKeyPackageMirror({
          identityId: normalizedIdentityId,
          clientId: normalizedClientId,
          ciphersuite: normalizedSuite,
          keyPackageB64: normalizedKeyPackageB64,
          publishedAt: normalizedPublishedAt,
        });
      }
    }

    if (normalizedRef) {
      await this.#trackKeyPackageByRef({
        kpRef: normalizedRef,
        identityId: normalizedIdentityId,
        clientId: normalizedClientId ?? normalizedIdentityId,
        ciphersuite: normalizedSuite,
        keyPackageB64: normalizedKeyPackageB64,
        status,
        publishedAt: normalizedPublishedAt,
        verifiedLocalAt: normalizedVerifiedLocalAt,
        privateMaterialReady,
        keyPackageHandleAlive,
        keyPackageExportable,
        keyPackageHealthCode: normalizedHealthCode,
        keyPackageProvenance,
        welcomeStageReady,
        welcomeStageReason,
      });
    }

    let identityRecord = null;
    try {
      const currentIdentityId = this.#normalizeIdentityId(
        this.identityRecord?.identityId ?? this.identityRecord?.identity_id ?? null,
      );
      if (currentIdentityId && currentIdentityId === normalizedIdentityId) {
        identityRecord = this.identityRecord;
      }
      if (!identityRecord) {
        const storedIdentity = await getItem(this.identityKey);
        const { record: normalizedStoredIdentity } = this.#normalizeIdentityRecord(storedIdentity);
        const storedIdentityId = this.#normalizeIdentityId(
          normalizedStoredIdentity?.identityId ?? normalizedStoredIdentity?.identity_id ?? null,
        );
        if (storedIdentityId && storedIdentityId === normalizedIdentityId) {
          identityRecord = normalizedStoredIdentity;
        }
      }
    } catch {}

    if (identityRecord && normalizedIdentityId) {
      const nextIdentity = {
        ...identityRecord,
        publishedKeyPackageRef:
          authoritativeRefForStorage ??
          identityRecord.publishedKeyPackageRef ??
          identityRecord.published_key_package_ref ??
          null,
        published_key_package_ref:
          authoritativeRefForStorage ??
          identityRecord.publishedKeyPackageRef ??
          identityRecord.published_key_package_ref ??
          null,
        publishedAt:
          normalizedPublishedAt ??
          identityRecord.publishedAt ??
          identityRecord.published_at ??
          null,
        published_at:
          normalizedPublishedAt ??
          identityRecord.publishedAt ??
          identityRecord.published_at ??
          null,
        privateMaterialReady:
          privateMaterialReady === true
            ? true
            : privateMaterialReady === false
              ? false
              : (identityRecord.privateMaterialReady ??
                identityRecord.private_material_ready ??
                null),
        private_material_ready:
          privateMaterialReady === true
            ? true
            : privateMaterialReady === false
              ? false
              : (identityRecord.privateMaterialReady ??
                identityRecord.private_material_ready ??
                null),
        privateMaterialVerifiedAt:
          normalizedVerifiedLocalAt ??
          identityRecord.privateMaterialVerifiedAt ??
          identityRecord.private_material_verified_at ??
          null,
        private_material_verified_at:
          normalizedVerifiedLocalAt ??
          identityRecord.privateMaterialVerifiedAt ??
          identityRecord.private_material_verified_at ??
          null,
        keyPackageHealthCode:
          normalizedHealthCode ??
          identityRecord.keyPackageHealthCode ??
          identityRecord.key_package_health_code ??
          null,
        key_package_health_code:
          normalizedHealthCode ??
          identityRecord.keyPackageHealthCode ??
          identityRecord.key_package_health_code ??
          null,
        keyPackageProvenance:
          normalizeKeyPackageProvenance(
            keyPackageProvenance ??
              identityRecord.keyPackageProvenance ??
              identityRecord.key_package_provenance ??
              null,
          ) ?? null,
        key_package_provenance:
          normalizeKeyPackageProvenance(
            keyPackageProvenance ??
              identityRecord.keyPackageProvenance ??
              identityRecord.key_package_provenance ??
              null,
          ) ?? null,
        welcomeStageReady:
          normalizeTriStateBoolean(welcomeStageReady) ??
          normalizeTriStateBoolean(
            identityRecord.welcomeStageReady ?? identityRecord.welcome_stage_ready,
          ),
        welcome_stage_ready:
          normalizeTriStateBoolean(welcomeStageReady) ??
          normalizeTriStateBoolean(
            identityRecord.welcomeStageReady ?? identityRecord.welcome_stage_ready,
          ),
        welcomeStageReason:
          ensureString(
            welcomeStageReason ??
              identityRecord.welcomeStageReason ??
              identityRecord.welcome_stage_reason ??
              null,
          ) || null,
        welcome_stage_reason:
          ensureString(
            welcomeStageReason ??
              identityRecord.welcomeStageReason ??
              identityRecord.welcome_stage_reason ??
              null,
          ) || null,
      };
      const { record: normalizedIdentity } = this.#normalizeIdentityRecord(nextIdentity);
      await setItem(this.identityKey, normalizedIdentity);
      this.#setIdentityState(normalizedIdentity);
    }

    if (preserveWelcomeTargetAuthority) {
      try {
        console.info(
          '[MLS] preserving unresolved welcome target as authoritative during key-package lifecycle update',
          {
            identityId: normalizedIdentityId,
            unresolvedTargetRef,
            localLifecycleRef: normalizedRef,
            groupId: unresolvedWelcomeTarget?.groupId ?? null,
            source: unresolvedWelcomeTarget?.source ?? null,
          },
        );
      } catch {}
    }
  }

  #pruneAckedWelcomeCache(now = Date.now()) {
    if (!this._ackedWelcomeTimestampsByGroup || !this._ackedWelcomeTimestampsByGroup.size) {
      this._ackedWelcomeIdsByGroup.clear();
      return;
    }

    for (const [groupId, timestampMap] of this._ackedWelcomeTimestampsByGroup.entries()) {
      if (!(timestampMap instanceof Map) || !timestampMap.size) {
        this._ackedWelcomeTimestampsByGroup.delete(groupId);
        this._ackedWelcomeIdsByGroup.delete(groupId);
        continue;
      }

      for (const [welcomeId, ts] of timestampMap.entries()) {
        const numericTs = Number(ts || 0);
        if (!Number.isFinite(numericTs) || now - numericTs > ACKED_WELCOME_IDS_MAX_AGE_MS) {
          timestampMap.delete(welcomeId);
        }
      }

      if (timestampMap.size > ACKED_WELCOME_IDS_MAX_PER_GROUP) {
        const sorted = Array.from(timestampMap.entries()).sort(
          (a, b) => Number(b[1] || 0) - Number(a[1] || 0),
        );
        timestampMap.clear();
        sorted.slice(0, ACKED_WELCOME_IDS_MAX_PER_GROUP).forEach(([welcomeId, ts]) => {
          timestampMap.set(welcomeId, ts);
        });
      }

      if (!timestampMap.size) {
        this._ackedWelcomeTimestampsByGroup.delete(groupId);
        this._ackedWelcomeIdsByGroup.delete(groupId);
      } else {
        this._ackedWelcomeTimestampsByGroup.set(groupId, timestampMap);
        this._ackedWelcomeIdsByGroup.set(groupId, new Set(Array.from(timestampMap.keys())));
      }
    }
  }

  async #ensureAckedWelcomeCacheLoaded() {
    if (this._ackedWelcomeIdsLoaded) {
      this.#pruneAckedWelcomeCache();
      return;
    }

    if (this._ackedWelcomeIdsLoadPromise) {
      await this._ackedWelcomeIdsLoadPromise;
      this.#pruneAckedWelcomeCache();
      return;
    }

    this._ackedWelcomeIdsLoadPromise = (async () => {
      try {
        const stored = await getItem(this.ackedWelcomesKey);
        if (stored && typeof stored === 'object') {
          for (const [groupIdRaw, entry] of Object.entries(stored)) {
            const groupId = ensureString(groupIdRaw);
            if (!groupId || !entry || typeof entry !== 'object') continue;
            const timestampMap = new Map();
            for (const [welcomeIdRaw, ts] of Object.entries(entry)) {
              const welcomeId = ensureString(welcomeIdRaw);
              const numericTs = Number(ts || 0);
              if (!welcomeId || !Number.isFinite(numericTs)) continue;
              timestampMap.set(welcomeId, numericTs);
            }
            if (timestampMap.size) {
              this._ackedWelcomeTimestampsByGroup.set(groupId, timestampMap);
              this._ackedWelcomeIdsByGroup.set(groupId, new Set(Array.from(timestampMap.keys())));
            }
          }
        }
      } catch (error) {
        debugLog('[MLS] Failed to load acked welcome cache from storage', { error });
      } finally {
        this._ackedWelcomeIdsLoaded = true;
        this._ackedWelcomeIdsLoadPromise = null;
      }
    })();

    await this._ackedWelcomeIdsLoadPromise;
    this.#pruneAckedWelcomeCache();
  }

  async #persistAckedWelcomeCache() {
    this.#pruneAckedWelcomeCache();
    const payload = {};
    try {
      for (const [groupId, timestampMap] of this._ackedWelcomeTimestampsByGroup.entries()) {
        if (!(timestampMap instanceof Map) || !timestampMap.size) continue;
        const entry = {};
        for (const [welcomeId, ts] of timestampMap.entries()) {
          entry[welcomeId] = Number(ts || Date.now());
        }
        if (Object.keys(entry).length) {
          payload[groupId] = entry;
        }
      }
      await setItem(this.ackedWelcomesKey, payload);
    } catch (error) {
      debugLog('[MLS] Failed to persist acked welcome cache', { error });
    }
  }

  #scheduleAckedWelcomeCachePersist() {
    if (this._ackedWelcomeIdsPersistScheduled) {
      return;
    }
    this._ackedWelcomeIdsPersistScheduled = true;
    setTimeout(async () => {
      this._ackedWelcomeIdsPersistScheduled = false;
      await this.#persistAckedWelcomeCache();
    }, 0);
  }

  async #resetIdentityState({ clearGroups = false } = {}) {
    try {
      await shutdownMlsWorker();
    } catch {}
    try {
      await setItem(this.identityKey, null);
    } catch {}
    try {
      await setItem(this.keyPackagesKey, null);
    } catch {}
    try {
      await setItem(this.keyPackagesByRefKey, null);
    } catch {}
    try {
      await setItem(this.clientIdsKey, null);
    } catch {}
    try {
      await setItem(this.deviceIdentityKey, null);
    } catch {}
    if (clearGroups) {
      try {
        await this.#saveGroups({});
      } catch {}
    }
    this.identityRecord = null;
    this.identityId = null;
    this.identityClientId = null;
    this.clientIdsBySuite.clear();
    this.keyPackagesByRef.clear();
    this._keyPackagesByRefLoaded = false;
    this._keyPackagesByRefLoadPromise = null;
    this._ackedWelcomeIdsByGroup.clear();
    this._ackedWelcomeTimestampsByGroup.clear();
    this._ackedWelcomeIdsLoaded = false;
    this._ackedWelcomeIdsLoadPromise = null;
  }

  async #clearStableIdentityState() {
    try {
      await writeStableValue(stableKeys.identityId(this.userId), null);
    } catch {}
    try {
      await writeStableValue(stableKeys.seed(this.userId), null);
    } catch {}
  }

  #isClientIdMismatchError(error) {
    const code = error?.code ?? error?.payload?.error?.code;
    return code === 'client_id_mismatch';
  }

  #isWelcomeRefMismatchError(error) {
    const code = error?.code ?? error?.payload?.error?.code;
    return code === 'welcome_ref_mismatch';
  }

  #isJoinBundleRecoveryError(error) {
    const code = error?.code ?? error?.payload?.error?.code;
    const status = Number(error?.status ?? error?.payload?.status ?? NaN);
    return (
      code === 'invalid_identity' ||
      code === 'key_package_required' ||
      code === 'identity_required' ||
      code === 'welcome_unavailable' ||
      ((!code || code === 'join_bundle_error') && (status === 422 || status === 503))
    );
  }

  #isAlreadyMemberNoWelcomeBundle(bundle) {
    if (!bundle || typeof bundle !== 'object') return false;
    const alreadyMember = Boolean(bundle?.already_member || bundle?.alreadyMember);
    const welcome =
      bundle?.welcome_b64 || bundle?.welcomeB64 || bundle?.welcome || bundle?.welcomeB64;
    return alreadyMember && !welcome;
  }

  #shouldAttemptForceJoinBundle(groupId, relatedGroupKeys = []) {
    const normalizedKeys = Array.from(
      new Set(
        [groupId, ...(Array.isArray(relatedGroupKeys) ? relatedGroupKeys : [relatedGroupKeys])]
          .map((value) => ensureString(value))
          .filter(Boolean),
      ),
    );
    if (!normalizedKeys.length) return false;
    const now = Date.now();
    let lastAttempt = 0;
    for (const key of normalizedKeys) {
      const candidate = this._forceJoinBundleDebounce.get(key) || 0;
      if (candidate > lastAttempt) {
        lastAttempt = candidate;
      }
    }
    if (now - lastAttempt < FORCE_JOIN_BUNDLE_DEBOUNCE_MS) {
      return false;
    }
    for (const key of normalizedKeys) {
      this._forceJoinBundleDebounce.set(key, now);
    }
    return true;
  }

  #shouldPublishBeforeJoinBundle({ groupId, identityId } = {}) {
    const normalizedGroupId = ensureString(groupId) || 'unknown-group';
    const normalizedIdentityId = this.#normalizeIdentityId(identityId) || 'unknown-identity';
    const key = `${normalizedGroupId}::${normalizedIdentityId}`;
    const now = Date.now();
    const lastPublish = this._forceJoinBundlePublishDebounce.get(key) || 0;
    if (now - lastPublish < FORCE_JOIN_BUNDLE_PUBLISH_DEBOUNCE_MS) {
      return false;
    }
    this._forceJoinBundlePublishDebounce.set(key, now);
    return true;
  }

  #resolveWelcomeTargetRef({ welcomeB64 = null, usedKpRef = null, preferredRefs = [] } = {}) {
    const normalizedUsedRef = normalizeHexRefValue(usedKpRef);
    if (normalizedUsedRef) {
      return normalizedUsedRef;
    }

    const normalizedWelcomeB64 = pickBase64String(welcomeB64, null);
    if (!normalizedWelcomeB64) {
      return null;
    }

    return (
      resolveMatchingWelcomeRefHex(normalizedWelcomeB64, preferredRefs) ||
      extractWelcomeRefHexFromB64(normalizedWelcomeB64)
    );
  }

  async #getTrackedKeyPackageRefEntry(kpRef) {
    const normalizedRef = normalizeHexRefValue(kpRef);
    if (!normalizedRef) {
      return null;
    }

    await this.#ensureKeyPackagesByRefLoaded().catch(() => null);
    const trackedEntry = this.keyPackagesByRef.get(normalizedRef) || null;
    return trackedEntry && typeof trackedEntry === 'object' ? trackedEntry : null;
  }

  async #isTrackedKeyPackageRefConsumed(kpRef) {
    const trackedEntry = await this.#getTrackedKeyPackageRefEntry(kpRef);
    return Boolean(
      trackedEntry &&
        (trackedEntry.status === 'consumed' || trackedEntry.consumed_by_welcome === true),
    );
  }

  #resolveActiveJoinBundleTargetKey({ groupId = null, identityId = null } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    if (!normalizedGroupId || !normalizedIdentityId) {
      return null;
    }
    return `${normalizedGroupId}:${normalizedIdentityId}`;
  }

  #registerActiveJoinBundleTarget({
    groupId = null,
    identityId = null,
    keyPackageB64 = null,
    keyPackageRef = null,
    ciphersuite = null,
    source = null,
  } = {}) {
    const targetKey = this.#resolveActiveJoinBundleTargetKey({ groupId, identityId });
    if (!targetKey) {
      return null;
    }
    const now = Date.now();
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    const normalizedRef = normalizeHexRefValue(keyPackageRef ?? null);
    const entry = {
      groupId: ensureString(groupId) ?? null,
      identityId: normalizedIdentityId,
      keyPackageB64: pickBase64String(keyPackageB64, null),
      targetRef: normalizedRef,
      ciphersuite: normalizeSuiteId(ciphersuite ?? null) ?? null,
      source: ensureString(source) ?? 'active_join_bundle_request',
      requestedAt: now,
      expiresAt: now + ACTIVE_JOIN_BUNDLE_TARGET_TTL_MS,
    };
    this._activeJoinBundleTargetByGroupIdentity.set(targetKey, entry);
    return entry;
  }

  async #getActiveJoinBundleTarget({
    groupId = null,
    identityId = null,
    preferredKeyPackageB64 = null,
    ciphersuite = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }
    const normalizedGroupId = ensureString(groupId);
    let targetKey = this.#resolveActiveJoinBundleTargetKey({
      groupId: normalizedGroupId,
      identityId: normalizedIdentityId,
    });
    let current = targetKey
      ? this._activeJoinBundleTargetByGroupIdentity.get(targetKey) || null
      : null;
    if (!current && !normalizedGroupId) {
      for (const [
        candidateKey,
        candidate,
      ] of this._activeJoinBundleTargetByGroupIdentity.entries()) {
        if (!candidate || typeof candidate !== 'object') {
          this._activeJoinBundleTargetByGroupIdentity.delete(candidateKey);
          continue;
        }
        const candidateIdentityId = this.#normalizeIdentityId(candidate.identityId ?? null);
        if (candidateIdentityId !== normalizedIdentityId) {
          continue;
        }
        const candidateExpiresAt = Number(candidate.expiresAt || 0) || 0;
        if (candidateExpiresAt > 0 && candidateExpiresAt <= Date.now()) {
          this._activeJoinBundleTargetByGroupIdentity.delete(candidateKey);
          continue;
        }
        if (!current || Number(candidate.requestedAt || 0) >= Number(current?.requestedAt || 0)) {
          targetKey = candidateKey;
          current = candidate;
        }
      }
    }
    if (!current || !targetKey) {
      return null;
    }
    const expiresAt = Number(current.expiresAt || 0) || 0;
    if (expiresAt > 0 && expiresAt <= Date.now()) {
      this._activeJoinBundleTargetByGroupIdentity.delete(targetKey);
      return null;
    }
    if (!current.targetRef) {
      const keyPackageB64 = pickBase64String(current.keyPackageB64, preferredKeyPackageB64, null);
      if (keyPackageB64) {
        const computedRef = normalizeHexRefValue(
          await computeKeyPackageRefHexFromB64(
            keyPackageB64,
            normalizeSuiteId(ciphersuite ?? current.ciphersuite ?? null) ?? null,
          ).catch(() => null),
        );
        if (computedRef) {
          const updated = {
            ...current,
            keyPackageB64,
            targetRef: computedRef,
            ciphersuite:
              normalizeSuiteId(ciphersuite ?? current.ciphersuite ?? null) ??
              current.ciphersuite ??
              null,
          };
          this._activeJoinBundleTargetByGroupIdentity.set(targetKey, updated);
          return updated;
        }
      }
    }
    return current;
  }

  #clearActiveJoinBundleTargets({ groupId = null, groupHandle = null, identityId = null } = {}) {
    if (!(this._activeJoinBundleTargetByGroupIdentity instanceof Map)) {
      return;
    }
    const normalizedGroupIds = new Set(
      [ensureString(groupId), ensureString(groupHandle)].filter(Boolean),
    );
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    for (const [key, entry] of this._activeJoinBundleTargetByGroupIdentity.entries()) {
      if (!entry || typeof entry !== 'object') {
        this._activeJoinBundleTargetByGroupIdentity.delete(key);
        continue;
      }
      const entryGroupId = ensureString(entry.groupId ?? null);
      const entryIdentityId = this.#normalizeIdentityId(entry.identityId ?? null);
      if (normalizedIdentityId && entryIdentityId && entryIdentityId !== normalizedIdentityId) {
        continue;
      }
      if (normalizedGroupIds.size && (!entryGroupId || !normalizedGroupIds.has(entryGroupId))) {
        continue;
      }
      this._activeJoinBundleTargetByGroupIdentity.delete(key);
    }
  }

  async #findUnresolvedWelcomeTargetForIdentity(identityId) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId);
    if (!normalizedIdentityId) {
      return null;
    }

    const groups = await this.#loadGroups().catch(() => null);
    if (!groups || typeof groups !== 'object') {
      return null;
    }

    await this.#ensureKeyPackagesByRefLoaded().catch(() => null);

    const allowedIdentityIds = new Set(
      [
        normalizedIdentityId,
        this.#normalizeIdentityId(this.identityClientId),
        this.#normalizeIdentityId(this.identityId),
        this.#normalizeIdentityId(
          this.identityRecord?.identityId ?? this.identityRecord?.identity_id ?? null,
        ),
      ].filter(Boolean),
    );

    const persistedProtectedDrift = await this.#resolveProtectedWelcomeTargetDrift({
      identityId: normalizedIdentityId,
    }).catch(() => null);
    const memoryProtectedDrift =
      this._protectedWelcomeTargetDriftByIdentity instanceof Map
        ? this._protectedWelcomeTargetDriftByIdentity.get(normalizedIdentityId) || null
        : null;
    const chooseProtectedDrift = (best, next) => {
      if (!best) return next || null;
      if (!next) return best;
      const bestDetectedAt = coerceTimestampMs(best?.detectedAt ?? null) ?? 0;
      const nextDetectedAt = coerceTimestampMs(next?.detectedAt ?? null) ?? 0;
      return nextDetectedAt >= bestDetectedAt ? next : best;
    };
    const protectedDrift = chooseProtectedDrift(persistedProtectedDrift, memoryProtectedDrift);
    const protectedDriftCurrentLocalRef = normalizeHexRefValue(
      protectedDrift?.currentLocalRef ?? null,
    );
    const protectedDriftTargetRef = normalizeHexRefValue(protectedDrift?.targetRef ?? null);
    const protectedRefToPreserve = protectedDriftTargetRef ?? protectedDriftCurrentLocalRef ?? null;
    if (protectedRefToPreserve) {
      const protectedGroup =
        protectedDrift?.groupId || protectedDrift?.groupHandle
          ? (this.#resolveStoredGroup(groups, {
              groupId: ensureString(protectedDrift?.groupId ?? null),
              groupHandle: ensureString(protectedDrift?.groupHandle ?? null),
            }).entry ?? null)
          : null;
      if (!this.#isGroupReady(protectedGroup)) {
        return {
          identityId: normalizedIdentityId,
          groupId: ensureString(protectedDrift?.groupId ?? protectedGroup?.groupId ?? null) ?? null,
          groupHandle:
            ensureString(protectedDrift?.groupHandle ?? protectedGroup?.groupHandle ?? null) ??
            null,
          conversationId:
            ensureString(
              protectedDrift?.conversationId ??
                protectedGroup?.conversationId ??
                protectedGroup?.conversation_id ??
                null,
            ) ?? null,
          targetRef: protectedRefToPreserve,
          welcomeTargetRef: protectedDriftTargetRef,
          currentLocalRef: protectedDriftCurrentLocalRef,
          epoch: normalizeEpochOrNull(
            protectedGroup?.welcomeTargetEpoch ??
              protectedGroup?.welcome_target_epoch ??
              protectedGroup?.epoch ??
              protectedGroup?.current_epoch ??
              protectedGroup?.currentEpoch ??
              null,
          ),
          fetchedAt:
            coerceTimestampMs(
              protectedDrift?.detectedAt ??
                protectedGroup?.welcomeTargetFetchedAt ??
                protectedGroup?.welcome_target_fetched_at ??
                protectedGroup?.fetchedAt ??
                protectedGroup?.fetched_at ??
                null,
            ) ?? 0,
          source: protectedDriftTargetRef
            ? 'protected_welcome_target_drift'
            : 'protected_welcome_target_drift_current_local_only',
          ciphersuite: normalizeSuiteId(
            protectedDrift?.ciphersuite ?? protectedGroup?.ciphersuite ?? null,
          ),
          keyPackageB64: null,
        };
      }
    }

    let selected = null;

    const registerCandidate = ({
      groupId = null,
      groupHandle = null,
      conversationId = null,
      targetRef = null,
      welcomeId = null,
      epoch = null,
      fetchedAt = null,
      source = null,
      keyPackageB64 = null,
      ciphersuite = null,
    } = {}) => {
      const normalizedRef = normalizeHexRefValue(targetRef);
      if (!normalizedRef) {
        return;
      }

      const trackedEntry = this.keyPackagesByRef.get(normalizedRef) || null;
      if (
        trackedEntry &&
        (trackedEntry.status === 'consumed' || trackedEntry.consumed_by_welcome === true)
      ) {
        return;
      }

      const normalizedEpoch = normalizeEpochOrNull(epoch);
      const normalizedFetchedAt = coerceTimestampMs(fetchedAt ?? null) ?? 0;
      const candidate = {
        identityId: normalizedIdentityId,
        groupId: ensureString(groupId) || null,
        groupHandle: ensureString(groupHandle) || null,
        conversationId: ensureString(conversationId) || null,
        targetRef: normalizedRef,
        welcomeId: ensureString(welcomeId) || null,
        epoch: normalizedEpoch,
        fetchedAt: normalizedFetchedAt,
        source: ensureString(source) || null,
        keyPackageB64: pickBase64String(keyPackageB64, null),
        ciphersuite: normalizeSuiteId(ciphersuite ?? null) ?? null,
      };

      if (!selected) {
        selected = candidate;
        return;
      }

      const selectedEpoch = normalizeEpochOrNull(selected.epoch);
      if (normalizedEpoch != null && (selectedEpoch == null || normalizedEpoch > selectedEpoch)) {
        selected = candidate;
        return;
      }

      if (normalizedEpoch != null && selectedEpoch != null && normalizedEpoch < selectedEpoch) {
        return;
      }

      if (normalizedFetchedAt >= (selected.fetchedAt ?? 0)) {
        selected = candidate;
      }
    };

    const pendingCreateBootstrapAuthority =
      this._conversationCreateBootstrapByIdentity instanceof Map
        ? this._conversationCreateBootstrapByIdentity.get(normalizedIdentityId) || null
        : null;
    if (pendingCreateBootstrapAuthority?.targetRef) {
      registerCandidate({
        groupId: null,
        groupHandle: null,
        conversationId: pendingCreateBootstrapAuthority.conversationId ?? null,
        targetRef: pendingCreateBootstrapAuthority.targetRef,
        fetchedAt: pendingCreateBootstrapAuthority.requestedAt ?? null,
        source: pendingCreateBootstrapAuthority.source ?? 'conversation_create_bootstrap',
        keyPackageB64: pendingCreateBootstrapAuthority.keyPackageB64 ?? null,
        ciphersuite: pendingCreateBootstrapAuthority.ciphersuite ?? null,
      });
    }

    const activeJoinBundleTarget = await this.#getActiveJoinBundleTarget({
      identityId: normalizedIdentityId,
    }).catch(() => null);
    if (activeJoinBundleTarget?.targetRef) {
      registerCandidate({
        groupId: activeJoinBundleTarget.groupId ?? null,
        groupHandle: activeJoinBundleTarget.groupId ?? null,
        conversationId: null,
        targetRef: activeJoinBundleTarget.targetRef,
        fetchedAt: activeJoinBundleTarget.requestedAt ?? null,
        source: activeJoinBundleTarget.source ?? 'active_join_bundle_request',
        keyPackageB64: activeJoinBundleTarget.keyPackageB64 ?? null,
        ciphersuite: activeJoinBundleTarget.ciphersuite ?? null,
      });
    }

    for (const [storedKey, rawEntry] of Object.entries(groups)) {
      const entry = rawEntry && typeof rawEntry === 'object' ? rawEntry : null;
      if (!entry) continue;

      const entryGroupId = ensureString(entry.groupId ?? entry.group_id ?? storedKey);
      const entryGroupHandle = ensureString(entry.groupHandle ?? entry.group_handle ?? null);
      const entryConversationId = ensureString(
        entry.conversationId ?? entry.conversation_id ?? null,
      );
      const pendingWelcomes = getPendingWelcomesList(entry);

      for (const pending of pendingWelcomes) {
        if (!pending || typeof pending !== 'object') continue;
        const targetIdentityId = this.#normalizeIdentityId(
          pending?.to_client_id ??
            pending?.toClientId ??
            pending?.client_id ??
            pending?.clientId ??
            pending?.identity_id ??
            pending?.identityId ??
            null,
        );
        if (targetIdentityId && !allowedIdentityIds.has(targetIdentityId)) {
          continue;
        }

        registerCandidate({
          groupId: entryGroupId,
          groupHandle: entryGroupHandle,
          conversationId: entryConversationId,
          targetRef: this.#resolveWelcomeTargetRef({
            welcomeB64: pending?.welcome_b64 ?? pending?.welcomeB64 ?? pending?.welcome ?? null,
            usedKpRef: pending?.used_kp_ref ?? pending?.usedKpRef ?? null,
          }),
          welcomeId: pending?.welcome_id ?? pending?.welcomeId ?? null,
          epoch: pending?.epoch ?? pending?.current_epoch ?? pending?.currentEpoch ?? null,
          fetchedAt:
            pending?.created_at ??
            pending?.createdAt ??
            pending?.updated_at ??
            pending?.updatedAt ??
            entry?.fetchedAt ??
            entry?.fetched_at ??
            null,
          source: 'pending_welcome',
        });
      }

      const entryIdentityIds = new Set(
        getIdentityCandidates(entry, {
          identityId: normalizedIdentityId,
          identityIds: Array.from(allowedIdentityIds),
        }),
      );
      if (!entryIdentityIds.has(normalizedIdentityId)) {
        continue;
      }

      const hasPersistedWelcome = Boolean(
        pickBase64String(
          entry?.recoveryWelcomeB64,
          entry?.recovery_welcome_b64,
          entry?.welcomeB64,
          entry?.welcome_b64,
          entry?.welcome,
          null,
        ),
      );
      const lockedTargetRef = normalizeHexRefValue(
        entry?.welcomeTargetRefLock ?? entry?.welcome_target_ref_lock ?? null,
      );
      if (this.#isGroupReady(entry)) {
        continue;
      }

      if (hasPersistedWelcome) {
        registerCandidate({
          groupId: entryGroupId,
          groupHandle: entryGroupHandle,
          conversationId: entryConversationId,
          targetRef: this.#resolveWelcomeTargetRef({
            welcomeB64: pickBase64String(
              entry?.recoveryWelcomeB64,
              entry?.recovery_welcome_b64,
              entry?.welcomeB64,
              entry?.welcome_b64,
              entry?.welcome,
              null,
            ),
            usedKpRef: entry?.used_kp_ref ?? entry?.usedKpRef ?? null,
          }),
          epoch: entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
          fetchedAt: entry?.fetchedAt ?? entry?.fetched_at ?? null,
          source: 'persisted_recovery_welcome',
        });
      }

      if (lockedTargetRef) {
        registerCandidate({
          groupId: entryGroupId,
          groupHandle: entryGroupHandle,
          conversationId: entryConversationId,
          targetRef: lockedTargetRef,
          epoch:
            entry?.welcomeTargetEpoch ??
            entry?.welcome_target_epoch ??
            entry?.epoch ??
            entry?.current_epoch ??
            entry?.currentEpoch ??
            null,
          fetchedAt:
            entry?.welcomeTargetFetchedAt ??
            entry?.welcome_target_fetched_at ??
            entry?.fetchedAt ??
            entry?.fetched_at ??
            null,
          source: 'persisted_target_lock',
        });
      }
    }

    if (!selected?.targetRef) {
      return selected;
    }

    const authoritativePublishedRef = normalizeHexRefValue(
      await this.#resolveAuthoritativePublishedKeyPackageRef({
        identityId: normalizedIdentityId,
      }).catch(() => null),
    );
    const selectedHasConcreteWelcome =
      selected?.source === 'pending_welcome' ||
      selected?.source === 'persisted_recovery_welcome' ||
      (selected?.keyPackageB64 &&
        (selected?.source === 'active_join_bundle_request' || selected?.source === 'join_bundle'));
    const selectedHasExplicitAuthority =
      selected?.source === 'conversation_create_bootstrap' ||
      selected?.source === 'persisted_target_lock';
    if (
      !selectedHasConcreteWelcome &&
      !selectedHasExplicitAuthority &&
      authoritativePublishedRef &&
      authoritativePublishedRef !== selected.targetRef
    ) {
      const authoritativeTrackedEntry =
        this.keyPackagesByRef.get(authoritativePublishedRef) || null;
      if (
        authoritativeTrackedEntry &&
        authoritativeTrackedEntry.status !== 'consumed' &&
        authoritativeTrackedEntry.consumed_by_welcome !== true
      ) {
        try {
          console.info(
            '[MLS] Ignoring stale unresolved welcome target behind authoritative key package ref',
            {
              identityId: normalizedIdentityId,
              staleTargetRef: selected.targetRef,
              authoritativePublishedRef,
              groupId: selected.groupId ?? null,
              source: selected.source ?? null,
            },
          );
        } catch {}
        return null;
      }
    }

    return selected;
  }

  async #resolveServerBoundKeyPackage({
    groupId = null,
    identityId = null,
    keyPackageB64 = null,
    ciphersuite = null,
    preferCurrentLocalKeyPackage = false,
    allowConsumedRefresh = false,
    context = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return {
        identityId: null,
        keyPackageB64: null,
        keyPackageRef: null,
        blockedOnTargetMismatch: false,
        source: 'missing_identity',
      };
    }

    const pinnedKeyPackage = await this.#resolvePinnedKeyPackageForWelcomeTarget({
      groupId,
      identityId: normalizedIdentityId,
      ciphersuite,
      preferredKeyPackageB64: keyPackageB64,
      preferCurrentLocalKeyPackage,
    }).catch(() => null);

    if (pinnedKeyPackage?.blockedOnTargetMismatch) {
      return {
        ...pinnedKeyPackage,
        identityId:
          this.#normalizeIdentityId(pinnedKeyPackage?.identityId ?? normalizedIdentityId) ||
          normalizedIdentityId,
      };
    }

    const requestIdentityId =
      this.#normalizeIdentityId(pinnedKeyPackage?.identityId ?? normalizedIdentityId) ||
      normalizedIdentityId;

    if (pinnedKeyPackage?.keyPackageB64) {
      return {
        identityId: requestIdentityId,
        keyPackageB64: pinnedKeyPackage.keyPackageB64,
        keyPackageRef: normalizeHexRefValue(pinnedKeyPackage?.targetRef ?? null),
        blockedOnTargetMismatch: false,
        source: pinnedKeyPackage?.source ?? 'pinned_welcome_target',
      };
    }

    let exported = null;
    try {
      exported = await this.exportKeyPackage(requestIdentityId, {
        refreshIfConsumed: allowConsumedRefresh === true,
      });
    } catch (error) {
      try {
        console.warn('[MLS] Failed to export join-capable key package for server-bound request', {
          groupId: ensureString(groupId) || null,
          identityId: requestIdentityId,
          context: context || null,
          error,
        });
      } catch {}
    }

    const refreshedKeyPackageB64 = exported?.keyPackage ?? null;
    const resolvedKeyPackageB64 = refreshedKeyPackageB64 ?? keyPackageB64 ?? null;
    const exportedIdentityId = this.#normalizeIdentityId(exported?.identityId ?? null);
    if (exportedIdentityId && exportedIdentityId !== requestIdentityId) {
      try {
        console.warn(
          '[MLS] Ignoring mismatched identity returned by server-bound key package export',
          {
            requestedIdentityId: requestIdentityId,
            exportedIdentityId,
            groupId: ensureString(groupId) || null,
            context: context || null,
          },
        );
      } catch {}
    }
    const resolvedIdentityId = requestIdentityId;
    const resolvedKeyPackageRef = resolvedKeyPackageB64
      ? await computeKeyPackageRefHexFromB64(
          resolvedKeyPackageB64,
          ciphersuite ?? this.preferredCiphersuite ?? null,
        ).catch(() => null)
      : null;

    return {
      identityId: resolvedIdentityId,
      keyPackageB64: resolvedKeyPackageB64,
      keyPackageRef: normalizeHexRefValue(resolvedKeyPackageRef),
      blockedOnTargetMismatch: false,
      source:
        refreshedKeyPackageB64 && allowConsumedRefresh === true
          ? 'refresh_if_consumed'
          : refreshedKeyPackageB64
            ? 'local_export'
            : 'preferred_key_package_fallback',
    };
  }

  async #resolveBootstrapPublishDisposition({
    identityId = null,
    groupId = null,
    groupHandle = null,
    conversationId = null,
    ciphersuite = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return {
        allowPublish: true,
        reason: 'missing_identity',
      };
    }

    const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
      normalizedIdentityId,
    ).catch(() => null);
    if (unresolvedWelcomeTarget?.targetRef) {
      return {
        allowPublish: false,
        reason: 'welcome_target_locked',
        identityId: normalizedIdentityId,
        groupId:
          ensureString(groupId ?? unresolvedWelcomeTarget.groupId ?? null) ??
          ensureString(unresolvedWelcomeTarget.groupId ?? null) ??
          null,
        groupHandle:
          ensureString(groupHandle ?? unresolvedWelcomeTarget.groupHandle ?? null) ??
          ensureString(unresolvedWelcomeTarget.groupHandle ?? null) ??
          null,
        conversationId:
          ensureString(conversationId ?? unresolvedWelcomeTarget.conversationId ?? null) ??
          ensureString(unresolvedWelcomeTarget.conversationId ?? null) ??
          null,
        targetRef: unresolvedWelcomeTarget.targetRef ?? null,
        source: unresolvedWelcomeTarget.source ?? null,
      };
    }

    let exported = null;
    try {
      exported = await this.exportKeyPackage(normalizedIdentityId, {
        refreshIfConsumed: false,
      });
    } catch {
      exported = null;
    }

    const localKeyPackageB64 = exported?.keyPackage ?? null;
    const localKeyPackageRef = localKeyPackageB64
      ? await computeKeyPackageRefHexFromB64(localKeyPackageB64, ciphersuite ?? null).catch(
          () => null,
        )
      : null;

    if (localKeyPackageB64) {
      return {
        allowPublish: false,
        reason: 'local_key_package_present',
        identityId: normalizedIdentityId,
        groupId: ensureString(groupId ?? null) ?? null,
        groupHandle: ensureString(groupHandle ?? null) ?? null,
        conversationId: ensureString(conversationId ?? null) ?? null,
        targetRef: localKeyPackageRef ?? null,
        source: 'local_export',
      };
    }

    return {
      allowPublish: true,
      reason: 'missing_local_key_package',
      identityId: normalizedIdentityId,
      groupId: ensureString(groupId ?? null) ?? null,
      groupHandle: ensureString(groupHandle ?? null) ?? null,
      conversationId: ensureString(conversationId ?? null) ?? null,
      source: 'local_export',
    };
  }

  async #findActiveBootstrapConvergenceForIdentity(identityId) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }

    const groups = await this.#loadGroups().catch(() => null);
    if (!groups || typeof groups !== 'object') {
      return null;
    }

    let selected = null;
    const chooseCandidate = (best, next) => {
      if (!next?.groupId && !next?.groupHandle) {
        return best;
      }
      if (!best?.groupId && !best?.groupHandle) {
        return next;
      }
      const nextEpoch = normalizeEpochOrNull(next?.epoch);
      const bestEpoch = normalizeEpochOrNull(best?.epoch);
      if (nextEpoch != null && (bestEpoch == null || nextEpoch > bestEpoch)) {
        return next;
      }
      if (nextEpoch == null && bestEpoch != null) {
        return best;
      }
      return (next?.fetchedAt ?? 0) >= (best?.fetchedAt ?? 0) ? next : best;
    };

    for (const [storedKey, rawEntry] of Object.entries(groups)) {
      const entry = rawEntry && typeof rawEntry === 'object' ? rawEntry : null;
      if (!entry || this.#isGroupReady(entry)) {
        continue;
      }

      const entryIdentityIds = new Set(
        getIdentityCandidates(entry, {
          identityId: normalizedIdentityId,
          identityIds: [normalizedIdentityId],
        }),
      );
      if (!entryIdentityIds.has(normalizedIdentityId)) {
        continue;
      }

      const pendingWelcomes = getPendingWelcomesList(entry);
      const lockedTargetRef = normalizeHexRefValue(
        entry?.welcomeTargetRefLock ?? entry?.welcome_target_ref_lock ?? null,
      );
      const hasPersistedWelcome = Boolean(
        pickBase64String(
          entry?.recoveryWelcomeB64,
          entry?.recovery_welcome_b64,
          entry?.welcomeB64,
          entry?.welcome_b64,
          entry?.welcome,
          null,
        ),
      );
      const bootstrapWindowUntil =
        coerceTimestampMs(entry?.bootstrapWindowUntil ?? entry?.bootstrap_window_until ?? null) ??
        0;
      const syncStatus =
        ensureString(entry?.syncStatus ?? entry?.sync_status ?? null)?.toLowerCase() || null;
      const runtimeState =
        ensureString(entry?.runtimeState ?? entry?.runtime_state ?? null)?.toLowerCase() || null;

      const convergenceActive =
        pendingWelcomes.length > 0 ||
        hasPersistedWelcome ||
        Boolean(lockedTargetRef) ||
        Boolean(entry?.needsRecovery) ||
        bootstrapWindowUntil > Date.now() ||
        syncStatus === 'bootstrapping' ||
        syncStatus === 'recovering' ||
        runtimeState === 'runtime_missing' ||
        runtimeState === 'recovery_required';

      if (!convergenceActive) {
        continue;
      }

      selected = chooseCandidate(selected, {
        identityId: normalizedIdentityId,
        groupId: ensureString(entry?.groupId ?? entry?.group_id ?? storedKey) || null,
        groupHandle: ensureString(entry?.groupHandle ?? entry?.group_handle ?? null) || null,
        conversationId:
          ensureString(entry?.conversationId ?? entry?.conversation_id ?? null) || null,
        targetRef: lockedTargetRef,
        pendingWelcomeCount: pendingWelcomes.length,
        hasPersistedWelcome,
        needsRecovery: Boolean(entry?.needsRecovery),
        syncStatus: syncStatus ?? null,
        runtimeState: runtimeState ?? null,
        epoch: entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
        fetchedAt:
          coerceTimestampMs(entry?.fetchedAt ?? entry?.fetched_at ?? null) ??
          coerceTimestampMs(entry?.updatedAt ?? entry?.updated_at ?? null) ??
          0,
      });
    }

    return selected;
  }

  async getBootstrapPublishDisposition({
    identityId = null,
    clientId = null,
    groupId = null,
    groupHandle = null,
    conversationId = null,
    ciphersuite = null,
  } = {}) {
    const ensuredIdentity = await this.ensureIdentity({ ciphersuite }).catch(() => null);
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ??
        ensuredIdentity?.identityId ??
        this.identityRecord?.identityId ??
        this.identityRecord?.identity_id ??
        this.identityId ??
        null,
    );
    const normalizedClientId = this.#normalizeIdentityId(
      clientId ??
        ensuredIdentity?.clientId ??
        ensuredIdentity?.client_id ??
        this.identityClientId ??
        this.identityRecord?.clientId ??
        this.identityRecord?.client_id ??
        null,
    );
    const normalizedSuite = normalizeSuiteId(
      ciphersuite ?? ensuredIdentity?.ciphersuite ?? this.preferredCiphersuite ?? null,
    );

    const localDisposition = await this.#resolveBootstrapPublishDisposition({
      identityId: normalizedIdentityId,
      groupId,
      groupHandle,
      conversationId,
      ciphersuite: normalizedSuite,
    });
    if (localDisposition?.reason === 'welcome_target_locked') {
      return {
        ...localDisposition,
        clientId: normalizedClientId ?? localDisposition?.clientId ?? null,
        ciphersuite: normalizedSuite ?? localDisposition?.ciphersuite ?? null,
      };
    }

    let serverKeyPackage = null;
    if (normalizedClientId && normalizedSuite != null) {
      try {
        serverKeyPackage = await fetchLatestMlsKeyPackage({
          clientId: normalizedClientId,
          ciphersuite: normalizedSuite,
        });
      } catch (error) {
        if (typeof console?.warn === 'function') {
          console.warn('[MLS] Failed to inspect server key package during bootstrap disposition', {
            identityId: normalizedIdentityId ?? null,
            clientId: normalizedClientId,
            ciphersuite: normalizedSuite,
            error,
          });
        }
      }
    }

    if (serverKeyPackage) {
      let serverKeyPackageRef = normalizeHexRefValue(
        serverKeyPackage?.key_package_ref ??
          serverKeyPackage?.keyPackageRef ??
          serverKeyPackage?.kp_ref ??
          serverKeyPackage?.ref ??
          serverKeyPackage?.key_package?.key_package_ref ??
          serverKeyPackage?.keyPackage?.key_package_ref ??
          null,
      );
      if (!serverKeyPackageRef) {
        const serverKeyPackageB64 = pickBase64String(
          serverKeyPackage?.key_package_b64,
          serverKeyPackage?.keyPackageB64,
          serverKeyPackage?.key_package?.key_package_b64,
          serverKeyPackage?.keyPackage?.key_package_b64,
          null,
        );
        if (serverKeyPackageB64) {
          serverKeyPackageRef = await computeKeyPackageRefHexFromB64(
            serverKeyPackageB64,
            normalizedSuite,
          ).catch(() => null);
          serverKeyPackageRef = normalizeHexRefValue(serverKeyPackageRef);
        }
      }

      if (
        localDisposition?.reason === 'local_key_package_present' &&
        localDisposition?.targetRef &&
        (!serverKeyPackageRef || serverKeyPackageRef !== localDisposition.targetRef)
      ) {
        return {
          allowPublish: true,
          reason: serverKeyPackageRef
            ? 'server_key_package_mismatch'
            : 'server_key_package_missing_ref',
          identityId: normalizedIdentityId,
          clientId: normalizedClientId,
          ciphersuite: normalizedSuite,
          groupId: ensureString(groupId ?? null) ?? null,
          groupHandle: ensureString(groupHandle ?? null) ?? null,
          conversationId: ensureString(conversationId ?? null) ?? null,
          targetRef: localDisposition.targetRef ?? null,
          authoritativeTargetRef: serverKeyPackageRef ?? null,
          source: 'local_export',
        };
      }

      return {
        allowPublish: false,
        reason: 'server_key_package_present',
        identityId: normalizedIdentityId,
        clientId: normalizedClientId,
        ciphersuite: normalizedSuite,
        groupId: ensureString(groupId ?? null) ?? null,
        groupHandle: ensureString(groupHandle ?? null) ?? null,
        conversationId: ensureString(conversationId ?? null) ?? null,
        source: 'server_latest',
      };
    }

    if (localDisposition?.reason === 'local_key_package_present') {
      return {
        allowPublish: true,
        reason: 'server_key_package_missing',
        identityId: normalizedIdentityId,
        clientId: normalizedClientId,
        ciphersuite: normalizedSuite,
        groupId: ensureString(groupId ?? null) ?? null,
        groupHandle: ensureString(groupHandle ?? null) ?? null,
        conversationId: ensureString(conversationId ?? null) ?? null,
        targetRef: localDisposition?.targetRef ?? null,
        source: 'local_export',
      };
    }

    return {
      ...localDisposition,
      identityId: normalizedIdentityId,
      clientId: normalizedClientId,
      ciphersuite: normalizedSuite,
    };
  }

  async registerConversationCreateBootstrapAuthority({
    identityId = null,
    clientId = null,
    keyPackageB64 = null,
    keyPackageRef = null,
    ciphersuite = null,
    conversationId = null,
    source = 'conversation_create_bootstrap',
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ??
        this.identityClientId ??
        this.identityRecord?.identityId ??
        this.identityId ??
        null,
    );
    if (!normalizedIdentityId) {
      return null;
    }

    const normalizedSuite = normalizeSuiteId(ciphersuite ?? this.preferredCiphersuite ?? null);
    const normalizedKeyPackageB64 = pickBase64String(keyPackageB64, null);
    let normalizedTargetRef = normalizeHexRefValue(keyPackageRef ?? null);
    if (!normalizedTargetRef && normalizedKeyPackageB64) {
      normalizedTargetRef = normalizeHexRefValue(
        await computeKeyPackageRefHexFromB64(normalizedKeyPackageB64, normalizedSuite).catch(
          () => null,
        ),
      );
    }
    if (!normalizedTargetRef) {
      return null;
    }

    const entry = {
      identityId: normalizedIdentityId,
      clientId:
        this.#normalizeIdentityId(clientId ?? null) ??
        this.#normalizeIdentityId(this.identityClientId ?? null) ??
        normalizedIdentityId,
      conversationId: ensureString(conversationId ?? null) ?? null,
      targetRef: normalizedTargetRef,
      keyPackageB64: normalizedKeyPackageB64,
      ciphersuite: normalizedSuite ?? null,
      source: ensureString(source ?? null) ?? 'conversation_create_bootstrap',
      requestedAt: Date.now(),
    };
    this._conversationCreateBootstrapByIdentity.set(normalizedIdentityId, entry);
    return entry;
  }

  clearConversationCreateBootstrapAuthority({ identityId = null } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ??
        this.identityClientId ??
        this.identityRecord?.identityId ??
        this.identityId ??
        null,
    );
    if (!normalizedIdentityId) {
      return false;
    }
    return this._conversationCreateBootstrapByIdentity.delete(normalizedIdentityId);
  }

  async adoptConversationCreateBootstrapAuthority({
    identityId = null,
    groupId = null,
    groupHandle = null,
    conversationId = null,
    ciphersuite = null,
    source = 'conversation_create_response',
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ??
        this.identityClientId ??
        this.identityRecord?.identityId ??
        this.identityId ??
        null,
    );
    const normalizedGroupId = ensureString(groupId ?? null);
    if (!normalizedIdentityId || !normalizedGroupId) {
      return null;
    }

    const authority = this._conversationCreateBootstrapByIdentity.get(normalizedIdentityId) || null;
    if (!authority?.targetRef) {
      return null;
    }

    const normalizedConversationId = ensureString(
      conversationId ?? authority?.conversationId ?? null,
    );
    const normalizedGroupHandle = ensureString(groupHandle ?? null);
    const normalizedSuite = normalizeSuiteId(
      ciphersuite ?? authority?.ciphersuite ?? this.preferredCiphersuite ?? null,
    );

    const updated = await this.#updateGroupEntry(normalizedGroupId, (prev) => ({
      ...(prev || {}),
      groupId: ensureString(prev?.groupId ?? normalizedGroupId) ?? normalizedGroupId,
      groupHandle:
        ensureString(prev?.groupHandle ?? normalizedGroupHandle ?? null) ??
        normalizedGroupHandle ??
        null,
      conversationId:
        ensureString(prev?.conversationId ?? prev?.conversation_id ?? normalizedConversationId) ??
        normalizedConversationId ??
        null,
      identityId:
        normalizedIdentityId ??
        this.#normalizeIdentityId(prev?.identityId ?? prev?.identity_id ?? null) ??
        null,
      ciphersuite: normalizeSuiteId(prev?.ciphersuite ?? null) ?? normalizedSuite ?? null,
      welcomeTargetRefLock: authority.targetRef,
      welcome_target_ref_lock: authority.targetRef,
      welcomeTargetIdentityId: normalizedIdentityId,
      welcome_target_identity_id: normalizedIdentityId,
      welcomeTargetEpoch:
        normalizeEpochOrNull(
          prev?.welcomeTargetEpoch ??
            prev?.welcome_target_epoch ??
            prev?.epoch ??
            prev?.current_epoch ??
            prev?.currentEpoch ??
            null,
        ) ?? null,
      welcome_target_epoch:
        normalizeEpochOrNull(
          prev?.welcome_target_epoch ??
            prev?.welcomeTargetEpoch ??
            prev?.epoch ??
            prev?.current_epoch ??
            prev?.currentEpoch ??
            null,
        ) ?? null,
      welcomeTargetFetchedAt: Date.now(),
      welcome_target_fetched_at: Date.now(),
    })).catch(() => null);

    await this.#markBootstrapConvergence(normalizedGroupId, {
      reason: `${source || 'conversation_create_response'}:bootstrap_authority`,
    }).catch(() => null);

    this._conversationCreateBootstrapByIdentity.delete(normalizedIdentityId);
    return updated;
  }

  async prepareConversationCreateBootstrap({
    identityId = null,
    clientId = null,
    ciphersuite = null,
    conversationId = null,
    forcePublish = false,
    source = 'conversation_create_bootstrap',
  } = {}) {
    const ensuredIdentity = await this.ensureIdentity({ ciphersuite }).catch(() => null);
    const normalizedIdentityId = this.#normalizeIdentityId(
      identityId ??
        ensuredIdentity?.identityId ??
        this.identityRecord?.identityId ??
        this.identityId ??
        null,
    );
    if (!normalizedIdentityId) {
      throw new Error('MLS identity unavailable for bootstrap preparation');
    }

    const normalizedSuite = normalizeSuiteId(
      ciphersuite ?? ensuredIdentity?.ciphersuite ?? this.preferredCiphersuite ?? null,
    );
    const normalizedClientId = this.#normalizeIdentityId(
      clientId ??
        ensuredIdentity?.clientId ??
        ensuredIdentity?.client_id ??
        this.identityClientId ??
        null,
    );

    const publishDisposition = await this.getBootstrapPublishDisposition({
      identityId: normalizedIdentityId,
      clientId: normalizedClientId,
      conversationId,
      ciphersuite: normalizedSuite,
    }).catch(() => null);

    const publishResult =
      publishDisposition?.allowPublish === false
        ? publishDisposition
        : await this.publishKeyPackageAndSync(normalizedIdentityId, {
            forcePublish,
            retryOnMissingClientId: true,
          });

    const exported = (await this.exportKeyPackage(normalizedIdentityId).catch(() => null)) || null;
    const keyPackageB64 = pickBase64String(
      exported?.keyPackage,
      ensuredIdentity?.keyPackage,
      ensuredIdentity?.key_package_b64,
      null,
    );
    const keyPackageRef =
      normalizeHexRefValue(
        publishResult?.targetRef ??
          publishDisposition?.targetRef ??
          (await computeKeyPackageRefHexFromB64(keyPackageB64, normalizedSuite).catch(
            () => null,
          )) ??
          null,
      ) ?? null;

    const authority = await this.registerConversationCreateBootstrapAuthority({
      identityId: normalizedIdentityId,
      clientId:
        this.#normalizeIdentityId(
          publishResult?.clientId ?? publishResult?.client_id ?? normalizedClientId,
        ) ?? normalizedIdentityId,
      keyPackageB64,
      keyPackageRef,
      ciphersuite: normalizedSuite,
      conversationId,
      source,
    });

    return {
      identity:
        ensuredIdentity ||
        this.identityRecord ||
        (normalizedIdentityId ? { identityId: normalizedIdentityId } : null),
      identityId: normalizedIdentityId,
      clientId:
        this.#normalizeIdentityId(
          publishResult?.clientId ??
            publishResult?.client_id ??
            this.identityClientId ??
            normalizedClientId,
        ) ?? normalizedIdentityId,
      ciphersuite: normalizedSuite ?? null,
      keyPackageB64,
      keyPackageRef: authority?.targetRef ?? keyPackageRef ?? null,
      authority: authority || null,
      publishResult: publishResult || null,
    };
  }

  async #resolvePinnedKeyPackageForWelcomeTarget({
    identityId = null,
    groupId = null,
    ciphersuite = null,
    preferredKeyPackageB64 = null,
    preferCurrentLocalKeyPackage = false,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }

    let unresolvedTarget = null;
    try {
      unresolvedTarget = await this.#findUnresolvedWelcomeTargetForIdentity(normalizedIdentityId);
    } catch {
      unresolvedTarget = null;
    }
    if (!unresolvedTarget?.targetRef) {
      return null;
    }

    const normalizedGroupId = ensureString(groupId);
    const unresolvedGroupId = ensureString(
      unresolvedTarget?.groupId ?? unresolvedTarget?.groupHandle ?? null,
    );
    if (normalizedGroupId && unresolvedGroupId && normalizedGroupId !== unresolvedGroupId) {
      return null;
    }

    const normalizedTargetRef = normalizeHexRefValue(unresolvedTarget.targetRef);
    if (!normalizedTargetRef) {
      return null;
    }
    const normalizedCurrentLocalRef = normalizeHexRefValue(
      unresolvedTarget.currentLocalRef ?? null,
    );
    let preferredCurrentRef = null;
    if (preferCurrentLocalKeyPackage && preferredKeyPackageB64) {
      preferredCurrentRef = normalizeHexRefValue(
        await computeKeyPackageRefHexFromB64(
          preferredKeyPackageB64,
          normalizeSuiteId(ciphersuite ?? null),
        ).catch(() => null),
      );
    }
    const shouldPreferCurrentLocalKeyPackage =
      preferCurrentLocalKeyPackage && Boolean(preferredCurrentRef || normalizedCurrentLocalRef);
    const desiredPinnedRef = shouldPreferCurrentLocalKeyPackage
      ? preferredCurrentRef || normalizedCurrentLocalRef
      : normalizedTargetRef;
    if (!desiredPinnedRef) {
      return null;
    }

    await this.#ensureKeyPackagesByRefLoaded();
    const trackedEntry = this.keyPackagesByRef.get(desiredPinnedRef) || null;
    const requestedSuite = normalizeSuiteId(ciphersuite ?? null);
    const trackedSuite = normalizeSuiteId(trackedEntry?.ciphersuite ?? null);
    if (
      trackedEntry &&
      (trackedEntry.status === 'consumed' || trackedEntry.consumed_by_welcome === true)
    ) {
      return null;
    }
    if (requestedSuite != null && trackedSuite != null && requestedSuite !== trackedSuite) {
      return null;
    }

    let keyPackageB64 = pickBase64String(
      unresolvedTarget?.keyPackageB64,
      trackedEntry?.key_package_b64,
      null,
    );
    if (!keyPackageB64 && preferredKeyPackageB64) {
      try {
        const preferredRef = await computeKeyPackageRefHexFromB64(
          preferredKeyPackageB64,
          requestedSuite ?? trackedSuite ?? null,
        );
        if (normalizeHexRefValue(preferredRef) === desiredPinnedRef) {
          keyPackageB64 = preferredKeyPackageB64;
        }
      } catch {}
    }
    const blockedOnTargetMismatch =
      Boolean(normalizedTargetRef) &&
      Boolean(normalizedCurrentLocalRef) &&
      normalizedCurrentLocalRef !== normalizedTargetRef &&
      !keyPackageB64;

    return {
      identityId:
        this.#normalizeIdentityId(
          trackedEntry?.identity_id ??
            trackedEntry?.client_id ??
            unresolvedTarget?.identityId ??
            normalizedIdentityId,
        ) ?? normalizedIdentityId,
      groupId: unresolvedTarget?.groupId ?? null,
      groupHandle: unresolvedTarget?.groupHandle ?? null,
      targetRef: desiredPinnedRef,
      welcomeTargetRef: normalizedTargetRef,
      currentLocalRef: normalizedCurrentLocalRef,
      keyPackageB64: keyPackageB64 ?? null,
      blockedOnTargetMismatch,
      source: unresolvedTarget?.source ?? 'welcome_target_lock',
    };
  }

  async #resolveWelcomeTargetValidation({
    identityId = null,
    groupId = null,
    welcomeB64 = null,
    ciphersuite = null,
    currentLocalKeyPackageB64 = null,
    currentLocalRef = null,
  } = {}) {
    const welcomeRefs = extractWelcomeRefHexesFromB64(welcomeB64);
    const normalizedCurrentLocalRef = normalizeHexRefValue(currentLocalRef);
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    const normalizedGroupId = ensureString(groupId) || null;

    let directPinnedTargetRef = null;
    let directPinnedSource = null;
    if (normalizedIdentityId) {
      const createBootstrapAuthority =
        this._conversationCreateBootstrapByIdentity instanceof Map
          ? this._conversationCreateBootstrapByIdentity.get(normalizedIdentityId) || null
          : null;
      const createBootstrapRef = normalizeHexRefValue(createBootstrapAuthority?.targetRef ?? null);
      if (createBootstrapRef) {
        directPinnedTargetRef = createBootstrapRef;
        directPinnedSource =
          ensureString(createBootstrapAuthority?.source ?? null) || 'conversation_create_bootstrap';
      }
    }
    if (normalizedGroupId) {
      try {
        const groups = (await this.#loadGroups().catch(() => null)) || {};
        const storedEntry =
          this.#resolveStoredGroup(groups, { groupId: normalizedGroupId })?.entry ??
          groups[normalizedGroupId] ??
          null;
        const storedLockedRef = normalizeHexRefValue(
          storedEntry?.welcomeTargetRefLock ?? storedEntry?.welcome_target_ref_lock ?? null,
        );
        if (storedLockedRef) {
          directPinnedTargetRef = storedLockedRef;
          directPinnedSource = 'persisted_target_lock';
        }
      } catch {}
    }
    if (!normalizedIdentityId) {
      return {
        welcomeRefs,
        currentLocalRef: normalizedCurrentLocalRef,
        currentLocalMatches: Boolean(
          normalizedCurrentLocalRef && welcomeRefs.includes(normalizedCurrentLocalRef),
        ),
        pinnedTargetRef: directPinnedTargetRef,
        pinnedTargetMatches: Boolean(
          directPinnedTargetRef && welcomeRefs.includes(directPinnedTargetRef),
        ),
        pinnedSource: directPinnedSource,
      };
    }

    const pinnedKeyPackage = await this.#resolvePinnedKeyPackageForWelcomeTarget({
      identityId: normalizedIdentityId,
      groupId,
      ciphersuite,
      preferredKeyPackageB64: currentLocalKeyPackageB64,
      preferCurrentLocalKeyPackage: false,
    }).catch(() => null);
    const pinnedTargetRef = normalizeHexRefValue(
      pinnedKeyPackage?.targetRef ?? pinnedKeyPackage?.welcomeTargetRef ?? null,
    );
    const effectivePinnedTargetRef = pinnedTargetRef ?? directPinnedTargetRef ?? null;

    return {
      welcomeRefs,
      currentLocalRef: normalizedCurrentLocalRef,
      currentLocalMatches: Boolean(
        normalizedCurrentLocalRef && welcomeRefs.includes(normalizedCurrentLocalRef),
      ),
      pinnedTargetRef: effectivePinnedTargetRef,
      pinnedTargetMatches: Boolean(
        effectivePinnedTargetRef && welcomeRefs.includes(effectivePinnedTargetRef),
      ),
      pinnedSource: ensureString(pinnedKeyPackage?.source ?? null) || directPinnedSource || null,
    };
  }

  async #persistWelcomeTarget({
    groupId,
    groupHandle = null,
    conversationId = null,
    identityId = null,
    welcomeB64 = null,
    ratchetTreeB64 = null,
    epoch = null,
    usedKpRef = null,
    source = null,
  } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedWelcomeB64 = pickBase64String(welcomeB64, null);
    if (!normalizedGroupId || !normalizedWelcomeB64) {
      return null;
    }

    const normalizedGroupHandle = ensureString(groupHandle);
    const normalizedConversationId = ensureString(conversationId);
    const normalizedIdentityId = this.#normalizeIdentityId(identityId);
    const normalizedRatchetTreeB64 = pickBase64String(ratchetTreeB64, null);
    const normalizedEpoch = normalizeEpochOrNull(epoch);
    const targetRef = this.#resolveWelcomeTargetRef({
      welcomeB64: normalizedWelcomeB64,
      usedKpRef,
    });
    const stalePendingAckMetaByGroup = new Map();
    const queueStalePendingAck = (entry) => {
      const staleWelcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
      if (!staleWelcomeId) {
        return;
      }
      const ackGroupId =
        ensureString(entry?.group_id ?? entry?.groupId ?? normalizedGroupId ?? null) ??
        normalizedGroupId;
      if (!ackGroupId) {
        return;
      }
      const meta = stalePendingAckMetaByGroup.get(ackGroupId) || {
        ids: new Set(),
        conversations: new Set(),
        clientIds: new Map(),
      };
      meta.ids.add(staleWelcomeId);
      const ackConversationId = ensureString(
        entry?.conversation_id ?? entry?.conversationId ?? normalizedConversationId ?? null,
      );
      if (ackConversationId) {
        meta.conversations.add(ackConversationId);
      }
      const ackClientId = ensureString(
        entry?.to_client_id ??
          entry?.toClientId ??
          entry?.client_id ??
          entry?.clientId ??
          entry?.identity_id ??
          entry?.identityId ??
          normalizedIdentityId ??
          null,
      );
      const clientKey = ackClientId ? String(ackClientId) : '';
      const clientSet = meta.clientIds.get(clientKey) || new Set();
      clientSet.add(staleWelcomeId);
      meta.clientIds.set(clientKey, clientSet);
      stalePendingAckMetaByGroup.set(ackGroupId, meta);
    };
    this.#clearActiveJoinBundleTargets({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
      identityId: normalizedIdentityId ?? null,
    });

    const persisted = await this.#updateGroupEntry(normalizedGroupId, (prev) => {
      const existing = prev && typeof prev === 'object' ? prev : {};
      const retainedPendingWelcomes = [];
      getPendingWelcomesList(existing).forEach((entry) => {
        const isStale = this.#isPendingWelcomeStaleAgainstAuthoritativeTarget({
          pendingWelcome: entry,
          authoritativeEpoch: normalizedEpoch,
          authoritativeTargetRef: targetRef,
        });
        if (isStale) {
          queueStalePendingAck(entry);
          return;
        }
        retainedPendingWelcomes.push(entry);
      });
      const pendingHandshakeQueue = buildPendingHandshakeQueue(
        retainedPendingWelcomes,
        normalizedGroupId,
      );
      return {
        ...existing,
        groupId: ensureString(existing?.groupId ?? normalizedGroupId) ?? normalizedGroupId,
        groupHandle:
          ensureString(existing?.groupHandle ?? normalizedGroupHandle ?? null) ??
          normalizedGroupHandle ??
          null,
        conversationId:
          ensureString(existing?.conversationId ?? existing?.conversation_id ?? null) ??
          normalizedConversationId ??
          null,
        identityId: normalizedIdentityId ?? existing?.identityId ?? existing?.identity_id ?? null,
        welcomeB64: normalizedWelcomeB64,
        recoveryWelcomeB64: normalizedWelcomeB64,
        ratchetTree:
          normalizedRatchetTreeB64 ??
          existing?.ratchetTreeB64 ??
          existing?.ratchet_tree_b64 ??
          existing?.ratchetTree ??
          null,
        ratchetTreeB64:
          normalizedRatchetTreeB64 ??
          existing?.ratchetTreeB64 ??
          existing?.ratchet_tree_b64 ??
          existing?.ratchetTree ??
          null,
        epoch:
          normalizedEpoch ??
          normalizeEpochOrNull(
            existing?.epoch ?? existing?.current_epoch ?? existing?.currentEpoch ?? null,
          ) ??
          0,
        current_epoch:
          normalizedEpoch ??
          normalizeEpochOrNull(
            existing?.epoch ?? existing?.current_epoch ?? existing?.currentEpoch ?? null,
          ) ??
          0,
        currentEpoch:
          normalizedEpoch ??
          normalizeEpochOrNull(
            existing?.epoch ?? existing?.current_epoch ?? existing?.currentEpoch ?? null,
          ) ??
          0,
        fetchedAt: Date.now(),
        used_kp_ref: targetRef ?? existing?.used_kp_ref ?? existing?.usedKpRef ?? null,
        usedKpRef: targetRef ?? existing?.usedKpRef ?? existing?.used_kp_ref ?? null,
        welcomeTargetRefLock:
          targetRef ??
          normalizeHexRefValue(
            existing?.welcomeTargetRefLock ?? existing?.welcome_target_ref_lock ?? null,
          ) ??
          null,
        welcome_target_ref_lock:
          targetRef ??
          normalizeHexRefValue(
            existing?.welcome_target_ref_lock ?? existing?.welcomeTargetRefLock ?? null,
          ) ??
          null,
        welcomeTargetIdentityId:
          normalizedIdentityId ??
          this.#normalizeIdentityId(
            existing?.welcomeTargetIdentityId ?? existing?.welcome_target_identity_id ?? null,
          ) ??
          null,
        welcome_target_identity_id:
          normalizedIdentityId ??
          this.#normalizeIdentityId(
            existing?.welcome_target_identity_id ?? existing?.welcomeTargetIdentityId ?? null,
          ) ??
          null,
        welcomeTargetEpoch:
          normalizedEpoch ??
          normalizeEpochOrNull(
            existing?.welcomeTargetEpoch ?? existing?.welcome_target_epoch ?? null,
          ) ??
          null,
        welcome_target_epoch:
          normalizedEpoch ??
          normalizeEpochOrNull(
            existing?.welcome_target_epoch ?? existing?.welcomeTargetEpoch ?? null,
          ) ??
          null,
        welcomeTargetFetchedAt: Date.now(),
        welcome_target_fetched_at: Date.now(),
        pending_welcomes: retainedPendingWelcomes,
        pendingWelcomes: retainedPendingWelcomes,
        pending_handshake_queue: pendingHandshakeQueue,
        pendingHandshakeQueue: pendingHandshakeQueue,
        lastWelcomeTargetSource:
          ensureString(source) ?? ensureString(existing?.lastWelcomeTargetSource ?? null) ?? null,
      };
    }).catch(() => null);

    if (stalePendingAckMetaByGroup.size) {
      for (const [ackGroupId, meta] of stalePendingAckMetaByGroup.entries()) {
        const conversations = Array.from(meta.conversations || []);
        const clientMap = meta.clientIds instanceof Map ? meta.clientIds : new Map();
        if (clientMap.size) {
          for (const [clientKey, idSet] of clientMap.entries()) {
            const ids = Array.from(idSet || []);
            if (!ids.length) {
              continue;
            }
            try {
              await this.acknowledgeWelcomes({
                groupId: ackGroupId,
                welcomeIds: ids,
                conversationIds: conversations,
                clientId: clientKey || null,
              });
            } catch (error) {
              console.warn(
                '[MLS] Failed to acknowledge stale pending welcomes after persisting authoritative welcome target',
                {
                  groupId: ackGroupId,
                  conversationIds: conversations,
                  clientId: clientKey || null,
                  welcomeIds: ids,
                  error,
                },
              );
            }
          }
          continue;
        }
        const ids = Array.from(meta.ids || []);
        if (!ids.length) {
          continue;
        }
        try {
          await this.acknowledgeWelcomes({
            groupId: ackGroupId,
            welcomeIds: ids,
            conversationIds: conversations,
            clientId: null,
          });
        } catch (error) {
          console.warn(
            '[MLS] Failed to acknowledge stale pending welcomes after persisting authoritative welcome target',
            {
              groupId: ackGroupId,
              conversationIds: conversations,
              clientId: null,
              welcomeIds: ids,
              error,
            },
          );
        }
      }
    }

    return persisted;
  }

  #isPendingWelcomeStaleAgainstAuthoritativeTarget({
    pendingWelcome,
    authoritativeEpoch = null,
    authoritativeTargetRef = null,
  } = {}) {
    if (!pendingWelcome || typeof pendingWelcome !== 'object') {
      return false;
    }

    const normalizedAuthoritativeEpoch = normalizeEpochOrNull(authoritativeEpoch);
    const normalizedAuthoritativeRef = normalizeHexRefValue(authoritativeTargetRef ?? null);
    if (normalizedAuthoritativeEpoch == null && !normalizedAuthoritativeRef) {
      return false;
    }

    const pendingEpoch = normalizeEpochOrNull(
      pendingWelcome?.epoch ??
        pendingWelcome?.current_epoch ??
        pendingWelcome?.currentEpoch ??
        null,
    );
    const pendingRef = normalizeHexRefValue(
      pendingWelcome?.used_kp_ref ??
        pendingWelcome?.usedKpRef ??
        this.#resolveWelcomeTargetRef({
          welcomeB64: pickBase64String(
            pendingWelcome?.welcome_b64,
            pendingWelcome?.welcomeB64,
            pendingWelcome?.welcome,
            null,
          ),
          usedKpRef: pendingWelcome?.used_kp_ref ?? pendingWelcome?.usedKpRef ?? null,
        }) ??
        null,
    );

    if (
      normalizedAuthoritativeEpoch != null &&
      pendingEpoch != null &&
      pendingEpoch < normalizedAuthoritativeEpoch
    ) {
      return true;
    }

    if (
      normalizedAuthoritativeRef &&
      pendingRef &&
      pendingRef !== normalizedAuthoritativeRef &&
      (normalizedAuthoritativeEpoch == null ||
        pendingEpoch == null ||
        pendingEpoch <= normalizedAuthoritativeEpoch)
    ) {
      return true;
    }

    return false;
  }

  #resolveAuthoritativeWelcomeTargetForGroup(groups, { groupId, groupHandle } = {}) {
    const stored =
      this.#resolveStoredGroup(groups, {
        groupId: ensureString(groupId ?? null),
        groupHandle: ensureString(groupHandle ?? null),
      })?.entry ?? null;
    if (!stored || typeof stored !== 'object') {
      return null;
    }

    const authoritativeEpoch = normalizeEpochOrNull(
      stored?.welcomeTargetEpoch ??
        stored?.welcome_target_epoch ??
        stored?.epoch ??
        stored?.current_epoch ??
        stored?.currentEpoch ??
        null,
    );
    const authoritativeTargetRef = normalizeHexRefValue(
      stored?.welcomeTargetRefLock ??
        stored?.welcome_target_ref_lock ??
        stored?.used_kp_ref ??
        stored?.usedKpRef ??
        null,
    );

    if (authoritativeEpoch == null && !authoritativeTargetRef) {
      return null;
    }

    return {
      stored,
      authoritativeEpoch,
      authoritativeTargetRef,
    };
  }

  async #recoverFromClientIdMismatch({ ciphersuite, context, groupId, error = null }) {
    const expectedClientId = this.#normalizeIdentityId(
      error?.payload?.error?.expected_client_id ??
        error?.payload?.error?.expectedClientId ??
        error?.payload?.expected_client_id ??
        error?.payload?.expectedClientId ??
        null,
    );
    const serverIdentity = await this.#ensureServerDeviceIdentity({ force: true });
    const serverClientId = this.#normalizeIdentityId(
      serverIdentity?.client_id ?? serverIdentity?.clientId ?? null,
    );
    const serverSeed = pickBase64String(
      serverIdentity?.seed_b64 ?? serverIdentity?.seedB64 ?? serverIdentity?.seed ?? null,
    );
    const targetClientId = expectedClientId || serverClientId || null;

    console.warn('[MLS] client_id mismatch detected; resetting identity state', {
      context,
      groupId,
      ciphersuite,
      expectedClientId: expectedClientId ?? null,
      serverClientId: serverClientId ?? null,
      targetClientId: targetClientId ?? null,
    });

    // Ensure mismatch recovery always re-aligns to backend device identity.
    this._ignoreServerDeviceIdentityOnce = false;
    try {
      await clearBackupRestoreFlag(this.userId);
    } catch {}
    if (targetClientId) {
      try {
        await writeStableValue(stableKeys.identityId(this.userId), targetClientId);
      } catch {}
    }
    if (serverSeed) {
      try {
        await writeStableValue(stableKeys.seed(this.userId), serverSeed);
      } catch {}
    }

    await this.#resetIdentityState();
    const refreshed = await this.ensureIdentity({ ciphersuite });
    const refreshedId = this.#normalizeIdentityId(refreshed?.identityId ?? this.identityId);
    if (!refreshedId) {
      const err = new Error('MLS identity unavailable after client_id mismatch reset');
      err.code = 'client_id_mismatch';
      throw err;
    }
    if (targetClientId && refreshedId !== targetClientId) {
      console.warn('[MLS] client_id mismatch recovery retained unexpected identity', {
        context,
        groupId,
        ciphersuite,
        targetClientId,
        refreshedId,
      });
    }
    return { identity: refreshed, identityId: refreshedId };
  }

  async #recoverFromWelcomeRefMismatch({ ciphersuite, context, groupId }) {
    console.warn('[MLS] welcome ref mismatch detected; realigning to current key package', {
      context,
      groupId,
      ciphersuite,
    });

    const identity = await this.ensureIdentity({ ciphersuite });
    const identityId = this.#normalizeIdentityId(identity?.identityId ?? this.identityId);
    if (!identityId) {
      const err = new Error('MLS identity unavailable during welcome_ref_mismatch recovery');
      err.code = 'welcome_ref_mismatch';
      throw err;
    }

    const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
      identityId,
    ).catch(() => null);
    if (unresolvedWelcomeTarget?.targetRef) {
      try {
        console.info('[MLS] welcome ref mismatch recovery preserved locked welcome target', {
          context,
          groupId,
          ciphersuite,
          identityId,
          targetRef: unresolvedWelcomeTarget.targetRef ?? null,
          source: unresolvedWelcomeTarget.source ?? null,
        });
      } catch {}
      return {
        identity,
        identityId,
        keyPackageB64: null,
        blockedOnTargetMismatch: true,
        source: unresolvedWelcomeTarget?.source ?? 'welcome_target_locked',
      };
    }

    let keyPackageB64 = null;
    try {
      const exported = await this.exportKeyPackage(identityId);
      keyPackageB64 = exported?.keyPackage ?? null;
    } catch {}

    if (!keyPackageB64) {
      await this.publishKeyPackageAndSync(identityId, {
        forcePublish: true,
        retryOnMissingClientId: true,
      });
      try {
        const exported = await this.exportKeyPackage(identityId);
        keyPackageB64 = exported?.keyPackage ?? null;
      } catch {}
    }

    if (!keyPackageB64) {
      const err = new Error('MLS key package unavailable after welcome_ref_mismatch recovery');
      err.code = 'welcome_ref_mismatch';
      throw err;
    }

    return { identity, identityId, keyPackageB64 };
  }

  async #replaceRejectedJoinBundleKeyPackage({
    ciphersuite,
    context,
    groupId,
    identityId,
    rejectedKeyPackageB64 = null,
  }) {
    const activeIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!activeIdentityId) {
      const err = new Error('MLS identity unavailable during key_package_required recovery');
      err.code = 'identity_required';
      throw err;
    }

    const normalizedSuite = normalizeSuiteId(ciphersuite ?? this.preferredCiphersuite ?? null);
    const normalizedGroupId = ensureString(groupId) || null;

    // The server rejected this request target, so it cannot own recovery anymore.
    // Durable pending/persisted Welcome targets remain authoritative and are checked below.
    this.#clearActiveJoinBundleTargets({
      groupId: normalizedGroupId,
      identityId: activeIdentityId,
    });
    const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
      activeIdentityId,
    ).catch(() => null);
    if (unresolvedWelcomeTarget?.targetRef) {
      try {
        console.info('[MLS] key_package_required recovery preserved locked welcome target', {
          context,
          groupId: normalizedGroupId,
          ciphersuite: normalizedSuite,
          identityId: activeIdentityId,
          targetRef: unresolvedWelcomeTarget.targetRef ?? null,
          source: unresolvedWelcomeTarget.source ?? null,
        });
      } catch {}
      return {
        identityId: activeIdentityId,
        keyPackageB64: null,
        blockedOnTargetMismatch: true,
        source: unresolvedWelcomeTarget?.source ?? 'welcome_target_locked',
      };
    }
    const activeClientId =
      this.#normalizeIdentityId(
        this.identityClientId ??
          this.identityRecord?.clientId ??
          this.identityRecord?.client_id ??
          null,
      ) ?? activeIdentityId;

    let rejectedRef = null;
    if (typeof rejectedKeyPackageB64 === 'string' && rejectedKeyPackageB64.trim()) {
      rejectedRef = await computeKeyPackageRefHexFromB64(
        rejectedKeyPackageB64,
        normalizedSuite ?? null,
      ).catch(() => null);
    }

    try {
      console.warn(
        '[MLS] join_bundle key package rejected by server; rotating a fresh authoritative key package',
        {
          context,
          groupId: normalizedGroupId,
          identityId: activeIdentityId,
          rejectedRef,
          ciphersuite: normalizedSuite,
        },
      );
    } catch {}

    if (rejectedRef) {
      await this.#markKeyPackageRefConsumed(rejectedRef, {
        groupId: normalizedGroupId,
        epoch: null,
      }).catch(() => null);
      await this.#persistKeyPackageLifecycle({
        identityId: activeIdentityId,
        clientId: activeClientId,
        ciphersuite: normalizedSuite,
        kpRef: rejectedRef,
        keyPackageB64: rejectedKeyPackageB64,
        status: 'consumed',
        consumedByWelcome: true,
        consumedAt: Date.now(),
        usedGroupId: normalizedGroupId,
        usedEpoch: null,
        keyPackageHealthCode: 'server_rejected_historical_consumed',
        welcomeStageReady: false,
        welcomeStageReason: 'server_rejected_historical_consumed',
      }).catch(() => null);
    }

    this.#clearPublishedKeyPackageMirror({
      identityId: activeIdentityId,
      clientId: activeClientId,
      ciphersuite: normalizedSuite,
    });
    this._protectedWelcomeTargetDriftByIdentity?.delete?.(activeIdentityId);
    await this.#clearProtectedWelcomeTargetDrift({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupId,
      identityId: activeIdentityId,
    }).catch(() => null);
    await this.#clearPersistedWelcomeState({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupId,
      clearRecoveryPayload: true,
      preserveWelcomeTargetLock: false,
    }).catch(() => null);

    const rotated = await workerRotateKeyPackage({
      identityId: activeIdentityId,
      ciphersuite: normalizedSuite ?? null,
    }).catch(() => null);
    const rotatedKeyPackageB64 =
      (typeof rotated?.keyPackage === 'string' && rotated.keyPackage.trim()) || null;
    if (!rotatedKeyPackageB64) {
      const err = new Error('MLS key package unavailable during key_package_required recovery');
      err.code = 'key_package_required';
      throw err;
    }

    await this.publishKeyPackageAndSync(activeIdentityId, {
      forcePublish: true,
      retryOnMissingClientId: true,
      overrideWelcomeTargetLock: true,
    });

    const refreshed = await this.#resolveServerBoundKeyPackage({
      groupId: normalizedGroupId,
      identityId: activeIdentityId,
      keyPackageB64: rotatedKeyPackageB64,
      ciphersuite: normalizedSuite ?? rotated?.ciphersuite ?? null,
      preferCurrentLocalKeyPackage: true,
      context: context ? `${context}_key_package_required` : 'join_bundle_key_package_required',
    }).catch(() => null);

    const replacementKeyPackageB64 = refreshed?.keyPackageB64 ?? rotatedKeyPackageB64;
    if (!replacementKeyPackageB64) {
      const err = new Error('MLS key package unavailable during key_package_required recovery');
      err.code = 'key_package_required';
      throw err;
    }

    return {
      identityId: this.#normalizeIdentityId(refreshed?.identityId ?? activeIdentityId),
      keyPackageB64: replacementKeyPackageB64,
    };
  }

  async #recoverJoinBundleKeyPackage({
    ciphersuite,
    context,
    groupId,
    identityId,
    preferredKeyPackageB64 = null,
    error = null,
  }) {
    const identity = await this.ensureIdentity({ ciphersuite });
    const activeIdentityId = this.#normalizeIdentityId(
      identityId ?? identity?.identityId ?? this.identityId,
    );

    if (!activeIdentityId) {
      const err = new Error('MLS identity unavailable during join_bundle recovery');
      err.code = 'identity_required';
      throw err;
    }

    const errorCode = error?.code ?? error?.payload?.error?.code ?? error?.payload?.code ?? null;
    if (errorCode === 'key_package_required') {
      const replacement = await this.#replaceRejectedJoinBundleKeyPackage({
        ciphersuite,
        context,
        groupId,
        identityId: activeIdentityId,
        rejectedKeyPackageB64: preferredKeyPackageB64,
      });
      if (replacement?.blockedOnTargetMismatch) {
        return replacement;
      }
      return replacement;
    }

    console.warn('[MLS] join_bundle key package missing; forcing key package publish', {
      context,
      groupId,
      ciphersuite,
      identityId: activeIdentityId,
    });

    const resolvedKeyPackage = await this.#resolveServerBoundKeyPackage({
      groupId,
      identityId: activeIdentityId,
      keyPackageB64: preferredKeyPackageB64,
      ciphersuite,
      context,
    }).catch(() => null);
    if (resolvedKeyPackage?.blockedOnTargetMismatch) {
      return {
        identityId: activeIdentityId,
        keyPackageB64: null,
        keyPackageRef: resolvedKeyPackage?.keyPackageRef ?? null,
        blockedOnTargetMismatch: true,
        source: resolvedKeyPackage?.source ?? 'welcome_target_locked',
      };
    }

    const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
      activeIdentityId,
    ).catch(() => null);
    if (unresolvedWelcomeTarget?.targetRef) {
      return {
        identityId: activeIdentityId,
        keyPackageB64: null,
        keyPackageRef: normalizeHexRefValue(unresolvedWelcomeTarget?.targetRef ?? null),
        blockedOnTargetMismatch: true,
        source: unresolvedWelcomeTarget?.source ?? 'welcome_target_locked',
      };
    }
    if (resolvedKeyPackage?.keyPackageB64) {
      try {
        console.info(
          '[MLS] join_bundle recovery reusing pinned key package for unresolved welcome target',
          {
            context,
            groupId,
            identityId: resolvedKeyPackage.identityId ?? activeIdentityId,
            targetRef: resolvedKeyPackage.keyPackageRef ?? null,
            source: resolvedKeyPackage.source ?? null,
          },
        );
      } catch {}
      return {
        identityId: resolvedKeyPackage.identityId ?? activeIdentityId,
        keyPackageB64: resolvedKeyPackage.keyPackageB64,
      };
    }

    await this.publishKeyPackageAndSync(activeIdentityId, {
      forcePublish: true,
      retryOnMissingClientId: true,
    });

    let keyPackageB64 = null;
    try {
      const refreshed = await this.#resolveServerBoundKeyPackage({
        groupId,
        identityId: activeIdentityId,
        ciphersuite,
        context,
      }).catch(() => null);
      keyPackageB64 = refreshed?.keyPackageB64 ?? null;
    } catch {}

    if (!keyPackageB64) {
      const err = new Error('MLS key package unavailable during join_bundle recovery');
      err.code = 'key_package_required';
      throw err;
    }

    return { identityId: activeIdentityId, keyPackageB64 };
  }

  async #recoverJoinAgainstCurrentLocalKeyPackage({
    groupId,
    identityId,
    keyPackageB64,
    ciphersuite,
    welcomeSource,
    expectedEpoch = null,
    context = 'join_group_preflight_welcome_ref_mismatch',
    reason = 'preflight_welcome_ref_mismatch',
  }) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedGroupId || !normalizedIdentityId || !keyPackageB64) {
      return null;
    }

    try {
      await this.#clearPersistedWelcomeState({
        groupId: normalizedGroupId,
        clearRecoveryPayload: true,
        preserveWelcomeTargetLock: false,
      });
    } catch {}

    try {
      const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
      await this.#reissueWelcomesIfNeeded({
        groupId: normalizedGroupId,
        identityId: normalizedIdentityId,
        keyPackageB64,
        token: __t ? `Bearer ${__t}` : undefined,
      }).catch(() => null);
    } catch {}

    try {
      const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
      const fetched = await this.#fetchJoinBundleWithRecovery({
        groupId: normalizedGroupId,
        identityId: normalizedIdentityId,
        keyPackageB64,
        token: __t ? `Bearer ${__t}` : undefined,
        ciphersuite: ciphersuite ?? this.preferredCiphersuite ?? null,
        context,
        preferReissueOnAlreadyMemberNoWelcome: true,
        preferCurrentLocalKeyPackage: true,
      }).catch(() => null);
      const bundle = fetched?.bundle ?? null;
      const bundleWelcome =
        ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
      const bundleTree =
        ensureString(bundle?.ratchet_tree_b64) || ensureString(bundle?.ratchetTreeB64) || null;
      const bundleEpoch =
        bundle?.epoch != null ? ensureEpoch(bundle.epoch) : normalizeEpochOrNull(expectedEpoch);

      if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
        const recoveredNoWelcome = await this.#recoverAlreadyMemberNoWelcomeRuntime({
          groupId: normalizedGroupId,
          identityId: fetched?.identityId || normalizedIdentityId,
          ciphersuite: ciphersuite ?? this.preferredCiphersuite ?? null,
          source: welcomeSource || 'joinGroup',
          reason: `${reason}_already_member_no_welcome`,
        }).catch(() => null);
        if (recoveredNoWelcome) {
          return recoveredNoWelcome;
        }
      }

      if (bundleWelcome && bundleEpoch != null) {
        await this.#persistWelcomeTarget({
          groupId: normalizedGroupId,
          identityId: fetched?.identityId || normalizedIdentityId,
          welcomeB64: bundleWelcome,
          ratchetTreeB64: bundleTree,
          epoch: bundleEpoch,
          usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
          source: context,
        }).catch(() => null);
        let recovered = await this.joinGroup({
          identityId: fetched?.identityId || normalizedIdentityId,
          welcomeB64: bundleWelcome,
          ratchetTreeB64: null,
          groupId: normalizedGroupId,
          welcomeSource: context,
          expectedEpoch: bundleEpoch,
          allowJoinBundleRecovery: false,
        }).catch(() => null);
        if (!recovered && bundleTree) {
          recovered = await this.joinGroup({
            identityId: fetched?.identityId || normalizedIdentityId,
            welcomeB64: bundleWelcome,
            ratchetTreeB64: bundleTree,
            groupId: normalizedGroupId,
            welcomeSource: context,
            expectedEpoch: bundleEpoch,
            allowJoinBundleRecovery: false,
          }).catch(() => null);
        }
        if (recovered) {
          return recovered;
        }
      }
    } catch {}

    try {
      const followUp = await this.syncWelcomes({
        groupId: normalizedGroupId,
        __pipelineBypass: true,
      }).catch(() => null);
      const joined =
        Array.isArray(followUp?.joined) && followUp.joined.length
          ? followUp.joined.find(
              (item) =>
                item?.groupId === normalizedGroupId || item?.groupHandle === normalizedGroupId,
            ) || followUp.joined[0]
          : null;
      if (joined) {
        return joined;
      }
    } catch {}

    return null;
  }

  async #fetchJoinBundleWithRecovery({
    groupId,
    identityId,
    keyPackageB64,
    token,
    ciphersuite,
    context,
    preferReissueOnAlreadyMemberNoWelcome = false,
    preferCurrentLocalKeyPackage = false,
  }) {
    const resolvedKeyPackage = await this.#resolveServerBoundKeyPackage({
      groupId,
      identityId,
      keyPackageB64,
      ciphersuite,
      preferCurrentLocalKeyPackage,
      context,
    }).catch(() => null);
    if (resolvedKeyPackage?.blockedOnTargetMismatch) {
      try {
        console.info(
          '[MLS] join_bundle preserved unresolved welcome target lock; waiting for explicit current-local recovery',
          {
            groupId: ensureString(groupId) || null,
            identityId:
              this.#normalizeIdentityId(resolvedKeyPackage?.identityId ?? identityId) || null,
            targetRef:
              resolvedKeyPackage?.welcomeTargetRef ?? resolvedKeyPackage?.targetRef ?? null,
            currentLocalRef: resolvedKeyPackage?.currentLocalRef ?? null,
            context: context || null,
          },
        );
      } catch {}
      return {
        bundle: null,
        identityId: this.#normalizeIdentityId(resolvedKeyPackage?.identityId ?? identityId) || null,
        recovered: false,
        blocked: true,
        reason: 'welcome_target_locked_mismatch',
      };
    }
    const requestIdentityId =
      this.#normalizeIdentityId(resolvedKeyPackage?.identityId ?? identityId) || 'unknown-identity';
    const requestKeyPackageB64 = resolvedKeyPackage?.keyPackageB64 ?? null;
    if (!requestKeyPackageB64) {
      return {
        bundle: null,
        identityId: requestIdentityId,
        recovered: false,
        blocked: true,
        reason: 'welcome_target_key_package_unavailable',
      };
    }
    const normalizedGroupId = ensureString(groupId) || 'unknown-group';
    const normalizedIdentityId = requestIdentityId;
    const activeJoinBundleTarget = this.#registerActiveJoinBundleTarget({
      groupId: normalizedGroupId,
      identityId: normalizedIdentityId,
      keyPackageB64: requestKeyPackageB64,
      keyPackageRef: resolvedKeyPackage?.keyPackageRef ?? null,
      ciphersuite,
      source: context || 'join_bundle',
    });
    const keyPackageFingerprint = requestKeyPackageB64
      ? `${String(requestKeyPackageB64).length}:${String(requestKeyPackageB64).slice(0, 64)}`
      : 'no-key-package';
    const flightKey = `${normalizedGroupId}:${normalizedIdentityId}:${keyPackageFingerprint}`;
    const noWelcomeCooldownKey = `${normalizedGroupId}:${normalizedIdentityId}`;

    const cachedResponse = this._joinBundleRecentResponses.get(flightKey);
    if (cachedResponse && Date.now() - cachedResponse.at <= JOIN_BUNDLE_RESPONSE_CACHE_TTL_MS) {
      return cachedResponse.value;
    }

    const inFlight = this._joinBundleFlights.get(flightKey);
    if (inFlight) {
      return inFlight;
    }

    const nowTs = Date.now();
    const noWelcomeCooldownUntil =
      this._joinBundleNoWelcomeCooldownByFlight.get(noWelcomeCooldownKey) || 0;
    if (noWelcomeCooldownUntil > nowTs) {
      let latestGroupResponse = null;
      for (const [cacheKey, cached] of this._joinBundleRecentResponses.entries()) {
        if (!cacheKey.startsWith(`${normalizedGroupId}:`)) continue;
        if (!cached) continue;
        if (!latestGroupResponse || cached.at > latestGroupResponse.at) {
          latestGroupResponse = cached;
        }
      }
      if (latestGroupResponse?.value) {
        return latestGroupResponse.value;
      }
      if (typeof console?.debug === 'function') {
        console.debug('[MLS] join_bundle skipped by no-welcome cooldown', {
          groupId: normalizedGroupId,
          identityId: normalizedIdentityId,
          context: context || null,
          cooldownMsRemaining: Math.max(0, noWelcomeCooldownUntil - nowTs),
        });
      }
      return {
        bundle: null,
        identityId,
        recovered: false,
        throttled: true,
      };
    }

    if (ensureString(groupId)) {
      const now = Date.now();
      const lastCallAt = this._joinBundleCallDebounceByGroup.get(normalizedGroupId) || 0;
      if (now - lastCallAt < JOIN_BUNDLE_GROUP_DEBOUNCE_MS) {
        let latestGroupResponse = null;
        for (const [cacheKey, cached] of this._joinBundleRecentResponses.entries()) {
          if (!cacheKey.startsWith(`${normalizedGroupId}:`)) continue;
          if (!cached) continue;
          if (!latestGroupResponse || cached.at > latestGroupResponse.at) {
            latestGroupResponse = cached;
          }
        }
        if (latestGroupResponse?.value) {
          return latestGroupResponse.value;
        }
        if (typeof console?.debug === 'function') {
          console.debug('[MLS] join_bundle skipped by group debounce', {
            groupId: normalizedGroupId,
            context: context || null,
          });
        }
        return {
          bundle: null,
          identityId,
          recovered: false,
          throttled: true,
        };
      }
      this._joinBundleCallDebounceByGroup.set(normalizedGroupId, now);
    }

    const task = (async () => {
      try {
        let bundle = await getJoinBundle({
          groupId,
          identityId: requestIdentityId,
          keyPackageB64: requestKeyPackageB64,
          token,
          reissue: false,
        });
        const initialNoWelcome = this.#isAlreadyMemberNoWelcomeBundle(bundle);
        if (initialNoWelcome && preferReissueOnAlreadyMemberNoWelcome) {
          try {
            bundle = await getJoinBundle({
              groupId,
              identityId: requestIdentityId,
              keyPackageB64: requestKeyPackageB64,
              token,
              reissue: true,
            });
          } catch {}
        }
        const noWelcomeAfterRetry = this.#isAlreadyMemberNoWelcomeBundle(bundle);
        if (noWelcomeAfterRetry) {
          this._joinBundleNoWelcomeCooldownByFlight.set(
            noWelcomeCooldownKey,
            Date.now() + JOIN_BUNDLE_NO_WELCOME_REISSUE_COOLDOWN_MS,
          );
          if (typeof console?.debug === 'function') {
            console.debug('[MLS] join_bundle no-op (already member)', {
              groupId: normalizedGroupId,
              identityId: normalizedIdentityId,
              context: context || null,
              preferReissueOnAlreadyMemberNoWelcome: Boolean(preferReissueOnAlreadyMemberNoWelcome),
              cooldownMs: JOIN_BUNDLE_NO_WELCOME_REISSUE_COOLDOWN_MS,
            });
          }
        } else {
          this._joinBundleNoWelcomeCooldownByFlight.delete(noWelcomeCooldownKey);
        }
        return {
          bundle,
          identityId: requestIdentityId,
          recovered: false,
        };
      } catch (error) {
        if (shouldBlockJoinBundleRecovery(error)) {
          const blockedReason = extractApiErrorCode(error) || 'welcome_unavailable';
          try {
            const normalizedGroupId = ensureString(groupId);
            if (normalizedGroupId) {
              await this.#updateGroupEntry(normalizedGroupId, (previous) => ({
                ...(previous || {}),
                recoveryBlockedMarker:
                  previous?.recoveryBlockedMarker ??
                  previous?.recovery_blocked_marker ??
                  `join_bundle:${blockedReason}`,
                recovery_blocked_marker:
                  previous?.recovery_blocked_marker ??
                  previous?.recoveryBlockedMarker ??
                  `join_bundle:${blockedReason}`,
                recoveryBlockedReason: blockedReason,
                recovery_blocked_reason: blockedReason,
                recoveryBlockedAt: new Date().toISOString(),
                recovery_blocked_at: new Date().toISOString(),
                readinessState: 'blocked',
                readiness_state: 'blocked',
                readinessReason: blockedReason,
                readiness_reason: blockedReason,
              }));
            }
          } catch {}
          return {
            bundle: null,
            identityId: requestIdentityId,
            recovered: false,
            blocked: true,
            reason: blockedReason,
          };
        }

        if (this.#isWelcomeRefMismatchError(error)) {
          const recovery = await this.#recoverFromWelcomeRefMismatch({
            ciphersuite,
            context,
            groupId,
          });
          if (recovery?.blockedOnTargetMismatch) {
            return {
              bundle: null,
              identityId: recovery.identityId ?? requestIdentityId,
              recovered: false,
              blocked: true,
              reason: recovery.source || 'welcome_target_locked_mismatch',
            };
          }
          const bundle = await getJoinBundle({
            groupId,
            identityId: recovery.identityId,
            keyPackageB64: recovery.keyPackageB64 ?? requestKeyPackageB64,
            token,
            reissue: true,
          });
          return {
            bundle,
            identityId: recovery.identityId,
            recovered: true,
          };
        }

        if (!this.#isClientIdMismatchError(error)) {
          if (this.#isJoinBundleRecoveryError(error)) {
            const recovery = await this.#recoverJoinBundleKeyPackage({
              ciphersuite,
              context,
              groupId,
              identityId: requestIdentityId,
              preferredKeyPackageB64: requestKeyPackageB64,
              error,
            });
            if (recovery?.blockedOnTargetMismatch) {
              return {
                bundle: null,
                identityId: recovery.identityId ?? requestIdentityId,
                recovered: false,
                blocked: true,
                reason: recovery.source || 'welcome_target_locked_mismatch',
              };
            }
            const bundle = await getJoinBundle({
              groupId,
              identityId: recovery.identityId,
              keyPackageB64: recovery.keyPackageB64,
              token,
              reissue: true,
            });
            return {
              bundle,
              identityId: recovery.identityId,
              recovered: true,
            };
          }
          throw error;
        }
        const recovery = await this.#recoverFromClientIdMismatch({
          ciphersuite,
          context,
          groupId,
          error,
        });
        let keyPackage = keyPackageB64;
        if (!keyPackage) {
          try {
            const exported = await this.exportKeyPackage(recovery.identityId);
            keyPackage = exported?.keyPackage ?? null;
          } catch {}
        }
        if (!keyPackage) {
          throw error;
        }
        const bundle = await getJoinBundle({
          groupId,
          identityId: recovery.identityId,
          keyPackageB64: keyPackage,
          token,
          reissue: true,
        });
        return { bundle, identityId: recovery.identityId, recovered: true };
      }
    })();

    this._joinBundleFlights.set(flightKey, task);
    try {
      const result = await task;
      this._joinBundleRecentResponses.set(flightKey, { at: Date.now(), value: result });
      const bundle = result?.bundle ?? null;
      const retainedWelcome =
        ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
      if (!retainedWelcome) {
        this.#clearActiveJoinBundleTargets({
          groupId: normalizedGroupId,
          identityId: result?.identityId ?? normalizedIdentityId,
        });
      }
      return result;
    } finally {
      if (this._joinBundleFlights.get(flightKey) === task) {
        this._joinBundleFlights.delete(flightKey);
      }
      if (!this._joinBundleRecentResponses.has(flightKey)) {
        this.#clearActiveJoinBundleTargets({
          groupId: normalizedGroupId,
          identityId: activeJoinBundleTarget?.identityId ?? normalizedIdentityId,
        });
      }
    }
  }

  #normalizeStoredKeyPackageCacheEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const normalized = {};

    const identityCandidate =
      entry.identityId ?? entry.identity_id ?? entry.identity ?? entry.client_id ?? null;
    const identityId = this.#normalizeIdentityId(identityCandidate);
    if (identityId) {
      normalized.identityId = identityId;
    }

    const suiteCandidate = entry.ciphersuite ?? entry.suite ?? null;
    const normalizedSuite = normalizeSuiteId(suiteCandidate);
    if (normalizedSuite != null) {
      normalized.ciphersuite = normalizedSuite;
    } else if (suiteCandidate != null) {
      normalized.ciphersuite = suiteCandidate;
    } else {
      normalized.ciphersuite = null;
    }

    const statusCandidate = typeof entry.status === 'string' ? entry.status.trim() : '';
    if (statusCandidate) {
      normalized.status = statusCandidate.toLowerCase();
    }
    const kpRef = normalizeHexRefValue(
      entry.ref ?? entry.kp_ref ?? entry.kpRef ?? entry.key_package_ref ?? entry.keyPackageRef,
    );
    if (kpRef) {
      normalized.ref = kpRef;
      normalized.kp_ref = kpRef;
    }
    const keyPackageB64 = pickBase64String(
      entry.keyPackageB64,
      entry.key_package_b64,
      entry.key_package,
      entry.keyPackage,
      null,
    );
    if (keyPackageB64) {
      normalized.keyPackageB64 = keyPackageB64;
    }
    const consumedAt = coerceTimestampMs(entry.consumedAt ?? entry.consumed_at ?? null);
    if (consumedAt != null) {
      normalized.consumedAt = consumedAt;
    }
    const usedGroupId = ensureString(entry.usedGroupId ?? entry.used_group_id ?? null);
    if (usedGroupId) {
      normalized.usedGroupId = usedGroupId;
    }
    const usedEpoch = normalizeEpochOrNull(entry.usedEpoch ?? entry.used_epoch ?? null);
    if (usedEpoch != null) {
      normalized.usedEpoch = usedEpoch;
    }

    const ttlCandidate = coerceNumber(entry.ttlMs ?? entry.ttl_ms ?? entry.ttlMilliseconds);
    if (ttlCandidate != null && Number.isFinite(ttlCandidate) && ttlCandidate >= 0) {
      normalized.ttlMs = ttlCandidate;
    }

    const expiresCandidate = coerceTimestampMs(entry.expiresAt ?? entry.expires_at);
    if (expiresCandidate != null) {
      normalized.expiresAt = expiresCandidate;
    }

    const fetchedCandidate = coerceTimestampMs(entry.fetchedAt ?? entry.fetched_at);
    if (fetchedCandidate != null) {
      normalized.fetchedAt = fetchedCandidate;
    }

    const deferredCandidate = coerceTimestampMs(
      entry.deferredUntil ?? entry.deferred_until ?? entry.retry_at,
    );
    if (deferredCandidate != null) {
      normalized.deferredUntil = deferredCandidate;
    }

    const lastPublishedCandidate = coerceTimestampMs(
      entry.lastPublishedAt ?? entry.last_published_at ?? entry.published_at,
    );
    if (lastPublishedCandidate != null) {
      normalized.lastPublishedAt = lastPublishedCandidate;
    }
    const privateMaterialReady = normalizeTriStateBoolean(
      entry.privateMaterialReady ?? entry.private_material_ready,
    );
    if (privateMaterialReady != null) {
      normalized.privateMaterialReady = privateMaterialReady;
    }
    const keyPackageHandleAlive = normalizeTriStateBoolean(
      entry.keyPackageHandleAlive ?? entry.key_package_handle_alive,
    );
    if (keyPackageHandleAlive != null) {
      normalized.keyPackageHandleAlive = keyPackageHandleAlive;
    }
    const keyPackageExportable = normalizeTriStateBoolean(
      entry.keyPackageExportable ?? entry.key_package_exportable,
    );
    if (keyPackageExportable != null) {
      normalized.keyPackageExportable = keyPackageExportable;
    }
    const keyPackageHealthCode =
      ensureString(entry.keyPackageHealthCode ?? entry.key_package_health_code ?? null) || null;
    if (keyPackageHealthCode) {
      normalized.keyPackageHealthCode = keyPackageHealthCode;
    }
    const keyPackageProvenance = normalizeKeyPackageProvenance(
      entry.keyPackageProvenance ?? entry.key_package_provenance ?? null,
    );
    if (keyPackageProvenance) {
      normalized.keyPackageProvenance = keyPackageProvenance;
    }
    const welcomeStageReady = normalizeTriStateBoolean(
      entry.welcomeStageReady ?? entry.welcome_stage_ready,
    );
    if (welcomeStageReady != null) {
      normalized.welcomeStageReady = welcomeStageReady;
    }
    const welcomeStageReason =
      ensureString(entry.welcomeStageReason ?? entry.welcome_stage_reason ?? null) || null;
    if (welcomeStageReason) {
      normalized.welcomeStageReason = welcomeStageReason;
    }

    return {
      identityId: normalized.identityId ?? null,
      ciphersuite: normalized.ciphersuite ?? null,
      status: normalized.status ?? undefined,
      ref: normalized.ref ?? null,
      kp_ref: normalized.kp_ref ?? null,
      keyPackageB64: normalized.keyPackageB64 ?? null,
      consumedAt: normalized.consumedAt ?? null,
      usedGroupId: normalized.usedGroupId ?? null,
      usedEpoch: normalized.usedEpoch ?? null,
      ttlMs: normalized.ttlMs ?? null,
      expiresAt: normalized.expiresAt ?? null,
      fetchedAt: normalized.fetchedAt ?? null,
      deferredUntil: normalized.deferredUntil ?? null,
      lastPublishedAt: normalized.lastPublishedAt ?? null,
      privateMaterialReady: normalized.privateMaterialReady ?? null,
      keyPackageHandleAlive: normalized.keyPackageHandleAlive ?? null,
      keyPackageExportable: normalized.keyPackageExportable ?? null,
      keyPackageHealthCode: normalized.keyPackageHealthCode ?? null,
      keyPackageProvenance: normalized.keyPackageProvenance ?? null,
      welcomeStageReady: normalized.welcomeStageReady ?? null,
      welcomeStageReason: normalized.welcomeStageReason ?? null,
    };
  }

  #serializeKeyPackageCacheEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const output = {};
    if (entry.identityId != null) {
      output.identityId = entry.identityId;
    }
    if (entry.ciphersuite != null) {
      output.ciphersuite = entry.ciphersuite;
    }
    if (typeof entry.status === 'string' && entry.status.trim()) {
      output.status = entry.status.trim();
    }
    const kpRef = normalizeHexRefValue(
      entry.ref ?? entry.kp_ref ?? entry.kpRef ?? entry.key_package_ref ?? entry.keyPackageRef,
    );
    if (kpRef) {
      output.ref = kpRef;
      output.kp_ref = kpRef;
      output.key_package_ref = kpRef;
    }
    const keyPackageB64 = pickBase64String(
      entry.keyPackageB64,
      entry.key_package_b64,
      entry.key_package,
      entry.keyPackage,
      null,
    );
    if (keyPackageB64) {
      output.keyPackageB64 = keyPackageB64;
      output.key_package_b64 = keyPackageB64;
    }
    if (Number.isFinite(entry.consumedAt)) {
      output.consumedAt = Math.floor(entry.consumedAt);
      output.consumed_at = Math.floor(entry.consumedAt);
    }
    if (entry.usedGroupId != null) {
      output.usedGroupId = entry.usedGroupId;
      output.used_group_id = entry.usedGroupId;
    }
    if (Number.isFinite(entry.usedEpoch)) {
      output.usedEpoch = Math.floor(entry.usedEpoch);
      output.used_epoch = Math.floor(entry.usedEpoch);
    }
    if (Number.isFinite(entry.ttlMs) && entry.ttlMs >= 0) {
      output.ttlMs = Math.floor(entry.ttlMs);
    }
    if (Number.isFinite(entry.expiresAt)) {
      output.expiresAt = Math.floor(entry.expiresAt);
    }
    if (Number.isFinite(entry.fetchedAt)) {
      output.fetchedAt = Math.floor(entry.fetchedAt);
    }
    if (Number.isFinite(entry.deferredUntil)) {
      output.deferredUntil = Math.floor(entry.deferredUntil);
    }
    if (Number.isFinite(entry.lastPublishedAt)) {
      output.lastPublishedAt = Math.floor(entry.lastPublishedAt);
    }
    if (entry.privateMaterialReady === true || entry.privateMaterialReady === false) {
      output.privateMaterialReady = entry.privateMaterialReady;
      output.private_material_ready = entry.privateMaterialReady;
    }
    if (entry.keyPackageHandleAlive === true || entry.keyPackageHandleAlive === false) {
      output.keyPackageHandleAlive = entry.keyPackageHandleAlive;
      output.key_package_handle_alive = entry.keyPackageHandleAlive;
    }
    if (entry.keyPackageExportable === true || entry.keyPackageExportable === false) {
      output.keyPackageExportable = entry.keyPackageExportable;
      output.key_package_exportable = entry.keyPackageExportable;
    }
    if (typeof entry.keyPackageHealthCode === 'string' && entry.keyPackageHealthCode.trim()) {
      output.keyPackageHealthCode = entry.keyPackageHealthCode.trim();
      output.key_package_health_code = entry.keyPackageHealthCode.trim();
    }
    const keyPackageProvenance = normalizeKeyPackageProvenance(entry.keyPackageProvenance);
    if (keyPackageProvenance) {
      output.keyPackageProvenance = keyPackageProvenance;
      output.key_package_provenance = keyPackageProvenance;
    }
    if (entry.welcomeStageReady === true || entry.welcomeStageReady === false) {
      output.welcomeStageReady = entry.welcomeStageReady;
      output.welcome_stage_ready = entry.welcomeStageReady;
    }
    if (typeof entry.welcomeStageReason === 'string' && entry.welcomeStageReason.trim()) {
      output.welcomeStageReason = entry.welcomeStageReason.trim();
      output.welcome_stage_reason = entry.welcomeStageReason.trim();
    }

    return output;
  }

  async #persistKeyPackageCache() {
    const payload = {};
    for (const [key, entry] of this.keyPackageStatusCache.entries()) {
      const serialized = this.#serializeKeyPackageCacheEntry(entry);
      if (serialized) {
        payload[key] = serialized;
      }
    }
    await setItem(this.keyPackagesKey, payload);
  }

  #schedulePersistKeyPackageCache() {
    if (this._keyPackageCachePersistScheduled) {
      return;
    }
    this._keyPackageCachePersistScheduled = true;
    Promise.resolve()
      .then(() => this.#persistKeyPackageCache())
      .catch((error) => {
        debugLog('[MLS] Failed to persist key package cache', { error });
      })
      .finally(() => {
        this._keyPackageCachePersistScheduled = false;
      });
  }

  async #ensureClientIdCacheLoaded() {
    if (this._clientIdsLoaded) {
      return;
    }

    if (this._clientIdsLoadPromise) {
      await this._clientIdsLoadPromise;
      return;
    }

    this._clientIdsLoadPromise = (async () => {
      try {
        const stored = await getItem(this.clientIdsKey);
        if (stored && typeof stored === 'object') {
          Object.entries(stored).forEach(([rawKey, rawValue]) => {
            const normalizedClientId = this.#normalizeIdentityId(rawValue);
            if (!normalizedClientId) {
              return;
            }
            let suiteKey = null;
            if (typeof rawKey === 'string' && rawKey.trim()) {
              suiteKey = rawKey.trim();
            } else {
              suiteKey = this.#normalizeSuiteKey(rawKey);
            }
            if (!suiteKey) {
              return;
            }
            this.clientIdsBySuite.set(suiteKey, normalizedClientId);
          });
        }
      } catch (error) {
        debugLog('[MLS] Failed to load MLS client id cache from storage', { error });
      } finally {
        this._clientIdsLoaded = true;
        this._clientIdsLoadPromise = null;
      }
    })();

    await this._clientIdsLoadPromise;
  }

  #getClientIdForSuite(ciphersuite) {
    const suiteKey = this.#normalizeSuiteKey(ciphersuite);
    if (!suiteKey) {
      return null;
    }
    return this.clientIdsBySuite.get(suiteKey) || null;
  }

  #adoptClientIdForSuite(ciphersuite, clientId) {
    const normalizedClientId = this.#normalizeIdentityId(clientId);
    const suiteKey = this.#normalizeSuiteKey(ciphersuite);
    if (!suiteKey) {
      return normalizedClientId ?? null;
    }

    if (!normalizedClientId) {
      if (this.clientIdsBySuite.has(suiteKey)) {
        this.clientIdsBySuite.delete(suiteKey);
        this.#schedulePersistClientIds();
      }
      return null;
    }

    const previous = this.clientIdsBySuite.get(suiteKey);
    if (previous === normalizedClientId) {
      return normalizedClientId;
    }

    this.clientIdsBySuite.set(suiteKey, normalizedClientId);
    this.#schedulePersistClientIds();
    return normalizedClientId;
  }

  async #persistClientIds() {
    const payload = {};
    for (const [suiteKey, clientId] of this.clientIdsBySuite.entries()) {
      if (!suiteKey || !clientId) {
        continue;
      }
      payload[suiteKey] = clientId;
    }
    await setItem(this.clientIdsKey, payload);
  }

  #schedulePersistClientIds() {
    if (this._clientIdsPersistScheduled) {
      return;
    }
    this._clientIdsPersistScheduled = true;
    Promise.resolve()
      .then(() => this.#persistClientIds())
      .catch((error) => {
        debugLog('[MLS] Failed to persist MLS client id cache', { error });
      })
      .finally(() => {
        this._clientIdsPersistScheduled = false;
      });
  }

  #clearClientIdCacheForSuite(ciphersuite) {
    const suiteKey = this.#normalizeSuiteKey(ciphersuite);
    if (!suiteKey) {
      return;
    }
    if (this.clientIdsBySuite.delete(suiteKey)) {
      this.#schedulePersistClientIds();
    }
  }

  #keyPackageCacheKey(identityId, ciphersuite) {
    const normalizedId = this.#normalizeIdentityId(identityId);
    if (!normalizedId) {
      return null;
    }
    const suiteKey = this.#normalizeSuiteKey(ciphersuite);
    return `${normalizedId}:${suiteKey}`;
  }

  #getKeyPackageCacheEntry(identityId, ciphersuite) {
    const directKey = this.#keyPackageCacheKey(identityId, ciphersuite);
    if (directKey && this.keyPackageStatusCache.has(directKey)) {
      return this.keyPackageStatusCache.get(directKey);
    }

    if (ciphersuite == null) {
      const normalizedId = this.#normalizeIdentityId(identityId);
      if (!normalizedId) return null;
      const prefix = `${normalizedId}:`;
      for (const [key, entry] of this.keyPackageStatusCache.entries()) {
        if (typeof key === 'string' && key.startsWith(prefix)) {
          return entry;
        }
      }
    }

    return null;
  }

  #updateKeyPackageCache(identityId, ciphersuite, updates = {}) {
    const cacheKey = this.#keyPackageCacheKey(identityId, ciphersuite);
    if (!cacheKey) {
      return null;
    }

    const previous = this.keyPackageStatusCache.get(cacheKey) || {};
    const next = {
      ...previous,
      identityId: this.#normalizeIdentityId(identityId) ?? previous.identityId ?? null,
      ciphersuite: ciphersuite ?? previous.ciphersuite ?? null,
      fetchedAt: updates.fetchedAt ?? Date.now(),
      ...updates,
    };

    if (updates.ttlMs === undefined && previous.ttlMs != null && next.ttlMs == null) {
      next.ttlMs = previous.ttlMs;
    }
    if (updates.expiresAt === undefined && previous.expiresAt != null && next.expiresAt == null) {
      next.expiresAt = previous.expiresAt;
    }
    if (
      updates.lastPublishedAt === undefined &&
      previous.lastPublishedAt != null &&
      next.lastPublishedAt == null
    ) {
      next.lastPublishedAt = previous.lastPublishedAt;
    }

    this.keyPackageStatusCache.set(cacheKey, next);
    this.#schedulePersistKeyPackageCache();
    return next;
  }

  #clearKeyPackageCacheForSuite(ciphersuite) {
    const suiteKey = this.#normalizeSuiteKey(ciphersuite);
    if (!suiteKey) {
      return;
    }

    let removed = false;
    for (const key of Array.from(this.keyPackageStatusCache.keys())) {
      if (typeof key === 'string' && key.endsWith(`:${suiteKey}`)) {
        this.keyPackageStatusCache.delete(key);
        removed = true;
      }
    }

    if (removed) {
      this.#schedulePersistKeyPackageCache();
    }
  }

  #clearKeyPackageCacheForIdentity(identityId) {
    const normalizedIdentity = this.#normalizeIdentityId(identityId);
    if (!normalizedIdentity) {
      return;
    }

    let removed = false;
    for (const key of Array.from(this.keyPackageStatusCache.keys())) {
      if (typeof key === 'string' && key.startsWith(`${normalizedIdentity}:`)) {
        this.keyPackageStatusCache.delete(key);
        removed = true;
      }
    }

    if (removed) {
      this.#schedulePersistKeyPackageCache();
    }
  }

  #persistPublishedKeyPackageMirror({
    identityId = null,
    clientId = null,
    ciphersuite = null,
    keyPackageB64 = null,
    publishedAt = null,
  } = {}) {
    if (typeof keyPackageB64 !== 'string' || !keyPackageB64.trim() || ciphersuite == null) {
      return;
    }

    try {
      const ls = getLocalStorageSafe();
      if (!ls) return;
      const payload = JSON.stringify({
        key_package_b64: keyPackageB64,
        updated_at: coerceTimestampMs(publishedAt ?? Date.now()) || Date.now(),
        published_at: coerceTimestampMs(publishedAt ?? Date.now()) || Date.now(),
        verifiedAt: Date.now(),
        client_id: this.#normalizeIdentityId(clientId ?? null) ?? null,
        identity_id: this.#normalizeIdentityId(identityId ?? null) ?? null,
        ciphersuite: normalizeSuiteId(ciphersuite),
      });
      for (const cacheId of new Set([
        this.#normalizeIdentityId(identityId ?? null),
        this.#normalizeIdentityId(clientId ?? null),
      ])) {
        if (!cacheId) continue;
        ls.setItem(`mls:keypkg:${cacheId}:${ciphersuite}`, payload);
      }
    } catch {}
  }

  #clearPublishedKeyPackageMirror({ identityId = null, clientId = null, ciphersuite = null } = {}) {
    if (ciphersuite == null) {
      return;
    }

    try {
      const ls = getLocalStorageSafe();
      if (!ls) return;
      for (const cacheId of new Set([
        this.#normalizeIdentityId(identityId ?? null),
        this.#normalizeIdentityId(clientId ?? null),
      ])) {
        if (!cacheId) continue;
        ls.removeItem(`mls:keypkg:${cacheId}:${ciphersuite}`);
      }
    } catch {}
  }

  #isKeyPackageEntryFresh(entry, now = Date.now()) {
    if (!entry) return false;
    if (entry.deferredUntil && entry.deferredUntil > now) {
      return false;
    }

    // A cache entry without a concrete key package reference is never authoritative.
    if (!entry.ref) {
      return false;
    }

    // Respect TTL on last publish even if server provided expiresAt/ttlMs
    if (entry.lastPublishedAt != null && now - entry.lastPublishedAt > KEY_PACKAGE_TTL_MS) {
      return false;
    }

    if (entry.expiresAt && entry.expiresAt > now) {
      return true;
    }

    if (entry.ttlMs != null && entry.fetchedAt != null) {
      if (now - entry.fetchedAt < entry.ttlMs) {
        return true;
      }
    }

    if (
      entry.lastPublishedAt != null &&
      now - entry.lastPublishedAt < KEY_PACKAGE_MIN_POST_INTERVAL_MS
    ) {
      return true;
    }

    return false;
  }

  #evaluateKeyPackageCache(identityId, ciphersuite, now = Date.now()) {
    const entry = this.#getKeyPackageCacheEntry(identityId, ciphersuite);
    if (!entry) {
      return { entry: null, proceed: true, needsRefresh: false };
    }

    if (entry.deferredUntil && entry.deferredUntil > now) {
      return { entry, proceed: false, deferred: true };
    }

    if (this.#isKeyPackageEntryFresh(entry, now)) {
      return { entry, proceed: false, skipped: true };
    }

    return { entry, proceed: true, needsRefresh: true };
  }

  async #applyKeyPackageMetadataToIdentity(metadata, { identityId: _identityId } = {}) {
    if (!metadata || typeof metadata !== 'object') {
      return null;
    }

    await this.#ensureClientIdCacheLoaded();

    const normalizedClientId = this.#normalizeIdentityId(metadata.clientId ?? null);
    const normalizedRequestedIdentityId = this.#normalizeIdentityId(
      _identityId ?? metadata.identityId ?? null,
    );
    const metadataSuite = normalizeSuiteId(metadata.ciphersuite ?? null);
    const mintedClientId =
      normalizedClientId ??
      (isCanonicalMlsClientId(normalizedRequestedIdentityId)
        ? normalizedRequestedIdentityId
        : null) ??
      null;

    let existing = this.identityRecord || (await getItem(this.identityKey));
    if (existing && typeof existing === 'object') {
      const { record: normalizedExisting, changed } = this.#normalizeIdentityRecord(existing);
      existing = normalizedExisting;
      if (changed) {
        await setItem(this.identityKey, existing);
      }
    }

    if (!mintedClientId) {
      if (metadataSuite != null) {
        this.#clearClientIdCacheForSuite(metadataSuite);
        this.#clearKeyPackageCacheForSuite(metadataSuite);
      }

      if (!existing || typeof existing !== 'object') {
        this.identityClientId = mintedClientId;
        return null;
      }

      const suiteKey = metadataSuite != null ? String(metadataSuite) : null;
      const nextClientIds =
        existing.clientIds && typeof existing.clientIds === 'object'
          ? { ...existing.clientIds }
          : {};
      if (suiteKey && suiteKey in nextClientIds) {
        delete nextClientIds[suiteKey];
      }

      const nextIdentity = { ...existing, clientId: null, clientIds: nextClientIds };
      await setItem(this.identityKey, nextIdentity);
      this.#setIdentityState(nextIdentity);

      this.identityClientId = mintedClientId;

      return nextIdentity;
    }

    if (!existing || typeof existing !== 'object') {
      if (metadataSuite != null) {
        this.#adoptClientIdForSuite(metadataSuite, mintedClientId);
      }
      this.identityClientId = mintedClientId;
      return null;
    }

    const existingSuite = normalizeSuiteId(existing.ciphersuite ?? null);
    const resolvedSuite = metadataSuite ?? existingSuite ?? null;
    if (resolvedSuite != null) {
      this.#adoptClientIdForSuite(resolvedSuite, mintedClientId);
    }

    const suiteKey = resolvedSuite != null ? String(resolvedSuite) : null;
    const nextClientIds =
      existing.clientIds && typeof existing.clientIds === 'object' ? { ...existing.clientIds } : {};
    if (suiteKey) {
      nextClientIds[suiteKey] = mintedClientId;
    }

    const nextIdentity = { ...existing, clientId: mintedClientId, clientIds: nextClientIds };
    await setItem(this.identityKey, nextIdentity);
    this.#setIdentityState(nextIdentity);
    this.identityClientId = mintedClientId;
    return nextIdentity;
  }

  async #refreshKeyPackageFromServer({ clientId, ciphersuite }) {
    await this.#ensureKeyPackageCacheLoaded();
    const normalizedId = this.#normalizeIdentityId(clientId);
    if (!normalizedId) {
      return null;
    }
    try {
      const response = await fetchLatestMlsKeyPackage(
        ciphersuite != null ? { clientId: normalizedId, ciphersuite } : { clientId: normalizedId },
      );
      if (!response) {
        return null;
      }

      const metadata = extractKeyPackageMetadata(response) || {};
      const cacheIdentity = metadata.clientId || metadata.identityId || normalizedId;
      const cacheCiphersuite = metadata.ciphersuite ?? ciphersuite ?? null;
      const status = metadata.status || 'unknown';
      let keyPackageRef = metadata.ref ?? null;
      if (!keyPackageRef && metadata.keyPackageB64) {
        keyPackageRef = await computeKeyPackageRefHexFromB64(
          metadata.keyPackageB64,
          cacheCiphersuite,
        ).catch(() => null);
      }
      const updated = this.#updateKeyPackageCache(cacheIdentity, cacheCiphersuite, {
        status,
        ttlMs: metadata.ttlMs ?? undefined,
        expiresAt: metadata.expiresAt ?? undefined,
        ref: keyPackageRef ?? undefined,
        deferredUntil: null,
      });
      await this.#trackKeyPackageByRef({
        kpRef: keyPackageRef ?? updated?.ref ?? null,
        identityId: metadata.identityId ?? cacheIdentity ?? normalizedId,
        clientId: metadata.clientId ?? cacheIdentity ?? normalizedId,
        ciphersuite: cacheCiphersuite,
        keyPackageB64: metadata.keyPackageB64 ?? null,
        status: 'unused',
      });

      await this.#applyKeyPackageMetadataToIdentity(metadata, { identityId: normalizedId });

      const matchedSuite = cacheCiphersuite ?? ciphersuite ?? null;
      const mappedSuiteClientId = this.#normalizeIdentityId(
        this.#getClientIdForSuite(matchedSuite),
      );
      const hasAlignedSuiteClientId =
        Boolean(mappedSuiteClientId) && mappedSuiteClientId === normalizedId;

      if (
        (status === 'fresh' || metadata.status === 'fresh') &&
        Boolean(updated?.ref) &&
        this.#normalizeIdentityId(cacheIdentity) === normalizedId &&
        this.#suiteKeysMatch(cacheCiphersuite, ciphersuite) &&
        hasAlignedSuiteClientId
      ) {
        debugLog('[MLS] Skipping key package publish - server reports fresh package', {
          identityId: normalizedId,
          ciphersuite: cacheCiphersuite ?? ciphersuite ?? null,
        });
        return {
          skipped: true,
          identityId: normalizedId,
          ciphersuite: cacheCiphersuite ?? ciphersuite ?? null,
          cacheEntry: updated,
        };
      }

      if (
        (status === 'fresh' || metadata.status === 'fresh') &&
        Boolean(updated?.ref) &&
        !hasAlignedSuiteClientId
      ) {
        debugLog(
          '[MLS] Server key package status fresh but suite client id is not aligned - forcing publish',
          {
            identityId: normalizedId,
            ciphersuite: cacheCiphersuite ?? ciphersuite ?? null,
            mappedSuiteClientId: mappedSuiteClientId ?? null,
          },
        );
      } else if ((status === 'fresh' || metadata.status === 'fresh') && !updated?.ref) {
        debugLog('[MLS] Server key package status fresh but missing ref - forcing publish', {
          identityId: normalizedId,
          ciphersuite: cacheCiphersuite ?? ciphersuite ?? null,
        });
      }
    } catch (error) {
      debugLog('[MLS] Failed to refresh key package status from server', {
        identityId: normalizedId,
        ciphersuite: ciphersuite ?? null,
        error,
      });
    }

    return null;
  }

  async #publishKeyPackageWithRetries({
    identityId,
    clientId,
    ciphersuite,
    payload,
    computedRef = null,
    force = false,
    allowConsumedRecovery = true,
  }) {
    let normalizedIdentityId = this.#normalizeIdentityId(identityId);
    if (!normalizedIdentityId) {
      throw new Error('MLS identity is not initialised');
    }

    await this.#ensureKeyPackageCacheLoaded();

    let normalizedClientId = this.#normalizeIdentityId(clientId);
    let cacheTargetId = normalizedClientId ?? normalizedIdentityId;
    let payloadTemplate = payload;
    let computedRefValue = computedRef;

    let lastError = null;

    for (let attempt = 0; attempt < KEY_PACKAGE_RETRY_DELAYS_MS.length; attempt += 1) {
      const delay = KEY_PACKAGE_RETRY_DELAYS_MS[attempt];
      if (delay > 0) {
        await sleep(delay);
      }

      try {
        const publishPayload = { ...payloadTemplate };
        if (normalizedIdentityId) {
          publishPayload.identityId = normalizedIdentityId;
        } else if ('identityId' in publishPayload) {
          delete publishPayload.identityId;
        }
        if (normalizedClientId) {
          publishPayload.clientId = normalizedClientId;
        } else if ('clientId' in publishPayload) {
          delete publishPayload.clientId;
        }

        const response = await publishMlsKeyPackage({ ...publishPayload, force });
        const responseStatus = response?.status ?? null;

        if (responseStatus === 409 || response?.alreadyExists) {
          const metadata = extractKeyPackageMetadata(response) || {};
          const resolvedMetadata = { ...metadata };
          if (resolvedMetadata.ciphersuite == null && ciphersuite != null) {
            resolvedMetadata.ciphersuite = ciphersuite;
          }
          const now = Date.now();
          const cacheIdentity =
            resolvedMetadata.clientId ||
            resolvedMetadata.identityId ||
            cacheTargetId ||
            normalizedIdentityId;
          const cacheSuite = resolvedMetadata.ciphersuite ?? ciphersuite;
          const keyPackageB64ForRef = pickBase64String(
            resolvedMetadata.keyPackageB64,
            publishPayload.keyPackage,
            publishPayload.key_package_b64,
            payloadTemplate?.keyPackage,
            payloadTemplate?.key_package_b64,
            null,
          );
          let resolvedRef = normalizeHexRefValue(resolvedMetadata.ref ?? computedRefValue ?? null);
          if (!resolvedRef && keyPackageB64ForRef) {
            resolvedRef = await computeKeyPackageRefHexFromB64(
              keyPackageB64ForRef,
              cacheSuite,
            ).catch(() => null);
          }
          this.#updateKeyPackageCache(cacheIdentity, cacheSuite, {
            status: 'fresh',
            ttlMs: resolvedMetadata.ttlMs ?? undefined,
            expiresAt: resolvedMetadata.expiresAt ?? undefined,
            deferredUntil: null,
            lastPublishedAt: now,
            ref: resolvedRef ?? undefined,
          });
          await this.#trackKeyPackageByRef({
            kpRef: resolvedRef ?? null,
            identityId: resolvedMetadata.identityId ?? normalizedIdentityId ?? null,
            clientId: resolvedMetadata.clientId ?? cacheIdentity ?? null,
            ciphersuite: cacheSuite,
            keyPackageB64: keyPackageB64ForRef ?? null,
            status: 'unused',
          });
          await this.#applyKeyPackageMetadataToIdentity(resolvedMetadata, {
            identityId: normalizedIdentityId,
          });
          debugLog('[MLS] Key package already published for identity', {
            identityId: cacheIdentity,
          });
          const resolvedSuite = resolvedMetadata.ciphersuite ?? ciphersuite ?? null;
          return {
            skipped: true,
            alreadyExists: true,
            identityId: cacheIdentity,
            clientId: resolvedMetadata.clientId ?? null,
            ciphersuite: resolvedSuite,
            cacheEntry: this.#getKeyPackageCacheEntry(cacheIdentity, cacheSuite),
          };
        }

        const metadata = extractKeyPackageMetadata(response) || {};
        const resolvedMetadata = { ...metadata };
        if (resolvedMetadata.ciphersuite == null && ciphersuite != null) {
          resolvedMetadata.ciphersuite = ciphersuite;
        }
        const now = Date.now();
        const cacheIdentity =
          resolvedMetadata.clientId ||
          resolvedMetadata.identityId ||
          cacheTargetId ||
          normalizedIdentityId;
        const cacheSuite = resolvedMetadata.ciphersuite ?? ciphersuite;
        const cacheEntry = this.#updateKeyPackageCache(cacheIdentity, cacheSuite, {
          status: 'fresh',
          ttlMs: resolvedMetadata.ttlMs ?? undefined,
          expiresAt: resolvedMetadata.expiresAt ?? undefined,
          deferredUntil: null,
          lastPublishedAt: now,
          ref: computedRefValue ?? undefined,
        });
        await this.#trackKeyPackageByRef({
          kpRef: cacheEntry?.ref ?? resolvedMetadata.ref ?? computedRefValue ?? null,
          identityId: resolvedMetadata.identityId ?? normalizedIdentityId ?? null,
          clientId: resolvedMetadata.clientId ?? cacheIdentity ?? null,
          ciphersuite: cacheSuite,
          keyPackageB64: pickBase64String(
            resolvedMetadata.keyPackageB64,
            publishPayload.keyPackage,
            publishPayload.key_package_b64,
            payloadTemplate?.keyPackage,
            payloadTemplate?.key_package_b64,
            null,
          ),
          status: 'unused',
        });

        if (!resolvedMetadata.clientId) {
          let snippet = null;
          if (typeof response?.responseText === 'string') {
            snippet = response.responseText;
          } else if (response && typeof response === 'object') {
            try {
              snippet = JSON.stringify(response);
            } catch {
              try {
                snippet = String(response);
              } catch {}
            }
          }
          if (snippet && snippet.length > 300) {
            snippet = `${snippet.slice(0, 300)}…`;
          }
          console.warn('[MLS] publishKeyPackage: missing client_id in response', {
            identityId: normalizedIdentityId,
            status: response?.status ?? null,
            responseSnippet: snippet,
          });
        }

        await this.#applyKeyPackageMetadataToIdentity(resolvedMetadata, {
          identityId: normalizedIdentityId,
        });

        debugLog('[MLS] Published key package', {
          identityId: cacheIdentity,
          ciphersuite,
        });

        const resolvedSuite = resolvedMetadata.ciphersuite ?? ciphersuite ?? null;
        return {
          ok: true,
          identityId: cacheIdentity,
          clientId: resolvedMetadata.clientId ?? null,
          ciphersuite: resolvedSuite,
          response,
          cacheEntry,
        };
      } catch (error) {
        if (error?.status === 401) {
          const reason =
            error?.payload?.message || error?.message || 'Unauthorized publishing MLS key package';
          const unauthorized = new Error(reason);
          unauthorized.code = 'MLS_PUBLISH_UNAUTHORIZED';
          unauthorized.status = error?.status || 401;
          if (error?.payload) {
            unauthorized.payload = error.payload;
          }
          throw unauthorized;
        }

        const errorCode =
          error?.code ?? error?.payload?.error?.code ?? error?.payload?.code ?? null;
        if (
          allowConsumedRecovery &&
          error?.status === 422 &&
          errorCode === 'key_package_required'
        ) {
          const rejectedRef = normalizeHexRefValue(
            error?.payload?.error?.rejected_ref ??
              error?.payload?.error?.rejectedRef ??
              computedRefValue ??
              null,
          );
          const rejectedKeyPackageB64 = pickBase64String(
            payloadTemplate?.keyPackageB64,
            payloadTemplate?.key_package_b64,
            null,
          );

          if (rejectedRef) {
            await this.#markKeyPackageRefConsumed(rejectedRef).catch(() => null);
            await this.#persistKeyPackageLifecycle({
              identityId: normalizedIdentityId,
              clientId: normalizedClientId ?? normalizedIdentityId,
              ciphersuite,
              kpRef: rejectedRef,
              keyPackageB64: rejectedKeyPackageB64,
              status: 'consumed',
              keyPackageHealthCode: 'server_rejected_historical_consumed',
              welcomeStageReady: false,
              welcomeStageReason: 'server_rejected_historical_consumed',
            }).catch(() => null);
          }

          this.#clearPublishedKeyPackageMirror({
            identityId: normalizedIdentityId,
            clientId: normalizedClientId,
            ciphersuite,
          });

          const rotated = await workerRotateKeyPackage({
            identityId: normalizedIdentityId,
            ciphersuite: normalizeSuiteId(ciphersuite ?? null),
          });
          const rotatedKeyPackageB64 = pickBase64String(rotated?.keyPackage, null);
          if (!rotatedKeyPackageB64) {
            const rotationError = new Error(
              'MLS key package rotation did not produce a fresh key package',
            );
            rotationError.code = 'MLS_KEY_PACKAGE_ROTATION_FAILED';
            throw rotationError;
          }

          const rotatedCiphersuite = rotated?.ciphersuite ?? ciphersuite ?? null;
          const rotatedRef = await computeKeyPackageRefHexFromB64(
            rotatedKeyPackageB64,
            rotatedCiphersuite,
          ).catch(() => null);
          if (rejectedRef && rotatedRef === rejectedRef) {
            const rotationError = new Error(
              'MLS key package rotation returned the rejected historical package',
            );
            rotationError.code = 'MLS_KEY_PACKAGE_ROTATION_NOT_FRESH';
            throw rotationError;
          }

          debugLog('[MLS] Replacing server-rejected historical key package', {
            identityId: normalizedIdentityId,
            clientId: normalizedClientId,
            ciphersuite: rotatedCiphersuite,
            rejectedRef,
            replacementRef: rotatedRef,
          });

          return this.#publishKeyPackageWithRetries({
            identityId: normalizedIdentityId,
            clientId: normalizedClientId,
            ciphersuite: rotatedCiphersuite,
            payload: {
              ...payloadTemplate,
              keyPackageB64: rotatedKeyPackageB64,
            },
            computedRef: rotatedRef,
            force,
            allowConsumedRecovery: false,
          });
        }

        if (this.#isClientIdMismatchError(error)) {
          const recovery = await this.#recoverFromClientIdMismatch({
            ciphersuite,
            context: 'publish_key_package',
            error,
          });
          normalizedIdentityId = recovery.identityId;
          normalizedClientId = this.#normalizeIdentityId(recovery.identity?.clientId ?? null);
          cacheTargetId = normalizedClientId ?? normalizedIdentityId;
          try {
            const exported = await this.exportKeyPackage(normalizedIdentityId);
            if (exported?.keyPackage) {
              payloadTemplate = { ...payloadTemplate, keyPackage: exported.keyPackage };
              computedRefValue = await computeKeyPackageRefHexFromB64(exported.keyPackage).catch(
                () => null,
              );
            }
          } catch {}
          lastError = error;
          continue;
        }

        if (error?.status === 409) {
          const now = Date.now();
          const metadata = extractKeyPackageMetadata(error?.payload) || {};
          const resolvedMetadata = { ...metadata };
          if (resolvedMetadata.ciphersuite == null && ciphersuite != null) {
            resolvedMetadata.ciphersuite = ciphersuite;
          }
          const cacheIdentity =
            resolvedMetadata.clientId ||
            resolvedMetadata.identityId ||
            cacheTargetId ||
            normalizedIdentityId;
          const cacheSuite = resolvedMetadata.ciphersuite ?? ciphersuite;
          const keyPackageB64ForRef = pickBase64String(
            resolvedMetadata.keyPackageB64,
            payloadTemplate?.keyPackage,
            payloadTemplate?.key_package_b64,
            null,
          );
          let resolvedRef = normalizeHexRefValue(resolvedMetadata.ref ?? computedRefValue ?? null);
          if (!resolvedRef && keyPackageB64ForRef) {
            resolvedRef = await computeKeyPackageRefHexFromB64(
              keyPackageB64ForRef,
              cacheSuite,
            ).catch(() => null);
          }
          const cacheEntry = this.#updateKeyPackageCache(cacheIdentity, cacheSuite, {
            status: 'fresh',
            ttlMs: resolvedMetadata.ttlMs ?? undefined,
            expiresAt: resolvedMetadata.expiresAt ?? undefined,
            deferredUntil: null,
            lastPublishedAt: now,
            ref: resolvedRef ?? computedRefValue ?? undefined,
          });
          await this.#trackKeyPackageByRef({
            kpRef: resolvedRef ?? cacheEntry?.ref ?? null,
            identityId: resolvedMetadata.identityId ?? normalizedIdentityId ?? null,
            clientId: resolvedMetadata.clientId ?? cacheIdentity ?? null,
            ciphersuite: cacheSuite,
            keyPackageB64: keyPackageB64ForRef ?? null,
            status: 'unused',
          });
          await this.#applyKeyPackageMetadataToIdentity(resolvedMetadata, {
            identityId: normalizedIdentityId,
          });
          debugLog('[MLS] Key package already published for identity', {
            identityId: cacheIdentity,
          });
          const resolvedSuite = resolvedMetadata.ciphersuite ?? ciphersuite ?? null;
          return {
            skipped: true,
            alreadyExists: true,
            identityId: cacheIdentity,
            clientId: resolvedMetadata.clientId ?? null,
            ciphersuite: resolvedSuite,
            cacheEntry,
          };
        }

        if (error?.status === 429 || (error?.status && error.status >= 500)) {
          lastError = error;
          continue;
        }

        lastError = error;
        break;
      }
    }

    if (lastError?.status === 429) {
      const deferredUntil = Date.now() + KEY_PACKAGE_DEFER_DURATION_MS;
      const cacheEntry = this.#updateKeyPackageCache(cacheTargetId, ciphersuite, {
        status: 'deferred',
        deferredUntil,
      });
      debugLog('[MLS] Deferring key package publish after rate limit', {
        identityId: cacheTargetId,
        ciphersuite,
        deferredUntil,
      });
      return {
        deferred: true,
        identityId: cacheTargetId,
        clientId: normalizedClientId ?? null,
        ciphersuite,
        retryAt: deferredUntil,
        cacheEntry,
        error: lastError,
      };
    }

    if (lastError) {
      throw lastError;
    }

    return {
      ok: false,
      identityId: cacheTargetId,
      clientId: normalizedClientId ?? null,
      ciphersuite,
    };
  }

  #updateRuntimeSuiteMetadata({ suites, suiteLabels, provider } = {}) {
    const entries = Array.isArray(suites)
      ? suites
          .map((value, index) => ({
            suite: normalizeSuiteId(value),
            label: Array.isArray(suiteLabels) ? suiteLabels[index] : undefined,
          }))
          .filter((entry) => entry.suite != null)
      : [];

    this.runtimeSupportedCiphersuites = entries.map((entry) => entry.suite);
    this.runtimeSupportedSuiteLabels = entries.map((entry) => {
      if (typeof entry.label === 'string' && entry.label.trim()) {
        return entry.label.trim();
      }
      const fallback = describeCiphersuite(entry.suite);
      return typeof fallback === 'string' && fallback ? fallback : null;
    });

    if (typeof provider === 'string' && provider.trim()) {
      this.runtimeProvider = provider.trim();
    } else if (!this.runtimeProvider) {
      this.runtimeProvider = DEFAULT_MLS_PROVIDER;
    }
  }

  #resolveSuiteLabel(value) {
    const numeric = normalizeSuiteId(value);
    if (numeric == null) {
      return null;
    }

    const index = Array.isArray(this.runtimeSupportedCiphersuites)
      ? this.runtimeSupportedCiphersuites.indexOf(numeric)
      : -1;

    if (index >= 0) {
      const label = this.runtimeSupportedSuiteLabels?.[index];
      if (typeof label === 'string' && label.trim()) {
        return label.trim();
      }
    }

    const fallback = describeCiphersuite(numeric);
    return typeof fallback === 'string' && fallback ? fallback : null;
  }

  async #singleFlightWithBackoff(map, retryMap, key, factory, options = {}) {
    const normalizedKey = key ?? '__default__';
    if (map.has(normalizedKey)) {
      return map.get(normalizedKey);
    }

    const runner = async () => {
      const retryState = retryMap.get(normalizedKey);
      const waitUntil = retryState?.nextAttemptAt ?? 0;
      const now = Date.now();
      if (waitUntil > now) {
        await sleep(waitUntil - now);
      }

      try {
        const result = await factory();
        retryMap.delete(normalizedKey);
        return result;
      } catch (error) {
        const previous = retryMap.get(normalizedKey) || { attempts: 0 };
        const attempts = previous.attempts + 1;
        const baseDelay = options.baseDelay ?? 500;
        const maxDelay = options.maxDelay ?? 10000;
        const jitterRatio = options.jitter ?? 0.25;
        let delay = baseDelay * 2 ** (attempts - 1);
        if (delay > maxDelay) {
          delay = maxDelay;
        }
        if (delay > 0 && jitterRatio > 0) {
          const jitterAmount = delay * jitterRatio;
          const min = Math.max(0, delay - jitterAmount);
          const max = delay + jitterAmount;
          delay = Math.round(min + Math.random() * (max - min));
        }
        retryMap.set(normalizedKey, {
          attempts,
          nextAttemptAt: Date.now() + delay,
        });
        throw error;
      }
    };

    const pending = runner();
    map.set(normalizedKey, pending);
    return pending.finally(() => {
      if (map.get(normalizedKey) === pending) {
        map.delete(normalizedKey);
      }
    });
  }

  // Prevents key package republish/ref churn while a join is in-flight for a given identity
  #keyPackageLocks = new Set();
  #keyPackageLockQueues = new Map();

  #isKeyPackageLocked(identityId) {
    const id = identityId != null ? String(identityId) : null;
    return id ? this.#keyPackageLocks.has(id) || this.#keyPackageLockQueues.has(id) : false;
  }

  async withKeyPackageLock(identityId, fn) {
    const id = identityId != null ? String(identityId) : null;
    if (!id || typeof fn !== 'function') {
      return typeof fn === 'function' ? fn() : undefined;
    }

    const previous = this.#keyPackageLockQueues.get(id) || Promise.resolve();
    let releaseCurrent;
    const current = new Promise((resolve) => {
      releaseCurrent = resolve;
    });
    this.#keyPackageLockQueues.set(
      id,
      previous.then(
        () => current,
        () => current,
      ),
    );

    await previous;
    this.#keyPackageLocks.add(id);
    try {
      return await fn();
    } finally {
      this.#keyPackageLocks.delete(id);
      try {
        releaseCurrent?.();
      } catch {}
      if (this.#keyPackageLockQueues.get(id) === current) {
        this.#keyPackageLockQueues.delete(id);
      }
    }
  }

  #scheduleServerGroupRetry({ conversationId, groupId, allowCreate = true, key } = {}) {
    const resolvedKey =
      key ||
      (conversationId != null
        ? `server:conversation:${conversationId}`
        : groupId != null
          ? `server:group:${groupId}`
          : null);
    if (!resolvedKey || typeof setTimeout !== 'function') {
      return;
    }

    const normalizedKey = String(resolvedKey);
    const attempt = (this.groupEnsureRetryBackoff.get(normalizedKey) || 0) + 1;
    this.groupEnsureRetryBackoff.set(normalizedKey, attempt);

    const baseDelay = 1000;
    const maxDelay = 30000;
    const delay = Math.min(maxDelay, baseDelay * 2 ** (attempt - 1));

    if (this.groupEnsureRetryTimers.has(normalizedKey)) {
      const existingTimer = this.groupEnsureRetryTimers.get(normalizedKey);
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(async () => {
      this.groupEnsureRetryTimers.delete(normalizedKey);
      try {
        await this.ensureServerGroup({ conversationId, groupId, allowCreate });
      } catch (error) {
        if (error?.code !== 'abort_bootstrap_missing_ratchet_tree') {
          console.warn('[MLS] ensureServerGroup retry failed', {
            conversationId,
            groupId,
            error,
          });
        }
      }
    }, delay);

    this.groupEnsureRetryTimers.set(normalizedKey, timer);
  }

  async #maybeProbeBootstrap({ conversationId, groupId, identityId, ciphersuite, epoch } = {}) {
    // If join repair is in-flight, skip probing to avoid flipping refs mid-join
    try {
      if (this._suppressPublishUntil && Date.now() < this._suppressPublishUntil) {
        if (typeof console?.debug === 'function') {
          console.debug('[MLS] Skipping bootstrap probe during join repair window');
        }
        return { skipped: true, reason: 'join_repair' };
      }
    } catch {}
    const normalizedConversationId = ensureString(conversationId);
    const normalizedGroupId = ensureString(groupId);
    const normalizedCiphersuite =
      ciphersuite == null
        ? null
        : (normalizeSuiteId(ciphersuite) ?? (typeof ciphersuite === 'string' ? ciphersuite : null));
    const normalizedEpoch = epoch == null ? null : ensureEpoch(epoch);

    if (!normalizedConversationId && !normalizedGroupId) {
      return { skipped: true };
    }

    if (!normalizedGroupId) {
      return { skipped: true, reason: 'missing_group' };
    }

    let runtimeIdentityId = ensureString(identityId);
    let ensuredIdentity = null;
    let senderClientId = null;
    let mintedClientId = null;

    try {
      ensuredIdentity = await this.ensureIdentity({});
    } catch (error) {
      if (!runtimeIdentityId) {
        console.warn('[MLS] Failed to resolve identity for bootstrap probe', error);
        return { failed: error };
      }
    }

    if (!runtimeIdentityId) {
      runtimeIdentityId = ensureString(ensuredIdentity?.identityId ?? this.identityId);
    }

    const resolveActiveClientId = () => {
      const suiteCandidate =
        normalizeSuiteId(normalizedCiphersuite) ??
        normalizeSuiteId(ensuredIdentity?.ciphersuite ?? null) ??
        null;

      if (mintedClientId) {
        const normalized = ensureString(mintedClientId);
        if (normalized) {
          return normalized;
        }
      }

      if (typeof this.getActiveMlsClientId === 'function') {
        const helper = this.getActiveMlsClientId(suiteCandidate ?? undefined);
        const normalized = ensureString(helper);
        if (normalized) {
          return normalized;
        }
      }

      if (suiteCandidate != null) {
        const cached = ensureString(this.#getClientIdForSuite(suiteCandidate));
        if (cached) {
          return cached;
        }

        if (ensuredIdentity?.clientIds && ensuredIdentity.clientIds[String(suiteCandidate)]) {
          const mapped = ensureString(ensuredIdentity.clientIds[String(suiteCandidate)]);
          if (mapped) {
            return mapped;
          }
        }
      }

      if (this.identityClientId) {
        const normalized = ensureString(this.identityClientId);
        if (normalized) {
          return normalized;
        }
      }

      return null;
    };

    senderClientId = resolveActiveClientId();

    if (!runtimeIdentityId) {
      return { skipped: true, reason: 'missing_identity' };
    }

    let publishedClientId = null;
    // Only publish if we genuinely lack a sender client id; avoid flipping ref before join
    if (!senderClientId) {
      try {
        const publishResult = await this.publishKeyPackageAndSync(runtimeIdentityId);
        mintedClientId = ensureString(publishResult?.clientId ?? null);
        publishedClientId = mintedClientId;
      } catch (publishError) {
        console.warn('[MLS] Failed to publish key package before bootstrap probe', publishError);
      }
    }

    if (!senderClientId && publishedClientId) {
      senderClientId = publishedClientId;
    }

    if (!senderClientId) {
      senderClientId = resolveActiveClientId();
    }

    if (!senderClientId) {
      if (typeof console?.error === 'function') {
        try {
          console.error('[MLS] Missing minted client_id for suite; aborting send/probe', {
            suite: normalizeSuiteId(normalizedCiphersuite) ?? null,
            identityId: runtimeIdentityId ?? null,
            conversationId: normalizedConversationId ?? null,
            groupId: normalizedGroupId ?? null,
          });
        } catch {}
      }
      return { skipped: true, reason: 'missing_client_id' };
    }

    const effectiveSenderClientId = senderClientId;

    const now = Date.now();
    const keys = [];
    if (normalizedConversationId) {
      keys.push(`conv:${normalizedConversationId}`);
    }
    if (normalizedGroupId) {
      keys.push(`group:${normalizedGroupId}`);
    }

    const inflightKey = normalizedGroupId ? `group:${normalizedGroupId}` : (keys[0] ?? null);

    for (const key of keys) {
      const last = this.#bootstrapProbeDebounce.get(key) || 0;
      if (now - last < BOOTSTRAP_PROBE_DEBOUNCE_MS) {
        return { skipped: true, reason: 'debounced' };
      }
    }

    if (inflightKey && this.#bootstrapProbeInflight.has(inflightKey)) {
      return { skipped: true, reason: 'inflight' };
    }

    if (inflightKey) {
      this.#bootstrapProbeInflight.add(inflightKey);
    }

    try {
      if (typeof console?.debug === 'function') {
        try {
          console.debug('[MLS] Probing server bootstrap for pending welcomes', {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
          });
        } catch {}
      }

      const generateClientMsgId = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          try {
            return crypto.randomUUID();
          } catch {}
        }
        return `probe-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
      };

      let probeResult = null;
      let probeError = null;

      const probeClientMsgId = generateClientMsgId();

      let probeEnvelopeB64 = null;
      const probeIssuedAt = Date.now();
      if (typeof this.buildBootstrapProbeEnvelope === 'function') {
        try {
          const builtEnvelope = await this.buildBootstrapProbeEnvelope({
            identityId: runtimeIdentityId ?? undefined,
            conversationId: normalizedConversationId ?? undefined,
            groupId: normalizedGroupId,
            senderClientId: effectiveSenderClientId,
            clientMsgId: probeClientMsgId,
            ciphersuite: normalizedCiphersuite ?? null,
            epoch: normalizedEpoch ?? undefined,
            timestamp: probeIssuedAt,
          });
          probeEnvelopeB64 = coerceProbeEnvelopeB64(builtEnvelope);
        } catch (error) {
          console.warn('[MLS] Failed to build bootstrap probe envelope', {
            conversationId: normalizedConversationId ?? null,
            groupId: normalizedGroupId,
            error,
          });
        }
      }

      if (!probeEnvelopeB64) {
        if (typeof console?.warn === 'function') {
          try {
            console.warn('[MLS] Skipping bootstrap probe; MLS envelope unavailable', {
              conversationId: normalizedConversationId ?? null,
              groupId: normalizedGroupId,
            });
          } catch {}
        }
        return { skipped: true, reason: 'missing_probe_envelope' };
      }

      try {
        probeResult = await probeMlsBootstrap({
          groupId: normalizedGroupId,
          senderClientId: effectiveSenderClientId,
          clientMsgId: probeClientMsgId,
          ciphersuite: normalizedCiphersuite ?? null,
          epoch: normalizedEpoch ?? 0,
          envelopeB64: probeEnvelopeB64,
        });
      } catch (error) {
        probeError = error;
        if (typeof console !== 'undefined' && typeof console.info === 'function') {
          try {
            debugLog('[MLS] bootstrap probe non-2xx; proceeding to refetch', {
              status: error?.status ?? error?.response?.status ?? null,
              text: error?.responseText ?? null,
            });
          } catch {}
        }
      }

      const timestamp = Date.now();
      for (const key of keys) {
        this.#bootstrapProbeDebounce.set(key, timestamp);
      }

      let refreshedSnapshot = null;
      if (normalizedConversationId) {
        try {
          const idObj = await this.ensureIdentity({});
          const suiteCandidate = normalizeSuiteId(
            normalizedCiphersuite ?? idObj?.ciphersuite ?? this.preferredCiphersuite ?? null,
          );
          const activeClientId =
            this.identityClientId ||
            (suiteCandidate != null ? this.#getClientIdForSuite(suiteCandidate) : null) ||
            idObj?.clientId ||
            null;
          refreshedSnapshot = await getGroupSnapshot({
            conversationId: normalizedConversationId,
            force: true,
            clientId: activeClientId || undefined,
          });
        } catch (error) {
          console.warn('[MLS] Failed to refresh MLS snapshot after bootstrap probe', {
            conversationId: normalizedConversationId,
            error,
          });
        }

        try {
          await this.syncWelcomes({
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
          });
        } catch (error) {
          console.warn('[MLS] Failed to sync welcomes after bootstrap probe', {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            error,
          });
        }
      } else if (normalizedGroupId) {
        try {
          await this.syncWelcomes({ groupId: normalizedGroupId });
        } catch (error) {
          console.warn('[MLS] Failed to sync welcomes after bootstrap probe', {
            groupId: normalizedGroupId,
            error,
          });
        }
      }

      if (probeResult?.busy) {
        if (typeof console?.debug === 'function') {
          try {
            console.debug('[MLS] Bootstrap probe deferred; backend busy', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
            });
          } catch {}
        }
        return { busy: true, snapshot: refreshedSnapshot, error: probeResult.error ?? null };
      }

      if (probeResult?.pending) {
        if (typeof console?.debug === 'function') {
          try {
            console.debug('[MLS] Bootstrap probe pending; awaiting welcomes', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
            });
          } catch {}
        }
        return {
          pending: true,
          invalidProbe: Boolean(probeResult.invalidProbe),
          snapshot: refreshedSnapshot,
          error: probeResult.error ?? null,
        };
      }

      if (probeError) {
        console.warn('[MLS] Bootstrap probe failed', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          error: probeError,
        });
        return { failed: probeError, snapshot: refreshedSnapshot };
      }

      return { ok: Boolean(probeResult?.ok ?? true), snapshot: refreshedSnapshot };
    } finally {
      if (inflightKey) {
        this.#bootstrapProbeInflight.delete(inflightKey);
      }
    }
  }

  #resolvePeerClientTargets(conversation, { activeIdentityId } = {}) {
    if (!conversation) return [];

    const candidates = [];
    const selfUserId = this.userId == null ? null : String(this.userId);

    const appendCandidate = (userId, clientId) => {
      const normalizedClientId = coerceClientId(clientId);
      if (!normalizedClientId) return;
      if (activeIdentityId && normalizedClientId === coerceClientId(activeIdentityId)) return;
      candidates.push({
        userId: userId == null ? null : String(userId),
        clientId: normalizedClientId,
      });
    };

    const participantList = Array.isArray(conversation.participants)
      ? conversation.participants
      : [];

    participantList.forEach((participant) => {
      if (!participant) return;
      const participantUserId =
        participant.id != null
          ? String(participant.id)
          : participant.user_id != null
            ? String(participant.user_id)
            : participant.userId != null
              ? String(participant.userId)
              : null;

      if (participantUserId && selfUserId && participantUserId === selfUserId) {
        return;
      }

      const directFields = [
        participant.mls_client_id,
        participant.mlsClientId,
        participant.mls_client,
        participant.mlsClient,
        participant.mls_identity_id,
        participant.mlsIdentityId,
      ];

      directFields.forEach((value) => {
        const normalized = coerceClientId(value);
        if (normalized) {
          appendCandidate(participantUserId, normalized);
        }
      });

      const listFields = [
        participant.mls_client_ids,
        participant.mlsClientIds,
        participant.mls_clients,
        participant.mlsClients,
      ];
      listFields.forEach((list) => {
        gatherClientIds(list).forEach((value) => appendCandidate(participantUserId, value));
      });
    });

    const meta = conversation.meta || {};
    const metaCandidates = [
      meta.mls_peer_client_id,
      meta.mlsPeerClientId,
      meta.mls_client_id,
      meta.mlsClientId,
    ];
    metaCandidates.forEach((value) => appendCandidate(meta?.peer_user_id ?? null, value));

    const metaListCandidates = [
      meta.mls_peer_client_ids,
      meta.mlsPeerClientIds,
      meta.mls_client_ids,
      meta.mlsClientIds,
    ];
    metaListCandidates.forEach((list) => {
      gatherClientIds(list).forEach((value) => appendCandidate(meta?.peer_user_id ?? null, value));
    });

    if (conversation.mls_client_id || conversation.mlsClientId) {
      appendCandidate(
        conversation.peer_user_id ?? conversation.peerUserId ?? null,
        conversation.mls_client_id || conversation.mlsClientId,
      );
    }

    const seen = new Set();
    return candidates.filter((candidate) => {
      if (!candidate.clientId) return false;
      const key = `${candidate.userId || 'unknown'}:${candidate.clientId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async #bootstrapLegacyConversation({ conversationId, conversation, identityId }) {
    const normalizedConversationId = normalizeUserId(conversationId);
    if (!normalizedConversationId) {
      return null;
    }

    const memberIds = collectConversationMemberIds(conversation, this.userId);

    let createdSnapshot = null;
    let createdLocalGroup = null;
    let resolvedRatchetTree = null;

    if (identityId) {
      try {
        createdLocalGroup = await createGroup({
          identityId,
          groupId: normalizedConversationId,
          ciphersuite: this.preferredCiphersuite,
        });
        resolvedRatchetTree =
          createdLocalGroup?.ratchetTree ??
          createdLocalGroup?.ratchet_tree_b64 ??
          createdLocalGroup?.ratchetTreeB64 ??
          null;
      } catch (error) {
        console.warn('[MLS] Failed to create local worker group before legacy bootstrap', {
          conversationId: normalizedConversationId,
          error,
        });
      }
    }

    const createPayload = {
      conversationId: normalizedConversationId,
      threadId: normalizedConversationId,
      members: memberIds,
    };

    if (createdLocalGroup?.groupId) {
      createPayload.preferredGroupId = createdLocalGroup.groupId;
    }

    if (resolvedRatchetTree) {
      createPayload.ratchetTreeB64 = resolvedRatchetTree;
    }

    if (resolvedRatchetTree) {
      try {
        createdSnapshot = await createMlsGroup(createPayload);
      } catch (error) {
        if (error?.status !== 409) {
          throw error;
        }
      }
    }

    let snapshot = null;
    if (createdSnapshot && typeof createdSnapshot === 'object') {
      snapshot = createdSnapshot.group || createdSnapshot;
    }

    if (!snapshot) {
      try {
        const idObj = await this.ensureIdentity({});
        const suiteCandidate = normalizeSuiteId(
          idObj?.ciphersuite ?? this.preferredCiphersuite ?? null,
        );
        const activeClientId =
          this.identityClientId ||
          (suiteCandidate != null ? this.#getClientIdForSuite(suiteCandidate) : null) ||
          idObj?.clientId ||
          null;
        snapshot = await getGroupSnapshot({
          conversationId: normalizedConversationId,
          clientId: activeClientId || undefined,
        });
      } catch (error) {
        if (error?.status !== 404) {
          throw error;
        }
      }
    }

    const groupId =
      snapshot?.group_id ??
      snapshot?.groupId ??
      createdSnapshot?.group_id ??
      createdSnapshot?.groupId ??
      normalizedConversationId;
    const groupHandle =
      snapshot?.group_handle ??
      snapshot?.groupHandle ??
      createdSnapshot?.group_handle ??
      createdSnapshot?.groupHandle ??
      null;

    if (normalizedConversationId && groupId) {
      try {
        this.conversationGroups.set(normalizedConversationId, groupId);
        persistKnownConversationGroupId(normalizedConversationId, groupId);
      } catch {}
    }

    let entry = null;

    if (snapshot) {
      try {
        entry = await this.#hydrateGroupFromSnapshot(snapshot, {
          fallbackGroupId: groupId,
          fallbackGroupHandle: groupHandle,
          identityId,
          clearRecovery: true,
        });
      } catch (error) {
        console.warn('[MLS] Failed to hydrate group after legacy bootstrap', {
          conversationId: normalizedConversationId,
          groupId,
          groupHandle,
          error,
        });
      }
    }

    if (!entry && groupId) {
      try {
        const groups = await this.#loadGroups();
        const nextGroups = {
          ...groups,
          [groupId]: {
            ...(groups[groupId] || {}),
            groupId,
            groupHandle,
            needsRecovery: true,
            recoveryReason: 'legacy_bootstrap_pending_snapshot',
            ready: false,
          },
        };
        await this.#saveGroups(nextGroups);
      } catch {}
    }

    return {
      snapshot: snapshot || null,
      entry: entry || null,
      groupId: groupId || null,
      groupHandle: groupHandle || null,
    };
  }

  async #fetchPeerKeyPackage({ clientId, ciphersuite } = {}) {
    const normalizedClientId = coerceClientId(clientId);
    if (!normalizedClientId) return null;
    try {
      const response = await fetchLatestMlsKeyPackage({
        clientId: normalizedClientId,
        ciphersuite,
      });
      if (!response) return null;
      const payload = response.key_package || response.keyPackage || response;
      const keyPackageB64 =
        payload?.key_package_b64 ??
        payload?.keyPackageB64 ??
        payload?.key_package ??
        payload?.keyPackage ??
        null;
      if (!keyPackageB64) return null;
      return { keyPackageB64 };
    } catch (error) {
      console.warn('[MLS] Failed to fetch peer key package', {
        clientId: normalizedClientId,
        error,
      });
      return null;
    }
  }

  async #mintPeerWelcome({ conversationId, groupHandle, identityId, createdLocal, ciphersuite }) {
    if (!groupHandle || !identityId) {
      return null;
    }

    const conversation = conversationId ? getKnownMLSConversation(conversationId) : null;
    const targets = this.#resolvePeerClientTargets(conversation, { activeIdentityId: identityId });
    if (!targets.length) {
      return null;
    }

    const suite =
      ciphersuite ??
      createdLocal?.ciphersuite ??
      this.preferredCiphersuite ??
      DEFAULT_MLS_CIPHERSUITE;

    const welcomes = [];
    let ratchetTreeB64 = pickBase64String(createdLocal?.ratchetTreeB64, createdLocal?.ratchetTree);
    let epoch = null;

    for (const target of targets) {
      const fetched = await this.#fetchPeerKeyPackage({
        clientId: target.clientId,
        ciphersuite: suite,
      });
      if (!fetched?.keyPackageB64) {
        continue;
      }

      try {
        const result = await addMemberFromKeyPackageBytes({
          groupHandle,
          identityId,
          keyPackageB64: fetched.keyPackageB64,
        });
        if (!result) {
          continue;
        }

        const resultTreeB64 = pickBase64String(
          result?.ratchetTreeB64,
          result?.ratchet_tree_b64,
          result?.ratchetTree,
          result?.ratchet_tree,
        );
        if (resultTreeB64) {
          ratchetTreeB64 = resultTreeB64;
        }

        const welcomeB64 = result.welcomeB64 ?? result.welcome_b64 ?? null;
        if (!welcomeB64) {
          continue;
        }

        const resolvedEpoch = ensureEpoch(result.epoch);
        if (Number.isFinite(resolvedEpoch)) {
          epoch = resolvedEpoch;
        }

        welcomes.push({
          welcomeB64,
          toClientId: target.clientId,
          toUserId: target.userId,
        });
      } catch (error) {
        console.warn('[MLS] Failed to add peer via key package bytes', {
          clientId: target.clientId,
          error,
        });
      }
    }

    if (welcomes.length === 0) {
      return null;
    }

    return {
      ratchetTreeB64,
      welcomes,
      epoch,
      ciphersuite: suite,
    };
  }

  async createWelcomeForConversation({ conversationId, recipients, ciphersuite } = {}) {
    if (!conversationId) {
      throw new Error('conversationId is required to create MLS welcome');
    }

    const recipientList = Array.isArray(recipients) ? recipients : [];
    if (recipientList.length === 0) {
      return null;
    }

    const identity = await this.ensureIdentity({ ciphersuite });
    const activeIdentityId = identity?.identityId || this.identityId || null;
    if (!activeIdentityId) {
      throw new Error('MLS identity is not initialised');
    }

    let ensured = await this.ensureGroupForConversation(
      {
        conversationId,
        identityId: activeIdentityId,
      },
      {
        __skipRecovery: true,
        __skipWait: true,
      },
    );

    if (ensured && ensured.identityId !== activeIdentityId) {
      ensured = {
        ...ensured,
        identityId: activeIdentityId,
      };
    }

    const ensureRuntimeGroup = async () => {
      let runtimeHandle = ensured?.groupHandle || ensured?.groupId || null;

      let ensuredIdentityId = ensured?.identityId || activeIdentityId;
      if (!ensuredIdentityId) {
        const refreshedIdentity = await this.ensureIdentity({
          ciphersuite: ciphersuite ?? this.preferredCiphersuite,
        });
        ensuredIdentityId = refreshedIdentity?.identityId || this.identityId || null;
      }

      let runtimeErrorCapture = null;

      try {
        const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
        const handle = ensured?.groupHandle || ensured?.groupId || runtimeHandle;
        const hasRuntimeGroup = runtimeGroups.some((entry) => entry?.groupHandle === handle);

        if (!hasRuntimeGroup) {
          let created = null;
          let identityRehydrated = false;

          while (!created) {
            try {
              created = await createGroup({
                identityId: ensuredIdentityId,
                groupId: ensured?.groupId || conversationId,
                ciphersuite: ensured?.ciphersuite ?? ciphersuite ?? this.preferredCiphersuite,
              });
            } catch (createError) {
              const message = String(createError?.message || '').toLowerCase();
              const identityMissing = message.includes('unknown identity');

              if (identityMissing && !identityRehydrated) {
                identityRehydrated = true;
                try {
                  const refreshedIdentity = await this.ensureIdentity({
                    ciphersuite: ensured?.ciphersuite ?? ciphersuite ?? this.preferredCiphersuite,
                  });
                  ensuredIdentityId =
                    refreshedIdentity?.identityId || this.identityId || ensuredIdentityId;
                  continue;
                } catch (identityError) {
                  console.warn('[MLS] Failed to rehydrate identity before creating group', {
                    conversationId,
                    error: identityError,
                  });
                }
              }

              runtimeErrorCapture = createError;
              break;
            }
          }

          if (created?.groupHandle) {
            runtimeHandle = created.groupHandle;
            ensured = {
              ...ensured,
              groupId: created.groupId ?? ensured?.groupId ?? conversationId,
              groupHandle: created.groupHandle,
              ratchetTree: created.ratchetTree ?? ensured?.ratchetTree ?? null,
              ratchetTreeB64: created.ratchetTree ?? ensured?.ratchetTreeB64 ?? null,
              epoch: created.epoch ?? ensured?.epoch ?? null,
              ciphersuite: created.ciphersuite ?? ensured?.ciphersuite ?? this.preferredCiphersuite,
              identityId: ensuredIdentityId,
            };

            try {
              const syncedRuntimeGroup = await this.#syncRuntimeGroupState({
                groupId: ensured.groupId,
                groupHandle: ensured.groupHandle,
                conversationId,
                source: 'createWelcomeForConversation',
                reason: 'runtime_group_created',
                allowSyncWelcomes: false,
                allowJoinBundleRepair: false,
                allowRuntimeRepair: false,
              });
              if (syncedRuntimeGroup?.groupId || syncedRuntimeGroup?.groupHandle) {
                ensured = {
                  ...ensured,
                  ...syncedRuntimeGroup,
                  groupId: syncedRuntimeGroup.groupId ?? ensured.groupId ?? conversationId,
                  groupHandle: syncedRuntimeGroup.groupHandle ?? ensured.groupHandle ?? null,
                };
              }
            } catch (persistError) {
              console.warn('[MLS] Failed to persist runtime group after creation', {
                conversationId,
                error: persistError,
              });
            }
          } else if (runtimeErrorCapture) {
            throw runtimeErrorCapture;
          }
        }
      } catch (runtimeError) {
        console.warn('[MLS] Failed to inspect runtime groups before welcome creation', {
          conversationId,
          error: runtimeError,
        });
      }

      return ensured?.groupHandle || ensured?.groupId || runtimeHandle;
    };

    let groupHandle = await ensureRuntimeGroup();
    if (!groupHandle) {
      groupHandle = ensured?.groupHandle || ensured?.groupId || null;
    }
    if (!groupHandle) {
      throw new Error('MLS group handle unavailable');
    }

    const dedupe = new Set();
    const welcomes = [];
    let ratchetTreeB64 = pickBase64String(ensured?.ratchetTreeB64, ensured?.ratchetTree);
    let epoch = ensureEpoch(ensured?.epoch);

    for (const recipient of recipientList) {
      if (!recipient) continue;

      const targetClientId = coerceClientId(
        recipient.identity_id || recipient.identityId || recipient.client_id || recipient.clientId,
      );
      if (!targetClientId) continue;
      if (targetClientId === coerceClientId(activeIdentityId)) continue;
      if (dedupe.has(targetClientId)) continue;

      const keyPackageB64 =
        recipient.key_package_b64 ||
        recipient.keyPackageB64 ||
        recipient.key_package ||
        recipient.keyPackage ||
        null;
      if (!keyPackageB64) continue;

      let attempts = 0;
      let result = null;
      // retry once if the local worker is missing the group
      while (attempts < 2 && !result) {
        try {
          result = await addMemberFromKeyPackageBytes({
            groupHandle,
            identityId: activeIdentityId,
            keyPackageB64,
          });
        } catch (error) {
          const message = String(error?.message || error);
          const unknownGroup = message.includes('unknown group');
          const missingKeyPackage = message.includes('KeyPackage.from_bytes is not available');
          if (unknownGroup && attempts === 0) {
            groupHandle = await ensureRuntimeGroup();
            attempts += 1;
            continue;
          }

          if (missingKeyPackage && attempts === 0) {
            try {
              await this.#recoverWorkerCapabilities({
                conversationId,
                ciphersuite: ensured?.ciphersuite ?? this.preferredCiphersuite,
              });
            } catch (recoverError) {
              console.warn('[MLS] Failed to recover worker capabilities before welcome creation', {
                conversationId,
                error: recoverError,
              });
            }
            groupHandle = await ensureRuntimeGroup();
            attempts += 1;
            continue;
          }

          console.warn('[MLS] Failed to create welcome for recipient', {
            conversationId,
            clientId: targetClientId,
            error,
          });
          break;
        }
      }

      if (!result) {
        continue;
      }

      const resultTreeB64 = pickBase64String(
        result?.ratchetTreeB64,
        result?.ratchet_tree_b64,
        result?.ratchetTree,
        result?.ratchet_tree,
      );
      if (resultTreeB64) {
        ratchetTreeB64 = resultTreeB64;
      }

      const welcomeB64 = result.welcomeB64 ?? result.welcome_b64 ?? null;
      if (!welcomeB64) {
        continue;
      }

      const resolvedEpoch = ensureEpoch(result.epoch);
      if (Number.isFinite(resolvedEpoch)) {
        epoch = resolvedEpoch;
      }

      try {
        const updatedRuntimeState = {
          ...ensured,
          groupId: ensured?.groupId ?? conversationId,
          groupHandle,
          ratchetTreeB64: ratchetTreeB64 ?? ensured?.ratchetTreeB64 ?? null,
          ratchetTree: ratchetTreeB64 ?? ensured?.ratchetTree ?? null,
          epoch: Number.isFinite(resolvedEpoch) ? resolvedEpoch : (ensured?.epoch ?? null),
          current_epoch: Number.isFinite(resolvedEpoch) ? resolvedEpoch : (ensured?.epoch ?? null),
          runtimeEpoch: Number.isFinite(resolvedEpoch)
            ? resolvedEpoch
            : normalizeEpochOrNull(ensured?.runtimeEpoch ?? ensured?.epoch ?? null),
          joined: true,
          needsRecovery: false,
          recoveryReason: null,
          pending_welcomes: [],
          pendingWelcomes: [],
        };
        ensured = await this.#updateGroupEntry(
          ensured?.groupId ?? conversationId,
          (prev) => this.#finalizeGroupReady({ ...(prev || {}), ...updatedRuntimeState }),
          { createIfMissing: true },
        );
        const syncedRuntimeGroup = await this.#syncRuntimeGroupState({
          groupId: ensured?.groupId ?? conversationId,
          groupHandle,
          conversationId,
          source: 'createWelcomeForConversation',
          reason: 'post_add_member_commit',
          allowSyncWelcomes: false,
          allowJoinBundleRepair: false,
          allowRuntimeRepair: false,
        }).catch(() => null);
        if (syncedRuntimeGroup?.groupId || syncedRuntimeGroup?.groupHandle) {
          ensured = {
            ...ensured,
            ...syncedRuntimeGroup,
            groupId: syncedRuntimeGroup.groupId ?? ensured?.groupId ?? conversationId,
            groupHandle: syncedRuntimeGroup.groupHandle ?? ensured?.groupHandle ?? groupHandle,
          };
        }
      } catch (persistAddMemberError) {
        console.warn('[MLS] Failed to persist runtime state after add-member welcome creation', {
          conversationId,
          groupId: ensured?.groupId ?? conversationId,
          error: persistAddMemberError,
        });
      }

      welcomes.push({
        welcomeB64,
        toClientId: targetClientId,
        toUserId:
          (recipient.user_id != null ? String(recipient.user_id) : null) ||
          (recipient.userId != null ? String(recipient.userId) : null),
      });
      dedupe.add(targetClientId);
    }

    if (welcomes.length === 0) {
      return null;
    }

    return {
      welcomes,
      ratchetTreeB64,
      epoch,
      ciphersuite: ciphersuite ?? ensured?.ciphersuite ?? this.preferredCiphersuite,
    };
  }

  async #recoverWorkerCapabilities({ conversationId, ciphersuite } = {}) {
    if (this.workerRecoveryPromise) {
      return this.workerRecoveryPromise;
    }

    this.workerRecoveryPromise = (async () => {
      const targetSuite =
        normalizeSuiteId(ciphersuite) ?? this.preferredCiphersuite ?? HYBRID_MLS_CIPHERSUITE;

      try {
        shutdownMlsWorker();
      } catch (error) {
        console.warn('[MLS] Failed to shutdown worker during recovery', error);
      }

      this.initPromise = null;
      this.identityPromise = null;

      try {
        await this.ensureInitialized();
      } catch (error) {
        console.warn('[MLS] Failed to reinitialize MLS runtime during recovery', error);
        throw error;
      }

      try {
        await this.ensureIdentity({ ciphersuite: targetSuite });
      } catch (error) {
        console.warn('[MLS] Failed to ensure identity during worker recovery', error);
        throw error;
      }

      try {
        await this.#warmRuntimeGroups();
      } catch (error) {
        console.warn('[MLS] Failed to warm runtime groups after worker recovery', {
          conversationId,
          error,
        });
      }
    })()
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        this.workerRecoveryPromise = null;
      });

    return this.workerRecoveryPromise;
  }

  #resetServerGroupRetry(keys = []) {
    const list = Array.isArray(keys) ? keys : [keys];
    for (const rawKey of list) {
      if (!rawKey) continue;
      const key = String(rawKey);
      if (this.groupEnsureRetryTimers.has(key)) {
        clearTimeout(this.groupEnsureRetryTimers.get(key));
        this.groupEnsureRetryTimers.delete(key);
      }
      if (this.groupEnsureRetryBackoff.has(key)) {
        this.groupEnsureRetryBackoff.delete(key);
      }
    }
  }

  #resolveGroupPipelineKey(groupId, groupHandle = null) {
    return ensureString(groupId) ?? ensureString(groupHandle) ?? null;
  }

  async #runGroupPipeline({ groupId, groupHandle = null, stage = 'unknown' } = {}, runner) {
    if (typeof runner !== 'function') {
      return null;
    }

    const key = this.#resolveGroupPipelineKey(groupId, groupHandle);
    if (!key) {
      return runner();
    }

    const previousTail = this.groupPipelineTails.get(key) || Promise.resolve();
    const hadQueue = this.groupPipelineTails.has(key);
    const waitStartedAt = Date.now();
    // Strict serialization: always wait for prior task to settle before running next.
    // Stage-level soft/hard timeout handling below is enough to unblock stale runners.
    if (hadQueue) {
      await previousTail.catch(() => null);
    }
    const waitedMs = Date.now() - waitStartedAt;
    if (hadQueue && typeof console?.debug === 'function') {
      console.debug(`[MLS] queued group pipeline task stage=${stage}`, {
        groupId: key,
        stage,
        waitedMs,
      });
    }

    let resolveCurrentTail;
    const currentTail = new Promise((resolve) => {
      resolveCurrentTail = resolve;
    });
    this.groupPipelineTails.set(key, currentTail);

    let timeoutId = null;
    let didTimeout = false;
    let tailReleased = false;
    const runnerPromise = Promise.resolve().then(() => runner());
    const releaseTail = ({ reason = 'runner_settled' } = {}) => {
      if (tailReleased) return;
      tailReleased = true;
      try {
        resolveCurrentTail();
      } catch {}
      if (this.groupPipelineTails.get(key) === currentTail) {
        this.groupPipelineTails.delete(key);
      }
    };
    runnerPromise.catch((error) => {
      if (!didTimeout) return;
      if (typeof console?.warn === 'function') {
        console.warn(`[MLS] group pipeline task rejected after timeout stage=${stage}`, {
          groupId: key,
          stage,
          error,
        });
      }
    });

    try {
      const timeoutMs =
        Number(GROUP_PIPELINE_STAGE_TIMEOUT_MS) > 0 ? Number(GROUP_PIPELINE_STAGE_TIMEOUT_MS) : 0;
      if (timeoutMs) {
        timeoutId = setTimeout(() => {
          didTimeout = true;
          if (typeof console?.warn === 'function') {
            console.warn(
              `[MLS] group pipeline task exceeded soft timeout while stage=${stage}; still waiting (strict)`,
              {
                groupId: key,
                stage,
                timeoutMs,
              },
            );
          }
        }, timeoutMs);
      }
      const result = await runnerPromise;
      if (didTimeout && typeof console?.info === 'function') {
        console.info(
          `[MLS] group pipeline task settled after soft-timeout warning stage=${stage}`,
          {
            groupId: key,
            stage,
            timeoutMs,
          },
        );
      }
      return result;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      releaseTail({ reason: didTimeout ? 'runner_settled_after_timeout' : 'runner_settled' });
    }
  }

  #isGroupReady(info) {
    if (!info) return false;
    if (info.needsRecovery) {
      return false;
    }
    if (info?.runtimeLive === false || info?.runtime_live === false) {
      return false;
    }
    if (Boolean(info?.pending_setup ?? info?.pendingSetup)) {
      return false;
    }
    const epoch = Number(info.epoch);
    if (!Number.isFinite(epoch) || epoch <= 0) {
      return false;
    }
    const runtimeEpoch = Number(
      info.runtimeEpoch ?? info.runtime_epoch ?? info.runtime_epoch_no ?? null,
    );
    const runtimeHandle = resolveRuntimeHandle(info);
    if (!runtimeHandle) {
      return false;
    }
    if (!Number.isFinite(runtimeEpoch) || runtimeEpoch <= 0) {
      return false;
    }
    if (runtimeEpoch < epoch) {
      return false;
    }

    const fetchedAtMs = coerceTimestampMs(info.fetchedAt ?? info.fetched_at ?? null);
    if (Number.isFinite(fetchedAtMs)) {
      const ageMs = Date.now() - fetchedAtMs;
      if (ageMs > GROUP_SNAPSHOT_STALE_AFTER_MS) {
        return false;
      }
    } else if (info.joined === true) {
      // Persisted entries without a fetch timestamp should be treated as stale to force refresh.
      return false;
    }

    const candidates = new Set();
    const addCandidate = (value) => {
      if (value == null) return;
      try {
        const normalized = String(value).trim();
        if (normalized) {
          candidates.add(normalized);
        }
      } catch {}
    };

    addCandidate(this.identityClientId);
    addCandidate(this.identityId);
    addCandidate(info.identityClientId);
    addCandidate(info.identity_id);
    addCandidate(info.clientId);
    addCandidate(info.client_id);
    addCandidate(info.identityId);

    if (!candidates.size) {
      return false;
    }

    const members = normalizeMemberList(info.members);
    // Some server refresh snapshots are intentionally sparse and omit members.
    // Do not force a not-ready loop when runtime state is otherwise healthy.
    if (!members.length) {
      return true;
    }
    return members.some((member) => {
      const normalized = member != null ? String(member).trim() : null;
      return normalized ? candidates.has(normalized) : false;
    });
  }

  #decorateGroupEntry(entry, { stampSyncAt = false } = {}) {
    if (!entry || typeof entry !== 'object') {
      return entry;
    }
    const membershipState = this.#resolveEntryMembershipState(entry);
    const enrichedEntry = {
      ...entry,
      currentIdentityMember: membershipState.currentIdentityMember,
      current_identity_member: membershipState.currentIdentityMember,
      currentUserMissing: membershipState.currentUserMissing,
      current_user_missing: membershipState.currentUserMissing,
      pendingSetup: membershipState.pendingSetup,
      pending_setup: membershipState.pendingSetup,
      runtimeBootstrapRequired: membershipState.runtimeBootstrapRequired,
      runtime_bootstrap_required: membershipState.runtimeBootstrapRequired,
      welcomeRequired: membershipState.welcomeRequired,
      welcome_required: membershipState.welcomeRequired,
    };
    const ready = this.#isGroupReady(enrichedEntry);
    const derived = deriveGroupSyncState(enrichedEntry, {
      ready,
      currentIdentityMember: membershipState.currentIdentityMember,
      welcomeRequired: membershipState.welcomeRequired,
      identityIds: membershipState.activeIdentityIds,
    });
    const lastSyncAt = stampSyncAt
      ? Date.now()
      : coerceTimestampMs(entry.lastSyncAt ?? entry.last_sync_at ?? null);
    return {
      ...enrichedEntry,
      targetSnapshotEpoch: derived.targetSnapshotEpoch,
      runtimeEpoch: derived.runtimeEpoch,
      runtimeState: derived.runtimeState,
      syncStatus: derived.syncStatus,
      canonicalState: derived.canonicalState,
      canonical_state: derived.canonicalState,
      syncAction: derived.syncAction,
      sync_action: derived.syncAction,
      epochAligned: derived.epochAligned,
      ready,
      ...(lastSyncAt != null
        ? {
            lastSyncAt,
            last_sync_at: lastSyncAt,
          }
        : {}),
    };
  }

  #classifyRuntimeState(info) {
    if (!info) return 'missing';
    return deriveGroupSyncState(info, { ready: this.#isGroupReady(info) }).runtimeState;
  }

  #getBootstrapWindowUntil(entry) {
    if (!entry || typeof entry !== 'object') return null;
    return coerceTimestampMs(entry.bootstrapWindowUntil ?? entry.bootstrap_window_until ?? null);
  }

  #isBootstrapConvergenceActive(entry, { now = Date.now() } = {}) {
    if (!entry || typeof entry !== 'object') return false;
    const windowUntil = this.#getBootstrapWindowUntil(entry);
    const startedAt = coerceTimestampMs(
      entry.bootstrapStartedAt ?? entry.bootstrap_started_at ?? null,
    );

    let effectiveWindowUntil = windowUntil;
    if (Number.isFinite(startedAt) && startedAt > 0) {
      const cappedWindowUntil = startedAt + BOOTSTRAP_CONVERGENCE_WINDOW_MS;
      effectiveWindowUntil =
        Number.isFinite(windowUntil) && windowUntil > 0
          ? Math.min(windowUntil, cappedWindowUntil)
          : cappedWindowUntil;
    }

    return Number.isFinite(effectiveWindowUntil) && effectiveWindowUntil > now;
  }

  #shouldDeferRecoveryDuringBootstrap(
    entry,
    { source = null, reason = null, pendingWelcomeCount = 0, runtimeHasGroup = false } = {},
  ) {
    if (!entry || typeof entry !== 'object') return false;
    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
    });
    const snapshotEpoch = normalizeEpochOrNull(
      entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
    );
    const entryRuntimeHandle = ensureString(resolveRuntimeHandle(entry));
    const entryRuntimeEpoch = normalizeEpochOrNull(
      entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
    );
    const entryRuntimeLive = Boolean(entry?.runtimeLive ?? entry?.runtime_live ?? false);
    const activeBootstrapWindow = this.#isBootstrapConvergenceActive(entry);
    const hasConcreteRuntimeOwnership = this.#hasConcreteRuntimeOwnership(entry, {
      runtimeHasGroup,
    });
    const authoritativeBootstrapWelcome = this.#hasAuthoritativeBootstrapWelcome(entry, {
      pendingWelcomeCount,
    });

    if (!membershipState.currentIdentityMember) return false;
    if (membershipState.pendingSetup) return false;
    if (!(snapshotEpoch != null && snapshotEpoch > 0)) return false;

    if (membershipState.runtimeBootstrapRequired) {
      return authoritativeBootstrapWelcome;
    }
    if (membershipState.welcomeRequired) return false;

    void source;
    void reason;

    if (pendingWelcomeCount > 0) {
      return true;
    }

    return Boolean(
      activeBootstrapWindow && (authoritativeBootstrapWelcome || hasConcreteRuntimeOwnership),
    );
  }

  async #markBootstrapConvergence(
    groupId,
    { reason = 'bootstrap_convergence', durationMs = BOOTSTRAP_CONVERGENCE_WINDOW_MS } = {},
  ) {
    if (!groupId) return null;
    const updated = await this.#updateGroupEntry(groupId, (prev) => {
      if (!prev) return prev;
      const now = Date.now();
      const previousStartedAt = coerceTimestampMs(
        prev.bootstrapStartedAt ?? prev.bootstrap_started_at ?? null,
      );
      const convergenceDurationMs = Math.max(
        1,
        Number(durationMs) || BOOTSTRAP_CONVERGENCE_WINDOW_MS,
      );
      const nextStartedAt =
        Number.isFinite(previousStartedAt) && previousStartedAt > 0 ? previousStartedAt : now;
      const nextWindowUntil = nextStartedAt + convergenceDurationMs;
      return {
        ...prev,
        bootstrapStartedAt: nextStartedAt,
        bootstrap_started_at: nextStartedAt,
        bootstrapWindowUntil: nextWindowUntil,
        bootstrap_window_until: nextWindowUntil,
        bootstrapReason: ensureString(reason) ?? ensureString(prev.bootstrapReason ?? null) ?? null,
        bootstrap_reason:
          ensureString(reason) ?? ensureString(prev.bootstrap_reason ?? null) ?? null,
      };
    }).catch(() => null);
    if (updated?.groupId || updated?.groupHandle) {
      void this.#scheduleBootstrapRecoveryCheck({
        groupId: ensureString(updated?.groupId ?? groupId ?? null),
        groupHandle: ensureString(updated?.groupHandle ?? null),
        conversationId: ensureString(updated?.conversationId ?? updated?.conversation_id ?? null),
        reason,
      });
    }
    return updated;
  }

  #clearBootstrapRecoveryTimer(keys = []) {
    const candidates = Array.from(
      new Set(
        (Array.isArray(keys) ? keys : [keys]).map((value) => ensureString(value)).filter(Boolean),
      ),
    );
    for (const key of candidates) {
      const timer = this.bootstrapRecoveryTimers.get(key);
      if (timer) {
        clearTimeout(timer);
        this.bootstrapRecoveryTimers.delete(key);
      }
    }
  }

  #resolveRuntimeBootstrapKey({ groupId = null, groupHandle = null, conversationId = null } = {}) {
    return (
      ensureString(groupId) ?? ensureString(groupHandle) ?? ensureString(conversationId) ?? null
    );
  }

  async #scheduleBootstrapRecoveryCheck({
    groupId = null,
    groupHandle = null,
    conversationId = null,
    reason = 'bootstrap_convergence',
  } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);
    const normalizedConversationId = ensureString(conversationId);
    const timerKey = normalizedGroupId ?? normalizedGroupHandle ?? normalizedConversationId ?? null;
    if (!timerKey || typeof setTimeout !== 'function') {
      return null;
    }

    let currentInfo = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
    }).catch(() => null);
    if (!currentInfo || this.#isGroupReady(currentInfo)) {
      this.#clearBootstrapRecoveryTimer([
        timerKey,
        normalizedGroupId,
        normalizedGroupHandle,
        normalizedConversationId,
      ]);
      return currentInfo;
    }

    const windowUntil = this.#getBootstrapWindowUntil(currentInfo);
    if (!(Number.isFinite(windowUntil) && windowUntil > Date.now())) {
      this.#clearBootstrapRecoveryTimer([
        timerKey,
        normalizedGroupId,
        normalizedGroupHandle,
        normalizedConversationId,
      ]);
      return currentInfo;
    }

    this.#clearBootstrapRecoveryTimer([
      timerKey,
      normalizedGroupId,
      normalizedGroupHandle,
      normalizedConversationId,
    ]);

    const delayMs = Math.max(250, windowUntil - Date.now() + 50);
    const timer = setTimeout(async () => {
      this.#clearBootstrapRecoveryTimer([
        timerKey,
        normalizedGroupId,
        normalizedGroupHandle,
        normalizedConversationId,
      ]);
      try {
        await this.#runGroupPipeline(
          {
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            stage: 'bootstrap_recovery_deadline',
          },
          async () => {
            const latestInfo = await this.groupInfo({
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupHandle,
            }).catch(() => null);
            if (!latestInfo || this.#isGroupReady(latestInfo)) {
              return latestInfo;
            }
            if (this.#isBootstrapConvergenceActive(latestInfo)) {
              return latestInfo;
            }

            const membershipState = this.#resolveEntryMembershipState(latestInfo, {
              activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
            });
            const pendingCount = getPendingWelcomesList(latestInfo).length;
            if (
              !membershipState.currentIdentityMember ||
              membershipState.pendingSetup ||
              membershipState.welcomeRequired ||
              pendingCount > 0
            ) {
              return latestInfo;
            }

            const runtimeState =
              ensureString(latestInfo?.runtimeState ?? latestInfo?.runtime_state ?? null) ??
              this.#classifyRuntimeState(latestInfo);
            const recoveryReason =
              runtimeState === 'runtime_behind'
                ? 'runtime_snapshot_mismatch'
                : 'missing_runtime_group';
            const targetGroupId = ensureString(latestInfo?.groupId ?? normalizedGroupId ?? null);
            if (!targetGroupId) {
              return latestInfo;
            }

            if (typeof console?.warn === 'function') {
              console.warn('[MLS] Bootstrap convergence deadline reached; escalating recovery', {
                groupId: targetGroupId,
                groupHandle: ensureString(latestInfo?.groupHandle ?? normalizedGroupHandle ?? null),
                conversationId:
                  ensureString(
                    latestInfo?.conversationId ??
                      latestInfo?.conversation_id ??
                      normalizedConversationId ??
                      null,
                  ) ?? null,
                runtimeState,
                reason,
                recoveryReason,
              });
            }

            await this.markGroupForRecovery(targetGroupId, recoveryReason).catch(() => null);
            return this.recoverGroup({
              groupId: targetGroupId,
              groupHandle: ensureString(latestInfo?.groupHandle ?? normalizedGroupHandle ?? null),
              reason: recoveryReason,
            }).catch(() => latestInfo);
          },
        );
      } catch (error) {
        console.warn('[MLS] Bootstrap recovery deadline task failed', {
          groupId: normalizedGroupId ?? timerKey,
          groupHandle: normalizedGroupHandle ?? null,
          conversationId: normalizedConversationId ?? null,
          reason,
          error,
        });
      }
    }, delayMs);

    this.bootstrapRecoveryTimers.set(timerKey, timer);
    if (normalizedGroupId && normalizedGroupId !== timerKey) {
      this.bootstrapRecoveryTimers.set(normalizedGroupId, timer);
    }
    if (normalizedGroupHandle && normalizedGroupHandle !== timerKey) {
      this.bootstrapRecoveryTimers.set(normalizedGroupHandle, timer);
    }
    if (normalizedConversationId && normalizedConversationId !== timerKey) {
      this.bootstrapRecoveryTimers.set(normalizedConversationId, timer);
    }

    return currentInfo;
  }

  _becameReady(groupId, info) {
    if (!groupId) return false;
    const ready = this.#isGroupReady(info);
    const was = this._readyGroups.has(groupId);
    if (ready) {
      this._readyGroups.add(groupId);
    } else if (was) {
      this._readyGroups.delete(groupId);
    }
    return ready && !was;
  }

  #settleWaitReadyFlights({ groupId = null, groupHandle = null, info = null, error = null } = {}) {
    const aliasKeys = new Set(
      [ensureString(groupId ?? null), ensureString(groupHandle ?? null)].filter(Boolean),
    );
    if (!aliasKeys.size) return;
    const seenFlights = new Set();
    for (const alias of aliasKeys) {
      const flight = this._waitReadyFlights.get(alias);
      if (!flight || seenFlights.has(flight)) continue;
      seenFlights.add(flight);
      try {
        if (error && typeof flight.reject === 'function') {
          flight.reject(error);
        } else if (info && typeof flight.resolve === 'function') {
          flight.resolve(info);
        }
      } catch {}
    }
  }

  #finalizeGroupReady(entry) {
    if (!entry) return entry;
    const groupId = entry.groupId || entry.groupHandle || null;
    if (!groupId) return entry;

    const groupHandle = entry.groupHandle ?? entry.group_handle ?? null;
    const fetchedAt = coerceTimestampMs(entry.fetchedAt ?? entry.fetched_at ?? Date.now());
    const normalizedEntry = this.#decorateGroupEntry(
      {
        ...entry,
        fetchedAt,
      },
      { stampSyncAt: true },
    );
    const ready = Boolean(normalizedEntry.ready);
    if (ready) {
      this.#clearActiveJoinBundleTargets({
        groupId,
        groupHandle,
        identityId:
          this.#normalizeIdentityId(
            normalizedEntry?.identityId ?? normalizedEntry?.identity_id ?? null,
          ) ?? null,
      });
      this.#clearBootstrapRecoveryTimer([
        groupId,
        groupHandle,
        ensureString(normalizedEntry?.conversationId ?? normalizedEntry?.conversation_id ?? null),
      ]);
      try {
        this.identityResetAttemptsByGroup.delete(String(groupId));
      } catch {}
      if (groupHandle) {
        try {
          this.identityResetAttemptsByGroup.delete(String(groupHandle));
        } catch {}
      }
      normalizedEntry.needsRecovery = false;
      normalizedEntry.recoveryReason = null;
      normalizedEntry.cryptoMismatch = false;
      normalizedEntry.crypto_mismatch = false;
      normalizedEntry.ratchetTreeMismatch = false;
      normalizedEntry.bootstrapStartedAt = null;
      normalizedEntry.bootstrap_started_at = null;
      normalizedEntry.bootstrapWindowUntil = null;
      normalizedEntry.bootstrap_window_until = null;
      normalizedEntry.bootstrapReason = null;
      normalizedEntry.bootstrap_reason = null;
      normalizedEntry.welcomeTargetRefLock = null;
      normalizedEntry.welcome_target_ref_lock = null;
      normalizedEntry.welcomeTargetIdentityId = null;
      normalizedEntry.welcome_target_identity_id = null;
      normalizedEntry.welcomeTargetEpoch = null;
      normalizedEntry.welcome_target_epoch = null;
      normalizedEntry.welcomeTargetFetchedAt = null;
      normalizedEntry.welcome_target_fetched_at = null;
      normalizedEntry.runtimeState = 'ready';
      normalizedEntry.syncStatus = 'ready';
      normalizedEntry.epochAligned = true;
      void this.#cacheHistoryKeyForEntry(normalizedEntry).catch(() => null);
    }
    const becameReady = this._becameReady(groupId, normalizedEntry);
    if (ready) {
      if (this.groupRecovery.has(groupId)) {
        const prevRecovering = true;
        this.groupRecovery.delete(groupId);
        try {
          debugLog('[MLS] group recovered', { groupId, epoch: normalizedEntry.epoch });
          debugLog('[MLS] group_state', {
            groupId,
            readyFrom: false,
            readyTo: true,
            recoveringFrom: prevRecovering,
            recoveringTo: false,
            epoch: normalizedEntry.epoch,
          });
        } catch {}
      } else {
        try {
          debugLog('[MLS] group_state', {
            groupId,
            readyFrom: false,
            readyTo: true,
            recoveringFrom: false,
            recoveringTo: false,
            epoch: normalizedEntry.epoch,
          });
        } catch {}
      }
      if (groupId != null) {
        try {
          this.missingRatchetTreeGroups.delete(String(groupId));
        } catch {}
      }
      this.#flushPendingWelcomeOrCommitTasks(groupId, groupHandle);
      this.#settleWaitReadyFlights({
        groupId,
        groupHandle,
        info: normalizedEntry,
      });
      // Persist a joined marker to allow short-circuiting hydration on reloads
      try {
        const persistJoined = async () => {
          const groups = await this.#loadGroups();
          const existing = groups[groupId] || {};
          const existingEpoch = ensureEpoch(existing?.epoch);
          const readyEpoch = ensureEpoch(normalizedEntry?.epoch);
          const nextEpoch = Math.max(existingEpoch, readyEpoch);
          const existingFetchedAt = coerceTimestampMs(
            existing?.fetchedAt ?? existing?.fetched_at ?? null,
          );
          const nextFetchedAt = Math.max(
            Number.isFinite(existingFetchedAt) ? existingFetchedAt : 0,
            coerceTimestampMs(Date.now()),
          );
          groups[groupId] = {
            ...existing,
            joined: true,
            epoch: nextEpoch,
            pending_welcomes: [],
            pendingWelcomes: [],
            missing_user_ids: [],
            missingUserIds: [],
            bootstrapStartedAt: null,
            bootstrap_started_at: null,
            bootstrapWindowUntil: null,
            bootstrap_window_until: null,
            bootstrapReason: null,
            bootstrap_reason: null,
            fetchedAt: nextFetchedAt,
          };
          await this.#saveGroups(groups);
        };
        // fire and forget
        persistJoined();
      } catch {}
      if (becameReady && this.events?.emit) {
        console.info('[MLS] group ready', { groupId, epoch: normalizedEntry.epoch });
        this.events.emit('mls:group-ready', { groupId, groupHandle, info: normalizedEntry });
      }
    } else {
      this.groupRecovery.set(groupId, true);
    }
    return normalizedEntry;
  }

  async #cacheHistoryKeyForEntry(
    entry,
    { label = HISTORY_DERIVED_KEY_LABEL, length = HISTORY_DERIVED_KEY_LENGTH } = {},
  ) {
    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const resolvedGroupId = ensureString(entry.groupId ?? null);
    const resolvedEpoch = ensureEpoch(
      entry.runtimeEpoch ?? entry.runtime_epoch ?? entry.epoch ?? entry.current_epoch ?? null,
    );
    const resolvedHandle = ensureString(
      entry.runtimeHandle ?? entry.runtime_handle ?? entry.groupHandle ?? null,
    );
    const resolvedConversationId = ensureString(
      entry.conversationId ?? entry.conversation_id ?? null,
    );

    if (!resolvedGroupId || !resolvedEpoch || !resolvedHandle) {
      return null;
    }

    const storageKey = historyDerivedKeyStorageKey(resolvedGroupId, resolvedEpoch);
    const cached = historyDerivedKeyCache.get(storageKey);
    if (cached) {
      return cached;
    }

    const stored = await loadHistoryDerivedKey(storageKey);
    if (stored) {
      historyDerivedKeyCache.set(storageKey, stored);
      return stored;
    }

    const result = await workerExportGroupKey({
      groupHandle: resolvedHandle,
      label,
      context: resolvedConversationId || resolvedGroupId,
      length,
    }).catch(() => null);

    const keyB64 = result?.key || null;
    if (keyB64) {
      historyDerivedKeyCache.set(storageKey, keyB64);
      await storeHistoryDerivedKey(storageKey, keyB64);
      return keyB64;
    }

    return null;
  }

  async waitForGroupReady({ groupId, groupHandle, signal, timeoutMs = 12000 } = {}) {
    const normalizedGroupId = groupId == null ? null : String(groupId);
    const normalizedGroupHandle = groupHandle == null ? null : String(groupHandle);
    const waitReadyAliases = new Set(
      [ensureString(normalizedGroupId), ensureString(normalizedGroupHandle)].filter(Boolean),
    );
    if (!waitReadyAliases.size) {
      return null;
    }

    const adoptWaitReadyFlightAliases = (flight) => {
      if (!flight || !(flight.aliases instanceof Set)) return;
      for (const alias of waitReadyAliases) {
        flight.aliases.add(alias);
        this._waitReadyFlights.set(alias, flight);
      }
    };

    for (const alias of waitReadyAliases) {
      const activeFlight = this._waitReadyFlights.get(alias);
      if (activeFlight?.promise) {
        adoptWaitReadyFlightAliases(activeFlight);
        return activeFlight.promise;
      }
    }

    let observedPendingWelcomes = false;
    let attemptedRecoveryRepair = false;
    let attemptedRecoveryPendingConvergence = false;
    const tryAwaitRecoveryConvergence = async (groupInfo) => {
      if (!groupInfo) {
        return null;
      }

      const convergenceGroupId = groupInfo?.groupId ?? normalizedGroupId ?? null;
      const convergenceGroupHandle = groupInfo?.groupHandle ?? normalizedGroupHandle ?? null;
      const convergenceReason = groupInfo?.recoveryReason || groupInfo?.reason || null;
      const convergenceRuntimeState =
        ensureString(groupInfo?.runtimeState ?? groupInfo?.runtime_state ?? null) ??
        this.#classifyRuntimeState(groupInfo);
      const recoveryPendingWelcomes = Array.isArray(groupInfo?.pending_welcomes)
        ? groupInfo.pending_welcomes
        : Array.isArray(groupInfo?.pendingWelcomes)
          ? groupInfo.pendingWelcomes
          : [];
      const membershipState = this.#resolveEntryMembershipState(groupInfo, {
        activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
      });
      const bootstrapWelcomeOwned = this.#hasUnresolvedBootstrapWelcome(groupInfo, {
        groupId: convergenceGroupId,
        groupHandle: convergenceGroupHandle,
        activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
      });
      const canStillConvergeFromPending =
        recoveryPendingWelcomes.length > 0 ||
        observedPendingWelcomes ||
        bootstrapWelcomeOwned ||
        membershipState.runtimeBootstrapRequired ||
        membershipState.welcomeRequired;
      const missingRuntimeRecovery =
        convergenceReason === 'missing_runtime_group' ||
        convergenceRuntimeState === 'runtime_missing' ||
        convergenceRuntimeState === 'runtime_behind';
      const shouldProbePendingAuthoritatively =
        missingRuntimeRecovery && !attemptedRecoveryPendingConvergence;

      if (!missingRuntimeRecovery) {
        return null;
      }

      if (attemptedRecoveryPendingConvergence) {
        return null;
      }

      const awaitedConvergence = await this.#awaitActiveConvergenceForGroup({
        groupId: convergenceGroupId,
        groupHandle: convergenceGroupHandle,
        timeoutMs: 1500,
      }).catch(() => false);

      if (
        !awaitedConvergence &&
        !canStillConvergeFromPending &&
        !shouldProbePendingAuthoritatively
      ) {
        return null;
      }

      if (convergenceGroupId) {
        await this.#markBootstrapConvergence(convergenceGroupId, {
          reason: `waitForGroupReady:${convergenceReason || 'missing_runtime_group'}`,
        }).catch(() => null);
      }

      if (canStillConvergeFromPending || shouldProbePendingAuthoritatively) {
        attemptedRecoveryPendingConvergence = true;
        await this.pullAndApplyPendingWelcomeOrCommit({
          groupId: convergenceGroupId ?? convergenceGroupHandle ?? null,
          __skipEnsureWait: true,
        }).catch(() => null);
      }

      const awaitedFollowupConvergence = await this.#awaitActiveConvergenceForGroup({
        groupId: convergenceGroupId,
        groupHandle: convergenceGroupHandle,
        timeoutMs: 1500,
      }).catch(() => false);

      const refreshed = await this.groupInfo({
        groupId: convergenceGroupId,
        groupHandle: convergenceGroupHandle,
      }).catch(() => null);

      if (refreshed && this.#isGroupReady(refreshed)) {
        return this.#finalizeGroupReady({
          ...groupInfo,
          ...refreshed,
          groupId: refreshed.groupId ?? convergenceGroupId,
          groupHandle: refreshed.groupHandle ?? convergenceGroupHandle,
        });
      }

      return {
        deferred: Boolean(
          awaitedConvergence ||
            awaitedFollowupConvergence ||
            canStillConvergeFromPending ||
            shouldProbePendingAuthoritatively,
        ),
        info: refreshed || groupInfo,
      };
    };
    const tryRecoverReadyState = async (groupInfo) => {
      if (attemptedRecoveryRepair) {
        return null;
      }
      attemptedRecoveryRepair = true;
      const recoveryGroupId = groupInfo?.groupId ?? normalizedGroupId ?? null;
      const recoveryGroupHandle = groupInfo?.groupHandle ?? normalizedGroupHandle ?? null;
      const recoveryReason = groupInfo?.recoveryReason || groupInfo?.reason || null;
      try {
        const recovered = await this.recoverGroup({
          groupId: recoveryGroupId,
          groupHandle: recoveryGroupHandle,
          reason: recoveryReason || 'wait_ready_recovery',
        });
        if (recovered && this.#isGroupReady(recovered)) {
          return this.#finalizeGroupReady({
            ...groupInfo,
            ...recovered,
            groupId: recovered.groupId ?? recoveryGroupId,
            groupHandle: recovered.groupHandle ?? recoveryGroupHandle,
          });
        }
        if (recovered && !recovered.needsRecovery) {
          const refreshed = await this.groupInfo({
            groupId: recoveryGroupId,
            groupHandle: recoveryGroupHandle,
          }).catch(() => recovered);
          if (refreshed && this.#isGroupReady(refreshed)) {
            return this.#finalizeGroupReady({
              ...groupInfo,
              ...refreshed,
              groupId: refreshed.groupId ?? recoveryGroupId,
              groupHandle: refreshed.groupHandle ?? recoveryGroupHandle,
            });
          }
        }
      } catch (error) {
        console.warn('[MLS] waitForGroupReady recovery attempt failed', {
          groupId: recoveryGroupId,
          groupHandle: recoveryGroupHandle,
          recoveryReason,
          error,
        });
      }
      return null;
    };
    const checkReady = async () => {
      // Short-circuit: if we have a persisted joined snapshot, treat as ready
      try {
        const groups = await this.#loadGroups();
        const entry =
          (normalizedGroupId && groups[normalizedGroupId]) ||
          Object.values(groups).find((g) => g?.groupHandle === normalizedGroupHandle) ||
          null;
        const entryPendingWelcomes = Array.isArray(entry?.pending_welcomes)
          ? entry.pending_welcomes
          : Array.isArray(entry?.pendingWelcomes)
            ? entry.pendingWelcomes
            : [];
        if (entryPendingWelcomes.length > 0) {
          observedPendingWelcomes = true;
        }
        if (entry) {
          const membershipBootstrap = await this.#ensureActiveMembershipBootstrap({
            entry,
            conversationId: ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
            groupId: ensureString(entry?.groupId ?? normalizedGroupId ?? null),
            groupHandle: ensureString(entry?.groupHandle ?? normalizedGroupHandle ?? null),
            identityId: ensureString(this.identityClientId ?? this.identityId ?? null),
            ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
            source: 'waitForGroupReady',
            reason: 'wait_ready_membership_bootstrap',
          }).catch(() => null);
          if (
            membershipBootstrap?.repaired &&
            (membershipBootstrap.repaired.ready === true ||
              this.#isGroupReady(membershipBootstrap.repaired))
          ) {
            return this.#finalizeGroupReady(membershipBootstrap.repaired);
          }

          const runtimeBootstrap = await this.#ensureCurrentIdentityRuntimeBootstrap({
            entry,
            conversationId: ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
            groupId: ensureString(entry?.groupId ?? normalizedGroupId ?? null),
            groupHandle: ensureString(entry?.groupHandle ?? normalizedGroupHandle ?? null),
            identityId: ensureString(this.identityClientId ?? this.identityId ?? null),
            ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
            source: 'waitForGroupReady',
            reason: 'wait_ready_runtime_bootstrap',
          }).catch(() => null);
          if (runtimeBootstrap?.attempted) {
            // Runtime bootstrap already owns snapshot, Welcome sync/reissue, and
            // runtime reconciliation for this wait. Do not start parallel recovery
            // lanes from the polling loop or timeout handler.
            attemptedRecoveryPendingConvergence = true;
            attemptedRecoveryRepair = true;
          }
          if (
            runtimeBootstrap?.repaired &&
            (runtimeBootstrap.repaired.ready === true ||
              this.#isGroupReady(runtimeBootstrap.repaired))
          ) {
            return this.#finalizeGroupReady(runtimeBootstrap.repaired);
          }

          const runtimeEpoch = Number(
            entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
          );

          const snapshotEpoch = Number(
            entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
          );

          const runtimeBehindSnapshot =
            Number.isFinite(runtimeEpoch) &&
            Number.isFinite(snapshotEpoch) &&
            snapshotEpoch > runtimeEpoch;

          const shouldProbeRuntime =
            !this.#isGroupReady(entry) &&
            (runtimeBehindSnapshot ||
              !resolveRuntimeHandle(entry) ||
              !Number.isFinite(runtimeEpoch) ||
              runtimeEpoch <= 0);
          const membershipState = this.#resolveEntryMembershipState(entry, {
            activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
          });
          const suppressServerManagedMemberJoinBundle =
            this.serverManagedWelcomes &&
            membershipBootstrap?.repaired == null &&
            membershipState.currentIdentityMember &&
            !membershipState.pendingSetup &&
            !membershipState.welcomeRequired;
          if (shouldProbeRuntime) {
            if (runtimeBehindSnapshot && !attemptedRecoveryPendingConvergence) {
              attemptedRecoveryPendingConvergence = true;
              try {
                await this.pullAndApplyPendingWelcomeOrCommit({
                  groupId: ensureString(
                    entry?.groupId ??
                      entry?.group_id ??
                      normalizedGroupId ??
                      normalizedGroupHandle ??
                      null,
                  ),
                });
              } catch {
                // ignore; follow-up sync will decide readiness
              }
            }
            const synced = await this.#syncRuntimeGroupState({
              groupId: ensureString(entry?.groupId ?? normalizedGroupId ?? null),
              groupHandle: ensureString(entry?.groupHandle ?? normalizedGroupHandle ?? null),
              conversationId: ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
              source: 'waitForGroupReady',
              reason: 'persisted_entry_runtime_probe',
              allowSyncWelcomes: false,
              allowJoinBundleRepair: !suppressServerManagedMemberJoinBundle,
              allowRuntimeRepair: false,
            }).catch(() => null);
            if (synced && this.#isGroupReady(synced)) {
              return this.#finalizeGroupReady(synced);
            }
          }
        }
        if (entry && entry.joined === true) {
          let runtimeHasGroup = false;
          try {
            const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
            const handle = resolveRuntimeHandle(entry);
            runtimeHasGroup = handle
              ? runtimeGroups.some((item) => item.groupHandle === handle)
              : false;
          } catch {}
          if (runtimeHasGroup) {
            const synced = await this.#syncRuntimeGroupState({
              groupId: ensureString(entry?.groupId ?? normalizedGroupId ?? null),
              groupHandle: ensureString(entry?.groupHandle ?? normalizedGroupHandle ?? null),
              conversationId: ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
              source: 'waitForGroupReady',
              reason: 'joined_entry_runtime_present',
              allowSyncWelcomes: true,
              allowJoinBundleRepair: false,
              allowRuntimeRepair: false,
            }).catch(() => null);
            return this.#finalizeGroupReady(synced || { ...entry });
          }
          // Recovery join is needed when runtime group is missing.
          // Creator may not have pending welcomes, so allow one-shot self-join when local
          // membership contains active identity.
          const activeIdentitySet = new Set(
            [
              ensureString(this.identityClientId),
              ensureString(this.identityId),
              ensureString(entry?.identityId ?? null),
            ].filter(Boolean),
          );
          const entryMembers = normalizeMemberList(entry?.members);
          const hasLocalMembership =
            activeIdentitySet.size > 0 &&
            entryMembers.some((member) => activeIdentitySet.has(ensureString(member)));

          void entryPendingWelcomes;
          void hasLocalMembership;
        }
      } catch {}
      let info = null;
      try {
        info = await this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
        });
      } catch (error) {
        console.warn('[MLS] waitForGroupReady failed to fetch group info', {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          error,
        });
        return null;
      }

      if (!info) {
        return null;
      }

      const createRecoveryError = (groupInfo) => {
        const recoveryError = new Error('MLS group requires recovery');
        recoveryError.code = 'MLS_GROUP_NEEDS_RECOVERY';
        recoveryError.groupId = groupInfo.groupId ?? normalizedGroupId ?? null;
        recoveryError.groupHandle = groupInfo.groupHandle ?? normalizedGroupHandle ?? null;
        recoveryError.recoveryReason = groupInfo.recoveryReason || null;
        return recoveryError;
      };

      if (info.needsRecovery) {
        const reason = info.recoveryReason || info.reason || null;
        const recoveryPendingWelcomes = Array.isArray(info?.pending_welcomes)
          ? info.pending_welcomes
          : Array.isArray(info?.pendingWelcomes)
            ? info.pendingWelcomes
            : [];
        const recoveryRuntimeState =
          ensureString(info?.runtimeState ?? info?.runtime_state ?? null) ??
          this.#classifyRuntimeState(info);
        const deferBootstrapRecovery =
          (reason === 'missing_runtime_group' || recoveryRuntimeState === 'runtime_missing') &&
          this.#shouldDeferRecoveryDuringBootstrap(info, {
            source: 'waitForGroupReady',
            reason: reason || 'wait_ready_bootstrap_convergence',
            pendingWelcomeCount: recoveryPendingWelcomes.length,
            runtimeHasGroup: false,
          });

        if (deferBootstrapRecovery) {
          const deferGroupId = info.groupId ?? normalizedGroupId ?? null;
          const deferGroupHandle = info.groupHandle ?? normalizedGroupHandle ?? null;
          if (deferGroupId) {
            await this.#markBootstrapConvergence(deferGroupId, {
              reason: `waitForGroupReady:${reason || 'missing_runtime_group'}`,
            }).catch(() => null);
          }
          if (!attemptedRecoveryPendingConvergence) {
            attemptedRecoveryPendingConvergence = true;
            try {
              await this.pullAndApplyPendingWelcomeOrCommit({
                groupId: deferGroupId ?? deferGroupHandle ?? null,
                __skipEnsureWait: true,
              });
            } catch {}
          }
          const refreshed = await this.groupInfo({
            groupId: deferGroupId,
            groupHandle: deferGroupHandle,
          }).catch(() => null);
          if (refreshed && this.#isGroupReady(refreshed)) {
            return this.#finalizeGroupReady({
              ...info,
              ...refreshed,
              groupId: refreshed.groupId ?? deferGroupId,
              groupHandle: refreshed.groupHandle ?? deferGroupHandle,
            });
          }
          return null;
        }

        const convergenceResult = await tryAwaitRecoveryConvergence(info);
        if (convergenceResult && !convergenceResult.deferred) {
          return convergenceResult;
        }
        if (convergenceResult?.deferred) {
          return null;
        }

        const recoveredInfo = await tryRecoverReadyState(info);
        if (recoveredInfo) {
          return recoveredInfo;
        }
        const activeIdentitySet = new Set(
          [
            ensureString(this.identityClientId),
            ensureString(this.identityId),
            ensureString(info?.identityId ?? null),
          ].filter(Boolean),
        );
        const infoMembers = normalizeMemberList(info?.members);
        const hasLocalMembership =
          activeIdentitySet.size > 0 &&
          infoMembers.some((member) => activeIdentitySet.has(ensureString(member)));
        void reason;
        void recoveryPendingWelcomes;
        void hasLocalMembership;
        const recoveryError = createRecoveryError(info);
        console.warn('[MLS] waitForGroupReady detected recovery state', {
          groupId: recoveryError.groupId,
          groupHandle: recoveryError.groupHandle,
          recoveryReason: recoveryError.recoveryReason,
        });
        throw recoveryError;
      }

      const normalized = {
        ...info,
        groupId: info.groupId ?? normalizedGroupId ?? null,
        groupHandle: info.groupHandle ?? normalizedGroupHandle ?? null,
      };
      const infoPendingWelcomes = Array.isArray(normalized?.pending_welcomes)
        ? normalized.pending_welcomes
        : Array.isArray(normalized?.pendingWelcomes)
          ? normalized.pendingWelcomes
          : [];
      if (infoPendingWelcomes.length > 0) {
        observedPendingWelcomes = true;
      }

      if (this.#isGroupReady(normalized)) {
        return this.#finalizeGroupReady(normalized);
      }

      return null;
    };

    let currentFlight = {
      aliases: new Set(waitReadyAliases),
      promise: null,
      resolve: null,
      reject: null,
    };
    const releaseFlight = () => {
      if (!currentFlight?.aliases) return;
      for (const alias of currentFlight.aliases) {
        if (this._waitReadyFlights.get(alias) === currentFlight) {
          this._waitReadyFlights.delete(alias);
        }
      }
    };

    const task = new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;
      let timeoutId = null;
      let abortCleanup = null;
      const startedAt = Date.now();
      const normalizedTimeoutMs = Number.isFinite(Number(timeoutMs))
        ? Math.max(1000, Number(timeoutMs))
        : 12000;

      const cleanup = () => {
        settled = true;
        if (typeof abortCleanup === 'function') {
          abortCleanup();
          abortCleanup = null;
        }
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (offReady) {
          offReady();
        }
        releaseFlight();
      };

      const settleSuccess = (info) => {
        if (settled) return;
        cleanup();
        resolve(info);
      };

      const settleError = (error) => {
        if (settled) return;
        cleanup();
        reject(error);
      };

      currentFlight.resolve = settleSuccess;
      currentFlight.reject = settleError;

      const schedulePoll = () => {
        if (settled) return;
        timer = setTimeout(async () => {
          try {
            const info = await checkReady();
            if (info) {
              settleSuccess(info);
              return;
            }
          } catch (error) {
            settleError(error);
            return;
          }
          schedulePoll();
        }, 500);
      };

      const onReady = (payload) => {
        if (settled || !payload) return;
        const payloadGroupId = payload.groupId != null ? String(payload.groupId) : null;
        const payloadGroupHandle = payload.groupHandle != null ? String(payload.groupHandle) : null;

        const matchesGroupId =
          normalizedGroupId != null &&
          payloadGroupId != null &&
          payloadGroupId === normalizedGroupId;
        const matchesHandle =
          normalizedGroupHandle != null &&
          payloadGroupHandle != null &&
          payloadGroupHandle === normalizedGroupHandle;

        if (!matchesGroupId && !matchesHandle) {
          return;
        }

        const info = payload.info || payload;
        const normalized = {
          ...info,
          groupId: info.groupId ?? payloadGroupId ?? normalizedGroupId ?? null,
          groupHandle: info.groupHandle ?? payloadGroupHandle ?? normalizedGroupHandle ?? null,
        };

        settleSuccess(this.#finalizeGroupReady(normalized));
      };

      const offReady = this.events?.on ? this.events.on('mls:group-ready', onReady) : null;

      timeoutId = setTimeout(async () => {
        if (settled) return;
        try {
          const latestInfo = await this.groupInfo({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
          }).catch(() => null);
          const latestGroupId = latestInfo?.groupId ?? normalizedGroupId ?? null;
          const latestGroupHandle = latestInfo?.groupHandle ?? normalizedGroupHandle ?? null;
          const awaitedConvergence = await this.#awaitActiveConvergenceForGroup({
            groupId: latestGroupId,
            groupHandle: latestGroupHandle,
            timeoutMs: Math.min(normalizedTimeoutMs, 5000),
          }).catch(() => false);
          if (awaitedConvergence) {
            const convergedInfo = await checkReady();
            if (convergedInfo) {
              settleSuccess(convergedInfo);
              return;
            }
          }
          if (latestInfo?.needsRecovery) {
            const recoveredInfo = await tryRecoverReadyState(latestInfo);
            if (recoveredInfo) {
              settleSuccess(recoveredInfo);
              return;
            }
          }
          if (latestInfo && !this.#isGroupReady(latestInfo)) {
            const membershipState = this.#resolveEntryMembershipState(latestInfo, {
              activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
            });
            const pendingWelcomeCount = getPendingWelcomesList(latestInfo).length;
            const bootstrapWelcomeOwned = this.#hasUnresolvedBootstrapWelcome(latestInfo, {
              groupId: latestInfo?.groupId ?? normalizedGroupId ?? null,
              groupHandle: latestInfo?.groupHandle ?? normalizedGroupHandle ?? null,
              activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
            });
            const runtimeState =
              ensureString(latestInfo?.runtimeState ?? latestInfo?.runtime_state ?? null) ??
              this.#classifyRuntimeState(latestInfo);
            const stuckRuntimeState =
              runtimeState === 'runtime_missing' || runtimeState === 'runtime_behind';
            const shouldAttemptFinalPendingConvergence =
              stuckRuntimeState &&
              !attemptedRecoveryPendingConvergence &&
              (pendingWelcomeCount > 0 ||
                observedPendingWelcomes ||
                bootstrapWelcomeOwned ||
                membershipState.runtimeBootstrapRequired ||
                membershipState.welcomeRequired);
            if (shouldAttemptFinalPendingConvergence) {
              const targetGroupId =
                latestInfo?.groupId ?? normalizedGroupId ?? normalizedGroupHandle ?? null;
              const targetGroupHandle = latestInfo?.groupHandle ?? normalizedGroupHandle ?? null;
              try {
                const convergenceBudgetMs = Math.max(1000, Math.min(normalizedTimeoutMs, 5000));
                const convergenceResult = await awaitWithTimeout(
                  () =>
                    this.pullAndApplyPendingWelcomeOrCommit({
                      groupId: targetGroupId ?? targetGroupHandle ?? null,
                      __skipEnsureWait: true,
                    }).catch(() => null),
                  convergenceBudgetMs,
                  null,
                );
                const convergenceFinalInfo = convergenceResult?.finalInfo || null;
                const refreshedInfo =
                  (await this.groupInfo({
                    groupId: targetGroupId,
                    groupHandle: targetGroupHandle,
                  }).catch(() => null)) || convergenceFinalInfo;
                if (refreshedInfo && this.#isGroupReady(refreshedInfo)) {
                  settleSuccess(
                    this.#finalizeGroupReady({
                      ...latestInfo,
                      ...refreshedInfo,
                      groupId: refreshedInfo.groupId ?? targetGroupId,
                      groupHandle: refreshedInfo.groupHandle ?? targetGroupHandle,
                    }),
                  );
                  return;
                }
                const awaitedFollowupConvergence = await this.#awaitActiveConvergenceForGroup({
                  groupId: targetGroupId,
                  groupHandle: targetGroupHandle,
                  timeoutMs: Math.min(normalizedTimeoutMs, 5000),
                }).catch(() => false);
                if (awaitedFollowupConvergence) {
                  const convergedInfo = await checkReady();
                  if (convergedInfo) {
                    settleSuccess(convergedInfo);
                    return;
                  }
                }
              } catch {}
            }
            if (
              stuckRuntimeState &&
              membershipState.currentIdentityMember &&
              !membershipState.pendingSetup &&
              !membershipState.welcomeRequired &&
              pendingWelcomeCount === 0
            ) {
              const recoveryReason =
                runtimeState === 'runtime_behind'
                  ? 'runtime_snapshot_mismatch'
                  : 'missing_runtime_group';
              const targetGroupId =
                latestInfo?.groupId ?? normalizedGroupId ?? normalizedGroupHandle ?? null;
              if (targetGroupId) {
                await this.markGroupForRecovery(targetGroupId, recoveryReason).catch(() => null);
              }
              const recoveryError = new Error('MLS group requires recovery');
              recoveryError.code = 'MLS_GROUP_NEEDS_RECOVERY';
              recoveryError.groupId = latestInfo?.groupId ?? normalizedGroupId ?? null;
              recoveryError.groupHandle = latestInfo?.groupHandle ?? normalizedGroupHandle ?? null;
              recoveryError.recoveryReason = recoveryReason;
              settleError(recoveryError);
              return;
            }
          }
        } catch {}
        const timeoutError = new Error('waitForGroupReady timeout');
        timeoutError.code = 'MLS_WAIT_FOR_GROUP_READY_TIMEOUT';
        timeoutError.groupId = normalizedGroupId;
        timeoutError.groupHandle = normalizedGroupHandle;
        timeoutError.elapsedMs = Date.now() - startedAt;
        settleError(timeoutError);
      }, normalizedTimeoutMs);

      if (signal && typeof signal.addEventListener === 'function') {
        const onAbort = () => {
          const abortError =
            signal.reason instanceof Error ? signal.reason : new Error('waitForGroupReady aborted');
          settleError(abortError);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        abortCleanup = () => signal.removeEventListener('abort', onAbort);
      }

      Promise.resolve(checkReady())
        .then((immediate) => {
          if (settled) return;
          if (immediate) {
            settleSuccess(immediate);
            return;
          }
          schedulePoll();
        })
        .catch((error) => {
          settleError(error);
        });
    });

    currentFlight.promise = task;
    adoptWaitReadyFlightAliases(currentFlight);

    return task;
  }

  async ensureGroupForConversation(
    { conversationId, conversation, groupId, groupHandle, identityId } = {},
    options = {},
  ) {
    const {
      __allowIdentityMismatchReset = true,
      __skipRecovery = false,
      __skipWait = false,
      __allowMismatchSelfJoin = false,
    } = options || {};
    const normalizedConversationId =
      conversationId != null
        ? String(conversationId)
        : conversation
          ? normalizeConversationId(conversation)
          : null;

    if (conversation) {
      try {
        isMLSConversation(conversation);
      } catch {}
    }

    let ensured = await this.ensureGroup(
      {
        conversationId: normalizedConversationId,
        groupId,
        groupHandle,
        identityId,
      },
      {
        __skipWait,
      },
    );

    if (ensured) {
      const identity = await this.ensureIdentity({ ciphersuite: ensured?.ciphersuite });
      let activeIdentityId = identityId || identity?.identityId || this.identityId || null;
      // Prefer a minted client identifier if available to avoid comparing against ephemeral IDs
      if (this.identityClientId && typeof this.identityClientId === 'string') {
        activeIdentityId = this.identityClientId;
      }
      if (activeIdentityId) {
        const members = normalizeMemberList(ensured.members);
        const hasIdentity = members.includes(activeIdentityId);
        const identityMatches =
          ensured.identityId == null || ensured.identityId === activeIdentityId;
        const ensuredPendingWelcomes = Array.isArray(ensured?.pending_welcomes)
          ? ensured.pending_welcomes
          : Array.isArray(ensured?.pendingWelcomes)
            ? ensured.pendingWelcomes
            : [];
        // Only self-join from mismatch path when explicitly allowed by orchestrator
        // and when we actually have a welcome to consume.
        const shouldAttemptMismatchSelfJoin =
          __allowMismatchSelfJoin === true && ensuredPendingWelcomes.length > 0;

        if ((!hasIdentity || !identityMatches) && shouldAttemptMismatchSelfJoin) {
          const joined = await this.#selfJoinGroup({
            conversationId: normalizedConversationId,
            groupId: ensured.groupId ?? groupId ?? null,
            groupHandle: ensured.groupHandle ?? groupHandle ?? null,
            identityId: activeIdentityId,
            ciphersuite: ensured?.ciphersuite ?? this.preferredCiphersuite,
          });
          if (joined) {
            ensured = this.#finalizeGroupReady({
              ...ensured,
              ...joined,
              groupId: joined.groupId ?? ensured.groupId ?? groupId ?? null,
              groupHandle: joined.groupHandle ?? ensured.groupHandle ?? groupHandle ?? null,
              identityId: joined.identityId ?? activeIdentityId,
              needsRecovery: false,
              recoveryReason: null,
            });
          }
        } else if (!hasIdentity || !identityMatches) {
          try {
            console.info('[MLS] Skipping self-join on member mismatch without recovery signal', {
              conversationId: normalizedConversationId ?? null,
              groupId: ensured.groupId ?? groupId ?? null,
              hasIdentity,
              identityMatches,
              pendingWelcomes: ensuredPendingWelcomes.length,
              needsRecovery: Boolean(ensured?.needsRecovery),
            });
          } catch {}
        }

        // If the persisted group entry is bound to a different identity than the current one,
        // reset the local snapshot and re-run ensure flow once to avoid staging a welcome
        // under the wrong identity after a reload/new session.
        if (
          __allowIdentityMismatchReset &&
          ensured?.identityId != null &&
          ensured.identityId !== activeIdentityId &&
          (ensured.groupId || ensured.groupHandle)
        ) {
          try {
            console.warn('[MLS] Detected identity mismatch — forcing full rejoin', {
              groupId: ensured.groupId ?? ensured.groupHandle ?? null,
              previousIdentityId: ensured.identityId,
              activeIdentityId,
            });
          } catch {}
          try {
            // Ensure a fresh key package exists for the active identity to avoid ref mismatch
            try {
              await this.forceRepublishKeyPackage({
                ciphersuite: ensured?.ciphersuite ?? this.preferredCiphersuite,
              });
            } catch {}
            // Nudge server to reissue a Welcome for the active identity before we reset local state
            try {
              {
                const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
                let keyPackageB64 = null;
                try {
                  const exported = await this.exportKeyPackage(activeIdentityId);
                  keyPackageB64 = exported?.keyPackage || null;
                } catch {}
                await this.#reissueWelcomesIfNeeded({
                  groupId: ensured.groupId ?? ensured.groupHandle,
                  identityId: activeIdentityId,
                  keyPackageB64,
                  token: __t ? 'Bearer ' + __t : undefined,
                });
              }
            } catch {}
            await this.resetGroup(ensured.groupId ?? ensured.groupHandle);
          } catch {}
          return this.ensureGroupForConversation(
            {
              conversationId: normalizedConversationId,
              conversation,
              groupId,
              groupHandle,
              identityId: activeIdentityId,
            },
            {
              __allowIdentityMismatchReset: false,
              __skipRecovery,
              __skipWait,
            },
          );
        }
      }
    }

    if (!__skipRecovery && ensured?.needsRecovery) {
      try {
        console.info('[MLS] ensureGroupForConversation leaving recovery to orchestrator', {
          conversationId: normalizedConversationId ?? null,
          groupId: ensured?.groupId ?? groupId ?? null,
          groupHandle: ensured?.groupHandle ?? groupHandle ?? null,
          recoveryReason: ensured?.recoveryReason ?? null,
        });
      } catch {}
    }

    if (ensured?.ready && (ensured?.groupId || ensured?.groupHandle)) {
      const refreshedReadyState = await this.groupInfo({
        groupId: ensured.groupId ?? groupId ?? null,
        groupHandle: ensured.groupHandle ?? groupHandle ?? null,
      }).catch(() => null);
      if (refreshedReadyState && !this.#isGroupReady(refreshedReadyState)) {
        ensured = refreshedReadyState;
      }
    }

    if (!ensured || ensured.ready || ensured.needsRecovery) {
      return ensured;
    }
    if (__skipWait) {
      return ensured;
    }

    const waitGroupId = ensured.groupId ?? groupId ?? null;
    const waitGroupHandle = ensured.groupHandle ?? groupHandle ?? null;

    if (!waitGroupId && !waitGroupHandle) {
      return ensured;
    }

    const readyInfo = await this.waitForGroupReady({
      groupId: waitGroupId,
      groupHandle: waitGroupHandle,
    });

    if (!readyInfo) {
      return ensured;
    }

    return this.#finalizeGroupReady({
      ...ensured,
      ...readyInfo,
      groupId: readyInfo.groupId ?? ensured.groupId ?? waitGroupId,
      groupHandle: readyInfo.groupHandle ?? ensured.groupHandle ?? waitGroupHandle,
    });
  }

  async #recoverAlreadyMemberNoWelcomeRuntime({
    groupId,
    groupHandle = null,
    conversationId = null,
    identityId = null,
    ciphersuite = null,
    source = 'unknown',
    reason = 'already_member_no_welcome',
  } = {}) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) {
      return null;
    }

    const initialInfo = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: groupHandle || null,
    }).catch(() => null);
    const resolvedConversationId = ensureString(
      conversationId ?? initialInfo?.conversationId ?? initialInfo?.conversation_id ?? null,
    );
    const resolvedGroupHandle = ensureString(
      groupHandle ?? initialInfo?.groupHandle ?? initialInfo?.group_handle ?? null,
    );
    const activeIdentityId = this.#normalizeIdentityId(
      identityId ??
        initialInfo?.identityId ??
        initialInfo?.identity_id ??
        this.identityClientId ??
        this.identityId ??
        null,
    );

    console.debug('[MLS] recovering runtime after already-member/no-welcome response', {
      groupId: normalizedGroupId,
      groupHandle: resolvedGroupHandle,
      conversationId: resolvedConversationId,
      identityId: activeIdentityId,
      source,
      reason,
    });

    try {
      this.#markPendingWelcomesForGroup([normalizedGroupId, resolvedGroupHandle], false);
      await this.#updateGroupEntry(normalizedGroupId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pending_welcomes: [],
          pendingWelcomes: [],
        };
      });
    } catch {}

    const synced = await this.#syncRuntimeGroupState({
      groupId: normalizedGroupId,
      groupHandle: resolvedGroupHandle || null,
      conversationId: resolvedConversationId || null,
      source,
      reason,
      allowSyncWelcomes: true,
      allowJoinBundleRepair: false,
    }).catch(() => null);
    if (synced && !synced.needsRecovery && (synced.ready === true || this.#isGroupReady(synced))) {
      return this.#finalizeGroupReady({
        ...synced,
        needsRecovery: false,
        recoveryReason: null,
      });
    }

    const authoritativeBootstrapWelcome = this.#hasUnresolvedBootstrapWelcome(
      synced || initialInfo,
      {
        groupId: normalizedGroupId,
        groupHandle: resolvedGroupHandle || null,
        activeIdentityId,
      },
    );
    if (authoritativeBootstrapWelcome) {
      return synced || initialInfo || null;
    }

    if (this.serverManagedWelcomes) {
      const recoveryWelcome = await this.#requestServerManagedRuntimeRecoveryWelcome({
        conversationId: resolvedConversationId,
        groupId: normalizedGroupId,
        groupHandle: resolvedGroupHandle || null,
        identityId: activeIdentityId,
        ciphersuite,
        source,
        reason,
      }).catch(() => null);
      if (
        recoveryWelcome?.repaired &&
        (recoveryWelcome.repaired.ready === true || this.#isGroupReady(recoveryWelcome.repaired))
      ) {
        return this.#finalizeGroupReady({
          ...recoveryWelcome.repaired,
          groupId: recoveryWelcome.repaired.groupId ?? normalizedGroupId,
          groupHandle: recoveryWelcome.repaired.groupHandle ?? resolvedGroupHandle ?? null,
          needsRecovery: false,
          recoveryReason: null,
        });
      }
    } else {
      const selfWelcomed = await this.#bootstrapRuntimeWithSelfWelcome({
        conversationId: resolvedConversationId,
        groupId: normalizedGroupId,
        groupHandle: resolvedGroupHandle || null,
        identityId: activeIdentityId,
        ciphersuite,
        source,
        reason,
      }).catch(() => null);
      if (selfWelcomed && (selfWelcomed.ready === true || this.#isGroupReady(selfWelcomed))) {
        return selfWelcomed;
      }
      const liveRuntime = await this.#resolveLiveRuntimeGroupForEntry(
        {
          ...(initialInfo || {}),
          groupId: normalizedGroupId,
          groupHandle: resolvedGroupHandle || null,
          runtimeHandle: resolvedGroupHandle || normalizedGroupId,
          identityId: activeIdentityId,
        },
        {
          groupId: normalizedGroupId,
          groupHandle: resolvedGroupHandle || null,
        },
      ).catch(() => null);
      const matchedLiveRuntime = liveRuntime?.runtimeGroup || null;
      if (matchedLiveRuntime) {
        const adoptedRuntime = this.#finalizeGroupReady({
          ...(initialInfo || {}),
          groupId: normalizedGroupId,
          groupHandle:
            ensureString(matchedLiveRuntime.groupHandle ?? null) ?? resolvedGroupHandle ?? null,
          runtimeHandle:
            ensureString(matchedLiveRuntime.groupHandle ?? null) ?? resolvedGroupHandle ?? null,
          identityId: activeIdentityId,
          ciphersuite:
            ciphersuite ??
            initialInfo?.ciphersuite ??
            normalizeSuiteId(matchedLiveRuntime?.ciphersuite ?? null) ??
            null,
          epoch:
            normalizeEpochOrNull(initialInfo?.epoch ?? null) ??
            normalizeEpochOrNull(matchedLiveRuntime?.epoch ?? null) ??
            null,
          runtimeEpoch: normalizeEpochOrNull(matchedLiveRuntime?.epoch ?? null) ?? null,
          needsRecovery: false,
          recoveryReason: null,
        });
        if (adoptedRuntime.ready === true || this.#isGroupReady(adoptedRuntime)) {
          return adoptedRuntime;
        }
      }
    }

    const info = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: resolvedGroupHandle || null,
    }).catch(() => initialInfo ?? null);
    const runtimeEpoch = normalizeEpochOrNull(
      info?.runtimeEpoch ?? info?.runtime_epoch ?? info?.runtime_epoch_no ?? null,
    );
    const snapshotEpoch = normalizeEpochOrNull(info?.epoch ?? null);
    const hasRuntimeHandle = Boolean(resolveRuntimeHandle(info));
    const runtimeAligned =
      runtimeEpoch != null &&
      snapshotEpoch != null &&
      runtimeEpoch > 0 &&
      snapshotEpoch > 0 &&
      runtimeEpoch >= snapshotEpoch;
    if (info?.needsRecovery && hasRuntimeHandle && runtimeAligned) {
      const cleared = await this.#updateGroupEntry(normalizedGroupId, (prev) => ({
        ...prev,
        needsRecovery: false,
        recoveryReason: null,
      }));
      return this.#finalizeGroupReady(cleared || info);
    }

    const latestInfo = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: resolvedGroupHandle || null,
    }).catch(() => info ?? null);
    const latestMembershipState = this.#resolveEntryMembershipState(latestInfo, {
      activeIdentityId,
    });
    const latestRuntimeState = latestInfo ? this.#classifyRuntimeState(latestInfo) : null;

    if (
      latestInfo &&
      !this.#isGroupReady(latestInfo) &&
      !latestInfo?.needsRecovery &&
      latestRuntimeState === 'runtime_missing' &&
      latestMembershipState.currentIdentityMember &&
      !latestMembershipState.pendingSetup &&
      !latestMembershipState.welcomeRequired
    ) {
      const recoveryReason = 'missing_runtime_group';
      try {
        await this.markGroupForRecovery(normalizedGroupId, recoveryReason);
      } catch {}
      if (typeof this.recoverGroup === 'function') {
        const recovered = await this.recoverGroup({
          groupId: normalizedGroupId,
          groupHandle: resolvedGroupHandle || null,
          reason: recoveryReason,
        }).catch(() => null);
        if (recovered) {
          return recovered;
        }
      }
      return this.groupInfo({
        groupId: normalizedGroupId,
        groupHandle: resolvedGroupHandle || null,
      }).catch(() => latestInfo);
    }

    return latestInfo || info || null;
  }

  async forceJoinBundle({
    conversationId,
    conversation,
    groupId,
    groupHandle,
    identityId,
    ciphersuite,
    token,
    preferReissueOnAlreadyMemberNoWelcome = false,
    __pipelineBypass = false,
  } = {}) {
    const normalizedConversationId =
      conversationId != null
        ? String(conversationId)
        : conversation
          ? normalizeConversationId(conversation)
          : null;
    let targetGroupId = groupId ?? null;
    if (!targetGroupId && normalizedConversationId) {
      targetGroupId = this.conversationGroups.get(normalizedConversationId) ?? null;
    }
    if (
      !targetGroupId &&
      normalizedConversationId &&
      typeof this.ensureServerGroup === 'function'
    ) {
      try {
        targetGroupId = await this.ensureServerGroup({
          conversationId: normalizedConversationId,
          allowCreate: false,
        });
      } catch {}
    }
    if (!targetGroupId) return null;

    if (!this.#shouldAttemptForceJoinBundle(targetGroupId, [groupHandle])) {
      return null;
    }

    // Recovery guard: before forcing join_bundle, always try one `/pending` preflight.
    // This keeps frontend aligned with backend-first pending/ack flow and avoids skipping
    // actionable welcomes that are not present in `/conversations/:id/mls-group`.
    const preflightKey = String(targetGroupId);
    const preflightSet = this._forceJoinPreflightActiveByGroup;
    const preflightNow = Date.now();
    const preflightLastAt =
      Number(this._forceJoinPendingPreflightAtByGroup.get(preflightKey) || 0) || 0;
    const shouldRunPreflight =
      !preflightSet.has(preflightKey) && preflightNow - preflightLastAt > 1500;
    if (shouldRunPreflight) {
      preflightSet.add(preflightKey);
      this._forceJoinPendingPreflightAtByGroup.set(preflightKey, preflightNow);
      try {
        const preflight = await awaitWithTimeout(
          () =>
            this.syncWelcomes({
              conversationId: normalizedConversationId,
              groupId: targetGroupId,
              __pipelineBypass: __pipelineBypass === true,
            }),
          FORCE_JOIN_PREFLIGHT_TIMEOUT_MS,
          { __timedOut: true },
        );
        if (preflight?.__timedOut) {
          console.warn('[MLS] forceJoinBundle pending preflight timed out', {
            groupId: targetGroupId,
            conversationId: normalizedConversationId,
            timeoutMs: FORCE_JOIN_PREFLIGHT_TIMEOUT_MS,
          });
        }
        if (Array.isArray(preflight?.joined) && preflight.joined.length) {
          const joinedMatch =
            preflight.joined.find((entry) => {
              if (!entry || typeof entry !== 'object') return false;
              const entryGroupId = ensureString(entry.groupId ?? entry.group_id ?? null);
              const entryGroupHandle = ensureString(
                entry.groupHandle ?? entry.group_handle ?? null,
              );
              return (
                (entryGroupId && entryGroupId === String(targetGroupId)) ||
                (groupHandle && entryGroupHandle && entryGroupHandle === String(groupHandle))
              );
            }) ?? preflight.joined[0];
          if (joinedMatch) {
            return this.#finalizeGroupReady({
              ...joinedMatch,
              groupId: joinedMatch.groupId ?? targetGroupId,
              groupHandle: joinedMatch.groupHandle ?? groupHandle ?? null,
              needsRecovery: false,
              recoveryReason: null,
            });
          }
        }
      } catch (error) {
        console.warn('[MLS] forceJoinBundle pending preflight failed', {
          groupId: targetGroupId,
          conversationId: normalizedConversationId,
          error,
        });
      } finally {
        preflightSet.delete(preflightKey);
      }
    }

    const identity = await this.ensureIdentity({ ciphersuite });
    let activeIdentityId = identityId || identity?.identityId || this.identityId || null;
    if (!activeIdentityId) return null;

    let bundle = null;
    try {
      const authToken =
        token || (typeof getAuthToken === 'function' ? `Bearer ${getAuthToken()}` : undefined);
      let exportedKeyPackage = null;
      try {
        exportedKeyPackage = await this.exportKeyPackage(activeIdentityId);
      } catch {}
      if (!exportedKeyPackage?.keyPackage) {
        console.warn('[MLS] Skipping forced join_bundle: missing key package', {
          identityId: activeIdentityId,
          groupId: targetGroupId,
        });
      } else {
        const fetched = await awaitWithTimeout(
          () =>
            this.#fetchJoinBundleWithRecovery({
              groupId: targetGroupId,
              identityId: activeIdentityId,
              keyPackageB64: exportedKeyPackage.keyPackage,
              token: authToken,
              ciphersuite,
              context: 'force_join_bundle',
              preferReissueOnAlreadyMemberNoWelcome,
            }),
          FORCE_JOIN_FETCH_TIMEOUT_MS,
          { __timedOut: true },
        );
        if (fetched?.__timedOut) {
          console.warn('[MLS] forceJoinBundle fetch timed out', {
            groupId: targetGroupId,
            identityId: activeIdentityId,
            conversationId: normalizedConversationId,
            timeoutMs: FORCE_JOIN_FETCH_TIMEOUT_MS,
          });
        }
        if (fetched?.blocked) {
          console.info('[MLS] forceJoinBundle blocked by locked welcome target', {
            groupId: targetGroupId,
            identityId: activeIdentityId,
            reason: fetched.reason ?? null,
          });
          return null;
        }
        bundle = fetched?.bundle ?? null;
        if (fetched?.identityId) {
          activeIdentityId = fetched.identityId;
        }
      }
    } catch (error) {
      console.warn('[MLS] forceJoinBundle failed to fetch join bundle', error);
    }

    const bundleWelcome = bundle?.welcome_b64 || bundle?.welcomeB64 || null;
    const bundleTree = bundle?.ratchet_tree_b64 || bundle?.ratchetTreeB64 || null;
    const bundleEpoch = bundle?.epoch != null ? ensureEpoch(bundle.epoch) : null;
    if (bundleWelcome && bundleEpoch != null) {
      await this.#persistWelcomeTarget({
        groupId: targetGroupId,
        groupHandle: groupHandle || null,
        conversationId: normalizedConversationId || null,
        identityId: activeIdentityId,
        welcomeB64: bundleWelcome,
        ratchetTreeB64: bundleTree,
        epoch: bundleEpoch,
        usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
        source: 'force_join_bundle',
      }).catch(() => null);
    }
    if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
      return this.#recoverAlreadyMemberNoWelcomeRuntime({
        groupId: targetGroupId,
        groupHandle: groupHandle || null,
        conversationId: normalizedConversationId || null,
        identityId: activeIdentityId,
        ciphersuite,
        source: 'forceJoinBundle',
        reason: 'already_member_no_welcome',
      });
    }

    const attemptJoinWithBundle = async (welcomeB64, treeB64, epoch) => {
      if (!welcomeB64 || epoch == null) return null;
      try {
        const joined = await awaitWithTimeout(
          () =>
            this.joinGroup({
              identityId: activeIdentityId,
              welcomeB64,
              ratchetTreeB64: treeB64 || null,
              groupId: targetGroupId,
              welcomeSource: 'join_bundle',
              expectedEpoch: epoch,
            }),
          JOIN_GROUP_WORKER_TIMEOUT_MS,
          null,
        );
        if (joined) {
          const lookupId = joined.groupId ?? targetGroupId;
          if (normalizedConversationId && lookupId) {
            this.conversationGroups.set(String(normalizedConversationId), lookupId);
            persistKnownConversationGroupId(String(normalizedConversationId), lookupId);
          }
          return this.#finalizeGroupReady({
            ...joined,
            groupId: lookupId,
            groupHandle: joined.groupHandle ?? groupHandle ?? null,
            identityId: activeIdentityId,
            needsRecovery: false,
            recoveryReason: null,
          });
        }
      } catch (error) {
        console.warn('[MLS] forceJoinBundle joinGroup failed', error);
      }
      return null;
    };

    let joined = null;
    if (bundleWelcome && bundleEpoch != null) {
      // Prefer no-tree first to avoid stale/epoch-mismatched trees.
      joined = await attemptJoinWithBundle(bundleWelcome, null, bundleEpoch);
      if (!joined && bundleTree) {
        joined = await attemptJoinWithBundle(bundleWelcome, bundleTree, bundleEpoch);
      }
    }

    if (!joined && bundleWelcome && bundleEpoch != null) {
      // Refetch a fresh join bundle once and retry without tree.
      try {
        const authToken =
          token || (typeof getAuthToken === 'function' ? `Bearer ${getAuthToken()}` : undefined);
        const exportedKeyPackage = await this.exportKeyPackage(activeIdentityId);
        if (exportedKeyPackage?.keyPackage) {
          const refreshed = await this.#fetchJoinBundleWithRecovery({
            groupId: targetGroupId,
            identityId: activeIdentityId,
            keyPackageB64: exportedKeyPackage.keyPackage,
            token: authToken,
            ciphersuite,
            context: 'force_join_bundle_retry',
          });
          const retryBundle = refreshed?.bundle ?? null;
          const retryWelcome = retryBundle?.welcome_b64 || retryBundle?.welcomeB64 || null;
          const retryEpoch = retryBundle?.epoch != null ? ensureEpoch(retryBundle.epoch) : null;
          if (retryWelcome && retryEpoch != null) {
            await this.#persistWelcomeTarget({
              groupId: targetGroupId,
              groupHandle: groupHandle || null,
              conversationId: normalizedConversationId || null,
              identityId: activeIdentityId,
              welcomeB64: retryWelcome,
              ratchetTreeB64: retryBundle?.ratchet_tree_b64 || retryBundle?.ratchetTreeB64 || null,
              epoch: retryEpoch,
              usedKpRef: retryBundle?.used_kp_ref ?? retryBundle?.usedKpRef ?? null,
              source: 'force_join_bundle_retry',
            }).catch(() => null);
            joined = await attemptJoinWithBundle(retryWelcome, null, retryEpoch);
          }
        }
      } catch (error) {
        console.warn('[MLS] forceJoinBundle retry failed', error);
      }
    }

    return joined;
  }

  async resetGroup(groupIdOrHandle) {
    const key = groupIdOrHandle == null ? null : String(groupIdOrHandle);
    if (!key) return false;

    let groups = null;
    try {
      groups = await this.#loadGroups();
    } catch {
      groups = null;
    }
    if (!groups || typeof groups !== 'object') {
      return false;
    }

    let targetKey = null;
    let entry = null;
    if (groups[key]) {
      targetKey = key;
      entry = groups[key];
    } else {
      const found = Object.entries(groups).find(
        ([k, v]) => v?.groupHandle === key || v?.groupId === key,
      );
      if (found) {
        targetKey = found[0];
        entry = found[1];
      }
    }
    if (!targetKey) {
      return false;
    }

    const normalizedGroupId = ensureString(entry?.groupId ?? targetKey);
    this.#clearBootstrapRecoveryTimer([
      key,
      normalizedGroupId,
      ensureString(entry?.groupHandle ?? null),
      ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
    ]);
    this.runtimeBootstrapTasks.delete(key);
    this.runtimeBootstrapTasks.delete(normalizedGroupId);
    this.runtimeBootstrapTaskStartedAt.delete(key);
    this.runtimeBootstrapTaskStartedAt.delete(normalizedGroupId);
    this.runtimeBootstrapCooldownByGroup.delete(key);
    this.runtimeBootstrapCooldownByGroup.delete(normalizedGroupId);
    this.identityResetAttemptsByGroup.delete(key);
    this.identityResetAttemptsByGroup.delete(normalizedGroupId);
    if (entry?.groupHandle) {
      this.identityResetAttemptsByGroup.delete(String(entry.groupHandle));
    }
    const candidateRuntimeHandles = new Set(
      [
        ensureString(targetKey),
        ensureString(entry?.groupHandle ?? null),
        ensureString(entry?.runtimeHandle ?? null),
      ].filter(Boolean),
    );

    // Drop stale runtime objects from worker so old sender-generation chains cannot be reused.
    await this.#dropStaleRuntimeGroupsForGroupId(normalizedGroupId, {
      source: 'resetGroup',
      reason: 'explicit_group_reset',
    });
    for (const handle of candidateRuntimeHandles) {
      await this.#removeRuntimeGroupHandle(handle, {
        source: 'resetGroup',
        reason: 'explicit_group_reset',
      });
    }

    const nextGroups = { ...groups };
    delete nextGroups[targetKey];
    await this.#saveGroups(nextGroups);

    // Clear conversation mapping cache if present
    try {
      for (const [convId, gid] of this.conversationGroups.entries()) {
        if (gid != null && String(gid) === String(entry?.groupId ?? targetKey)) {
          this.conversationGroups.delete(convId);
          clearKnownConversationGroupId(convId);
          try {
            resetConversationMlsGroupCache(convId);
          } catch {}
        }
      }
    } catch {}

    // Clear any persisted welcome state for this group
    try {
      await this.#clearPersistedWelcomeState({
        groupId: entry?.groupId ?? targetKey,
        groupHandle: entry?.groupHandle ?? null,
        clearRecoveryPayload: true,
      });
    } catch {}

    // Mark as recovering so ensures will rebuild cleanly
    try {
      this.groupRecovery.set(entry?.groupId ?? targetKey, true);
    } catch {}

    return true;
  }

  async #resetConversationLocalGroupState(
    conversationId,
    { groupId = null, groupHandle = null, reason = 'server_snapshot_missing' } = {},
  ) {
    const normalizedConversationId = ensureString(conversationId);
    if (!normalizedConversationId) {
      return false;
    }

    const candidateGroupIds = new Set();
    [groupId, groupHandle].forEach((value) => {
      const normalized = ensureString(value);
      if (normalized) {
        candidateGroupIds.add(normalized);
      }
    });

    try {
      const mappedGroupId = ensureString(this.conversationGroups.get(normalizedConversationId));
      if (mappedGroupId) {
        candidateGroupIds.add(mappedGroupId);
      }
    } catch {}

    try {
      const knownConversation = getKnownMLSConversation(normalizedConversationId);
      const knownGroupId = resolveConversationMlsGroupId(knownConversation);
      if (knownGroupId) {
        candidateGroupIds.add(String(knownGroupId));
      }
    } catch {}

    try {
      const groups = (await this.#loadGroups().catch(() => null)) || {};
      Object.entries(groups).forEach(([storedKey, entry]) => {
        if (!entry || typeof entry !== 'object') return;
        const entryConversationId = ensureString(
          entry?.conversationId ?? entry?.conversation_id ?? null,
        );
        if (entryConversationId !== normalizedConversationId) return;
        const entryGroupId = ensureString(entry?.groupId ?? entry?.group_id ?? storedKey);
        const entryGroupHandle = ensureString(entry?.groupHandle ?? entry?.group_handle ?? null);
        if (entryGroupId) {
          candidateGroupIds.add(entryGroupId);
        }
        if (entryGroupHandle) {
          candidateGroupIds.add(entryGroupHandle);
        }
      });
    } catch {}

    let cleared = false;
    for (const candidate of candidateGroupIds) {
      try {
        const reset = await this.resetGroup(candidate);
        cleared = Boolean(reset) || cleared;
      } catch {}
    }

    try {
      this.conversationGroups.delete(normalizedConversationId);
    } catch {}
    try {
      clearKnownConversationGroupId(normalizedConversationId);
    } catch {}
    try {
      resetConversationMlsGroupCache(normalizedConversationId);
    } catch {}

    if (cleared && typeof console?.info === 'function') {
      console.info('[MLS] Cleared stale local conversation group state', {
        conversationId: normalizedConversationId,
        reason,
        clearedGroupCandidates: Array.from(candidateGroupIds),
      });
    }

    return cleared;
  }

  #enqueuePendingWelcomeOrCommit(keys, task, logger) {
    if (typeof task !== 'function') {
      return undefined;
    }

    const normalizedKeys = Array.from(
      new Set(
        (Array.isArray(keys) ? keys : [keys])
          .filter((key) => key != null && key !== '')
          .map((key) => String(key)),
      ),
    );

    if (!normalizedKeys.length) {
      return task();
    }

    const alreadyReady = normalizedKeys.some((key) => this._readyGroups.has(key));
    if (alreadyReady) {
      return task();
    }

    let entry = null;
    for (const key of normalizedKeys) {
      if (this.pendingWelcomeCommitTasks.has(key)) {
        entry = this.pendingWelcomeCommitTasks.get(key);
        break;
      }
    }

    if (!entry) {
      entry = { tasks: [], keys: new Set() };
    }

    normalizedKeys.forEach((key) => {
      entry.keys.add(key);
      this.pendingWelcomeCommitTasks.set(key, entry);
    });

    entry.tasks.push(task);

    const log = logger?.debug
      ? (message, meta) => {
          try {
            logger.debug(message, meta);
          } catch {}
        }
      : console?.debug
        ? (message, meta) => {
            try {
              console.debug(message, meta);
            } catch {}
          }
        : null;

    if (log) {
      log('[MLS] Group not ready — gating pending welcome/commit', { groupId: normalizedKeys[0] });
    }

    return undefined;
  }

  #flushPendingWelcomeOrCommitTasks(groupId, groupHandle) {
    const candidateKeys = new Set();
    if (groupId != null) {
      candidateKeys.add(String(groupId));
    }
    if (groupHandle != null) {
      candidateKeys.add(String(groupHandle));
    }

    const conversation = findKnownConversationByGroupId(groupId);
    const conversationId = normalizeConversationId(conversation);
    if (conversationId) {
      candidateKeys.add(String(conversationId));
    }

    if (groupId != null) {
      for (const [convId, mappedGroupId] of this.conversationGroups.entries()) {
        if (mappedGroupId != null && String(mappedGroupId) === String(groupId)) {
          candidateKeys.add(String(convId));
        }
      }
    }

    const fallbackConversation = getKnownMLSConversation(groupId);
    const fallbackConversationId = normalizeConversationId(fallbackConversation);
    if (fallbackConversationId) {
      candidateKeys.add(String(fallbackConversationId));
    }

    if (!candidateKeys.size) {
      return;
    }

    const processed = new Set();
    for (const key of candidateKeys) {
      if (!key) continue;
      const entry = this.pendingWelcomeCommitTasks.get(key);
      if (!entry || processed.has(entry)) {
        continue;
      }
      processed.add(entry);
      entry.keys.forEach((ownedKey) => {
        this.pendingWelcomeCommitTasks.delete(ownedKey);
      });
      entry.keys.clear();
      const tasks = entry.tasks.splice(0, entry.tasks.length);
      if (!tasks.length) {
        continue;
      }
      tasks
        .reduce((promise, fn) => promise.then(() => fn()), Promise.resolve())
        .catch((error) => {
          console.error('[MLS] Error draining pending welcome/commit queue', {
            groupId: key,
            error,
          });
        });
    }
  }

  async #awaitPendingEnsureForGroup({
    groupId,
    groupHandle,
    strictOnTimeout = true,
    timeoutMs = ENSURE_GROUP_WAIT_TIMEOUT_MS,
    source = 'unknown',
  } = {}) {
    const keys = new Set();
    if (groupId != null) {
      const normalizedGroupId = String(groupId);
      keys.add(`gid:${normalizedGroupId}`);
      keys.add(`gh:${normalizedGroupId}`);
      const conversation = findKnownConversationByGroupId(normalizedGroupId);
      const conversationId = normalizeConversationId(conversation);
      if (conversationId) {
        keys.add(`conv:${conversationId}`);
      }
      const conversationById = getKnownMLSConversation(normalizedGroupId);
      const normalizedConversationById = normalizeConversationId(conversationById);
      if (normalizedConversationById) {
        keys.add(`conv:${normalizedConversationById}`);
      }
      for (const [convId, mappedGroupId] of this.conversationGroups.entries()) {
        if (mappedGroupId != null && String(mappedGroupId) === normalizedGroupId) {
          keys.add(`conv:${convId}`);
        }
      }
    }

    if (groupHandle != null) {
      const normalizedHandle = String(groupHandle);
      keys.add(`gh:${normalizedHandle}`);
      keys.add(`gid:${normalizedHandle}`);
    }

    const waitForEnsureWithTimeout = async (promise, timeoutMs = ENSURE_GROUP_WAIT_TIMEOUT_MS) => {
      let timerId = null;
      try {
        const timeout = new Promise((resolve) => {
          timerId = setTimeout(() => resolve({ timeout: true }), timeoutMs);
        });
        const result = await Promise.race([
          Promise.resolve(promise)
            .then(() => ({ resolved: true }))
            .catch((error) => ({ resolved: false, error })),
          timeout,
        ]);
        return result;
      } finally {
        if (timerId) {
          clearTimeout(timerId);
        }
      }
    };

    for (const key of keys) {
      if (!key) continue;
      const pending = this.groupEnsureTasks.get(key);
      if (!pending || typeof pending.then !== 'function') {
        continue;
      }
      const waited = await waitForEnsureWithTimeout(pending, timeoutMs);
      if (waited?.resolved) {
        return true;
      }
      if (waited?.timeout) {
        if (typeof console?.debug === 'function') {
          console.debug('[MLS] ensureGroup wait timed out', {
            key,
            timeoutMs,
            strictOnTimeout,
            source,
          });
        }
        if (!strictOnTimeout) {
          continue;
        }
        try {
          await Promise.resolve(pending);
        } catch {}
        return true;
      }
      if (waited?.error) {
        console.debug('[MLS] ensureGroup task rejected before pull/apply', {
          key,
          error: waited.error,
        });
      }
    }

    return false;
  }

  async #awaitActiveConvergenceForGroup({ groupId, groupHandle, timeoutMs = 4000 } = {}) {
    const keys = new Set([ensureString(groupId), ensureString(groupHandle)].filter(Boolean));
    if (!keys.size) {
      return false;
    }

    await this.#awaitPendingEnsureForGroup({
      groupId: ensureString(groupId),
      groupHandle: ensureString(groupHandle),
      strictOnTimeout: false,
      timeoutMs: Math.min(
        1000,
        Number.isFinite(Number(timeoutMs)) ? Math.max(250, Number(timeoutMs)) : 1000,
      ),
      source: 'waitForGroupReady',
    }).catch(() => false);

    const tasks = [];
    const seen = new Set();
    const addTask = (task) => {
      if (!task || typeof task.then !== 'function' || seen.has(task)) {
        return;
      }
      seen.add(task);
      tasks.push(task);
    };

    for (const key of keys) {
      addTask(this.groupPipelineTails?.get(key) || null);
      addTask(this._pendingApplyFlights?.get(key) || null);
      addTask(this.runtimeBootstrapTasks?.get(key) || null);
      addTask(this.recoveryTasks?.get(key) || null);
    }

    if (!tasks.length) {
      return false;
    }

    let timerId = null;
    try {
      const normalizedTimeoutMs = Number.isFinite(Number(timeoutMs))
        ? Math.max(1000, Number(timeoutMs))
        : 4000;
      const result = await Promise.race([
        Promise.allSettled(tasks).then(() => ({ settled: true })),
        new Promise((resolve) => {
          timerId = setTimeout(() => resolve({ timedOut: true }), normalizedTimeoutMs);
        }),
      ]);
      return Boolean(result?.settled || result?.timedOut);
    } finally {
      if (timerId) {
        clearTimeout(timerId);
      }
    }
  }

  // Public helper to force resend of gated pending tasks for a group
  async resendPendingForGroup(groupIdOrHandle) {
    const key = groupIdOrHandle == null ? null : String(groupIdOrHandle);
    if (!key) return false;
    try {
      const info = await this.groupInfo({ groupId: key, groupHandle: key }).catch(() => null);
      if (!this.#isGroupReady(info)) {
        try {
          console.info('[MLS] resendPendingForGroup skipped until orchestrator marks group ready', {
            groupId: info?.groupId || key,
            groupHandle: info?.groupHandle || null,
          });
        } catch {}
        return false;
      }
      this.#flushPendingWelcomeOrCommitTasks(info?.groupId || key, info?.groupHandle || null);
      return true;
    } catch {
      return false;
    }
  }

  async ensurePendingQueueLoaded() {
    if (this.pendingQueueLoadPromise) {
      return this.pendingQueueLoadPromise;
    }
    this.pendingQueueLoadPromise = loadPendingQueueFromStorage(this.pendingQueueStorageKey)
      .then((loaded) => {
        this.pendingMlsQueue = loaded instanceof Map ? loaded : new Map();
        return this.pendingMlsQueue;
      })
      .catch((error) => {
        console.warn('[MLS] failed to initialize manager pending queue', error);
        this.pendingMlsQueue = new Map();
        return this.pendingMlsQueue;
      });
    return this.pendingQueueLoadPromise;
  }

  persistPendingQueue() {
    return persistPendingQueueToStorage(this.pendingQueueStorageKey, this.pendingMlsQueue);
  }

  listPendingGroupIds() {
    return Array.from(this.pendingMlsQueue.keys());
  }

  listPendingEntries(groupId) {
    if (!groupId) return [];
    const queue = this.pendingMlsQueue.get(String(groupId));
    if (!(queue instanceof Map) || queue.size === 0) return [];
    return Array.from(queue.values())
      .sort((a, b) => {
        const aSeq = parseCursor(a?.envelope?.server_seq ?? a?.server_seq ?? null);
        const bSeq = parseCursor(b?.envelope?.server_seq ?? b?.server_seq ?? null);
        if (aSeq != null && bSeq != null && aSeq !== bSeq) {
          return aSeq - bSeq;
        }
        const aTime = Number(a?.enqueuedAt || 0) || 0;
        const bTime = Number(b?.enqueuedAt || 0) || 0;
        return aTime - bTime;
      })
      .map((entry) => ({
        ...entry,
        envelope: entry?.envelope ? { ...entry.envelope } : null,
        pendingRecovery: entry?.pendingRecovery !== false,
        pendingRuntime: Boolean(
          entry?.pendingRuntime ||
            entry?.envelope?.pending_runtime ||
            entry?.envelope?.pendingRuntime ||
            entry?.envelope?.header?.pending_runtime,
        ),
      }));
  }

  getPendingEntry(groupId, envelope) {
    if (!groupId || !envelope) return null;
    const queue = this.pendingMlsQueue.get(String(groupId));
    if (!(queue instanceof Map) || queue.size === 0) return null;
    const target = getPendingEnvelopeIdentifiers(envelope);
    for (const value of queue.values()) {
      if (
        pendingIdentifiersMatch(target, getPendingEnvelopeIdentifiers(value?.envelope || value))
      ) {
        return value;
      }
    }
    return null;
  }

  enqueuePendingEntry(groupId, entry, { limit = 100 } = {}) {
    if (!groupId || !entry) return entry;
    const normalizedGroupId = String(groupId);
    const queue = this.pendingMlsQueue.get(normalizedGroupId) || new Map();
    const queueKey =
      getPendingQueueKeyForEntry(entry) ||
      `tmp:${Date.now()}:${Math.random().toString(16).slice(2)}`;
    const normalized = {
      ...entry,
      pendingRecovery: entry?.pendingRecovery !== false,
      pendingRuntime: Boolean(
        entry?.pendingRuntime ||
          entry?.envelope?.pending_runtime ||
          entry?.envelope?.pendingRuntime ||
          entry?.envelope?.header?.pending_runtime,
      ),
      queueKey,
      enqueuedAt: Number(entry?.enqueuedAt || Date.now()),
    };
    if (queue.has(queueKey)) {
      queue.set(queueKey, { ...queue.get(queueKey), ...normalized });
    } else {
      queue.set(queueKey, normalized);
    }
    while (queue.size > limit) {
      const oldestKey = queue.keys().next().value;
      if (!oldestKey) break;
      queue.delete(oldestKey);
    }
    this.pendingMlsQueue.set(normalizedGroupId, queue);
    void this.persistPendingQueue();
    return queue.get(queueKey);
  }

  removePendingEntry(groupId, entry) {
    if (!groupId || !entry) return false;
    const normalizedGroupId = String(groupId);
    const queue = this.pendingMlsQueue.get(normalizedGroupId);
    if (!(queue instanceof Map) || queue.size === 0) return false;
    const keyCandidates = new Set();
    if (entry?.queueKey) keyCandidates.add(entry.queueKey);
    const derivedKey = getPendingQueueKeyForEntry(entry);
    if (derivedKey) keyCandidates.add(derivedKey);
    const target = getPendingEnvelopeIdentifiers(entry?.envelope || entry);
    let removed = false;
    for (const [key, value] of queue.entries()) {
      if (keyCandidates.has(key)) {
        queue.delete(key);
        removed = true;
        break;
      }
      if (
        pendingIdentifiersMatch(target, getPendingEnvelopeIdentifiers(value?.envelope || value))
      ) {
        queue.delete(key);
        removed = true;
        break;
      }
    }
    if (queue.size === 0) {
      this.pendingMlsQueue.delete(normalizedGroupId);
    }
    if (removed) {
      void this.persistPendingQueue();
    }
    return removed;
  }

  clearPendingQueue(groupId = null) {
    if (groupId != null) {
      const normalizedGroupId = String(groupId);
      if (this.pendingMlsQueue.delete(normalizedGroupId)) {
        void this.persistPendingQueue();
        return true;
      }
      return false;
    }

    if (this.pendingMlsQueue.size === 0) {
      return false;
    }

    this.pendingMlsQueue = new Map();
    void this.persistPendingQueue();
    return true;
  }

  #normalizePersistedGroupEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return { entry, changed: false };
    }

    const next = { ...entry };
    let changed = false;

    const setValue = (key, value) => {
      if (value === undefined) {
        if (Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
        return;
      }
      if (next[key] !== value) {
        next[key] = value;
        changed = true;
      }
    };

    const welcomeB64 = pickBase64String(
      entry.welcomeB64,
      entry.welcome_b64,
      entry.welcome,
      entry.welcomeBuffer,
      entry.welcome_buffer,
    );
    const recoveryWelcomeB64 = pickBase64String(
      entry.recoveryWelcomeB64,
      entry.recovery_welcome_b64,
      entry.welcomeB64,
      entry.welcome_b64,
    );
    setValue('welcomeB64', welcomeB64 || null);
    setValue('recoveryWelcomeB64', recoveryWelcomeB64 || null);
    setValue('welcome', undefined);
    setValue('welcomeBuffer', undefined);
    setValue('welcome_buffer', undefined);
    setValue('welcome_b64', undefined);
    setValue('recovery_welcome_b64', undefined);

    const fetchedAt = coerceTimestampMs(entry.fetchedAt ?? entry.fetched_at ?? null);
    setValue('fetchedAt', fetchedAt ?? 0);
    setValue('fetched_at', undefined);

    const ratchetTreeB64 = pickBase64String(
      entry.ratchetTreeB64,
      entry.ratchet_tree_b64,
      entry.ratchetTree,
      entry.ratchet_tree,
      entry.ratchetTreeBuffer,
      entry.ratchet_tree_buffer,
    );
    setValue('ratchetTree', ratchetTreeB64 || null);
    setValue('ratchetTreeB64', ratchetTreeB64 || null);
    setValue('ratchet_tree', undefined);
    setValue('ratchet_tree_b64', undefined);
    setValue('ratchetTreeBuffer', undefined);
    setValue('ratchet_tree_buffer', undefined);

    const membersSource =
      entry.members ??
      entry.memberIds ??
      entry.member_ids ??
      entry.member_client_ids ??
      entry.clients ??
      [];
    const normalizedMembers = normalizeMemberList(membersSource);
    setValue('members', normalizedMembers);
    setValue('memberIds', undefined);
    setValue('member_ids', undefined);
    setValue('member_client_ids', undefined);
    setValue('clients', undefined);

    const conversationId = ensureString(entry.conversationId ?? entry.conversation_id ?? null);
    setValue('conversationId', conversationId || null);
    setValue('conversation_id', undefined);
    const normalizedEpoch = normalizeEpochOrNull(
      entry.epoch ?? entry.current_epoch ?? entry.currentEpoch,
    );
    setValue('epoch', normalizedEpoch ?? 0);
    setValue('current_epoch', normalizedEpoch ?? 0);
    setValue('currentEpoch', normalizedEpoch ?? 0);
    const normalizedRuntimeEpoch = normalizeEpochOrNull(
      entry.runtimeEpoch ?? entry.runtime_epoch ?? entry.runtime_epoch_no ?? null,
    );
    setValue('runtimeEpoch', normalizedRuntimeEpoch ?? null);
    setValue('runtime_epoch', undefined);
    setValue('runtime_epoch_no', undefined);
    const normalizedLastServerSeq = parseCursor(
      entry.lastServerSeq ?? entry.last_server_seq ?? entry.lastServerSequence ?? null,
    );
    setValue('lastServerSeq', normalizedLastServerSeq ?? 0);
    setValue('last_server_seq', normalizedLastServerSeq ?? 0);
    setValue('lastServerSequence', undefined);
    const normalizedProcessedServerSeqs = normalizeProcessedServerSeqs(
      entry.processedServerSeqs ??
        entry.processed_server_seqs ??
        entry.processedServerSequences ??
        [],
      {
        baseCursor: normalizedLastServerSeq ?? 0,
      },
    );
    setValue('processedServerSeqs', normalizedProcessedServerSeqs);
    setValue('processed_server_seqs', normalizedProcessedServerSeqs);
    setValue('processedServerSequences', undefined);
    const usedKpRef = normalizeHexRefValue(
      entry.used_kp_ref ?? entry.usedKpRef ?? entry.kp_ref ?? entry.kpRef ?? null,
    );
    setValue('used_kp_ref', usedKpRef || null);
    setValue('usedKpRef', usedKpRef || null);
    const welcomeTargetRefLock = normalizeHexRefValue(
      entry.welcomeTargetRefLock ?? entry.welcome_target_ref_lock ?? null,
    );
    setValue('welcomeTargetRefLock', welcomeTargetRefLock || null);
    setValue('welcome_target_ref_lock', undefined);
    const welcomeTargetIdentityId = this.#normalizeIdentityId(
      entry.welcomeTargetIdentityId ?? entry.welcome_target_identity_id ?? null,
    );
    setValue('welcomeTargetIdentityId', welcomeTargetIdentityId || null);
    setValue('welcome_target_identity_id', undefined);
    const welcomeTargetEpoch = normalizeEpochOrNull(
      entry.welcomeTargetEpoch ?? entry.welcome_target_epoch ?? null,
    );
    setValue('welcomeTargetEpoch', welcomeTargetEpoch ?? null);
    setValue('welcome_target_epoch', undefined);
    const welcomeTargetFetchedAt = coerceTimestampMs(
      entry.welcomeTargetFetchedAt ?? entry.welcome_target_fetched_at ?? null,
    );
    setValue('welcomeTargetFetchedAt', welcomeTargetFetchedAt ?? null);
    setValue('welcome_target_fetched_at', undefined);

    const hasPendingField =
      Object.prototype.hasOwnProperty.call(entry, 'pending_welcomes') ||
      Object.prototype.hasOwnProperty.call(entry, 'pendingWelcomes');
    if (hasPendingField) {
      const normalizedPending = normalizePendingWelcomesFromSnapshot(
        {
          ...entry,
          pending_welcomes: Array.isArray(entry.pending_welcomes)
            ? entry.pending_welcomes
            : Array.isArray(entry.pendingWelcomes)
              ? entry.pendingWelcomes
              : [],
        },
        {
          conversationId: conversationId || null,
          groupId: ensureString(entry.groupId ?? entry.group_id ?? null),
          groupHandle: ensureString(entry.groupHandle ?? entry.group_handle ?? null),
        },
      );
      setValue('pending_welcomes', normalizedPending);
      setValue('pendingWelcomes', normalizedPending);
      const pendingHandshakeQueue = buildPendingHandshakeQueue(
        normalizedPending,
        ensureString(entry.groupId ?? entry.group_id ?? null),
      );
      setValue('pending_handshake_queue', pendingHandshakeQueue);
      setValue('pendingHandshakeQueue', pendingHandshakeQueue);
    }

    const hasMissingField =
      Object.prototype.hasOwnProperty.call(entry, 'missing_user_ids') ||
      Object.prototype.hasOwnProperty.call(entry, 'missingUserIds');
    if (hasMissingField) {
      const normalizedMissing = normalizeUserIdList(
        entry.missing_user_ids ?? entry.missingUserIds ?? [],
      );
      setValue('missing_user_ids', normalizedMissing);
      setValue('missingUserIds', normalizedMissing);
    }

    const existingPendingHandshakeQueue = Array.isArray(entry.pending_handshake_queue)
      ? entry.pending_handshake_queue
      : Array.isArray(entry.pendingHandshakeQueue)
        ? entry.pendingHandshakeQueue
        : null;
    if (existingPendingHandshakeQueue) {
      const normalizedPendingHandshakeQueue = buildPendingHandshakeQueue(
        existingPendingHandshakeQueue,
        ensureString(entry.groupId ?? entry.group_id ?? null),
      );
      setValue('pending_handshake_queue', normalizedPendingHandshakeQueue);
      setValue('pendingHandshakeQueue', normalizedPendingHandshakeQueue);
    }

    const serializedGroupState = pickBase64String(
      entry.group_state_b64,
      entry.serializedGroupState,
      entry.groupStateB64,
      null,
    );
    const rebuiltGroupState = serializedGroupState || serializeGroupStateSnapshot(next);
    setValue('group_state_b64', rebuiltGroupState || null);
    setValue('serializedGroupState', rebuiltGroupState || null);
    setValue('groupStateB64', rebuiltGroupState || null);

    return changed ? { entry: next, changed: true } : { entry, changed: false };
  }

  #normalizePersistedGroups(groups) {
    const normalized = {};
    let changed = false;
    Object.entries(groups || {}).forEach(([key, value]) => {
      if (!value || typeof value !== 'object') {
        normalized[key] = value;
        return;
      }
      const { entry, changed: entryChanged } = this.#normalizePersistedGroupEntry(value);
      normalized[key] = entry;
      if (entryChanged) {
        changed = true;
      }
    });
    return { groups: normalized, changed };
  }

  async #coercePersistedMemberLists() {
    let store;
    try {
      store = (await getItem(this.groupsKey)) || {};
    } catch (error) {
      console.warn('[MLS] Failed to read persisted groups while normalizing members', error);
      return;
    }

    let mutated = false;

    Object.entries(store).forEach(([key, entry]) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      if (!containsObjectMembers(entry.members)) {
        return;
      }

      const normalizedMembers = normalizeMemberList(entry.members);
      store[key] = {
        ...entry,
        members: normalizedMembers,
      };
      mutated = true;
    });

    if (mutated) {
      try {
        await setItem(this.groupsKey, store);
      } catch (error) {
        console.warn('[MLS] Failed to persist normalized MLS members', error);
        return;
      }
      this._groupsNormalized = false;
    }
  }

  async #loadGroups() {
    const stored = (await getItem(this.groupsKey)) || {};
    let loaded = stored;
    if (!this._groupsNormalized) {
      const { groups, changed } = this.#normalizePersistedGroups(stored);
      if (changed) {
        await setItem(this.groupsKey, groups);
      }
      this._groupsNormalized = true;
      loaded = changed ? groups : stored;
    }

    // Rehydrate the in-memory conversation->group map from persisted groups so
    // debug/send flows do not depend on earlier runtime-only map population.
    try {
      Object.entries(loaded || {}).forEach(([key, entry]) => {
        if (!entry || typeof entry !== 'object') return;
        const conversationId = ensureString(entry.conversationId ?? entry.conversation_id ?? null);
        const groupId = ensureString(entry.groupId ?? entry.group_id ?? key);
        if (conversationId && groupId) {
          this.conversationGroups.set(conversationId, groupId);
        }
      });
    } catch {}

    return loaded;
  }

  async #saveGroups(groups) {
    const input = groups || {};
    const { groups: normalized } = this.#normalizePersistedGroups(input);
    await setItem(this.groupsKey, normalized);
    this._groupsNormalized = true;
    scheduleMlsBackup(this.userId, 'groups');
  }

  #resolveStoredGroup(groups, { groupId, groupHandle } = {}) {
    if (!groups) {
      return { key: null, entry: null };
    }

    if (groupId && groups[groupId]) {
      return { key: groupId, entry: groups[groupId] };
    }

    const entries = Object.entries(groups);
    if (groupHandle) {
      const found = entries.find(([, entry]) => entry?.groupHandle === groupHandle);
      if (found) {
        return { key: found[0], entry: found[1] };
      }
    }

    if (groupId) {
      const found = entries.find(([, entry]) => entry?.groupId === groupId);
      if (found) {
        return { key: found[0], entry: found[1] };
      }
    }

    return { key: null, entry: null };
  }

  async #persistProtectedWelcomeTargetDrift({
    groupId = null,
    groupHandle = null,
    conversationId = null,
    identityId = null,
    targetRef = null,
    currentLocalRef = null,
    ciphersuite = null,
    source = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    const normalizedTargetRef = normalizeHexRefValue(targetRef);
    const normalizedCurrentLocalRef = normalizeHexRefValue(currentLocalRef);
    if (!normalizedIdentityId || !normalizedTargetRef || !normalizedCurrentLocalRef) {
      return null;
    }

    const groups = await this.#loadGroups().catch(() => null);
    if (!groups || typeof groups !== 'object') {
      return null;
    }

    const { key: resolvedKey, entry: resolvedEntry } = this.#resolveStoredGroup(groups, {
      groupId: ensureString(groupId ?? null),
      groupHandle: ensureString(groupHandle ?? null),
    });
    const storageKey =
      resolvedKey ?? ensureString(groupId ?? null) ?? ensureString(groupHandle ?? null) ?? null;
    if (!storageKey) {
      return null;
    }

    const updated = {
      ...(resolvedEntry && typeof resolvedEntry === 'object' ? resolvedEntry : {}),
      groupId:
        ensureString(resolvedEntry?.groupId ?? groupId ?? null) ??
        ensureString(groupId ?? storageKey) ??
        storageKey,
      groupHandle:
        ensureString(resolvedEntry?.groupHandle ?? groupHandle ?? null) ??
        ensureString(groupHandle ?? groupId ?? storageKey) ??
        null,
      identityId:
        normalizedIdentityId ??
        this.#normalizeIdentityId(
          resolvedEntry?.identityId ?? resolvedEntry?.identity_id ?? null,
        ) ??
        null,
      fetchedAt:
        coerceTimestampMs(resolvedEntry?.fetchedAt ?? resolvedEntry?.fetched_at ?? null) ??
        Date.now(),
      epoch:
        normalizeEpochOrNull(
          resolvedEntry?.epoch ??
            resolvedEntry?.current_epoch ??
            resolvedEntry?.currentEpoch ??
            null,
        ) ?? 0,
      current_epoch:
        normalizeEpochOrNull(
          resolvedEntry?.current_epoch ??
            resolvedEntry?.currentEpoch ??
            resolvedEntry?.epoch ??
            null,
        ) ?? 0,
      currentEpoch:
        normalizeEpochOrNull(
          resolvedEntry?.currentEpoch ??
            resolvedEntry?.current_epoch ??
            resolvedEntry?.epoch ??
            null,
        ) ?? 0,
      runtimeEpoch:
        normalizeEpochOrNull(
          resolvedEntry?.runtimeEpoch ??
            resolvedEntry?.runtime_epoch ??
            resolvedEntry?.runtime_epoch_no ??
            null,
        ) ?? null,
      joined: resolvedEntry?.joined === true,
      protectedWelcomeTargetDrift: true,
      protectedWelcomeTargetDriftIdentityId: normalizedIdentityId,
      protectedWelcomeTargetDriftTargetRef: normalizedTargetRef,
      protectedWelcomeTargetDriftCurrentLocalRef: normalizedCurrentLocalRef,
      protectedWelcomeTargetDriftCiphersuite:
        normalizeSuiteId(ciphersuite ?? resolvedEntry?.ciphersuite ?? null) ?? null,
      protectedWelcomeTargetDriftSource: ensureString(source ?? null) ?? null,
      protectedWelcomeTargetDriftDetectedAt: Date.now(),
      welcomeTargetRefLock:
        normalizedTargetRef ??
        normalizeHexRefValue(
          resolvedEntry?.welcomeTargetRefLock ?? resolvedEntry?.welcome_target_ref_lock ?? null,
        ) ??
        null,
      welcomeTargetIdentityId:
        normalizedIdentityId ??
        this.#normalizeIdentityId(
          resolvedEntry?.welcomeTargetIdentityId ??
            resolvedEntry?.welcome_target_identity_id ??
            null,
        ) ??
        null,
      welcomeTargetFetchedAt:
        coerceTimestampMs(
          resolvedEntry?.welcomeTargetFetchedAt ?? resolvedEntry?.welcome_target_fetched_at ?? null,
        ) ?? Date.now(),
      conversationId:
        ensureString(conversationId ?? resolvedEntry?.conversationId ?? null) ??
        ensureString(resolvedEntry?.conversationId ?? null) ??
        null,
    };
    groups[storageKey] = updated;
    await this.#saveGroups(groups);
    return groups[storageKey];
  }

  async #clearProtectedWelcomeTargetDrift({
    groupId = null,
    groupHandle = null,
    identityId = null,
  } = {}) {
    const groups = await this.#loadGroups().catch(() => null);
    if (!groups || typeof groups !== 'object') {
      return;
    }

    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    const candidateKeys = new Set(
      [ensureString(groupId ?? null), ensureString(groupHandle ?? null)].filter(Boolean),
    );
    let mutated = false;

    for (const [key, entry] of Object.entries(groups)) {
      if (!entry || typeof entry !== 'object') continue;
      const entryGroupId = ensureString(entry.groupId ?? entry.group_id ?? key);
      const entryGroupHandle = ensureString(entry.groupHandle ?? entry.group_handle ?? null);
      const entryIdentityId = this.#normalizeIdentityId(
        entry.protectedWelcomeTargetDriftIdentityId ?? null,
      );
      const matchesKey =
        !candidateKeys.size ||
        candidateKeys.has(String(key)) ||
        (entryGroupId && candidateKeys.has(entryGroupId)) ||
        (entryGroupHandle && candidateKeys.has(entryGroupHandle));
      const matchesIdentity =
        !normalizedIdentityId || (entryIdentityId && entryIdentityId === normalizedIdentityId);
      if (!matchesKey || !matchesIdentity) {
        continue;
      }
      const next = { ...entry };
      let changed = false;
      [
        'protectedWelcomeTargetDrift',
        'protectedWelcomeTargetDriftIdentityId',
        'protectedWelcomeTargetDriftTargetRef',
        'protectedWelcomeTargetDriftCurrentLocalRef',
        'protectedWelcomeTargetDriftCiphersuite',
        'protectedWelcomeTargetDriftSource',
        'protectedWelcomeTargetDriftDetectedAt',
      ].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(next, field) && next[field] != null) {
          next[field] = field === 'protectedWelcomeTargetDrift' ? false : null;
          changed = true;
        }
      });
      if (changed) {
        groups[key] = next;
        mutated = true;
      }
    }

    if (mutated) {
      await this.#saveGroups(groups);
    }
  }

  async #resolveProtectedWelcomeTargetDrift({
    groupId = null,
    groupHandle = null,
    identityId = null,
  } = {}) {
    const groups = await this.#loadGroups().catch(() => null);
    if (!groups || typeof groups !== 'object') {
      return null;
    }

    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
    const resolved = this.#resolveStoredGroup(groups, {
      groupId: ensureString(groupId ?? null),
      groupHandle: ensureString(groupHandle ?? null),
    });
    let entry = resolved?.entry ?? null;
    if (!entry && normalizedIdentityId) {
      entry =
        Object.values(groups).find((candidate) => {
          if (!candidate || typeof candidate !== 'object') {
            return false;
          }
          const candidateIdentityId = this.#normalizeIdentityId(
            candidate?.protectedWelcomeTargetDriftIdentityId ??
              candidate?.identityId ??
              candidate?.identity_id ??
              null,
          );
          if (!candidateIdentityId || candidateIdentityId !== normalizedIdentityId) {
            return false;
          }
          return Boolean(
            normalizeHexRefValue(candidate?.protectedWelcomeTargetDriftTargetRef ?? null) &&
              normalizeHexRefValue(candidate?.protectedWelcomeTargetDriftCurrentLocalRef ?? null),
          );
        }) || null;
    }
    const hasPersistedDrift =
      entry?.protectedWelcomeTargetDrift === true ||
      (normalizeHexRefValue(entry?.protectedWelcomeTargetDriftTargetRef ?? null) &&
        normalizeHexRefValue(entry?.protectedWelcomeTargetDriftCurrentLocalRef ?? null));
    if (!entry || !hasPersistedDrift) {
      return null;
    }
    const entryIdentityId = this.#normalizeIdentityId(
      entry?.protectedWelcomeTargetDriftIdentityId ?? null,
    );
    if (normalizedIdentityId && entryIdentityId && normalizedIdentityId !== entryIdentityId) {
      return null;
    }
    return {
      identityId: entryIdentityId ?? normalizedIdentityId ?? null,
      groupId: ensureString(entry?.groupId ?? null) ?? null,
      groupHandle: ensureString(entry?.groupHandle ?? null) ?? null,
      conversationId: ensureString(entry?.conversationId ?? null) ?? null,
      targetRef: normalizeHexRefValue(entry?.protectedWelcomeTargetDriftTargetRef ?? null),
      currentLocalRef: normalizeHexRefValue(
        entry?.protectedWelcomeTargetDriftCurrentLocalRef ?? null,
      ),
      ciphersuite: normalizeSuiteId(entry?.protectedWelcomeTargetDriftCiphersuite ?? null) ?? null,
      source: ensureString(entry?.protectedWelcomeTargetDriftSource ?? null) ?? null,
      detectedAt: coerceTimestampMs(entry?.protectedWelcomeTargetDriftDetectedAt ?? null),
    };
  }

  #runtimeGroupMatchesEntry(entry, runtimeEntry) {
    if (!entry || !runtimeEntry) return false;
    const runtimeCandidates = new Set(
      [runtimeEntry.groupHandle, runtimeEntry.groupId]
        .map((value) => ensureString(value))
        .filter(Boolean),
    );
    if (!runtimeCandidates.size) return false;
    const entryCandidates = new Set(
      [
        ensureString(entry.groupId),
        ensureString(entry.groupHandle),
        ensureString(resolveRuntimeHandle(entry)),
      ].filter(Boolean),
    );
    if (!entryCandidates.size) return false;
    for (const candidate of entryCandidates) {
      if (runtimeCandidates.has(candidate)) {
        return true;
      }
    }
    return false;
  }

  #runtimeHasGroupForEntry(entry, runtimeGroups = []) {
    if (!entry || !Array.isArray(runtimeGroups) || !runtimeGroups.length) {
      return false;
    }
    return Boolean(this.#resolveRuntimeGroupForEntry(entry, runtimeGroups));
  }

  async #resolveConcreteRuntimeForEpoch({
    groupId = null,
    groupHandle = null,
    fallback = null,
    requiredEpoch = null,
  } = {}) {
    const normalizedGroupId = ensureString(groupId ?? fallback?.groupId ?? fallback?.group_id);
    const normalizedGroupHandle = ensureString(
      groupHandle ?? fallback?.groupHandle ?? fallback?.group_handle,
    );
    if (!normalizedGroupId && !normalizedGroupHandle) {
      return null;
    }

    const latestInfo = await this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
    }).catch(() => null);
    const effectiveInfo = {
      ...(fallback && typeof fallback === 'object' ? fallback : {}),
      ...(latestInfo && typeof latestInfo === 'object' ? latestInfo : {}),
    };
    const probeEntry = {
      ...effectiveInfo,
      groupId: ensureString(
        latestInfo?.groupId ??
          latestInfo?.group_id ??
          normalizedGroupId ??
          fallback?.groupId ??
          fallback?.group_id,
      ),
      groupHandle: ensureString(
        latestInfo?.groupHandle ??
          latestInfo?.group_handle ??
          normalizedGroupHandle ??
          fallback?.groupHandle ??
          fallback?.group_handle,
      ),
    };
    const runtimeGroups = normalizeRuntimeGroups(await workerListGroups().catch(() => []));
    const liveRuntimeGroup = this.#resolveRuntimeGroupForEntry(probeEntry, runtimeGroups);
    if (!liveRuntimeGroup) {
      return null;
    }

    const liveRuntimeEpoch = normalizeEpochOrNull(liveRuntimeGroup?.epoch ?? null);
    const requiredEpochCandidates = [
      requiredEpoch,
      latestInfo?.epoch,
      latestInfo?.currentEpoch,
      latestInfo?.current_epoch,
    ]
      .map((value) => normalizeEpochOrNull(value))
      .filter((value) => value != null);
    const normalizedRequiredEpoch = requiredEpochCandidates.length
      ? Math.max(...requiredEpochCandidates)
      : null;
    if (
      liveRuntimeEpoch == null ||
      liveRuntimeEpoch <= 0 ||
      (normalizedRequiredEpoch != null && liveRuntimeEpoch < normalizedRequiredEpoch)
    ) {
      return null;
    }

    const runtimeState = ensureString(
      latestInfo?.runtimeState ??
        latestInfo?.runtime_state ??
        effectiveInfo?.runtimeState ??
        effectiveInfo?.runtime_state ??
        null,
    );
    if (runtimeState === 'runtime_missing' || runtimeState === 'recovery_required') {
      return null;
    }

    return {
      ...effectiveInfo,
      groupId: probeEntry.groupId,
      groupHandle:
        ensureString(liveRuntimeGroup?.groupHandle ?? null) ?? probeEntry.groupHandle ?? null,
      runtimeHandle:
        ensureString(liveRuntimeGroup?.groupHandle ?? liveRuntimeGroup?.groupId ?? null) ??
        ensureString(resolveRuntimeHandle(effectiveInfo)) ??
        null,
      runtimeEpoch: liveRuntimeEpoch,
      runtime_epoch: liveRuntimeEpoch,
      runtimeLive: true,
      runtime_live: true,
    };
  }

  #getRehydrateBlockedUntil(entry) {
    return coerceTimestampMs(entry?.rehydrateBlockedUntil ?? null);
  }

  #canAttemptWelcomeRehydrate(entry) {
    const blockedUntil = this.#getRehydrateBlockedUntil(entry);
    return !blockedUntil || blockedUntil <= Date.now();
  }

  #hasConcreteRuntimeOwnership(entry, { runtimeHasGroup = false } = {}) {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    if (runtimeHasGroup) {
      return true;
    }

    const runtimeEpoch = normalizeEpochOrNull(
      entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
    );
    if (runtimeEpoch != null && runtimeEpoch > 0) {
      return true;
    }

    return Boolean(entry?.runtimeLive ?? entry?.runtime_live ?? false);
  }

  async #awaitConcreteRuntimeMaterialization({
    groupId = null,
    groupHandle = null,
    conversationId = null,
    requiredEpoch = null,
    source = 'unknown',
    reason = 'runtime_materialization',
    timeoutMs = 5000,
    pollIntervalMs = 250,
  } = {}) {
    const normalizedGroupId = ensureString(groupId ?? null);
    const normalizedGroupHandle = ensureString(groupHandle ?? null);
    const normalizedConversationId = ensureString(conversationId ?? null);
    if (!normalizedGroupId && !normalizedGroupHandle) {
      return null;
    }

    const normalizedTimeoutMs = Number.isFinite(Number(timeoutMs))
      ? Math.max(0, Number(timeoutMs))
      : 0;
    const normalizedPollIntervalMs = Number.isFinite(Number(pollIntervalMs))
      ? Math.max(50, Number(pollIntervalMs))
      : 250;
    const deadline = Date.now() + normalizedTimeoutMs;
    let latestInfo =
      (await this.groupInfo({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
      }).catch(() => null)) || null;

    while (Date.now() <= deadline) {
      const synced = await this.#syncRuntimeGroupState({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        conversationId: normalizedConversationId,
        source,
        reason,
        allowSyncWelcomes: false,
        allowJoinBundleRepair: false,
        allowRuntimeRepair: false,
      }).catch(() => null);

      if (synced) {
        latestInfo = synced;
      } else {
        latestInfo =
          (await this.groupInfo({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
          }).catch(() => null)) || latestInfo;
      }

      const concreteRuntime = await this.#resolveConcreteRuntimeForEpoch({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        fallback: latestInfo,
        requiredEpoch,
      });
      if (concreteRuntime) {
        return concreteRuntime;
      }

      const materializationTargetEpoch = normalizeEpochOrNull(requiredEpoch);
      const observedRuntimeEpoch = normalizeEpochOrNull(
        latestInfo?.runtimeEpoch ??
          latestInfo?.runtime_epoch ??
          latestInfo?.runtime_epoch_no ??
          null,
      );
      if (
        materializationTargetEpoch != null &&
        observedRuntimeEpoch != null &&
        observedRuntimeEpoch > 0 &&
        observedRuntimeEpoch < materializationTargetEpoch
      ) {
        // This wait loop only observes worker materialization; it does not apply
        // pending commits. Return immediately so the owning pending-apply pipeline
        // can replay the missing handshakes instead of burning the full timeout.
        break;
      }

      const remainingMs = deadline - Date.now();
      if (remainingMs <= 0) {
        break;
      }
      await sleep(Math.min(normalizedPollIntervalMs, remainingMs));
    }

    return latestInfo;
  }

  async #probeRuntimeGroupByKnownHandle({
    entry,
    groupId = null,
    groupHandle = null,
    conversationId = null,
    fallbackEpoch = null,
  } = {}) {
    const candidateHandles = [
      ensureString(resolveRuntimeHandle(entry)),
      ensureString(entry?.groupHandle ?? entry?.group_handle ?? null),
      ensureString(groupHandle),
    ].filter(Boolean);
    if (!candidateHandles.length) {
      return null;
    }

    const contextValue =
      ensureString(conversationId) ??
      ensureString(groupId) ??
      ensureString(entry?.groupId ?? entry?.group_id ?? null) ??
      '';

    for (const candidateHandle of candidateHandles) {
      try {
        const probe = await awaitWithTimeout(
          () =>
            workerDebugGroupRuntimeState({
              groupHandle: candidateHandle,
              includeExportKey: false,
              label: HISTORY_DERIVED_KEY_LABEL,
              context: contextValue,
              length: 1,
            }),
          1500,
          null,
        );
        if (!probe?.found) {
          continue;
        }

        const probedEpoch = normalizeEpochOrNull(
          probe?.epoch ??
            probe?.currentEpoch ??
            probe?.snapshot?.epoch ??
            probe?.snapshot?.currentEpoch ??
            null,
        );
        return {
          groupId:
            ensureString(probe?.groupId ?? null) ??
            ensureString(entry?.groupId ?? entry?.group_id ?? null) ??
            ensureString(groupId) ??
            null,
          groupHandle: candidateHandle,
          epoch:
            probedEpoch ??
            normalizeEpochOrNull(
              entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
            ) ??
            normalizeEpochOrNull(
              entry?.epoch ?? entry?.currentEpoch ?? entry?.current_epoch ?? null,
            ) ??
            normalizeEpochOrNull(fallbackEpoch) ??
            null,
          ciphersuite:
            normalizeSuiteId(probe?.ciphersuite ?? null) ??
            normalizeSuiteId(entry?.ciphersuite ?? null) ??
            null,
          __probedByHandle: true,
        };
      } catch {}
    }

    return null;
  }

  async #convergeCurrentMemberAuthoritativeWelcome({
    entry,
    conversationId = null,
    groupId = null,
    groupHandle = null,
    identityId = null,
    source = 'unknown',
    reason = 'current_member_authoritative_welcome',
  } = {}) {
    const normalizedGroupId = ensureString(groupId ?? entry?.groupId ?? entry?.group_id ?? null);
    const normalizedGroupHandle = ensureString(
      groupHandle ?? entry?.groupHandle ?? entry?.group_handle ?? null,
    );
    const normalizedConversationId = ensureString(
      conversationId ?? entry?.conversationId ?? entry?.conversation_id ?? null,
    );
    const activeIdentityId =
      ensureString(identityId) ?? ensureString(this.identityClientId ?? this.identityId ?? null);

    let latestEntry = entry || null;
    const hasConcreteRuntime = (info) =>
      Boolean(
        info &&
          (this.#isGroupReady(info) ||
            this.#hasConcreteRuntimeOwnership(info, {
              runtimeHasGroup:
                ensureString(resolveRuntimeHandle(info)) != null &&
                normalizeEpochOrNull(
                  info?.runtimeEpoch ?? info?.runtime_epoch ?? info?.runtime_epoch_no ?? null,
                ) != null,
            })),
      );

    const refreshLatestEntry = async () => {
      try {
        const groups = await this.#loadGroups();
        const stored = this.#resolveStoredGroup(groups, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
        });
        if (stored?.entry) {
          latestEntry = stored.entry;
          return latestEntry;
        }
      } catch {}

      const info = await this.groupInfo({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
      }).catch(() => null);
      if (info) {
        latestEntry = info;
      }
      return latestEntry;
    };

    const finalizeObservedRuntimeOwnership = async (candidate) => {
      const runtimeHandle =
        ensureString(resolveRuntimeHandle(candidate)) ??
        ensureString(candidate?.groupHandle ?? null) ??
        null;
      const runtimeEpoch =
        normalizeEpochOrNull(
          candidate?.runtimeEpoch ??
            candidate?.runtime_epoch ??
            candidate?.runtime_epoch_no ??
            null,
        ) ??
        normalizeEpochOrNull(
          candidate?.epoch ?? candidate?.currentEpoch ?? candidate?.current_epoch ?? null,
        );
      if (!runtimeHandle || !(runtimeEpoch != null && runtimeEpoch > 0) || !normalizedGroupId) {
        return candidate;
      }

      const finalized =
        (await this.#updateGroupEntry(normalizedGroupId, (prev) => {
          const prevEpoch = normalizeEpochOrNull(
            prev?.epoch ?? prev?.currentEpoch ?? prev?.current_epoch ?? null,
          );
          const prevRuntimeEpoch = normalizeEpochOrNull(
            prev?.runtimeEpoch ?? prev?.runtime_epoch ?? prev?.runtime_epoch_no ?? null,
          );
          const effectiveEpoch =
            prevEpoch != null ? Math.max(prevEpoch, runtimeEpoch) : runtimeEpoch;
          const effectiveRuntimeEpoch =
            prevRuntimeEpoch != null ? Math.max(prevRuntimeEpoch, runtimeEpoch) : runtimeEpoch;
          return this.#finalizeGroupReady({
            ...(prev || {}),
            ...(candidate || {}),
            groupId: normalizedGroupId,
            groupHandle:
              ensureString(candidate?.groupHandle ?? normalizedGroupHandle ?? null) ??
              ensureString(prev?.groupHandle ?? null) ??
              runtimeHandle,
            runtimeHandle,
            runtimeEpoch: effectiveRuntimeEpoch,
            runtime_epoch: effectiveRuntimeEpoch,
            epoch: effectiveEpoch,
            currentEpoch: effectiveEpoch,
            current_epoch: effectiveEpoch,
            joined: true,
            pending_welcomes: [],
            pendingWelcomes: [],
            runtimeLive: true,
            runtime_live: true,
            needsRecovery: false,
            recoveryReason: null,
          });
        }).catch(() => null)) || candidate;

      const pendingWelcomeIds = getPendingWelcomesList(finalized)
        .map((pending) => ensureString(pending?.welcome_id ?? pending?.welcomeId ?? null))
        .filter(Boolean);
      if (pendingWelcomeIds.length) {
        await this.acknowledgeWelcomes({
          groupId: normalizedGroupId,
          conversationId: normalizedConversationId ?? undefined,
          welcomeIds: pendingWelcomeIds,
          clientId: activeIdentityId,
        }).catch(() => null);
      }

      await refreshLatestEntry();
      return latestEntry || finalized;
    };

    for (let attempt = 0; attempt < 2; attempt += 1) {
      await refreshLatestEntry();
      if (hasConcreteRuntime(latestEntry)) {
        return { repaired: latestEntry };
      }

      const canRehydrate =
        latestEntry &&
        this.#canAttemptPersistedWelcomeRecovery(latestEntry, {
          source: `${source || 'unknown'}:current_member_authoritative_welcome`,
        });
      if (canRehydrate) {
        const restored = await this.#rehydrateGroupFromWelcome(latestEntry, {
          allowForceRejoinRecovery: false,
          source: `${source || 'unknown'}:current_member_authoritative_welcome`,
        }).catch(() => null);
        if (restored?.groupHandle) {
          const restoredGroupId =
            ensureString(restored?.groupId ?? normalizedGroupId ?? null) ?? normalizedGroupId;
          const restoredGroupHandle =
            ensureString(restored?.groupHandle ?? normalizedGroupHandle ?? null) ??
            normalizedGroupHandle;
          if (restoredGroupId) {
            try {
              const groups = await this.#loadGroups();
              groups[restoredGroupId] = {
                ...(groups[restoredGroupId] || {}),
                ...restored,
                groupId: restoredGroupId,
                groupHandle: restoredGroupHandle,
              };
              await this.#saveGroups(groups);
              latestEntry = groups[restoredGroupId];
            } catch {
              latestEntry = {
                ...restored,
                groupId: restoredGroupId,
                groupHandle: restoredGroupHandle,
              };
            }
          } else {
            latestEntry = restored;
          }
          const materialized = await this.#awaitConcreteRuntimeMaterialization({
            groupId: restoredGroupId,
            groupHandle: restoredGroupHandle,
            conversationId:
              ensureString(
                restored?.conversationId ??
                  restored?.conversation_id ??
                  normalizedConversationId ??
                  null,
              ) ?? normalizedConversationId,
            source,
            reason: `${reason}_authoritative_welcome_materialization`,
            timeoutMs: 3000,
            pollIntervalMs: 150,
          }).catch(() => null);
          if (materialized) {
            latestEntry = materialized;
          } else {
            await refreshLatestEntry();
          }
          latestEntry = await finalizeObservedRuntimeOwnership(latestEntry);
          if (hasConcreteRuntime(latestEntry)) {
            return { repaired: latestEntry };
          }
        }
      }

      const synced = await this.#syncRuntimeGroupState({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        conversationId: normalizedConversationId,
        source,
        reason: `${reason}_authoritative_welcome_followup`,
        allowSyncWelcomes: false,
        allowJoinBundleRepair: false,
        allowRuntimeRepair: false,
      }).catch(() => null);
      if (synced) {
        latestEntry = synced;
      } else {
        await refreshLatestEntry();
      }
      await refreshLatestEntry();
      latestEntry = await finalizeObservedRuntimeOwnership(latestEntry);

      if (hasConcreteRuntime(latestEntry)) {
        return { repaired: latestEntry };
      }

      const unresolvedWelcome =
        latestEntry &&
        this.#hasUnresolvedBootstrapWelcome(latestEntry, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          activeIdentityId,
        });
      if (!unresolvedWelcome && !canRehydrate) {
        break;
      }

      try {
        await this.syncWelcomes({
          conversationId: normalizedConversationId ?? undefined,
          groupId: normalizedGroupId,
          __pipelineBypass: true,
        });
      } catch (error) {
        console.warn(
          '[MLS] Failed authoritative welcome convergence sync while bootstrapping runtime',
          {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            identityId: activeIdentityId,
            source,
            reason,
            error,
          },
        );
      }
    }

    await refreshLatestEntry();
    return {
      repaired: latestEntry || null,
      skipped: true,
      reason:
        latestEntry &&
        this.#hasUnresolvedBootstrapWelcome(latestEntry, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          activeIdentityId,
        })
          ? 'awaiting_authoritative_welcome_bootstrap'
          : 'current_member_welcome_runtime_followup',
    };
  }

  #hasAuthoritativeBootstrapWelcome(entry, { pendingWelcomeCount = null } = {}) {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const unresolvedPendingWelcomeCount =
      pendingWelcomeCount == null ? getPendingWelcomesList(entry).length : pendingWelcomeCount;
    if (unresolvedPendingWelcomeCount > 0) {
      return true;
    }

    const persistedWelcomeB64 = pickBase64String(
      entry?.recoveryWelcomeB64,
      entry?.recovery_welcome_b64,
      entry?.welcomeB64,
      entry?.welcome_b64,
      entry?.welcome,
      entry?.welcomeBuffer,
      entry?.welcome_buffer,
    );
    if (!persistedWelcomeB64 || !this.#canAttemptWelcomeRehydrate(entry)) {
      return false;
    }

    if (!this.#hasJoinedGroupHistory(entry)) {
      return true;
    }

    return (
      this.#isBootstrapConvergenceActive(entry) &&
      !this.#isGroupReady(entry) &&
      this.#hasActiveRuntimeBootstrapTaskForEntry(entry)
    );
  }

  #hasJoinedGroupHistory(entry) {
    return hasJoinedGroupHistory(entry);
  }

  #hasActiveRuntimeBootstrapTaskForEntry(entry) {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const runtimeBootstrapKey = this.#resolveRuntimeBootstrapKey({
      groupId: ensureString(entry?.groupId ?? entry?.group_id ?? null),
      groupHandle: ensureString(entry?.groupHandle ?? entry?.group_handle ?? null),
      conversationId: ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
    });
    if (!runtimeBootstrapKey) {
      return false;
    }

    const task = this.runtimeBootstrapTasks?.get(runtimeBootstrapKey) || null;
    if (!task || typeof task.then !== 'function') {
      return false;
    }

    const startedAt = Number(this.runtimeBootstrapTaskStartedAt?.get(runtimeBootstrapKey) || 0);
    if (!(Number.isFinite(startedAt) && startedAt > 0)) {
      return true;
    }

    const ageMs = Date.now() - startedAt;
    if (
      Number.isFinite(RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS) &&
      RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS > 0 &&
      ageMs > RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS
    ) {
      return false;
    }

    return true;
  }

  #resolveEntryMembershipState(entry, { activeIdentityId = null } = {}) {
    if (!entry || typeof entry !== 'object') {
      return {
        activeIdentityIds: [],
        members: [],
        currentIdentityMember: false,
        currentUserMissing: false,
        pendingSetup: false,
        runtimeBootstrapRequired: false,
        welcomeRequired: false,
      };
    }

    const activeIdentityIds = getIdentityCandidates(entry, {
      identityId: activeIdentityId ?? null,
      identityIds: [
        ensureString(this.identityClientId),
        ensureString(this.identityId),
        ensureString(this.identityRecord?.identityId ?? null),
      ],
    });
    const members = getNormalizedMembers(entry);
    const missingUsers = normalizeUserIdList(
      entry?.missing_user_ids ?? entry?.missingUserIds ?? [],
    );
    const currentUserId = ensureString(this.userId);
    const currentUserMissing =
      Boolean(currentUserId) &&
      missingUsers
        .map((value) => ensureString(value))
        .filter(Boolean)
        .includes(currentUserId);
    const pendingSetup = Boolean(entry?.pending_setup ?? entry?.pendingSetup ?? currentUserMissing);
    const currentIdentityMember = hasCurrentIdentityMembership(entry, {
      identityIds: activeIdentityIds,
    });
    const pendingWelcomeCount = getPendingWelcomesList(entry).length;
    const hasPersistedWelcomePayload = Boolean(
      pickBase64String(
        entry?.recoveryWelcomeB64,
        entry?.recovery_welcome_b64,
        entry?.welcomeB64,
        entry?.welcome_b64,
        entry?.welcome,
        null,
      ),
    );
    const runtimeBootstrapRequired =
      activeIdentityIds.length > 0 &&
      currentIdentityMember &&
      !this.#hasJoinedGroupHistory(entry) &&
      (pendingSetup || pendingWelcomeCount > 0 || hasPersistedWelcomePayload);
    const welcomeRequired =
      activeIdentityIds.length > 0 &&
      ((!currentIdentityMember && (members.length > 0 || pendingSetup)) ||
        runtimeBootstrapRequired);

    return {
      activeIdentityIds,
      members,
      currentIdentityMember,
      currentUserMissing,
      pendingSetup,
      runtimeBootstrapRequired,
      welcomeRequired,
    };
  }

  #resolvePendingApplyRuntimeAuthority(
    groupInfo,
    { groupId = null, groupHandle = null, joinedDuringWelcomeSync = false } = {},
  ) {
    if (!groupInfo || typeof groupInfo !== 'object') {
      return {
        runtimeState: 'missing',
        runtimeBehindSnapshot: false,
        effectivePendingWelcomeCount: 0,
        shouldDeferEscalation: false,
      };
    }

    const activeIdentityId = ensureString(this.identityClientId ?? this.identityId ?? null);
    const normalizedGroupId = ensureString(groupInfo?.groupId ?? groupId ?? null);
    const normalizedGroupHandle = ensureString(groupInfo?.groupHandle ?? groupHandle ?? null);
    const runtimeState =
      ensureString(groupInfo?.runtimeState ?? groupInfo?.runtime_state ?? null) ??
      this.#classifyRuntimeState(groupInfo);
    const membershipState = this.#resolveEntryMembershipState(groupInfo, {
      activeIdentityId,
    });
    const pendingWelcomeCount = getPendingWelcomesList(groupInfo).length;
    const bootstrapWelcomeOwned = this.#hasUnresolvedBootstrapWelcome(groupInfo, {
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
      activeIdentityId,
    });
    const effectivePendingWelcomeCount = Math.max(
      pendingWelcomeCount,
      bootstrapWelcomeOwned ? 1 : 0,
    );
    const infoEpoch = normalizeEpochOrNull(groupInfo?.epoch ?? null);
    const runtimeEpoch = normalizeEpochOrNull(
      groupInfo?.runtimeEpoch ?? groupInfo?.runtime_epoch ?? groupInfo?.runtime_epoch_no ?? null,
    );
    const runtimeBehindSnapshot =
      infoEpoch != null && runtimeEpoch != null && runtimeEpoch < infoEpoch;
    const shouldDeferEscalation =
      joinedDuringWelcomeSync ||
      effectivePendingWelcomeCount > 0 ||
      membershipState.runtimeBootstrapRequired ||
      membershipState.welcomeRequired ||
      this.#shouldDeferRecoveryDuringBootstrap(groupInfo, {
        source: 'pullAndApplyPendingWelcomeOrCommit',
        reason: 'pending_apply_runtime_authority',
        pendingWelcomeCount: effectivePendingWelcomeCount,
        runtimeHasGroup: runtimeState !== 'runtime_missing',
      });

    return {
      runtimeState,
      runtimeBehindSnapshot,
      effectivePendingWelcomeCount,
      bootstrapWelcomeOwned,
      membershipState,
      shouldDeferEscalation,
    };
  }

  #shouldSuppressPendingWelcomeForJoinedHistory(
    entry,
    { activeIdentityId = null, welcomeEpoch = null } = {},
  ) {
    if (!entry || typeof entry !== 'object') {
      return false;
    }

    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: activeIdentityId ?? null,
    });
    const alreadyJoinedCurrentIdentity =
      membershipState.currentIdentityMember && this.#hasJoinedGroupHistory(entry);
    if (!alreadyJoinedCurrentIdentity) {
      return false;
    }

    const runtimeEpoch = normalizeEpochOrNull(
      entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
    );
    const snapshotEpoch = normalizeEpochOrNull(
      entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
    );
    const normalizedWelcomeEpoch = normalizeEpochOrNull(welcomeEpoch);
    const runtimeState =
      ensureString(entry?.runtimeState ?? entry?.runtime_state ?? null) ??
      this.#classifyRuntimeState(entry);
    const runtimeHandle = resolveRuntimeHandle(entry);
    const runtimeIncomplete =
      Boolean(entry?.needsRecovery) ||
      !this.#isGroupReady(entry) ||
      !runtimeHandle ||
      runtimeEpoch == null ||
      runtimeEpoch <= 0 ||
      runtimeState === 'runtime_missing' ||
      runtimeState === 'runtime_behind' ||
      runtimeState === 'recovery_required';

    if (runtimeIncomplete) {
      return false;
    }

    if (
      normalizedWelcomeEpoch != null &&
      runtimeEpoch != null &&
      runtimeEpoch < normalizedWelcomeEpoch
    ) {
      return false;
    }

    if (snapshotEpoch != null && runtimeEpoch != null && runtimeEpoch < snapshotEpoch) {
      return false;
    }

    return true;
  }

  async #ensureActiveMembershipBootstrap({
    entry,
    conversationId = null,
    groupId = null,
    groupHandle = null,
    identityId = null,
    ciphersuite = null,
    source = 'unknown',
    reason = 'membership_bootstrap',
  } = {}) {
    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: identityId ?? null,
    });
    if (
      !membershipState.pendingSetup &&
      !membershipState.currentUserMissing &&
      !membershipState.welcomeRequired
    ) {
      return { skipped: true, membershipState };
    }

    const normalizedConversationId = ensureString(
      conversationId ?? entry?.conversationId ?? entry?.conversation_id ?? null,
    );
    const normalizedGroupId = ensureString(groupId ?? entry?.groupId ?? entry?.group_id ?? null);
    const normalizedGroupHandle = ensureString(
      groupHandle ?? entry?.groupHandle ?? entry?.group_handle ?? null,
    );
    const activeIdentity =
      ensureString(identityId) ??
      membershipState.activeIdentityIds[0] ??
      ensureString(this.identityClientId ?? this.identityId ?? null);
    const delegateCurrentMemberRuntimeBootstrap =
      membershipState.currentIdentityMember &&
      membershipState.welcomeRequired &&
      !membershipState.pendingSetup &&
      !membershipState.currentUserMissing;

    if (!activeIdentity || !normalizedGroupId) {
      return { skipped: true, reason: 'missing_membership_context', membershipState };
    }

    if (delegateCurrentMemberRuntimeBootstrap) {
      if (typeof console?.info === 'function') {
        console.info(
          '[MLS] Membership bootstrap delegating current-member welcome runtime follow-up',
          {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            identityId: activeIdentity,
            source,
            reason,
          },
        );
      }
      return {
        membershipState,
        skipped: true,
        reason: 'delegated_current_member_runtime_bootstrap',
      };
    }

    const pendingWelcomes = getPendingWelcomesList(entry);
    const welcomeAlreadyTargetedToActiveIdentity = pendingWelcomes.some((pending) => {
      if (!pending || typeof pending !== 'object') {
        return false;
      }
      const targetIdentityId = this.#normalizeIdentityId(
        pending?.to_client_id ??
          pending?.toClientId ??
          pending?.client_id ??
          pending?.clientId ??
          pending?.identity_id ??
          pending?.identityId ??
          null,
      );
      if (targetIdentityId && targetIdentityId !== activeIdentity) {
        return false;
      }
      return Boolean(
        pickBase64String(pending?.welcome_b64, pending?.welcomeB64, pending?.welcome, null) ||
          normalizeHexRefValue(pending?.used_kp_ref ?? pending?.usedKpRef ?? null),
      );
    });
    const persistedWelcomeTargetRef = normalizeHexRefValue(
      entry?.welcomeTargetRefLock ??
        entry?.welcome_target_ref_lock ??
        this.#resolveWelcomeTargetRef({
          welcomeB64: pickBase64String(
            entry?.recoveryWelcomeB64,
            entry?.recovery_welcome_b64,
            entry?.welcomeB64,
            entry?.welcome_b64,
            entry?.welcome,
            null,
          ),
          usedKpRef: entry?.used_kp_ref ?? entry?.usedKpRef ?? null,
        }) ??
        null,
    );
    const wantsMembershipKeyPackagePublish =
      !welcomeAlreadyTargetedToActiveIdentity &&
      !persistedWelcomeTargetRef &&
      (membershipState.pendingSetup ||
        membershipState.currentUserMissing ||
        membershipState.welcomeRequired);
    const membershipPublishDisposition = wantsMembershipKeyPackagePublish
      ? await this.#resolveBootstrapPublishDisposition({
          identityId: activeIdentity,
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          conversationId: normalizedConversationId,
          ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
        }).catch(() => null)
      : null;
    const shouldPublishMembershipKeyPackage =
      wantsMembershipKeyPackagePublish && membershipPublishDisposition?.allowPublish !== false;

    if (shouldPublishMembershipKeyPackage) {
      const forcePublish = membershipState.pendingSetup || membershipState.currentUserMissing;
      try {
        await this.publishKeyPackageAndSync(activeIdentity, {
          forcePublish,
          retryOnMissingClientId: true,
        });
      } catch (error) {
        console.warn('[MLS] Failed to publish key package while bootstrapping membership', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    } else if (
      membershipState.pendingSetup ||
      membershipState.currentUserMissing ||
      membershipState.welcomeRequired
    ) {
      try {
        const skipReason =
          membershipPublishDisposition?.reason ??
          (welcomeAlreadyTargetedToActiveIdentity || persistedWelcomeTargetRef
            ? 'welcome_target_locked'
            : 'publish_not_required');
        console.info('[MLS] Membership bootstrap skipping key package publish', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          skipReason,
          pendingWelcomeCount: pendingWelcomes.length,
          welcomeTargetRef:
            persistedWelcomeTargetRef ?? membershipPublishDisposition?.targetRef ?? null,
        });
      } catch {}
    }

    const targetEpoch = normalizeEpochOrNull(entry?.epoch ?? entry?.current_epoch ?? null) ?? 0;
    if (normalizedConversationId && (membershipState.pendingSetup || targetEpoch <= 0)) {
      try {
        await this.#maybeProbeBootstrap({
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
          epoch: targetEpoch,
        });
      } catch (error) {
        console.warn('[MLS] Failed bootstrap probe while bootstrapping membership', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    }

    if (normalizedConversationId) {
      try {
        const refreshedSnapshot = await getGroupSnapshot({
          conversationId: normalizedConversationId,
          force: true,
          clientId: activeIdentity,
          reissue: true,
        });
        if (refreshedSnapshot) {
          await this.#ingestPendingWelcomesFromSnapshot(refreshedSnapshot, {
            conversationId: normalizedConversationId,
            identityId: activeIdentity,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
          });
        }
      } catch (error) {
        console.warn('[MLS] Failed to refetch snapshot while bootstrapping membership', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    }

    try {
      await this.syncWelcomes({
        conversationId: normalizedConversationId ?? undefined,
        groupId: normalizedGroupId,
        __pipelineBypass: true,
      });
    } catch (error) {
      console.warn('[MLS] Failed to sync welcomes while bootstrapping membership', {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        identityId: activeIdentity,
        source,
        reason,
        error,
      });
    }

    const preferServerManagedWelcomeRecoveryOnly =
      this.serverManagedWelcomes &&
      !membershipState.pendingSetup &&
      !membershipState.currentUserMissing;

    let repaired = await this.#syncRuntimeGroupState({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
      conversationId: normalizedConversationId,
      source,
      reason,
      allowSyncWelcomes: false,
      allowJoinBundleRepair: !preferServerManagedWelcomeRecoveryOnly,
    }).catch(() => null);

    if (
      !preferServerManagedWelcomeRecoveryOnly &&
      !this.#isGroupReady(repaired) &&
      typeof this.forceJoinBundle === 'function'
    ) {
      try {
        const joined = await this.forceJoinBundle({
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          identityId: activeIdentity,
          ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
          preferReissueOnAlreadyMemberNoWelcome: true,
          __pipelineBypass: true,
        });
        if (joined) {
          repaired = joined;
        }
      } catch (error) {
        console.warn('[MLS] Failed to force join while bootstrapping membership', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    }

    if (!this.#isGroupReady(repaired) && !this.serverManagedWelcomes) {
      const selfWelcomed = await this.#bootstrapRuntimeWithSelfWelcome({
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: activeIdentity,
        ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
        source,
        reason,
      }).catch(() => null);

      if (this.#isGroupReady(selfWelcomed)) {
        repaired = selfWelcomed;
      }
    }

    return {
      membershipState,
      repaired: repaired || null,
    };
  }

  #shouldBootstrapRuntimeForCurrentIdentity(entry, { activeIdentityId = null } = {}) {
    if (!entry || typeof entry !== 'object') {
      return false;
    }
    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: activeIdentityId ?? null,
    });
    if (!membershipState.currentIdentityMember) {
      return false;
    }
    if (membershipState.pendingSetup || membershipState.welcomeRequired) {
      return false;
    }
    const snapshotEpoch = normalizeEpochOrNull(
      entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
    );
    if (!(snapshotEpoch != null && snapshotEpoch > 0)) {
      return false;
    }
    if (entry?.runtimeLive === false || entry?.runtime_live === false) {
      return true;
    }
    const runtimeHandle = resolveRuntimeHandle(entry);
    const runtimeEpoch = normalizeEpochOrNull(
      entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
    );
    return !runtimeHandle || !(runtimeEpoch != null && runtimeEpoch > 0);
  }

  async #resolveLiveRuntimeGroupForEntry(entry, { groupId = null, groupHandle = null } = {}) {
    const candidate = entry && typeof entry === 'object' ? { ...entry } : {};
    const normalizedGroupId = ensureString(
      groupId ?? candidate?.groupId ?? candidate?.group_id ?? null,
    );
    const normalizedGroupHandle = ensureString(
      groupHandle ?? candidate?.groupHandle ?? candidate?.group_handle ?? null,
    );
    const normalizedRuntimeHandle = ensureString(resolveRuntimeHandle(candidate));

    if (!normalizedGroupId && !normalizedGroupHandle && !normalizedRuntimeHandle) {
      return { runtimeGroups: [], runtimeGroup: null };
    }

    const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
    const runtimeGroup = this.#resolveRuntimeGroupForEntry(
      {
        ...candidate,
        groupId: normalizedGroupId ?? candidate?.groupId ?? null,
        groupHandle: normalizedGroupHandle ?? candidate?.groupHandle ?? null,
        runtimeHandle: normalizedRuntimeHandle ?? candidate?.runtimeHandle ?? null,
      },
      runtimeGroups,
    );

    return {
      runtimeGroups,
      runtimeGroup: runtimeGroup || null,
    };
  }

  async #bootstrapRuntimeWithSelfWelcome({
    conversationId = null,
    groupId = null,
    groupHandle = null,
    identityId = null,
    ciphersuite = null,
    allowServerManaged = false,
    source = 'unknown',
    reason = 'self_welcome_runtime_bootstrap',
  } = {}) {
    if (this.serverManagedWelcomes && allowServerManaged !== true) {
      if (typeof console?.info === 'function') {
        console.info('[MLS] Skipping self-welcome runtime bootstrap for server-managed group', {
          conversationId,
          groupId,
          groupHandle,
          identityId,
          source,
          reason,
        });
      }
      return null;
    }

    const normalizedConversationId = ensureString(conversationId ?? null);
    const normalizedGroupId = ensureString(groupId ?? null);
    const normalizedGroupHandle = ensureString(groupHandle ?? null);
    const activeIdentity =
      ensureString(identityId) ?? ensureString(this.identityClientId ?? this.identityId ?? null);

    if (!normalizedGroupId || !activeIdentity) {
      return null;
    }

    const authToken =
      typeof getAuthToken === 'function' && getAuthToken() ? `Bearer ${getAuthToken()}` : undefined;

    let minted = null;
    try {
      minted = await joinWithSelfWelcome(normalizedGroupId, activeIdentity, authToken, {
        conversationId: normalizedConversationId ?? undefined,
        preferredCiphersuite: ciphersuite ?? this.preferredCiphersuite,
        supportedCiphersuites: this.runtimeSupportedSuites ?? [],
      });
    } catch (error) {
      console.warn('[MLS] Failed to mint self welcome while bootstrapping runtime', {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: activeIdentity,
        source,
        reason,
        error,
      });
      return null;
    }

    if (!minted?.welcomeB64 && !minted?.welcomeBuffer) {
      return null;
    }

    try {
      const joined = await this.joinGroup({
        identityId: activeIdentity,
        welcome: minted.welcomeBuffer ?? null,
        welcomeB64: minted.welcomeB64 ?? null,
        ratchetTree: minted.ratchetTreeBuffer ?? null,
        ratchetTreeB64: minted.ratchetTreeB64 ?? null,
        groupId: normalizedGroupId,
        welcomeSource: minted.welcomeSource ?? 'mls-group',
        expectedEpoch: minted.epoch ?? null,
        welcomeSuite: minted.welcomeSuite ?? null,
        forceRejoin: true,
      });
      if (!joined) {
        return null;
      }
      const joinedWasReady = this.#isGroupReady(joined);
      if (joined?.ready === true || joinedWasReady) {
        return this.#finalizeGroupReady({
          ...joined,
          groupId: joined.groupId ?? normalizedGroupId,
          groupHandle: joined.groupHandle ?? normalizedGroupHandle ?? null,
          identityId: joined.identityId ?? activeIdentity,
          needsRecovery: false,
          recoveryReason: null,
        });
      }

      const liveRuntime = await this.#resolveLiveRuntimeGroupForEntry(
        {
          ...joined,
          groupId: joined.groupId ?? normalizedGroupId,
          groupHandle: joined.groupHandle ?? normalizedGroupHandle ?? null,
          runtimeHandle:
            joined.runtimeHandle ?? joined.groupHandle ?? normalizedGroupHandle ?? null,
        },
        {
          groupId: joined.groupId ?? normalizedGroupId,
          groupHandle: joined.groupHandle ?? normalizedGroupHandle ?? null,
        },
      ).catch(() => null);
      const matchedLiveRuntime = liveRuntime?.runtimeGroup || null;
      const liveRuntimeEpoch = normalizeEpochOrNull(matchedLiveRuntime?.epoch ?? null);

      return this.#finalizeGroupReady({
        ...joined,
        groupId: joined.groupId ?? normalizedGroupId,
        groupHandle: joined.groupHandle ?? normalizedGroupHandle ?? null,
        runtimeHandle:
          ensureString(matchedLiveRuntime?.groupHandle ?? null) ??
          joined.runtimeHandle ??
          joined.groupHandle ??
          normalizedGroupHandle ??
          null,
        runtimeEpoch:
          liveRuntimeEpoch ??
          normalizeEpochOrNull(joined.runtimeEpoch ?? joined.runtime_epoch ?? joined.epoch ?? null),
        identityId: joined.identityId ?? activeIdentity,
        needsRecovery: joinedWasReady ? false : !matchedLiveRuntime,
        recoveryReason: joinedWasReady || matchedLiveRuntime ? null : 'missing_runtime_group',
      });
    } catch (error) {
      console.warn('[MLS] Failed to apply self welcome while bootstrapping runtime', {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: activeIdentity,
        source,
        reason,
        error,
      });
      return null;
    }
  }

  async #requestServerManagedRuntimeRecoveryWelcome({
    conversationId = null,
    groupId = null,
    groupHandle = null,
    identityId = null,
    ciphersuite = null,
    source = 'unknown',
    reason = 'runtime_recovery_welcome',
    refreshSnapshot = true,
    syncAfterRequest = true,
  } = {}) {
    if (!this.serverManagedWelcomes) {
      return { skipped: true, reason: 'not_server_managed' };
    }

    const normalizedConversationId = ensureString(conversationId ?? null);
    const normalizedGroupId = ensureString(groupId ?? null);
    const normalizedGroupHandle = ensureString(groupHandle ?? null);
    const activeIdentity =
      ensureString(identityId) ?? ensureString(this.identityClientId ?? this.identityId ?? null);

    if (!normalizedGroupId || !activeIdentity) {
      return { skipped: true, reason: 'missing_recovery_context' };
    }

    let keyPackageB64 = null;
    try {
      const exported = await this.exportKeyPackage(activeIdentity);
      keyPackageB64 = exported?.keyPackage || null;
    } catch {}

    if (!keyPackageB64) {
      try {
        await this.publishKeyPackageAndSync(activeIdentity, {
          forcePublish: true,
          retryOnMissingClientId: true,
        });
      } catch (error) {
        console.warn('[MLS] Failed to force publish key package for runtime recovery welcome', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }

      try {
        const exported = await this.exportKeyPackage(activeIdentity);
        keyPackageB64 = exported?.keyPackage || null;
      } catch {}
    }

    const authToken =
      typeof getAuthToken === 'function' && getAuthToken() ? `Bearer ${getAuthToken()}` : undefined;

    try {
      await this.#reissueWelcomesIfNeeded({
        groupId: normalizedGroupId,
        identityId: activeIdentity,
        keyPackageB64,
        token: authToken,
        ciphersuite: ciphersuite ?? this.preferredCiphersuite ?? null,
      });
    } catch (error) {
      console.warn('[MLS] Failed to request server-managed runtime recovery welcome', {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: activeIdentity,
        source,
        reason,
        error,
      });
      return { skipped: false, error };
    }

    if (normalizedConversationId && refreshSnapshot) {
      try {
        const refreshedSnapshot = await getGroupSnapshot({
          conversationId: normalizedConversationId,
          force: true,
          clientId: activeIdentity,
          reissue: true,
        });
        if (refreshedSnapshot) {
          const ingested = await this.#ingestPendingWelcomesFromSnapshot(refreshedSnapshot, {
            conversationId: normalizedConversationId,
            identityId: activeIdentity,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
          });
          if (Array.isArray(ingested?.joined) && ingested.joined.length) {
            const matched =
              ingested.joined.find((item) => {
                if (!item) return false;
                if (normalizedGroupId && item.groupId === normalizedGroupId) return true;
                if (normalizedGroupHandle && item.groupHandle === normalizedGroupHandle) {
                  return true;
                }
                return false;
              }) ?? ingested.joined[0];
            if (matched) {
              return { repaired: matched };
            }
          }
        }
      } catch (error) {
        console.warn('[MLS] Failed to refetch snapshot after runtime recovery welcome request', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    }

    if (syncAfterRequest) {
      try {
        const synced = await this.syncWelcomes({
          conversationId: normalizedConversationId ?? undefined,
          groupId: normalizedGroupId,
          __pipelineBypass: true,
        });
        if (Array.isArray(synced?.joined) && synced.joined.length) {
          const matched =
            synced.joined.find((item) => {
              if (!item) return false;
              if (normalizedGroupId && item.groupId === normalizedGroupId) return true;
              if (normalizedGroupHandle && item.groupHandle === normalizedGroupHandle) {
                return true;
              }
              return false;
            }) ?? synced.joined[0];
          if (matched) {
            return { repaired: matched };
          }
        }
      } catch (error) {
        console.warn('[MLS] Failed to sync welcomes after runtime recovery welcome request', {
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          identityId: activeIdentity,
          source,
          reason,
          error,
        });
      }
    }

    return { skipped: false };
  }

  async #ensureCurrentIdentityRuntimeBootstrap({
    entry,
    conversationId = null,
    groupId = null,
    groupHandle = null,
    identityId = null,
    ciphersuite = null,
    source = 'unknown',
    reason = 'runtime_bootstrap',
  } = {}) {
    const normalizedConversationId = ensureString(
      conversationId ?? entry?.conversationId ?? entry?.conversation_id ?? null,
    );
    const normalizedGroupId = ensureString(groupId ?? entry?.groupId ?? entry?.group_id ?? null);
    const normalizedGroupHandle = ensureString(
      groupHandle ?? entry?.groupHandle ?? entry?.group_handle ?? null,
    );
    const activeIdentity =
      ensureString(identityId) ?? ensureString(this.identityClientId ?? this.identityId ?? null);
    if (!normalizedGroupId || !activeIdentity) {
      return { skipped: true, reason: 'missing_runtime_bootstrap_context' };
    }
    const runtimeBootstrapKey = this.#resolveRuntimeBootstrapKey({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
      conversationId: normalizedConversationId,
    });
    if (!runtimeBootstrapKey) {
      return { skipped: true, reason: 'missing_runtime_bootstrap_key' };
    }

    const inFlight = this.runtimeBootstrapTasks.get(runtimeBootstrapKey);
    if (inFlight) {
      const startedAt = Number(this.runtimeBootstrapTaskStartedAt.get(runtimeBootstrapKey) || 0);
      const ageMs = Number.isFinite(startedAt) && startedAt > 0 ? Date.now() - startedAt : null;
      const timedOut =
        Number.isFinite(ageMs) &&
        Number.isFinite(RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS) &&
        ageMs > RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS;
      if (!timedOut) {
        return inFlight;
      }
      this.runtimeBootstrapTasks.delete(runtimeBootstrapKey);
      this.runtimeBootstrapTaskStartedAt.delete(runtimeBootstrapKey);
    }

    const lastAttemptAt = Number(
      this.runtimeBootstrapCooldownByGroup.get(runtimeBootstrapKey) || 0,
    );
    if (
      Number.isFinite(lastAttemptAt) &&
      lastAttemptAt > 0 &&
      Date.now() - lastAttemptAt < RUNTIME_BOOTSTRAP_RETRY_COOLDOWN_MS
    ) {
      return { skipped: true, reason: 'runtime_bootstrap_cooldown' };
    }

    const task = (async () => {
      const timedOutMarker = { __runtimeBootstrapTimedOut: true };
      const runBootstrap = async () => {
        const liveRuntime = await this.#resolveLiveRuntimeGroupForEntry(entry, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
        }).catch(() => null);
        const matchedLiveRuntime = liveRuntime?.runtimeGroup || null;
        const shouldBootstrapByPersistedState = this.#shouldBootstrapRuntimeForCurrentIdentity(
          entry,
          {
            activeIdentityId: activeIdentity,
          },
        );
        if (!shouldBootstrapByPersistedState && matchedLiveRuntime) {
          return { skipped: true };
        }

        let runtimeBootstrapEntry = entry;
        let pendingWelcomeHint = this.#hasUnresolvedBootstrapWelcome(runtimeBootstrapEntry, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          activeIdentityId: activeIdentity,
        });
        const membershipState = this.#resolveEntryMembershipState(entry, {
          activeIdentityId: activeIdentity,
        });
        const snapshotEpoch = normalizeEpochOrNull(
          entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
        );
        const preferServerManagedRecoveryOnly =
          this.serverManagedWelcomes &&
          membershipState.currentIdentityMember &&
          !membershipState.pendingSetup &&
          !membershipState.welcomeRequired;
        if (
          !membershipState.currentIdentityMember ||
          membershipState.pendingSetup ||
          !(snapshotEpoch != null && snapshotEpoch > 0)
        ) {
          return { skipped: true };
        }

        await this.#markBootstrapConvergence(normalizedGroupId, {
          reason: `${source || 'unknown'}:${reason || 'runtime_bootstrap'}`,
        });

        if (!matchedLiveRuntime && normalizedGroupId) {
          await this.#updateGroupEntry(normalizedGroupId, (prev) => ({
            ...(prev || {}),
            runtimeLive: false,
            runtime_live: false,
            runtimeEpoch: null,
            runtime_epoch: null,
          })).catch(() => null);
        }

        const wantsRuntimeBootstrapKeyPackageRefresh =
          !pendingWelcomeHint &&
          Boolean(membershipState.pendingSetup || membershipState.currentUserMissing);
        const runtimeBootstrapPublishDisposition = wantsRuntimeBootstrapKeyPackageRefresh
          ? await this.#resolveBootstrapPublishDisposition({
              identityId: activeIdentity,
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupHandle,
              conversationId: normalizedConversationId,
              ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
            }).catch(() => null)
          : null;
        const shouldRefreshKeyPackage =
          wantsRuntimeBootstrapKeyPackageRefresh &&
          runtimeBootstrapPublishDisposition?.allowPublish !== false;

        if (shouldRefreshKeyPackage) {
          try {
            await this.publishKeyPackageAndSync(activeIdentity, {
              forcePublish: false,
              retryOnMissingClientId: true,
            });
          } catch (error) {
            console.warn('[MLS] Failed to refresh key package while bootstrapping runtime', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
              identityId: activeIdentity,
              source,
              reason,
              error,
            });
          }
        } else if (typeof console?.info === 'function') {
          console.info('[MLS] Skipping key package refresh during runtime bootstrap', {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            identityId: activeIdentity,
            pendingWelcomeHint,
            skipReason:
              runtimeBootstrapPublishDisposition?.reason ??
              (pendingWelcomeHint ? 'pending_welcome_hint' : 'publish_not_required'),
            targetRef: runtimeBootstrapPublishDisposition?.targetRef ?? null,
            pendingSetup: membershipState.pendingSetup,
            currentUserMissing: membershipState.currentUserMissing,
          });
        }

        if (normalizedConversationId) {
          try {
            const refreshedSnapshot = await getGroupSnapshot({
              conversationId: normalizedConversationId,
              force: true,
              clientId: activeIdentity,
              reissue: true,
            });
            if (refreshedSnapshot) {
              const ingested = await this.#ingestPendingWelcomesFromSnapshot(refreshedSnapshot, {
                conversationId: normalizedConversationId,
                identityId: activeIdentity,
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupHandle,
              });
              const joined = Array.isArray(ingested?.joined)
                ? (ingested.joined.find(
                    (item) =>
                      item?.groupId === normalizedGroupId ||
                      (normalizedGroupHandle && item?.groupHandle === normalizedGroupHandle),
                  ) ?? ingested.joined[0])
                : null;
              if (joined) {
                return { repaired: joined, attempted: true };
              }
            }
          } catch (error) {
            console.warn('[MLS] Failed to refetch snapshot while bootstrapping runtime', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
              identityId: activeIdentity,
              source,
              reason,
              error,
            });
          }
        }

        try {
          const groups = await this.#loadGroups();
          runtimeBootstrapEntry =
            groups[normalizedGroupId] ||
            Object.values(groups).find(
              (item) =>
                item?.groupId === normalizedGroupId || item?.groupHandle === normalizedGroupHandle,
            ) ||
            runtimeBootstrapEntry;
        } catch {}
        pendingWelcomeHint = this.#hasUnresolvedBootstrapWelcome(runtimeBootstrapEntry, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          activeIdentityId: activeIdentity,
        });

        // Always consume an already-issued Welcome before asking the server for a
        // replacement. This is the one pending-fetch owner for this attempt.
        {
          try {
            const welcomeSync = await this.syncWelcomes({
              conversationId: normalizedConversationId ?? undefined,
              groupId: normalizedGroupId,
              __pipelineBypass: true,
            });
            if (Array.isArray(welcomeSync?.joined) && welcomeSync.joined.length) {
              const matched =
                welcomeSync.joined.find((item) => {
                  if (!item) return false;
                  if (normalizedGroupId && item.groupId === normalizedGroupId) return true;
                  if (normalizedGroupHandle && item.groupHandle === normalizedGroupHandle) {
                    return true;
                  }
                  return false;
                }) ?? welcomeSync.joined[0];
              if (matched) {
                return { repaired: matched, attempted: true };
              }
            }
          } catch (error) {
            console.warn('[MLS] Failed welcome sync while bootstrapping runtime', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
              identityId: activeIdentity,
              source,
              reason,
              error,
            });
          }
        }

        if (membershipState.welcomeRequired) {
          return this.#convergeCurrentMemberAuthoritativeWelcome({
            entry: runtimeBootstrapEntry,
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            identityId: activeIdentity,
            source,
            reason: `${reason}_authoritative_welcome_runtime_followup`,
          });
        }

        let repaired = null;

        if (this.serverManagedWelcomes && !pendingWelcomeHint) {
          const recoveryWelcome = await this.#requestServerManagedRuntimeRecoveryWelcome({
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            identityId: activeIdentity,
            ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
            source,
            reason: `${reason}_server_reissue`,
            // This bootstrap task fetched and ingested the authoritative snapshot
            // immediately above. Reusing that result keeps one snapshot owner per
            // recovery attempt and prevents polling/reissue amplification.
            refreshSnapshot: false,
            syncAfterRequest: false,
          }).catch(() => null);

          if (this.#isGroupReady(recoveryWelcome?.repaired)) {
            return { repaired: recoveryWelcome.repaired };
          }
        }

        if (preferServerManagedRecoveryOnly) {
          if (pendingWelcomeHint) {
            const synced = await this.#syncRuntimeGroupState({
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupHandle,
              conversationId: normalizedConversationId,
              source,
              reason: `${reason}_authoritative_welcome_followup`,
              allowSyncWelcomes: false,
              allowJoinBundleRepair: false,
              allowRuntimeRepair: false,
            }).catch(() => null);

            return {
              repaired: synced || null,
              skipped: true,
              reason: synced
                ? 'awaiting_authoritative_welcome_bootstrap'
                : 'authoritative_welcome_pending',
            };
          }

          repaired = await this.#bootstrapRuntimeWithSelfWelcome({
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            identityId: activeIdentity,
            ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
            allowServerManaged: true,
            source,
            reason: `${reason}_server_managed_self_welcome_fallback`,
          }).catch(() => null);

          if (this.#isGroupReady(repaired)) {
            return { repaired };
          }

          const synced = await this.#syncRuntimeGroupState({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            conversationId: normalizedConversationId,
            source,
            reason: `${reason}_server_managed_followup`,
            allowSyncWelcomes: false,
            allowJoinBundleRepair: false,
            allowRuntimeRepair: false,
          }).catch(() => null);

          return {
            repaired: synced || repaired || null,
            skipped: true,
            reason: synced
              ? 'awaiting_server_managed_recovery'
              : 'server_managed_self_welcome_fallback_failed',
          };
        }

        repaired = await this.#bootstrapRuntimeWithSelfWelcome({
          conversationId: normalizedConversationId,
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          identityId: activeIdentity,
          ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
          allowServerManaged: true,
          source,
          reason: `${reason}_snapshot_self_welcome`,
        }).catch(() => null);

        if (this.#isGroupReady(repaired)) {
          return { repaired };
        }

        try {
          await this.syncWelcomes({
            conversationId: normalizedConversationId ?? undefined,
            groupId: normalizedGroupId,
            __pipelineBypass: true,
          });
        } catch (error) {
          console.warn('[MLS] Failed welcome sync while bootstrapping runtime', {
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            identityId: activeIdentity,
            source,
            reason,
            error,
          });
        }

        if (typeof this.forceJoinBundle === 'function') {
          try {
            repaired = await this.forceJoinBundle({
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupHandle,
              identityId: activeIdentity,
              ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
              preferReissueOnAlreadyMemberNoWelcome: true,
              __pipelineBypass: true,
            });
          } catch (error) {
            console.warn('[MLS] Failed force join while bootstrapping runtime', {
              conversationId: normalizedConversationId,
              groupId: normalizedGroupId,
              identityId: activeIdentity,
              source,
              reason,
              error,
            });
          }
        }

        if (this.#isGroupReady(repaired)) {
          return { repaired };
        }

        if (!this.serverManagedWelcomes) {
          repaired = await this.#bootstrapRuntimeWithSelfWelcome({
            conversationId: normalizedConversationId,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
            identityId: activeIdentity,
            ciphersuite: ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
            source,
            reason: `${reason}_self_welcome`,
          }).catch(() => repaired ?? null);

          if (this.#isGroupReady(repaired)) {
            return { repaired };
          }
        }

        const synced = await this.#syncRuntimeGroupState({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          conversationId: normalizedConversationId,
          source,
          reason,
          allowSyncWelcomes: false,
          allowJoinBundleRepair: !preferServerManagedRecoveryOnly,
        }).catch(() => null);

        return {
          repaired: synced || repaired || null,
        };
      };

      const result = await awaitWithTimeout(
        runBootstrap,
        RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS,
        timedOutMarker,
      );

      if (result !== timedOutMarker) {
        return {
          ...(result || {}),
          attempted: true,
        };
      }

      console.warn('[MLS] Runtime bootstrap task timed out', {
        conversationId: normalizedConversationId,
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        identityId: activeIdentity,
        source,
        reason,
        timeoutMs: RUNTIME_BOOTSTRAP_TASK_TIMEOUT_MS,
      });

      const latestInfo = await this.groupInfo({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
      }).catch(() => null);

      return {
        repaired: latestInfo || {
          ...(entry || {}),
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle,
          identityId: activeIdentity,
          ready: false,
          needsRecovery: Boolean(latestInfo?.needsRecovery ?? entry?.needsRecovery ?? false),
          recoveryReason:
            ensureString(
              latestInfo?.recoveryReason ??
                latestInfo?.reason ??
                entry?.recoveryReason ??
                entry?.reason ??
                null,
            ) ?? 'runtime_bootstrap_timeout',
        },
        skipped: true,
        reason: 'runtime_bootstrap_timeout',
      };
    })().finally(() => {
      if (this.runtimeBootstrapTasks.get(runtimeBootstrapKey) === task) {
        this.runtimeBootstrapTasks.delete(runtimeBootstrapKey);
      }
      this.runtimeBootstrapTaskStartedAt.delete(runtimeBootstrapKey);
      this.runtimeBootstrapCooldownByGroup.set(runtimeBootstrapKey, Date.now());
    });

    this.runtimeBootstrapTasks.set(runtimeBootstrapKey, task);
    this.runtimeBootstrapTaskStartedAt.set(runtimeBootstrapKey, Date.now());
    return task;
  }

  #canAttemptPersistedWelcomeRecovery(entry, { source = 'unknown' } = {}) {
    if (!this.#canAttemptWelcomeRehydrate(entry)) {
      return false;
    }

    const persistedWelcomeB64 = pickBase64String(
      entry?.recoveryWelcomeB64,
      entry?.recovery_welcome_b64,
      entry?.welcomeB64,
      entry?.welcome_b64,
      entry?.welcome,
      entry?.welcomeBuffer,
      entry?.welcome_buffer,
    );
    if (!persistedWelcomeB64) {
      return false;
    }

    const pendingWelcomeCount = getPendingWelcomesList(entry).length;
    if (pendingWelcomeCount > 0) {
      return true;
    }

    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
    });
    if (
      membershipState.currentIdentityMember &&
      !membershipState.pendingSetup &&
      !membershipState.welcomeRequired
    ) {
      if (typeof console?.info === 'function') {
        console.info(
          '[MLS] Skipping persisted welcome recovery because server no longer has a pending welcome for the current member',
          {
            source,
            groupId: ensureString(entry?.groupId ?? null),
            groupHandle: ensureString(entry?.groupHandle ?? null),
            epoch: normalizeEpochOrNull(entry?.epoch ?? null),
            joined: Boolean(entry?.joined),
            runtimeEpoch: normalizeEpochOrNull(
              entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
            ),
          },
        );
      }
      return false;
    }

    const runtimeState =
      ensureString(entry?.runtimeState ?? entry?.runtime_state ?? null) ??
      this.#classifyRuntimeState(entry);
    const runtimeEpoch = normalizeEpochOrNull(
      entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
    );
    const runtimeHandle = resolveRuntimeHandle(entry);
    const hasConcreteRuntimeOwnership = this.#hasConcreteRuntimeOwnership(entry);
    const runtimeMissing =
      runtimeState === 'runtime_missing' ||
      runtimeState === 'recovery_required' ||
      !hasConcreteRuntimeOwnership;

    if (runtimeMissing) {
      if (typeof console?.info === 'function') {
        console.info('[MLS] Allowing persisted welcome recovery for missing runtime', {
          source,
          groupId: ensureString(entry?.groupId ?? null),
          groupHandle: ensureString(entry?.groupHandle ?? null),
          epoch: normalizeEpochOrNull(entry?.epoch ?? null),
          runtimeEpoch,
          runtimeHandle: ensureString(runtimeHandle ?? null),
          concreteRuntimeOwnership: hasConcreteRuntimeOwnership,
          joined: Boolean(entry?.joined),
          runtimeState,
        });
      }
      return true;
    }

    if (!this.#hasJoinedGroupHistory(entry)) {
      return true;
    }

    if (typeof console?.info === 'function') {
      console.info(
        '[MLS] Skipping persisted welcome recovery for joined group without pending welcome',
        {
          source,
          groupId: ensureString(entry?.groupId ?? null),
          groupHandle: ensureString(entry?.groupHandle ?? null),
          epoch: normalizeEpochOrNull(entry?.epoch ?? null),
          runtimeEpoch: normalizeEpochOrNull(
            entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
          ),
          joined: Boolean(entry?.joined),
        },
      );
    }
    return false;
  }

  async #setRehydrateBlockedUntil(groupId, delayMs, reason = null) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) return null;
    const waitMs = Number.isFinite(Number(delayMs)) ? Math.max(0, Number(delayMs)) : 0;
    const blockedUntil = Date.now() + waitMs;
    return this.#updateGroupEntry(normalizedGroupId, (prev) => ({
      ...prev,
      rehydrateBlockedUntil: blockedUntil,
      rehydrateBackoffReason: reason || null,
    })).catch(() => null);
  }

  async #dropStaleRuntimeGroupsForGroupId(
    groupId,
    { keepHandle = null, source = null, reason = null, traceId = null } = {},
  ) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) {
      return null;
    }
    try {
      return await workerDropRuntimeGroupsByGroupId({
        groupId: normalizedGroupId,
        keepHandle: ensureString(keepHandle),
      });
    } catch (error) {
      if (typeof console?.warn === 'function') {
        console.warn('[MLS] Failed to drop stale runtime groups', {
          groupId: normalizedGroupId,
          keepHandle: keepHandle ?? null,
          source: source || null,
          reason: reason || null,
          traceId: traceId || null,
          error,
        });
      }
      return null;
    }
  }

  async #removeRuntimeGroupHandle(
    groupHandle,
    { source = null, reason = null, traceId = null } = {},
  ) {
    const normalizedGroupHandle = ensureString(groupHandle);
    if (!normalizedGroupHandle) {
      return null;
    }
    try {
      return await workerRemoveRuntimeGroup({ groupHandle: normalizedGroupHandle });
    } catch (error) {
      if (typeof console?.warn === 'function') {
        console.warn('[MLS] Failed to remove runtime group handle', {
          groupHandle: normalizedGroupHandle,
          source: source || null,
          reason: reason || null,
          traceId: traceId || null,
          error,
        });
      }
      return null;
    }
  }

  async #clearRuntimeHandleState(
    groupId,
    { groupHandle = null, runtimeHandle = null, source = null, reason = null, traceId = null } = {},
  ) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);
    const normalizedRuntimeHandle = ensureString(runtimeHandle);

    if (normalizedGroupId) {
      await this.#dropStaleRuntimeGroupsForGroupId(normalizedGroupId, {
        keepHandle: null,
        source,
        reason,
        traceId,
      }).catch(() => null);
    }

    if (normalizedRuntimeHandle) {
      await this.#removeRuntimeGroupHandle(normalizedRuntimeHandle, {
        source,
        reason,
        traceId,
      }).catch(() => null);
    }

    if (!normalizedGroupId) {
      return null;
    }

    return this.#updateGroupEntry(normalizedGroupId, (prev) => ({
      ...prev,
      groupId: normalizedGroupId ?? prev?.groupId ?? null,
      groupHandle: normalizedGroupHandle ?? prev?.groupHandle ?? null,
      runtimeHandle: null,
      runtimeEpoch: null,
      runtimeLive: false,
      runtime_live: false,
    })).catch(() => null);
  }

  async #repairRuntimeAfterStaleHandle({
    groupInfo = null,
    groupId = null,
    groupHandle = null,
    runtimeHandle = null,
    conversationId = null,
    source = 'unknown',
    reason = 'stale_runtime_handle',
    traceId = null,
    allowSyncWelcomes = true,
    allowJoinBundleRepair = true,
  } = {}) {
    const normalizedGroupId = ensureString(groupInfo?.groupId ?? groupId ?? null);
    const normalizedGroupHandle = ensureString(groupInfo?.groupHandle ?? groupHandle ?? null);
    const normalizedRuntimeHandle = ensureString(
      runtimeHandle ?? groupInfo?.runtimeHandle ?? groupInfo?.groupHandle ?? null,
    );
    const normalizedConversationId = ensureString(
      groupInfo?.conversationId ?? conversationId ?? null,
    );

    if (!normalizedGroupId && !normalizedGroupHandle) {
      return null;
    }

    await this.#clearRuntimeHandleState(normalizedGroupId, {
      groupHandle: normalizedGroupHandle,
      runtimeHandle: normalizedRuntimeHandle,
      source,
      reason,
      traceId,
    }).catch(() => null);

    let repaired = await this.#syncRuntimeGroupState({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
      conversationId: normalizedConversationId,
      source,
      reason,
      traceId,
      allowSyncWelcomes,
      allowJoinBundleRepair,
    }).catch(() => null);

    if (!this.#isGroupReady(repaired) && normalizedConversationId && normalizedGroupId) {
      try {
        await this.#recoverWorkerCapabilities({
          conversationId: normalizedConversationId,
          ciphersuite: groupInfo?.ciphersuite ?? repaired?.ciphersuite ?? null,
        });
      } catch {}
      repaired = await this.#syncRuntimeGroupState({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        conversationId: normalizedConversationId,
        source,
        reason: `${reason}_after_worker_recovery`,
        traceId,
        allowSyncWelcomes,
        allowJoinBundleRepair,
      }).catch(() => repaired);
    }

    if (repaired?.groupId || repaired?.groupHandle) {
      return repaired;
    }

    return this.groupInfo({
      groupId: normalizedGroupId,
      groupHandle: normalizedGroupHandle,
    }).catch(() => null);
  }

  #resolveRuntimeGroupForEntry(entry, runtimeGroups = []) {
    if (!entry || !Array.isArray(runtimeGroups) || !runtimeGroups.length) {
      return null;
    }

    const entryGroupId = ensureString(entry.groupId ?? null);
    const entryGroupHandle = ensureString(entry.groupHandle ?? null);
    const entryRuntimeHandle = ensureString(resolveRuntimeHandle(entry));
    const entryEpoch = normalizeEpochOrNull(entry?.epoch ?? null);

    const candidates = runtimeGroups
      .filter((runtimeEntry) => this.#runtimeGroupMatchesEntry(entry, runtimeEntry))
      .map((runtimeEntry) => {
        const runtimeHandle = ensureString(runtimeEntry?.groupHandle ?? null);
        const runtimeGroupId = ensureString(runtimeEntry?.groupId ?? null);
        const runtimeEpoch = normalizeEpochOrNull(runtimeEntry?.epoch ?? null);
        const exactRuntimeHandle = Boolean(
          entryRuntimeHandle && runtimeHandle && runtimeHandle === entryRuntimeHandle,
        );
        const exactGroupHandle = Boolean(
          entryGroupHandle && runtimeHandle && runtimeHandle === entryGroupHandle,
        );
        const exactGroupId = Boolean(
          entryGroupId && runtimeGroupId && runtimeGroupId === entryGroupId,
        );

        let score = 0;
        // Persisted runtimeHandle can become stale after join/rehydrate. Prefer the
        // canonical snapshot/server group handle over an older runtime-only handle.
        if (exactGroupHandle) score += 1200;
        if (exactRuntimeHandle) score += 400;
        if (exactGroupId) score += 100;

        return {
          runtimeEntry,
          score,
          runtimeEpoch: runtimeEpoch != null ? runtimeEpoch : -1,
          runtimeHandle: runtimeHandle || '',
          exactRuntimeHandle,
          exactGroupHandle,
          exactGroupId,
        };
      });

    if (!candidates.length) {
      return null;
    }

    const hasExactHandleMatch = candidates.some(
      (item) => item.exactRuntimeHandle || item.exactGroupHandle,
    );
    if (!hasExactHandleMatch) {
      const strictGroupIdMatches = candidates.filter((item) => item.exactGroupId);
      if (strictGroupIdMatches.length === 1) {
        return strictGroupIdMatches[0].runtimeEntry || null;
      }
      if (!strictGroupIdMatches.length) {
        return null;
      }

      // Ambiguous runtime groups can exist locally for the same groupId.
      // In that case prefer the runtime whose epoch is closest to stored epoch,
      // biasing towards not jumping ahead to a future runtime state.
      if (entryEpoch != null) {
        const sameEpoch = strictGroupIdMatches.filter((item) => item.runtimeEpoch === entryEpoch);
        if (sameEpoch.length === 1) {
          return sameEpoch[0].runtimeEntry || null;
        }
        if (sameEpoch.length > 1) {
          sameEpoch.sort((a, b) => String(b.runtimeHandle).localeCompare(String(a.runtimeHandle)));
          return sameEpoch[0].runtimeEntry || null;
        }

        const notAhead = strictGroupIdMatches
          .filter((item) => item.runtimeEpoch >= 0 && item.runtimeEpoch <= entryEpoch)
          .sort((a, b) => b.runtimeEpoch - a.runtimeEpoch);
        if (notAhead.length) {
          return notAhead[0].runtimeEntry || null;
        }

        const ahead = strictGroupIdMatches
          .filter((item) => item.runtimeEpoch >= 0 && item.runtimeEpoch > entryEpoch)
          .sort((a, b) => a.runtimeEpoch - b.runtimeEpoch);
        if (ahead.length) {
          return ahead[0].runtimeEntry || null;
        }
      }

      // Last resort deterministic pick to avoid "missing runtime" recovery churn loops.
      strictGroupIdMatches.sort((a, b) => {
        if (b.runtimeEpoch !== a.runtimeEpoch) return b.runtimeEpoch - a.runtimeEpoch;
        return String(b.runtimeHandle).localeCompare(String(a.runtimeHandle));
      });
      return strictGroupIdMatches[0].runtimeEntry || null;
    }

    candidates.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.runtimeEpoch !== a.runtimeEpoch) return b.runtimeEpoch - a.runtimeEpoch;
      return String(b.runtimeHandle).localeCompare(String(a.runtimeHandle));
    });

    return candidates[0].runtimeEntry || null;
  }

  async #syncRuntimeGroupState({
    groupId,
    groupHandle,
    conversationId = null,
    source = null,
    reason = null,
    traceId = null,
    allowSyncWelcomes = true,
    allowJoinBundleRepair = false,
    allowRuntimeRepair = true,
  } = {}) {
    let normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);
    if (!normalizedGroupId && normalizedGroupHandle) {
      try {
        const groups = (await this.#loadGroups()) || {};
        const matched = Object.values(groups).find((entry) => {
          if (!entry || typeof entry !== 'object') return false;
          return ensureString(entry.groupHandle) === normalizedGroupHandle;
        });
        const matchedGroupId = ensureString(matched?.groupId ?? null);
        if (matchedGroupId) {
          normalizedGroupId = matchedGroupId;
        }
      } catch {}
    }
    const syncKey = normalizedGroupId ?? normalizedGroupHandle ?? null;
    if (!syncKey) return null;

    const inFlight = this.runtimeSyncTasks.get(syncKey);
    if (inFlight) {
      const startedAt = Number(this.runtimeSyncTaskStartedAt.get(syncKey) || 0);
      const inFlightAgeMs =
        Number.isFinite(startedAt) && startedAt > 0 ? Date.now() - startedAt : null;
      const inFlightTimedOut =
        Number.isFinite(inFlightAgeMs) &&
        Number.isFinite(RUNTIME_SYNC_TASK_TIMEOUT_MS) &&
        RUNTIME_SYNC_TASK_TIMEOUT_MS > 0 &&
        inFlightAgeMs > RUNTIME_SYNC_TASK_TIMEOUT_MS;
      if (!inFlightTimedOut) {
        return inFlight;
      }
      if (typeof console?.warn === 'function') {
        console.warn('[MLS] runtime sync task stale; replacing in-flight task', {
          groupId: syncKey,
          startedAt,
          ageMs: inFlightAgeMs,
          timeoutMs: RUNTIME_SYNC_TASK_TIMEOUT_MS,
          source: source || null,
          reason: reason || null,
          traceId: traceId || null,
        });
      }
      if (this.runtimeSyncTasks.get(syncKey) === inFlight) {
        this.runtimeSyncTasks.delete(syncKey);
      }
      if (this.runtimeSyncTaskStartedAt.get(syncKey) != null) {
        this.runtimeSyncTaskStartedAt.delete(syncKey);
      }
    }

    const task = (async () => {
      return await (async () => {
        const withLocalTimeout = async (promiseFactory, timeoutMs, timeoutValue = null) => {
          const ms = Number(timeoutMs);
          if (!Number.isFinite(ms) || ms <= 0) {
            try {
              return await promiseFactory();
            } catch {
              return timeoutValue;
            }
          }
          let timerId = null;
          const timeoutToken = { __mlsTimeout: true };
          try {
            const result = await Promise.race([
              Promise.resolve().then(() => promiseFactory()),
              new Promise((resolve) => {
                timerId = setTimeout(() => resolve(timeoutToken), ms);
              }),
            ]);
            if (result === timeoutToken) return timeoutValue;
            return result;
          } catch {
            return timeoutValue;
          } finally {
            if (timerId) clearTimeout(timerId);
          }
        };
        await withLocalTimeout(() => this.ensureInitialized(), 5000, null);
        const identity = await withLocalTimeout(() => this.ensureIdentity({}), 2500, null);
        const activeIdentityId = ensureString(
          this.identityClientId ?? identity?.identityId ?? this.identityId ?? null,
        );

        const loadEntry = async () => {
          const groups = await this.#loadGroups();
          const stored = this.#resolveStoredGroup(groups, {
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle,
          });
          return { groups, storageKey: stored.key, entry: stored.entry || null };
        };

        const listRuntimeGroups = async () => {
          try {
            const listed = await withLocalTimeout(() => workerListGroups(), 2000, []);
            return normalizeRuntimeGroups(Array.isArray(listed) ? listed : []);
          } catch (error) {
            console.warn('[MLS] Failed to list runtime groups while syncing runtime state', {
              groupId: syncKey,
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              error,
            });
            return [];
          }
        };
        const probeRuntimeHandle = async (
          runtimeEntry,
          { fallbackGroupId = null, fallbackGroupHandle = null, fallbackEpoch = null } = {},
        ) =>
          this.#probeRuntimeGroupByKnownHandle({
            entry: runtimeEntry,
            groupId: fallbackGroupId,
            groupHandle: fallbackGroupHandle,
            conversationId: resolvedConversationId,
            fallbackEpoch,
          });
        let { groups, storageKey, entry } = await loadEntry();
        if (!entry) {
          return null;
        }

        let resolvedGroupId = ensureString(
          entry.groupId ?? storageKey ?? normalizedGroupId ?? null,
        );
        let resolvedGroupHandle = ensureString(entry.groupHandle ?? normalizedGroupHandle ?? null);
        const knownConversation = findKnownConversationByGroupId(
          resolvedGroupId ?? normalizedGroupId ?? syncKey,
        );
        const resolvedConversationId =
          ensureString(conversationId) ??
          ensureString(entry.conversationId ?? null) ??
          (knownConversation ? normalizeConversationId(knownConversation) : null);
        const snapshotRatchetTreeB64 = pickBase64String(entry?.ratchetTreeB64, entry?.ratchetTree);
        const snapshotRatchetTreeSha = await computeSha256HexFromBase64(snapshotRatchetTreeB64);
        let runtimeGroups = await listRuntimeGroups();
        let matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
          { ...entry, groupId: resolvedGroupId, groupHandle: resolvedGroupHandle },
          runtimeGroups,
        );
        let runtimeHasGroup = Boolean(matchedRuntimeGroup);
        if (!runtimeHasGroup) {
          const probedRuntimeGroup = await probeRuntimeHandle(entry, {
            fallbackGroupId: resolvedGroupId,
            fallbackGroupHandle: resolvedGroupHandle,
            fallbackEpoch: entry?.epoch ?? null,
          });
          if (probedRuntimeGroup?.groupHandle) {
            matchedRuntimeGroup = probedRuntimeGroup;
            runtimeHasGroup = true;
          }
        }

        // If multiple runtime handles exist for the same groupId, keep only one.
        // Otherwise stale runtime handles can keep old sender-generation chains alive.
        if (resolvedGroupId) {
          const sameGroupRuntimeGroups = runtimeGroups.filter((item) => {
            const runtimeGroupId = ensureString(item?.groupId ?? null);
            const runtimeHandle = ensureString(item?.groupHandle ?? null);
            return Boolean(runtimeGroupId && runtimeHandle && runtimeGroupId === resolvedGroupId);
          });

          if (sameGroupRuntimeGroups.length > 1) {
            let keepHandle = ensureString(matchedRuntimeGroup?.groupHandle ?? null);

            if (snapshotRatchetTreeSha) {
              for (const candidate of sameGroupRuntimeGroups) {
                const candidateHandle = ensureString(candidate?.groupHandle ?? null);
                if (!candidateHandle) continue;
                try {
                  const candidateDebug = await withLocalTimeout(
                    () =>
                      workerDebugGroupRuntimeState({
                        groupHandle: candidateHandle,
                        includeExportKey: false,
                        label: HISTORY_DERIVED_KEY_LABEL,
                        context: resolvedConversationId ?? resolvedGroupId ?? syncKey ?? '',
                        length: 1,
                      }),
                    1200,
                    null,
                  );
                  const candidateTreeSha = ensureString(candidateDebug?.ratchetTreeSha256 ?? null);
                  if (candidateTreeSha && candidateTreeSha === snapshotRatchetTreeSha) {
                    keepHandle = candidateHandle;
                    break;
                  }
                } catch {}
              }
            }

            await this.#dropStaleRuntimeGroupsForGroupId(resolvedGroupId, {
              keepHandle,
              source: source || 'syncRuntimeGroupState',
              reason: 'duplicate_runtime_handles',
              traceId,
            });
            runtimeGroups = await listRuntimeGroups();
            matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
              { ...entry, groupId: resolvedGroupId, groupHandle: resolvedGroupHandle },
              runtimeGroups,
            );
            runtimeHasGroup = Boolean(matchedRuntimeGroup);
          }
        }
        if (matchedRuntimeGroup?.groupHandle) {
          const normalizedMatchedHandle = ensureString(matchedRuntimeGroup.groupHandle);
          const normalizedMatchedRuntimeEpoch = normalizeEpochOrNull(
            matchedRuntimeGroup?.epoch ?? null,
          );
          const normalizedStoredRuntimeEpoch = normalizeEpochOrNull(
            entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
          );
          // Some worker list snapshots transiently report epoch=0 right after join.
          // Do not infer runtime epoch from snapshot epoch. Keep only live runtime
          // epoch, or a previously persisted runtime epoch when the handle is unchanged.
          const runtimeEpochForStore =
            normalizedMatchedRuntimeEpoch != null && normalizedMatchedRuntimeEpoch > 0
              ? normalizedMatchedRuntimeEpoch
              : entry?.runtimeHandle === normalizedMatchedHandle &&
                  normalizedStoredRuntimeEpoch != null &&
                  normalizedStoredRuntimeEpoch > 0
                ? normalizedStoredRuntimeEpoch
                : null;
          if (normalizedMatchedHandle && entry?.runtimeHandle !== normalizedMatchedHandle) {
            const targetId = resolvedGroupId ?? syncKey;
            groups[targetId] = {
              ...(groups[targetId] || {}),
              ...entry,
              groupId: targetId,
              runtimeHandle: normalizedMatchedHandle,
              runtimeEpoch: runtimeEpochForStore ?? null,
              groupHandle: ensureString(entry?.groupHandle ?? null) ?? normalizedMatchedHandle,
            };
            await this.#saveGroups(groups);
            entry = groups[targetId];
          }
        }

        if (runtimeHasGroup && matchedRuntimeGroup?.groupHandle) {
          const storedEpoch = normalizeEpochOrNull(entry?.epoch ?? null);
          const runtimeEpochFromWorkerList = normalizeEpochOrNull(
            matchedRuntimeGroup?.epoch ?? null,
          );
          const runtimeHandleMatchesPersisted = Boolean(
            ensureString(entry?.runtimeHandle ?? null) &&
              ensureString(matchedRuntimeGroup?.groupHandle ?? null) &&
              ensureString(entry?.runtimeHandle ?? null) ===
                ensureString(matchedRuntimeGroup?.groupHandle ?? null),
          );
          const runtimeEpochFromEntry = runtimeHandleMatchesPersisted
            ? normalizeEpochOrNull(
                entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
              )
            : null;
          // Epoch=0 from worker list can be transient immediately after join/apply.
          // Prefer known-good persisted/runtime epoch for drift checks.
          const runtimeEpochCandidates = [runtimeEpochFromWorkerList, runtimeEpochFromEntry].filter(
            (value) => value != null && value > 0,
          );
          const runtimeEpoch = runtimeEpochCandidates.length
            ? Math.max(...runtimeEpochCandidates)
            : (runtimeEpochFromWorkerList ?? runtimeEpochFromEntry);
          const runtimeEpochUnknown = runtimeEpoch == null || runtimeEpoch <= 0;
          const hasEpochDrift =
            !runtimeEpochUnknown &&
            storedEpoch != null &&
            runtimeEpoch != null &&
            runtimeEpoch !== storedEpoch;
          const runtimeAhead = hasEpochDrift && runtimeEpoch > storedEpoch;
          const runtimeBehind = hasEpochDrift && runtimeEpoch < storedEpoch;
          const epochGap = runtimeBehind
            ? Math.max(0, Number(storedEpoch) - Number(runtimeEpoch))
            : runtimeAhead
              ? Math.max(0, Number(runtimeEpoch) - Number(storedEpoch))
              : 0;
          // A newer runtime epoch usually means local snapshot lagged behind.
          const hasEpochMismatch = runtimeBehind;
          // Runtime epoch=0/unknown can leave us stuck with a handle that cannot decrypt.
          // Treat this as mismatch when persisted snapshot epoch has already advanced.
          const unknownRuntimeWithAdvancedSnapshot =
            runtimeEpochUnknown && storedEpoch != null && storedEpoch > 0;

          // Tree hash mismatch is non-authoritative for recovery decisions here.
          // Avoid blocking sync pipeline on runtime debug probes.
          let hasRatchetTreeMismatch = false;
          let runtimeDebugEpoch = null;
          if (snapshotRatchetTreeSha && !runtimeAhead) {
            const runtimeDebug = await withLocalTimeout(
              () =>
                workerDebugGroupRuntimeState({
                  groupHandle: matchedRuntimeGroup.groupHandle,
                  includeExportKey: false,
                  label: HISTORY_DERIVED_KEY_LABEL,
                  context: resolvedConversationId ?? resolvedGroupId ?? syncKey ?? '',
                  length: 1,
                }),
              1200,
              null,
            );
            const runtimeTreeSha = ensureString(runtimeDebug?.ratchetTreeSha256 ?? null);
            runtimeDebugEpoch = normalizeEpochOrNull(runtimeDebug?.epoch ?? null);
            hasRatchetTreeMismatch =
              Boolean(runtimeTreeSha) && runtimeTreeSha !== snapshotRatchetTreeSha;
          }
          const authoritativeRuntimeEpochCandidates = [
            runtimeDebugEpoch,
            runtimeEpochFromWorkerList,
            runtimeEpochFromEntry,
          ].filter((value) => value != null && value > 0);
          const authoritativeRuntimeEpoch = authoritativeRuntimeEpochCandidates.length
            ? Math.max(...authoritativeRuntimeEpochCandidates)
            : (runtimeDebugEpoch ?? runtimeEpochFromWorkerList ?? runtimeEpochFromEntry);
          const sameEpochTreeMismatch =
            hasRatchetTreeMismatch &&
            authoritativeRuntimeEpoch != null &&
            storedEpoch != null &&
            authoritativeRuntimeEpoch === storedEpoch;

          if (runtimeAhead && typeof console?.info === 'function') {
            console.info('[MLS] Runtime ahead of persisted snapshot; skipping forced recovery', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              storedEpoch: storedEpoch ?? null,
              runtimeEpoch: runtimeEpoch ?? null,
            });
          }

          const pendingWelcomeCount = getPendingWelcomesList(entry).length;
          const isPendingApplySyncPath =
            source === 'pullAndApplyPendingWelcomeOrCommit' &&
            (reason === 'pending_message_apply' || reason === 'pending_handshake_apply');
          const suppressRecoveryForStablePendingSync =
            !allowJoinBundleRepair && isPendingApplySyncPath;
          const isSnapshotAdvanceSyncPath =
            reason === 'apply_server_snapshot' || reason === 'apply_server_snapshot_advanced';
          const suppressRecoveryForSnapshotAdvance =
            isSnapshotAdvanceSyncPath && runtimeHasGroup && !unknownRuntimeWithAdvancedSnapshot;
          const suppressRecoveryForBootstrapConvergence = this.#shouldDeferRecoveryDuringBootstrap(
            entry,
            {
              source,
              reason,
              pendingWelcomeCount,
              runtimeHasGroup,
            },
          );

          // Ratchet-tree bytes can legitimately differ in representation while epoch/state is valid.
          // Do not force recovery on tree mismatch alone; only recover when runtime is behind epoch.
          // Also avoid forcing recovery while processing pending sync with no pending welcomes.
          const shouldForceRecovery =
            allowRuntimeRepair &&
            (hasEpochMismatch || unknownRuntimeWithAdvancedSnapshot) &&
            !suppressRecoveryForStablePendingSync &&
            !suppressRecoveryForSnapshotAdvance &&
            !suppressRecoveryForBootstrapConvergence;
          if (
            hasRatchetTreeMismatch &&
            !shouldForceRecovery &&
            typeof console?.info === 'function'
          ) {
            console.info(
              '[MLS] Runtime tree hash differs from snapshot, but epoch is healthy; skipping recovery',
              {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: resolvedGroupId ?? syncKey,
                storedEpoch: storedEpoch ?? null,
                runtimeEpoch: runtimeEpoch ?? null,
              },
            );
          }
          if (
            suppressRecoveryForStablePendingSync &&
            hasEpochMismatch &&
            typeof console?.info === 'function'
          ) {
            console.info('[MLS] Suppressing runtime recovery during in-flight pending apply', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              storedEpoch: storedEpoch ?? null,
              runtimeEpoch: runtimeEpoch ?? null,
              epochGap,
              pendingWelcomeCount,
              allowJoinBundleRepair,
            });
          }
          if (
            suppressRecoveryForSnapshotAdvance &&
            (hasEpochMismatch || unknownRuntimeWithAdvancedSnapshot) &&
            typeof console?.info === 'function'
          ) {
            console.info(
              '[MLS] Suppressing runtime recovery during snapshot advance; pending apply will converge',
              {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: resolvedGroupId ?? syncKey,
                storedEpoch: storedEpoch ?? null,
                runtimeEpoch: runtimeEpoch ?? null,
                epochGap,
                unknownRuntimeWithAdvancedSnapshot,
              },
            );
          }
          if (
            suppressRecoveryForBootstrapConvergence &&
            (hasEpochMismatch || unknownRuntimeWithAdvancedSnapshot) &&
            typeof console?.info === 'function'
          ) {
            console.info(
              '[MLS] Deferring runtime recovery during active bootstrap convergence window',
              {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: resolvedGroupId ?? syncKey,
                storedEpoch: storedEpoch ?? null,
                runtimeEpoch: runtimeEpoch ?? null,
                epochGap,
                unknownRuntimeWithAdvancedSnapshot,
                pendingWelcomeCount,
                bootstrapWindowUntil: this.#getBootstrapWindowUntil(entry),
              },
            );
            await this.#markBootstrapConvergence(resolvedGroupId ?? syncKey, {
              reason: `${source || 'unknown'}:${reason || 'runtime_sync'}`,
            });
          }
          if (shouldForceRecovery) {
            const targetId = resolvedGroupId ?? syncKey;
            if (typeof console?.warn === 'function') {
              console.warn('[MLS] Runtime snapshot mismatch detected; forcing recovery', {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: targetId,
                storedEpoch: storedEpoch ?? null,
                runtimeEpoch: runtimeEpoch ?? null,
                runtimeAhead,
                runtimeBehind,
                epochGap,
                hasEpochMismatch,
                unknownRuntimeWithAdvancedSnapshot,
                hasRatchetTreeMismatch,
                sameEpochTreeMismatch,
              });
            }
            await this.#dropStaleRuntimeGroupsForGroupId(targetId, {
              keepHandle: null,
              source: source || 'syncRuntimeGroupState',
              reason: 'runtime_snapshot_mismatch',
              traceId,
            });
            if (matchedRuntimeGroup?.groupHandle) {
              await this.#removeRuntimeGroupHandle(matchedRuntimeGroup.groupHandle, {
                source: source || 'syncRuntimeGroupState',
                reason: 'runtime_snapshot_mismatch',
                traceId,
              });
            }
            try {
              await this.#updateGroupEntry(targetId, (prev) => ({
                ...prev,
                runtimeHandle: null,
                runtimeEpoch: null,
                lastServerSeq: null,
                needsRecovery: true,
                recoveryReason: 'runtime_snapshot_mismatch',
                cryptoMismatch: true,
                crypto_mismatch: true,
                ratchetTreeMismatch: hasRatchetTreeMismatch || sameEpochTreeMismatch,
              }));
            } catch {}
            try {
              await this.markGroupForRecovery(targetId, 'runtime_snapshot_mismatch');
            } catch {}
            groups = await this.#loadGroups().catch(() => groups);
            entry = (groups && groups[targetId]) || {
              ...entry,
              runtimeHandle: null,
              runtimeEpoch: null,
              lastServerSeq: null,
              needsRecovery: true,
              recoveryReason: 'runtime_snapshot_mismatch',
              cryptoMismatch: true,
              crypto_mismatch: true,
              ratchetTreeMismatch: hasRatchetTreeMismatch || sameEpochTreeMismatch,
            };
            runtimeHasGroup = false;
            matchedRuntimeGroup = null;
          }
        }

        if (
          !runtimeHasGroup &&
          this.#canAttemptPersistedWelcomeRecovery(entry, {
            source: source || 'sync_runtime_group_state',
          })
        ) {
          const restored = await this.#rehydrateGroupFromWelcome(entry, {
            allowForceRejoinRecovery: false,
            source: 'sync_runtime_group_state',
          }).catch(() => null);
          if (restored?.groupHandle) {
            resolvedGroupId = ensureString(restored.groupId ?? resolvedGroupId ?? null);
            resolvedGroupHandle = ensureString(restored.groupHandle ?? resolvedGroupHandle ?? null);
            const targetId = resolvedGroupId ?? syncKey;
            groups[targetId] = {
              ...(groups[targetId] || {}),
              ...restored,
              groupId: targetId,
              groupHandle: resolvedGroupHandle ?? restored.groupHandle ?? null,
            };
            await this.#saveGroups(groups);
            entry = groups[targetId];
            runtimeGroups = await listRuntimeGroups();
            runtimeHasGroup = this.#runtimeHasGroupForEntry(entry, runtimeGroups);
            if (runtimeHasGroup && typeof console?.info === 'function') {
              console.info('MDBG runtime group rehydrated from persisted welcome', {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: targetId,
                conversationId: resolvedConversationId,
              });
            }
          } else if (entry?.groupId) {
            await this.#setRehydrateBlockedUntil(
              entry.groupId,
              REHYDRATE_RETRY_BACKOFF_MS,
              'sync_runtime_group_state_failed',
            );
          }
        }

        if (!runtimeHasGroup && !allowRuntimeRepair) {
          return (
            (await this.groupInfo({
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle ?? null,
            }).catch(() => null)) || {
              ...(entry || {}),
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle ?? entry?.groupHandle ?? null,
              runtimeHandle: ensureString(resolveRuntimeHandle(entry)) ?? null,
              runtimeEpoch:
                normalizeEpochOrNull(
                  entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
                ) ?? null,
              runtimeLive: false,
              runtime_live: false,
            }
          );
        }

        // Prefer pending/welcome pipeline before join_bundle recovery. If backend has
        // a pending welcome for this client, syncWelcomes will consume+ack it.
        if (!runtimeHasGroup && allowSyncWelcomes) {
          try {
            const syncResult = await this.syncWelcomes({
              conversationId: resolvedConversationId ?? undefined,
              groupId: resolvedGroupId ?? syncKey,
              __pipelineBypass: true,
            });
            if (Array.isArray(syncResult?.joined) && syncResult.joined.length) {
              const matched =
                syncResult.joined.find((item) => {
                  if (!item) return false;
                  if (resolvedGroupId && item.groupId === resolvedGroupId) return true;
                  if (resolvedGroupHandle && item.groupHandle === resolvedGroupHandle) return true;
                  return false;
                }) ?? syncResult.joined[0];
              if (matched?.groupId) {
                resolvedGroupId = ensureString(matched.groupId ?? resolvedGroupId ?? null);
                resolvedGroupHandle = ensureString(
                  matched.groupHandle ?? resolvedGroupHandle ?? null,
                );
                groups = await this.#loadGroups();
                const targetId = resolvedGroupId ?? syncKey;
                groups[targetId] = {
                  ...(groups[targetId] || {}),
                  ...matched,
                  groupId: targetId,
                  groupHandle: resolvedGroupHandle ?? matched.groupHandle ?? null,
                };
                await this.#saveGroups(groups);
                entry = groups[targetId];
                runtimeGroups = await listRuntimeGroups();
                matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(entry, runtimeGroups);
                runtimeHasGroup = Boolean(matchedRuntimeGroup);
              }
            }
          } catch (error) {
            console.warn('[MLS] Failed to sync welcomes while repairing runtime state', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              conversationId: resolvedConversationId,
              error,
            });
          }
        }

        const pendingWelcomeHintForJoinRepair = this.#hasUnresolvedBootstrapWelcome(entry, {
          groupId: ensureString(resolvedGroupId ?? syncKey ?? null),
          groupHandle: ensureString(resolvedGroupHandle ?? entry?.groupHandle ?? null),
          activeIdentityId,
        });

        const effectiveRecoveryReason = (() => {
          const reasonCode = ensureString(entry?.recoveryReason ?? null);
          if (reasonCode) return reasonCode;
          return !runtimeHasGroup ? 'missing_runtime_group' : null;
        })();
        const membershipStateForRuntimeRepair = this.#resolveEntryMembershipState(entry, {
          activeIdentityId,
        });
        const suppressServerManagedMemberJoinBundle =
          this.serverManagedWelcomes &&
          membershipStateForRuntimeRepair.currentIdentityMember &&
          !membershipStateForRuntimeRepair.pendingSetup &&
          !membershipStateForRuntimeRepair.welcomeRequired;

        if (
          !runtimeHasGroup &&
          allowSyncWelcomes &&
          this.serverManagedWelcomes &&
          membershipStateForRuntimeRepair.currentIdentityMember &&
          !membershipStateForRuntimeRepair.pendingSetup &&
          !membershipStateForRuntimeRepair.welcomeRequired &&
          !pendingWelcomeHintForJoinRepair
        ) {
          try {
            const recoveryWelcome = await this.#requestServerManagedRuntimeRecoveryWelcome({
              conversationId: resolvedConversationId,
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle,
              identityId: activeIdentityId,
              ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
              source: source || 'syncRuntimeGroupState',
              reason: reason || 'runtime_missing_server_reissue',
            });
            if (recoveryWelcome?.repaired?.groupId) {
              resolvedGroupId = ensureString(
                recoveryWelcome.repaired.groupId ?? resolvedGroupId ?? null,
              );
              resolvedGroupHandle = ensureString(
                recoveryWelcome.repaired.groupHandle ?? resolvedGroupHandle ?? null,
              );
              groups = await this.#loadGroups();
              const targetId = resolvedGroupId ?? syncKey;
              groups[targetId] = {
                ...(groups[targetId] || {}),
                ...recoveryWelcome.repaired,
                groupId: targetId,
                groupHandle: resolvedGroupHandle ?? recoveryWelcome.repaired.groupHandle ?? null,
              };
              await this.#saveGroups(groups);
              entry = groups[targetId];
              runtimeGroups = await listRuntimeGroups();
              matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(entry, runtimeGroups);
              runtimeHasGroup = Boolean(matchedRuntimeGroup);
              if (runtimeHasGroup && typeof console?.info === 'function') {
                console.info('[MLS] Repaired runtime via server-managed recovery welcome', {
                  source: source || null,
                  reason: reason || null,
                  traceId: traceId || null,
                  groupId: targetId,
                  conversationId: resolvedConversationId,
                });
              }
            }
          } catch (error) {
            console.warn('[MLS] Failed server-managed runtime recovery welcome flow', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              conversationId: resolvedConversationId,
              error,
            });
          }
        }

        if (
          !runtimeHasGroup &&
          membershipStateForRuntimeRepair.currentIdentityMember &&
          !membershipStateForRuntimeRepair.pendingSetup &&
          !membershipStateForRuntimeRepair.welcomeRequired &&
          !pendingWelcomeHintForJoinRepair
        ) {
          try {
            const selfWelcomed = await this.#bootstrapRuntimeWithSelfWelcome({
              conversationId: resolvedConversationId,
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle,
              identityId: activeIdentityId,
              ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
              allowServerManaged: true,
              source: source || 'syncRuntimeGroupState',
              reason: `${reason || 'runtime_repair'}_snapshot_self_welcome`,
            });
            if (selfWelcomed?.groupId) {
              resolvedGroupId = ensureString(selfWelcomed.groupId ?? resolvedGroupId ?? null);
              resolvedGroupHandle = ensureString(
                selfWelcomed.groupHandle ?? resolvedGroupHandle ?? null,
              );
              groups = await this.#loadGroups();
              const targetId = resolvedGroupId ?? syncKey;
              groups[targetId] = {
                ...(groups[targetId] || {}),
                ...selfWelcomed,
                groupId: targetId,
                groupHandle: resolvedGroupHandle ?? selfWelcomed.groupHandle ?? null,
              };
              await this.#saveGroups(groups);
              entry = groups[targetId];
              runtimeGroups = await listRuntimeGroups();
              matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(entry, runtimeGroups);
              runtimeHasGroup = Boolean(matchedRuntimeGroup);
            }
          } catch (error) {
            console.warn('[MLS] Failed snapshot self-welcome runtime repair', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              conversationId: resolvedConversationId,
              error,
            });
          }
        }

        const recoveryReasonAllowsForceJoin = (() => {
          const reasonCode = effectiveRecoveryReason;
          if (!reasonCode) return false;
          if (reasonCode === WAITING_RATCHET_TREE_REASON) return false;
          return true;
        })();
        const preferReissueForRuntimeRepair = effectiveRecoveryReason === 'missing_runtime_group';
        // Respect caller intent strictly. Critical sync/reload paths now keep
        // join_bundle repair enabled so missing runtime groups can self-heal.
        const joinBundleRepairAllowed = Boolean(allowJoinBundleRepair);

        const shouldAttemptForceJoinBundle =
          !runtimeHasGroup &&
          activeIdentityId &&
          joinBundleRepairAllowed &&
          !suppressServerManagedMemberJoinBundle &&
          this.#shouldAttemptForceJoinBundle(resolvedGroupId ?? syncKey, [
            ensureString(resolvedGroupHandle ?? null),
            ensureString(syncKey),
          ]) &&
          typeof this.forceJoinBundle === 'function' &&
          !pendingWelcomeHintForJoinRepair &&
          recoveryReasonAllowsForceJoin;

        if (
          !runtimeHasGroup &&
          pendingWelcomeHintForJoinRepair &&
          typeof console?.info === 'function'
        ) {
          console.info('[MLS] Skipping join_bundle; pending welcome hint present', {
            source: source || null,
            reason: reason || null,
            traceId: traceId || null,
            groupId: resolvedGroupId ?? syncKey,
            groupHandle: resolvedGroupHandle ?? null,
          });
        }
        if (
          !runtimeHasGroup &&
          suppressServerManagedMemberJoinBundle &&
          typeof console?.info === 'function'
        ) {
          console.info('[MLS] Suppressing join_bundle for server-managed member runtime repair', {
            source: source || null,
            reason: reason || null,
            traceId: traceId || null,
            groupId: resolvedGroupId ?? syncKey,
            groupHandle: resolvedGroupHandle ?? null,
            conversationId: resolvedConversationId,
          });
        }
        if (!runtimeHasGroup && typeof console?.info === 'function') {
          console.info('[MLS] Runtime repair decision', {
            source: source || null,
            reason: reason || null,
            traceId: traceId || null,
            groupId: resolvedGroupId ?? syncKey,
            groupHandle: resolvedGroupHandle ?? null,
            allowJoinBundleRepair: joinBundleRepairAllowed,
            pendingWelcomeHintForJoinRepair,
            suppressServerManagedMemberJoinBundle,
            recoveryReason: effectiveRecoveryReason,
            currentIdentityMember: membershipStateForRuntimeRepair.currentIdentityMember,
            pendingSetup: membershipStateForRuntimeRepair.pendingSetup,
            welcomeRequired: membershipStateForRuntimeRepair.welcomeRequired,
            hasWelcomeB64: Boolean(entry?.welcomeB64 || entry?.welcome),
            hasRecoveryWelcomeB64: Boolean(
              entry?.recoveryWelcomeB64 || entry?.recovery_welcome_b64,
            ),
            runtimeEpoch: normalizeEpochOrNull(entry?.runtimeEpoch ?? entry?.runtime_epoch ?? null),
            storedEpoch: normalizeEpochOrNull(entry?.epoch ?? entry?.current_epoch ?? null),
            rehydrateBlockedUntil: this.#getRehydrateBlockedUntil(entry),
          });
          console.info(
            `[MLS] Runtime repair decision detail ${JSON.stringify({
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle ?? null,
              allowJoinBundleRepair: joinBundleRepairAllowed,
              pendingWelcomeHintForJoinRepair,
              suppressServerManagedMemberJoinBundle,
              recoveryReason: effectiveRecoveryReason,
              currentIdentityMember: membershipStateForRuntimeRepair.currentIdentityMember,
              pendingSetup: membershipStateForRuntimeRepair.pendingSetup,
              welcomeRequired: membershipStateForRuntimeRepair.welcomeRequired,
              hasWelcomeB64: Boolean(entry?.welcomeB64 || entry?.welcome),
              hasRecoveryWelcomeB64: Boolean(
                entry?.recoveryWelcomeB64 || entry?.recovery_welcome_b64,
              ),
              runtimeEpoch: normalizeEpochOrNull(
                entry?.runtimeEpoch ?? entry?.runtime_epoch ?? null,
              ),
              storedEpoch: normalizeEpochOrNull(entry?.epoch ?? entry?.current_epoch ?? null),
              rehydrateBlockedUntil: this.#getRehydrateBlockedUntil(entry),
            })}`,
          );
        }

        if (shouldAttemptForceJoinBundle) {
          try {
            const joined = await this.forceJoinBundle({
              conversationId: resolvedConversationId,
              groupId: resolvedGroupId ?? syncKey,
              groupHandle: resolvedGroupHandle,
              identityId: activeIdentityId,
              ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
              preferReissueOnAlreadyMemberNoWelcome: preferReissueForRuntimeRepair,
              __pipelineBypass: true,
            });
            if (joined?.groupId) {
              resolvedGroupId = ensureString(joined.groupId ?? resolvedGroupId ?? null);
              resolvedGroupHandle = ensureString(joined.groupHandle ?? resolvedGroupHandle ?? null);
              groups = await this.#loadGroups();
              const targetId = resolvedGroupId ?? syncKey;
              groups[targetId] = {
                ...(groups[targetId] || {}),
                ...joined,
                groupId: targetId,
                groupHandle: resolvedGroupHandle ?? joined.groupHandle ?? null,
              };
              await this.#saveGroups(groups);
              entry = groups[targetId];
              runtimeGroups = await listRuntimeGroups();
              runtimeHasGroup = this.#runtimeHasGroupForEntry(entry, runtimeGroups);
              if (runtimeHasGroup && typeof console?.info === 'function') {
                console.info('MDBG runtime group repaired via join_bundle', {
                  source: source || null,
                  reason: reason || null,
                  traceId: traceId || null,
                  groupId: targetId,
                  conversationId: resolvedConversationId,
                });
              }
            }
          } catch (error) {
            console.warn('[MLS] Failed to repair runtime via join_bundle', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: resolvedGroupId ?? syncKey,
              conversationId: resolvedConversationId,
              error,
            });
          }
        }

        runtimeGroups = await listRuntimeGroups();
        matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
          { ...entry, groupId: resolvedGroupId, groupHandle: resolvedGroupHandle },
          runtimeGroups,
        );
        runtimeHasGroup = Boolean(matchedRuntimeGroup);
        if (!runtimeHasGroup) {
          const probedRuntimeGroup = await probeRuntimeHandle(entry, {
            fallbackGroupId: resolvedGroupId,
            fallbackGroupHandle: resolvedGroupHandle,
            fallbackEpoch: entry?.epoch ?? null,
          });
          if (probedRuntimeGroup?.groupHandle) {
            matchedRuntimeGroup = probedRuntimeGroup;
            runtimeHasGroup = true;
            if (typeof console?.info === 'function') {
              console.info('[MLS] Recovered runtime group via direct handle probe', {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: resolvedGroupId ?? syncKey,
                groupHandle: resolvedGroupHandle ?? null,
                runtimeHandle: probedRuntimeGroup.groupHandle,
              });
            }
          }
        }

        if (!runtimeHasGroup) {
          const recoveryGroupId = resolvedGroupId ?? syncKey;
          const suppressMissingRuntimeRecovery = this.#shouldDeferRecoveryDuringBootstrap(entry, {
            source,
            reason,
            pendingWelcomeCount: pendingWelcomeHintForJoinRepair ? 1 : 0,
            runtimeHasGroup: false,
          });
          if (suppressMissingRuntimeRecovery) {
            const preservedRuntimeHandle =
              ensureString(resolveRuntimeHandle(entry)) ??
              ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
              null;
            const preservedRuntimeEpoch =
              normalizeEpochOrNull(
                entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
              ) ?? null;
            await this.#markBootstrapConvergence(recoveryGroupId, {
              reason: `${source || 'unknown'}:${reason || 'runtime_missing'}`,
            });
            try {
              await this.#updateGroupEntry(recoveryGroupId, (prev) => ({
                ...prev,
                runtimeHandle:
                  preservedRuntimeHandle ?? ensureString(resolveRuntimeHandle(prev)) ?? null,
                runtimeEpoch:
                  preservedRuntimeEpoch ??
                  normalizeEpochOrNull(
                    prev?.runtimeEpoch ?? prev?.runtime_epoch ?? prev?.runtime_epoch_no ?? null,
                  ) ??
                  null,
                runtimeLive: false,
                runtime_live: false,
                needsRecovery: false,
                recoveryReason: null,
              }));
            } catch {}
            if (typeof console?.info === 'function') {
              console.info('[MLS] Runtime group still materializing; deferring recovery', {
                source: source || null,
                reason: reason || null,
                traceId: traceId || null,
                groupId: recoveryGroupId,
                groupHandle: resolvedGroupHandle ?? null,
                conversationId: resolvedConversationId,
              });
            }
            return this.groupInfo({
              groupId: recoveryGroupId,
              groupHandle: resolvedGroupHandle ?? null,
            }).catch(() => null);
          }
          try {
            await this.markGroupForRecovery(recoveryGroupId, 'missing_runtime_group');
          } catch {}
          if (typeof console?.warn === 'function') {
            console.warn('[MLS] Runtime group still missing after repair attempts', {
              source: source || null,
              reason: reason || null,
              traceId: traceId || null,
              groupId: recoveryGroupId,
              groupHandle: resolvedGroupHandle ?? null,
              conversationId: resolvedConversationId,
            });
          }
          return this.groupInfo({
            groupId: recoveryGroupId,
            groupHandle: resolvedGroupHandle ?? null,
          }).catch(() => null);
        }

        const finalGroupId = resolvedGroupId ?? syncKey;
        const runtimeEpochFromWorkerList = normalizeEpochOrNull(matchedRuntimeGroup?.epoch ?? null);
        const runtimeHandleMatchesPersisted = Boolean(
          ensureString(entry?.runtimeHandle ?? null) &&
            ensureString(matchedRuntimeGroup?.groupHandle ?? null) &&
            ensureString(entry?.runtimeHandle ?? null) ===
              ensureString(matchedRuntimeGroup?.groupHandle ?? null),
        );
        const runtimeEpochFromEntry = runtimeHandleMatchesPersisted
          ? normalizeEpochOrNull(
              entry?.runtimeEpoch ?? entry?.runtime_epoch ?? entry?.runtime_epoch_no ?? null,
            )
          : null;
        const entryEpoch = normalizeEpochOrNull(entry?.epoch ?? null);
        const runtimeEpochCandidates = [runtimeEpochFromWorkerList, runtimeEpochFromEntry].filter(
          (value) => value != null && value > 0,
        );
        const runtimeEpoch = runtimeEpochCandidates.length
          ? Math.max(...runtimeEpochCandidates)
          : (runtimeEpochFromWorkerList ?? runtimeEpochFromEntry);
        const finalizedEpoch =
          runtimeEpoch != null
            ? entryEpoch != null
              ? Math.max(entryEpoch, runtimeEpoch)
              : runtimeEpoch
            : entryEpoch;
        let finalized = this.#finalizeGroupReady({
          ...entry,
          groupId: finalGroupId,
          groupHandle:
            resolvedGroupHandle ??
            ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
            entry?.groupHandle ??
            null,
          runtimeHandle:
            ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
            ensureString(entry?.runtimeHandle ?? null) ??
            null,
          runtimeEpoch:
            runtimeEpoch != null && runtimeEpoch > 0
              ? runtimeEpoch
              : (normalizeEpochOrNull(entry?.runtimeEpoch ?? null) ?? finalizedEpoch ?? null),
          epoch: finalizedEpoch ?? entry?.epoch ?? null,
          currentEpoch:
            finalizedEpoch != null
              ? finalizedEpoch
              : (entry?.currentEpoch ?? entry?.current_epoch ?? null),
          identityId: entry?.identityId ?? activeIdentityId ?? null,
          needsRecovery: false,
          recoveryReason: null,
          cryptoMismatch: false,
          crypto_mismatch: false,
          ratchetTreeMismatch: false,
        });
        finalized = await this.#updateGroupEntry(finalGroupId, (prev) => {
          const prevEpoch = normalizeEpochOrNull(prev?.epoch ?? null);
          const nextEpoch = normalizeEpochOrNull(finalized?.epoch ?? null);
          const mergedEpoch =
            prevEpoch != null && (nextEpoch == null || prevEpoch > nextEpoch)
              ? prevEpoch
              : nextEpoch;

          const prevRuntimeEpoch = normalizeEpochOrNull(
            prev?.runtimeEpoch ?? prev?.runtime_epoch ?? prev?.runtime_epoch_no ?? null,
          );
          const nextRuntimeEpoch = normalizeEpochOrNull(
            finalized?.runtimeEpoch ??
              finalized?.runtime_epoch ??
              finalized?.runtime_epoch_no ??
              null,
          );
          const mergedRuntimeEpoch =
            prevRuntimeEpoch != null &&
            (nextRuntimeEpoch == null || prevRuntimeEpoch > nextRuntimeEpoch)
              ? prevRuntimeEpoch
              : nextRuntimeEpoch;

          const prevLastServerSeq = normalizeEpochOrNull(
            prev?.lastServerSeq ?? prev?.last_server_seq ?? null,
          );
          const nextLastServerSeq = normalizeEpochOrNull(
            finalized?.lastServerSeq ?? finalized?.last_server_seq ?? null,
          );
          const mergedLastServerSeq =
            prevLastServerSeq != null &&
            (nextLastServerSeq == null || prevLastServerSeq > nextLastServerSeq)
              ? prevLastServerSeq
              : nextLastServerSeq;

          return {
            ...prev,
            ...finalized,
            groupId: finalGroupId,
            groupHandle: finalized.groupHandle ?? prev.groupHandle ?? null,
            epoch: mergedEpoch ?? finalized?.epoch ?? prev?.epoch ?? null,
            currentEpoch:
              mergedEpoch ??
              finalized?.currentEpoch ??
              finalized?.current_epoch ??
              prev?.currentEpoch ??
              prev?.current_epoch ??
              null,
            current_epoch:
              mergedEpoch ??
              finalized?.current_epoch ??
              finalized?.currentEpoch ??
              prev?.current_epoch ??
              prev?.currentEpoch ??
              null,
            runtimeEpoch:
              mergedRuntimeEpoch ??
              finalized?.runtimeEpoch ??
              finalized?.runtime_epoch ??
              prev?.runtimeEpoch ??
              prev?.runtime_epoch ??
              null,
            runtime_epoch:
              mergedRuntimeEpoch ??
              finalized?.runtime_epoch ??
              finalized?.runtimeEpoch ??
              prev?.runtime_epoch ??
              prev?.runtimeEpoch ??
              null,
            lastServerSeq:
              mergedLastServerSeq ??
              finalized?.lastServerSeq ??
              finalized?.last_server_seq ??
              prev?.lastServerSeq ??
              prev?.last_server_seq ??
              null,
            last_server_seq:
              mergedLastServerSeq ??
              finalized?.last_server_seq ??
              finalized?.lastServerSeq ??
              prev?.last_server_seq ??
              prev?.lastServerSeq ??
              null,
            needsRecovery: false,
            recoveryReason: null,
            runtimeSyncedAt: Date.now(),
          };
        }).catch(() => finalized);
        return this.groupInfo({
          groupId: finalGroupId,
          groupHandle: finalized?.groupHandle ?? null,
        }).catch(() => finalized);
      })();
    })()
      .catch((error) => {
        console.warn('[MLS] Runtime group sync failed', {
          source: source || null,
          reason: reason || null,
          traceId: traceId || null,
          groupId: syncKey,
          error,
        });
        return null;
      })
      .finally(() => {
        if (this.runtimeSyncTasks.get(syncKey) === task) {
          this.runtimeSyncTasks.delete(syncKey);
        }
        this.runtimeSyncTaskStartedAt.delete(syncKey);
      });

    this.runtimeSyncTasks.set(syncKey, task);
    this.runtimeSyncTaskStartedAt.set(syncKey, Date.now());
    return task;
  }

  async #warmRuntimeGroups() {
    if (this.warmRuntimePromise) {
      return this.warmRuntimePromise;
    }

    this.warmRuntimePromise = (async () => {
      let groups;
      try {
        groups = await this.#loadGroups();
      } catch (error) {
        console.warn('[MLS] Failed to load groups during warmup', error);
        return;
      }

      const values = Object.values(groups || {});
      if (!values.length) {
        return;
      }

      let runtimeGroups = [];
      try {
        runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
      } catch (error) {
        console.warn('[MLS] Failed to list worker groups during warmup', error);
        return;
      }

      let mutated = false;
      for (const entry of values) {
        if (!entry?.groupId && !entry?.groupHandle) continue;
        const matchedRuntime = this.#resolveRuntimeGroupForEntry(entry, runtimeGroups);
        const matchedRuntimeHandle = ensureString(matchedRuntime?.groupHandle ?? null);
        if (matchedRuntimeHandle) {
          if (entry?.groupId) {
            const currentRuntimeHandle = ensureString(entry?.runtimeHandle ?? null);
            const nextRuntimeEpoch = normalizeEpochOrNull(matchedRuntime?.epoch ?? null);
            if (
              currentRuntimeHandle !== matchedRuntimeHandle ||
              normalizeEpochOrNull(entry?.runtimeEpoch ?? null) !== nextRuntimeEpoch
            ) {
              groups[entry.groupId] = {
                ...(groups[entry.groupId] || entry),
                runtimeHandle: matchedRuntimeHandle,
                runtimeEpoch:
                  nextRuntimeEpoch ?? normalizeEpochOrNull(entry?.runtimeEpoch ?? null) ?? null,
                groupHandle: ensureString(entry?.groupHandle ?? null) ?? matchedRuntimeHandle,
              };
              mutated = true;
            }
          }
          continue;
        }
        if (entry?.needsRecovery && entry?.recoveryReason === 'mls_join_timeout') continue;
        if (!entry?.welcomeB64) continue;
        // Avoid eager welcome-join during generic warmup when we already have a
        // persisted group snapshot. Conversation-scoped ensureGroup performs the
        // authoritative hydration path and prevents duplicate runtime churn.
        const hasPersistedSnapshot = Boolean(
          entry?.serializedGroupState || entry?.groupStateB64 || entry?.group_state_b64,
        );
        if (hasPersistedSnapshot) continue;

        if (!this.#canAttemptWelcomeRehydrate(entry)) {
          continue;
        }

        try {
          const restored = await this.#rehydrateGroupFromWelcome(entry, {
            allowForceRejoinRecovery: false,
            source: 'warm_runtime_groups',
          });
          if (restored?.groupHandle) {
            groups[restored.groupId] = restored;
            mutated = true;
            runtimeGroups = normalizeRuntimeGroups(await workerListGroups().catch(() => []));
          } else if (entry?.groupId) {
            await this.#setRehydrateBlockedUntil(
              entry.groupId,
              REHYDRATE_RETRY_BACKOFF_MS,
              'warm_runtime_groups_failed',
            );
          }
        } catch (error) {
          console.warn('[MLS] Failed to restore runtime group during warmup', {
            groupId: entry?.groupId,
            groupHandle: entry?.groupHandle,
            error,
          });
        }
      }

      if (mutated) {
        await this.#saveGroups(groups);
      }
    })()
      .catch((error) => {
        console.warn('[MLS] warm runtime groups failed', error);
      })
      .finally(() => {
        this.warmRuntimePromise = null;
      });

    return this.warmRuntimePromise;
  }

  async #rehydrateGroupFromWelcome(
    existing,
    { allowForceRejoinRecovery = false, source = 'unknown' } = {},
  ) {
    const welcomeB64 = pickBase64String(
      existing?.recoveryWelcomeB64,
      existing?.recovery_welcome_b64,
      existing?.welcomeB64,
      existing?.welcome,
      existing?.welcomeBuffer,
      existing?.welcome_buffer,
    );

    const ratchetTreeB64 = pickBase64String(
      existing?.ratchetTreeB64,
      existing?.ratchetTree,
      existing?.ratchet_tree_b64,
      existing?.ratchet_tree,
      existing?.ratchetTreeBuffer,
      existing?.ratchet_tree_buffer,
    );

    if (!welcomeB64) {
      return null;
    }

    assertBase64Payload(welcomeB64, ratchetTreeB64);

    try {
      console.info('[MLS] Attempting persisted welcome rehydrate', {
        source,
        groupId: existing?.groupId ?? null,
        groupHandle: existing?.groupHandle ?? null,
        epoch: normalizeEpochOrNull(existing?.epoch ?? existing?.current_epoch ?? null),
        hasWelcomeB64: Boolean(existing?.welcomeB64 || existing?.welcome),
        hasRecoveryWelcomeB64: Boolean(
          existing?.recoveryWelcomeB64 || existing?.recovery_welcome_b64,
        ),
        hasRatchetTreeB64: Boolean(ratchetTreeB64),
        needsRecovery: Boolean(existing?.needsRecovery),
        recoveryReason: existing?.recoveryReason ?? null,
      });
      console.info(
        `[MLS] Attempting persisted welcome rehydrate detail ${JSON.stringify({
          source,
          groupId: existing?.groupId ?? null,
          groupHandle: existing?.groupHandle ?? null,
          epoch: normalizeEpochOrNull(existing?.epoch ?? existing?.current_epoch ?? null),
          hasWelcomeB64: Boolean(existing?.welcomeB64 || existing?.welcome),
          hasRecoveryWelcomeB64: Boolean(
            existing?.recoveryWelcomeB64 || existing?.recovery_welcome_b64,
          ),
          hasRatchetTreeB64: Boolean(ratchetTreeB64),
          needsRecovery: Boolean(existing?.needsRecovery),
          recoveryReason: existing?.recoveryReason ?? null,
        })}`,
      );
    } catch {}

    let ratchetTreeBytesForWorker = null;
    if (MLS_WORKER_BYTES_ONLY && ratchetTreeB64) {
      const treeCandidates = [
        existing?.ratchetTreeBytes,
        existing?.ratchet_tree_bytes,
        existing?.ratchetTreeBuffer,
        existing?.ratchet_tree_buffer,
      ];
      for (const candidate of treeCandidates) {
        const coerced = coerceUint8Array(candidate);
        if (coerced && coerced.length > 0) {
          ratchetTreeBytesForWorker = coerced;
          break;
        }
      }
      if (!ratchetTreeBytesForWorker) {
        ratchetTreeBytesForWorker = decodeBase64ToBytes(ratchetTreeB64);
      }
    }

    const identity = await this.#ensureIdentity({ ciphersuite: existing.ciphersuite });
    const activeIdentityId = existing.identityId || identity?.identityId;
    if (!activeIdentityId) {
      return null;
    }

    try {
      const result = await awaitWithTimeout(
        () =>
          workerJoinGroup({
            identityId: activeIdentityId,
            welcomeB64,
            ratchetTreeB64,
            groupId: existing.groupId,
            ...(MLS_WORKER_BYTES_ONLY &&
            ratchetTreeBytesForWorker &&
            ratchetTreeBytesForWorker.length
              ? { ratchetTreeBytes: ratchetTreeBytesForWorker }
              : {}),
          }),
        JOIN_GROUP_WORKER_TIMEOUT_MS,
        null,
      );
      if (!result) {
        const timeoutError = new Error('MLS rehydrate join timeout');
        timeoutError.code = 'MLS_REHYDRATE_JOIN_TIMEOUT';
        throw timeoutError;
      }

      const runtimeVerificationEntry = {
        groupId: existing.groupId || result.groupId || result.groupHandle || null,
        groupHandle: result.groupHandle || existing.groupHandle || null,
        runtimeHandle: result.groupHandle || result.group_handle || null,
        identityId: activeIdentityId,
        epoch: ensureEpoch(result.epoch ?? existing.epoch),
      };
      let matchedRuntimeGroup = null;
      const probeRuntimeGroupByHandle = async () => {
        const candidateHandles = [
          ensureString(result.groupHandle ?? null),
          ensureString(result.group_handle ?? null),
          ensureString(existing.groupHandle ?? null),
        ].filter(Boolean);
        if (!candidateHandles.length) {
          return null;
        }

        const probeContext = ensureString(
          existing.groupId ?? result.groupId ?? result.groupHandle ?? existing.groupHandle ?? null,
        );

        for (const candidateHandle of candidateHandles) {
          try {
            const probe = await awaitWithTimeout(
              () =>
                workerDebugGroupRuntimeState({
                  groupHandle: candidateHandle,
                  includeExportKey: false,
                  label: HISTORY_DERIVED_KEY_LABEL,
                  context: probeContext ?? '',
                  length: 1,
                }),
              1500,
              null,
            );
            if (!probe?.found) {
              continue;
            }

            const probedEpoch = normalizeEpochOrNull(
              probe?.epoch ??
                probe?.currentEpoch ??
                probe?.snapshot?.epoch ??
                probe?.snapshot?.currentEpoch ??
                null,
            );
            return {
              groupId:
                ensureString(probe?.groupId ?? null) ??
                ensureString(existing.groupId ?? result.groupId ?? result.groupHandle ?? null) ??
                null,
              groupHandle: candidateHandle,
              epoch: probedEpoch ?? ensureEpoch(result.epoch ?? existing.epoch),
              ciphersuite:
                normalizeSuiteId(probe?.ciphersuite ?? null) ??
                normalizeSuiteId(result.ciphersuite ?? null) ??
                normalizeSuiteId(existing.ciphersuite ?? null) ??
                null,
              __probedByHandle: true,
            };
          } catch {}
        }

        return null;
      };
      for (const delayMs of [0, 75, 200, 400, 800, 1500]) {
        if (delayMs > 0) {
          await sleep(delayMs);
        }
        try {
          const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
          matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
            runtimeVerificationEntry,
            runtimeGroups,
          );
        } catch {
          matchedRuntimeGroup = null;
        }
        if (!matchedRuntimeGroup) {
          matchedRuntimeGroup = await probeRuntimeGroupByHandle();
        }
        if (matchedRuntimeGroup) {
          break;
        }
      }
      if (!matchedRuntimeGroup) {
        const runtimeMissingError = new Error('MLS runtime group missing after rehydrate join');
        runtimeMissingError.code = 'MLS_GROUP_NEEDS_RECOVERY';
        runtimeMissingError.groupId =
          existing.groupId || result.groupId || result.groupHandle || null;
        runtimeMissingError.groupHandle = result.groupHandle || existing.groupHandle || null;
        runtimeMissingError.recoveryReason = 'missing_runtime_group';
        throw runtimeMissingError;
      }

      const members = normalizeMemberList(existing.members);
      if (!members.includes(activeIdentityId)) {
        members.push(activeIdentityId);
      }

      const resolvedRatchetTreeB64 =
        pickBase64String(
          result?.ratchetTreeB64,
          result?.ratchet_tree_b64,
          result?.ratchetTree,
          result?.ratchet_tree,
          existing?.ratchetTreeB64,
          existing?.ratchetTree,
        ) || null;
      const matchedRuntimeHandle = ensureString(matchedRuntimeGroup?.groupHandle ?? null);
      const matchedRuntimeEpoch = normalizeEpochOrNull(matchedRuntimeGroup?.epoch ?? null);
      const resolvedEpoch = ensureEpoch(result.epoch ?? existing.epoch);
      const effectiveRuntimeEpoch =
        matchedRuntimeEpoch != null && resolvedEpoch != null
          ? Math.max(matchedRuntimeEpoch, resolvedEpoch)
          : matchedRuntimeEpoch != null
            ? matchedRuntimeEpoch
            : resolvedEpoch > 0
              ? resolvedEpoch
              : null;

      try {
        console.info('[MLS] Persisted welcome rehydrate restored runtime', {
          source,
          groupId: existing?.groupId ?? result.groupId ?? result.groupHandle ?? null,
          groupHandle:
            ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
            result.groupHandle ??
            existing?.groupHandle ??
            null,
          runtimeEpoch: effectiveRuntimeEpoch,
          resultEpoch: resolvedEpoch,
        });
        console.info(
          `[MLS] Persisted welcome rehydrate restored runtime detail ${JSON.stringify({
            source,
            groupId: existing?.groupId ?? result.groupId ?? result.groupHandle ?? null,
            groupHandle:
              ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
              result.groupHandle ??
              existing?.groupHandle ??
              null,
            runtimeEpoch: effectiveRuntimeEpoch,
            resultEpoch: resolvedEpoch,
          })}`,
        );
      } catch {}

      const bootstrapStartedAt = Date.now();
      const bootstrapWindowUntil = bootstrapStartedAt + BOOTSTRAP_CONVERGENCE_WINDOW_MS;

      return {
        ...existing,
        groupHandle: result.groupHandle || existing.groupHandle,
        runtimeHandle:
          matchedRuntimeHandle ??
          ensureString(result.groupHandle ?? result.group_handle ?? null) ??
          ensureString(existing.runtimeHandle ?? existing.runtime_handle ?? null) ??
          null,
        groupId: existing.groupId || result.groupId || result.groupHandle,
        ciphersuite: result.ciphersuite || existing.ciphersuite || null,
        ratchetTree: resolvedRatchetTreeB64,
        ratchetTreeB64: resolvedRatchetTreeB64,
        epoch: resolvedEpoch,
        current_epoch: resolvedEpoch,
        currentEpoch: resolvedEpoch,
        runtimeEpoch: effectiveRuntimeEpoch,
        runtime_epoch: effectiveRuntimeEpoch,
        lastServerSeq: parseCursor(existing?.lastServerSeq),
        members,
        identityId: activeIdentityId,
        recoveryWelcomeB64: welcomeB64,
        bootstrapStartedAt,
        bootstrap_started_at: bootstrapStartedAt,
        bootstrapWindowUntil,
        bootstrap_window_until: bootstrapWindowUntil,
        bootstrapReason: 'welcome_rehydrate_convergence',
        bootstrap_reason: 'welcome_rehydrate_convergence',
        rehydrateBlockedUntil: null,
        rehydrateBackoffReason: null,
        pending_handshake_queue: [],
        pendingHandshakeQueue: [],
        joined: true,
        needsRecovery: false,
        recoveryReason: null,
      };
    } catch (error) {
      console.warn('[MLS] Failed to rehydrate group from welcome', {
        groupId: existing.groupId,
        groupHandle: existing.groupHandle,
        source,
        hasWelcomeB64: Boolean(existing?.welcomeB64 || existing?.welcome),
        hasRecoveryWelcomeB64: Boolean(
          existing?.recoveryWelcomeB64 || existing?.recovery_welcome_b64,
        ),
        hasRatchetTreeB64: Boolean(ratchetTreeB64),
        recoveryReason: existing?.recoveryReason ?? null,
        error,
      });
      try {
        console.warn(
          `[MLS] Failed to rehydrate group from welcome detail ${JSON.stringify({
            groupId: existing?.groupId ?? null,
            groupHandle: existing?.groupHandle ?? null,
            source,
            hasWelcomeB64: Boolean(existing?.welcomeB64 || existing?.welcome),
            hasRecoveryWelcomeB64: Boolean(
              existing?.recoveryWelcomeB64 || existing?.recovery_welcome_b64,
            ),
            hasRatchetTreeB64: Boolean(ratchetTreeB64),
            recoveryReason: existing?.recoveryReason ?? null,
            errorMessage: error?.message || String(error),
            errorCode: error?.code || null,
          })}`,
        );
      } catch {}
      const message = String(error?.message || '').toLowerCase();
      const missingKeyPackage =
        message.includes('no matching key package') || message.includes('missing key package');
      if (missingKeyPackage && allowForceRejoinRecovery === true) {
        try {
          const recovered = await this.joinGroup({
            identityId: activeIdentityId,
            welcomeB64,
            ratchetTreeB64,
            groupId: existing.groupId,
            welcomeSource: 'rehydrate_missing_key_recovery',
            expectedEpoch: existing?.epoch ?? null,
            forceRejoin: true,
          });
          if (recovered) {
            return recovered;
          }
        } catch (recoveryError) {
          console.warn('[MLS] Rehydrate recovery via joinGroup failed', {
            groupId: existing.groupId,
            groupHandle: existing.groupHandle,
            error: recoveryError,
          });
        }
      }
      return null;
    }
  }

  #markPendingWelcomesForGroup(groupIds, hasPending) {
    if (!this._pendingWelcomesByGroup) {
      this._pendingWelcomesByGroup = new Map();
    }
    const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    for (const rawId of ids) {
      const key = rawId != null ? String(rawId) : null;
      if (!key) continue;
      if (hasPending) {
        this._pendingWelcomesByGroup.set(key, true);
      } else {
        this._pendingWelcomesByGroup.delete(key);
      }
    }
  }

  #hasUnresolvedBootstrapWelcome(
    entry,
    { groupId = null, groupHandle = null, activeIdentityId = null } = {},
  ) {
    const normalizedGroupId = ensureString(groupId ?? entry?.groupId ?? entry?.group_id ?? null);
    const normalizedGroupHandle = ensureString(
      groupHandle ?? entry?.groupHandle ?? entry?.group_handle ?? null,
    );
    const aliasKeys = [normalizedGroupId, normalizedGroupHandle].filter(Boolean);

    if (
      aliasKeys.some((key) => Boolean(this._pendingWelcomesByGroup?.get(key))) ||
      getPendingWelcomesList(entry).length > 0
    ) {
      return true;
    }

    const persistedWelcomeB64 = pickBase64String(
      entry?.recoveryWelcomeB64,
      entry?.recovery_welcome_b64,
      entry?.welcomeB64,
      entry?.welcome_b64,
      entry?.welcome,
    );
    if (!persistedWelcomeB64) {
      return false;
    }

    const bootstrapConvergenceActive = this.#isBootstrapConvergenceActive(entry);
    const runtimeState =
      ensureString(entry?.runtimeState ?? entry?.runtime_state ?? null) ??
      this.#classifyRuntimeState(entry);
    const welcomeStillOwnsBootstrap =
      bootstrapConvergenceActive && runtimeState !== 'aligned' && !this.#isGroupReady(entry);

    if (hasJoinedGroupHistory(entry) && !welcomeStillOwnsBootstrap) {
      return false;
    }

    const membershipState = this.#resolveEntryMembershipState(entry, {
      activeIdentityId: ensureString(
        activeIdentityId ?? this.identityClientId ?? this.identityId ?? null,
      ),
    });

    if (membershipState.pendingSetup) {
      return false;
    }

    return Boolean(
      welcomeStillOwnsBootstrap ||
        membershipState.currentIdentityMember ||
        membershipState.welcomeRequired ||
        membershipState.runtimeBootstrapRequired,
    );
  }

  #getActivePendingTargetIds() {
    const activeUserId = ensureString(this.userId);
    const activeClientIds = new Set();

    [
      ensureString(this.identityClientId),
      ensureString(this.identityId),
      ensureString(this.identityRecord?.identityId ?? null),
      ensureString(this.identityRecord?.clientId ?? null),
    ]
      .filter(Boolean)
      .forEach((value) => activeClientIds.add(String(value)));

    try {
      if (this.clientIdsBySuite && typeof this.clientIdsBySuite.values === 'function') {
        for (const clientId of this.clientIdsBySuite.values()) {
          const normalized = ensureString(clientId);
          if (normalized) {
            activeClientIds.add(normalized);
          }
        }
      }
    } catch {}

    return { activeUserId, activeClientIds };
  }

  async #shouldDeferNewerPendingWelcome({
    groups = null,
    groupId = null,
    conversationId = null,
    oldestPendingMessageEpoch = null,
  } = {}) {
    if (!(oldestPendingMessageEpoch != null && oldestPendingMessageEpoch > 0)) {
      return false;
    }

    const normalizedGroupId = ensureString(groupId ?? null);
    const normalizedConversationId = ensureString(conversationId ?? null);
    let storedEntry = null;

    if (groups && typeof groups === 'object') {
      storedEntry =
        this.#resolveStoredGroup(groups, {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupId,
        })?.entry ?? null;
    }

    if (!storedEntry && normalizedConversationId && groups && typeof groups === 'object') {
      storedEntry =
        Object.values(groups).find((entry) => {
          const storedConversationId = ensureString(
            entry?.conversationId ?? entry?.conversation_id ?? null,
          );
          if (storedConversationId !== normalizedConversationId) {
            return false;
          }
          if (!normalizedGroupId) {
            return true;
          }
          return (
            ensureString(entry?.groupId ?? null) === normalizedGroupId ||
            ensureString(entry?.groupHandle ?? null) === normalizedGroupId
          );
        }) ?? null;
    }

    const runtimeState =
      ensureString(storedEntry?.runtimeState ?? storedEntry?.runtime_state ?? null) ??
      this.#classifyRuntimeState(storedEntry);
    const storedRuntimeEpoch = normalizeEpochOrNull(
      storedEntry?.runtimeEpoch ??
        storedEntry?.runtime_epoch ??
        storedEntry?.runtime_epoch_no ??
        null,
    );
    const storedRuntimeHandle = resolveRuntimeHandle(storedEntry);
    const storedRuntimeUsable =
      Boolean(storedRuntimeHandle) &&
      storedRuntimeEpoch != null &&
      storedRuntimeEpoch > 0 &&
      !Boolean(storedEntry?.needsRecovery) &&
      runtimeState !== 'runtime_missing' &&
      runtimeState !== 'recovery_required';

    if (storedRuntimeUsable) {
      return true;
    }

    try {
      const liveRuntime = await this.#resolveLiveRuntimeGroupForEntry(storedEntry, {
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupId,
      });
      if (liveRuntime?.runtimeGroup) {
        return true;
      }
    } catch {}

    return false;
  }

  #isPendingWelcomeForActiveTarget(entry, { activeUserId = null, activeClientIds = null } = {}) {
    if (!isPendingWelcomeMessage(entry)) {
      return false;
    }

    const targetUserId = ensureString(entry?.to_user_id ?? entry?.toUserId ?? null);
    if (activeUserId && targetUserId && targetUserId !== activeUserId) {
      return false;
    }

    const targetClientId = ensureString(
      entry?.to_client_id ??
        entry?.toClientId ??
        entry?.client_id ??
        entry?.clientId ??
        entry?.identity_id ??
        entry?.identityId ??
        null,
    );
    if (activeClientIds?.size && targetClientId && !activeClientIds.has(targetClientId)) {
      return false;
    }

    return true;
  }

  #canAdvancePendingCursorPastSkippedItems(items, nextCursor) {
    const normalizedNextCursor = parseCursor(nextCursor);
    if (!(normalizedNextCursor != null && normalizedNextCursor > 0)) {
      return false;
    }
    if (!Array.isArray(items) || !items.length) {
      return false;
    }

    const targets = this.#getActivePendingTargetIds();
    let sawForeignWelcome = false;

    for (const item of items) {
      if (!item || typeof item !== 'object') {
        return false;
      }
      if (isPendingHandshakeMessage(item) || isPendingApplicationMessage(item)) {
        return false;
      }
      if (!isPendingWelcomeMessage(item)) {
        return false;
      }
      if (this.#isPendingWelcomeForActiveTarget(item, targets)) {
        return false;
      }
      sawForeignWelcome = true;
    }

    return sawForeignWelcome;
  }

  #filterPersistablePendingWelcomes(
    pendingWelcomes,
    { groupId = null, source = null, traceId = null } = {},
  ) {
    if (!Array.isArray(pendingWelcomes) || !pendingWelcomes.length) {
      return [];
    }

    const normalizedGroupId = ensureString(groupId);
    const activeUserId = ensureString(this.userId);
    const activeClientIds = new Set();
    [
      ensureString(this.identityClientId),
      ensureString(this.identityId),
      ensureString(this.identityRecord?.identityId ?? null),
    ]
      .filter(Boolean)
      .forEach((value) => activeClientIds.add(String(value)));

    try {
      if (this.clientIdsBySuite && typeof this.clientIdsBySuite.values === 'function') {
        for (const clientId of this.clientIdsBySuite.values()) {
          const normalized = ensureString(clientId);
          if (normalized) {
            activeClientIds.add(normalized);
          }
        }
      }
    } catch {}

    let filtered = pendingWelcomes.slice();
    let droppedForUser = 0;
    let droppedForClient = 0;
    let droppedForAck = 0;
    let droppedForDedupe = 0;

    if (activeUserId) {
      filtered = filtered.filter((entry) => {
        const targetUserId = ensureString(entry?.to_user_id ?? entry?.toUserId ?? null);
        if (!targetUserId || targetUserId === activeUserId) {
          return true;
        }
        droppedForUser += 1;
        return false;
      });
    }

    if (activeClientIds.size) {
      filtered = filtered.filter((entry) => {
        const targetClientId = ensureString(
          entry?.to_client_id ??
            entry?.toClientId ??
            entry?.client_id ??
            entry?.clientId ??
            entry?.identity_id ??
            entry?.identityId ??
            null,
        );
        if (!targetClientId || activeClientIds.has(targetClientId)) {
          return true;
        }
        droppedForClient += 1;
        return false;
      });
    }

    filtered = filtered.filter((entry) => {
      const entryGroupId =
        ensureString(entry?.group_id ?? entry?.groupId ?? normalizedGroupId ?? null) ??
        normalizedGroupId;
      const welcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
      if (!entryGroupId || !welcomeId) {
        return true;
      }
      const acked = this._ackedWelcomeIdsByGroup?.get(entryGroupId);
      if (acked && acked.has(welcomeId)) {
        droppedForAck += 1;
        return false;
      }
      if (this.#isWelcomeAckPending(entryGroupId, welcomeId)) {
        droppedForAck += 1;
        return false;
      }
      return true;
    });

    const deduped = [];
    const seenWelcomeIds = new Set();
    for (const entry of filtered) {
      const welcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
      if (welcomeId) {
        if (seenWelcomeIds.has(welcomeId)) {
          droppedForDedupe += 1;
          continue;
        }
        seenWelcomeIds.add(welcomeId);
      }
      deduped.push(entry);
    }

    const totalDropped = droppedForUser + droppedForClient + droppedForAck + droppedForDedupe;
    if (totalDropped > 0 && typeof console?.info === 'function') {
      console.info('MDBG pending welcomes filtered before persist', {
        source: source || 'unknown',
        traceId: traceId || null,
        groupId: normalizedGroupId,
        droppedTotal: totalDropped,
        droppedForUser,
        droppedForClient,
        droppedForAck,
        droppedForDedupe,
        kept: deduped.length,
      });
    }

    return deduped;
  }

  #markWelcomeAckPending(groupId, welcomeIds, ttlMs = 15000) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) return;
    const ids = Array.from(
      new Set(
        (Array.isArray(welcomeIds) ? welcomeIds : [welcomeIds])
          .map((value) => ensureString(value))
          .filter(Boolean),
      ),
    );
    if (!ids.length) return;
    const now = Date.now();
    const expiresAt = now + Math.max(1000, Number(ttlMs) || 15000);
    const groupMap = this._pendingWelcomeAckByGroup.get(normalizedGroupId) || new Map();
    ids.forEach((id) => {
      groupMap.set(id, expiresAt);
    });
    this._pendingWelcomeAckByGroup.set(normalizedGroupId, groupMap);
  }

  #clearPendingWelcomeAck(groupId, welcomeIds = []) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) return;
    const groupMap = this._pendingWelcomeAckByGroup.get(normalizedGroupId);
    if (!groupMap || !groupMap.size) return;
    const ids = Array.from(
      new Set(
        (Array.isArray(welcomeIds) ? welcomeIds : [welcomeIds])
          .map((value) => ensureString(value))
          .filter(Boolean),
      ),
    );
    if (!ids.length) return;
    ids.forEach((id) => groupMap.delete(id));
    if (!groupMap.size) {
      this._pendingWelcomeAckByGroup.delete(normalizedGroupId);
    } else {
      this._pendingWelcomeAckByGroup.set(normalizedGroupId, groupMap);
    }
  }

  #isWelcomeAckPending(groupId, welcomeId) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedWelcomeId = ensureString(welcomeId);
    if (!normalizedGroupId || !normalizedWelcomeId) return false;
    const groupMap = this._pendingWelcomeAckByGroup.get(normalizedGroupId);
    if (!groupMap || !groupMap.size) return false;
    const expiresAt = Number(groupMap.get(normalizedWelcomeId) || 0);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      groupMap.delete(normalizedWelcomeId);
      if (!groupMap.size) {
        this._pendingWelcomeAckByGroup.delete(normalizedGroupId);
      } else {
        this._pendingWelcomeAckByGroup.set(normalizedGroupId, groupMap);
      }
      return false;
    }
    return true;
  }

  async #reissueWelcomesIfNeeded({
    groupId,
    identityId,
    keyPackageB64,
    token,
    ciphersuite = null,
  }) {
    if (!groupId || !identityId) return null;
    const recoveryIdentityId =
      this.#normalizeIdentityId(this.identityClientId ?? identityId) ?? identityId;

    return welcomeRecoveryCoordinator.run({
      userId: this.userId,
      identityId: recoveryIdentityId,
      groupId,
      task: async () => {
        const resolvedKeyPackage = await this.#resolveServerBoundKeyPackage({
          groupId,
          identityId,
          keyPackageB64,
          ciphersuite,
          allowConsumedRefresh: true,
          context: 'reissue_welcomes',
        }).catch(() => null);
        if (resolvedKeyPackage?.blockedOnTargetMismatch) {
          return null;
        }
        const requestIdentityId =
          this.#normalizeIdentityId(resolvedKeyPackage?.identityId ?? identityId) ?? identityId;
        const requestKeyPackageB64 = resolvedKeyPackage?.keyPackageB64 ?? null;
        if (!requestKeyPackageB64) return null;

        try {
          return await reissueMlsWelcomes({
            groupId,
            identityId: requestIdentityId,
            keyPackageB64: requestKeyPackageB64,
            token,
          });
        } catch (error) {
          if (!this.#isJoinBundleRecoveryError(error)) {
            throw error;
          }

          const info = await this.groupInfo({ groupId }).catch(() => null);
          const recovery = await this.#recoverJoinBundleKeyPackage({
            ciphersuite: ciphersuite ?? info?.ciphersuite ?? null,
            context: 'reissue_welcomes',
            groupId,
            identityId,
          });
          if (recovery?.blockedOnTargetMismatch) return null;

          return reissueMlsWelcomes({
            groupId,
            identityId: recovery.identityId,
            keyPackageB64: recovery.keyPackageB64,
            token,
          });
        }
      },
    });
  }

  async #ingestPendingWelcomesFromSnapshot(snapshot, options = {}) {
    if (!snapshot) {
      return { joined: [] };
    }

    const {
      conversationId = null,
      identityId: providedIdentityId = null,
      identityId = null,
      groupId: fallbackGroupId = null,
      groupHandle: fallbackGroupHandle = null,
    } = options || {};

    let pendingWelcomes = normalizePendingWelcomesFromSnapshot(snapshot, {
      conversationId,
      groupId: fallbackGroupId,
      groupHandle: fallbackGroupHandle,
    });

    const historicalBootstrapWelcomeByGroup = new Map();
    const canUseHistoricalBootstrapWelcome = async ({
      groupId,
      groupHandle = null,
      snapshotEpoch = null,
    } = {}) => {
      const normalizedGroupId = ensureString(groupId);
      const normalizedGroupHandle = ensureString(groupHandle);
      const cacheKey = normalizedGroupId || normalizedGroupHandle || '__unknown__';
      if (historicalBootstrapWelcomeByGroup.has(cacheKey)) {
        return historicalBootstrapWelcomeByGroup.get(cacheKey);
      }

      let allowHistoricalBootstrap = false;
      try {
        const info = await this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
        }).catch(() => null);
        const runtimeEpoch = normalizeEpochOrNull(
          info?.runtimeEpoch ?? info?.runtime_epoch ?? info?.runtime_epoch_no ?? null,
        );
        const snapshotEpochForInfo = normalizeEpochOrNull(
          info?.epoch ?? info?.current_epoch ?? info?.currentEpoch ?? snapshotEpoch ?? null,
        );
        const runtimeHandle = resolveRuntimeHandle(info);
        const runtimeState = ensureString(info?.runtimeState ?? info?.runtime_state ?? null);
        const runtimeMissing =
          runtimeState === 'runtime_missing' ||
          runtimeState === 'recovery_required' ||
          (!runtimeHandle &&
            (runtimeEpoch == null || runtimeEpoch <= 0) &&
            snapshotEpochForInfo != null &&
            snapshotEpochForInfo > 0);
        allowHistoricalBootstrap = !this.#isGroupReady(info) && runtimeMissing;
      } catch {}

      historicalBootstrapWelcomeByGroup.set(cacheKey, allowHistoricalBootstrap);
      return allowHistoricalBootstrap;
    };
    const isAllowedHistoricalBootstrapWelcome = ({
      pendingWelcome,
      groupId,
      groupHandle = null,
      authoritativeEpoch = null,
      authoritativeTargetRef = null,
    } = {}) => {
      const cacheKey = ensureString(groupId) || ensureString(groupHandle) || '__unknown__';
      if (historicalBootstrapWelcomeByGroup.get(cacheKey) !== true) {
        return false;
      }

      const pendingEpoch = normalizeEpochOrNull(
        pendingWelcome?.epoch ??
          pendingWelcome?.current_epoch ??
          pendingWelcome?.currentEpoch ??
          null,
      );
      const normalizedAuthoritativeEpoch = normalizeEpochOrNull(authoritativeEpoch);
      const normalizedAuthoritativeTargetRef = normalizeHexRefValue(authoritativeTargetRef);
      const pendingTargetRef = normalizeHexRefValue(
        pendingWelcome?.used_kp_ref ?? pendingWelcome?.usedKpRef ?? null,
      );
      return (
        pendingEpoch != null &&
        normalizedAuthoritativeEpoch != null &&
        pendingEpoch < normalizedAuthoritativeEpoch &&
        (!normalizedAuthoritativeTargetRef ||
          (pendingTargetRef && pendingTargetRef === normalizedAuthoritativeTargetRef))
      );
    };

    // Suppress stale historical welcomes only when runtime is already present.
    // If runtime is missing, the newest available welcome may be the only bootstrap
    // anchor we have; let it stage the group first, then replay commits to catch up.
    try {
      const snapshotEpoch = ensureEpoch(
        snapshot?.epoch ?? snapshot?.current_epoch ?? snapshot?.currentEpoch ?? null,
      );
      if (Number.isFinite(snapshotEpoch)) {
        const filteredWelcomes = [];
        for (const welcome of pendingWelcomes) {
          const welcomeGroupId = ensureString(
            welcome?.group_id ?? welcome?.groupId ?? fallbackGroupId ?? null,
          );
          const welcomeGroupHandle = ensureString(
            welcome?.group_handle ?? welcome?.groupHandle ?? fallbackGroupHandle ?? null,
          );
          const candidate =
            welcome?.epoch ?? welcome?.current_epoch ?? welcome?.currentEpoch ?? null;
          const numeric = Number(candidate);
          const welcomeEpoch = Number.isFinite(numeric) ? ensureEpoch(numeric) : null;
          if (welcomeEpoch == null || welcomeEpoch >= snapshotEpoch) {
            filteredWelcomes.push(welcome);
            continue;
          }
          if (
            await canUseHistoricalBootstrapWelcome({
              groupId: welcomeGroupId,
              groupHandle: welcomeGroupHandle,
              snapshotEpoch,
            })
          ) {
            filteredWelcomes.push(welcome);
          }
        }
        pendingWelcomes = filteredWelcomes;
        if (pendingWelcomes.length === 0) {
          return { joined: [] };
        }
      }
    } catch {}

    // Drop welcomes targeted at other client identities to avoid joining the wrong leaf.
    try {
      const idObj = await this.ensureIdentity({});
      const activeIdentityId =
        providedIdentityId || identityId || idObj?.identityId || this.identityId || null;
      const suiteCandidate = normalizeSuiteId(
        idObj?.ciphersuite ?? this.preferredCiphersuite ?? null,
      );
      const activeClientId =
        this.identityClientId ||
        (suiteCandidate != null ? this.#getClientIdForSuite(suiteCandidate) : null) ||
        idObj?.clientId ||
        null;
      const allowedIds = new Set(
        [activeIdentityId, activeClientId].filter(Boolean).map((value) => String(value)),
      );
      const shouldStrictlyFilterByClient = Boolean(activeClientId);
      if (allowedIds.size && shouldStrictlyFilterByClient) {
        const beforeCount = pendingWelcomes.length;
        pendingWelcomes = pendingWelcomes.filter((entry) => {
          const entryId = ensureString(
            entry?.identity_id ??
              entry?.identityId ??
              entry?.client_id ??
              entry?.clientId ??
              entry?.to_client_id ??
              entry?.toClientId ??
              null,
          );
          if (!entryId) return true;
          return allowedIds.has(String(entryId));
        });
        if (beforeCount !== pendingWelcomes.length && typeof console?.debug === 'function') {
          console.debug('[MLS] filtered pending welcomes for other client ids', {
            dropped: beforeCount - pendingWelcomes.length,
            kept: pendingWelcomes.length,
            identityId: activeIdentityId || null,
            clientId: activeClientId || null,
          });
        }
      }
    } catch {}

    // Drop welcomes targeted at other users (defensive guard).
    try {
      const expectedUserId = this.userId != null ? String(this.userId) : null;
      if (expectedUserId) {
        const beforeCount = pendingWelcomes.length;
        pendingWelcomes = pendingWelcomes.filter((entry) => {
          const targetUserId = ensureString(
            entry?.to_user_id ?? entry?.toUserId ?? entry?.user_id ?? entry?.userId ?? null,
          );
          if (!targetUserId) return true;
          return targetUserId === expectedUserId;
        });
        if (beforeCount !== pendingWelcomes.length && typeof console?.debug === 'function') {
          console.debug('[MLS] filtered pending welcomes for other users', {
            dropped: beforeCount - pendingWelcomes.length,
            kept: pendingWelcomes.length,
            userId: expectedUserId,
          });
        }
      }
    } catch {}

    // Drop welcomes we have already acknowledged or skipped.
    try {
      await this.#ensureAckedWelcomeCacheLoaded();
      const ackedByGroup = this._ackedWelcomeIdsByGroup;
      const pendingAckByGroup = this._pendingWelcomeAckByGroup;
      const hasAckedCache = Boolean(ackedByGroup && ackedByGroup.size);
      const hasPendingAckCache = Boolean(pendingAckByGroup && pendingAckByGroup.size);
      if (hasAckedCache || hasPendingAckCache) {
        let runtimeHandles = null;
        try {
          const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
          runtimeHandles = new Set(
            runtimeGroups
              .map((item) => item?.groupHandle || item?.groupId || null)
              .filter(Boolean)
              .map((value) => String(value)),
          );
        } catch {}

        const beforeCount = pendingWelcomes.length;
        pendingWelcomes = pendingWelcomes.filter((entry) => {
          const entryGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? null,
          );
          const entryGroupHandle = ensureString(entry?.group_handle ?? entry?.groupHandle ?? null);
          const entryWelcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
          if (!entryGroupId || !entryWelcomeId) return true;
          const cached = ackedByGroup.get(entryGroupId);
          const pendingAck = this.#isWelcomeAckPending(entryGroupId, entryWelcomeId);
          if (!(cached && cached.has(entryWelcomeId)) && !pendingAck) {
            return true;
          }

          // If the runtime group is missing, keep the welcome so we can rejoin.
          if (runtimeHandles && runtimeHandles.size) {
            const runtimeHasGroup =
              runtimeHandles.has(String(entryGroupId)) ||
              (entryGroupHandle && runtimeHandles.has(String(entryGroupHandle)));
            if (!runtimeHasGroup) {
              return true;
            }
          }

          return false;
        });
        if (beforeCount !== pendingWelcomes.length && typeof console?.debug === 'function') {
          console.debug('[MLS] skipped already-acked welcomes', {
            dropped: beforeCount - pendingWelcomes.length,
            kept: pendingWelcomes.length,
          });
        }
      }
    } catch {}

    // Keep only the newest pending welcome per group to avoid acking stale ids.
    try {
      if (pendingWelcomes.length > 1) {
        const beforeCount = pendingWelcomes.length;
        const newestByGroup = new Map();
        for (const entry of pendingWelcomes) {
          const entryGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? null,
          );
          if (!entryGroupId) continue;

          const epochCandidate =
            entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null;
          const numericEpoch = Number(epochCandidate);
          const entryEpoch = Number.isFinite(numericEpoch) ? ensureEpoch(numericEpoch) : null;

          const createdAtCandidate =
            entry?.created_at ?? entry?.createdAt ?? entry?.created_at_ms ?? null;
          const createdAtMs = createdAtCandidate
            ? Date.parse(String(createdAtCandidate))
            : Number.NaN;

          const existing = newestByGroup.get(entryGroupId) || null;
          if (!existing) {
            newestByGroup.set(entryGroupId, { entry, epoch: entryEpoch, createdAtMs });
            continue;
          }

          const existingEpoch = existing.epoch;
          if (entryEpoch != null && (existingEpoch == null || entryEpoch > existingEpoch)) {
            newestByGroup.set(entryGroupId, { entry, epoch: entryEpoch, createdAtMs });
            continue;
          }

          if (entryEpoch != null && existingEpoch != null && entryEpoch < existingEpoch) {
            continue;
          }

          const existingCreatedAtMs = existing.createdAtMs;
          if (Number.isFinite(createdAtMs) && Number.isFinite(existingCreatedAtMs)) {
            if (createdAtMs > existingCreatedAtMs) {
              newestByGroup.set(entryGroupId, { entry, epoch: entryEpoch, createdAtMs });
            }
            continue;
          }

          if (Number.isFinite(createdAtMs) && !Number.isFinite(existingCreatedAtMs)) {
            newestByGroup.set(entryGroupId, { entry, epoch: entryEpoch, createdAtMs });
            continue;
          }
        }

        pendingWelcomes = Array.from(newestByGroup.values()).map((item) => item.entry);
        if (beforeCount !== pendingWelcomes.length && typeof console?.debug === 'function') {
          console.debug('[MLS] keeping newest pending welcome per group', {
            dropped: beforeCount - pendingWelcomes.length,
            kept: pendingWelcomes.length,
          });
        }
      }
    } catch {}

    // Once the current identity has already joined a group, do not consume
    // later pending welcomes for that same identity again. They can create
    // duplicate self-join loops and epoch churn on creator/recovery paths.
    try {
      if (pendingWelcomes.length) {
        const groups = await this.#loadGroups().catch(() => ({}));
        const activeIdentityForJoinHistory = ensureString(
          providedIdentityId ?? identityId ?? this.identityClientId ?? this.identityId ?? null,
        );
        const skipAckByGroup = new Map();
        const filteredForJoinHistory = [];

        for (const entry of pendingWelcomes) {
          const entryGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? null,
          );
          const entryGroupHandle = ensureString(
            entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
          );
          const entryEpoch = normalizeEpochOrNull(
            entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
          );
          const stored =
            this.#resolveStoredGroup(groups, {
              groupId: entryGroupId,
              groupHandle: entryGroupHandle,
            })?.entry ?? null;
          const membershipEntry = stored || null;
          const shouldSuppressForJoinHistory =
            this.#shouldSuppressPendingWelcomeForJoinedHistory(membershipEntry, {
              activeIdentityId: activeIdentityForJoinHistory,
              welcomeEpoch: entryEpoch,
            }) &&
            Boolean(
              await this.#resolveConcreteRuntimeForEpoch({
                groupId: entryGroupId,
                groupHandle: entryGroupHandle,
                fallback: membershipEntry,
                requiredEpoch: Math.max(
                  entryEpoch ?? 0,
                  normalizeEpochOrNull(
                    membershipEntry?.epoch ??
                      membershipEntry?.currentEpoch ??
                      membershipEntry?.current_epoch ??
                      null,
                  ) ?? 0,
                ),
              }),
            );

          if (!shouldSuppressForJoinHistory) {
            filteredForJoinHistory.push(entry);
            continue;
          }

          const ackGroupId = entryGroupId || ensureString(membershipEntry?.groupId ?? null);
          const ackWelcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
          if (!ackGroupId || !ackWelcomeId) {
            continue;
          }

          const meta = skipAckByGroup.get(ackGroupId) || {
            ids: new Set(),
            conversations: new Set(),
            clientIds: new Map(),
          };
          meta.ids.add(ackWelcomeId);
          const ackClientId = ensureString(
            entry?.to_client_id ??
              entry?.toClientId ??
              entry?.client_id ??
              entry?.clientId ??
              entry?.identity_id ??
              entry?.identityId ??
              null,
          );
          const clientKey = ackClientId ? String(ackClientId) : '';
          const clientSet = meta.clientIds.get(clientKey) || new Set();
          clientSet.add(ackWelcomeId);
          meta.clientIds.set(clientKey, clientSet);
          const entryConversationId = ensureString(
            entry?.conversation_id ?? entry?.conversationId ?? conversationId,
          );
          if (entryConversationId) {
            meta.conversations.add(entryConversationId);
          }
          skipAckByGroup.set(ackGroupId, meta);
        }

        pendingWelcomes = filteredForJoinHistory;

        for (const [groupKey, meta] of skipAckByGroup.entries()) {
          const conversations = Array.from(meta.conversations);
          if (meta.clientIds instanceof Map && meta.clientIds.size) {
            for (const [clientKey, idSet] of meta.clientIds.entries()) {
              const ids = Array.from(idSet || []);
              if (!ids.length) continue;
              await this.acknowledgeWelcomes({
                groupId: groupKey,
                welcomeIds: ids,
                conversationIds: conversations,
                clientId: clientKey || null,
              });
            }
          } else {
            const ids = Array.from(meta.ids || []);
            if (ids.length) {
              await this.acknowledgeWelcomes({
                groupId: groupKey,
                welcomeIds: ids,
                conversationIds: conversations,
                clientId: null,
              });
            }
          }
        }
      }
    } catch (error) {
      console.warn('[MLS] Failed joined-history pending welcome suppression', {
        conversationId,
        groupId: fallbackGroupId ?? null,
        error,
      });
    }

    if (!pendingWelcomes.length) {
      const resolvedConversationId =
        ensureString(conversationId) ??
        ensureString(snapshot?.conversation_id ?? snapshot?.conversationId ?? null);
      const resolvedGroupId = ensureString(
        snapshot?.group_id ?? snapshot?.groupId ?? fallbackGroupId ?? null,
      );
      const resolvedGroupHandle = ensureString(
        snapshot?.group_handle ?? snapshot?.groupHandle ?? fallbackGroupHandle ?? null,
      );

      let shouldClearWelcomes = true;
      let authoritativeCurrentMemberWithoutWelcome = false;
      try {
        const groups = await this.#loadGroups().catch(() => ({}));
        const storedEntry =
          this.#resolveStoredGroup(groups, {
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
          })?.entry ?? null;
        if (storedEntry) {
          const membershipState = this.#resolveEntryMembershipState(storedEntry, {
            activeIdentityId: ensureString(
              this.identityClientId ??
                this.identityId ??
                storedEntry?.identityId ??
                storedEntry?.identity_id ??
                null,
            ),
          });
          authoritativeCurrentMemberWithoutWelcome =
            membershipState.currentIdentityMember &&
            !membershipState.pendingSetup &&
            !membershipState.welcomeRequired;
        }
      } catch {}
      try {
        const info = await this.groupInfo({
          groupId: resolvedGroupId,
          groupHandle: resolvedGroupHandle,
        }).catch(() => null);
        if (info?.ready) {
          shouldClearWelcomes = true;
        } else if (authoritativeCurrentMemberWithoutWelcome) {
          shouldClearWelcomes = true;
        } else {
          shouldClearWelcomes = false;
        }
      } catch {
        shouldClearWelcomes = authoritativeCurrentMemberWithoutWelcome;
      }

      if (!shouldClearWelcomes) {
        try {
          const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
          const targetHandle = resolvedGroupHandle ?? resolvedGroupId ?? null;
          const runtimeHasGroup = runtimeGroups.some((item) => {
            if (!item) return false;
            const handle = item.groupHandle || item.groupId || null;
            if (!handle) return false;
            if (targetHandle && String(handle) === String(targetHandle)) return true;
            return resolvedGroupId != null && String(handle) === String(resolvedGroupId);
          });
          if (runtimeHasGroup) {
            shouldClearWelcomes = true;
          }
        } catch {}
      }

      if (shouldClearWelcomes) {
        try {
          await this.#clearPersistedWelcomeState({
            conversationId: resolvedConversationId,
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
            clearRecoveryPayload: authoritativeCurrentMemberWithoutWelcome,
          });
        } catch (error) {
          if (typeof console?.debug === 'function') {
            try {
              console.debug('[MLS] Failed to clear persisted welcomes after empty snapshot', error);
            } catch {}
          }
        }
      } else if (typeof console?.debug === 'function') {
        try {
          console.debug('[MLS] Skipping welcome state clear; runtime group not ready', {
            groupId: resolvedGroupId ?? null,
            groupHandle: resolvedGroupHandle ?? null,
          });
        } catch {}
      }

      try {
        this.#markPendingWelcomesForGroup([resolvedGroupId, resolvedGroupHandle], false);
      } catch {}
      return { joined: [] };
    }

    try {
      const pendingGroupIds = new Set();
      for (const entry of pendingWelcomes) {
        const entryGroupId = ensureString(
          entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? fallbackGroupHandle ?? null,
        );
        if (entryGroupId) pendingGroupIds.add(entryGroupId);
      }
      if (pendingGroupIds.size) {
        this.#markPendingWelcomesForGroup(Array.from(pendingGroupIds), true);
        await Promise.allSettled(
          Array.from(pendingGroupIds).map((pendingGroupId) =>
            this.#markBootstrapConvergence(pendingGroupId, {
              reason: 'ingestPendingWelcomesFromSnapshot:pending_welcome',
            }),
          ),
        );
      }
    } catch {}

    // If the MLS group is already ready locally, auto-ack pending welcomes
    // to avoid reissue churn and repeated pending lists. Only do this when
    // the runtime has actually joined the group (joined marker or runtime presence).
    try {
      if (pendingWelcomes.length) {
        const grouped = new Map();
        for (const entry of pendingWelcomes) {
          const entryGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? fallbackGroupHandle ?? null,
          );
          if (!entryGroupId) continue;
          const list = grouped.get(entryGroupId) || [];
          list.push(entry);
          grouped.set(entryGroupId, list);
        }
        for (const [groupId, entries] of grouped.entries()) {
          const requiredEpoch = entries.reduce((highestEpoch, pendingEntry) => {
            const pendingEpoch = normalizeEpochOrNull(
              pendingEntry?.epoch ?? pendingEntry?.currentEpoch ?? pendingEntry?.current_epoch,
            );
            return pendingEpoch != null ? Math.max(highestEpoch, pendingEpoch) : highestEpoch;
          }, 0);
          const concreteRuntime = await this.#resolveConcreteRuntimeForEpoch({
            groupId,
            requiredEpoch,
          });
          if (!concreteRuntime) continue;

          const welcomeIds = entries
            .map((entry) => ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null))
            .filter(Boolean);
          if (!welcomeIds.length) continue;

          const ackClientId = ensureString(
            entries[0]?.to_client_id ??
              entries[0]?.toClientId ??
              entries[0]?.client_id ??
              entries[0]?.clientId ??
              entries[0]?.identity_id ??
              entries[0]?.identityId ??
              null,
          );
          await this.acknowledgeWelcomes({
            groupId,
            welcomeIds,
            conversationId,
            clientId: ackClientId || null,
          });
          pendingWelcomes = pendingWelcomes.filter((entry) => {
            const entryGroupId = ensureString(
              entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? fallbackGroupHandle ?? null,
            );
            return entryGroupId !== groupId;
          });
        }
        if (!pendingWelcomes.length) {
          return { joined: [] };
        }
      }
    } catch {}

    const ackByGroup = new Map();
    if (pendingWelcomes.length) {
      try {
        const authoritativeGroups = (await this.#loadGroups().catch(() => null)) || {};
        for (const entry of pendingWelcomes) {
          const targetGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? fallbackGroupHandle ?? null,
          );
          if (!targetGroupId) continue;

          const targetGroupHandle = ensureString(
            entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
          );
          const authoritative = this.#resolveAuthoritativeWelcomeTargetForGroup(
            authoritativeGroups,
            { groupId: targetGroupId, groupHandle: targetGroupHandle },
          );
          const pendingEpoch = normalizeEpochOrNull(
            entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
          );
          if (
            authoritative?.authoritativeEpoch != null &&
            pendingEpoch != null &&
            pendingEpoch < authoritative.authoritativeEpoch
          ) {
            await canUseHistoricalBootstrapWelcome({
              groupId: targetGroupId,
              groupHandle: targetGroupHandle,
              snapshotEpoch: authoritative.authoritativeEpoch,
            });
          }
        }
        const beforeCount = pendingWelcomes.length;
        pendingWelcomes = pendingWelcomes.filter((entry) => {
          const targetGroupId = ensureString(
            entry?.group_id ?? entry?.groupId ?? fallbackGroupId ?? fallbackGroupHandle ?? null,
          );
          if (!targetGroupId) {
            return true;
          }

          const authoritative = this.#resolveAuthoritativeWelcomeTargetForGroup(
            authoritativeGroups,
            {
              groupId: targetGroupId,
              groupHandle: ensureString(
                entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
              ),
            },
          );
          if (!authoritative) {
            return true;
          }

          const { authoritativeEpoch, authoritativeTargetRef } = authoritative;
          const historicalBootstrapAllowed = isAllowedHistoricalBootstrapWelcome({
            pendingWelcome: entry,
            groupId: targetGroupId,
            groupHandle: ensureString(
              entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
            ),
            authoritativeEpoch,
            authoritativeTargetRef,
          });
          if (
            historicalBootstrapAllowed ||
            !this.#isPendingWelcomeStaleAgainstAuthoritativeTarget({
              pendingWelcome: entry,
              authoritativeEpoch,
              authoritativeTargetRef,
            })
          ) {
            return true;
          }

          const ackId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
          if (ackId) {
            this.#markWelcomeAckPending(targetGroupId, [ackId]);
            const meta = ackByGroup.get(targetGroupId) || {
              ids: new Set(),
              conversations: new Set(),
              clientIds: new Map(),
            };
            meta.ids.add(ackId);
            const ackClientId = ensureString(
              entry?.to_client_id ??
                entry?.toClientId ??
                entry?.client_id ??
                entry?.clientId ??
                entry?.identity_id ??
                entry?.identityId ??
                null,
            );
            const clientKey = ackClientId ? String(ackClientId) : '';
            const clientSet = meta.clientIds.get(clientKey) || new Set();
            clientSet.add(ackId);
            meta.clientIds.set(clientKey, clientSet);
            const entryConversationId = ensureString(
              entry?.conversation_id ?? entry?.conversationId ?? conversationId,
            );
            if (entryConversationId) {
              meta.conversations.add(entryConversationId);
            }
            ackByGroup.set(targetGroupId, meta);
          }

          return false;
        });
        if (beforeCount !== pendingWelcomes.length && typeof console?.info === 'function') {
          console.info(
            '[MLS] Suppressing stale pending welcomes behind a fresher authoritative target',
            {
              dropped: beforeCount - pendingWelcomes.length,
              kept: pendingWelcomes.length,
              conversationId: conversationId ?? null,
              groupId: fallbackGroupId ?? null,
              groupHandle: fallbackGroupHandle ?? null,
            },
          );
        }
      } catch (error) {
        console.warn(
          '[MLS] Failed to suppress stale pending welcomes against authoritative target',
          {
            conversationId,
            groupId: fallbackGroupId ?? null,
            groupHandle: fallbackGroupHandle ?? null,
            error,
          },
        );
      }
    }
    const joined = [];
    const authoritativeGroupsForJoin = (await this.#loadGroups().catch(() => null)) || {};
    // Track groups that had stale welcomes auto-acked so we can resync them.
    const resyncGroups = new Set();
    const resolveConcreteRuntimeForWelcomeAck = async ({
      groupId: ackGroupId,
      groupHandle: ackGroupHandle = null,
      fallback = null,
      requiredEpoch = null,
    } = {}) => {
      return this.#resolveConcreteRuntimeForEpoch({
        groupId: ackGroupId,
        groupHandle: ackGroupHandle,
        fallback,
        requiredEpoch,
      });
    };

    for (const entry of pendingWelcomes) {
      const welcomeB64 = pickBase64String(
        entry?.welcomeB64,
        entry?.welcome_b64,
        entry?.welcome,
        entry?.welcomeBuffer,
        entry?.welcome_buffer,
      );
      if (!welcomeB64) {
        continue;
      }

      const targetGroupId =
        ensureString(entry.group_id ?? entry.groupId ?? fallbackGroupId ?? fallbackGroupHandle) ??
        null;
      if (!targetGroupId) {
        continue;
      }

      const authoritativeForJoin = this.#resolveAuthoritativeWelcomeTargetForGroup(
        authoritativeGroupsForJoin,
        {
          groupId: targetGroupId,
          groupHandle: ensureString(
            entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
          ),
        },
      );
      if (
        authoritativeForJoin &&
        !isAllowedHistoricalBootstrapWelcome({
          pendingWelcome: entry,
          groupId: targetGroupId,
          groupHandle: ensureString(
            entry?.group_handle ?? entry?.groupHandle ?? fallbackGroupHandle ?? null,
          ),
          authoritativeEpoch: authoritativeForJoin.authoritativeEpoch,
          authoritativeTargetRef: authoritativeForJoin.authoritativeTargetRef,
        }) &&
        this.#isPendingWelcomeStaleAgainstAuthoritativeTarget({
          pendingWelcome: entry,
          authoritativeEpoch: authoritativeForJoin.authoritativeEpoch,
          authoritativeTargetRef: authoritativeForJoin.authoritativeTargetRef,
        })
      ) {
        const staleAckId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
        if (staleAckId) {
          this.#markWelcomeAckPending(targetGroupId, [staleAckId]);
          const meta = ackByGroup.get(targetGroupId) || {
            ids: new Set(),
            conversations: new Set(),
            clientIds: new Map(),
          };
          meta.ids.add(staleAckId);
          const staleClientId = ensureString(
            entry?.to_client_id ??
              entry?.toClientId ??
              entry?.client_id ??
              entry?.clientId ??
              entry?.identity_id ??
              entry?.identityId ??
              null,
          );
          const staleClientKey = staleClientId ? String(staleClientId) : '';
          const staleClientSet = meta.clientIds.get(staleClientKey) || new Set();
          staleClientSet.add(staleAckId);
          meta.clientIds.set(staleClientKey, staleClientSet);
          const staleConversationId = ensureString(
            entry?.conversation_id ?? entry?.conversationId ?? conversationId,
          );
          if (staleConversationId) {
            meta.conversations.add(staleConversationId);
          }
          ackByGroup.set(targetGroupId, meta);
        }
        continue;
      }

      const ratchetTreeB64 =
        pickBase64String(
          entry?.ratchetTreeB64,
          entry?.ratchet_tree_b64,
          entry?.ratchetTree,
          entry?.ratchet_tree,
          entry?.ratchetTreeBuffer,
          entry?.ratchet_tree_buffer,
          snapshot?.ratchetTreeB64,
          snapshot?.ratchet_tree_b64,
          snapshot?.ratchetTree,
          snapshot?.ratchet_tree,
        ) || null;

      assertBase64Payload(welcomeB64, ratchetTreeB64);

      const epochCandidate = entry.epoch ?? entry.current_epoch ?? entry.currentEpoch ?? null;
      const numericEpoch = Number(epochCandidate);
      const normalizedEpoch = Number.isFinite(numericEpoch) ? ensureEpoch(numericEpoch) : null;
      const requiredRuntimeEpoch = Math.max(
        normalizedEpoch ?? 0,
        normalizeEpochOrNull(authoritativeForJoin?.authoritativeEpoch ?? null) ?? 0,
        normalizeEpochOrNull(
          snapshot?.epoch ?? snapshot?.current_epoch ?? snapshot?.currentEpoch ?? null,
        ) ?? 0,
      );

      // Atomic epoch guard: avoid ingesting stale welcomes. If the welcome is newer
      // than the snapshot, attempt one forced snapshot refresh before rejecting it.
      try {
        let snapshotEpoch = ensureEpoch(
          snapshot?.epoch ?? snapshot?.current_epoch ?? snapshot?.currentEpoch ?? null,
        );
        let epochMismatch =
          normalizedEpoch != null && snapshotEpoch != null && normalizedEpoch !== snapshotEpoch;
        let allowHistoricalBootstrap = false;
        if (
          epochMismatch &&
          normalizedEpoch != null &&
          snapshotEpoch != null &&
          normalizedEpoch < snapshotEpoch
        ) {
          allowHistoricalBootstrap = await canUseHistoricalBootstrapWelcome({
            groupId: targetGroupId,
            groupHandle: ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
            snapshotEpoch,
          });
          if (allowHistoricalBootstrap) {
            epochMismatch = false;
          }
        }
        if (epochMismatch && normalizedEpoch > snapshotEpoch) {
          try {
            const refreshedSnapshot = await getGroupSnapshot({
              conversationId,
              force: true,
              clientId: identityId,
            }).catch(() => null);
            const refreshedEpoch = ensureEpoch(
              refreshedSnapshot?.epoch ??
                refreshedSnapshot?.current_epoch ??
                refreshedSnapshot?.currentEpoch ??
                null,
            );
            if (refreshedEpoch != null) {
              snapshotEpoch = refreshedEpoch;
              if (normalizedEpoch >= refreshedEpoch) {
                epochMismatch = false;
              }
            } else {
              // No authoritative epoch available after forced refresh; do not
              // block join on this guard to avoid recovery deadlocks.
              epochMismatch = false;
            }
          } catch {}
        }
        if (epochMismatch) {
          try {
            const origin = entry?.__origin || options?.origin || 'unknown';
            const reqId = entry?.__requestId || options?.requestId || null;
            const cableMsgId = entry?.__cableMsgId || options?.cableMsgId || null;
            const welcomeRefs = extractWelcomeRefHexesFromB64(welcomeB64);
            let localRef = null;
            try {
              const idObj = await this.ensureIdentity({});
              const exported = await this.exportKeyPackage(
                idObj?.identityId || this.identityId || null,
              );
              localRef = await computeKeyPackageRefHexFromB64(exported?.keyPackage);
            } catch {}
            console.warn('[MLS] Skipping welcome due to epoch mismatch', {
              expected: snapshotEpoch,
              got: normalizedEpoch,
              origin,
              requestId: reqId,
              cableMsgId,
              groupId: targetGroupId,
              welcomeRef: welcomeRefs[0] || null,
              welcomeRefs,
              localRef,
              pendingFetchId: (() => {
                try {
                  return this._pendingFetchCorrelation.get(String(targetGroupId)) || null;
                } catch {
                  return null;
                }
              })(),
              snapshotFetchId: (() => {
                try {
                  return this._snapshotFetchCorrelation.get(String(targetGroupId)) || null;
                } catch {
                  return null;
                }
              })(),
            });
          } catch {}
          // Deterministic single-flow: do not run join/reissue loops from ingest.
          // Let orchestrator own recovery decisions for epoch mismatch.
          try {
            const staleAckId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
            if (staleAckId) {
              this.#markWelcomeAckPending(targetGroupId, [staleAckId]);
              const meta = ackByGroup.get(targetGroupId) || {
                ids: new Set(),
                conversations: new Set(),
                clientIds: new Map(),
              };
              meta.ids.add(staleAckId);
              const staleClientId = ensureString(
                entry?.to_client_id ??
                  entry?.toClientId ??
                  entry?.client_id ??
                  entry?.clientId ??
                  entry?.identity_id ??
                  entry?.identityId ??
                  identityId ??
                  null,
              );
              const staleClientKey = staleClientId ? String(staleClientId) : '';
              const staleClientSet = meta.clientIds.get(staleClientKey) || new Set();
              staleClientSet.add(staleAckId);
              meta.clientIds.set(staleClientKey, staleClientSet);
              const staleConversationId = ensureString(
                entry?.conversation_id ?? entry?.conversationId ?? conversationId,
              );
              if (staleConversationId) {
                meta.conversations.add(staleConversationId);
              }
              ackByGroup.set(targetGroupId, meta);
            }
            await this.markGroupForRecovery(
              targetGroupId,
              `epoch_mismatch:${normalizedEpoch ?? 'unknown'}`,
            ).catch(() => null);
          } catch {}
          resyncGroups.add(String(targetGroupId));
          continue;
        }
      } catch {}

      try {
        // Log details about the welcome being used for join
        try {
          const origin = entry?.__origin || options?.origin || 'unknown';
          const reqId = entry?.__requestId || options?.requestId || null;
          let welcomeTargetValidation = null;
          let localRef = null;
          let localKeyPackageB64 = null;
          let localKeyPackageSuite = null;
          let activeIdentityForMismatch = null;
          try {
            const idObj = await this.ensureIdentity({});
            activeIdentityForMismatch =
              providedIdentityId || identityId || idObj?.identityId || this.identityId || null;
            if (activeIdentityForMismatch) {
              const exported = await this.exportKeyPackage(activeIdentityForMismatch);
              localKeyPackageB64 = exported?.keyPackage || null;
              localKeyPackageSuite = normalizeSuiteId(
                exported?.ciphersuite ??
                  entry?.ciphersuite ??
                  snapshot?.ciphersuite ??
                  this.preferredCiphersuite ??
                  null,
              );
              localRef = localKeyPackageB64
                ? await computeKeyPackageRefHexFromB64(
                    localKeyPackageB64,
                    localKeyPackageSuite ?? null,
                  ).catch(() => null)
                : null;
              welcomeTargetValidation = await this.#resolveWelcomeTargetValidation({
                identityId: activeIdentityForMismatch,
                groupId: targetGroupId,
                welcomeB64,
                ciphersuite: localKeyPackageSuite ?? this.preferredCiphersuite ?? null,
                currentLocalKeyPackageB64: localKeyPackageB64,
                currentLocalRef: localRef,
              }).catch(() => null);
            }
          } catch {}
          const welcomeRefs =
            Array.isArray(welcomeTargetValidation?.welcomeRefs) &&
            welcomeTargetValidation.welcomeRefs.length
              ? welcomeTargetValidation.welcomeRefs
              : extractWelcomeRefHexesFromB64(welcomeB64);
          const pinnedTargetRef = normalizeHexRefValue(
            welcomeTargetValidation?.pinnedTargetRef ?? null,
          );
          const pinnedTargetMatches = welcomeTargetValidation?.pinnedTargetMatches === true;
          const pinnedSource = ensureString(welcomeTargetValidation?.pinnedSource ?? null) || null;
          debugLog('[MLS DEBUG] joining with welcome', {
            origin,
            requestId: reqId,
            groupId: targetGroupId,
            welcomeEpoch: normalizedEpoch,
            snapshotEpoch: ensureEpoch(
              snapshot?.epoch ?? snapshot?.current_epoch ?? snapshot?.currentEpoch ?? null,
            ),
            welcomeRef: welcomeRefs[0] || null,
            welcomeRefs,
            localRef,
            pinnedTargetRef,
            pinnedSource,
            pendingFetchId: (() => {
              try {
                return this._pendingFetchCorrelation.get(String(targetGroupId)) || null;
              } catch {
                return null;
              }
            })(),
            snapshotFetchId: (() => {
              try {
                return this._snapshotFetchCorrelation.get(String(targetGroupId)) || null;
              } catch {
                return null;
              }
            })(),
          });

          if (
            localRef &&
            welcomeRefs.length &&
            !welcomeRefs.includes(localRef) &&
            !pinnedTargetMatches
          ) {
            if (typeof console?.warn === 'function') {
              console.warn(
                '[MLS] Pending welcome target mismatches current local key package; skipping stale welcome staging',
                {
                  groupId: targetGroupId,
                  welcomeRef: welcomeRefs[0] || null,
                  welcomeRefs,
                  localRef,
                  pinnedTargetRef,
                  pinnedSource,
                  targetClientId:
                    ensureString(
                      entry?.to_client_id ??
                        entry?.toClientId ??
                        entry?.client_id ??
                        entry?.clientId ??
                        entry?.identity_id ??
                        entry?.identityId ??
                        null,
                    ) || null,
                  origin,
                  requestId: reqId,
                },
              );
            }
            const staleAckId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
            if (staleAckId) {
              this.#markWelcomeAckPending(targetGroupId, [staleAckId]);
              const meta = ackByGroup.get(targetGroupId) || {
                ids: new Set(),
                conversations: new Set(),
                clientIds: new Map(),
              };
              meta.ids.add(staleAckId);
              const staleClientId = ensureString(
                entry?.to_client_id ??
                  entry?.toClientId ??
                  entry?.client_id ??
                  entry?.clientId ??
                  entry?.identity_id ??
                  entry?.identityId ??
                  null,
              );
              const staleClientKey = staleClientId ? String(staleClientId) : '';
              const staleClientSet = meta.clientIds.get(staleClientKey) || new Set();
              staleClientSet.add(staleAckId);
              meta.clientIds.set(staleClientKey, staleClientSet);
              const staleConversationId = ensureString(
                entry?.conversation_id ?? entry?.conversationId ?? conversationId,
              );
              if (staleConversationId) {
                meta.conversations.add(staleConversationId);
              }
              ackByGroup.set(targetGroupId, meta);
            }
            let recoveredCurrentLocalJoin = null;
            if (activeIdentityForMismatch && localKeyPackageB64) {
              recoveredCurrentLocalJoin = await this.#recoverJoinAgainstCurrentLocalKeyPackage({
                groupId: targetGroupId,
                identityId: activeIdentityForMismatch,
                keyPackageB64: localKeyPackageB64,
                ciphersuite: localKeyPackageSuite ?? this.preferredCiphersuite ?? null,
                welcomeSource: 'pending_welcome',
                expectedEpoch: normalizedEpoch,
                context: 'pending_welcome_preflight_current_local_mismatch',
                reason: 'pending_welcome_preflight_current_local_mismatch',
              }).catch(() => null);
            }
            if (recoveredCurrentLocalJoin) {
              joined.push(recoveredCurrentLocalJoin);
            } else {
              resyncGroups.add(String(targetGroupId));
            }
            continue;
          }
        } catch {}

        // If runtime is already ready for this group/epoch, do not consume the
        // same pending welcome again. Only ACK it to collapse duplicate ingest lanes.
        try {
          const readyInfo = await resolveConcreteRuntimeForWelcomeAck({
            groupId: targetGroupId,
            groupHandle: ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
            requiredEpoch: requiredRuntimeEpoch,
          });
          if (readyInfo) {
            joined.push(readyInfo);
            const ackId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
            if (ackId) {
              this.#markWelcomeAckPending(targetGroupId, [ackId]);
              const meta = ackByGroup.get(targetGroupId) || {
                ids: new Set(),
                conversations: new Set(),
                clientIds: new Map(),
              };
              meta.ids.add(ackId);
              const ackClientId = ensureString(
                entry?.to_client_id ??
                  entry?.toClientId ??
                  entry?.client_id ??
                  entry?.clientId ??
                  entry?.identity_id ??
                  entry?.identityId ??
                  null,
              );
              const clientKey = ackClientId ? String(ackClientId) : '';
              const clientSet = meta.clientIds.get(clientKey) || new Set();
              clientSet.add(ackId);
              meta.clientIds.set(clientKey, clientSet);
              const entryConversationId = ensureString(
                entry?.conversation_id ?? entry?.conversationId ?? conversationId,
              );
              if (entryConversationId) {
                meta.conversations.add(entryConversationId);
              }
              ackByGroup.set(targetGroupId, meta);
            }
            continue;
          }
        } catch {}

        await this.#persistWelcomeTarget({
          groupId: targetGroupId,
          groupHandle: ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
          conversationId:
            ensureString(entry?.conversation_id ?? entry?.conversationId ?? conversationId) ?? null,
          identityId,
          welcomeB64,
          ratchetTreeB64,
          epoch: normalizedEpoch,
          usedKpRef: entry?.used_kp_ref ?? entry?.usedKpRef ?? null,
          source: 'pending_welcome',
        }).catch(() => null);

        const result = await this.joinGroup({
          identityId,
          welcomeB64,
          ratchetTreeB64,
          groupId: targetGroupId,
          welcomeSource: 'pending_welcome',
          expectedEpoch: normalizedEpoch,
        });

        if (result) {
          let finalResult = result;
          let runtimeConcrete = await resolveConcreteRuntimeForWelcomeAck({
            groupId: targetGroupId,
            groupHandle:
              ensureString(result?.groupHandle ?? null) ??
              ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
            fallback: result,
            requiredEpoch: requiredRuntimeEpoch,
          });
          if (runtimeConcrete) {
            finalResult = runtimeConcrete;
          }
          if (!runtimeConcrete) {
            const materialized = await this.#awaitConcreteRuntimeMaterialization({
              groupId: targetGroupId,
              groupHandle:
                ensureString(result?.groupHandle ?? null) ??
                ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
              conversationId:
                ensureString(
                  result?.conversationId ??
                    result?.conversation_id ??
                    entry?.conversation_id ??
                    entry?.conversationId ??
                    conversationId ??
                    null,
                ) ?? null,
              source: 'syncWelcomes',
              reason: 'pending_welcome_runtime_materialization',
              requiredEpoch: requiredRuntimeEpoch,
              timeoutMs: 5000,
            }).catch(() => null);
            if (materialized) {
              finalResult = materialized;
              runtimeConcrete = await resolveConcreteRuntimeForWelcomeAck({
                groupId: targetGroupId,
                groupHandle:
                  ensureString(materialized?.groupHandle ?? null) ??
                  ensureString(result?.groupHandle ?? null) ??
                  ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
                fallback: materialized,
                requiredEpoch: requiredRuntimeEpoch,
              });
              if (runtimeConcrete) {
                finalResult = runtimeConcrete;
              }
            }
          }
          if (!runtimeConcrete) {
            if (typeof console?.warn === 'function') {
              console.warn(
                '[MLS] Pending welcome retained until the local runtime reaches the authoritative epoch',
                {
                  groupId: targetGroupId,
                  welcomeId: entry?.welcome_id ?? entry?.welcomeId ?? null,
                  epoch: normalizedEpoch,
                  requiredRuntimeEpoch,
                },
              );
            }
            continue;
          }

          joined.push(finalResult);

          const ackId = ensureString(entry.welcome_id ?? entry.welcomeId ?? null);
          if (ackId) {
            this.#markWelcomeAckPending(targetGroupId, [ackId]);
            const meta = ackByGroup.get(targetGroupId) || {
              ids: new Set(),
              conversations: new Set(),
              clientIds: new Map(),
            };
            meta.ids.add(ackId);
            const ackClientId = ensureString(
              entry?.to_client_id ??
                entry?.toClientId ??
                entry?.client_id ??
                entry?.clientId ??
                entry?.identity_id ??
                entry?.identityId ??
                null,
            );
            const clientKey = ackClientId ? String(ackClientId) : '';
            const clientSet = meta.clientIds.get(clientKey) || new Set();
            clientSet.add(ackId);
            meta.clientIds.set(clientKey, clientSet);
            const entryConversationId = ensureString(
              entry.conversation_id ?? entry.conversationId ?? conversationId,
            );
            if (entryConversationId) {
              meta.conversations.add(entryConversationId);
            }
            ackByGroup.set(targetGroupId, meta);
          }
        }
      } catch (error) {
        try {
          const readyInfo = await resolveConcreteRuntimeForWelcomeAck({
            groupId: targetGroupId,
            groupHandle: ensureString(entry?.group_handle ?? entry?.groupHandle ?? null),
            requiredEpoch: requiredRuntimeEpoch,
          });
          if (readyInfo) {
            joined.push(readyInfo);
            const ackId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
            if (ackId) {
              this.#markWelcomeAckPending(targetGroupId, [ackId]);
              const meta = ackByGroup.get(targetGroupId) || {
                ids: new Set(),
                conversations: new Set(),
                clientIds: new Map(),
              };
              meta.ids.add(ackId);
              const ackClientId = ensureString(
                entry?.to_client_id ??
                  entry?.toClientId ??
                  entry?.client_id ??
                  entry?.clientId ??
                  entry?.identity_id ??
                  entry?.identityId ??
                  null,
              );
              const clientKey = ackClientId ? String(ackClientId) : '';
              const clientSet = meta.clientIds.get(clientKey) || new Set();
              clientSet.add(ackId);
              meta.clientIds.set(clientKey, clientSet);
              const entryConversationId = ensureString(
                entry?.conversation_id ?? entry?.conversationId ?? conversationId,
              );
              if (entryConversationId) {
                meta.conversations.add(entryConversationId);
              }
              ackByGroup.set(targetGroupId, meta);
            }
            continue;
          }
        } catch {}

        console.warn('[MLS] Failed to ingest pending welcome', {
          conversationId,
          groupId: targetGroupId,
          welcomeId: entry?.welcome_id ?? entry?.welcomeId ?? null,
          error,
        });

        try {
          const message = (error?.message || String(error || '')).toLowerCase();
          const isKeyPackageMismatch =
            message.includes('no matching key package') ||
            message.includes('missing key package') ||
            message.includes('key package mismatch') ||
            message.includes('mismatch');
          if (isKeyPackageMismatch) {
            // Track repeated mismatches per group and self-heal if necessary.
            // Important: do not auto-ack a welcome that we failed to ingest.
            try {
              this._joinFailures = this._joinFailures || new Map();
              const count = (this._joinFailures.get(targetGroupId) || 0) + 1;
              this._joinFailures.set(targetGroupId, count);
              if (count >= 3) {
                await this.#clearPersistedWelcomeState({ groupId: targetGroupId });
                this._joinFailures.set(targetGroupId, 0);
              }
            } catch {}

            // Ask backend for a fresh join bundle (without forcing a publish here)
            // so ref alignment can be repaired server-side first.
            try {
              const idObj = await this.ensureIdentity({});
              const activeId =
                providedIdentityId || identityId || idObj?.identityId || this.identityId || null;
              if (activeId && targetGroupId) {
                const exported = await this.exportKeyPackage(activeId).catch(() => null);
                const activeKeyPackageB64 = exported?.keyPackage || null;
                if (activeKeyPackageB64) {
                  const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
                  await this.#fetchJoinBundleWithRecovery({
                    groupId: targetGroupId,
                    identityId: activeId,
                    keyPackageB64: activeKeyPackageB64,
                    token: __t ? 'Bearer ' + __t : undefined,
                    ciphersuite: this.preferredCiphersuite ?? null,
                    context: 'pending_welcome_mismatch',
                  }).catch(() => null);
                }
              }
            } catch (bundleError) {
              console.warn('[MLS] Failed join_bundle recovery during pending welcome ingest', {
                groupId: targetGroupId,
                welcomeId: entry?.welcome_id ?? entry?.welcomeId ?? null,
                error: bundleError,
              });
            }

            resyncGroups.add(String(targetGroupId));
          }
        } catch (recoveryError) {
          console.warn('[MLS] pending welcome mismatch recovery failed', {
            groupId: targetGroupId,
            welcomeId: entry?.welcome_id ?? entry?.welcomeId ?? null,
            error: recoveryError,
          });
        }
      }
    }

    for (const [groupKey, meta] of ackByGroup.entries()) {
      const conversations = Array.from(meta.conversations);
      const clientMap = meta.clientIds instanceof Map ? meta.clientIds : new Map();
      if (clientMap.size) {
        for (const [clientKey, idSet] of clientMap.entries()) {
          const ids = Array.from(idSet || []);
          if (!ids.length) continue;
          await this.acknowledgeWelcomes({
            groupId: groupKey,
            welcomeIds: ids,
            conversationIds: conversations,
            clientId: clientKey ? clientKey : null,
          });
        }
        continue;
      }
      const ids = Array.from(meta.ids || []);
      if (!ids.length) continue;
      await this.acknowledgeWelcomes({
        groupId: groupKey,
        welcomeIds: ids,
        conversationIds: conversations,
        clientId: null,
      });
    }

    // If we attempted mismatch recovery, trigger a one-shot resync per group.
    if (resyncGroups.size) {
      for (const gid of resyncGroups) {
        try {
          // brief delay to allow backend to mint replacement
          await new Promise((r) => setTimeout(r, 300));
          const followUp = await this.syncWelcomes({
            conversationId,
            groupId: gid,
            __pipelineBypass: true,
          });
          if (Array.isArray(followUp?.joined) && followUp.joined.length) {
            joined.push(...followUp.joined);
          }
        } catch (resyncError) {
          console.warn('[MLS] Failed to resync welcomes after mismatch recovery', {
            conversationId,
            groupId: gid,
            error: resyncError,
          });
        }
      }
    }

    return { joined };
  }

  async #selfJoinGroup({ conversationId, groupId, groupHandle, identityId, ciphersuite } = {}) {
    const normalizedConversationId = conversationId != null ? String(conversationId) : null;
    const resolvedConversation =
      normalizedConversationId != null ? getKnownMLSConversation(normalizedConversationId) : null;

    let resolvedGroupId = groupId ?? null;
    let resolvedGroupHandle = groupHandle ?? null;

    if (!resolvedGroupId && resolvedConversation) {
      resolvedGroupId =
        resolvedConversation.mls_group_id ??
        resolvedConversation.mlsGroupId ??
        resolvedConversation.meta?.mls_group_id ??
        null;
    }

    if (!resolvedGroupHandle) {
      resolvedGroupHandle = resolvedGroupId ?? null;
    }

    const targetGroupId = resolvedGroupId ?? resolvedGroupHandle ?? null;
    if (!targetGroupId) {
      return null;
    }

    const identity = await this.ensureIdentity({ ciphersuite });
    let activeIdentityId = identityId || identity?.identityId || this.identityId || null;
    if (!activeIdentityId) {
      return null;
    }

    // Defer publish until after we attempt to ingest/sync welcomes, to avoid
    // flipping localRef and invalidating a matching server Welcome mid-join.

    const conversationIdForFetch =
      normalizedConversationId ??
      (resolvedConversation ? normalizeConversationId(resolvedConversation) : null);

    const recordConversationGroup = (entry) => {
      const mapConversationId = normalizedConversationId ?? conversationIdForFetch ?? null;
      if (mapConversationId && entry?.groupId) {
        this.conversationGroups.set(mapConversationId, entry.groupId);
        persistKnownConversationGroupId(mapConversationId, entry.groupId);
      }
    };

    if (conversationIdForFetch) {
      // Prefer a server-side atomic join bundle to avoid races (epoch+tree+welcome)
      try {
        const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
        let exportedKeyPackage = null;
        try {
          exportedKeyPackage = await this.exportKeyPackage(activeIdentityId);
        } catch {}
        if (!exportedKeyPackage?.keyPackage) {
          if (
            this.#shouldPublishBeforeJoinBundle({
              groupId: targetGroupId,
              identityId: activeIdentityId,
            })
          ) {
            try {
              await this.publishKeyPackageAndSync(activeIdentityId, { forcePublish: true });
            } catch {}
          }
          try {
            exportedKeyPackage = await this.exportKeyPackage(activeIdentityId);
          } catch {}
        }
        if (!exportedKeyPackage?.keyPackage) {
          console.warn('[MLS] Skipping join_bundle: missing local key package', {
            identityId: activeIdentityId,
            groupId: targetGroupId,
          });
          throw new Error('MLS key package unavailable for join_bundle');
        }
        const fetched = await this.#fetchJoinBundleWithRecovery({
          groupId: targetGroupId,
          identityId: activeIdentityId,
          keyPackageB64: exportedKeyPackage.keyPackage,
          token: __t ? 'Bearer ' + __t : undefined,
          ciphersuite,
          context: 'self_join_bundle',
        }).catch(() => null);
        if (fetched?.blocked) {
          console.info('[MLS] self join_bundle blocked by locked welcome target', {
            groupId: targetGroupId,
            identityId: activeIdentityId,
            reason: fetched.reason ?? null,
          });
          return null;
        }
        const bundle = fetched?.bundle ?? null;
        if (fetched?.identityId) {
          activeIdentityId = fetched.identityId;
        }
        const bundleWelcome = bundle?.welcome_b64 || bundle?.welcomeB64 || null;
        const bundleTree = bundle?.ratchet_tree_b64 || bundle?.ratchetTreeB64 || null;
        const bundleEpoch = bundle?.epoch != null ? ensureEpoch(bundle.epoch) : null;
        if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
          console.debug('[MLS] self join_bundle noop: already member, continue snapshot sync', {
            groupId: targetGroupId,
            identityId: activeIdentityId,
          });
        }
        if (bundleWelcome && bundleEpoch != null) {
          try {
            let result = await this.joinGroup({
              identityId: activeIdentityId,
              welcomeB64: bundleWelcome,
              ratchetTreeB64: null,
              groupId: targetGroupId,
              welcomeSource: 'join_bundle',
              expectedEpoch: bundleEpoch,
            });
            if (!result && bundleTree) {
              result = await this.joinGroup({
                identityId: activeIdentityId,
                welcomeB64: bundleWelcome,
                ratchetTreeB64: bundleTree,
                groupId: targetGroupId,
                welcomeSource: 'join_bundle',
                expectedEpoch: bundleEpoch,
              });
            }
            if (result) {
              recordConversationGroup(result);
              return result;
            }
          } catch (bundleErr) {
            console.warn(
              '[MLS] initial join_bundle failed, continuing with snapshot/welcomes',
              bundleErr,
            );
          }
        }
      } catch {}
      let snapshot = null;
      try {
        const idObj = await this.ensureIdentity({});
        const suiteCandidate = normalizeSuiteId(
          ciphersuite ?? idObj?.ciphersuite ?? this.preferredCiphersuite ?? null,
        );
        const activeClientId =
          this.identityClientId ||
          (suiteCandidate != null ? this.#getClientIdForSuite(suiteCandidate) : null) ||
          idObj?.clientId ||
          null;
        snapshot = await getGroupSnapshot({
          conversationId: conversationIdForFetch,
          force: true,
          clientId: activeClientId || undefined,
          reissue: true,
        });
      } catch (error) {
        console.warn('[MLS] Failed to fetch MLS group snapshot before self-join', {
          conversationId: conversationIdForFetch,
          groupId: targetGroupId,
          error,
        });
      }

      if (snapshot) {
        const ingestion = await this.#ingestPendingWelcomesFromSnapshot(snapshot, {
          conversationId: conversationIdForFetch,
          identityId: activeIdentityId,
          groupId: targetGroupId,
          groupHandle: resolvedGroupHandle ?? targetGroupId ?? null,
          origin: 'snapshot:selfJoin',
          requestId: `selfjoin-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
        });

        if (ingestion?.joined?.length) {
          const matched =
            ingestion.joined.find(
              (item) =>
                item?.groupId === targetGroupId ||
                (resolvedGroupHandle && item?.groupHandle === resolvedGroupHandle),
            ) ?? ingestion.joined[0];
          if (matched) {
            recordConversationGroup(matched);
            return matched;
          }
        }
      }

      try {
        const synced = await this.syncWelcomes({
          conversationId: conversationIdForFetch,
          groupId: targetGroupId,
        });
        if (synced?.joined?.length) {
          const matched =
            synced.joined.find(
              (item) =>
                item?.groupId === targetGroupId ||
                (resolvedGroupHandle && item?.groupHandle === resolvedGroupHandle),
            ) ?? synced.joined[0];
          if (matched) {
            recordConversationGroup(matched);
            return matched;
          }
        }
      } catch (error) {
        console.warn('[MLS] Failed to sync welcomes during self-join', {
          conversationId: conversationIdForFetch,
          groupId: targetGroupId,
          error,
        });
      }

      // If we still haven't joined, publish a fresh KeyPackage once and try syncing again.
      try {
        const fallbackPublishDisposition = await this.#resolveBootstrapPublishDisposition({
          identityId: activeIdentityId,
          groupId: targetGroupId,
          groupHandle: resolvedGroupHandle ?? targetGroupId ?? null,
          conversationId: conversationIdForFetch,
          ciphersuite,
        }).catch(() => null);
        if (fallbackPublishDisposition?.allowPublish !== false) {
          await this.publishKeyPackageAndSync(activeIdentityId);
          // Allow backend/sidecar propagation before attempting to fetch a join bundle/welcomes
          try {
            await sleep(650);
          } catch {}
        } else if (typeof console?.info === 'function') {
          console.info(
            '[MLS] Self-join fallback preserving current local key package; skipping publish',
            {
              conversationId: conversationIdForFetch,
              groupId: targetGroupId,
              identityId: activeIdentityId,
              skipReason: fallbackPublishDisposition?.reason ?? 'publish_not_required',
              targetRef: fallbackPublishDisposition?.targetRef ?? null,
            },
          );
        }
        // Prefer an atomic join bundle first after publish
        try {
          const __t2 = typeof getAuthToken === 'function' ? getAuthToken() : null;
          let bundleAfterPublish = null;
          let exportedKeyPackage = null;
          try {
            exportedKeyPackage = await this.exportKeyPackage(activeIdentityId);
          } catch {}
          if (exportedKeyPackage?.keyPackage) {
            const fetched = await this.#fetchJoinBundleWithRecovery({
              groupId: targetGroupId,
              identityId: activeIdentityId,
              keyPackageB64: exportedKeyPackage.keyPackage,
              token: __t2 ? 'Bearer ' + __t2 : undefined,
              ciphersuite,
              context: 'self_join_bundle_after_publish',
            }).catch(() => null);
            bundleAfterPublish = fetched?.bundle ?? null;
            if (fetched?.identityId) {
              activeIdentityId = fetched.identityId;
            }
          } else {
            console.warn('[MLS] Skipping join_bundle after publish: missing key package', {
              identityId: activeIdentityId,
              groupId: targetGroupId,
            });
          }
          const bWelcome =
            bundleAfterPublish?.welcome_b64 || bundleAfterPublish?.welcomeB64 || null;
          const bTree =
            bundleAfterPublish?.ratchet_tree_b64 || bundleAfterPublish?.ratchetTreeB64 || null;
          const bEpoch =
            bundleAfterPublish?.epoch != null ? ensureEpoch(bundleAfterPublish.epoch) : null;
          if (bWelcome && bTree && bEpoch != null) {
            const joined = await this.joinGroup({
              identityId: activeIdentityId,
              welcomeB64: bWelcome,
              ratchetTreeB64: bTree,
              groupId: targetGroupId,
              welcomeSource: 'join_bundle',
              expectedEpoch: bEpoch,
            }).catch(() => null);
            if (joined) {
              recordConversationGroup(joined);
              return joined;
            }
          }
        } catch {}
        const syncedAfterPublish = await this.syncWelcomes({
          conversationId: conversationIdForFetch,
          groupId: targetGroupId,
        });
        if (syncedAfterPublish?.joined?.length) {
          const matched =
            syncedAfterPublish.joined.find(
              (item) =>
                item?.groupId === targetGroupId ||
                (resolvedGroupHandle && item?.groupHandle === resolvedGroupHandle),
            ) ?? syncedAfterPublish.joined[0];
          if (matched) {
            recordConversationGroup(matched);
            return matched;
          }
        }
      } catch (error) {
        console.warn('[MLS] Failed to publish/sync during self-join fallback', {
          conversationId: conversationIdForFetch,
          groupId: targetGroupId,
          error,
        });
      }

      const allowReissue = true;
      if (allowReissue) {
        try {
          {
            const __t3 = typeof getAuthToken === 'function' ? getAuthToken() : null;
            let keyPackageB64 = null;
            try {
              const exported = await this.exportKeyPackage(activeIdentityId);
              keyPackageB64 = exported?.keyPackage || null;
            } catch {}
            await this.#reissueWelcomesIfNeeded({
              groupId: targetGroupId,
              identityId: activeIdentityId,
              keyPackageB64,
              token: __t3 ? 'Bearer ' + __t3 : undefined,
            });
          }
          const reissued = await this.syncWelcomes({
            conversationId: conversationIdForFetch,
            groupId: targetGroupId,
          });
          if (reissued?.joined?.length) {
            const matched =
              reissued.joined.find(
                (item) =>
                  item?.groupId === targetGroupId ||
                  (resolvedGroupHandle && item?.groupHandle === resolvedGroupHandle),
              ) ?? reissued.joined[0];
            if (matched) {
              recordConversationGroup(matched);
              return matched;
            }
          }
        } catch (error) {
          console.warn('[MLS] Failed to reissue welcomes during self-join', {
            conversationId: conversationIdForFetch,
            groupId: targetGroupId,
            error,
          });
        }
      }
    }

    return null;
  }

  async #updateGroupEntry(groupId, updater) {
    if (!groupId) return null;
    const groups = await this.#loadGroups();
    const existing = groups[groupId] || null;
    if (!existing) return null;
    const nextBase =
      typeof updater === 'function' ? updater(existing) : { ...existing, ...updater };
    if (!nextBase || typeof nextBase !== 'object') {
      return existing;
    }
    const next = { ...nextBase };
    const nextHasOwn = (key) => Object.prototype.hasOwnProperty.call(nextBase, key);
    const existingCursor = parseCursor(
      existing?.lastServerSeq ?? existing?.last_server_seq ?? existing?.lastServerSequence ?? null,
    );
    const nextHasExplicitCursor =
      nextHasOwn('lastServerSeq') ||
      nextHasOwn('last_server_seq') ||
      nextHasOwn('lastServerSequence');
    if (!nextHasExplicitCursor && existingCursor != null) {
      next.lastServerSeq = existingCursor;
      next.last_server_seq = existingCursor;
    }
    const nextHasExplicitProcessedSeqs =
      nextHasOwn('processedServerSeqs') ||
      nextHasOwn('processed_server_seqs') ||
      nextHasOwn('processedServerSequences');
    if (!nextHasExplicitProcessedSeqs) {
      const preservedProcessedSeqs = normalizeProcessedServerSeqs(
        existing?.processedServerSeqs ??
          existing?.processed_server_seqs ??
          existing?.processedServerSequences ??
          [],
        {
          baseCursor: existingCursor ?? 0,
        },
      );
      next.processedServerSeqs = preservedProcessedSeqs;
      next.processed_server_seqs = preservedProcessedSeqs;
    }
    const decorated = this.#decorateGroupEntry(next, { stampSyncAt: true });
    groups[groupId] = decorated;
    await this.#saveGroups(groups);
    return decorated;
  }

  async #persistServerGroupSnapshot(snapshot, options = {}) {
    if (!snapshot || typeof snapshot !== 'object') {
      return null;
    }

    const {
      fallbackGroupId = null,
      fallbackGroupHandle = null,
      fallbackConversationId = null,
      clearRecovery = true,
      skipFinalize = false,
      allowSnapshotServerSeqAdvance = false,
      source = null,
      traceId = null,
    } = options;

    const normalizedGroupId = snapshot.group_id ?? snapshot.groupId ?? null;
    const normalizedGroupHandle = snapshot.group_handle ?? snapshot.groupHandle ?? null;
    const normalizedEpoch =
      snapshot.epoch ?? snapshot.current_epoch ?? snapshot.currentEpoch ?? null;
    const normalizedLastServerSeq =
      snapshot.last_server_seq ?? snapshot.lastServerSeq ?? snapshot.last_msg_seq ?? null;
    const hasMembersField =
      Object.prototype.hasOwnProperty.call(snapshot, 'members') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'member_ids') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'memberIds') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'member_client_ids') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'memberClientIds') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'clients');
    const membersSource = hasMembersField
      ? (snapshot.members ??
        snapshot.member_ids ??
        snapshot.memberIds ??
        snapshot.member_client_ids ??
        snapshot.memberClientIds ??
        snapshot.clients ??
        [])
      : null;
    const normalizedMembers = hasMembersField ? normalizeMemberList(membersSource) : null;
    const snapshotRatchetTreeB64 = pickBase64String(
      snapshot.ratchet_tree_b64,
      snapshot.ratchetTreeB64,
      snapshot.ratchet_tree,
      snapshot.ratchetTree,
    );
    const snapshotWelcomeB64 = pickBase64String(snapshot.welcome_b64, snapshot.welcomeB64);
    const snapshotConversationId = ensureString(
      snapshot.conversation_id ?? snapshot.conversationId ?? fallbackConversationId ?? null,
    );
    const hasRuntimeHandleField =
      Object.prototype.hasOwnProperty.call(snapshot, 'runtime_handle') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'runtimeHandle');
    const hasCiphersuiteField =
      Object.prototype.hasOwnProperty.call(snapshot, 'ciphersuite') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'cipher_suite');
    const hasIdentityField =
      Object.prototype.hasOwnProperty.call(snapshot, 'identity_id') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'identityId');
    const hasRatchetTreeField =
      Object.prototype.hasOwnProperty.call(snapshot, 'ratchet_tree_b64') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'ratchetTreeB64') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'ratchet_tree') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'ratchetTree');
    const hasWelcomeField =
      Object.prototype.hasOwnProperty.call(snapshot, 'welcome_b64') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'welcomeB64');
    const hasPendingField =
      Object.prototype.hasOwnProperty.call(snapshot, 'pending_welcomes') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'pendingWelcomes');
    const hasMissingField =
      Object.prototype.hasOwnProperty.call(snapshot, 'missing_user_ids') ||
      Object.prototype.hasOwnProperty.call(snapshot, 'missingUserIds');
    const normalizedPendingWelcomes = hasPendingField
      ? normalizePendingWelcomesFromSnapshot(snapshot, {
          conversationId: snapshotConversationId,
          groupId: normalizedGroupId ?? fallbackGroupId ?? null,
          groupHandle: normalizedGroupHandle ?? fallbackGroupHandle ?? null,
        })
      : null;
    const normalizedMissingUserIds = hasMissingField
      ? normalizeUserIdList(snapshot.missing_user_ids ?? snapshot.missingUserIds ?? [])
      : null;

    const normalized = {
      groupId: normalizedGroupId ?? fallbackGroupId ?? null,
      groupHandle: normalizedGroupHandle ?? fallbackGroupHandle ?? null,
      epoch: normalizedEpoch,
      current_epoch: normalizedEpoch,
      currentEpoch: normalizedEpoch,
      lastServerSeq: normalizedLastServerSeq,
      conversationId: snapshotConversationId ?? null,
      fetchedAt: Date.now(),
    };
    if (hasMembersField) {
      normalized.members = normalizedMembers;
    }
    if (hasRuntimeHandleField) {
      normalized.runtimeHandle = snapshot.runtime_handle ?? snapshot.runtimeHandle ?? null;
    }
    if (hasCiphersuiteField) {
      normalized.ciphersuite = snapshot.ciphersuite ?? snapshot.cipher_suite ?? null;
    }
    if (hasIdentityField) {
      normalized.identityId = snapshot.identity_id ?? snapshot.identityId ?? null;
    }
    if (hasRatchetTreeField) {
      normalized.ratchetTree = snapshotRatchetTreeB64;
      normalized.ratchetTreeB64 = snapshotRatchetTreeB64;
    }
    if (hasWelcomeField) {
      normalized.welcomeB64 = snapshotWelcomeB64 || null;
      normalized.recoveryWelcomeB64 = snapshotWelcomeB64 || null;
    }
    if (hasPendingField) {
      const filteredPendingWelcomes = this.#filterPersistablePendingWelcomes(
        normalizedPendingWelcomes,
        {
          groupId: normalizedGroupId ?? fallbackGroupId ?? null,
          source,
          traceId,
        },
      );
      normalized.pending_welcomes = filteredPendingWelcomes;
      normalized.pendingWelcomes = filteredPendingWelcomes;
      const pendingHandshakeQueue = buildPendingHandshakeQueue(
        filteredPendingWelcomes,
        normalized.groupId ?? fallbackGroupId ?? null,
      );
      normalized.pending_handshake_queue = pendingHandshakeQueue;
      normalized.pendingHandshakeQueue = pendingHandshakeQueue;
    }
    if (hasMissingField) {
      normalized.missing_user_ids = normalizedMissingUserIds;
      normalized.missingUserIds = normalizedMissingUserIds;
    }

    let groups = await this.#loadGroups();
    let { key: resolvedKey, entry: resolvedEntry } = this.#resolveStoredGroup(groups, {
      groupId: normalized.groupId,
      groupHandle: normalized.groupHandle,
    });

    let targetKey =
      resolvedKey ??
      normalized.groupId ??
      fallbackGroupId ??
      normalized.groupHandle ??
      fallbackGroupHandle ??
      null;

    if (!targetKey) {
      return null;
    }

    let existing = groups[targetKey] || resolvedEntry || {};
    const existingEpoch = ensureEpoch(
      existing?.epoch ?? existing?.current_epoch ?? existing?.currentEpoch ?? null,
    );
    const incomingEpoch = ensureEpoch(normalizedEpoch);
    const staleEpochSnapshot =
      Number.isFinite(existingEpoch) &&
      Number.isFinite(incomingEpoch) &&
      incomingEpoch < existingEpoch;
    const acceptAuthoritativeReset =
      staleEpochSnapshot &&
      shouldAcceptAuthoritativeSnapshotReset({
        existing,
        incomingEpoch,
        snapshotGroupHandle: normalized.groupHandle ?? fallbackGroupHandle ?? null,
        snapshotRatchetTreeB64,
        snapshotWelcomeB64,
        normalizedPendingWelcomes,
        snapshotIdentityId: snapshot.identity_id ?? snapshot.identityId ?? null,
      });
    if (acceptAuthoritativeReset) {
      if (typeof console?.warn === 'function') {
        console.warn('[MLS] Accepting authoritative server snapshot reset', {
          source: source || null,
          traceId: traceId || null,
          groupId:
            ensureString(existing?.groupId ?? null) ??
            ensureString(normalizedGroupId ?? fallbackGroupId ?? null) ??
            null,
          existingEpoch,
          incomingEpoch,
          existingGroupHandle: ensureString(
            existing?.groupHandle ?? existing?.runtimeHandle ?? existing?.runtime_handle ?? null,
          ),
          incomingGroupHandle: ensureString(normalized.groupHandle ?? fallbackGroupHandle ?? null),
        });
      }
      try {
        await this.resetGroup(ensureString(existing?.groupId ?? targetKey ?? null));
      } catch {}
      groups = await this.#loadGroups();
      ({ key: resolvedKey, entry: resolvedEntry } = this.#resolveStoredGroup(groups, {
        groupId: normalized.groupId,
        groupHandle: normalized.groupHandle,
      }));
      targetKey =
        resolvedKey ??
        normalized.groupId ??
        fallbackGroupId ??
        normalized.groupHandle ??
        fallbackGroupHandle ??
        null;
      if (!targetKey) {
        return null;
      }
      existing = groups[targetKey] || resolvedEntry || {};
    }
    if (staleEpochSnapshot && !acceptAuthoritativeReset) {
      if (typeof console?.info === 'function') {
        console.info('[MLS] Ignoring stale server snapshot (older epoch)', {
          source: source || null,
          traceId: traceId || null,
          groupId:
            ensureString(existing?.groupId ?? null) ??
            ensureString(normalizedGroupId ?? fallbackGroupId ?? null) ??
            null,
          existingEpoch,
          incomingEpoch,
        });
      }
      const preserved = existing && Object.keys(existing).length ? existing : null;
      if (!preserved) {
        return null;
      }
      return skipFinalize ? preserved : this.#finalizeGroupReady(preserved);
    }

    const merged = {
      ...existing,
      ...normalized,
    };
    const existingLastServerSeq =
      existing?.lastServerSeq == null ? null : ensureEpoch(existing.lastServerSeq);
    const snapshotLastServerSeq =
      normalizedLastServerSeq == null ? null : ensureEpoch(normalizedLastServerSeq);
    const mergedLastServerSeq =
      merged?.lastServerSeq == null ? null : ensureEpoch(merged.lastServerSeq);

    const canonicalGroupId =
      merged.groupId != null
        ? merged.groupId
        : fallbackGroupId != null
          ? fallbackGroupId
          : existing.groupId != null
            ? existing.groupId
            : targetKey;
    const canonicalGroupHandle =
      merged.groupHandle != null
        ? merged.groupHandle
        : fallbackGroupHandle != null
          ? fallbackGroupHandle
          : existing.groupHandle || null;

    const mergedMembers = Array.isArray(merged.members) ? merged.members : null;
    const existingMembers = Array.isArray(existing.members) ? existing.members : null;
    const sanitized = {
      ...merged,
      groupId: canonicalGroupId,
      groupHandle: canonicalGroupHandle,
      runtimeHandle: merged.runtimeHandle ?? existing.runtimeHandle ?? null,
      epoch:
        merged.epoch == null && existing.epoch != null
          ? ensureEpoch(existing.epoch)
          : ensureEpoch(merged.epoch),
      // Keep cursor authoritative to locally processed messages only.
      // Snapshot/WS metadata may be ahead of local decrypt/apply progress.
      // Advancing cursor from snapshot can drop required commit/application frames as stale.
      lastServerSeq: (() => {
        if (allowSnapshotServerSeqAdvance === true) {
          return mergedLastServerSeq;
        }
        if (existingLastServerSeq != null) {
          return existingLastServerSeq;
        }
        return snapshotLastServerSeq == null ? null : 0;
      })(),
      members: normalizeMemberList(
        mergedMembers && mergedMembers.length ? mergedMembers : existingMembers,
      ),
      conversationId: ensureString(merged.conversationId ?? existing.conversationId ?? null),
      needsRecovery: clearRecovery ? false : Boolean(merged.needsRecovery),
      recoveryReason: clearRecovery ? null : merged.recoveryReason || null,
    };
    sanitized.current_epoch = sanitized.epoch;
    sanitized.currentEpoch = sanitized.epoch;
    const usedKpRef = normalizeHexRefValue(
      snapshot.used_kp_ref ??
        snapshot.usedKpRef ??
        merged.used_kp_ref ??
        merged.usedKpRef ??
        existing.used_kp_ref ??
        existing.usedKpRef ??
        null,
    );
    if (usedKpRef) {
      sanitized.used_kp_ref = usedKpRef;
      sanitized.usedKpRef = usedKpRef;
    }
    const processedServerSeqs = normalizeProcessedServerSeqs(
      snapshot.processed_server_seqs ??
        snapshot.processedServerSeqs ??
        merged.processed_server_seqs ??
        merged.processedServerSeqs ??
        existing.processed_server_seqs ??
        existing.processedServerSeqs ??
        [],
      {
        baseCursor: sanitized.lastServerSeq ?? 0,
      },
    );
    sanitized.processed_server_seqs = processedServerSeqs;
    sanitized.processedServerSeqs = processedServerSeqs;
    if (hasPendingField) {
      const pendingSource = normalizedPendingWelcomes;
      const pendingNormalized = normalizePendingWelcomesFromSnapshot(
        {
          ...snapshot,
          group_id: sanitized.groupId,
          group_handle: sanitized.groupHandle,
          conversation_id: sanitized.conversationId,
          pending_welcomes: pendingSource,
        },
        {
          conversationId: sanitized.conversationId,
          groupId: sanitized.groupId,
          groupHandle: sanitized.groupHandle,
        },
      );
      const filteredPending = this.#filterPersistablePendingWelcomes(pendingNormalized, {
        groupId: sanitized.groupId,
        source,
        traceId,
      });
      sanitized.pending_welcomes = filteredPending;
      sanitized.pendingWelcomes = filteredPending;
      const pendingHandshakeQueue = buildPendingHandshakeQueue(filteredPending, sanitized.groupId);
      sanitized.pending_handshake_queue = pendingHandshakeQueue;
      sanitized.pendingHandshakeQueue = pendingHandshakeQueue;
    } else {
      // Pending API is the source of truth. If snapshot omits pending fields,
      // clear locally cached pending state to avoid stale self-join loops.
      sanitized.pending_welcomes = [];
      sanitized.pendingWelcomes = [];
      sanitized.pending_handshake_queue = [];
      sanitized.pendingHandshakeQueue = [];
    }
    if (
      hasMissingField ||
      Array.isArray(merged.missing_user_ids) ||
      Array.isArray(existing.missing_user_ids)
    ) {
      const missingSource = hasMissingField
        ? normalizedMissingUserIds
        : Array.isArray(merged.missing_user_ids)
          ? merged.missing_user_ids
          : Array.isArray(existing.missing_user_ids)
            ? existing.missing_user_ids
            : [];
      const missingNormalized = normalizeUserIdList(missingSource);
      sanitized.missing_user_ids = missingNormalized;
      sanitized.missingUserIds = missingNormalized;
    }
    if (!Array.isArray(sanitized.pending_handshake_queue)) {
      const pendingSource = Array.isArray(sanitized.pending_welcomes)
        ? sanitized.pending_welcomes
        : Array.isArray(sanitized.pendingWelcomes)
          ? sanitized.pendingWelcomes
          : [];
      sanitized.pending_handshake_queue = buildPendingHandshakeQueue(
        pendingSource,
        sanitized.groupId,
      );
      sanitized.pendingHandshakeQueue = sanitized.pending_handshake_queue;
    }
    const persistedPendingWelcomeCount = Array.isArray(sanitized.pending_welcomes)
      ? sanitized.pending_welcomes.length
      : Array.isArray(sanitized.pendingWelcomes)
        ? sanitized.pendingWelcomes.length
        : 0;
    const welcomeTargetIdentityId = this.#normalizeIdentityId(
      sanitized.welcomeTargetIdentityId ??
        sanitized.welcome_target_identity_id ??
        sanitized.identityId ??
        sanitized.identity_id ??
        existing.welcomeTargetIdentityId ??
        existing.welcome_target_identity_id ??
        existing.identityId ??
        existing.identity_id ??
        null,
    );
    const existingWelcomeTargetRefLock = normalizeHexRefValue(
      existing?.welcomeTargetRefLock ?? existing?.welcome_target_ref_lock ?? null,
    );
    const createBootstrapAuthority =
      welcomeTargetIdentityId && this._conversationCreateBootstrapByIdentity instanceof Map
        ? this._conversationCreateBootstrapByIdentity.get(welcomeTargetIdentityId) || null
        : null;
    const createBootstrapTargetRef = normalizeHexRefValue(
      createBootstrapAuthority?.targetRef ?? null,
    );
    const effectiveWelcomeTargetRef =
      normalizeHexRefValue(usedKpRef) ??
      existingWelcomeTargetRefLock ??
      createBootstrapTargetRef ??
      null;
    const hasPersistedWelcomePayload = Boolean(
      pickBase64String(
        sanitized.recoveryWelcomeB64,
        sanitized.recovery_welcome_b64,
        sanitized.welcomeB64,
        sanitized.welcome_b64,
        sanitized.welcome,
        null,
      ),
    );
    const shouldPreserveWelcomeTargetLock = Boolean(
      effectiveWelcomeTargetRef &&
        (persistedPendingWelcomeCount > 0 ||
          hasPersistedWelcomePayload ||
          !this.#isGroupReady(sanitized)),
    );
    const welcomeTargetEpoch = normalizeEpochOrNull(
      sanitized.welcomeTargetEpoch ??
        sanitized.welcome_target_epoch ??
        sanitized.epoch ??
        sanitized.current_epoch ??
        sanitized.currentEpoch ??
        existing.welcomeTargetEpoch ??
        existing.welcome_target_epoch ??
        null,
    );
    if (shouldPreserveWelcomeTargetLock) {
      sanitized.welcomeTargetRefLock = effectiveWelcomeTargetRef;
      sanitized.welcome_target_ref_lock = effectiveWelcomeTargetRef;
      sanitized.welcomeTargetIdentityId = welcomeTargetIdentityId;
      sanitized.welcome_target_identity_id = welcomeTargetIdentityId;
      sanitized.welcomeTargetEpoch = welcomeTargetEpoch;
      sanitized.welcome_target_epoch = welcomeTargetEpoch;
      sanitized.welcomeTargetFetchedAt = Date.now();
      sanitized.welcome_target_fetched_at = sanitized.welcomeTargetFetchedAt;
    } else {
      sanitized.welcomeTargetRefLock = null;
      sanitized.welcome_target_ref_lock = null;
      sanitized.welcomeTargetIdentityId = null;
      sanitized.welcome_target_identity_id = null;
      sanitized.welcomeTargetEpoch = null;
      sanitized.welcome_target_epoch = null;
      sanitized.welcomeTargetFetchedAt = null;
      sanitized.welcome_target_fetched_at = null;
    }
    if (persistedPendingWelcomeCount === 0 && this.#hasJoinedGroupHistory(sanitized)) {
      sanitized.welcomeB64 = null;
      sanitized.recoveryWelcomeB64 = null;
      sanitized.welcome_b64 = null;
      sanitized.recovery_welcome_b64 = null;
    }
    sanitized.group_state_b64 = serializeGroupStateSnapshot(sanitized);
    sanitized.serializedGroupState = sanitized.group_state_b64;

    const decoratedSanitized = this.#decorateGroupEntry(sanitized, { stampSyncAt: true });
    const nextGroups = { ...groups };
    const finalKey = sanitized.groupId ?? targetKey;
    nextGroups[finalKey] = { ...nextGroups[finalKey], ...decoratedSanitized };
    if (resolvedKey && resolvedKey !== finalKey) {
      delete nextGroups[resolvedKey];
    } else if (targetKey !== finalKey) {
      delete nextGroups[targetKey];
    }

    const mappedConversationId = ensureString(sanitized?.conversationId ?? null);
    const mappedGroupId = ensureString(sanitized?.groupId ?? finalKey ?? null);
    if (mappedConversationId && mappedGroupId) {
      try {
        this.conversationGroups.set(mappedConversationId, mappedGroupId);
        persistKnownConversationGroupId(mappedConversationId, mappedGroupId);
      } catch {}
    }

    if (usedKpRef) {
      await this.#registerIssuedWelcomeTargetRef(usedKpRef, {
        identityId: welcomeTargetIdentityId,
        clientId: welcomeTargetIdentityId,
        ciphersuite: sanitized.ciphersuite ?? existing.ciphersuite ?? null,
        groupId: sanitized.groupId,
        epoch: sanitized.epoch,
      });
    }

    await this.#saveGroups(nextGroups);
    // Avoid auto self-join here; joining should be driven by explicit welcome/join bundle flow.
    const nextEntry = nextGroups[finalKey];
    const pendingCount = Array.isArray(nextEntry?.pending_welcomes)
      ? nextEntry.pending_welcomes.length
      : Array.isArray(nextEntry?.pendingWelcomes)
        ? nextEntry.pendingWelcomes.length
        : null;
    this.#markPendingWelcomesForGroup(
      [nextEntry?.groupId ?? finalKey, nextEntry?.groupHandle ?? null],
      pendingCount != null ? pendingCount > 0 : false,
    );
    try {
      if (typeof console?.info === 'function') {
        console.info('MDBG IndexedDB group snapshot persisted', {
          source: source || 'unknown',
          traceId: traceId || null,
          storage: 'IndexedDB(localForage)',
          key: finalKey,
          groupId: nextEntry?.groupId ?? finalKey,
          groupHandle: nextEntry?.groupHandle ?? null,
          conversationId: nextEntry?.conversationId ?? null,
          epoch: nextEntry?.epoch ?? null,
          pendingWelcomes: pendingCount,
          ready: Boolean(nextEntry?.ready),
        });
      }
    } catch {}
    if (skipFinalize) {
      return nextEntry;
    }
    return this.#finalizeGroupReady(nextEntry);
  }

  async #clearPersistedWelcomeState({
    conversationId,
    groupId,
    groupHandle,
    clearRecoveryPayload = false,
    preserveWelcomeTargetLock = false,
  } = {}) {
    const normalizedConversationId = ensureString(conversationId);
    const normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);

    const groups = await this.#loadGroups();
    if (!groups || typeof groups !== 'object') {
      return;
    }

    const candidateKeys = new Set();
    if (normalizedGroupId) {
      candidateKeys.add(String(normalizedGroupId));
    }
    if (normalizedGroupHandle) {
      candidateKeys.add(String(normalizedGroupHandle));
    }

    if (normalizedConversationId) {
      const mappedGroupId = this.conversationGroups.get(normalizedConversationId);
      if (mappedGroupId != null) {
        candidateKeys.add(String(mappedGroupId));
      }
      const knownConversation = getKnownMLSConversation(normalizedConversationId);
      const knownGroupId = resolveConversationMlsGroupId(knownConversation);
      if (knownGroupId) {
        candidateKeys.add(String(knownGroupId));
      }
    }

    let mutated = false;

    for (const [key, entry] of Object.entries(groups)) {
      if (!entry) continue;

      const entryGroupId = ensureString(entry.groupId ?? entry.group_id ?? key);
      const entryGroupHandle = ensureString(entry.groupHandle ?? entry.group_handle ?? null);
      const entryConversationId = ensureString(
        entry.conversationId ?? entry.conversation_id ?? null,
      );

      let matches = false;
      if (candidateKeys.size) {
        if (candidateKeys.has(String(key))) {
          matches = true;
        } else if (entryGroupId && candidateKeys.has(entryGroupId)) {
          matches = true;
        } else if (entryGroupHandle && candidateKeys.has(entryGroupHandle)) {
          matches = true;
        }
      }

      if (
        !matches &&
        normalizedConversationId &&
        entryConversationId === normalizedConversationId
      ) {
        matches = true;
      }

      if (!matches) {
        continue;
      }

      const next = { ...entry };
      let changed = false;
      const preservedTargetRef = preserveWelcomeTargetLock
        ? normalizeHexRefValue(
            next?.welcomeTargetRefLock ??
              next?.welcome_target_ref_lock ??
              next?.used_kp_ref ??
              next?.usedKpRef ??
              this.#resolveWelcomeTargetRef({
                welcomeB64: pickBase64String(
                  next?.recoveryWelcomeB64,
                  next?.recovery_welcome_b64,
                  next?.welcomeB64,
                  next?.welcome_b64,
                  next?.welcome,
                  null,
                ),
                usedKpRef: next?.used_kp_ref ?? next?.usedKpRef ?? null,
              }),
          )
        : null;
      const preservedTargetIdentity = preserveWelcomeTargetLock
        ? this.#normalizeIdentityId(
            next?.welcomeTargetIdentityId ??
              next?.welcome_target_identity_id ??
              next?.identityId ??
              next?.identity_id ??
              null,
          )
        : null;
      const preservedTargetEpoch = preserveWelcomeTargetLock
        ? normalizeEpochOrNull(
            next?.welcomeTargetEpoch ??
              next?.welcome_target_epoch ??
              next?.epoch ??
              next?.current_epoch ??
              next?.currentEpoch ??
              null,
          )
        : null;

      if (clearRecoveryPayload) {
        if (next.welcomeB64 !== null) {
          next.welcomeB64 = null;
          changed = true;
        }
        if (next.recoveryWelcomeB64 !== null) {
          next.recoveryWelcomeB64 = null;
          changed = true;
        }
      } else if (!next.recoveryWelcomeB64 && next.welcomeB64) {
        next.recoveryWelcomeB64 = next.welcomeB64;
        changed = true;
      }
      if (clearRecoveryPayload && next.welcome_b64 !== null) {
        next.welcome_b64 = null;
        changed = true;
      } else if (next.welcome_b64) {
        next.welcome_b64 = null;
        changed = true;
      }
      if (clearRecoveryPayload && next.recovery_welcome_b64 !== null) {
        next.recovery_welcome_b64 = null;
        changed = true;
      } else if (next.recovery_welcome_b64) {
        next.recovery_welcome_b64 = null;
        changed = true;
      }
      if (next.welcome) {
        next.welcome = null;
        changed = true;
      }
      if (next.welcomeBuffer) {
        next.welcomeBuffer = null;
        changed = true;
      }
      if (next.welcome_buffer) {
        next.welcome_buffer = null;
        changed = true;
      }
      if (Array.isArray(next.pendingWelcomes) && next.pendingWelcomes.length) {
        next.pendingWelcomes = [];
        changed = true;
      }
      if (Array.isArray(next.pending_welcomes) && next.pending_welcomes.length) {
        next.pending_welcomes = [];
        changed = true;
      }
      if (Array.isArray(next.pendingHandshakeQueue) && next.pendingHandshakeQueue.length) {
        next.pendingHandshakeQueue = [];
        changed = true;
      }
      if (Array.isArray(next.pending_handshake_queue) && next.pending_handshake_queue.length) {
        next.pending_handshake_queue = [];
        changed = true;
      }
      if (preserveWelcomeTargetLock && preservedTargetRef) {
        if (next.welcomeTargetRefLock !== preservedTargetRef) {
          next.welcomeTargetRefLock = preservedTargetRef;
          changed = true;
        }
        if (next.welcome_target_ref_lock !== preservedTargetRef) {
          next.welcome_target_ref_lock = preservedTargetRef;
          changed = true;
        }
        if ((next.welcomeTargetIdentityId ?? null) !== (preservedTargetIdentity ?? null)) {
          next.welcomeTargetIdentityId = preservedTargetIdentity ?? null;
          changed = true;
        }
        if ((next.welcome_target_identity_id ?? null) !== (preservedTargetIdentity ?? null)) {
          next.welcome_target_identity_id = preservedTargetIdentity ?? null;
          changed = true;
        }
        if ((next.welcomeTargetEpoch ?? null) !== (preservedTargetEpoch ?? null)) {
          next.welcomeTargetEpoch = preservedTargetEpoch ?? null;
          changed = true;
        }
        if ((next.welcome_target_epoch ?? null) !== (preservedTargetEpoch ?? null)) {
          next.welcome_target_epoch = preservedTargetEpoch ?? null;
          changed = true;
        }
        next.welcomeTargetFetchedAt = Date.now();
        next.welcome_target_fetched_at = next.welcomeTargetFetchedAt;
        changed = true;
      } else {
        if (next.welcomeTargetRefLock != null) {
          next.welcomeTargetRefLock = null;
          changed = true;
        }
        if (next.welcome_target_ref_lock != null) {
          next.welcome_target_ref_lock = null;
          changed = true;
        }
        if (next.welcomeTargetIdentityId != null) {
          next.welcomeTargetIdentityId = null;
          changed = true;
        }
        if (next.welcome_target_identity_id != null) {
          next.welcome_target_identity_id = null;
          changed = true;
        }
        if (next.welcomeTargetEpoch != null) {
          next.welcomeTargetEpoch = null;
          changed = true;
        }
        if (next.welcome_target_epoch != null) {
          next.welcome_target_epoch = null;
          changed = true;
        }
        if (next.welcomeTargetFetchedAt != null) {
          next.welcomeTargetFetchedAt = null;
          changed = true;
        }
        if (next.welcome_target_fetched_at != null) {
          next.welcome_target_fetched_at = null;
          changed = true;
        }
        if (next.protectedWelcomeTargetDrift === true) {
          next.protectedWelcomeTargetDrift = false;
          changed = true;
        }
        [
          'protectedWelcomeTargetDriftIdentityId',
          'protectedWelcomeTargetDriftTargetRef',
          'protectedWelcomeTargetDriftCurrentLocalRef',
          'protectedWelcomeTargetDriftCiphersuite',
          'protectedWelcomeTargetDriftSource',
          'protectedWelcomeTargetDriftDetectedAt',
        ].forEach((field) => {
          if (next[field] != null) {
            next[field] = null;
            changed = true;
          }
        });
      }

      if (!changed) {
        continue;
      }

      groups[key] = next;
      this.#clearActiveJoinBundleTargets({
        groupId: entryGroupId ?? key,
        groupHandle: entryGroupHandle ?? null,
        identityId:
          this.#normalizeIdentityId(
            next?.welcomeTargetIdentityId ??
              next?.welcome_target_identity_id ??
              next?.identityId ??
              next?.identity_id ??
              null,
          ) ?? null,
      });
      mutated = true;
    }

    if (mutated) {
      await this.#saveGroups(groups);
    }
  }

  async #hydrateGroupFromSnapshot(snapshot, options = {}) {
    if (!snapshot) return null;

    const {
      fallbackGroupId = null,
      fallbackGroupHandle = null,
      identityId = null,
      clearRecovery = true,
      allowRatchetTreeSynthesis = true,
      source = 'hydrate:group_snapshot',
      traceId = null,
    } = options || {};

    const persisted = await this.#persistServerGroupSnapshot(snapshot, {
      fallbackGroupId,
      fallbackGroupHandle,
      clearRecovery,
      skipFinalize: true,
      source,
      traceId,
    });
    if (!persisted?.groupId) {
      return persisted;
    }

    let entry = persisted;
    let runtimeHasGroup = false;
    try {
      const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
      const matchedRuntime = this.#resolveRuntimeGroupForEntry(entry, runtimeGroups);
      const matchedRuntimeHandle = ensureString(matchedRuntime?.groupHandle ?? null);
      runtimeHasGroup = Boolean(matchedRuntimeHandle);
      if (runtimeHasGroup && entry?.groupId && matchedRuntimeHandle) {
        const matchedRuntimeEpoch = normalizeEpochOrNull(matchedRuntime?.epoch ?? null);
        const updatedEntry = await this.#updateGroupEntry(entry.groupId, (prev) => ({
          ...prev,
          runtimeHandle: matchedRuntimeHandle,
          runtimeEpoch:
            matchedRuntimeEpoch ??
            normalizeEpochOrNull(prev?.runtimeEpoch ?? prev?.runtime_epoch ?? null) ??
            null,
          groupHandle: ensureString(prev?.groupHandle ?? null) ?? matchedRuntimeHandle,
        })).catch(() => null);
        entry = updatedEntry || entry;
      }
    } catch (error) {
      console.warn('[MLS] Failed to verify runtime during hydration', {
        groupId: entry.groupId,
        error,
      });
    }

    let shouldRecordHydratedEpoch = false;

    if (!runtimeHasGroup) {
      let snapshotRatchetTree =
        snapshot?.ratchet_tree_b64 ??
        snapshot?.ratchetTreeB64 ??
        snapshot?.ratchet_tree ??
        snapshot?.ratchetTree ??
        null;
      const entryRatchetTree = entry?.ratchetTree ?? null;
      const logKeySource =
        entry?.groupId ??
        snapshot?.group_id ??
        snapshot?.groupId ??
        fallbackGroupId ??
        fallbackGroupHandle ??
        null;
      const logKey = logKeySource == null ? null : String(logKeySource);
      const hasRatchetTree = Boolean(entryRatchetTree || snapshotRatchetTree);

      if (!hasRatchetTree) {
        if (logKey && !this.missingRatchetTreeGroups.has(logKey)) {
          this.missingRatchetTreeGroups.add(logKey);
          debugLog('[MLS] Snapshot missing ratchet tree; waiting for next snapshot', {
            groupId: entry?.groupId ?? snapshot?.group_id ?? fallbackGroupId ?? null,
            groupHandle:
              entry?.groupHandle ?? snapshot?.group_handle ?? fallbackGroupHandle ?? null,
          });
        }

        const waitingEntry = {
          ...(entry || {}),
          groupId: entry?.groupId ?? snapshot?.group_id ?? fallbackGroupId ?? null,
          groupHandle: entry?.groupHandle ?? snapshot?.group_handle ?? fallbackGroupHandle ?? null,
          needsRecovery: true,
          recoveryReason: WAITING_RATCHET_TREE_REASON,
          reason: 'missing_ratchet_tree',
        };

        if (waitingEntry.groupId) {
          try {
            await this.#updateGroupEntry(waitingEntry.groupId, (prev) => ({
              ...prev,
              ...waitingEntry,
            }));
          } catch {}
        }

        if (allowRatchetTreeSynthesis === false || this.serverManagedWelcomes) {
          return this.#finalizeGroupReady(waitingEntry);
        }

        let synthesizedEntry = null;

        if (identityId) {
          try {
            const synthesisGroupId =
              entry?.groupId ??
              snapshot?.group_id ??
              fallbackGroupId ??
              fallbackGroupHandle ??
              null;
            const created = await createGroup({
              identityId,
              groupId: synthesisGroupId,
              ciphersuite: snapshot?.ciphersuite ?? entry?.ciphersuite ?? this.preferredCiphersuite,
            });

            if (created?.ratchetTree) {
              const repairedSnapshot = {
                ...snapshot,
                group_id:
                  created?.groupId ??
                  synthesisGroupId ??
                  snapshot?.group_id ??
                  fallbackGroupId ??
                  null,
                group_handle:
                  created?.groupHandle ??
                  snapshot?.group_handle ??
                  entry?.groupHandle ??
                  fallbackGroupHandle ??
                  null,
                ratchet_tree_b64: created.ratchetTree,
                epoch:
                  created?.epoch ?? snapshot?.epoch ?? snapshot?.current_epoch ?? entry?.epoch ?? 0,
                identity_id: identityId ?? snapshot?.identity_id ?? entry?.identityId ?? null,
                members: snapshot?.members ?? entry?.members ?? [],
              };

              synthesizedEntry = await this.#persistServerGroupSnapshot(repairedSnapshot, {
                fallbackGroupId:
                  repairedSnapshot.group_id ?? synthesisGroupId ?? fallbackGroupId ?? null,
                fallbackGroupHandle:
                  repairedSnapshot.group_handle ??
                  entry?.groupHandle ??
                  fallbackGroupHandle ??
                  null,
                clearRecovery: true,
                skipFinalize: true,
              });
            }
          } catch (error) {
            console.warn('[MLS] Failed to synthesize ratchet tree for snapshot', {
              groupId: entry?.groupId ?? snapshot?.group_id ?? fallbackGroupId ?? null,
              groupHandle:
                entry?.groupHandle ?? snapshot?.group_handle ?? fallbackGroupHandle ?? null,
              error,
            });
          }
        }

        if (synthesizedEntry?.ratchetTree) {
          entry = synthesizedEntry;
          runtimeHasGroup = true;
          shouldRecordHydratedEpoch = true;
        } else {
          entry = {
            ...entry,
            ...waitingEntry,
          };
        }

        snapshotRatchetTree = entry?.ratchetTree ?? snapshotRatchetTree;
      }

      const hasResolvedRatchetTree = Boolean(entry?.ratchetTree || snapshotRatchetTree);

      if (!hasResolvedRatchetTree) {
        return this.#finalizeGroupReady(entry);
      }

      if (logKey) {
        this.missingRatchetTreeGroups.delete(logKey);
      }

      const lastEpoch = hydratedEpochsByGroup.get(entry.groupId);
      if (lastEpoch === entry.epoch) {
        return this.#finalizeGroupReady(entry);
      }

      debugLog('[MLS] hydrating worker group from snapshot', {
        groupId: entry.groupId,
        epoch: entry.epoch,
      });

      let rehydrated = null;
      if (
        entry?.welcomeB64 &&
        this.#canAttemptPersistedWelcomeRecovery(entry, {
          source: 'hydrate_group_snapshot',
        })
      ) {
        rehydrated = await this.#rehydrateGroupFromWelcome(entry, {
          allowForceRejoinRecovery: false,
          source: 'hydrate_group_snapshot',
        });
      } else if (entry?.welcomeB64) {
        debugLog('[MLS] Rehydrate attempt skipped due to active backoff', {
          groupId: entry?.groupId ?? null,
          source: 'hydrate_group_snapshot',
          blockedUntil: this.#getRehydrateBlockedUntil(entry),
        });
      }

      // Do not synthesize a fresh group from snapshot-only data.
      // Joining must use a Welcome (or join bundle) so the device has valid secrets.

      if (rehydrated) {
        entry = rehydrated;
        runtimeHasGroup = true;
        shouldRecordHydratedEpoch = true;
      } else if (entry?.groupId && entry?.welcomeB64) {
        await this.#setRehydrateBlockedUntil(
          entry.groupId,
          REHYDRATE_RETRY_BACKOFF_MS,
          'hydrate_group_snapshot_failed',
        );
      }
    }

    if (runtimeHasGroup) {
      if (shouldRecordHydratedEpoch && entry?.groupId) {
        hydratedEpochsByGroup.set(entry.groupId, entry.epoch);
      }
    } else {
      if (entry?.groupId) {
        hydratedEpochsByGroup.delete(entry.groupId);
      }

      try {
        await this.markGroupForRecovery(entry.groupId, 'missing_runtime_group');
      } catch {}
      return this.#finalizeGroupReady({
        ...entry,
        needsRecovery: true,
        recoveryReason: entry.recoveryReason || 'missing_runtime_group',
      });
    }

    return this.#finalizeGroupReady(entry);
  }

  async groupInfo({ groupId, groupHandle } = {}) {
    const groups = await this.#loadGroups();
    let entry = null;
    if (groupId && groups[groupId]) {
      entry = groups[groupId];
    }
    if (!entry && groupHandle) {
      entry = Object.values(groups).find((item) => item.groupHandle === groupHandle) || null;
    }
    if (!entry) return null;
    const epoch = ensureEpoch(entry.epoch);
    const members = normalizeMemberList(entry.members);
    const runtimeHandle = resolveRuntimeHandle(entry);
    const normalized = {
      ...entry,
      epoch,
      runtimeEpoch: normalizeEpochOrNull(
        entry.runtimeEpoch ?? entry.runtime_epoch ?? entry.runtime_epoch_no ?? null,
      ),
      lastServerSeq: parseCursor(
        entry.lastServerSeq ?? entry.last_server_seq ?? entry.lastServerSequence ?? null,
      ),
      processedServerSeqs: normalizeProcessedServerSeqs(
        entry.processedServerSeqs ?? entry.processed_server_seqs ?? [],
        {
          baseCursor: entry.lastServerSeq ?? entry.last_server_seq ?? 0,
        },
      ),
      members,
      runtimeHandle,
      needsRecovery: Boolean(entry.needsRecovery),
      recoveryReason: entry.recoveryReason || null,
    };
    normalized.processed_server_seqs = normalized.processedServerSeqs;

    const shouldVerifyLiveRuntime =
      Boolean(runtimeHandle) ||
      Boolean(
        normalized.runtimeEpoch != null &&
          Number.isFinite(Number(normalized.runtimeEpoch)) &&
          Number(normalized.runtimeEpoch) > 0,
      ) ||
      Boolean(entry?.runtimeLive ?? entry?.runtime_live ?? false);

    if (shouldVerifyLiveRuntime) {
      try {
        const liveRuntime = await this.#resolveLiveRuntimeGroupForEntry(normalized, {
          groupId,
          groupHandle,
        });
        const matchedRuntime = liveRuntime?.runtimeGroup || null;
        if (matchedRuntime) {
          const liveRuntimeHandle = ensureString(matchedRuntime?.groupHandle ?? null);
          const persistedRuntimeEpoch = normalizeEpochOrNull(
            normalized.runtimeEpoch ??
              normalized.runtime_epoch ??
              normalized.runtime_epoch_no ??
              null,
          );
          const runtimeContext = ensureString(
            normalized.conversationId ?? normalized.conversation_id ?? normalized.groupId ?? null,
          );
          const runtimeDebug = liveRuntimeHandle
            ? await workerDebugGroupRuntimeState({
                groupHandle: liveRuntimeHandle,
                includeExportKey: false,
                label: HISTORY_DERIVED_KEY_LABEL,
                context: runtimeContext || null,
                length: 1,
              }).catch(() => null)
            : null;
          const liveRuntimeEpoch =
            normalizeEpochOrNull(runtimeDebug?.epoch ?? null) ??
            normalizeEpochOrNull(matchedRuntime?.epoch ?? null);
          const snapshotRatchetTreeB64 = pickBase64String(
            normalized?.ratchetTreeB64,
            normalized?.ratchetTree,
          );
          const snapshotRatchetTreeSha = await computeSha256HexFromBase64(snapshotRatchetTreeB64);
          const runtimeTreeSha = ensureString(runtimeDebug?.ratchetTreeSha256 ?? null);
          normalized.runtimeLive = true;
          normalized.runtime_live = true;
          if (liveRuntimeHandle) {
            normalized.runtimeHandle = liveRuntimeHandle;
            normalized.runtime_handle = liveRuntimeHandle;
          }
          if (liveRuntimeEpoch != null && liveRuntimeEpoch > 0) {
            normalized.runtimeEpoch = liveRuntimeEpoch;
            normalized.runtime_epoch = liveRuntimeEpoch;
          } else if (persistedRuntimeEpoch != null && persistedRuntimeEpoch > 0) {
            // A live runtime with an unknown debug epoch should not erase a concrete
            // persisted epoch that came from a successful welcome/bootstrap join.
            normalized.runtimeEpoch = persistedRuntimeEpoch;
            normalized.runtime_epoch = persistedRuntimeEpoch;
          } else {
            normalized.runtimeEpoch = null;
            normalized.runtime_epoch = null;
          }
          const sameEpochTreeMismatch =
            Boolean(snapshotRatchetTreeSha) &&
            Boolean(runtimeTreeSha) &&
            liveRuntimeEpoch != null &&
            epoch != null &&
            liveRuntimeEpoch === epoch &&
            runtimeTreeSha !== snapshotRatchetTreeSha;
          normalized.cryptoMismatch = sameEpochTreeMismatch;
          normalized.crypto_mismatch = sameEpochTreeMismatch;
          normalized.ratchetTreeMismatch = sameEpochTreeMismatch;
        } else {
          normalized.runtimeLive = false;
          normalized.runtime_live = false;
          normalized.runtimeEpoch = null;
          normalized.runtime_epoch = null;
        }
      } catch {}
    }

    // Self-heal stale recovery flags: runtime can already be healthy while a previous
    // missing-runtime recovery marker remains persisted.
    if (normalized.needsRecovery) {
      const readyCandidate = {
        ...normalized,
        needsRecovery: false,
        recoveryReason: null,
      };
      if (this.#isGroupReady(readyCandidate)) {
        normalized.needsRecovery = false;
        normalized.recoveryReason = null;
      }
    }
    if (
      normalized.epoch !== entry.epoch ||
      normalized.runtimeEpoch !==
        (entry.runtimeEpoch ?? entry.runtime_epoch ?? entry.runtime_epoch_no ?? null) ||
      normalized.members !== entry.members ||
      normalized.runtimeLive !== (entry.runtimeLive ?? entry.runtime_live ?? undefined) ||
      normalized.needsRecovery !== Boolean(entry.needsRecovery) ||
      normalized.recoveryReason !== (entry.recoveryReason || null) ||
      Boolean(normalized.cryptoMismatch ?? false) !== Boolean(entry.cryptoMismatch ?? false) ||
      Boolean(normalized.ratchetTreeMismatch ?? false) !==
        Boolean(entry.ratchetTreeMismatch ?? false)
    ) {
      groups[normalized.groupId] = {
        ...(groups[normalized.groupId] || {}),
        ...normalized,
      };
      await this.#saveGroups(groups);
    }
    const sanitized = this.#decorateGroupEntry(normalized);
    if (sanitized.welcomeB64) {
      sanitized.hasRecoveryWelcome = true;
      delete sanitized.welcomeB64;
    }
    const finalEntry = {
      ...sanitized,
      runtimeState: this.#classifyRuntimeState(sanitized),
    };
    if (finalEntry.ready) {
      this._becameReady(finalEntry.groupId || finalEntry.groupHandle, finalEntry);
    }
    return finalEntry;
  }

  async exportHistoryKey({ conversationId, groupId, groupHandle, epoch, label, length } = {}) {
    const normalizedConversationId =
      conversationId == null
        ? null
        : typeof conversationId === 'string'
          ? conversationId
          : String(conversationId);
    let resolvedGroupId = groupId != null ? String(groupId) : null;
    if (!resolvedGroupId && normalizedConversationId) {
      resolvedGroupId = this.conversationGroups.get(String(normalizedConversationId)) || null;
    }
    let resolvedEpoch = epoch == null ? null : ensureEpoch(epoch);

    let info = null;
    if (!resolvedGroupId && groupHandle) {
      info = await this.groupInfo({ groupHandle }).catch(() => null);
      resolvedGroupId = info?.groupId || null;
      if (resolvedEpoch == null) {
        resolvedEpoch = info?.epoch != null ? ensureEpoch(info.epoch) : null;
      }
    }

    if (!resolvedGroupId) return null;

    if (!info) {
      info = await this.groupInfo({ groupId: resolvedGroupId, groupHandle }).catch(() => null);
    }
    const currentEpoch = info?.epoch != null ? ensureEpoch(info.epoch) : null;
    if (resolvedEpoch == null) {
      resolvedEpoch = currentEpoch;
    }
    if (resolvedEpoch == null) return null;

    const storageKey = historyDerivedKeyStorageKey(resolvedGroupId, resolvedEpoch);
    const cached = historyDerivedKeyCache.get(storageKey);
    if (cached) return cached;

    const stored = await loadHistoryDerivedKey(storageKey);
    if (stored) {
      historyDerivedKeyCache.set(storageKey, stored);
      return stored;
    }

    if (currentEpoch != null && resolvedEpoch !== currentEpoch) {
      return null;
    }

    const resolvedHandle = groupHandle || info?.runtimeHandle || info?.groupHandle || null;
    if (!resolvedHandle) return null;

    const result = await workerExportGroupKey({
      groupHandle: resolvedHandle,
      label: label || HISTORY_DERIVED_KEY_LABEL,
      context: normalizedConversationId || resolvedGroupId,
      length: length || HISTORY_DERIVED_KEY_LENGTH,
    }).catch(() => null);

    const keyB64 = result?.key || null;
    if (keyB64) {
      historyDerivedKeyCache.set(storageKey, keyB64);
      await storeHistoryDerivedKey(storageKey, keyB64);
      return keyB64;
    }

    return null;
  }

  async updateGroupEpoch(groupId, epoch) {
    if (!groupId) return null;
    const nextEpoch = ensureEpoch(epoch);
    return this.#updateGroupEntry(groupId, (prev) => {
      const current = ensureEpoch(prev.epoch);
      const currentRuntimeEpoch = ensureEpoch(
        prev?.runtimeEpoch ?? prev?.runtime_epoch ?? prev?.runtime_epoch_no ?? null,
      );
      const nextRuntimeEpoch =
        nextEpoch > currentRuntimeEpoch ? nextEpoch : currentRuntimeEpoch || null;
      const nextSnapshotEpoch = nextEpoch > current ? nextEpoch : current || null;
      const snapshotUnchanged = nextSnapshotEpoch === current;
      const runtimeUnchanged = nextRuntimeEpoch === currentRuntimeEpoch;
      if (snapshotUnchanged && runtimeUnchanged) {
        return prev;
      }
      return {
        ...prev,
        epoch: nextSnapshotEpoch,
        current_epoch: nextSnapshotEpoch,
        currentEpoch: nextSnapshotEpoch,
        runtimeEpoch: nextRuntimeEpoch,
        runtime_epoch: nextRuntimeEpoch,
      };
    });
  }

  async recordGroupMember(groupId, clientId) {
    if (!groupId || !clientId) return null;
    return this.#updateGroupEntry(groupId, (prev) => {
      const members = normalizeMemberList(prev.members);
      if (members.includes(clientId)) return prev;
      return { ...prev, members: [...members, clientId] };
    });
  }

  async ensureServerGroup({ conversationId, groupId, allowCreate = true } = {}) {
    const normalizedConversationId =
      conversationId == null
        ? null
        : typeof conversationId === 'string'
          ? conversationId
          : String(conversationId);
    const fallbackGroupId = groupId == null ? null : String(groupId);

    const knownConversation =
      normalizedConversationId != null ? getKnownMLSConversation(normalizedConversationId) : null;
    const conversationProtocol =
      knownConversation?.e2ee_protocol ?? knownConversation?.e2eeProtocol ?? null;
    const conversationEnabled =
      knownConversation?.e2ee_enabled ?? knownConversation?.e2eeEnabled ?? null;
    const persistedConversationGroupId =
      knownConversation?.mls_group_id ??
      knownConversation?.mlsGroupId ??
      knownConversation?.meta?.mls_group_id ??
      null;

    if (!normalizedConversationId && !fallbackGroupId) {
      return fallbackGroupId;
    }

    const conversationTaskKey =
      normalizedConversationId != null ? `server:conversation:${normalizedConversationId}` : null;
    const fallbackTaskKey = fallbackGroupId != null ? `server:group:${fallbackGroupId}` : null;

    for (const key of [conversationTaskKey, fallbackTaskKey]) {
      if (key && this.serverEnsureTasks.has(key)) {
        return this.serverEnsureTasks.get(key);
      }
    }

    await this.ensureInitialized();
    const identity = await this.ensureIdentity({ name: this.identityName });
    const activeIdentityId = identity?.identityId || null;

    const trackedKeys = new Set();
    const attachTaskKey = (key) => {
      if (!key || !task) return;
      if (this.serverEnsureTasks.has(key)) {
        return;
      }
      this.serverEnsureTasks.set(key, task);
      trackedKeys.add(key);
    };

    let task;
    let snapshot = null;
    let snapshotMissingOnServer = false;
    let shouldForceSnapshotFetch = false;
    if (normalizedConversationId && knownConversation) {
      shouldForceSnapshotFetch = trackConversationSnapshotFingerprint(
        normalizedConversationId,
        knownConversation,
      );
      if (shouldForceSnapshotFetch) {
        resetConversationMlsGroupCache(normalizedConversationId);
      }
    }
    if (normalizedConversationId) {
      try {
        snapshot = await getGroupSnapshot({
          conversationId: normalizedConversationId,
          force: shouldForceSnapshotFetch,
        });
      } catch (error) {
        if (error?.status === 404) {
          snapshotMissingOnServer = true;
        } else {
          console.warn('[MLS] Failed to fetch conversation MLS group', error);
        }
      }
    }

    if (
      snapshotMissingOnServer &&
      normalizedConversationId &&
      (persistedConversationGroupId || this.conversationGroups.has(normalizedConversationId))
    ) {
      await this.#resetConversationLocalGroupState(normalizedConversationId, {
        groupId: persistedConversationGroupId ?? fallbackGroupId ?? null,
        reason: 'missing_group_snapshot',
      }).catch(() => {});
    }

    task = (async () => {
      let currentSnapshot = snapshot;

      if (allowCreate && normalizedConversationId && currentSnapshot) {
        const snapshotGroupId = ensureString(
          currentSnapshot?.group_id ?? currentSnapshot?.groupId ?? null,
        );
        const persistedGroupId = ensureString(persistedConversationGroupId);
        if (snapshotGroupId && persistedGroupId && snapshotGroupId !== persistedGroupId) {
          try {
            await this.resetGroup(persistedGroupId);
          } catch {}
          this.conversationGroups.delete(normalizedConversationId);
          resetConversationMlsGroupCache(normalizedConversationId);
        }

        const snapshotSuite = normalizeSuiteId(
          currentSnapshot?.ciphersuite ??
            currentSnapshot?.ciphersuite_id ??
            currentSnapshot?.ciphersuiteId ??
            null,
        );
        const supportedSuites = normalizeSuiteList(this.runtimeSupportedCiphersuites);
        const wantsPQ = Boolean(this.wantPQ);
        const pqMismatch =
          wantsPQ && snapshotSuite != null && snapshotSuite !== HYBRID_MLS_CIPHERSUITE;
        const suiteUnsupported =
          snapshotSuite != null &&
          supportedSuites.length > 0 &&
          !supportedSuites.includes(snapshotSuite);

        if (pqMismatch || suiteUnsupported) {
          try {
            console.warn('[MLS] server snapshot ciphersuite mismatch; attempting staging reset', {
              conversationId: normalizedConversationId,
              groupId: currentSnapshot?.group_id ?? currentSnapshot?.groupId ?? null,
              snapshotSuite,
              supportedSuites,
              wantsPQ,
            });
          } catch {}

          try {
            await resetConversationMlsGroup({ conversationId: normalizedConversationId });
            currentSnapshot = await getGroupSnapshot({
              conversationId: normalizedConversationId,
              force: true,
            });
          } catch (resetError) {
            console.warn(
              '[MLS] Failed to reset MLS conversation group after ciphersuite mismatch',
              {
                conversationId: normalizedConversationId,
                error: resetError,
              },
            );
          }
        }
      }

      const shouldBootstrapLegacy =
        allowCreate &&
        !currentSnapshot &&
        normalizedConversationId &&
        conversationEnabled === true &&
        String(conversationProtocol || '').toLowerCase() === 'mls' &&
        !persistedConversationGroupId;

      if (shouldBootstrapLegacy) {
        try {
          const bootstrap = await this.#bootstrapLegacyConversation({
            conversationId: normalizedConversationId,
            conversation: knownConversation,
            identityId: activeIdentityId,
          });

          if (bootstrap?.snapshot && !currentSnapshot) {
            currentSnapshot = bootstrap.snapshot;
          }

          if (!currentSnapshot) {
            try {
              currentSnapshot = await getGroupSnapshot({
                conversationId: normalizedConversationId,
                force: true,
              });
            } catch (refetchError) {
              if (refetchError?.status !== 404) {
                console.warn('[MLS] Failed to refetch MLS group after legacy bootstrap', {
                  conversationId: normalizedConversationId,
                  error: refetchError,
                });
              }
            }
          }
        } catch (bootstrapError) {
          console.warn('[MLS] Failed to bootstrap legacy server conversation group', {
            conversationId: normalizedConversationId,
            error: bootstrapError,
          });
        }
      }

      if (!currentSnapshot && allowCreate && normalizedConversationId) {
        if (this.serverManagedWelcomes) {
          try {
            currentSnapshot = await getGroupSnapshot({
              conversationId: normalizedConversationId,
              force: true,
              reissue: true,
              clientId: activeIdentityId || undefined,
            });
          } catch (error) {
            if (error?.status !== 404) {
              console.warn('[MLS] Failed to refetch server-managed MLS snapshot', {
                conversationId: normalizedConversationId,
                error,
              });
            }
          }
        }

        if (!currentSnapshot && this.serverManagedWelcomes) {
          const retryGroupId = fallbackGroupId ?? persistedConversationGroupId ?? null;
          const retryOptions = {
            conversationId: normalizedConversationId,
            groupId: retryGroupId,
            allowCreate,
          };

          const retryKeys = [];
          if (conversationTaskKey) {
            retryKeys.push(conversationTaskKey);
          }
          if (retryGroupId) {
            retryKeys.push(`server:group:${retryGroupId}`);
          } else if (fallbackTaskKey) {
            retryKeys.push(fallbackTaskKey);
          }

          if (retryKeys.length === 0) {
            this.#scheduleServerGroupRetry(retryOptions);
          } else {
            retryKeys.forEach((key) => {
              this.#scheduleServerGroupRetry({ ...retryOptions, key });
            });
          }
        }
      }

      if (
        !currentSnapshot &&
        allowCreate &&
        normalizedConversationId &&
        !this.serverManagedWelcomes
      ) {
        // 1) Create a local MLS group in the worker to obtain the ratchet tree (if we have an identity)
        let createdLocal = null;
        if (activeIdentityId) {
          try {
            createdLocal = await createGroup({
              identityId: activeIdentityId,
              groupId: fallbackGroupId,
              ciphersuite: this.preferredCiphersuite,
            });
          } catch (error) {
            console.warn(
              '[MLS] Failed to create local worker group before server bootstrap',
              error,
            );
          }
        }

        const snapshotMembersBase = ensureArray([activeIdentityId].filter(Boolean));

        if (createdLocal?.groupId || createdLocal?.groupHandle) {
          try {
            await this.#persistServerGroupSnapshot(
              {
                group_id:
                  createdLocal.groupId ?? fallbackGroupId ?? createdLocal.groupHandle ?? null,
                group_handle:
                  createdLocal.groupHandle ?? fallbackGroupId ?? createdLocal.groupId ?? null,
                epoch: ensureEpoch(createdLocal.epoch),
                last_server_seq: 0,
                members: snapshotMembersBase,
                identity_id: activeIdentityId,
                ciphersuite: createdLocal.ciphersuite ?? this.preferredCiphersuite ?? null,
                ratchet_tree_b64:
                  createdLocal.ratchetTree ??
                  createdLocal.ratchet_tree_b64 ??
                  createdLocal.ratchetTreeB64 ??
                  null,
              },
              {
                fallbackGroupId:
                  fallbackGroupId ?? createdLocal.groupId ?? createdLocal.groupHandle ?? null,
                fallbackGroupHandle:
                  createdLocal.groupHandle ?? fallbackGroupId ?? createdLocal.groupId ?? null,
                clearRecovery: true,
              },
            );
          } catch (error) {
            console.warn(
              '[MLS] Failed to persist local worker group before server bootstrap',
              error,
            );
          }
        }

        const resolvedRatchetTree =
          createdLocal?.ratchetTree ??
          createdLocal?.ratchet_tree_b64 ??
          createdLocal?.ratchetTreeB64 ??
          null;

        if (!resolvedRatchetTree) {
          const retryGroupId =
            fallbackGroupId ?? createdLocal?.groupId ?? createdLocal?.groupHandle ?? null;
          const abortError = new Error('MLS bootstrap aborted: missing ratchet tree');
          abortError.code = 'abort_bootstrap_missing_ratchet_tree';
          abortError.conversationId = normalizedConversationId;
          abortError.groupId = retryGroupId;

          const retryOptions = {
            conversationId: normalizedConversationId,
            groupId: retryGroupId,
            allowCreate,
          };

          const retryKeys = [];
          if (conversationTaskKey) {
            retryKeys.push(conversationTaskKey);
          }
          if (retryGroupId) {
            retryKeys.push(`server:group:${retryGroupId}`);
          } else if (fallbackTaskKey) {
            retryKeys.push(fallbackTaskKey);
          }

          if (retryKeys.length === 0) {
            this.#scheduleServerGroupRetry(retryOptions);
          } else {
            retryKeys.forEach((key) => {
              this.#scheduleServerGroupRetry({ ...retryOptions, key });
            });
          }

          throw abortError;
        }

        // 2) Bootstrap the server group WITH the ratchet tree
        try {
          await createMlsGroup({
            conversationId: normalizedConversationId,
            preferredGroupId: createdLocal?.groupId || fallbackGroupId || null,
            ratchetTreeB64: resolvedRatchetTree,
            members: snapshotMembersBase,
          });
        } catch (error) {
          if (error?.status !== 409) {
            console.warn('[MLS] Failed to create MLS group for conversation', error);
          }
        }

        // 3) Mint a welcome for the peer once the server metadata exists
        let mintedWelcome = null;
        let shouldMintWelcome = true;
        if (this.serverManagedWelcomes) {
          const snapshotMembers = Array.isArray(currentSnapshot?.members)
            ? currentSnapshot.members
            : [];
          const snapshotPending = Array.isArray(currentSnapshot?.pending_welcomes)
            ? currentSnapshot.pending_welcomes
            : Array.isArray(currentSnapshot?.pendingWelcomes)
              ? currentSnapshot.pendingWelcomes
              : [];
          if (snapshotMembers.length > 1 && snapshotPending.length === 0) {
            shouldMintWelcome = false;
          }
        }

        if (shouldMintWelcome && activeIdentityId && createdLocal?.groupHandle) {
          try {
            mintedWelcome = await this.#mintPeerWelcome({
              conversationId: normalizedConversationId,
              groupHandle: createdLocal.groupHandle,
              identityId: activeIdentityId,
              createdLocal,
              ciphersuite: createdLocal.ciphersuite ?? this.preferredCiphersuite,
            });
          } catch (error) {
            console.warn('[MLS] Failed to mint MLS welcome for peer', {
              conversationId: normalizedConversationId,
              error,
            });
          }

          if (mintedWelcome?.ratchetTreeB64) {
            createdLocal.ratchetTree = mintedWelcome.ratchetTreeB64;
            createdLocal.ratchetTreeB64 = mintedWelcome.ratchetTreeB64;
          }
        }

        if (Array.isArray(mintedWelcome?.welcomes) && mintedWelcome.welcomes.length) {
          const uploadGroupId = createdLocal?.groupId || fallbackGroupId || null;
          try {
            await this.uploadWelcome({
              groupId: uploadGroupId,
              epoch: mintedWelcome.epoch ?? null,
              welcomes: mintedWelcome.welcomes,
              ratchetTreeB64: mintedWelcome.ratchetTreeB64 ?? resolvedRatchetTree,
              ciphersuite:
                mintedWelcome.ciphersuite ?? createdLocal?.ciphersuite ?? this.preferredCiphersuite,
            });
          } catch (error) {
            console.warn('[MLS] Failed to upload minted welcome', error);
          }
        }

        // 3) Refetch snapshot after creation
        if (!currentSnapshot) {
          try {
            currentSnapshot = await getGroupSnapshot({
              conversationId: normalizedConversationId,
              force: true,
            });
          } catch (error) {
            if (error?.status !== 404) {
              console.warn('[MLS] Failed to refetch MLS group after creation', error);
            }
          }
        }
      }

      let hydrated = null;
      const snapshotGroupId =
        currentSnapshot?.group_id ?? currentSnapshot?.groupId ?? fallbackGroupId;
      const snapshotEpoch = normalizeEpochOrNull(
        currentSnapshot?.epoch ??
          currentSnapshot?.current_epoch ??
          currentSnapshot?.currentEpoch ??
          null,
      );

      if (snapshotGroupId) {
        attachTaskKey(`server:group:${snapshotGroupId}`);
      }

      if (currentSnapshot) {
        try {
          hydrated = await this.#hydrateGroupFromSnapshot(currentSnapshot, {
            fallbackGroupId: snapshotGroupId,
            fallbackGroupHandle:
              currentSnapshot?.group_handle ?? currentSnapshot?.groupHandle ?? null,
            identityId: activeIdentityId,
          });
        } catch (error) {
          console.warn('[MLS] Failed to hydrate group from snapshot', error);
        }
      }

      if (!hydrated && fallbackGroupId && currentSnapshot) {
        try {
          const groups = await this.#loadGroups();
          const existing =
            groups[fallbackGroupId] ||
            Object.values(groups).find((entry) => entry?.groupHandle === fallbackGroupId) ||
            null;
          if (existing) {
            hydrated = await this.#hydrateGroupFromSnapshot(
              {
                group_id: existing.groupId,
                group_handle: existing.groupHandle,
                epoch: existing.epoch,
                members: existing.members,
                ratchet_tree_b64: existing.ratchetTree,
                identity_id: existing.identityId,
              },
              {
                fallbackGroupId: existing.groupId || fallbackGroupId,
                fallbackGroupHandle: existing.groupHandle || null,
                identityId: activeIdentityId,
                clearRecovery: !existing.needsRecovery,
              },
            );
          }
        } catch (error) {
          console.warn('[MLS] Failed to hydrate group from local snapshot', error);
        }
      }

      const resolvedGroupId = hydrated?.groupId ?? snapshotGroupId ?? fallbackGroupId;

      if (resolvedGroupId) {
        attachTaskKey(`server:group:${resolvedGroupId}`);
      }

      if (normalizedConversationId && resolvedGroupId) {
        this.conversationGroups.set(normalizedConversationId, resolvedGroupId);
        persistKnownConversationGroupId(normalizedConversationId, resolvedGroupId);
      }

      const waitingForRatchetTree =
        hydrated?.needsRecovery && hydrated?.recoveryReason === WAITING_RATCHET_TREE_REASON;

      const runPendingConvergeOnce = async (targetGroupId) => {
        const normalizedTargetGroupId = ensureString(targetGroupId);
        if (!normalizedTargetGroupId) return;
        let resolvedSince = 0;
        try {
          const groups = (await this.#loadGroups().catch(() => null)) || {};
          const existingEntry =
            groups[normalizedTargetGroupId] ||
            Object.values(groups).find(
              (entry) =>
                entry?.groupId === normalizedTargetGroupId ||
                entry?.groupHandle === normalizedTargetGroupId,
            ) ||
            null;
          const cursor = parseCursor(
            existingEntry?.lastServerSeq ?? existingEntry?.last_server_seq ?? null,
          );
          resolvedSince = cursor == null ? 0 : cursor;
        } catch {}
        await this.pullAndApplyPendingWelcomeOrCommit({
          groupId: normalizedTargetGroupId,
          since: resolvedSince,
        });
      };

      let didRunPendingConverge = false;

      if (resolvedGroupId && hydrated && (!hydrated.needsRecovery || waitingForRatchetTree)) {
        try {
          await runPendingConvergeOnce(resolvedGroupId);
          didRunPendingConverge = true;
          if (!hydrated.needsRecovery) {
            this.#resetServerGroupRetry([
              conversationTaskKey,
              fallbackTaskKey,
              `server:group:${resolvedGroupId}`,
            ]);
          }
        } catch (error) {
          console.warn('[MLS] Failed to sync pending MLS commits', {
            groupId: resolvedGroupId,
            error,
          });
          try {
            await this.markGroupForRecovery(resolvedGroupId, 'pending_sync_failed');
          } catch {}
          throw error;
        }
      }

      // If runtime hydration is still missing/blocked, do one more pending sync attempt.
      // This prevents missing creator welcomes when snapshot was fetched before runtime settled.
      if (!didRunPendingConverge && resolvedGroupId && (!hydrated || hydrated.needsRecovery)) {
        try {
          await runPendingConvergeOnce(resolvedGroupId);
          didRunPendingConverge = true;
        } catch (error) {
          console.warn('[MLS] fallback pending sync after hydration state failed', {
            conversationId: normalizedConversationId,
            groupId: resolvedGroupId,
            error,
          });
        }
      }

      if (resolvedGroupId && hydrated?.needsRecovery) {
        const reason = hydrated.recoveryReason || 'needs_recovery';
        if (reason === WAITING_RATCHET_TREE_REASON) {
          this.groupRecovery.set(resolvedGroupId, true);
        } else {
          try {
            await this.markGroupForRecovery(resolvedGroupId, reason);
          } catch {}
        }
      }

      return resolvedGroupId;
    })();

    if (conversationTaskKey) {
      attachTaskKey(conversationTaskKey);
    }
    if (fallbackTaskKey) {
      attachTaskKey(fallbackTaskKey);
    }

    try {
      return await task;
    } finally {
      trackedKeys.forEach((key) => {
        if (this.serverEnsureTasks.get(key) === task) {
          this.serverEnsureTasks.delete(key);
        }
      });
    }
  }

  async markGroupForRecovery(groupId, reason) {
    if (!groupId) return null;
    return this.#updateGroupEntry(groupId, (prev) => ({
      ...prev,
      ready: false,
      needsRecovery: true,
      recoveryReason: reason || null,
    }));
  }

  async recoverGroup({ groupId, groupHandle, reason, __pipelineBypass = false } = {}) {
    if (!groupId && !groupHandle) {
      return null;
    }

    const pipelineGroupId = ensureString(groupId ?? null);
    const pipelineGroupHandle = ensureString(groupHandle ?? null);
    if (!__pipelineBypass && (pipelineGroupId || pipelineGroupHandle)) {
      return this.#runGroupPipeline(
        {
          groupId: pipelineGroupId,
          groupHandle: pipelineGroupHandle,
          stage: 'recoverGroup',
        },
        () =>
          this.recoverGroup({
            groupId: pipelineGroupId ?? groupId ?? null,
            groupHandle: pipelineGroupHandle ?? groupHandle ?? null,
            reason,
            __pipelineBypass: true,
          }),
      );
    }

    const key = String(groupId || groupHandle);
    if (this.recoveryTasks.has(key)) {
      return this.recoveryTasks.get(key);
    }

    const task = (async () => {
      const normalizedReason = reason || 'needs_recovery';
      await this.ensureInitialized();
      await this.#warmRuntimeGroups().catch(() => {});
      let groups;
      try {
        groups = await this.#loadGroups();
      } catch (error) {
        console.warn('[MLS] Failed to load groups during recovery', error);
        return null;
      }

      const { key: storageKey, entry: initialEntry } = this.#resolveStoredGroup(groups, {
        groupId,
        groupHandle,
      });
      let entry = initialEntry;

      if (!entry) {
        try {
          if (groupId) {
            await this.markGroupForRecovery(groupId, normalizedReason);
          }
        } catch {}
        return null;
      }

      let resolvedGroupId = entry.groupId || storageKey || groupId || null;
      let resolvedHandle = entry.groupHandle || groupHandle || null;

      const initialInfo = await this.groupInfo({
        groupId: resolvedGroupId,
        groupHandle: resolvedHandle,
      }).catch(() => null);
      if (
        initialInfo?.ready ||
        (initialInfo?.runtimeState === 'runtime_behind' && !initialInfo?.needsRecovery)
      ) {
        return initialInfo;
      }

      if (storageKey) {
        try {
          await this.markGroupForRecovery(storageKey, normalizedReason);
        } catch {}
      }

      const applyRecoveredEntry = async (nextEntry) => {
        if (!nextEntry) return null;
        if (storageKey) {
          const merged = await this.#updateGroupEntry(storageKey, () => ({
            ...nextEntry,
            needsRecovery: false,
            recoveryReason: null,
          }));
          return merged;
        }

        const latest = await this.#loadGroups();
        const keyToUse = nextEntry.groupId || groupId;
        latest[keyToUse] = {
          ...(latest[keyToUse] || {}),
          ...nextEntry,
          needsRecovery: false,
          recoveryReason: null,
        };
        await this.#saveGroups(latest);
        return latest[keyToUse];
      };

      const rehydrateBlockedUntil = coerceTimestampMs(entry?.rehydrateBlockedUntil ?? null);
      const persistedRecoveryWelcome = pickBase64String(
        entry?.recoveryWelcomeB64,
        entry?.recovery_welcome_b64,
        entry?.welcomeB64,
        entry?.welcome_b64,
        entry?.welcome,
      );
      if (
        persistedRecoveryWelcome &&
        this.#canAttemptPersistedWelcomeRecovery(entry, {
          source: 'recover_group',
        })
      ) {
        try {
          const restored = await this.#rehydrateGroupFromWelcome(entry, {
            allowForceRejoinRecovery: true,
            source: 'recover_group',
          });
          if (restored?.groupHandle) {
            await applyRecoveredEntry(restored);
            return this.groupInfo({ groupId: resolvedGroupId, groupHandle: resolvedHandle });
          }
          if (storageKey) {
            await this.#setRehydrateBlockedUntil(
              storageKey,
              REHYDRATE_MISSING_KEY_BACKOFF_MS,
              'recover_group_failed',
            );
          }
        } catch (error) {
          console.warn('[MLS] Failed to rehydrate group during recovery', {
            groupId: resolvedGroupId,
            groupHandle: resolvedHandle,
            error,
          });
          if (storageKey) {
            await this.#setRehydrateBlockedUntil(
              storageKey,
              REHYDRATE_MISSING_KEY_BACKOFF_MS,
              'recover_group_error',
            );
          }
        }
      }

      const recoverConversation =
        resolvedGroupId != null ? findKnownConversationByGroupId(resolvedGroupId) : null;
      const recoverConversationId =
        ensureString(entry?.conversationId ?? null) ??
        (recoverConversation ? normalizeConversationId(recoverConversation) : null);

      let joinedEntry = null;
      try {
        const syncResult = await this.syncWelcomes({
          conversationId: recoverConversationId,
          groupId: resolvedGroupId ?? resolvedHandle ?? null,
          __pipelineBypass: true,
        });
        if (syncResult?.joined?.length) {
          joinedEntry =
            syncResult.joined.find((item) => {
              if (!item) return false;
              if (resolvedGroupId && item.groupId === resolvedGroupId) return true;
              if (resolvedHandle && item.groupHandle === resolvedHandle) return true;
              return false;
            }) || null;
          if (joinedEntry) {
            await applyRecoveredEntry(joinedEntry);
          }
        }
      } catch (error) {
        console.warn('[MLS] Failed to sync welcomes during recovery', {
          groupId: resolvedGroupId,
          groupHandle: resolvedHandle,
          error,
        });
      }

      // Re-read latest persisted entry after pending/welcome sync to avoid forcing
      // join_bundle when server pending path is available for this identity.
      try {
        const groups = await this.#loadGroups();
        const refreshed =
          (storageKey && groups[storageKey]) ||
          (resolvedGroupId && groups[resolvedGroupId]) ||
          Object.values(groups).find(
            (item) => item?.groupId === resolvedGroupId || item?.groupHandle === resolvedHandle,
          ) ||
          null;
        if (refreshed) {
          entry = refreshed;
          resolvedGroupId = ensureString(refreshed.groupId ?? resolvedGroupId ?? null);
          resolvedHandle = ensureString(refreshed.groupHandle ?? resolvedHandle ?? null);
        }
      } catch {}

      const membershipBootstrap = await this.#ensureActiveMembershipBootstrap({
        entry,
        conversationId: recoverConversationId,
        groupId: resolvedGroupId ?? storageKey ?? groupId ?? null,
        groupHandle: resolvedHandle ?? groupHandle ?? null,
        identityId: ensureString(this.identityClientId ?? this.identityId ?? null),
        ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
        source: 'recoverGroup',
        reason: normalizedReason || 'recover_group_membership_bootstrap',
      }).catch(() => null);
      if (membershipBootstrap?.repaired) {
        const refreshedInfo = await this.groupInfo({
          groupId: membershipBootstrap.repaired.groupId ?? resolvedGroupId,
          groupHandle: membershipBootstrap.repaired.groupHandle ?? resolvedHandle,
        }).catch(() => null);
        const repairedInfo =
          (refreshedInfo && this.#isGroupReady(refreshedInfo)
            ? refreshedInfo
            : membershipBootstrap.repaired) || refreshedInfo;
        if (
          repairedInfo &&
          (repairedInfo.ready === true ||
            this.#isGroupReady(repairedInfo) ||
            (repairedInfo.runtimeState === 'runtime_behind' && !repairedInfo.needsRecovery))
        ) {
          return repairedInfo;
        }
      }

      const runtimeBootstrap = await this.#ensureCurrentIdentityRuntimeBootstrap({
        entry,
        conversationId: recoverConversationId,
        groupId: resolvedGroupId ?? storageKey ?? groupId ?? null,
        groupHandle: resolvedHandle ?? groupHandle ?? null,
        identityId: ensureString(this.identityClientId ?? this.identityId ?? null),
        ciphersuite: entry?.ciphersuite ?? this.preferredCiphersuite,
        source: 'recoverGroup',
        reason: normalizedReason || 'recover_group_runtime_bootstrap',
      }).catch(() => null);
      if (runtimeBootstrap?.repaired) {
        const refreshedInfo = await this.groupInfo({
          groupId: runtimeBootstrap.repaired.groupId ?? resolvedGroupId,
          groupHandle: runtimeBootstrap.repaired.groupHandle ?? resolvedHandle,
        }).catch(() => null);
        const repairedInfo =
          (refreshedInfo && this.#isGroupReady(refreshedInfo)
            ? refreshedInfo
            : runtimeBootstrap.repaired) || refreshedInfo;
        if (
          repairedInfo &&
          (repairedInfo.ready === true ||
            this.#isGroupReady(repairedInfo) ||
            (repairedInfo.runtimeState === 'runtime_behind' && !repairedInfo.needsRecovery))
        ) {
          return repairedInfo;
        }
      }

      const pendingWelcomeHintForRecoveryJoin = this.#hasUnresolvedBootstrapWelcome(entry, {
        groupId: ensureString(resolvedGroupId ?? storageKey ?? groupId ?? null),
        groupHandle: ensureString(resolvedHandle ?? null),
        activeIdentityId: ensureString(this.identityClientId ?? this.identityId ?? null),
      });

      if (pendingWelcomeHintForRecoveryJoin && typeof console?.info === 'function') {
        console.info('[MLS] Recovery skipping join_bundle; pending welcome hint present', {
          groupId: resolvedGroupId ?? null,
          groupHandle: resolvedHandle ?? null,
        });
      } else if (typeof console?.info === 'function') {
        console.info('[MLS] Recovery leaving join_bundle decision to orchestrator', {
          groupId: resolvedGroupId ?? null,
          groupHandle: resolvedHandle ?? null,
          conversationId: recoverConversationId ?? null,
          reason: normalizedReason,
        });
      }

      const info = await this.groupInfo({
        groupId: resolvedGroupId,
        groupHandle: resolvedHandle,
      }).catch(() => null);
      if (info && !info.needsRecovery) {
        return info;
      }

      return info;
    })()
      .catch((error) => {
        console.warn('[MLS] recoverGroup failed', { groupId, groupHandle, error });
        return null;
      })
      .finally(() => {
        this.recoveryTasks.delete(key);
      });

    this.recoveryTasks.set(key, task);
    return task;
  }

  async pullAndApplyPendingWelcomeOrCommit({
    groupId,
    since,
    __pipelineBypass = false,
    __skipEnsureWait = false,
  } = {}) {
    const pipelineGroupId = ensureString(groupId);
    if (!__pipelineBypass && pipelineGroupId) {
      return this.#runGroupPipeline(
        {
          groupId: pipelineGroupId,
          stage: 'pullAndApplyPendingWelcomeOrCommit',
        },
        () =>
          this.pullAndApplyPendingWelcomeOrCommit({
            groupId: pipelineGroupId,
            since,
            __pipelineBypass: true,
            __skipEnsureWait,
          }),
      );
    }

    const execute = async ({ groups: prefetchedGroups, existing: prefetchedExisting } = {}) => {
      const knownConversation = groupId != null ? findKnownConversationByGroupId(groupId) : null;
      const knownConversationId = knownConversation
        ? normalizeConversationId(knownConversation)
        : null;
      // When pull/apply is explicitly bypassing the group pipeline (or ensure wait),
      // force syncWelcomes to bypass too; otherwise pending fetch can get stuck behind
      // an in-flight stage and never issue /pending on startup races.
      const bypassSyncPipeline = __pipelineBypass || __skipEnsureWait;
      const result = await this.syncWelcomes({
        since,
        conversationId: knownConversationId,
        groupId,
        __pipelineBypass: bypassSyncPipeline,
      });
      if (!groupId) {
        return result;
      }

      let groups = prefetchedGroups || null;
      if (!groups) {
        groups = await this.#loadGroups();
      }

      let existing = prefetchedExisting || null;
      if (!existing && groupId) {
        existing =
          Object.values(groups).find(
            (entry) => entry?.groupId === groupId || entry?.groupHandle === groupId,
          ) || null;
      }
      const joinedDuringWelcomeSync = Array.isArray(result?.joined)
        ? result.joined.some((item) => {
            if (!item) return false;
            if (groupId && item.groupId === groupId) return true;
            if (existing?.groupId && item.groupId === existing.groupId) return true;
            if (existing?.groupHandle && item.groupHandle === existing.groupHandle) return true;
            return false;
          })
        : false;

      if (!__skipEnsureWait) {
        await this.#awaitPendingEnsureForGroup({
          groupId: existing?.groupId ?? groupId,
          groupHandle: existing?.groupHandle ?? null,
        });
      }

      const persistedCursor = parseCursor(
        since ?? existing?.lastServerSeq ?? existing?.last_server_seq ?? null,
      );
      const runtimeSyncedInfo = await awaitWithTimeout(
        () =>
          this.#syncRuntimeGroupState({
            groupId: existing?.groupId ?? groupId,
            groupHandle: existing?.groupHandle ?? null,
            conversationId: knownConversationId,
            source: 'pullAndApplyPendingWelcomeOrCommit',
            reason: 'pending_message_apply',
            allowSyncWelcomes: false,
            allowJoinBundleRepair: false,
            allowRuntimeRepair: false,
          }),
        RUNTIME_SYNC_STAGE_FAST_TIMEOUT_MS,
        null,
      ).catch((error) => {
        console.warn('[MLS] runtime sync failed before pending apply', {
          groupId: existing?.groupId ?? groupId ?? null,
          error,
        });
        return null;
      });
      if (runtimeSyncedInfo?.groupId || runtimeSyncedInfo?.groupHandle) {
        existing = {
          ...(existing || {}),
          ...runtimeSyncedInfo,
          groupId: runtimeSyncedInfo.groupId ?? existing?.groupId ?? groupId,
          groupHandle: runtimeSyncedInfo.groupHandle ?? existing?.groupHandle ?? null,
        };
      }
      const targetGroupId = ensureString(existing?.groupId ?? groupId ?? null) ?? groupId;
      const targetGroupHandle = ensureString(existing?.groupHandle ?? null);

      try {
        const info = await this.groupInfo({
          groupId: targetGroupId,
          groupHandle: targetGroupHandle ?? null,
        }).catch(() => null);
        let resolvedInfo = info;
        let pendingApplyAuthority = this.#resolvePendingApplyRuntimeAuthority(resolvedInfo, {
          groupId: targetGroupId,
          groupHandle: targetGroupHandle ?? null,
          joinedDuringWelcomeSync,
        });
        let runtimeBehindSnapshot = pendingApplyAuthority.runtimeBehindSnapshot;
        if (!this.#isGroupReady(resolvedInfo) && !runtimeBehindSnapshot) {
          const deferredEscalation = pendingApplyAuthority.shouldDeferEscalation;
          if (deferredEscalation) {
            if (typeof console?.info === 'function') {
              console.info(
                '[MLS] Pending apply observed unresolved runtime bootstrap; deferring recovery escalation',
                {
                  groupId: targetGroupId,
                  runtimeState: pendingApplyAuthority.runtimeState,
                  joinedDuringWelcomeSync,
                  pendingWelcomeCount: pendingApplyAuthority.effectivePendingWelcomeCount,
                  currentIdentityMember:
                    pendingApplyAuthority.membershipState?.currentIdentityMember ?? false,
                  pendingSetup: pendingApplyAuthority.membershipState?.pendingSetup ?? false,
                  welcomeRequired: pendingApplyAuthority.membershipState?.welcomeRequired ?? false,
                },
              );
            }
          } else {
            await this.markGroupForRecovery(targetGroupId, 'missing_runtime_group').catch(
              () => null,
            );
            return result;
          }

        }
        // Server snapshots can move to epoch N+1 before local runtime applies the commit frame.
        // In that state, fetching pending with `since=lastServerSeq` can skip the exact boundary
        // commit we still need to apply. Pull one step back only while runtime is behind.
        const effectiveCursor = (() => {
          if (!(persistedCursor != null && persistedCursor > 0)) {
            return 0;
          }
          if (!runtimeBehindSnapshot) {
            return persistedCursor;
          }
          // Special-case: when cursor == 1, we MUST backtrack to 0.
          // Otherwise an exclusive `since=1` can skip the boundary commit at seq=1.
          if (persistedCursor === 1) {
            return 0;
          }
          return Math.max(1, persistedCursor - 1);
        })();

        // Preflight consumes handshake-only pending frames (commit/proposal) to keep runtime epoch
        // aligned, but intentionally avoids application payload decrypt here.
        let pendingCursor = effectiveCursor;
        const seenPendingCursors = new Set();
        for (let pageIndex = 0; pageIndex < 5; pageIndex += 1) {
          const cursorKey = String(pendingCursor ?? 0);
          if (seenPendingCursors.has(cursorKey)) {
            break;
          }
          seenPendingCursors.add(cursorKey);

          const pendingResponse = await this.fetchGroupMessages({
            groupId: targetGroupId,
            since: pendingCursor,
          });
          const pendingItems = Array.isArray(pendingResponse?.items) ? pendingResponse.items : [];
          const handshakeApply = await this.#applyPendingHandshakeFrames({
            groupId: targetGroupId,
            groupHandle: targetGroupHandle ?? null,
            items: pendingItems,
          });
          if ((handshakeApply?.applied ?? 0) > 0) {
            const postApplyInfo = await awaitWithTimeout(
              () =>
                this.#syncRuntimeGroupState({
                  groupId: targetGroupId,
                  groupHandle: targetGroupHandle ?? null,
                  conversationId: knownConversationId,
                  source: 'pullAndApplyPendingWelcomeOrCommit',
                  reason: 'pending_handshake_apply',
                  allowSyncWelcomes: false,
                  allowJoinBundleRepair: false,
                  allowRuntimeRepair: false,
                }),
              RUNTIME_SYNC_STAGE_FAST_TIMEOUT_MS,
              null,
            ).catch((error) => {
              console.warn('[MLS] runtime sync failed after pending handshake apply', {
                groupId: targetGroupId,
                error,
              });
              return null;
            });
            if (postApplyInfo?.groupId || postApplyInfo?.groupHandle) {
              existing = {
                ...(existing || {}),
                ...postApplyInfo,
                groupId: postApplyInfo.groupId ?? existing?.groupId ?? targetGroupId,
                groupHandle:
                  postApplyInfo.groupHandle ?? existing?.groupHandle ?? targetGroupHandle,
              };
            }
          }

          const nextCursor = parseCursor(
            pendingResponse?.nextCursor ?? pendingResponse?.next_cursor ?? null,
          );
          const advancedPastSkippedItems = this.#canAdvancePendingCursorPastSkippedItems(
            pendingItems,
            nextCursor,
          );
          if (advancedPastSkippedItems) {
            await this.noteServerSequence({
              groupId: targetGroupId,
              groupHandle: targetGroupHandle ?? null,
              serverSeq: nextCursor,
            }).catch(() => null);
          }
          const latestPendingInfo =
            (await this.groupInfo({
              groupId: targetGroupId,
              groupHandle: targetGroupHandle ?? null,
            }).catch(() => null)) || null;
          const latestInfoEpoch = normalizeEpochOrNull(latestPendingInfo?.epoch ?? null);
          const latestRuntimeEpoch = normalizeEpochOrNull(
            latestPendingInfo?.runtimeEpoch ??
              latestPendingInfo?.runtime_epoch ??
              latestPendingInfo?.runtime_epoch_no ??
              null,
          );
          const latestRuntimeBehindSnapshot =
            latestInfoEpoch != null &&
            latestRuntimeEpoch != null &&
            latestRuntimeEpoch < latestInfoEpoch;
          const shouldContinuePendingCatchUp =
            nextCursor != null &&
            nextCursor > (pendingCursor ?? 0) &&
            (advancedPastSkippedItems ||
              joinedDuringWelcomeSync ||
              latestRuntimeBehindSnapshot ||
              (handshakeApply?.applied ?? 0) > 0 ||
              (handshakeApply?.staleDropped ?? 0) > 0);

          if (!shouldContinuePendingCatchUp) {
            break;
          }

          if (typeof console?.info === 'function') {
            console.info('[MLS] Continuing paginated pending catch-up', {
              groupId: targetGroupId,
              pageIndex: pageIndex + 1,
              since: pendingCursor,
              nextCursor,
              advancedPastSkippedItems,
              joinedDuringWelcomeSync,
              runtimeBehindSnapshot: latestRuntimeBehindSnapshot,
              handshakeApplied: handshakeApply?.applied ?? 0,
              handshakeStaleDropped: handshakeApply?.staleDropped ?? 0,
            });
          }
          pendingCursor = nextCursor;
        }
      } catch (error) {
        console.warn('[MLS] Failed to fetch pending MLS messages', {
          groupId: targetGroupId,
          error,
        });
      }

      let finalInfo =
        (await this.groupInfo({
          groupId: targetGroupId,
          groupHandle: targetGroupHandle ?? null,
        }).catch(() => null)) || null;
      if (finalInfo?.runtimeState === 'runtime_behind' && !finalInfo?.needsRecovery) {
        const finalAuthority = this.#resolvePendingApplyRuntimeAuthority(finalInfo, {
          groupId: targetGroupId,
          groupHandle: targetGroupHandle ?? null,
          joinedDuringWelcomeSync,
        });
        if (!finalAuthority.shouldDeferEscalation) {
          await this.markGroupForRecovery(targetGroupId, 'runtime_snapshot_mismatch').catch(
            () => null,
          );
          finalInfo = (await this.groupInfo({
            groupId: targetGroupId,
            groupHandle: targetGroupHandle ?? null,
          }).catch(() => null)) || {
            ...finalInfo,
            needsRecovery: true,
            recoveryReason: 'runtime_snapshot_mismatch',
          };
        }
      }
      return finalInfo ? { ...(result || {}), finalInfo } : result;
    };

    if (!groupId) {
      return execute();
    }

    if (!__skipEnsureWait) {
      await this.#awaitPendingEnsureForGroup({ groupId });
    }

    let prefetchedGroups = null;
    let prefetchedExisting = null;
    if (groupId != null) {
      try {
        prefetchedGroups = await this.#loadGroups();
        prefetchedExisting =
          prefetchedGroups[groupId] ||
          Object.values(prefetchedGroups).find(
            (entry) => entry?.groupId === groupId || entry?.groupHandle === groupId,
          ) ||
          null;
      } catch {
        prefetchedGroups = null;
        prefetchedExisting = null;
      }
    }

    const runWithPrefetch = async () => {
      if (!__skipEnsureWait && (prefetchedExisting?.groupId || prefetchedExisting?.groupHandle)) {
        await this.#awaitPendingEnsureForGroup({
          groupId: prefetchedExisting?.groupId ?? prefetchedExisting?.groupHandle,
          groupHandle: prefetchedExisting?.groupHandle ?? null,
        });
      }
      return execute({ groups: prefetchedGroups, existing: prefetchedExisting });
    };
    const singleFlightKey =
      ensureString(prefetchedExisting?.groupId ?? prefetchedExisting?.groupHandle ?? groupId) ||
      '__global__';
    const existingFlight = this._pendingApplyFlights?.get(singleFlightKey) || null;
    const existingFlightStartedAt = Number(
      this._pendingApplyFlightStartedAt?.get(singleFlightKey) || 0,
    );
    const existingFlightAgeMs =
      Number.isFinite(existingFlightStartedAt) && existingFlightStartedAt > 0
        ? Date.now() - existingFlightStartedAt
        : null;
    const existingFlightTimedOut =
      Number.isFinite(existingFlightAgeMs) &&
      Number.isFinite(PENDING_APPLY_FLIGHT_TIMEOUT_MS) &&
      PENDING_APPLY_FLIGHT_TIMEOUT_MS > 0 &&
      existingFlightAgeMs > PENDING_APPLY_FLIGHT_TIMEOUT_MS;
    if (existingFlight && typeof existingFlight.then === 'function') {
      if (!existingFlightTimedOut) {
        return existingFlight;
      }
      if (typeof console?.warn === 'function') {
        console.warn('[MLS] pending apply single-flight stale; replacing in-flight task', {
          groupId: singleFlightKey,
          ageMs: existingFlightAgeMs,
          timeoutMs: PENDING_APPLY_FLIGHT_TIMEOUT_MS,
        });
      }
      if (this._pendingApplyFlights?.get(singleFlightKey) === existingFlight) {
        this._pendingApplyFlights.delete(singleFlightKey);
      }
      if (this._pendingApplyFlightStartedAt?.get(singleFlightKey) != null) {
        this._pendingApplyFlightStartedAt.delete(singleFlightKey);
      }
    }
    const task = Promise.resolve()
      .then(() => runWithPrefetch())
      .finally(() => {
        if (this._pendingApplyFlights?.get(singleFlightKey) === task) {
          this._pendingApplyFlights.delete(singleFlightKey);
        }
        if (this._pendingApplyFlightStartedAt?.get(singleFlightKey) != null) {
          this._pendingApplyFlightStartedAt.delete(singleFlightKey);
        }
      });
    this._pendingApplyFlights.set(singleFlightKey, task);
    this._pendingApplyFlightStartedAt.set(singleFlightKey, Date.now());
    return task;
  }

  async #applyPendingHandshakeFrames({ groupId, groupHandle = null, items = [] } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);
    if (!normalizedGroupId || !Array.isArray(items) || items.length === 0) {
      return { applied: 0, staleDropped: 0, errors: 0 };
    }

    const pendingHandshakes = items
      .filter((entry) => isPendingHandshakeMessage(entry))
      .filter((entry) => Boolean(getPendingMessageCiphertextB64(entry)));
    if (!pendingHandshakes.length) {
      return { applied: 0, staleDropped: 0, errors: 0 };
    }

    pendingHandshakes.sort((a, b) => {
      const aSeq = parseCursor(a?.server_seq ?? null);
      const bSeq = parseCursor(b?.server_seq ?? null);
      if (aSeq != null && bSeq != null && aSeq !== bSeq) {
        return aSeq - bSeq;
      }
      if (aSeq != null) return -1;
      if (bSeq != null) return 1;
      return 0;
    });

    let applied = 0;
    let staleDropped = 0;
    let errors = 0;
    let latestKnownRuntimeEpoch = normalizeEpochOrNull(
      (
        await this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
        }).catch(() => null)
      )?.runtimeEpoch ?? null,
    );

    for (const entry of pendingHandshakes) {
      const ciphertext = getPendingMessageCiphertextB64(entry);
      if (!ciphertext) continue;
      const serverSeq = parseCursor(entry?.server_seq ?? null);
      const serverMsgId = ensureString(entry?.server_msg_id ?? entry?.serverMsgId ?? null);
      const incomingEpoch = normalizeEpochOrNull(
        entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null,
      );

      try {
        // Handshake frames (commit/proposal) are exactly what moves runtime from N -> N+1.
        // Allow decrypt here even when snapshot says "not ready" to avoid a circular gate.
        await this.decrypt({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
          ciphertext,
          serverSeq,
          serverMsgId,
          epochHint: incomingEpoch,
          allowNotReady: true,
          traceId: `pending-handshake:${serverMsgId ?? serverSeq ?? Date.now()}`,
        });
        if (incomingEpoch != null) {
          await this.updateGroupEpoch(normalizedGroupId, incomingEpoch).catch(() => null);
          latestKnownRuntimeEpoch =
            latestKnownRuntimeEpoch == null
              ? incomingEpoch
              : Math.max(latestKnownRuntimeEpoch, incomingEpoch);
        }
        // Advance local pending cursor only after a successful handshake apply.
        // Without this, the same commit can replay from /pending?since=0 and create loops.
        await this.noteServerSequence({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
          serverSeq,
          serverMsgId,
        }).catch(() => null);
        const refreshedGroup = await this.groupInfo({
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
        }).catch(() => null);
        if (refreshedGroup) {
          void this.#cacheHistoryKeyForEntry(refreshedGroup).catch(() => null);
        }
        applied += 1;
      } catch (error) {
        const message = String(error?.message || '').toLowerCase();
        const requiresRuntimeRecovery =
          error?.code === 'MLS_GROUP_NEEDS_RECOVERY' ||
          message.includes('group requires recovery') ||
          message.includes('unknown group') ||
          message.includes('missing runtime');
        const epochMismatch =
          message.includes("message epoch differs from the group's epoch") ||
          message.includes('epoch differs from the group');
        const generationTooOld =
          message.includes('generation is too old to be processed') ||
          message.includes('message generation too old');
        // Only drop a handshake as stale when a live runtime epoch proves we have
        // already applied that generation. Snapshot epoch alone is not sufficient:
        // snapshot can legitimately advance ahead of local runtime while the exact
        // boundary commit is still waiting in /pending.
        const staleByEpoch =
          incomingEpoch != null &&
          latestKnownRuntimeEpoch != null &&
          incomingEpoch <= latestKnownRuntimeEpoch;

        if ((epochMismatch || generationTooOld) && staleByEpoch) {
          await this.noteServerSequence({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle ?? null,
            serverSeq,
            serverMsgId,
          }).catch(() => null);
          staleDropped += 1;
          continue;
        }

        if (requiresRuntimeRecovery) {
          const runtimeInfo = await this.groupInfo({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle ?? null,
          }).catch(() => null);
          const recovered = await this.recoverGroup({
            groupId: normalizedGroupId,
            groupHandle:
              ensureString(runtimeInfo?.groupHandle ?? null) ?? normalizedGroupHandle ?? null,
            reason:
              ensureString(error?.recoveryReason ?? runtimeInfo?.recoveryReason ?? null) ??
              'pending_handshake_runtime_recovery',
            __pipelineBypass: true,
          }).catch(() => null);

          if (recovered && !recovered?.needsRecovery) {
            try {
              await this.decrypt({
                groupId: normalizedGroupId,
                groupHandle:
                  ensureString(recovered?.groupHandle ?? null) ??
                  ensureString(runtimeInfo?.groupHandle ?? null) ??
                  normalizedGroupHandle ??
                  null,
                ciphertext,
                serverSeq,
                serverMsgId,
                epochHint: incomingEpoch,
                allowNotReady: true,
                allowRehydrate: false,
                traceId: `pending-handshake-recovery:${serverMsgId ?? serverSeq ?? Date.now()}`,
              });
              if (incomingEpoch != null) {
                await this.updateGroupEpoch(normalizedGroupId, incomingEpoch).catch(() => null);
                latestKnownRuntimeEpoch =
                  latestKnownRuntimeEpoch == null
                    ? incomingEpoch
                    : Math.max(latestKnownRuntimeEpoch, incomingEpoch);
              }
              await this.noteServerSequence({
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupHandle ?? null,
                serverSeq,
                serverMsgId,
              }).catch(() => null);
              applied += 1;
              continue;
            } catch {}
          }
        }

        if (epochMismatch) {
          const runtimeInfo = await this.groupInfo({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle ?? null,
          }).catch(() => null);
          const repaired = await this.#repairRuntimeAfterStaleHandle({
            groupInfo: runtimeInfo,
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupHandle ?? null,
            runtimeHandle: ensureString(
              runtimeInfo?.runtimeHandle ?? runtimeInfo?.groupHandle ?? null,
            ),
            conversationId: ensureString(runtimeInfo?.conversationId ?? null),
            source: 'pullAndApplyPendingWelcomeOrCommit',
            reason: 'pending_handshake_epoch_mismatch',
            traceId: serverMsgId ?? (serverSeq != null ? `seq:${serverSeq}` : null),
            allowSyncWelcomes: true,
            allowJoinBundleRepair: true,
          }).catch(() => null);

          if (repaired && !repaired?.needsRecovery) {
            try {
              await this.decrypt({
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupHandle ?? null,
                ciphertext,
                serverSeq,
                serverMsgId,
                epochHint: incomingEpoch,
                allowNotReady: true,
                allowRehydrate: false,
                traceId: `pending-handshake-retry:${serverMsgId ?? serverSeq ?? Date.now()}`,
              });
              if (incomingEpoch != null) {
                await this.updateGroupEpoch(normalizedGroupId, incomingEpoch).catch(() => null);
                latestKnownRuntimeEpoch =
                  latestKnownRuntimeEpoch == null
                    ? incomingEpoch
                    : Math.max(latestKnownRuntimeEpoch, incomingEpoch);
              }
              await this.noteServerSequence({
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupHandle ?? null,
                serverSeq,
                serverMsgId,
              }).catch(() => null);
              applied += 1;
              continue;
            } catch {}
          }
        }

        errors += 1;
        console.warn('[MLS] Failed to apply pending handshake frame', {
          groupId: normalizedGroupId,
          groupHandle: normalizedGroupHandle ?? null,
          serverSeq,
          serverMsgId,
          incomingEpoch: incomingEpoch ?? null,
          error,
        });
      }
    }

    return { applied, staleDropped, errors };
  }

  async ensureInitialized() {
    if (!this.initPromise) {
      this.initPromise = (async () => {
        const initOptions = {
          options: {
            allowExperimental: true,
          },
          // Hint persistent storage backend to OpenMLS runtime when supported.
          storage: {
            backend: 'idb',
          },
        };
        if (this.preferredCiphersuite != null) {
          initOptions.preferredCiphersuite = this.preferredCiphersuite;
        }

        let initResult;
        try {
          initResult = await initMls(initOptions);
        } catch (error) {
          this.initPromise = null;
          throw error;
        }

        let supportedSuites = [];
        let suiteLabels = [];
        const providerLabel =
          initResult && typeof initResult === 'object' && typeof initResult.provider === 'string'
            ? initResult.provider
            : null;

        if (initResult && typeof initResult === 'object') {
          supportedSuites = Array.isArray(initResult.suites) ? initResult.suites : [];
          suiteLabels = Array.isArray(initResult.suiteLabels) ? initResult.suiteLabels : [];
          this.#updateRuntimeSuiteMetadata({
            suites: supportedSuites,
            suiteLabels,
            provider: providerLabel,
          });
        } else {
          try {
            supportedSuites = await getSupportedCiphersuites();
          } catch {
            supportedSuites = [];
          }
          this.#updateRuntimeSuiteMetadata({ suites: supportedSuites, provider: providerLabel });
        }

        const normalizedSupported = Array.isArray(supportedSuites)
          ? supportedSuites.map((suite) => normalizeSuiteId(suite)).filter((suite) => suite != null)
          : [];

        if (this.wantPQ && !normalizedSupported.includes(HYBRID_MLS_CIPHERSUITE)) {
          const error = new Error(
            'Post-quantum ciphersuite 0x004d is not supported by the current OpenMLS build. ' +
              'Run scripts/fetch_openmls_wasm.sh or check the CI fetch step.',
          );
          error.code = 'mls_missing_pq_suite';
          error.details = {
            supportedSuites: normalizedSupported,
            provider: this.runtimeProvider || providerLabel || DEFAULT_MLS_PROVIDER,
          };
          try {
            console.error('[MLS] Post-quantum support required but unavailable', error.details);
          } catch {}
          this.initPromise = null;
          throw error;
        }

        await this.#coercePersistedMemberLists();

        const runtimeProvider = this.runtimeProvider || providerLabel || DEFAULT_MLS_PROVIDER;
        const activeSuite =
          normalizeSuiteId(initResult?.ciphersuite) ??
          (this.preferredCiphersuite != null
            ? normalizeSuiteId(this.preferredCiphersuite)
            : this.wantPQ
              ? HYBRID_MLS_CIPHERSUITE
              : (normalizedSupported[0] ?? null));
        const activeSuiteLabel = this.#resolveSuiteLabel(activeSuite) || suiteLabels[0] || null;

        const telemetryPayload = {
          provider: runtimeProvider,
          ciphersuite: activeSuite ?? null,
          suiteLabel: activeSuiteLabel || null,
          suites: normalizedSupported,
        };

        pushMlsTelemetry('mls.runtime_ready', telemetryPayload);

        if (typeof console?.info === 'function') {
          try {
            debugLog('[MLS] OpenMLS runtime ready', telemetryPayload);
          } catch {}
        }

        try {
          await this.#warmRuntimeGroups();
        } catch (error) {
          console.warn('[MLS] Failed to warm runtime groups after init', error);
        }

        this.initWantPQ = this.wantPQ;
        return initResult;
      })();
    }
    return this.initPromise;
  }

  setWantPQ(wantPQ) {
    const normalized = Boolean(wantPQ);
    if (normalized === this.wantPQ) {
      return;
    }

    this.wantPQ = normalized;

    if (normalized && !this.preferredCiphersuiteExplicit && this.preferredCiphersuite == null) {
      this.preferredCiphersuite = HYBRID_MLS_CIPHERSUITE;
    }

    if (normalized && this.initWantPQ !== true) {
      this.initPromise = null;
      this.initWantPQ = null;
      this.ensureInitialized().catch(() => {});
    }
  }

  async ensureIdentity({ name, ciphersuite } = {}) {
    await this.ensureInitialized();
    await this.#ensureClientIdCacheLoaded();

    if (!this.identityPromise) {
      this.identityPromise = this.#ensureIdentity({ name, ciphersuite })
        .catch((error) => {
          this.identityPromise = null;
          throw error;
        })
        .then((identity) => {
          this.identityPromise = null;
          if (identity) {
            this.#setIdentityState(identity);
          }
          return identity;
        });
    }

    return this.identityPromise;
  }

  getActiveMlsClientId(options = {}) {
    if (!this._clientIdsLoaded && !this._clientIdsLoadPromise) {
      this.#ensureClientIdCacheLoaded().catch(() => {});
    }

    let preferredSuite = null;
    if (typeof options === 'number' || typeof options === 'string') {
      preferredSuite = normalizeSuiteId(options);
    } else if (options && typeof options === 'object') {
      preferredSuite = normalizeSuiteId(options.ciphersuite ?? options.suite ?? null);
    }

    const identitySuite = normalizeSuiteId(this.identityRecord?.ciphersuite ?? null);
    const fallbackSuite = normalizeSuiteId(this.preferredCiphersuite);
    const targetSuite = preferredSuite ?? identitySuite ?? fallbackSuite ?? null;

    const normalizedIdentityId = this.#normalizeIdentityId(
      this.identityRecord?.identityId ?? this.identityId ?? null,
    );
    const ensureMinted = (candidate) => {
      const normalized = this.#normalizeIdentityId(candidate);
      if (!normalized) {
        return null;
      }
      return normalized;
    };

    if (targetSuite != null) {
      const suiteClientId = ensureMinted(this.#getClientIdForSuite(targetSuite));
      if (suiteClientId) {
        return suiteClientId;
      }
      if (this.identityRecord?.clientIds && this.identityRecord.clientIds[String(targetSuite)]) {
        const direct = ensureMinted(this.identityRecord.clientIds[String(targetSuite)]);
        if (direct) {
          return direct;
        }
      }
    }

    const normalizedActive = ensureMinted(this.identityClientId ?? null);
    if (normalizedActive) {
      return normalizedActive;
    }

    if (this.identityRecord?.clientId != null) {
      const direct = ensureMinted(this.identityRecord.clientId);
      if (direct) {
        return direct;
      }
    }

    if (this.identityRecord?.clientIds && typeof this.identityRecord.clientIds === 'object') {
      for (const value of Object.values(this.identityRecord.clientIds)) {
        const minted = ensureMinted(value);
        if (minted) {
          return minted;
        }
      }
    }

    if (this.clientIdsBySuite.size) {
      for (const clientId of this.clientIdsBySuite.values()) {
        const minted = ensureMinted(clientId);
        if (minted) {
          return minted;
        }
      }
    }

    if (normalizedIdentityId && isCanonicalMlsClientId(normalizedIdentityId)) {
      return normalizedIdentityId;
    }

    return null;
  }

  async #ensureServerDeviceIdentity({ force = false } = {}) {
    if (force) {
      this.serverDeviceIdentity = null;
    }

    if (this.serverDeviceIdentity) {
      return this.serverDeviceIdentity;
    }

    if (!this.serverDeviceIdentityPromise) {
      this.serverDeviceIdentityPromise = Promise.resolve()
        .then(() => getMlsDeviceIdentity())
        .then((payload) => {
          if (payload && typeof payload === 'object') {
            this.serverDeviceIdentity = payload;
            const metadata = pickDeviceIdentityMetadata(payload);
            const persisted = {
              device_id: metadata.deviceId ?? null,
              deviceId: metadata.deviceId ?? null,
              client_id: metadata.clientId ?? null,
              clientId: metadata.clientId ?? null,
              credential_public_key_b64: metadata.credentialPublicKeyB64 ?? null,
              credentialPublicKeyB64: metadata.credentialPublicKeyB64 ?? null,
              signature_private_key_ref: metadata.signaturePrivateKeyRef ?? null,
              signaturePrivateKeyRef: metadata.signaturePrivateKeyRef ?? null,
              updated_at: new Date().toISOString(),
            };
            setItem(this.deviceIdentityKey, persisted).catch(() => {});
          }
          return this.serverDeviceIdentity;
        })
        .catch((error) => {
          console.warn('[MLS] Failed to fetch server device identity', error);
          return null;
        })
        .finally(() => {
          this.serverDeviceIdentityPromise = null;
        });
    }

    return this.serverDeviceIdentityPromise;
  }

  async #ensureIdentity({ name, ciphersuite } = {}) {
    const loginNonce = getMlsLoginNonce(this.userId);
    const normalizedLoginNonce = normalizeLoginNonce(loginNonce);
    const readCachedKeyPackageB64 = (clientId, suite) => {
      try {
        const ls = getLocalStorageSafe();
        if (!ls || !clientId || suite == null) return null;
        const key = `mls:keypkg:${clientId}:${suite}`;
        const raw = ls.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.key_package_b64 === 'string' ? parsed.key_package_b64 : null;
      } catch {
        return null;
      }
    };
    const stampLoginNonce = (record) => {
      if (!normalizedLoginNonce || !record || typeof record !== 'object') {
        return record;
      }
      const existing = normalizeLoginNonce(record.loginNonce ?? record.login_nonce ?? null);
      if (existing === normalizedLoginNonce) {
        return record;
      }
      return {
        ...record,
        loginNonce: normalizedLoginNonce,
        login_nonce: normalizedLoginNonce,
      };
    };

    const serverIdentity = await this.#ensureServerDeviceIdentity();
    const serverClientId = this.#normalizeIdentityId(
      serverIdentity?.client_id ?? serverIdentity?.clientId ?? null,
    );
    const serverSeed = pickBase64String(
      serverIdentity?.seed_b64 ?? serverIdentity?.seedB64 ?? serverIdentity?.seed ?? null,
    );
    const restoreFlagActive = await isBackupRestoreFlagSet(this.userId);
    const ignoreServerIdentityOnce = this._ignoreServerDeviceIdentityOnce === true;
    if (ignoreServerIdentityOnce) {
      this._ignoreServerDeviceIdentityOnce = false;
    }
    const effectiveServerClientId =
      restoreFlagActive || ignoreServerIdentityOnce ? null : serverClientId;
    const effectiveServerSeed = restoreFlagActive || ignoreServerIdentityOnce ? null : serverSeed;
    const serverDeviceMetadata = pickDeviceIdentityMetadata(serverIdentity);
    const stampDeviceIdentityMetadata = (record) => {
      if (!record || typeof record !== 'object') {
        return record;
      }
      const normalizedDeviceId = this.#normalizeIdentityId(
        serverDeviceMetadata.deviceId ?? record.deviceId ?? record.device_id ?? record.identityId,
      );
      const normalizedClientId = this.#normalizeIdentityId(
        serverDeviceMetadata.clientId ?? record.clientId ?? record.client_id ?? normalizedDeviceId,
      );
      const credentialPublicKeyB64 = pickBase64String(
        serverDeviceMetadata.credentialPublicKeyB64,
        record.credentialPublicKeyB64,
        record.credential_public_key_b64,
        record.credentialPublicKey,
        record.credential_public_key,
        null,
      );
      const signingSeedB64 = pickBase64String(
        record.signaturePrivateKeySeedB64,
        record.signature_private_key_seed_b64,
        record.signing_private_key_b64,
        record.signingPrivateKeyB64,
        record.seed,
        null,
      );
      const signingRef = ensureString(
        record.signaturePrivateKeyRef ??
          record.signature_private_key_ref ??
          serverDeviceMetadata.signaturePrivateKeyRef ??
          (normalizedDeviceId ? `identity-signing:${normalizedDeviceId}` : null),
      );
      return {
        ...record,
        deviceId: normalizedDeviceId ?? null,
        device_id: normalizedDeviceId ?? null,
        clientId: normalizedClientId ?? null,
        client_id: normalizedClientId ?? null,
        credentialPublicKeyB64: credentialPublicKeyB64 ?? null,
        credential_public_key_b64: credentialPublicKeyB64 ?? null,
        signaturePrivateKeySeedB64: signingSeedB64 ?? null,
        signature_private_key_seed_b64: signingSeedB64 ?? null,
        signaturePrivateKeyRef: signingRef ?? null,
        signature_private_key_ref: signingRef ?? null,
      };
    };
    const stampIdentityForStorage = (record) =>
      stampDeviceIdentityMetadata(stampLoginNonce(record));
    const persistDeviceIdentityRecord = async (record) => {
      if (!record || typeof record !== 'object') return;
      const normalized = stampDeviceIdentityMetadata(record);
      const payload = {
        device_id: normalized.device_id ?? null,
        deviceId: normalized.deviceId ?? null,
        client_id: normalized.client_id ?? null,
        clientId: normalized.clientId ?? null,
        identity_id: normalized.identityId ?? normalized.identity_id ?? null,
        identityId: normalized.identityId ?? normalized.identity_id ?? null,
        signature_private_key_seed_b64:
          normalized.signature_private_key_seed_b64 ?? normalized.seed ?? null,
        signaturePrivateKeySeedB64:
          normalized.signaturePrivateKeySeedB64 ?? normalized.seed ?? null,
        signature_private_key_ref:
          normalized.signature_private_key_ref ?? normalized.signaturePrivateKeyRef ?? null,
        signaturePrivateKeyRef:
          normalized.signaturePrivateKeyRef ?? normalized.signature_private_key_ref ?? null,
        credential_public_key_b64:
          normalized.credential_public_key_b64 ?? normalized.credentialPublicKeyB64 ?? null,
        credentialPublicKeyB64:
          normalized.credentialPublicKeyB64 ?? normalized.credential_public_key_b64 ?? null,
        updated_at: new Date().toISOString(),
      };
      try {
        await setItem(this.deviceIdentityKey, payload);
      } catch (error) {
        debugLog('[MLS] Failed to persist device identity metadata', { error });
      }
    };
    const trackIdentityKeyPackage = async (
      record,
      { keyPackageB64 = null, status = 'unused' } = {},
    ) => {
      if (!record || typeof record !== 'object') {
        return;
      }
      const resolvedKeyPackageB64 = pickBase64String(
        keyPackageB64,
        record.keyPackage,
        record.key_package_b64,
        record.keyPackageB64,
        null,
      );
      if (!resolvedKeyPackageB64) {
        return;
      }
      const suite = normalizeSuiteId(
        record.ciphersuite ?? ciphersuite ?? this.preferredCiphersuite,
      );
      const kpRef = await computeKeyPackageRefHexFromB64(resolvedKeyPackageB64, suite).catch(
        () => null,
      );
      if (!kpRef) {
        return;
      }
      await this.#trackKeyPackageByRef({
        kpRef,
        identityId: record.identityId ?? record.identity_id ?? null,
        clientId: record.clientId ?? record.client_id ?? record.identityId ?? null,
        ciphersuite: suite,
        keyPackageB64: resolvedKeyPackageB64,
        status,
      });
    };
    const resolveIdentityMutationLock = async (identityRecord = null) => {
      const normalizedIdentityId = this.#normalizeIdentityId(
        identityRecord?.identityId ?? identityRecord?.identity_id ?? null,
      );
      if (!normalizedIdentityId) {
        return null;
      }
      const suiteHint = normalizeSuiteId(
        identityRecord?.ciphersuite ?? ciphersuite ?? this.preferredCiphersuite,
      );
      const createBootstrapAuthority =
        this._conversationCreateBootstrapByIdentity instanceof Map
          ? this._conversationCreateBootstrapByIdentity.get(normalizedIdentityId) || null
          : null;
      const createBootstrapTargetRef = normalizeHexRefValue(
        createBootstrapAuthority?.targetRef ?? null,
      );
      if (createBootstrapTargetRef) {
        return {
          identityId: normalizedIdentityId,
          ciphersuite: suiteHint,
          targetRef: createBootstrapTargetRef,
          source: createBootstrapAuthority?.source ?? 'conversation_create_bootstrap',
          groupId: null,
          groupHandle: null,
          conversationId: ensureString(createBootstrapAuthority?.conversationId ?? null) ?? null,
        };
      }
      const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
        normalizedIdentityId,
      ).catch(() => null);
      const protectedDrift = await this.#resolveProtectedWelcomeTargetDrift({
        identityId: normalizedIdentityId,
        ciphersuite: suiteHint,
      }).catch(() => null);
      if (!protectedDrift && !unresolvedWelcomeTarget) {
        return null;
      }
      const targetRef = normalizeHexRefValue(
        protectedDrift?.targetRef ??
          unresolvedWelcomeTarget?.targetRef ??
          (await this.#resolveAuthoritativePublishedKeyPackageRef({
            identityId: normalizedIdentityId,
            ciphersuite: suiteHint,
          }).catch(() => null)) ??
          null,
      );
      if (!targetRef) {
        return null;
      }
      return {
        identityId: normalizedIdentityId,
        ciphersuite: suiteHint,
        targetRef,
        source:
          protectedDrift?.source ?? unresolvedWelcomeTarget?.source ?? 'authoritative_published',
        groupId:
          ensureString(protectedDrift?.groupId ?? unresolvedWelcomeTarget?.groupId ?? null) ?? null,
        groupHandle:
          ensureString(
            protectedDrift?.groupHandle ?? unresolvedWelcomeTarget?.groupHandle ?? null,
          ) ?? null,
        conversationId:
          ensureString(
            protectedDrift?.conversationId ?? unresolvedWelcomeTarget?.conversationId ?? null,
          ) ?? null,
      };
    };
    const previewSeedRestoredKeyPackage = async ({
      identityId = null,
      resolvedName = null,
      seedValue = null,
      suiteHint = null,
    } = {}) => {
      const normalizedSeed = pickBase64String(seedValue, null);
      if (!resolvedName || !normalizedSeed) {
        return null;
      }
      const normalizedSuite = normalizeSuiteId(
        suiteHint ?? ciphersuite ?? this.preferredCiphersuite ?? null,
      );
      try {
        const preview = await previewIdentityKeyPackage({
          name: resolvedName,
          ciphersuite: normalizedSuite ?? null,
          seed: normalizedSeed,
          identityId: this.#normalizeIdentityId(identityId ?? null) ?? null,
        });
        const keyPackageB64 = pickBase64String(
          preview?.keyPackage,
          preview?.keyPackageB64,
          preview?.key_package_b64,
          null,
        );
        if (!keyPackageB64) {
          return null;
        }
        const previewSuite = normalizeSuiteId(preview?.ciphersuite ?? normalizedSuite ?? null);
        const keyPackageRef = normalizeHexRefValue(
          await computeKeyPackageRefHexFromB64(keyPackageB64, previewSuite).catch(() => null),
        );
        return {
          keyPackageB64,
          keyPackageRef,
          ciphersuite: previewSuite ?? null,
        };
      } catch (error) {
        try {
          console.warn('[MLS] failed to preview seed-restored key package', {
            identityId: this.#normalizeIdentityId(identityId ?? null) ?? null,
            ciphersuite: normalizedSuite ?? null,
            error: error?.message ?? String(error),
          });
        } catch {}
        return null;
      }
    };
    const retireSeedRestoreAuthority = async ({
      identityId = null,
      clientId = null,
      ciphersuite: suiteHint = null,
      expectedRef = null,
      restoredRef = null,
      reason = 'seed_restore_ref_drift',
    } = {}) => {
      const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? null);
      if (!normalizedIdentityId) {
        return;
      }
      const normalizedClientId =
        this.#normalizeIdentityId(clientId ?? null) ?? normalizedIdentityId;
      const normalizedSuite = normalizeSuiteId(
        suiteHint ?? ciphersuite ?? this.preferredCiphersuite,
      );
      try {
        console.info('[MLS] seed restore key package drift retired stale welcome ownership', {
          identityId: normalizedIdentityId,
          clientId: normalizedClientId ?? null,
          expectedRef: normalizeHexRefValue(expectedRef ?? null),
          restoredRef: normalizeHexRefValue(restoredRef ?? null),
          ciphersuite: normalizedSuite ?? null,
          reason,
        });
      } catch {}

      this.#clearPublishedKeyPackageMirror({
        identityId: normalizedIdentityId,
        clientId: normalizedClientId,
        ciphersuite: normalizedSuite ?? null,
      });
      this.#clearKeyPackageCacheForIdentity(normalizedIdentityId);
      if (normalizedClientId && normalizedClientId !== normalizedIdentityId) {
        this.#clearKeyPackageCacheForIdentity(normalizedClientId);
      }
      if (normalizedSuite != null) {
        this.#clearClientIdCacheForSuite(normalizedSuite);
      }
      this._protectedWelcomeTargetDriftByIdentity?.delete?.(normalizedIdentityId);
      await this.#clearProtectedWelcomeTargetDrift({
        identityId: normalizedIdentityId,
      }).catch(() => null);

      const groups = await this.#loadGroups().catch(() => null);
      if (!groups || typeof groups !== 'object') {
        return;
      }

      for (const [key, entry] of Object.entries(groups)) {
        if (!entry || typeof entry !== 'object') {
          continue;
        }
        const entryIdentityId = this.#normalizeIdentityId(
          entry?.welcomeTargetIdentityId ??
            entry?.welcome_target_identity_id ??
            entry?.protectedWelcomeTargetDriftIdentityId ??
            entry?.identityId ??
            entry?.identity_id ??
            null,
        );
        if (!entryIdentityId || entryIdentityId !== normalizedIdentityId) {
          continue;
        }
        await this.#clearPersistedWelcomeState({
          conversationId:
            ensureString(entry?.conversationId ?? entry?.conversation_id ?? null) ?? null,
          groupId: ensureString(entry?.groupId ?? entry?.group_id ?? key) ?? String(key),
          groupHandle: ensureString(entry?.groupHandle ?? entry?.group_handle ?? null) ?? null,
          clearRecoveryPayload: true,
          preserveWelcomeTargetLock: false,
        }).catch(() => null);
        const entryGroupId = ensureString(entry?.groupId ?? entry?.group_id ?? key) ?? String(key);
        const entryGroupHandle =
          ensureString(entry?.groupHandle ?? entry?.group_handle ?? null) ?? null;
        await this.#clearRuntimeHandleState(entryGroupId, {
          groupHandle: entryGroupHandle,
          runtimeHandle: ensureString(entry?.runtimeHandle ?? entry?.runtime_handle ?? null),
          source: 'retireSeedRestoreAuthority',
          reason,
        }).catch(() => null);
        await this.#updateGroupEntry(entryGroupId, (current) => ({
          ...current,
          ready: false,
          joined: false,
          needsRecovery: true,
          recoveryReason: reason,
        })).catch(() => null);
      }
    };
    const reconcileSeedRestoreAuthority = async ({
      identityRecord = null,
      identityId = null,
      clientId = null,
      resolvedName = null,
      seedValue = null,
      suiteHint = null,
      reason = 'seed_restore_ref_drift',
    } = {}) => {
      const normalizedIdentityId = this.#normalizeIdentityId(
        identityId ?? identityRecord?.identityId ?? identityRecord?.identity_id ?? null,
      );
      if (!normalizedIdentityId || !resolvedName || !seedValue) {
        return null;
      }
      const preview = await previewSeedRestoredKeyPackage({
        identityId: normalizedIdentityId,
        resolvedName,
        seedValue,
        suiteHint,
      });
      const restoredPreviewRef = normalizeHexRefValue(preview?.keyPackageRef ?? null);
      if (!restoredPreviewRef) {
        return preview;
      }
      const normalizedSuite = normalizeSuiteId(
        preview?.ciphersuite ??
          suiteHint ??
          identityRecord?.ciphersuite ??
          ciphersuite ??
          this.preferredCiphersuite,
      );
      const authoritativeRef = normalizeHexRefValue(
        (await this.#resolveAuthoritativePublishedKeyPackageRef({
          identityId: normalizedIdentityId,
          ciphersuite: normalizedSuite,
        }).catch(() => null)) ??
          identityRecord?.publishedKeyPackageRef ??
          identityRecord?.published_key_package_ref ??
          null,
      );
      if (!authoritativeRef || authoritativeRef === restoredPreviewRef) {
        return preview;
      }
      await retireSeedRestoreAuthority({
        identityId: normalizedIdentityId,
        clientId:
          clientId ?? identityRecord?.clientId ?? identityRecord?.client_id ?? normalizedIdentityId,
        ciphersuite: normalizedSuite,
        expectedRef: authoritativeRef,
        restoredRef: restoredPreviewRef,
        reason,
      });
      return {
        ...preview,
        retiredAuthoritativeRef: authoritativeRef,
      };
    };

    let identity = await getItem(this.identityKey);
    if (identity && typeof identity === 'object') {
      const { record: normalizedIdentity, changed } = this.#normalizeIdentityRecord(identity);
      identity = normalizedIdentity;
      if (
        effectiveServerClientId &&
        identity?.identityId &&
        effectiveServerClientId !== identity.identityId
      ) {
        await this.#resetIdentityState();
        identity = null;
      } else {
        const stampedIdentity = stampIdentityForStorage(identity);
        identity = stampedIdentity;
        if (changed || stampedIdentity !== normalizedIdentity) {
          await setItem(this.identityKey, identity);
        }
      }
    }

    const persistStableIdentity = async (record, seedValue) => {
      if (!record?.identityId) return;
      await writeStableValue(stableKeys.identityId(this.userId), record.identityId);
      if (seedValue) {
        await writeStableValue(stableKeys.seed(this.userId), seedValue);
      }
      if (normalizedLoginNonce) {
        await writeStableValue(stableKeys.loginNonce(this.userId), normalizedLoginNonce);
      }
      scheduleMlsBackup(this.userId, 'identity');
      await clearBackupRestoreFlag(this.userId);
    };

    if (identity?.identityId) {
      try {
        const exported = await workerExportKeyPackage({ identityId: identity.identityId });
        const suiteHint = normalizeSuiteId(identity.ciphersuite ?? this.preferredCiphersuite);
        const exportedB64 = exported?.keyPackage ?? null;
        const cachedB64 =
          readCachedKeyPackageB64(identity.identityId, suiteHint) ||
          readCachedKeyPackageB64(identity.clientId, suiteHint);
        let protectedTargetMismatchPersisted = false;
        if (exportedB64 && cachedB64) {
          const [exportedRef, cachedRef] = await Promise.all([
            computeKeyPackageRefHexFromB64(exportedB64, suiteHint),
            computeKeyPackageRefHexFromB64(cachedB64, suiteHint),
          ]);
          if (exportedRef && cachedRef && exportedRef !== cachedRef) {
            const unresolvedWelcomeTarget = await this.#findUnresolvedWelcomeTargetForIdentity(
              identity.identityId,
            ).catch(() => null);
            const protectedTargetRef = normalizeHexRefValue(
              unresolvedWelcomeTarget?.targetRef ??
                (await this.#resolveAuthoritativePublishedKeyPackageRef({
                  identityId: identity.identityId,
                  ciphersuite: suiteHint,
                }).catch(() => null)) ??
                null,
            );
            if (protectedTargetRef && exportedRef !== protectedTargetRef) {
              console.info(
                '[MLS] worker key package mismatch under protected welcome target; rehydrating identity',
                {
                  identityId: identity.identityId,
                  suite: suiteHint,
                  exportedRef,
                  cachedRef,
                  protectedTargetRef,
                  source: unresolvedWelcomeTarget?.source ?? 'authoritative_published',
                },
              );
              console.warn(
                '[MLS] protected welcome target drift detected; preserving current worker key package for join recovery',
                {
                  identityId: identity.identityId,
                  suite: suiteHint,
                  exportedRef,
                  cachedRef,
                  protectedTargetRef,
                },
              );
              this._protectedWelcomeTargetDriftByIdentity.set(identity.identityId, {
                identityId: identity.identityId,
                groupId: ensureString(unresolvedWelcomeTarget?.groupId ?? null) ?? null,
                groupHandle: ensureString(unresolvedWelcomeTarget?.groupHandle ?? null) ?? null,
                conversationId:
                  ensureString(unresolvedWelcomeTarget?.conversationId ?? null) ?? null,
                targetRef: protectedTargetRef,
                currentLocalRef: normalizeHexRefValue(exportedRef ?? null),
                ciphersuite: suiteHint ?? null,
                source: unresolvedWelcomeTarget?.source ?? 'authoritative_published',
                detectedAt: Date.now(),
              });
              await this.#persistProtectedWelcomeTargetDrift({
                groupId: unresolvedWelcomeTarget?.groupId ?? null,
                groupHandle: unresolvedWelcomeTarget?.groupHandle ?? null,
                conversationId: unresolvedWelcomeTarget?.conversationId ?? null,
                identityId: identity.identityId,
                targetRef: protectedTargetRef,
                currentLocalRef: exportedRef ?? null,
                ciphersuite: suiteHint ?? null,
                source: unresolvedWelcomeTarget?.source ?? 'authoritative_published',
              }).catch(() => null);
              protectedTargetMismatchPersisted = true;
            }
            if (protectedTargetMismatchPersisted) {
              // Seed-based identity recreation does not guarantee the exact previously issued
              // key package ref. When a locked welcome target exists, keep the worker's current
              // local key package stable and let join recovery fetch a matching fresh welcome
              // for that exact ref instead of mutating the worker again.
              this.#clearPublishedKeyPackageMirror({
                identityId: identity.identityId ?? null,
                clientId: identity.clientId ?? identity.identityId ?? null,
                ciphersuite: suiteHint ?? null,
              });
              const markProtectedDriftCacheStale = (id) => {
                if (!id || suiteHint == null) return;
                this.#updateKeyPackageCache(id, suiteHint, {
                  status: 'stale',
                  ref: null,
                  expiresAt: 0,
                  ttlMs: 0,
                  lastPublishedAt: 0,
                  deferredUntil: null,
                  fetchedAt: Date.now(),
                });
              };
              markProtectedDriftCacheStale(identity.identityId);
              markProtectedDriftCacheStale(identity.clientId);
              const nextIdentity = stampIdentityForStorage({
                ...identity,
                keyPackage:
                  exportedB64 ??
                  identity?.keyPackage ??
                  identity?.key_package_b64 ??
                  identity?.keyPackageB64 ??
                  null,
                publishedKeyPackageRef: null,
                published_key_package_ref: null,
                publishedAt: null,
                published_at: null,
              });
              await setItem(this.identityKey, nextIdentity);
              this.#setIdentityState(nextIdentity);
              await persistStableIdentity(nextIdentity, nextIdentity.seed);
              await persistDeviceIdentityRecord(nextIdentity);
              await trackIdentityKeyPackage(nextIdentity, {
                keyPackageB64:
                  exportedB64 ??
                  nextIdentity?.keyPackage ??
                  nextIdentity?.key_package_b64 ??
                  nextIdentity?.keyPackageB64 ??
                  null,
                status: 'unused',
              });
              return nextIdentity;
            }
            // Worker is authoritative for active identity state; refresh stale browser cache.
            console.info('[MLS] worker key package mismatch; refreshing cached key package', {
              identityId: identity.identityId,
              suite: suiteHint,
              exportedRef,
              cachedRef,
            });

            // The local worker can recreate a fresh key package before it is published.
            // Clear the published mirror instead of overwriting it with unpublished bytes.
            this.#clearPublishedKeyPackageMirror({
              identityId: identity.identityId ?? null,
              clientId: identity.clientId ?? identity.identityId ?? null,
              ciphersuite: suiteHint ?? null,
            });

            // Force next publish to re-post worker-exported key package to backend.
            // Otherwise stale freshness metadata can skip publish and keep server refs drifted.
            const markCacheStale = (id) => {
              if (!id) return;
              this.#updateKeyPackageCache(id, suiteHint, {
                status: 'stale',
                ref: null,
                expiresAt: 0,
                ttlMs: 0,
                lastPublishedAt: 0,
                deferredUntil: null,
                fetchedAt: Date.now(),
              });
            };
            markCacheStale(identity.identityId);
            markCacheStale(identity.clientId);

            // Clear any persisted suite->client mapping for this suite so publish does not
            // keep consulting a stale identity.clientIds entry.
            if (suiteHint != null) {
              try {
                this.#clearClientIdCacheForSuite(suiteHint);
              } catch {}
              try {
                const suiteKey = this.#normalizeSuiteKey(suiteHint);
                if (suiteKey && identity?.clientIds && typeof identity.clientIds === 'object') {
                  if (Object.prototype.hasOwnProperty.call(identity.clientIds, suiteKey)) {
                    const nextClientIds = { ...identity.clientIds };
                    delete nextClientIds[suiteKey];
                    identity = {
                      ...identity,
                      clientIds: nextClientIds,
                    };
                    await setItem(this.identityKey, identity);
                  }
                }
              } catch {}
            }

            this.#setIdentityState(identity);
            await persistStableIdentity(identity, identity.seed);
            await persistDeviceIdentityRecord(identity);
            await trackIdentityKeyPackage(identity, {
              keyPackageB64: exportedB64,
              status: 'unused',
            });
            return identity;
          }
        }
        this.#setIdentityState(identity);
        await persistStableIdentity(identity, identity.seed);
        await persistDeviceIdentityRecord(identity);
        await trackIdentityKeyPackage(identity, {
          keyPackageB64: exportedB64,
          status: 'unused',
        });
        return identity;
      } catch (error) {
        const message = (error?.message || '').toLowerCase();
        if (
          !message.includes('unknown identity') &&
          !message.includes('no mls identity initialised') &&
          !message.includes('identity handle unavailable') &&
          !message.includes('key package handle unavailable') &&
          !message.includes('key package unavailable for export')
        ) {
          throw error;
        }

        const identityMutationLock = await resolveIdentityMutationLock(identity).catch(() => null);

        if (identity?.seed) {
          try {
            const resolvedName =
              ensureString(identity.identityName ?? identity.identity_name ?? null) ||
              this.#resolveIdentityName(name, identity.identityId);
            const seedRestoreAuthority = await reconcileSeedRestoreAuthority({
              identityRecord: identity,
              identityId: identity.identityId,
              clientId: identity.clientId ?? identity.identityId ?? null,
              resolvedName,
              seedValue: identity.seed,
              suiteHint: ciphersuite || identity.ciphersuite || this.preferredCiphersuite,
              reason: 'stored_identity_seed_restore_ref_drift',
            });
            if (identityMutationLock) {
              const lockedTargetRef = normalizeHexRefValue(identityMutationLock.targetRef ?? null);
              const previewRef = normalizeHexRefValue(
                seedRestoreAuthority?.keyPackageRef ?? null,
              );
              const retiredRef = normalizeHexRefValue(
                seedRestoreAuthority?.retiredAuthoritativeRef ?? null,
              );
              const lockResolved = Boolean(
                lockedTargetRef &&
                  previewRef &&
                  (previewRef === lockedTargetRef || retiredRef === lockedTargetRef),
              );
              if (!lockResolved) {
                const lockedError = new Error(
                  'MLS identity seed restore is blocked by an unresolved Welcome target',
                );
                lockedError.code = 'MLS_IDENTITY_MUTATION_LOCKED';
                throw lockedError;
              }
            }
            const restored = await createIdentity({
              name: resolvedName,
              ciphersuite: ciphersuite || identity.ciphersuite || this.preferredCiphersuite,
              seed: identity.seed,
              identityId: identity.identityId,
            });

            const restoredIdentity = stampIdentityForStorage({
              identityId: restored.identityId || identity.identityId,
              ciphersuite: restored.ciphersuite || identity.ciphersuite || null,
              keyPackage: restored.keyPackage || identity.keyPackage || null,
              seed: identity.seed,
              identityName: resolvedName,
              clientId: identity.clientId ?? identity.identityId ?? restored.identityId ?? null,
              clientIds:
                identity.clientIds && typeof identity.clientIds === 'object'
                  ? { ...identity.clientIds }
                  : {},
            });

            const { record: nextIdentity } = this.#normalizeIdentityRecord(restoredIdentity);

            await setItem(this.identityKey, nextIdentity);
            this.#setIdentityState(nextIdentity);
            await persistStableIdentity(nextIdentity, identity.seed);
            await persistDeviceIdentityRecord(nextIdentity);
            await trackIdentityKeyPackage(nextIdentity, {
              keyPackageB64: restored?.keyPackage ?? null,
              status: 'unused',
            });
            await this.#debugKeyStoreCheckpoint('post_create_identity_rehydrate', {
              identityId: nextIdentity?.identityId ?? null,
              ciphersuite: nextIdentity?.ciphersuite ?? null,
            });
            return nextIdentity;
          } catch (rehydrateError) {
            if (rehydrateError?.code !== 'MLS_IDENTITY_MUTATION_LOCKED') {
              console.warn('[MLS] failed to rehydrate identity, creating a new one', rehydrateError);
            }
          }
        }

        // If the worker cannot restore this identity from its seed, do not mint a different
        // key package while an unresolved Welcome still targets the stored package. The
        // enrollment coordinator owns that transition and will explicitly reissue the Welcome.
        if (identityMutationLock) {
          try {
            console.info(
              '[MLS] preserving stored identity because welcome-target convergence owns re-enrollment',
              {
                identityId: identityMutationLock.identityId,
                targetRef: identityMutationLock.targetRef ?? null,
                source: identityMutationLock.source ?? null,
                groupId: identityMutationLock.groupId ?? null,
              },
            );
          } catch {}
          this.#setIdentityState(identity);
          await persistStableIdentity(identity, identity.seed);
          await persistDeviceIdentityRecord(identity);
          await trackIdentityKeyPackage(identity, {
            keyPackageB64:
              identity?.keyPackage ?? identity?.key_package_b64 ?? identity?.keyPackageB64 ?? null,
            status: 'unused',
          });
          return identity;
        }

        // Worker has no identity; attempt restoration from stable local storage
        let stableId = await readStableValue(stableKeys.identityId(this.userId));
        let stableSeed = await readStableValue(stableKeys.seed(this.userId));
        let stableLoginNonce = await readStableValue(stableKeys.loginNonce(this.userId));
        if (effectiveServerClientId) {
          stableId = effectiveServerClientId;
        }
        if (effectiveServerSeed) {
          stableSeed = effectiveServerSeed;
        }
        const normalizedStableLoginNonce = normalizeLoginNonce(stableLoginNonce);
        if (
          normalizedLoginNonce &&
          normalizedStableLoginNonce &&
          normalizedStableLoginNonce !== normalizedLoginNonce
        ) {
          stableId = null;
          stableSeed = null;
          await writeStableValue(stableKeys.identityId(this.userId), null);
          await writeStableValue(stableKeys.seed(this.userId), null);
          await writeStableValue(stableKeys.loginNonce(this.userId), null);
        }
        if (stableId && !isCanonicalMlsClientId(stableId)) {
          stableId = null;
        }
        if (stableSeed || stableId) {
          try {
            const resolvedName =
              ensureString(identity?.identityName ?? identity?.identity_name ?? null) ||
              this.#resolveIdentityName(name, stableId);
            const storedStableIdentity = stableId
              ? await this.#resolveStoredIdentityRecord(stableId).catch(() => null)
              : null;
            await reconcileSeedRestoreAuthority({
              identityRecord: storedStableIdentity,
              identityId: stableId,
              clientId:
                storedStableIdentity?.clientId ??
                storedStableIdentity?.client_id ??
                stableId ??
                null,
              resolvedName,
              seedValue: stableSeed,
              suiteHint: ciphersuite || identity?.ciphersuite || this.preferredCiphersuite,
              reason: 'stable_seed_restore_ref_drift',
            });
            const restored = await createIdentity({
              name: resolvedName,
              ciphersuite: ciphersuite || identity?.ciphersuite || this.preferredCiphersuite,
              seed: stableSeed || undefined,
              identityId: stableId || undefined,
            });
            const restoredIdentity = stampIdentityForStorage({
              identityId: restored.identityId,
              ciphersuite: restored.ciphersuite || identity?.ciphersuite || null,
              keyPackage: restored.keyPackage || null,
              seed: stableSeed || null,
              identityName: resolvedName,
              clientId: stableId || restored.identityId || null,
              clientIds: {},
            });
            const { record: nextIdentity } = this.#normalizeIdentityRecord(restoredIdentity);
            await setItem(this.identityKey, nextIdentity);
            this.#setIdentityState(nextIdentity);
            await persistStableIdentity(nextIdentity, stableSeed);
            await persistDeviceIdentityRecord(nextIdentity);
            await trackIdentityKeyPackage(nextIdentity, {
              keyPackageB64: restored?.keyPackage ?? null,
              status: 'unused',
            });
            await this.#debugKeyStoreCheckpoint('post_create_identity_restore_stable', {
              identityId: nextIdentity?.identityId ?? null,
              ciphersuite: nextIdentity?.ciphersuite ?? null,
            });
            if (typeof console?.info === 'function') {
              try {
                console.info('[MLS] identity restored from stable seed', {
                  identityId: nextIdentity?.identityId ?? null,
                  source: effectiveServerSeed
                    ? 'server_seed'
                    : stableSeed
                      ? 'local_seed'
                      : 'unknown',
                });
              } catch {}
            }
            return nextIdentity;
          } catch (restoreError) {
            console.warn(
              '[MLS] failed to restore identity from stable store; creating new',
              restoreError,
            );
          }
        }
        // Fall back to fresh identity
        console.warn('[MLS] identity missing in worker, creating a new one');
      }
    }

    let persistedSeed = await readStableValue(stableKeys.seed(this.userId));
    let preferredIdentityId = await readStableValue(stableKeys.identityId(this.userId));
    let stableLoginNonce = await readStableValue(stableKeys.loginNonce(this.userId));
    if (effectiveServerClientId) {
      preferredIdentityId = effectiveServerClientId;
    }
    if (effectiveServerSeed) {
      persistedSeed = effectiveServerSeed;
    }
    const normalizedStableLoginNonce = normalizeLoginNonce(stableLoginNonce);
    if (
      normalizedLoginNonce &&
      normalizedStableLoginNonce &&
      normalizedStableLoginNonce !== normalizedLoginNonce
    ) {
      persistedSeed = null;
      preferredIdentityId = null;
      await writeStableValue(stableKeys.identityId(this.userId), null);
      await writeStableValue(stableKeys.seed(this.userId), null);
      await writeStableValue(stableKeys.loginNonce(this.userId), null);
    }
    const seed = persistedSeed || generateSeed();
    if (!isCanonicalMlsClientId(preferredIdentityId)) {
      preferredIdentityId = generateMlsClientId();
      await writeStableValue(stableKeys.identityId(this.userId), preferredIdentityId);
    }
    if (typeof console?.info === 'function') {
      try {
        console.info('[MLS] identity created new', {
          identityId: preferredIdentityId ?? null,
          source: persistedSeed ? 'stable_seed' : 'generated_seed',
        });
      } catch {}
    }
    const resolvedName = this.#resolveIdentityName(name, preferredIdentityId);
    const result = await createIdentity({
      name: resolvedName,
      ciphersuite: ciphersuite || this.preferredCiphersuite,
      seed,
      // Use a persisted MLS client id (mls-client-...) to keep the same client identity across reloads.
      // Do not reuse legacy UUID-only identity ids from the server; a fresh browser context must be able
      // to obtain a Welcome by joining as a new MLS client.
      identityId: preferredIdentityId,
    });

    const initialIdentity = stampIdentityForStorage({
      identityId: result.identityId,
      ciphersuite: result.ciphersuite,
      keyPackage: result.keyPackage,
      seed,
      identityName: resolvedName,
      clientId: preferredIdentityId,
      clientIds: {},
    });

    const { record: normalizedIdentity } = this.#normalizeIdentityRecord(initialIdentity);
    identity = normalizedIdentity;

    await setItem(this.identityKey, identity);
    // Persist stable identityId + seed to survive IndexedDB resets
    await persistStableIdentity(identity, seed);
    this.#setIdentityState(identity);
    await persistDeviceIdentityRecord(identity);
    await trackIdentityKeyPackage(identity, {
      keyPackageB64: result?.keyPackage ?? null,
      status: 'unused',
    });
    await this.#debugKeyStoreCheckpoint('post_create_identity_new', {
      identityId: identity?.identityId ?? null,
      ciphersuite: identity?.ciphersuite ?? null,
    });
    return identity;
  }

  async ensureGroup(
    { conversationId, identityId, groupId, groupHandle, ciphersuite } = {},
    options = {},
  ) {
    const { __skipWait = false } = options || {};
    const normalizedConversationId =
      conversationId == null
        ? null
        : typeof conversationId === 'string'
          ? conversationId
          : String(conversationId);
    const aliasKeys = new Set();

    if (normalizedConversationId != null) {
      aliasKeys.add(`conv:${normalizedConversationId}`);
      const knownConversation = getKnownMLSConversation(normalizedConversationId);
      const knownConversationGroupId =
        knownConversation?.mls_group_id ??
        knownConversation?.mlsGroupId ??
        knownConversation?.meta?.mls_group_id ??
        null;
      if (knownConversationGroupId != null) {
        aliasKeys.add(`gid:${String(knownConversationGroupId)}`);
      }
      if (this.conversationGroups.has(normalizedConversationId)) {
        const knownGroupId = this.conversationGroups.get(normalizedConversationId);
        if (knownGroupId != null) {
          aliasKeys.add(`gid:${String(knownGroupId)}`);
        }
      }
    }

    if (groupId != null) {
      aliasKeys.add(`gid:${String(groupId)}`);
    }

    if (groupHandle != null) {
      aliasKeys.add(`gh:${String(groupHandle)}`);
    }

    for (const key of aliasKeys) {
      if (this.groupEnsureTasks.has(key)) {
        return this.groupEnsureTasks.get(key);
      }
    }

    if (!aliasKeys.size) {
      aliasKeys.add(`anon:${Date.now()}:${Math.random().toString(36).slice(2)}`);
    }

    const ensureBody = async () => {
      await this.ensureInitialized();
      await this.#warmRuntimeGroups().catch(() => {});

      try {
        const groups = await this.#loadGroups();
        let existing = null;
        if (groupId && groups[groupId]) {
          existing = groups[groupId];
        } else if (groupHandle) {
          existing =
            Object.values(groups).find((entry) => entry?.groupHandle === groupHandle) || null;
        } else if (
          normalizedConversationId &&
          this.conversationGroups.has(normalizedConversationId)
        ) {
          const mappedId = this.conversationGroups.get(normalizedConversationId);
          if (mappedId && groups[mappedId]) {
            existing = groups[mappedId];
          }
        }
        const allowConversationScopedLocalReadyShortCircuit = !normalizedConversationId;
        if (existing && allowConversationScopedLocalReadyShortCircuit) {
          let info = await this.groupInfo({
            groupId: existing.groupId,
            groupHandle: existing.groupHandle,
          });
          if (!info?.ready && info?.epoch && identityId) {
            try {
              await this.recordGroupMember(info.groupId, identityId);
              info = await this.groupInfo({
                groupId: existing.groupId,
                groupHandle: existing.groupHandle,
              });
            } catch {}
          }
          if (info?.ready) {
            const synced = await this.#syncRuntimeGroupState({
              groupId: info?.groupId ?? existing.groupId,
              groupHandle: info?.groupHandle ?? existing.groupHandle,
              conversationId: normalizedConversationId,
              source: 'ensureGroup',
              reason: 'ensure_group_preflight',
              allowSyncWelcomes: true,
              allowJoinBundleRepair: false,
            }).catch(() => null);
            if (synced) {
              return synced;
            }
            return info;
          }
        }
      } catch {}

      const identity = await this.ensureIdentity({ ciphersuite, name: this.identityName });
      const activeIdentityId = identityId || identity?.identityId || null;
      if (!activeIdentityId) {
        throw new Error('MLS identity is not initialised');
      }

      const knownConversation =
        normalizedConversationId != null ? getKnownMLSConversation(normalizedConversationId) : null;
      const conversationProtocol =
        knownConversation?.e2ee_protocol ?? knownConversation?.e2eeProtocol ?? null;
      const conversationEnabled =
        knownConversation?.e2ee_enabled ?? knownConversation?.e2eeEnabled ?? null;
      const persistedConversationGroupId =
        knownConversation?.mls_group_id ??
        knownConversation?.mlsGroupId ??
        knownConversation?.meta?.mls_group_id ??
        null;

      const adoptOrRegisterKeys = (...keys) => {
        for (const key of keys) {
          if (!key) continue;
          if (this.groupEnsureTasks.has(key)) {
            const existing = this.groupEnsureTasks.get(key);
            if (existing && existing !== task) {
              const existingMeta = this.groupEnsureTaskMetadata.get(existing);
              const currentMeta = this.groupEnsureTaskMetadata.get(task);
              if (!existingMeta || !currentMeta || existingMeta.order <= currentMeta.order) {
                if (trackedKeys.has(key)) {
                  trackedKeys.delete(key);
                }
                return existing;
              }
            }
          }
          registerEnsureTaskKey(key);
        }
        return null;
      };

      const adoptResolvedIdentifiers = () =>
        adoptOrRegisterKeys(
          resolvedGroupId != null ? `gid:${String(resolvedGroupId)}` : null,
          resolvedGroupHandle != null ? `gh:${String(resolvedGroupHandle)}` : null,
          normalizedConversationId != null ? `conv:${normalizedConversationId}` : null,
        );

      let snapshot = null;
      let snapshotMissingOnServer = false;
      let shouldForceSnapshotFetch = false;
      if (normalizedConversationId && knownConversation) {
        shouldForceSnapshotFetch = trackConversationSnapshotFingerprint(
          normalizedConversationId,
          knownConversation,
        );
        if (shouldForceSnapshotFetch) {
          resetConversationMlsGroupCache(normalizedConversationId);
        }
      }
      if (normalizedConversationId) {
        try {
          snapshot = await getGroupSnapshot({
            conversationId: normalizedConversationId,
            force: shouldForceSnapshotFetch,
            clientId: activeIdentityId ?? undefined,
          });
        } catch (error) {
          if (error?.status === 404) {
            snapshotMissingOnServer = true;
          } else {
            console.warn('[MLS] Failed to fetch MLS group snapshot for conversation', {
              conversationId: normalizedConversationId,
              error,
            });
          }
        }
      }

      if (
        snapshotMissingOnServer &&
        normalizedConversationId &&
        (persistedConversationGroupId || this.conversationGroups.has(normalizedConversationId))
      ) {
        await this.#resetConversationLocalGroupState(normalizedConversationId, {
          groupId: persistedConversationGroupId ?? groupId ?? null,
          groupHandle: groupHandle ?? null,
          reason: 'missing_group_snapshot',
        }).catch(() => {});
      }

      let groups = null;
      let resolvedEntry = null;
      let resolvedGroupId = groupId ?? null;
      let resolvedGroupHandle = groupHandle ?? null;

      if (
        !snapshot &&
        normalizedConversationId &&
        knownConversation &&
        conversationEnabled === true &&
        String(conversationProtocol || '').toLowerCase() === 'mls' &&
        !persistedConversationGroupId
      ) {
        try {
          const bootstrap = await this.#bootstrapLegacyConversation({
            conversationId: normalizedConversationId,
            conversation: knownConversation,
            identityId: activeIdentityId,
          });
          if (bootstrap?.snapshot && !snapshot) {
            snapshot = bootstrap.snapshot;
          }
          if (bootstrap?.groupId) {
            const normalizedBootstrapGroupId = normalizeUserId(bootstrap.groupId);
            if (normalizedBootstrapGroupId) {
              this.conversationGroups.set(normalizedConversationId, normalizedBootstrapGroupId);
              persistKnownConversationGroupId(normalizedConversationId, normalizedBootstrapGroupId);
            }
          }
          if (bootstrap?.entry) {
            resolvedEntry = bootstrap.entry;
            resolvedGroupId = bootstrap.entry.groupId ?? resolvedGroupId;
            resolvedGroupHandle = bootstrap.entry.groupHandle ?? resolvedGroupHandle;
            groups = await this.#loadGroups();
            const bootstrapAdoption = adoptResolvedIdentifiers();
            if (bootstrapAdoption) {
              return bootstrapAdoption;
            }
          }
        } catch (bootstrapError) {
          console.warn('[MLS] Failed to bootstrap legacy conversation group', {
            conversationId: normalizedConversationId,
            error: bootstrapError,
          });
        }
      }

      if (!groups) {
        groups = await this.#loadGroups();
      }

      if (snapshot) {
        const snapshotEpochCandidate = Number(
          snapshot?.epoch ?? snapshot?.current_epoch ?? snapshot?.currentEpoch ?? 0,
        );
        const normalizedSnapshotEpoch = Number.isFinite(snapshotEpochCandidate)
          ? ensureEpoch(snapshotEpochCandidate)
          : null;
        const snapshotPendingWelcomes = Array.isArray(snapshot?.pending_welcomes)
          ? snapshot.pending_welcomes
          : Array.isArray(snapshot?.pendingWelcomes)
            ? snapshot.pendingWelcomes
            : [];
        const snapshotMembers = normalizeMemberList(snapshot?.members);
        const snapshotSuite = normalizeSuiteId(
          snapshot?.ciphersuite ??
            snapshot?.cipher_suite ??
            snapshot?.ciphersuite_id ??
            snapshot?.ciphersuiteId ??
            null,
        );
        const activeIdentityCandidate =
          this.identityClientId ||
          (snapshotSuite != null ? this.#getClientIdForSuite(snapshotSuite) : null) ||
          identity?.clientId ||
          activeIdentityId ||
          null;
        const activeIdentityNormalized = ensureString(activeIdentityCandidate);
        const activeIdentitySet = new Set(
          [activeIdentityNormalized, ensureString(activeIdentityId)].filter(Boolean).map(String),
        );
        const hasActiveMember =
          snapshotMembers.length > 0 &&
          Array.from(activeIdentitySet).some((id) => snapshotMembers.includes(String(id)));
        const snapshotGroupId =
          ensureString(snapshot?.group_id ?? snapshot?.groupId ?? null) ??
          ensureString(resolvedGroupId) ??
          null;
        const existingEntryBeforeRecovery =
          snapshotGroupId && groups[snapshotGroupId]
            ? groups[snapshotGroupId]
            : (this.#resolveStoredGroup(groups, {
                groupId: snapshotGroupId ?? resolvedGroupId ?? null,
                groupHandle:
                  ensureString(snapshot?.group_handle ?? snapshot?.groupHandle ?? null) ??
                  ensureString(resolvedGroupHandle) ??
                  null,
              })?.entry ?? null);
        const localLooksHealthyBeforeRecovery = Boolean(
          existingEntryBeforeRecovery &&
            existingEntryBeforeRecovery.ready &&
            !existingEntryBeforeRecovery.needsRecovery,
        );
        const probeGroupId =
          ensureString(resolvedGroupId) ??
          ensureString(snapshot?.group_id ?? snapshot?.groupId ?? null);

        if (!resolvedGroupId && probeGroupId) {
          resolvedGroupId = probeGroupId;
        }
        if (normalizedConversationId && probeGroupId) {
          const cachedGroupId = this.conversationGroups.get(normalizedConversationId) || null;
          const passedGroupId = groupId != null ? String(groupId) : null;
          const staleGroupId =
            cachedGroupId && cachedGroupId !== probeGroupId
              ? cachedGroupId
              : passedGroupId && passedGroupId !== probeGroupId
                ? passedGroupId
                : null;
          if (staleGroupId && staleGroupId !== probeGroupId) {
            try {
              await this.resetGroup(staleGroupId);
            } catch {}
            this.conversationGroups.set(normalizedConversationId, probeGroupId);
            try {
              persistKnownConversationGroupId(normalizedConversationId, probeGroupId);
            } catch {}
            try {
              resetConversationMlsGroupCache(normalizedConversationId);
            } catch {}
          }
        }

        if (
          normalizedConversationId &&
          probeGroupId &&
          normalizedSnapshotEpoch === 0 &&
          snapshotPendingWelcomes.length === 0
        ) {
          const probeResult = await this.#maybeProbeBootstrap({
            conversationId: normalizedConversationId,
            groupId: probeGroupId,
            identityId: activeIdentityId,
            ciphersuite:
              snapshot?.ciphersuite ?? snapshot?.cipher_suite ?? snapshot?.ciphersuite_id ?? null,
            epoch: normalizedSnapshotEpoch ?? 0,
          });

          if (probeResult?.snapshot) {
            snapshot = probeResult.snapshot;
          }
        }

        try {
          const ingestion = await this.#ingestPendingWelcomesFromSnapshot(snapshot, {
            conversationId: normalizedConversationId,
            identityId: activeIdentityId,
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
          });
          if (ingestion?.joined?.length) {
            const primaryJoined =
              ingestion.joined.find((item) => item?.groupId) ?? ingestion.joined[0] ?? null;
            if (primaryJoined?.groupId && primaryJoined.groupId !== resolvedGroupId) {
              resolvedGroupId = primaryJoined.groupId;
            }
            if (primaryJoined?.groupHandle && primaryJoined.groupHandle !== resolvedGroupHandle) {
              resolvedGroupHandle = primaryJoined.groupHandle;
            }
            groups = await this.#loadGroups();
          }
          void localLooksHealthyBeforeRecovery;
          void hasActiveMember;
        } catch (welcomeError) {
          console.warn('[MLS] Failed to ingest pending welcomes from snapshot', {
            conversationId: normalizedConversationId,
            error: welcomeError,
          });
        }
      }

      const stored = this.#resolveStoredGroup(groups, { groupId, groupHandle });
      resolvedEntry = stored.entry;

      resolvedGroupId =
        resolvedEntry?.groupId ??
        resolvedGroupId ??
        snapshot?.group_id ??
        snapshot?.groupId ??
        null;
      resolvedGroupHandle =
        resolvedEntry?.groupHandle ??
        resolvedGroupHandle ??
        snapshot?.group_handle ??
        snapshot?.groupHandle ??
        null;

      const adoptionCandidate = adoptResolvedIdentifiers();
      if (adoptionCandidate) {
        return adoptionCandidate;
      }

      if (normalizedConversationId && resolvedGroupId) {
        this.conversationGroups.set(normalizedConversationId, resolvedGroupId);
      }

      let hydratedFromSnapshot = null;
      if (snapshot) {
        try {
          hydratedFromSnapshot = await this.#hydrateGroupFromSnapshot(snapshot, {
            fallbackGroupId:
              resolvedGroupId ??
              snapshot?.group_id ??
              snapshot?.groupId ??
              normalizedConversationId ??
              null,
            fallbackGroupHandle:
              resolvedGroupHandle ?? snapshot?.group_handle ?? snapshot?.groupHandle ?? null,
            identityId: activeIdentityId,
            clearRecovery: true,
            allowRatchetTreeSynthesis: false,
          });
        } catch (error) {
          console.warn('[MLS] Failed to hydrate group from snapshot', {
            conversationId: normalizedConversationId,
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
            error,
          });
        }
      }

      if (hydratedFromSnapshot) {
        resolvedEntry = hydratedFromSnapshot;
        resolvedGroupId = hydratedFromSnapshot.groupId ?? resolvedGroupId;
        resolvedGroupHandle = hydratedFromSnapshot.groupHandle ?? resolvedGroupHandle;
        groups = await this.#loadGroups();
        const hydratedAdoption = adoptResolvedIdentifiers();
        if (hydratedAdoption) {
          return hydratedAdoption;
        }
      } else if (!resolvedEntry) {
        groups = await this.#loadGroups();
        const fallback = this.#resolveStoredGroup(groups, {
          groupId: resolvedGroupId,
          groupHandle: resolvedGroupHandle,
        });
        resolvedEntry = fallback.entry;
        const fallbackAdoption = adoptResolvedIdentifiers();
        if (fallbackAdoption) {
          return fallbackAdoption;
        }
      }

      if (!resolvedEntry) {
        const missingAdoption = adoptResolvedIdentifiers();
        if (missingAdoption) {
          return missingAdoption;
        }
        const missingEntry = {
          groupId: resolvedGroupId ?? null,
          groupHandle: resolvedGroupHandle ?? null,
          needsRecovery: true,
          recoveryReason: snapshot ? WAITING_RATCHET_TREE_REASON : 'missing_group_snapshot',
          ready: false,
          reason: snapshot ? 'missing_ratchet_tree' : 'missing_group_snapshot',
        };
        return this.#finalizeGroupReady(missingEntry);
      }

      const membershipBootstrap = await this.#ensureActiveMembershipBootstrap({
        entry: resolvedEntry,
        conversationId: normalizedConversationId,
        groupId: resolvedGroupId ?? resolvedEntry?.groupId ?? null,
        groupHandle: resolvedGroupHandle ?? resolvedEntry?.groupHandle ?? null,
        identityId: activeIdentityId,
        ciphersuite,
        source: 'ensureGroup',
        reason: 'ensure_group_membership_bootstrap',
      }).catch(() => null);
      if (membershipBootstrap?.repaired) {
        resolvedEntry = membershipBootstrap.repaired;
        resolvedGroupId = membershipBootstrap.repaired.groupId ?? resolvedGroupId;
        resolvedGroupHandle = membershipBootstrap.repaired.groupHandle ?? resolvedGroupHandle;
        groups = await this.#loadGroups();
        const membershipAdoption = adoptResolvedIdentifiers();
        if (membershipAdoption) {
          return membershipAdoption;
        }
      }

      const runtimeBootstrap = await this.#ensureCurrentIdentityRuntimeBootstrap({
        entry: resolvedEntry,
        conversationId: normalizedConversationId,
        groupId: resolvedGroupId ?? resolvedEntry?.groupId ?? null,
        groupHandle: resolvedGroupHandle ?? resolvedEntry?.groupHandle ?? null,
        identityId: activeIdentityId,
        ciphersuite,
        source: 'ensureGroup',
        reason: 'ensure_group_runtime_bootstrap',
      }).catch(() => null);
      if (runtimeBootstrap?.repaired) {
        const repairedEntry = runtimeBootstrap.repaired;
        const repairedGroupId = repairedEntry.groupId ?? resolvedGroupId;
        const repairedReady = this.#isGroupReady(repairedEntry);
        const persistedRepairedEntry = repairedGroupId
          ? await this.#updateGroupEntry(repairedGroupId, (previous) => ({
              ...(previous || {}),
              ...repairedEntry,
              groupId: repairedGroupId,
              runtimeLive: repairedReady ? true : repairedEntry.runtimeLive,
              runtime_live: repairedReady ? true : repairedEntry.runtime_live,
              needsRecovery: repairedReady ? false : Boolean(repairedEntry.needsRecovery),
              recoveryReason: repairedReady ? null : repairedEntry.recoveryReason || null,
            })).catch(() => null)
          : null;
        resolvedEntry = persistedRepairedEntry || repairedEntry;
        resolvedGroupId = resolvedEntry.groupId ?? resolvedGroupId;
        resolvedGroupHandle = resolvedEntry.groupHandle ?? resolvedGroupHandle;
        groups = await this.#loadGroups();
        const runtimeAdoption = adoptResolvedIdentifiers();
        if (runtimeAdoption) {
          return runtimeAdoption;
        }
      }
      let runtimeGroups = [];
      try {
        runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
      } catch (error) {
        console.warn('[MLS] Failed to list groups while ensuring group', error);
      }

      let matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
        {
          ...resolvedEntry,
          groupId: resolvedGroupId ?? resolvedEntry?.groupId ?? null,
          groupHandle: resolvedGroupHandle ?? resolvedEntry?.groupHandle ?? null,
        },
        runtimeGroups,
      );
      let runtimeHasGroup = Boolean(matchedRuntimeGroup);
      if (!runtimeHasGroup) {
        const probedRuntimeGroup = await this.#probeRuntimeGroupByKnownHandle({
          entry: resolvedEntry,
          groupId: resolvedGroupId ?? resolvedEntry?.groupId ?? null,
          groupHandle: resolvedGroupHandle ?? resolvedEntry?.groupHandle ?? null,
          conversationId: normalizedConversationId,
          fallbackEpoch: resolvedEntry?.epoch ?? snapshot?.epoch ?? null,
        }).catch(() => null);
        if (probedRuntimeGroup?.groupHandle) {
          matchedRuntimeGroup = probedRuntimeGroup;
          runtimeHasGroup = true;
        }
      }
      const matchedRuntimeHandle = ensureString(matchedRuntimeGroup?.groupHandle ?? null);
      if (runtimeHasGroup && matchedRuntimeHandle && resolvedEntry?.groupId) {
        const matchedRuntimeEpoch =
          normalizeEpochOrNull(matchedRuntimeGroup?.epoch ?? null) ??
          normalizeEpochOrNull(
            resolvedEntry?.runtimeEpoch ??
              resolvedEntry?.runtime_epoch ??
              resolvedEntry?.runtime_epoch_no ??
              null,
          ) ??
          normalizeEpochOrNull(
            resolvedEntry?.epoch ??
              resolvedEntry?.currentEpoch ??
              resolvedEntry?.current_epoch ??
              null,
          );
        const resolvedMembershipState = this.#resolveEntryMembershipState(resolvedEntry, {
          activeIdentityId: activeIdentityId ?? null,
        });
        const pendingWelcomeIds = getPendingWelcomesList(resolvedEntry)
          .map((pending) => ensureString(pending?.welcome_id ?? pending?.welcomeId ?? null))
          .filter(Boolean);
        try {
          await this.#updateGroupEntry(resolvedEntry.groupId, (prev) => {
            const effectiveEpoch =
              matchedRuntimeEpoch != null
                ? Math.max(
                    matchedRuntimeEpoch,
                    normalizeEpochOrNull(
                      prev?.epoch ?? prev?.currentEpoch ?? prev?.current_epoch ?? null,
                    ) ?? 0,
                  )
                : (normalizeEpochOrNull(
                    prev?.epoch ?? prev?.currentEpoch ?? prev?.current_epoch ?? null,
                  ) ?? null);
            const baseNext = {
              ...(prev || {}),
              runtimeHandle: matchedRuntimeHandle,
              runtimeEpoch: matchedRuntimeEpoch,
              runtime_epoch: matchedRuntimeEpoch,
              groupHandle: ensureString(prev?.groupHandle ?? null) ?? matchedRuntimeHandle,
              runtimeLive: matchedRuntimeEpoch != null,
              runtime_live: matchedRuntimeEpoch != null,
              joined:
                prev?.joined ||
                resolvedEntry?.joined ||
                (resolvedMembershipState.currentIdentityMember && matchedRuntimeEpoch != null),
              epoch: effectiveEpoch,
              currentEpoch: effectiveEpoch,
              current_epoch: effectiveEpoch,
            };
            if (
              resolvedMembershipState.currentIdentityMember &&
              resolvedMembershipState.welcomeRequired &&
              matchedRuntimeEpoch != null
            ) {
              return this.#finalizeGroupReady({
                ...baseNext,
                pending_welcomes: [],
                pendingWelcomes: [],
                needsRecovery: false,
                recoveryReason: null,
              });
            }
            return baseNext;
          });
          if (
            resolvedMembershipState.currentIdentityMember &&
            resolvedMembershipState.welcomeRequired &&
            pendingWelcomeIds.length
          ) {
            await this.acknowledgeWelcomes({
              groupId: resolvedEntry.groupId,
              conversationId: normalizedConversationId ?? undefined,
              welcomeIds: pendingWelcomeIds,
              clientId: activeIdentityId,
            }).catch(() => null);
          }
          const latest = await this.#loadGroups();
          resolvedEntry = latest?.[resolvedEntry.groupId] ?? resolvedEntry;
        } catch {}
      }

      if (
        !runtimeHasGroup &&
        resolvedEntry?.welcomeB64 &&
        this.#canAttemptPersistedWelcomeRecovery(resolvedEntry, {
          source: 'ensure_group_for_conversation',
        })
      ) {
        const restored = await this.#rehydrateGroupFromWelcome(resolvedEntry, {
          allowForceRejoinRecovery: false,
          source: 'ensure_group_for_conversation',
        });
        if (restored?.groupHandle) {
          runtimeHasGroup = true;
          resolvedEntry = restored;
          resolvedGroupId = restored.groupId ?? resolvedGroupId;
          resolvedGroupHandle = restored.groupHandle ?? resolvedGroupHandle;
          const latestGroups = await this.#loadGroups();
          latestGroups[restored.groupId] = restored;
          await this.#saveGroups(latestGroups);
          groups = latestGroups;
          runtimeGroups = normalizeRuntimeGroups(await workerListGroups().catch(() => []));
          matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
            {
              ...restored,
              groupId: resolvedGroupId ?? restored.groupId ?? null,
              groupHandle: resolvedGroupHandle ?? restored.groupHandle ?? null,
            },
            runtimeGroups,
          );
          runtimeHasGroup = Boolean(matchedRuntimeGroup);
          const restoredAdoption = adoptResolvedIdentifiers();
          if (restoredAdoption) {
            return restoredAdoption;
          }
        } else if (resolvedEntry?.groupId) {
          await this.#setRehydrateBlockedUntil(
            resolvedEntry.groupId,
            REHYDRATE_RETRY_BACKOFF_MS,
            'ensure_group_for_conversation_failed',
          );
        }
      }

      if (!runtimeHasGroup) {
        const suppressMissingRuntimeRecovery = this.#shouldDeferRecoveryDuringBootstrap(
          resolvedEntry,
          {
            source: 'ensureGroup',
            reason: 'missing_runtime_group',
            pendingWelcomeCount: getPendingWelcomesList(resolvedEntry).length,
            runtimeHasGroup: false,
          },
        );
        if (suppressMissingRuntimeRecovery) {
          const preservedRuntimeHandle =
            ensureString(resolveRuntimeHandle(resolvedEntry)) ??
            ensureString(matchedRuntimeGroup?.groupHandle ?? null) ??
            null;
          const preservedRuntimeEpoch =
            normalizeEpochOrNull(
              resolvedEntry?.runtimeEpoch ??
                resolvedEntry?.runtime_epoch ??
                resolvedEntry?.runtime_epoch_no ??
                null,
            ) ?? null;
          if (resolvedGroupId) {
            await this.#markBootstrapConvergence(resolvedGroupId, {
              reason: 'ensureGroup:missing_runtime_group',
            });
          }
          const deferredRuntimeEntry = (resolvedGroupId &&
            (await this.#updateGroupEntry(resolvedGroupId, (prev) => ({
              ...prev,
              groupId: resolvedGroupId ?? prev?.groupId ?? null,
              groupHandle: resolvedGroupHandle ?? prev?.groupHandle ?? null,
              runtimeHandle:
                preservedRuntimeHandle ?? ensureString(resolveRuntimeHandle(prev)) ?? null,
              runtimeEpoch:
                preservedRuntimeEpoch ??
                normalizeEpochOrNull(
                  prev?.runtimeEpoch ?? prev?.runtime_epoch ?? prev?.runtime_epoch_no ?? null,
                ) ??
                null,
              runtimeLive: false,
              runtime_live: false,
              needsRecovery: false,
              recoveryReason: null,
              ready: false,
            })).catch(() => null))) || {
            ...resolvedEntry,
            groupId: resolvedGroupId ?? resolvedEntry.groupId ?? null,
            groupHandle: resolvedGroupHandle ?? resolvedEntry.groupHandle ?? null,
            runtimeHandle: preservedRuntimeHandle,
            runtimeEpoch: preservedRuntimeEpoch,
            runtimeLive: false,
            runtime_live: false,
            needsRecovery: false,
            recoveryReason: null,
            ready: false,
            reason: 'bootstrapping_runtime_group',
          };
          resolvedEntry = deferredRuntimeEntry;
        } else {
          const runtimeAdoption = adoptResolvedIdentifiers();
          if (runtimeAdoption) {
            return runtimeAdoption;
          }
          const flagged = {
            ...resolvedEntry,
            groupId: resolvedGroupId ?? resolvedEntry.groupId ?? null,
            groupHandle: resolvedGroupHandle ?? resolvedEntry.groupHandle ?? null,
            needsRecovery: true,
            recoveryReason: resolvedEntry.recoveryReason || 'missing_runtime_group',
            ready: false,
            reason: resolvedEntry.recoveryReason || 'missing_runtime_group',
          };
          if (flagged.groupId) {
            try {
              await this.markGroupForRecovery(flagged.groupId, flagged.recoveryReason);
            } catch {}
          }
          const repairedMembership = await this.#ensureActiveMembershipBootstrap({
            entry: flagged,
            conversationId: normalizedConversationId,
            groupId: flagged.groupId,
            groupHandle: flagged.groupHandle,
            identityId: activeIdentityId,
            ciphersuite,
            source: 'ensureGroup',
            reason: 'missing_runtime_membership_bootstrap',
          }).catch(() => null);
          if (repairedMembership?.repaired) {
            return this.#finalizeGroupReady(repairedMembership.repaired);
          }
          return this.#finalizeGroupReady(flagged);
        }
      }

      const normalizedMembers = normalizeMemberList(resolvedEntry.members);
      if (!normalizedMembers.includes(activeIdentityId)) {
        normalizedMembers.push(activeIdentityId);
      }

      const normalizedEntry = {
        ...resolvedEntry,
        groupId: resolvedGroupId ?? resolvedEntry.groupId ?? null,
        groupHandle: resolvedGroupHandle ?? resolvedEntry.groupHandle ?? null,
        epoch: ensureEpoch(resolvedEntry.epoch),
        lastServerSeq: (() => {
          const parsed = parseCursor(
            resolvedEntry?.lastServerSeq ??
              resolvedEntry?.last_server_seq ??
              resolvedEntry?.lastServerSequence ??
              null,
          );
          return parsed == null ? null : parsed;
        })(),
        members: normalizedMembers,
        identityId: resolvedEntry.identityId ?? activeIdentityId,
        needsRecovery: Boolean(resolvedEntry.needsRecovery),
        recoveryReason: resolvedEntry.recoveryReason || null,
      };

      if (normalizedEntry.groupId) {
        const latestGroups = await this.#loadGroups();
        latestGroups[normalizedEntry.groupId] = {
          ...(latestGroups[normalizedEntry.groupId] || {}),
          ...normalizedEntry,
        };
        await this.#saveGroups(latestGroups);
      }

      const finalAdoption = adoptResolvedIdentifiers();
      if (finalAdoption) {
        return finalAdoption;
      }

      const waitGroupId = normalizedEntry.groupId ?? resolvedGroupId ?? null;
      const waitGroupHandle = normalizedEntry.groupHandle ?? resolvedGroupHandle ?? null;

      if (!__skipWait && !normalizedEntry.needsRecovery && !this.#isGroupReady(normalizedEntry)) {
        if (waitGroupId != null || waitGroupHandle != null) {
          try {
            const readyInfo = await this.waitForGroupReady({
              groupId: waitGroupId,
              groupHandle: waitGroupHandle,
            });
            if (readyInfo) {
              return this.#finalizeGroupReady({
                ...normalizedEntry,
                ...readyInfo,
                groupId: readyInfo.groupId ?? normalizedEntry.groupId ?? waitGroupId,
                groupHandle:
                  readyInfo.groupHandle ?? normalizedEntry.groupHandle ?? waitGroupHandle,
              });
            }
          } catch (waitError) {
            if (waitError?.code === 'MLS_GROUP_NEEDS_RECOVERY') {
              const recoveryEntry = {
                ...normalizedEntry,
                groupId: waitError.groupId ?? normalizedEntry.groupId ?? waitGroupId ?? null,
                groupHandle:
                  waitError.groupHandle ?? normalizedEntry.groupHandle ?? waitGroupHandle ?? null,
                needsRecovery: true,
                recoveryReason: waitError.recoveryReason ?? normalizedEntry.recoveryReason ?? null,
              };
              return this.#finalizeGroupReady(recoveryEntry);
            }
            throw waitError;
          }
        }
      }

      return this.#finalizeGroupReady(normalizedEntry);
    };

    let resolveTask;
    let rejectTask;
    const task = new Promise((resolve, reject) => {
      resolveTask = resolve;
      rejectTask = reject;
    });

    const trackedKeys = new Set(aliasKeys);
    const registerEnsureTaskKey = (key) => {
      if (!key) {
        return;
      }
      if (trackedKeys.has(key)) {
        this.groupEnsureTasks.set(key, task);
        return;
      }
      trackedKeys.add(key);
      this.groupEnsureTasks.set(key, task);
    };

    for (const key of trackedKeys) {
      this.groupEnsureTasks.set(key, task);
    }

    this.groupEnsureTaskMetadata.set(task, {
      order: (this.groupEnsureTaskSequence = (this.groupEnsureTaskSequence || 0) + 1),
    });

    (async () => {
      try {
        const result = await ensureBody();
        if (result && typeof result.then === 'function') {
          const awaited = await result;
          resolveTask(awaited);
        } else {
          resolveTask(result);
        }
      } catch (error) {
        rejectTask(error);
      } finally {
        for (const key of trackedKeys) {
          this.groupEnsureTasks.delete(key);
        }
        this.groupEnsureTaskMetadata.delete(task);
      }
    })();

    return task;
  }

  async exportKeyPackage(identityId, { refreshIfConsumed = false } = {}) {
    await this.ensureInitialized();
    const result = await workerExportKeyPackage({ identityId, refreshIfConsumed });
    const resolvedIdentityId = result?.identityId || identityId || null;
    const resolvedSuite = result?.ciphersuite || this.preferredCiphersuite || null;
    const keyPackageB64 = result?.keyPackage || null;
    return {
      identityId: resolvedIdentityId,
      keyPackage: keyPackageB64,
      ciphersuite: resolvedSuite,
    };
  }

  async #getWorkerIdentityHealth({
    identityId = null,
    includeKeyPackage = false,
    expectedRef = null,
    ciphersuite = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }
    const result = await workerGetIdentityHealth({
      identityId: normalizedIdentityId,
      includeKeyPackage,
    });
    const computedRef =
      includeKeyPackage && result?.keyPackageB64
        ? await computeKeyPackageRefHexFromB64(
            result.keyPackageB64,
            normalizeSuiteId(ciphersuite ?? result?.ciphersuite ?? this.preferredCiphersuite),
          ).catch(() => null)
        : null;
    const normalizedExpectedRef = normalizeHexRefValue(expectedRef);
    const authoritativePublishedRef = await this.#resolveAuthoritativePublishedKeyPackageRef({
      identityId: normalizedIdentityId,
      ciphersuite,
    });
    return {
      ...result,
      publishedKeyPackageRef: computedRef,
      expectedKeyPackageRef: normalizedExpectedRef,
      publishedKeyPackageRefMatchesExpected:
        computedRef && normalizedExpectedRef ? computedRef === normalizedExpectedRef : null,
      currentLocalKeyPackageRef: computedRef,
      authoritativePublishedKeyPackageRef: authoritativePublishedRef,
      currentLocalKeyPackageRefMatchesExpected:
        computedRef && normalizedExpectedRef ? computedRef === normalizedExpectedRef : null,
      authoritativePublishedKeyPackageRefMatchesExpected:
        authoritativePublishedRef && normalizedExpectedRef
          ? authoritativePublishedRef === normalizedExpectedRef
          : null,
      currentLocalKeyPackageRefMatchesPublished:
        computedRef && authoritativePublishedRef ? computedRef === authoritativePublishedRef : null,
    };
  }

  async #getWorkerKeyPackageHealth({
    identityId = null,
    includeKeyPackage = false,
    expectedRef = null,
    ciphersuite = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }
    const result = await workerGetKeyPackageHealth({
      identityId: normalizedIdentityId,
      includeKeyPackage,
    });
    const computedRef =
      includeKeyPackage && result?.keyPackageB64
        ? await computeKeyPackageRefHexFromB64(
            result.keyPackageB64,
            normalizeSuiteId(ciphersuite ?? result?.ciphersuite ?? this.preferredCiphersuite),
          ).catch(() => null)
        : null;
    const normalizedExpectedRef = normalizeHexRefValue(expectedRef);
    const authoritativePublishedRef = await this.#resolveAuthoritativePublishedKeyPackageRef({
      identityId: normalizedIdentityId,
      ciphersuite,
    });
    return {
      ...result,
      publishedKeyPackageRef: computedRef,
      expectedKeyPackageRef: normalizedExpectedRef,
      publishedKeyPackageRefMatchesExpected:
        computedRef && normalizedExpectedRef ? computedRef === normalizedExpectedRef : null,
      currentLocalKeyPackageRef: computedRef,
      authoritativePublishedKeyPackageRef: authoritativePublishedRef,
      currentLocalKeyPackageRefMatchesExpected:
        computedRef && normalizedExpectedRef ? computedRef === normalizedExpectedRef : null,
      authoritativePublishedKeyPackageRefMatchesExpected:
        authoritativePublishedRef && normalizedExpectedRef
          ? authoritativePublishedRef === normalizedExpectedRef
          : null,
      currentLocalKeyPackageRefMatchesPublished:
        computedRef && authoritativePublishedRef ? computedRef === authoritativePublishedRef : null,
    };
  }

  async #resolveStoredIdentityRecord(identityId = null) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }

    try {
      const currentIdentityId = this.#normalizeIdentityId(
        this.identityRecord?.identityId ?? this.identityRecord?.identity_id ?? null,
      );
      if (currentIdentityId && currentIdentityId === normalizedIdentityId) {
        return this.identityRecord;
      }
    } catch {}

    try {
      const storedIdentity = await getItem(this.identityKey);
      if (!storedIdentity || typeof storedIdentity !== 'object') {
        return null;
      }
      const { record: normalizedIdentity } = this.#normalizeIdentityRecord(storedIdentity);
      const storedIdentityId = this.#normalizeIdentityId(
        normalizedIdentity?.identityId ?? normalizedIdentity?.identity_id ?? null,
      );
      return storedIdentityId && storedIdentityId === normalizedIdentityId
        ? normalizedIdentity
        : null;
    } catch {
      return null;
    }
  }

  async #resolveAuthoritativePublishedKeyPackageRef({
    identityId = null,
    ciphersuite = null,
    preferProtectedCurrentLocal = false,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }

    const resolvedIdentity = await this.#resolveStoredIdentityRecord(normalizedIdentityId);
    const normalizedSuite = normalizeSuiteId(
      ciphersuite ?? resolvedIdentity?.ciphersuite ?? this.preferredCiphersuite ?? null,
    );
    const persistedProtectedDrift = await this.#resolveProtectedWelcomeTargetDrift({
      identityId: normalizedIdentityId,
    }).catch(() => null);
    const memoryProtectedDrift =
      this._protectedWelcomeTargetDriftByIdentity instanceof Map
        ? this._protectedWelcomeTargetDriftByIdentity.get(normalizedIdentityId) || null
        : null;
    const protectedDrift = persistedProtectedDrift || memoryProtectedDrift || null;
    const protectedDriftSuite = normalizeSuiteId(protectedDrift?.ciphersuite ?? null);
    const protectedDriftCurrentLocalRef = normalizeHexRefValue(
      protectedDrift?.currentLocalRef ?? null,
    );
    const protectedDriftTargetRef = normalizeHexRefValue(protectedDrift?.targetRef ?? null);
    const protectedDriftApplies =
      (protectedDriftCurrentLocalRef || protectedDriftTargetRef) &&
      (normalizedSuite == null ||
        protectedDriftSuite == null ||
        protectedDriftSuite === normalizedSuite);
    if (protectedDriftApplies) {
      return preferProtectedCurrentLocal
        ? (protectedDriftCurrentLocalRef ?? protectedDriftTargetRef ?? null)
        : (protectedDriftTargetRef ?? protectedDriftCurrentLocalRef ?? null);
    }

    const chooseCandidate = (best, next) => {
      if (!next?.ref) {
        return best;
      }
      if (!best?.ref) {
        return next;
      }
      if ((next.score ?? 0) !== (best.score ?? 0)) {
        return (next.score ?? 0) > (best.score ?? 0) ? next : best;
      }
      return (next.timestamp ?? 0) >= (best.timestamp ?? 0) ? next : best;
    };

    let bestCandidate = null;
    const identityPublishedRef = normalizeHexRefValue(
      resolvedIdentity?.publishedKeyPackageRef ??
        resolvedIdentity?.published_key_package_ref ??
        null,
    );
    if (identityPublishedRef) {
      bestCandidate = {
        ref: identityPublishedRef,
        score: 10,
        timestamp: coerceTimestampMs(
          resolvedIdentity?.publishedAt ?? resolvedIdentity?.published_at ?? null,
        ),
      };
    }

    const candidateIds = new Set([normalizedIdentityId]);
    const addCandidateId = (value) => {
      const normalizedValue = this.#normalizeIdentityId(value);
      if (normalizedValue) {
        candidateIds.add(normalizedValue);
      }
    };

    addCandidateId(resolvedIdentity?.clientId ?? resolvedIdentity?.client_id ?? null);
    addCandidateId(this.identityClientId ?? null);
    addCandidateId(this.#getClientIdForSuite(normalizedSuite));

    if (resolvedIdentity?.clientIds && typeof resolvedIdentity.clientIds === 'object') {
      if (normalizedSuite != null) {
        addCandidateId(resolvedIdentity.clientIds[String(normalizedSuite)]);
        addCandidateId(resolvedIdentity.clientIds[this.#normalizeSuiteKey(normalizedSuite)]);
      }
      for (const value of Object.values(resolvedIdentity.clientIds)) {
        addCandidateId(value);
      }
    }

    await this.#ensureKeyPackageCacheLoaded().catch(() => null);
    for (const candidateId of candidateIds) {
      const entry = this.#getKeyPackageCacheEntry(candidateId, normalizedSuite);
      const candidateRef = normalizeHexRefValue(entry?.ref ?? entry?.kp_ref ?? null);
      if (!candidateRef) {
        continue;
      }
      const entryStatus = ensureString(entry?.status ?? null)?.toLowerCase() || null;
      bestCandidate = chooseCandidate(bestCandidate, {
        ref: candidateRef,
        score:
          100 +
          (entryStatus && entryStatus !== 'consumed' ? 20 : 0) +
          (normalizedSuite != null &&
          normalizeSuiteId(entry?.ciphersuite ?? null) === normalizedSuite
            ? 5
            : 0),
        timestamp:
          coerceTimestampMs(
            entry?.lastPublishedAt ?? entry?.last_published_at ?? entry?.published_at ?? null,
          ) ??
          coerceTimestampMs(entry?.fetchedAt ?? entry?.fetched_at ?? null) ??
          0,
      });
    }

    await this.#ensureKeyPackagesByRefLoaded().catch(() => null);
    for (const entry of this.keyPackagesByRef.values()) {
      if (!entry || typeof entry !== 'object') {
        continue;
      }
      const entryRef = normalizeHexRefValue(
        entry.kp_ref ?? entry.kpRef ?? entry.key_package_ref ?? entry.ref,
      );
      if (!entryRef) {
        continue;
      }
      const entryIdentityId = this.#normalizeIdentityId(
        entry.identity_id ?? entry.identityId ?? null,
      );
      const entryClientId = this.#normalizeIdentityId(entry.client_id ?? entry.clientId ?? null);
      if (!candidateIds.has(entryIdentityId) && !candidateIds.has(entryClientId)) {
        continue;
      }
      const entrySuite = normalizeSuiteId(entry.ciphersuite ?? null);
      if (normalizedSuite != null && entrySuite != null && entrySuite !== normalizedSuite) {
        continue;
      }
      const publishedAt =
        coerceTimestampMs(entry.published_at ?? entry.publishedAt ?? null) ?? null;
      if (publishedAt == null) {
        continue;
      }
      const entryStatus = ensureString(entry.status ?? null)?.toLowerCase() || null;
      bestCandidate = chooseCandidate(bestCandidate, {
        ref: entryRef,
        score:
          80 +
          (entryStatus && entryStatus !== 'consumed' ? 20 : 0) +
          (normalizedSuite != null && entrySuite === normalizedSuite ? 5 : 0),
        timestamp: publishedAt,
      });
    }

    return bestCandidate?.ref ?? null;
  }

  async #rehydratePublishedIdentity({
    identityId = null,
    ciphersuite = null,
    expectedKeyPackageRef = null,
  } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return null;
    }
    try {
      console.warn(
        '[MLS] Skipping published identity rehydrate because MLS key packages cannot be safely reconstructed from seed for an existing welcome target',
        {
          identityId: normalizedIdentityId,
          ciphersuite: normalizeSuiteId(ciphersuite ?? this.preferredCiphersuite ?? null) ?? null,
          expectedRef: normalizeHexRefValue(expectedKeyPackageRef ?? null),
        },
      );
    } catch {}

    return null;
  }

  async #shouldRehydratePublishedIdentity({ identityId = null, ciphersuite = null } = {}) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) {
      return false;
    }

    try {
      const workerState = await workerDebugIdentityState({
        identityId: normalizedIdentityId,
        includeKeyPackage: false,
      });
      return !(
        workerState?.identityFound === true &&
        workerState?.identityHandleAlive === true &&
        workerState?.keyPackageHandleAlive === true &&
        workerState?.privateMaterialReady === true &&
        workerState?.welcomeStageReady !== false &&
        normalizeSuiteId(workerState?.ciphersuite ?? ciphersuite ?? this.preferredCiphersuite) !=
          null
      );
    } catch {
      return false;
    }
  }

  async #debugKeyStoreCheckpoint(
    checkpoint,
    {
      identityId = null,
      groupId = null,
      welcomeB64 = null,
      ciphersuite = null,
      publishResult = null,
    } = {},
  ) {
    const normalizedIdentityId = this.#normalizeIdentityId(identityId ?? this.identityId ?? null);
    if (!normalizedIdentityId) return;

    let workerState = null;
    try {
      workerState = await this.#getWorkerKeyPackageHealth({
        identityId: normalizedIdentityId,
        includeKeyPackage: true,
        ciphersuite,
      });
      if (!workerState) {
        workerState = await workerDebugIdentityState({
          identityId: normalizedIdentityId,
          includeKeyPackage: true,
        });
      }
    } catch (error) {
      try {
        console.warn('MDBG KEYTRACE checkpoint failed to query worker', {
          checkpoint,
          identityId: normalizedIdentityId,
          groupId: groupId ?? null,
          error: error?.message || String(error || ''),
        });
      } catch {}
      return;
    }

    let localPublicKeyReference = null;
    let welcomePublicKeyReference = null;
    let welcomeLength = 0;
    let welcomeHead = null;
    const suiteHint =
      normalizeSuiteId(ciphersuite ?? workerState?.ciphersuite ?? this.preferredCiphersuite) ??
      null;

    try {
      localPublicKeyReference = await computeKeyPackageRefHexFromB64(
        workerState?.keyPackageB64 || null,
        suiteHint,
      );
    } catch {}
    try {
      welcomePublicKeyReference = resolveMatchingWelcomeRefHex(welcomeB64, [
        localPublicKeyReference,
      ]);
    } catch {}
    try {
      const bytes = decodeBase64ToBytes(welcomeB64);
      welcomeLength = bytes?.length || 0;
      if (welcomeLength > 0) {
        welcomeHead = Array.from(bytes.slice(0, Math.min(8, bytes.length)))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
      }
    } catch {}

    const keyReferenceMatch =
      localPublicKeyReference && welcomePublicKeyReference
        ? localPublicKeyReference === welcomePublicKeyReference
        : null;

    try {
      console.info('MDBG KEYTRACE checkpoint', {
        checkpoint,
        groupId: groupId ?? null,
        identityId: normalizedIdentityId,
        ciphersuite: suiteHint,
        workerInstanceId: workerState?.workerInstanceId ?? null,
        workerBootedAt: workerState?.workerBootedAt ?? null,
        providerReady: workerState?.providerReady ?? null,
        activeCiphersuite: workerState?.activeCiphersuite ?? null,
        identityFound: workerState?.identityFound ?? false,
        identityHandleAlive: workerState?.identityHandleAlive ?? false,
        keyPackageHandleAlive: workerState?.keyPackageHandleAlive ?? false,
        keyPackageExportable: workerState?.keyPackageExportable ?? false,
        keyPackageBytesLength: workerState?.keyPackageBytesLength ?? 0,
        identityCount: workerState?.identityCount ?? null,
        groupCount: workerState?.groupCount ?? null,
        identityPtr: workerState?.identityPtr ?? null,
        keyPackagePtr: workerState?.keyPackagePtr ?? null,
        keyPackageProvenance: workerState?.keyPackageProvenance ?? null,
        welcomeStageReady: workerState?.welcomeStageReady ?? null,
        welcomeStageReason: workerState?.welcomeStageReason ?? null,
        privateMaterialReady: workerState?.privateMaterialReady ?? false,
        keyPackageHealthCode: workerState?.healthCode ?? workerState?.diagnosticCode ?? null,
        identityClientId: this.identityClientId ?? null,
        publicKeyReference: workerState?.publishedKeyPackageRef ?? localPublicKeyReference,
        welcomePublicKeyReference,
        welcomeLength,
        welcomeHead,
        keyReferenceMatch,
        publishResult: publishResult
          ? {
              skipped: Boolean(publishResult?.skipped),
              deferred: Boolean(publishResult?.deferred),
              alreadyExists: Boolean(publishResult?.alreadyExists),
              clientId: publishResult?.clientId ?? null,
              ciphersuite: publishResult?.ciphersuite ?? null,
            }
          : null,
      });
    } catch {}
  }

  async publishKeyPackage(
    identityId,
    { forcePublish = false, overrideWelcomeTargetLock = false } = {},
  ) {
    const resolveWelcomeTargetPublishLock = async () => {
      const activeIdentity = await this.ensureIdentity({});
      const targetIdentityId =
        identityId ||
        activeIdentity?.identityId ||
        this.identityId ||
        this.identityClientId ||
        null;
      if (!targetIdentityId) {
        return null;
      }
      const unresolvedWelcomeTarget =
        await this.#findUnresolvedWelcomeTargetForIdentity(targetIdentityId);
      if (!unresolvedWelcomeTarget) {
        return null;
      }
      return {
        identityId: targetIdentityId,
        groupId: unresolvedWelcomeTarget.groupId ?? null,
        groupHandle: unresolvedWelcomeTarget.groupHandle ?? null,
        targetRef: unresolvedWelcomeTarget.targetRef ?? null,
        source: unresolvedWelcomeTarget.source ?? null,
      };
    };

    // Respect join-repair suppression window unless explicitly forced
    try {
      if (!forcePublish && this._suppressPublishUntil && Date.now() < this._suppressPublishUntil) {
        if (typeof console?.debug === 'function') {
          console.debug('[MLS] Suppressing publishKeyPackage during join repair window');
        }
        return { skipped: true, deferred: true };
      }
    } catch {}
    try {
      const welcomeTargetLock = await resolveWelcomeTargetPublishLock();
      if (welcomeTargetLock && !overrideWelcomeTargetLock) {
        if (typeof console?.info === 'function') {
          console.info('[MLS] publishKeyPackage suppressed to preserve unresolved welcome target', {
            identityId: welcomeTargetLock.identityId,
            groupId: welcomeTargetLock.groupId,
            groupHandle: welcomeTargetLock.groupHandle,
            targetRef: welcomeTargetLock.targetRef,
            source: welcomeTargetLock.source,
            forcePublish,
            overrideWelcomeTargetLock,
          });
        }
        return {
          skipped: true,
          locked: true,
          reason: 'welcome_target_locked',
          identityId: welcomeTargetLock.identityId,
          targetRef: welcomeTargetLock.targetRef,
          groupId: welcomeTargetLock.groupId,
          groupHandle: welcomeTargetLock.groupHandle,
        };
      }
    } catch {}
    // Respect per-identity lock to avoid flipping refs mid-join
    try {
      if (!forcePublish) {
        const identity = await this.ensureIdentity({});
        const targetIdentityId = identityId || identity?.identityId || this.identityId || null;
        if (targetIdentityId && this.#isKeyPackageLocked(String(targetIdentityId))) {
          if (typeof console?.debug === 'function') {
            console.debug('[MLS] publishKeyPackage locked during join');
          }
          return { skipped: true, locked: true, identityId: targetIdentityId };
        }
      }
    } catch {}
    await this.ensureInitialized();
    await this.#ensureClientIdCacheLoaded();
    const publishOwnerIdentity =
      this.#normalizeIdentityId(
        identityId ??
          this.identityId ??
          this.identityRecord?.identityId ??
          this.identityRecord?.identity_id ??
          this.identityClientId ??
          null,
      ) ?? 'self';

    return keyPackagePublishCoordinator.run({
      userId: this.userId,
      identityId: publishOwnerIdentity,
      reuseRecentResult: !forcePublish,
      task: async () => {
        const welcomeTargetLock = await resolveWelcomeTargetPublishLock().catch(() => null);
        if (welcomeTargetLock && !overrideWelcomeTargetLock) {
          if (typeof console?.info === 'function') {
            console.info(
              '[MLS] publishKeyPackage single-flight preserved unresolved welcome target lock',
              {
                identityId: welcomeTargetLock.identityId,
                groupId: welcomeTargetLock.groupId,
                groupHandle: welcomeTargetLock.groupHandle,
                targetRef: welcomeTargetLock.targetRef,
                source: welcomeTargetLock.source,
                forcePublish,
                overrideWelcomeTargetLock,
              },
            );
          }
          return {
            skipped: true,
            locked: true,
            reason: 'welcome_target_locked',
            identityId: welcomeTargetLock.identityId,
            targetRef: welcomeTargetLock.targetRef,
            groupId: welcomeTargetLock.groupId,
            groupHandle: welcomeTargetLock.groupHandle,
          };
        }
        // Optional global kill-switch to avoid ref churn during welcome reissue
        if (globalThis && globalThis.__MLS_SUPPRESS_PUBLISH__ === true && !forcePublish) {
          const identity = await this.ensureIdentity({});
          const targetIdentityId = identityId || identity.identityId || null;
          const preferredSuite = identity?.ciphersuite ?? this.preferredCiphersuite ?? null;
          debugLog('[MLS] Suppressing key package publish due to global flag', {
            identityId: targetIdentityId,
            ciphersuite: preferredSuite,
          });
          return {
            skipped: true,
            reason: 'suppressed',
            identityId: targetIdentityId,
            ciphersuite: preferredSuite,
          };
        }
        const identity = await this.ensureIdentity({});
        const targetIdentityId = identityId || identity.identityId;
        if (!targetIdentityId) {
          throw new Error('MLS identity is not initialised');
        }

        const normalizedIdentityId = this.#normalizeIdentityId(targetIdentityId);
        const preferredSuite = identity?.ciphersuite ?? this.preferredCiphersuite ?? null;
        const identityClientIds =
          identity?.clientIds && typeof identity.clientIds === 'object' ? identity.clientIds : null;
        const isSkipStateRuntimeSafe = ({ candidateClientId, suite, entry }) => {
          if (!entry?.ref) {
            return false;
          }
          const normalizedCandidate = this.#normalizeIdentityId(candidateClientId);
          if (!normalizedCandidate) {
            return false;
          }
          if (normalizedIdentityId && normalizedCandidate !== normalizedIdentityId) {
            return false;
          }
          const suiteKey = this.#normalizeSuiteKey(suite);
          const cachedSuiteClientId = this.#normalizeIdentityId(this.#getClientIdForSuite(suite));
          const identitySuiteClientId = suiteKey
            ? this.#normalizeIdentityId(identityClientIds?.[suiteKey] ?? null)
            : null;
          if (cachedSuiteClientId && cachedSuiteClientId !== normalizedCandidate) {
            return false;
          }
          if (identitySuiteClientId && identitySuiteClientId !== normalizedCandidate) {
            return false;
          }
          return true;
        };
        const preferredSuiteKey = this.#normalizeSuiteKey(preferredSuite);
        const preferredClientId = preferredSuiteKey ? identityClientIds?.[preferredSuiteKey] : null;
        const identityClientId = this.#normalizeIdentityId(
          preferredClientId ??
            identity?.clientId ??
            identity?.client_id ??
            this.identityClientId ??
            null,
        );
        const cachedPreferredClientId = this.#normalizeIdentityId(
          this.#getClientIdForSuite(preferredSuite),
        );
        let initialClientId = identityClientId;
        if (!initialClientId && cachedPreferredClientId) {
          if (cachedPreferredClientId === normalizedIdentityId) {
            initialClientId = cachedPreferredClientId;
          } else {
            this.#clearClientIdCacheForSuite(preferredSuite);
          }
        }
        if (
          cachedPreferredClientId &&
          identityClientId &&
          cachedPreferredClientId !== identityClientId
        ) {
          this.#clearClientIdCacheForSuite(preferredSuite);
        }
        let exported = null;
        let publishExport = null;
        const exportKeyPackageReadOnlyOnce = async () => {
          if (exported) return exported;
          exported = await this.exportKeyPackage(targetIdentityId);
          return exported;
        };
        const rotateKeyPackageForPublishOnce = async () => {
          if (publishExport) return publishExport;
          publishExport = await workerRotateKeyPackage({
            identityId: targetIdentityId,
            ciphersuite: resolvedCiphersuite ?? preferredSuite ?? null,
          });
          return publishExport;
        };

        await this.#ensureKeyPackageCacheLoaded();
        let firstCheck = { entry: null, proceed: true, needsRefresh: false };
        if (initialClientId) {
          firstCheck = this.#evaluateKeyPackageCache(initialClientId, preferredSuite);
        }
        if (!forcePublish && firstCheck.deferred) {
          debugLog('[MLS] Skipping key package publish - deferred window active', {
            identityId: initialClientId,
            ciphersuite: firstCheck.entry?.ciphersuite ?? preferredSuite ?? null,
          });
          return {
            deferred: true,
            identityId: normalizedIdentityId ?? initialClientId ?? null,
            ciphersuite: firstCheck.entry?.ciphersuite ?? preferredSuite ?? null,
            cacheEntry: firstCheck.entry ?? null,
          };
        }

        if (!forcePublish && !firstCheck.entry) {
          const refreshIdentity = initialClientId || normalizedIdentityId;
          if (refreshIdentity) {
            const refreshed = await this.#refreshKeyPackageFromServer({
              clientId: refreshIdentity,
              ciphersuite: preferredSuite ?? null,
            });
            if (refreshed?.skipped) {
              return refreshed;
            }
            firstCheck = this.#evaluateKeyPackageCache(refreshIdentity, preferredSuite);
          }
        }

        if (!forcePublish && firstCheck.skipped) {
          const firstSkipSuite = firstCheck.entry?.ciphersuite ?? preferredSuite ?? null;
          let shouldSkip = isSkipStateRuntimeSafe({
            candidateClientId: initialClientId,
            suite: firstSkipSuite,
            entry: firstCheck.entry,
          });
          if (!shouldSkip) {
            debugLog(
              '[MLS] Fresh key package cache exists but runtime suite client id is not aligned - forcing publish',
              {
                identityId: initialClientId ?? null,
                ciphersuite: firstSkipSuite,
                cacheRef: firstCheck.entry?.ref ?? null,
              },
            );
          }
          if (firstCheck.entry?.ref) {
            try {
              const exportedForCheck = await exportKeyPackageReadOnlyOnce();
              const localRef = await computeKeyPackageRefHexFromB64(
                exportedForCheck?.keyPackage,
                exportedForCheck?.ciphersuite ?? preferredSuite ?? null,
              );
              if (localRef && localRef !== firstCheck.entry.ref) {
                shouldSkip = false;
                debugLog('[MLS] Key package ref changed; forcing publish', {
                  identityId: initialClientId,
                  cachedRef: firstCheck.entry.ref,
                  localRef,
                });
              }
            } catch {}
          }
          if (shouldSkip) {
            debugLog('[MLS] Skipping key package publish - cache still fresh', {
              identityId: initialClientId,
              ciphersuite: firstCheck.entry?.ciphersuite ?? preferredSuite ?? null,
            });
            return {
              skipped: true,
              identityId: normalizedIdentityId ?? initialClientId ?? null,
              ciphersuite: firstCheck.entry?.ciphersuite ?? preferredSuite ?? null,
              cacheEntry: firstCheck.entry ?? null,
            };
          }
        }

        if (!exported) {
          exported = await exportKeyPackageReadOnlyOnce();
        }
        if (!exported?.keyPackage) {
          debugLog('[MLS] Skipping key package publish - export missing key package', {
            identityId: targetIdentityId,
            ciphersuite: exported?.ciphersuite ?? preferredSuite ?? null,
          });
          return {
            skipped: true,
            reason: 'missing_key_package',
            identityId: targetIdentityId,
            ciphersuite: exported?.ciphersuite ?? preferredSuite ?? null,
          };
        }

        const resolvedIdentityId =
          (exported && exported.identityId != null ? exported.identityId : null) ||
          (identityId != null ? identityId : null) ||
          identity.identityId;

        let resolvedCiphersuite = exported.ciphersuite ?? preferredSuite ?? null;

        const resolvedSuiteKey = this.#normalizeSuiteKey(resolvedCiphersuite);
        const resolvedClientId = resolvedSuiteKey ? identityClientIds?.[resolvedSuiteKey] : null;
        const resolvedIdentityClientId = this.#normalizeIdentityId(
          resolvedClientId ??
            identity?.clientId ??
            identity?.client_id ??
            this.identityClientId ??
            null,
        );
        const cachedSuiteClientId = this.#normalizeIdentityId(
          this.#getClientIdForSuite(resolvedCiphersuite),
        );
        let suiteClientId = resolvedIdentityClientId || initialClientId;
        if (!suiteClientId && cachedSuiteClientId) {
          if (cachedSuiteClientId === normalizedIdentityId) {
            suiteClientId = cachedSuiteClientId;
          } else {
            this.#clearClientIdCacheForSuite(resolvedCiphersuite);
          }
        }
        if (
          cachedSuiteClientId &&
          resolvedIdentityClientId &&
          cachedSuiteClientId !== resolvedIdentityClientId
        ) {
          this.#clearClientIdCacheForSuite(resolvedCiphersuite);
        }

        let postExportCheck = { entry: null, proceed: true, needsRefresh: false };
        if (suiteClientId) {
          postExportCheck = this.#evaluateKeyPackageCache(suiteClientId, resolvedCiphersuite);
        }
        if (!forcePublish && postExportCheck.deferred) {
          debugLog('[MLS] Skipping key package publish after export - deferred window active', {
            identityId: suiteClientId,
            ciphersuite: postExportCheck.entry?.ciphersuite ?? resolvedCiphersuite ?? null,
          });
          return {
            deferred: true,
            identityId: normalizedIdentityId ?? suiteClientId ?? null,
            ciphersuite: postExportCheck.entry?.ciphersuite ?? resolvedCiphersuite ?? null,
            cacheEntry: postExportCheck.entry ?? null,
          };
        }

        if (!forcePublish && postExportCheck.skipped) {
          const postSkipSuite = postExportCheck.entry?.ciphersuite ?? resolvedCiphersuite ?? null;
          let shouldSkip = isSkipStateRuntimeSafe({
            candidateClientId: suiteClientId,
            suite: postSkipSuite,
            entry: postExportCheck.entry,
          });
          if (!shouldSkip) {
            debugLog(
              '[MLS] Fresh key package cache after export is not runtime-safe - forcing publish',
              {
                identityId: suiteClientId ?? null,
                ciphersuite: postSkipSuite,
                cacheRef: postExportCheck.entry?.ref ?? null,
              },
            );
          }
          if (postExportCheck.entry?.ref && exported?.keyPackage) {
            try {
              const localRef = await computeKeyPackageRefHexFromB64(
                exported.keyPackage,
                resolvedCiphersuite ?? null,
              );
              if (localRef && localRef !== postExportCheck.entry.ref) {
                shouldSkip = false;
                debugLog('[MLS] Key package ref changed after export; forcing publish', {
                  identityId: suiteClientId,
                  cachedRef: postExportCheck.entry.ref,
                  localRef,
                });
              }
            } catch {}
          }
          if (shouldSkip) {
            debugLog('[MLS] Skipping key package publish after export - cache still fresh', {
              identityId: suiteClientId,
              ciphersuite: postExportCheck.entry?.ciphersuite ?? resolvedCiphersuite ?? null,
            });
            return {
              skipped: true,
              identityId: normalizedIdentityId ?? suiteClientId ?? null,
              ciphersuite: postExportCheck.entry?.ciphersuite ?? resolvedCiphersuite ?? null,
              cacheEntry: postExportCheck.entry ?? null,
            };
          }
        }

        if (postExportCheck.needsRefresh && suiteClientId) {
          const refreshed = await this.#refreshKeyPackageFromServer({
            clientId: suiteClientId,
            ciphersuite: resolvedCiphersuite,
          });
          if (refreshed?.skipped) {
            return refreshed;
          }
        }

        const publishClientId = suiteClientId;
        let publishExported = exported;
        let publishComputedRef = await computeKeyPackageRefHexFromB64(exported.keyPackage).catch(
          () => null,
        );
        if (publishComputedRef) {
          const currentRefConsumed = await this.#isTrackedKeyPackageRefConsumed(
            publishComputedRef,
          ).catch(() => false);
          if (currentRefConsumed) {
            const activeConvergence = await this.#findActiveBootstrapConvergenceForIdentity(
              targetIdentityId,
            ).catch(() => null);
            if (activeConvergence) {
              debugLog('[MLS] Deferring consumed key package refresh during active convergence', {
                identityId: targetIdentityId,
                consumedRef: publishComputedRef,
                groupId: activeConvergence.groupId ?? null,
                groupHandle: activeConvergence.groupHandle ?? null,
                conversationId: activeConvergence.conversationId ?? null,
                pendingWelcomeCount: activeConvergence.pendingWelcomeCount ?? 0,
                hasPersistedWelcome: activeConvergence.hasPersistedWelcome ?? false,
                needsRecovery: activeConvergence.needsRecovery ?? false,
                syncStatus: activeConvergence.syncStatus ?? null,
                runtimeState: activeConvergence.runtimeState ?? null,
              });
              return {
                skipped: true,
                deferred: true,
                reason: 'consumed_key_package_convergence_locked',
                identityId: normalizedIdentityId ?? targetIdentityId ?? null,
                targetRef: publishComputedRef,
                groupId: activeConvergence.groupId ?? null,
                groupHandle: activeConvergence.groupHandle ?? null,
                conversationId: activeConvergence.conversationId ?? null,
              };
            }
            debugLog('[MLS] Refreshing consumed key package only at final publish step', {
              identityId: targetIdentityId,
              consumedRef: publishComputedRef,
              ciphersuite: resolvedCiphersuite,
            });
            publishExported = await rotateKeyPackageForPublishOnce();
            resolvedCiphersuite = publishExported?.ciphersuite ?? resolvedCiphersuite ?? null;
            publishComputedRef =
              (publishExported?.keyPackage
                ? await computeKeyPackageRefHexFromB64(
                    publishExported.keyPackage,
                    resolvedCiphersuite ?? null,
                  ).catch(() => null)
                : null) ?? publishComputedRef;
          }
        }

        const publishPayload = {
          keyPackageB64: publishExported.keyPackage,
        };

        if (normalizedIdentityId) {
          publishPayload.identityId = normalizedIdentityId;
        }

        if (publishExported.ciphersuite != null) {
          publishPayload.ciphersuite = publishExported.ciphersuite;
        }

        return this.#publishKeyPackageWithRetries({
          identityId: normalizedIdentityId,
          clientId: publishClientId ?? null,
          ciphersuite: resolvedCiphersuite,
          payload: publishPayload,
          computedRef: publishComputedRef,
          force: forcePublish,
        });
      },
    });
  }

  async listGroups() {
    await this.ensureInitialized();
    const cached = await this.#loadGroups();
    const runtime = normalizeRuntimeGroups(await workerListGroups());
    const merged = new Map();

    Object.values(cached).forEach((entry) => {
      const normalized = {
        ...entry,
        epoch: ensureEpoch(entry.epoch),
        members: normalizeMemberList(entry.members),
      };
      const sanitized = { ...normalized };
      if (sanitized.welcomeB64) {
        sanitized.hasRecoveryWelcome = true;
        delete sanitized.welcomeB64;
      }
      merged.set(normalized.groupId, sanitized);
    });

    runtime.forEach((entry) => {
      if (!merged.has(entry.groupHandle)) {
        merged.set(entry.groupHandle, {
          groupId: entry.groupHandle,
          groupHandle: entry.groupHandle,
          ciphersuite: entry.ciphersuite,
        });
      }
    });

    return Array.from(merged.values());
  }

  async debugGroupRuntimeState({
    conversationId = null,
    groupId = null,
    groupHandle = null,
    includeExportKey = true,
    label = HISTORY_DERIVED_KEY_LABEL,
    context = null,
    contextB64 = null,
    length = HISTORY_DERIVED_KEY_LENGTH,
  } = {}) {
    await this.ensureInitialized();

    const normalizedConversationId =
      conversationId == null
        ? null
        : typeof conversationId === 'string'
          ? conversationId
          : String(conversationId);
    let resolvedGroupId = groupId != null ? String(groupId) : null;
    if (!resolvedGroupId && normalizedConversationId) {
      resolvedGroupId = this.conversationGroups.get(String(normalizedConversationId)) || null;
    }

    let info = null;
    if (resolvedGroupId || groupHandle) {
      info = await this.groupInfo({
        groupId: resolvedGroupId || undefined,
        groupHandle: groupHandle || undefined,
      }).catch(() => null);
    }

    let runtimeGroups = [];
    try {
      runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
    } catch {}

    const runtimeCandidates = runtimeGroups.filter((entry) => {
      const runtimeGroupId = ensureString(entry?.groupId ?? null);
      const runtimeHandle = ensureString(entry?.groupHandle ?? null);
      if (resolvedGroupId && runtimeGroupId && runtimeGroupId === resolvedGroupId) return true;
      if (groupHandle && runtimeHandle && runtimeHandle === groupHandle) return true;
      if (info?.groupHandle && runtimeHandle && runtimeHandle === info.groupHandle) return true;
      return false;
    });

    const resolvedRuntimeHandle = info?.runtimeHandle || info?.groupHandle || groupHandle || null;

    if (!resolvedRuntimeHandle) {
      return {
        found: false,
        reason: 'missing_runtime_handle',
        conversationId: normalizedConversationId,
        groupId: resolvedGroupId || info?.groupId || null,
        groupHandle: info?.groupHandle || groupHandle || null,
        snapshot: info || null,
        runtimeGroups,
        runtimeCandidates,
      };
    }

    const exportContext =
      context ?? normalizedConversationId ?? resolvedGroupId ?? info?.groupId ?? null;
    const workerState = await workerDebugGroupRuntimeState({
      groupHandle: resolvedRuntimeHandle,
      includeExportKey,
      label,
      context: exportContext,
      contextB64,
      length,
    }).catch((error) => {
      const code = ensureString(error?.code ?? null);
      if (code === 'MLS_WORKER_CALL_TIMEOUT') {
        // Keep debug endpoint responsive and recover worker in background.
        void this.#recoverWorkerCapabilities({
          conversationId: normalizedConversationId,
          ciphersuite: info?.ciphersuite ?? null,
        }).catch(() => {});
      }
      return {
        found: false,
        code: code || null,
        error: error?.message || String(error || ''),
      };
    });

    return {
      found: Boolean(workerState?.found),
      conversationId: normalizedConversationId,
      groupId: resolvedGroupId || info?.groupId || workerState?.groupId || null,
      groupHandle: info?.groupHandle || groupHandle || null,
      runtimeHandle: resolvedRuntimeHandle,
      runtimeGroups,
      runtimeCandidates,
      snapshot: info
        ? {
            epoch: info?.epoch ?? null,
            ready: info?.ready ?? null,
            needsRecovery: info?.needsRecovery ?? null,
            recoveryReason: info?.recoveryReason ?? null,
            lastServerSeq: (() => {
              const parsed = parseCursor(
                info?.lastServerSeq ?? info?.last_server_seq ?? info?.lastServerSequence ?? null,
              );
              return parsed == null ? null : parsed;
            })(),
            ciphersuite: info?.ciphersuite ?? null,
          }
        : null,
      worker: workerState || null,
    };
  }

  async debugDumpRuntimeState({
    conversationId = null,
    includeExportKey = true,
    includeKeyPackage = false,
    label = HISTORY_DERIVED_KEY_LABEL,
    context = null,
    contextB64 = null,
    length = HISTORY_DERIVED_KEY_LENGTH,
  } = {}) {
    await this.ensureInitialized();

    const normalizedConversationId =
      conversationId == null
        ? null
        : typeof conversationId === 'string'
          ? conversationId
          : String(conversationId);
    const conversationGroupId = normalizedConversationId
      ? this.conversationGroups.get(String(normalizedConversationId)) || null
      : null;

    let cachedGroups = {};
    try {
      cachedGroups = (await this.#loadGroups()) || {};
    } catch {}

    let runtimeGroups = [];
    try {
      runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
    } catch {}

    const workerDump = await workerDebugDumpRuntimeState({
      includeExportKey,
      includeKeyPackage,
      label,
      context: context ?? normalizedConversationId ?? conversationGroupId ?? null,
      contextB64,
      length,
    }).catch((error) => {
      const code = ensureString(error?.code ?? null);
      if (code === 'MLS_WORKER_CALL_TIMEOUT') {
        void this.#recoverWorkerCapabilities({
          conversationId: normalizedConversationId,
        }).catch(() => {});
      }
      return {
        found: false,
        code: code || null,
        error: error?.message || String(error || ''),
      };
    });

    const snapshots = Object.values(cachedGroups || {}).map((entry) => ({
      groupId: entry?.groupId ?? null,
      groupHandle: entry?.groupHandle ?? null,
      runtimeHandle: entry?.runtimeHandle ?? null,
      identityId: entry?.identityId ?? null,
      ciphersuite: entry?.ciphersuite ?? null,
      epoch: entry?.epoch == null ? null : ensureEpoch(entry.epoch),
      lastServerSeq: (() => {
        const parsed = parseCursor(
          entry?.lastServerSeq ?? entry?.last_server_seq ?? entry?.lastServerSequence ?? null,
        );
        return parsed == null ? null : parsed;
      })(),
      ready: this.#isGroupReady(entry),
      needsRecovery: Boolean(entry?.needsRecovery),
      recoveryReason: entry?.recoveryReason || null,
      membersCount: Array.isArray(entry?.members) ? entry.members.length : null,
      hasRecoveryWelcome: Boolean(entry?.welcomeB64),
    }));

    let focus = null;
    if (conversationGroupId) {
      try {
        focus = await this.debugGroupRuntimeState({
          conversationId: normalizedConversationId,
          includeExportKey,
          label,
          context: context ?? normalizedConversationId ?? conversationGroupId,
          contextB64,
          length,
        });
      } catch {}
    }

    return {
      generatedAt: new Date().toISOString(),
      conversationId: normalizedConversationId,
      conversationGroupId,
      identityId: this.identityId || null,
      identityClientId: this.identityClientId || null,
      preferredCiphersuite: this.preferredCiphersuite ?? null,
      runtimeGroups,
      snapshots,
      conversationGroups: Array.from(this.conversationGroups.entries()).map(([conv, gid]) => ({
        conversationId: conv,
        groupId: gid || null,
      })),
      focus,
      worker: workerDump,
    };
  }

  async encrypt({ groupId, groupHandle, identityId, plaintext }) {
    const resolveActiveRuntimeHandle = async (
      groupInfo = null,
      { fallbackHandle = null, allowDirectFallback = false } = {},
    ) => {
      const directFallback = allowDirectFallback ? ensureString(fallbackHandle) : null;
      if (!groupInfo) return directFallback;
      try {
        const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
        const matchedRuntime = this.#resolveRuntimeGroupForEntry(groupInfo, runtimeGroups);
        const matchedRuntimeHandle = ensureString(matchedRuntime?.groupHandle ?? null);
        if (matchedRuntimeHandle) {
          if (groupInfo.groupId && groupInfo.runtimeHandle !== matchedRuntimeHandle) {
            await this.#updateGroupEntry(groupInfo.groupId, (prev) => ({
              ...prev,
              runtimeHandle: matchedRuntimeHandle,
              groupHandle: ensureString(prev?.groupHandle ?? null) ?? matchedRuntimeHandle,
            })).catch(() => null);
          }
          return matchedRuntimeHandle;
        }
      } catch {}
      return null;
    };

    const createEncryptRecoveryError = ({
      code = 'E2EE_NOT_READY',
      message = 'E2EE not ready',
      groupInfo = null,
      fallbackGroupId = null,
      fallbackGroupHandle = null,
      recoveryReason = null,
    } = {}) => {
      const error = new Error(message);
      error.code = code;
      error.groupId = groupInfo?.groupId || fallbackGroupId || null;
      error.groupHandle = groupInfo?.groupHandle || ensureString(fallbackGroupHandle) || null;
      if (recoveryReason) {
        error.recoveryReason = recoveryReason;
      }
      return error;
    };

    const recoverRuntimeHandleForEncrypt = async ({
      groupInfo = null,
      fallbackGroupId = null,
      fallbackGroupHandle = null,
      reason = 'encrypt_runtime_missing',
    } = {}) => {
      const targetGroupId = ensureString(groupInfo?.groupId ?? fallbackGroupId ?? null);
      const targetGroupHandle = ensureString(groupInfo?.groupHandle ?? fallbackGroupHandle ?? null);
      if (!targetGroupId && !targetGroupHandle) {
        return null;
      }

      const targetConversationId = ensureString(groupInfo?.conversationId ?? null);

      if (targetGroupId) {
        await this.#updateGroupEntry(targetGroupId, (prev) => ({
          ...prev,
          runtimeHandle: null,
          runtimeEpoch: null,
        })).catch(() => null);
      }

      let repaired = await this.#syncRuntimeGroupState({
        groupId: targetGroupId,
        groupHandle: targetGroupHandle,
        conversationId: targetConversationId,
        source: 'encrypt',
        reason,
        allowSyncWelcomes: true,
        allowJoinBundleRepair: true,
      }).catch(() => null);

      if (!this.#isGroupReady(repaired) && targetConversationId) {
        try {
          await this.#recoverWorkerCapabilities({
            conversationId: targetConversationId,
            ciphersuite: groupInfo?.ciphersuite ?? repaired?.ciphersuite ?? null,
          });
        } catch {}
        repaired = await this.#syncRuntimeGroupState({
          groupId: targetGroupId,
          groupHandle: targetGroupHandle,
          conversationId: targetConversationId,
          source: 'encrypt',
          reason: `${reason}_after_worker_recovery`,
          allowSyncWelcomes: true,
          allowJoinBundleRepair: true,
        }).catch(() => repaired);
      }

      return repaired;
    };

    const encryptWithRuntimeRecovery = async ({
      groupInfo = null,
      fallbackGroupId = null,
      fallbackGroupHandle = null,
      activeIdentityId,
    }) => {
      let effectiveInfo = groupInfo;
      let effectiveHandle = await resolveActiveRuntimeHandle(effectiveInfo, {
        fallbackHandle: fallbackGroupHandle,
        allowDirectFallback:
          !effectiveInfo && !ensureString(fallbackGroupId) && Boolean(fallbackGroupHandle),
      });

      if (!effectiveHandle) {
        effectiveInfo =
          (await recoverRuntimeHandleForEncrypt({
            groupInfo: effectiveInfo,
            fallbackGroupId,
            fallbackGroupHandle,
            reason: 'encrypt_live_runtime_missing',
          })) || effectiveInfo;
        if (effectiveInfo?.needsRecovery) {
          throw createEncryptRecoveryError({
            code: 'MLS_GROUP_NEEDS_RECOVERY',
            message: 'MLS group requires recovery',
            groupInfo: effectiveInfo,
            fallbackGroupId,
            fallbackGroupHandle,
            recoveryReason: effectiveInfo.recoveryReason || 'runtime_missing',
          });
        }
        effectiveHandle = await resolveActiveRuntimeHandle(effectiveInfo, {
          fallbackHandle: fallbackGroupHandle,
          allowDirectFallback:
            !effectiveInfo && !ensureString(fallbackGroupId) && Boolean(fallbackGroupHandle),
        });
      }

      if (!effectiveHandle) {
        throw createEncryptRecoveryError({
          groupInfo: effectiveInfo,
          fallbackGroupId,
          fallbackGroupHandle,
          message: 'E2EE runtime handle unavailable',
        });
      }

      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const response = await encryptMessage({
            groupHandle: effectiveHandle,
            identityId: activeIdentityId,
            plaintext,
          });
          return {
            response,
            runtimeHandle: effectiveHandle,
            groupInfo: effectiveInfo,
          };
        } catch (error) {
          lastError = error;
          const message = String(error?.message || '').toLowerCase();
          const unknownGroup = message.includes('unknown group');
          if (!unknownGroup || attempt > 0) {
            throw error;
          }

          if (typeof console?.warn === 'function') {
            console.warn('[MLS] Encrypt encountered stale runtime handle; rebuilding runtime', {
              groupId: effectiveInfo?.groupId || fallbackGroupId || null,
              groupHandle: effectiveInfo?.groupHandle || fallbackGroupHandle || null,
              runtimeHandle: effectiveHandle,
              error: error?.message || String(error),
            });
          }

          effectiveInfo =
            (await recoverRuntimeHandleForEncrypt({
              groupInfo: effectiveInfo,
              fallbackGroupId,
              fallbackGroupHandle,
              reason: 'encrypt_unknown_group_retry',
            })) || effectiveInfo;
          if (effectiveInfo?.needsRecovery) {
            throw createEncryptRecoveryError({
              code: 'MLS_GROUP_NEEDS_RECOVERY',
              message: 'MLS group requires recovery',
              groupInfo: effectiveInfo,
              fallbackGroupId,
              fallbackGroupHandle,
              recoveryReason: effectiveInfo.recoveryReason || 'runtime_missing',
            });
          }

          effectiveHandle = await resolveActiveRuntimeHandle(effectiveInfo, {
            fallbackHandle: fallbackGroupHandle,
            allowDirectFallback:
              !effectiveInfo && !ensureString(fallbackGroupId) && Boolean(fallbackGroupHandle),
          });
          if (!effectiveHandle) {
            throw createEncryptRecoveryError({
              groupInfo: effectiveInfo,
              fallbackGroupId,
              fallbackGroupHandle,
              message: 'E2EE runtime handle unavailable',
            });
          }
        }
      }

      throw (
        lastError ||
        createEncryptRecoveryError({
          groupInfo: effectiveInfo,
          fallbackGroupId,
          fallbackGroupHandle,
          message: 'Failed to encrypt message',
        })
      );
    };

    const ensureRuntimeAlignedBeforeEncrypt = async ({
      groupInfo = null,
      fallbackGroupId = null,
      fallbackGroupHandle = null,
    } = {}) => {
      if (groupInfo && !groupInfo?.needsRecovery && this.#isGroupReady(groupInfo)) {
        return groupInfo;
      }

      const normalizedGroupId = ensureString(groupInfo?.groupId ?? fallbackGroupId ?? null);
      const normalizedGroupHandle = ensureString(
        groupInfo?.groupHandle ?? fallbackGroupHandle ?? null,
      );
      if (!normalizedGroupId && !normalizedGroupHandle) {
        return groupInfo;
      }

      const synced = await this.#syncRuntimeGroupState({
        groupId: normalizedGroupId,
        groupHandle: normalizedGroupHandle,
        conversationId: ensureString(groupInfo?.conversationId ?? null),
        source: 'encrypt',
        reason: 'encrypt_preflight_sync',
        allowSyncWelcomes: true,
        allowJoinBundleRepair: false,
      }).catch(() => null);

      const nextInfo = synced || groupInfo;
      if (nextInfo?.needsRecovery) {
        const recoveryError = new Error('MLS group requires recovery');
        recoveryError.code = 'MLS_GROUP_NEEDS_RECOVERY';
        recoveryError.groupId = nextInfo.groupId || normalizedGroupId || null;
        recoveryError.groupHandle = nextInfo.groupHandle || normalizedGroupHandle || null;
        recoveryError.recoveryReason = nextInfo.recoveryReason || 'runtime_snapshot_mismatch';
        throw recoveryError;
      }

      if (nextInfo && !this.#isGroupReady(nextInfo)) {
        const pendingError = new Error('E2EE not ready');
        pendingError.code = 'E2EE_NOT_READY';
        pendingError.groupId = nextInfo.groupId || normalizedGroupId || null;
        pendingError.groupHandle = nextInfo.groupHandle || normalizedGroupHandle || null;
        throw pendingError;
      }

      return nextInfo;
    };

    if (groupHandle) {
      const info = await this.groupInfo({ groupId, groupHandle }).catch(() => null);
      const runtimeAlignedInfo = await ensureRuntimeAlignedBeforeEncrypt({
        groupInfo: info,
        fallbackGroupId: groupId,
        fallbackGroupHandle: groupHandle,
      });
      const identity = await this.ensureIdentity({});
      const activeIdentityId = identityId || identity.identityId;
      const effectiveHandle = await resolveActiveRuntimeHandle(runtimeAlignedInfo, {
        fallbackHandle: groupHandle,
        allowDirectFallback: !groupId,
      });
      if (!effectiveHandle) {
        const recovered = await encryptWithRuntimeRecovery({
          groupInfo: runtimeAlignedInfo,
          fallbackGroupId: groupId,
          fallbackGroupHandle: groupHandle,
          activeIdentityId,
        });
        return {
          ciphertext: recovered.response.ciphertext,
          envelope_b64: recovered.response.ciphertext,
          groupHandle: recovered.runtimeHandle,
          groupId: recovered.groupInfo?.groupId || groupId || null,
          ciphersuite: recovered.groupInfo?.ciphersuite ?? null,
          epoch: ensureEpoch(recovered.groupInfo?.epoch ?? null),
        };
      }
      const recovered = await encryptWithRuntimeRecovery({
        groupInfo: runtimeAlignedInfo,
        fallbackGroupId: groupId,
        fallbackGroupHandle: effectiveHandle,
        activeIdentityId,
      });
      return {
        ciphertext: recovered.response.ciphertext,
        envelope_b64: recovered.response.ciphertext,
        groupHandle: recovered.runtimeHandle,
        groupId: recovered.groupInfo?.groupId || groupId || null,
        ciphersuite: recovered.groupInfo?.ciphersuite ?? null,
        epoch: ensureEpoch(recovered.groupInfo?.epoch ?? null),
      };
    }

    const groups = await this.#loadGroups();
    const entries = Object.values(groups);
    let group = null;

    if (groupHandle) {
      group = entries.find((entry) => entry.groupHandle === groupHandle) || null;
    }

    if (!group && groupId && groups[groupId]) {
      group = groups[groupId];
    }

    if (!group && groupHandle) {
      try {
        const identity = await this.ensureIdentity({});
        const activeIdentityId = identityId || identity.identityId;
        const response = await encryptMessage({
          groupHandle,
          identityId: activeIdentityId,
          plaintext,
        });
        return {
          ciphertext: response.ciphertext,
          envelope_b64: response.ciphertext,
          groupHandle,
          groupId: groupId || null,
          ciphersuite: null,
          epoch: null,
        };
      } catch {}
    }

    if (!group) {
      group = await this.ensureGroup({ groupId, groupHandle, identityId });
    }

    const normalizedGroup = (await this.groupInfo({
      groupId: group.groupId,
      groupHandle: group.groupHandle,
    })) || {
      ...group,
      epoch: ensureEpoch(group.epoch),
      members: normalizeMemberList(group.members),
      runtimeHandle: resolveRuntimeHandle(group),
    };

    const runtimeAlignedGroup =
      (await ensureRuntimeAlignedBeforeEncrypt({
        groupInfo: normalizedGroup,
        fallbackGroupId: normalizedGroup.groupId ?? groupId ?? null,
        fallbackGroupHandle: normalizedGroup.groupHandle ?? groupHandle ?? null,
      })) || normalizedGroup;

    const identity = await this.ensureIdentity({});
    const activeIdentityId = identityId || identity.identityId;
    const recovered = await encryptWithRuntimeRecovery({
      groupInfo: runtimeAlignedGroup,
      fallbackGroupId: runtimeAlignedGroup?.groupId ?? groupId ?? null,
      fallbackGroupHandle: runtimeAlignedGroup?.groupHandle ?? groupHandle ?? null,
      activeIdentityId,
    });

    try {
      await this.recordGroupMember(
        recovered.groupInfo?.groupId ?? runtimeAlignedGroup.groupId,
        activeIdentityId,
      );
    } catch {}

    return {
      ciphertext: recovered.response.ciphertext,
      envelope_b64: recovered.response.ciphertext,
      groupHandle: recovered.runtimeHandle,
      groupId: recovered.groupInfo?.groupId ?? runtimeAlignedGroup.groupId,
      ciphersuite: recovered.groupInfo?.ciphersuite ?? runtimeAlignedGroup.ciphersuite,
      epoch: ensureEpoch(recovered.groupInfo?.epoch ?? runtimeAlignedGroup.epoch),
    };
  }

  async decrypt({
    groupId,
    groupHandle,
    ciphertext,
    serverSeq,
    serverMsgId,
    epochHint = null,
    allowNotReady = false,
    allowRehydrate = true,
    traceId = null,
  }) {
    const effectiveTraceId =
      traceId ||
      (serverSeq != null
        ? `seq:${serverSeq}`
        : serverMsgId != null
          ? `smid:${serverMsgId}`
          : `trace:${Date.now()}`);
    const trace = (stage, data = {}) =>
      traceMlsSession(stage, {
        traceId: effectiveTraceId,
        lineHint: 'sessionManagerMLS.js:decrypt',
        groupId: groupId ?? null,
        groupHandle: groupHandle ?? null,
        serverSeq: serverSeq ?? null,
        serverMsgId: serverMsgId ?? null,
        allowNotReady: Boolean(allowNotReady),
        allowRehydrate: Boolean(allowRehydrate),
        ...data,
      });

    trace('decrypt:start', {
      ciphertextLength: String(ciphertext || '').length,
    });
    const groups = await this.#loadGroups();
    const entries = Object.values(groups);
    let group = null;

    if (groupHandle) {
      group = entries.find((entry) => entry.groupHandle === groupHandle) || null;
    }

    if (!group && groupId && groups[groupId]) {
      group = groups[groupId];
    }

    if (!group) {
      trace('decrypt:group_context_missing');
      throw new Error('Group context not found locally for MLS decrypt');
    }

    const normalizedGroup = (await this.groupInfo({
      groupId: group.groupId,
      groupHandle: group.groupHandle,
    })) || {
      ...group,
      epoch: ensureEpoch(group.epoch),
      members: normalizeMemberList(group.members),
    };

    if (normalizedGroup.needsRecovery) {
      trace('decrypt:group_needs_recovery', {
        recoveryReason: normalizedGroup.recoveryReason || null,
      });
      const recoveryError = new Error('MLS group requires recovery');
      recoveryError.code = 'MLS_GROUP_NEEDS_RECOVERY';
      recoveryError.groupId = normalizedGroup.groupId;
      recoveryError.groupHandle = normalizedGroup.groupHandle;
      recoveryError.recoveryReason = normalizedGroup.recoveryReason || null;
      throw recoveryError;
    }

    if (!this.#isGroupReady(normalizedGroup) && !allowNotReady) {
      trace('decrypt:group_not_ready', {
        normalizedGroupId: normalizedGroup.groupId || null,
      });
      if (normalizedGroup.groupId) {
        this.groupRecovery.set(normalizedGroup.groupId, true);
      }
      const pendingError = new Error('E2EE not ready');
      pendingError.code = 'E2EE_NOT_READY';
      pendingError.groupId = normalizedGroup.groupId;
      pendingError.groupHandle = normalizedGroup.groupHandle;
      throw pendingError;
    }

    let result;
    let effectiveHandle =
      normalizedGroup.runtimeHandle || normalizedGroup.groupHandle || normalizedGroup.groupId;
    try {
      const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
      const matchedRuntime = this.#resolveRuntimeGroupForEntry(normalizedGroup, runtimeGroups);
      const matchedRuntimeHandle = ensureString(matchedRuntime?.groupHandle ?? null);
      if (matchedRuntimeHandle) {
        effectiveHandle = matchedRuntimeHandle;
        if (normalizedGroup.runtimeHandle !== matchedRuntimeHandle && normalizedGroup.groupId) {
          await this.#updateGroupEntry(normalizedGroup.groupId, (prev) => ({
            ...prev,
            runtimeHandle: matchedRuntimeHandle,
            groupHandle: ensureString(prev?.groupHandle ?? null) ?? matchedRuntimeHandle,
          })).catch(() => null);
        }
      }
    } catch {}
    try {
      trace('decrypt:worker_decrypt_call', {
        effectiveHandle,
      });
      result = await decryptMessage({
        groupHandle: effectiveHandle,
        ciphertext,
        epochHint,
      });
      trace('decrypt:worker_decrypt_ok', {
        hasText: Boolean(result?.text),
        hasPlaintextB64: Boolean(result?.plaintext),
      });
    } catch (error) {
      const message = (error?.message || '').toLowerCase();
      trace('decrypt:worker_decrypt_error', {
        effectiveHandle,
        errorMessage: error?.message || String(error),
      });
      if (message.includes('unknown group')) {
        const rehydrateBlockedUntil = coerceTimestampMs(group?.rehydrateBlockedUntil ?? null);
        const canAttemptRehydrate =
          allowRehydrate &&
          Boolean(group?.recoveryWelcomeB64 || group?.welcomeB64) &&
          (!rehydrateBlockedUntil || rehydrateBlockedUntil <= Date.now());
        trace('decrypt:unknown_group', {
          canAttemptRehydrate,
          hasRecoveryWelcome: Boolean(group?.recoveryWelcomeB64 || group?.welcomeB64),
        });
        if (canAttemptRehydrate) {
          let restored = null;
          try {
            restored = await this.#rehydrateGroupFromWelcome(group, {
              allowForceRejoinRecovery: false,
              source: 'decrypt_unknown_group',
            });
            trace('decrypt:rehydrate_attempt_result', {
              restored: Boolean(restored?.groupHandle),
              restoredGroupId: restored?.groupId || null,
              restoredGroupHandle: restored?.groupHandle || null,
            });
          } catch (rehydrateError) {
            console.warn('[MLS] Failed to rehydrate group after unknown runtime miss', {
              groupId: group?.groupId,
              groupHandle: group?.groupHandle,
              error: rehydrateError,
            });
          }

          if (restored?.groupHandle) {
            let persisted = false;
            try {
              const updated = await this.#updateGroupEntry(restored.groupId, (prev) => ({
                ...prev,
                ...restored,
                needsRecovery: false,
                recoveryReason: null,
                rehydrateBlockedUntil: null,
              }));
              persisted = Boolean(updated);
            } catch (rehydratePersistError) {
              console.warn('[MLS] Failed to persist rehydrated group entry after decrypt', {
                groupId: restored.groupId,
                groupHandle: restored.groupHandle,
                error: rehydratePersistError,
              });
            }

            if (!persisted) {
              try {
                const latestGroups = await this.#loadGroups();
                latestGroups[restored.groupId] = {
                  ...(latestGroups[restored.groupId] || {}),
                  ...restored,
                  needsRecovery: false,
                  recoveryReason: null,
                  rehydrateBlockedUntil: null,
                };
                await this.#saveGroups(latestGroups);
              } catch (persistFallbackError) {
                console.warn(
                  '[MLS] Failed to store rehydrated group entry fallback after decrypt',
                  {
                    groupId: restored.groupId,
                    groupHandle: restored.groupHandle,
                    error: persistFallbackError,
                  },
                );
              }
            }

            return this.decrypt({
              groupId: restored.groupId,
              groupHandle: restored.groupHandle,
              ciphertext,
              serverSeq,
              serverMsgId,
              epochHint,
              allowRehydrate: false,
              traceId: effectiveTraceId,
            });
          }
          try {
            await this.#setRehydrateBlockedUntil(
              normalizedGroup.groupId,
              REHYDRATE_RETRY_BACKOFF_MS,
              'decrypt_unknown_group_failed',
            );
          } catch {}
        }

        try {
          await this.markGroupForRecovery(normalizedGroup.groupId, 'missing_runtime_group');
        } catch {}
        const wrapped = new Error('MLS group requires recovery');
        wrapped.code = 'MLS_GROUP_NEEDS_RECOVERY';
        wrapped.cause = error;
        wrapped.groupId = group.groupId;
        wrapped.groupHandle = group.groupHandle;
        wrapped.recoveryReason = 'missing_runtime_group';
        throw wrapped;
      }
      if (
        allowRehydrate &&
        (message.includes("message epoch differs from the group's epoch") ||
          message.includes('epoch differs from the group'))
      ) {
        trace('decrypt:epoch_mismatch_runtime_repair_start', {
          effectiveHandle,
        });
        const repaired = await this.#repairRuntimeAfterStaleHandle({
          groupInfo: normalizedGroup,
          groupId: normalizedGroup.groupId,
          groupHandle: normalizedGroup.groupHandle ?? groupHandle ?? null,
          runtimeHandle: effectiveHandle,
          conversationId: normalizedGroup.conversationId ?? null,
          source: 'decrypt',
          reason: 'decrypt_epoch_mismatch',
          traceId: effectiveTraceId,
          allowSyncWelcomes: true,
          allowJoinBundleRepair: true,
        }).catch(() => null);
        trace('decrypt:epoch_mismatch_runtime_repair_result', {
          repairedGroupId: repaired?.groupId || null,
          repairedGroupHandle: repaired?.groupHandle || null,
          repairedRuntimeHandle: repaired?.runtimeHandle || null,
          repairedReady: Boolean(repaired && this.#isGroupReady(repaired)),
          repairedNeedsRecovery: Boolean(repaired?.needsRecovery),
          repairedRecoveryReason: repaired?.recoveryReason || null,
        });
        if (repaired?.needsRecovery) {
          const wrapped = new Error('MLS group requires recovery');
          wrapped.code = 'MLS_GROUP_NEEDS_RECOVERY';
          wrapped.cause = error;
          wrapped.groupId = repaired.groupId || normalizedGroup.groupId || null;
          wrapped.groupHandle = repaired.groupHandle || normalizedGroup.groupHandle || null;
          wrapped.recoveryReason = repaired.recoveryReason || 'runtime_snapshot_mismatch';
          throw wrapped;
        }
        if (repaired?.groupId || repaired?.groupHandle) {
          return this.decrypt({
            groupId: repaired?.groupId || normalizedGroup.groupId,
            groupHandle: repaired?.groupHandle || normalizedGroup.groupHandle,
            ciphertext,
            serverSeq,
            serverMsgId,
            epochHint,
            allowNotReady,
            allowRehydrate: false,
            traceId: effectiveTraceId,
          });
        }
      }
      if (message.includes('sender data decryption error')) {
        trace('decrypt:sender_data_error');
        const wrapped = new Error('MLS sender data decryption error');
        wrapped.code = 'MLS_SENDER_DATA_DECRYPTION_ERROR';
        wrapped.cause = error;
        wrapped.groupId = group.groupId;
        wrapped.groupHandle = group.groupHandle;
        throw wrapped;
      }
      throw error;
    }

    const decryptedRatchetTreeB64 = pickBase64String(
      result?.ratchetTreeB64,
      result?.ratchet_tree_b64,
      result?.ratchetTree,
      result?.ratchet_tree,
    );

    if (decryptedRatchetTreeB64) {
      try {
        await this.#updateGroupEntry(normalizedGroup.groupId, (prev) => ({
          ...prev,
          ratchetTree: decryptedRatchetTreeB64,
          ratchetTreeB64: decryptedRatchetTreeB64,
        }));
      } catch {}
    }

    const nextCursor = parseCursor(serverSeq);

    if (nextCursor != null) {
      try {
        await this.#updateGroupEntry(normalizedGroup.groupId, (prev) => ({
          ...prev,
          ...buildProcessedServerSeqState(prev, nextCursor),
        }));
      } catch {}
    }

    trace('decrypt:success', {
      hasPlaintext: Boolean(result?.text),
      hasPlaintextB64: Boolean(result?.plaintext),
      ciphersuite: normalizedGroup.ciphersuite ?? null,
    });

    return {
      plaintext: result.text ?? null,
      plaintextBase64: result.plaintext,
      ciphersuite: normalizedGroup.ciphersuite,
    };
  }

  async joinGroup({
    identityId,
    welcome,
    welcomeB64,
    ratchetTree,
    ratchetTreeB64,
    groupId,
    welcomeSource = null,
    expectedEpoch = null,
    welcomeSuite: providedWelcomeSuite = null,
    allowJoinBundleRecovery = true,
    forceRejoin = false,
    allowMatchedRefIdentityRehydrate = true,
  }) {
    if (!welcome && !welcomeB64) throw new Error('welcome payload is required');

    await this.ensureInitialized();
    const identity = await this.ensureIdentity({});
    const canonicalIdentityId = this.#normalizeIdentityId(
      identity?.identityId || this.identityId || null,
    );
    const activeIdentityId = identityId || identity.identityId;
    if (!activeIdentityId) throw new Error('MLS identity is not initialised');

    let welcomeBytes = coerceUint8Array(welcome);
    let persistedWelcomeB64 = coerceBase64String(welcomeB64);
    if (!welcomeBytes && persistedWelcomeB64) {
      welcomeBytes = decodeBase64ToBytes(persistedWelcomeB64);
      if (!welcomeBytes) {
        persistedWelcomeB64 = null;
      }
    }
    if (!welcomeBytes) {
      throw new Error('welcome payload is required');
    }

    if (!persistedWelcomeB64) {
      try {
        persistedWelcomeB64 = toBase64(welcomeBytes);
      } catch {}
    }

    let ratchetTreeBytes = coerceUint8Array(ratchetTree);
    let persistedRatchetTreeB64 = coerceBase64String(ratchetTreeB64);
    if (!ratchetTreeBytes && persistedRatchetTreeB64) {
      ratchetTreeBytes = decodeBase64ToBytes(persistedRatchetTreeB64);
      if (!ratchetTreeBytes) {
        persistedRatchetTreeB64 = null;
      }
    }

    if (ratchetTreeBytes && !persistedRatchetTreeB64) {
      try {
        persistedRatchetTreeB64 = toBase64(ratchetTreeBytes);
      } catch {}
    }

    if (!persistedWelcomeB64) {
      throw new Error('welcome payload is required');
    }

    if (!persistedRatchetTreeB64) {
      persistedRatchetTreeB64 = null;
    }

    assertBase64Payload(persistedWelcomeB64, persistedRatchetTreeB64);

    const normalizedGroupId = groupId != null ? String(groupId) : null;
    let normalizedEpoch = expectedEpoch != null ? ensureEpoch(expectedEpoch) : null;
    const normalizedActiveIdentityId = this.#normalizeIdentityId(activeIdentityId);
    const persistedProtectedTargetDrift =
      normalizedGroupId && normalizedActiveIdentityId
        ? await this.#resolveProtectedWelcomeTargetDrift({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupId,
            identityId: normalizedActiveIdentityId,
          }).catch(() => null)
        : null;
    const protectedTargetDrift =
      persistedProtectedTargetDrift ||
      (normalizedActiveIdentityId && this._protectedWelcomeTargetDriftByIdentity instanceof Map
        ? this._protectedWelcomeTargetDriftByIdentity.get(normalizedActiveIdentityId) || null
        : null);
    let currentWelcomeRef = null;
    try {
      currentWelcomeRef = normalizeHexRefValue(extractWelcomeRefHexFromB64(persistedWelcomeB64));
    } catch {}
    let effectiveProtectedTargetDrift = protectedTargetDrift;
    if (
      !effectiveProtectedTargetDrift &&
      normalizedGroupId &&
      normalizedActiveIdentityId &&
      currentWelcomeRef
    ) {
      try {
        const groups = await this.#loadGroups().catch(() => null);
        const { entry: storedEntry } =
          groups && typeof groups === 'object'
            ? this.#resolveStoredGroup(groups, {
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupId,
              })
            : { entry: null };
        const storedTargetRef = normalizeHexRefValue(
          storedEntry?.welcomeTargetRefLock ?? storedEntry?.welcome_target_ref_lock ?? null,
        );
        const storedIdentityId = this.#normalizeIdentityId(
          storedEntry?.welcomeTargetIdentityId ??
            storedEntry?.welcome_target_identity_id ??
            storedEntry?.identityId ??
            storedEntry?.identity_id ??
            null,
        );
        if (
          storedTargetRef &&
          welcomeContainsKeyPackageRefHex(persistedWelcomeB64, storedTargetRef) &&
          (!storedIdentityId || storedIdentityId === normalizedActiveIdentityId)
        ) {
          const localExport = await this.exportKeyPackage(normalizedActiveIdentityId).catch(
            () => null,
          );
          const localKeyPackageB64 = localExport?.keyPackage || null;
          const localRef = localKeyPackageB64
            ? await computeKeyPackageRefHexFromB64(
                localKeyPackageB64,
                normalizeSuiteId(
                  localExport?.ciphersuite ??
                    storedEntry?.ciphersuite ??
                    providedWelcomeSuite ??
                    this.preferredCiphersuite ??
                    null,
                ) ?? null,
              ).catch(() => null)
            : null;
          if (localRef && localRef !== storedTargetRef) {
            effectiveProtectedTargetDrift = {
              identityId: normalizedActiveIdentityId,
              groupId: ensureString(storedEntry?.groupId ?? normalizedGroupId) ?? normalizedGroupId,
              groupHandle:
                ensureString(storedEntry?.groupHandle ?? normalizedGroupId) ?? normalizedGroupId,
              conversationId:
                ensureString(storedEntry?.conversationId ?? storedEntry?.conversation_id ?? null) ??
                null,
              targetRef: storedTargetRef,
              currentLocalRef: localRef,
              ciphersuite:
                normalizeSuiteId(
                  storedEntry?.ciphersuite ??
                    localExport?.ciphersuite ??
                    providedWelcomeSuite ??
                    this.preferredCiphersuite ??
                    null,
                ) ?? null,
              source: 'persisted_target_lock',
              detectedAt: Date.now(),
            };
            this._protectedWelcomeTargetDriftByIdentity.set(
              normalizedActiveIdentityId,
              effectiveProtectedTargetDrift,
            );
            await this.#persistProtectedWelcomeTargetDrift({
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupId,
              conversationId: effectiveProtectedTargetDrift?.conversationId ?? null,
              identityId: normalizedActiveIdentityId,
              targetRef: storedTargetRef,
              currentLocalRef: localRef,
              ciphersuite: effectiveProtectedTargetDrift?.ciphersuite ?? null,
              source: 'persisted_target_lock',
            }).catch(() => null);
          }
        }
      } catch {}
    }

    if (effectiveProtectedTargetDrift && normalizedActiveIdentityId && persistedWelcomeB64) {
      const driftTargetRef = normalizeHexRefValue(effectiveProtectedTargetDrift?.targetRef ?? null);
      const driftCurrentLocalRef = normalizeHexRefValue(
        effectiveProtectedTargetDrift?.currentLocalRef ?? null,
      );
      const driftGroupId = ensureString(effectiveProtectedTargetDrift?.groupId ?? null) ?? null;
      const driftGroupHandle =
        ensureString(effectiveProtectedTargetDrift?.groupHandle ?? null) ?? null;
      const driftAppliesToGroup =
        !normalizedGroupId ||
        !driftGroupId ||
        normalizedGroupId === driftGroupId ||
        normalizedGroupId === driftGroupHandle;
      const driftMatchesWelcome = Boolean(driftTargetRef)
        ? welcomeContainsKeyPackageRefHex(persistedWelcomeB64, driftTargetRef)
        : false;
      const driftRequiresFreshBundle =
        driftMatchesWelcome &&
        Boolean(driftCurrentLocalRef) &&
        driftCurrentLocalRef !== driftTargetRef;
      if (driftAppliesToGroup && driftRequiresFreshBundle && normalizedGroupId) {
        try {
          console.info(
            '[MLS] Protected welcome target drift requires fresh current-local recovery; skipping seed-based identity realignment',
            {
              groupId: normalizedGroupId,
              identityId: normalizedActiveIdentityId,
              staleTargetRef: driftTargetRef,
              currentLocalRef: driftCurrentLocalRef,
              source: effectiveProtectedTargetDrift?.source ?? null,
            },
          );
        } catch {}
        try {
          console.info(
            '[MLS] preflighting stale protected welcome target against current local key package',
            {
              groupId: normalizedGroupId,
              identityId: normalizedActiveIdentityId,
              staleTargetRef: driftTargetRef,
              currentLocalRef: driftCurrentLocalRef,
              source: effectiveProtectedTargetDrift?.source ?? null,
            },
          );
        } catch {}
        try {
          await this.#clearPersistedWelcomeState({
            groupId: normalizedGroupId,
            clearRecoveryPayload: true,
            preserveWelcomeTargetLock: false,
          });
        } catch {}
        try {
          const localExport = await this.exportKeyPackage(normalizedActiveIdentityId).catch(
            () => null,
          );
          const currentKeyPackageB64 = localExport?.keyPackage || null;
          const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
          if (currentKeyPackageB64) {
            const fetched = await this.#fetchJoinBundleWithRecovery({
              groupId: normalizedGroupId,
              identityId: normalizedActiveIdentityId,
              keyPackageB64: currentKeyPackageB64,
              token: __t ? `Bearer ${__t}` : undefined,
              ciphersuite:
                normalizeSuiteId(
                  effectiveProtectedTargetDrift?.ciphersuite ??
                    providedWelcomeSuite ??
                    this.preferredCiphersuite ??
                    null,
                ) ?? null,
              context: 'join_group_protected_target_drift_preflight',
              preferReissueOnAlreadyMemberNoWelcome: true,
              preferCurrentLocalKeyPackage: true,
            }).catch(() => null);
            if (fetched?.blocked) {
              return null;
            }
            const bundle = fetched?.bundle ?? null;
            const bundleWelcome =
              ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
            const bundleTree =
              ensureString(bundle?.ratchet_tree_b64) ||
              ensureString(bundle?.ratchetTreeB64) ||
              null;
            const bundleEpoch = bundle?.epoch != null ? ensureEpoch(bundle.epoch) : normalizedEpoch;
            if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
              this._protectedWelcomeTargetDriftByIdentity.delete(normalizedActiveIdentityId);
              await this.#clearProtectedWelcomeTargetDrift({
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupId,
                identityId: normalizedActiveIdentityId,
              }).catch(() => null);
              const recoveredNoWelcome = await this.#recoverAlreadyMemberNoWelcomeRuntime({
                groupId: normalizedGroupId,
                identityId: fetched?.identityId || normalizedActiveIdentityId,
                ciphersuite:
                  normalizeSuiteId(
                    effectiveProtectedTargetDrift?.ciphersuite ??
                      providedWelcomeSuite ??
                      this.preferredCiphersuite ??
                      null,
                  ) ?? null,
                source: welcomeSource || 'joinGroup',
                reason: 'protected_target_drift_already_member_no_welcome',
              }).catch(() => null);
              if (recoveredNoWelcome) {
                return recoveredNoWelcome;
              }
            }
            if (bundleWelcome && bundleEpoch != null) {
              await this.#persistWelcomeTarget({
                groupId: normalizedGroupId,
                identityId: fetched?.identityId || normalizedActiveIdentityId,
                welcomeB64: bundleWelcome,
                ratchetTreeB64: bundleTree,
                epoch: bundleEpoch,
                usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
                source: 'protected_target_drift_preflight',
              }).catch(() => null);
              persistedWelcomeB64 = bundleWelcome;
              welcomeBytes = decodeBase64ToBytes(bundleWelcome) || welcomeBytes;
              normalizedEpoch = bundleEpoch;
              if (bundleTree) {
                persistedRatchetTreeB64 = bundleTree;
                ratchetTreeBytes = decodeBase64ToBytes(bundleTree) || ratchetTreeBytes;
              }
              this._protectedWelcomeTargetDriftByIdentity.delete(normalizedActiveIdentityId);
              await this.#clearProtectedWelcomeTargetDrift({
                groupId: normalizedGroupId,
                groupHandle: normalizedGroupId,
                identityId: normalizedActiveIdentityId,
              }).catch(() => null);
            }
          }
        } catch {}
      }
    }

    if (normalizedGroupId && forceRejoin === true) {
      await this.#dropStaleRuntimeGroupsForGroupId(normalizedGroupId, {
        keepHandle: null,
        source: 'joinGroup',
        reason: 'force_rejoin',
      }).catch(() => null);
      await this.#updateGroupEntry(normalizedGroupId, (prev) => ({
        ...(prev || {}),
        runtimeHandle: null,
        runtimeEpoch: null,
        runtimeLive: false,
        runtime_live: false,
      })).catch(() => null);
    }

    const tryResolveRuntimeReadyGroup = async ({ checkpoint } = {}) => {
      if (!normalizedGroupId) {
        return null;
      }
      try {
        const groups = await this.#loadGroups();
        let existingEntry = groups[normalizedGroupId] || null;
        let existingStorageKey = existingEntry ? normalizedGroupId : null;

        if (!existingEntry) {
          const byHandle = Object.entries(groups).find(
            ([, item]) => item?.groupHandle === normalizedGroupId,
          );
          if (byHandle) {
            existingStorageKey = byHandle[0];
            existingEntry = byHandle[1];
          }
        }

        const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
        const runtimeGroupById =
          runtimeGroups.find(
            (item) => item?.groupId != null && String(item.groupId) === String(normalizedGroupId),
          ) || null;

        if (!existingEntry && runtimeGroupById) {
          const byRuntimeHandle = Object.entries(groups).find(
            ([, item]) => item?.groupHandle === runtimeGroupById.groupHandle,
          );
          if (byRuntimeHandle) {
            existingStorageKey = byRuntimeHandle[0];
            existingEntry = byRuntimeHandle[1];
          } else {
            existingEntry = {
              groupId: normalizedGroupId,
              groupHandle: runtimeGroupById.groupHandle || normalizedGroupId,
              runtimeHandle: runtimeGroupById.groupHandle || null,
              ciphersuite:
                normalizeSuiteId(runtimeGroupById.ciphersuite ?? null) ??
                this.preferredCiphersuite ??
                null,
              identityId: activeIdentityId,
              members: resolvePreservedMembers(groups, {
                groupId: normalizedGroupId,
                groupHandle: runtimeGroupById.groupHandle || normalizedGroupId,
                identityId: activeIdentityId,
              }),
              epoch: normalizedEpoch ?? 1,
            };
          }
        }

        if (!existingEntry) {
          return null;
        }

        // During explicit recovery we must rebuild from the latest welcome/join bundle
        // instead of short-circuiting to an already-running (possibly stale) runtime group.
        if (forceRejoin === true || existingEntry?.needsRecovery === true) {
          return null;
        }

        const existingEpoch = normalizeEpochOrNull(existingEntry?.epoch ?? null);
        const epochSatisfied =
          normalizedEpoch == null || (existingEpoch != null && existingEpoch >= normalizedEpoch);
        const existingIdentityId = this.#normalizeIdentityId(existingEntry?.identityId ?? null);
        const existingMembers = normalizeMemberList(existingEntry?.members);
        const identitySatisfied =
          !normalizedActiveIdentityId ||
          !existingIdentityId ||
          existingIdentityId === normalizedActiveIdentityId ||
          existingMembers.includes(normalizedActiveIdentityId);

        if (!epochSatisfied || !identitySatisfied) {
          return null;
        }

        const runtimeHandleCandidates = new Set(
          [
            ensureString(existingEntry?.runtimeHandle ?? null),
            ensureString(existingEntry?.groupHandle ?? null),
            ensureString(existingEntry?.groupId ?? null),
            normalizedGroupId,
          ].filter(Boolean),
        );
        const runtimeGroupId = ensureString(existingEntry?.groupId ?? normalizedGroupId);
        const matchedRuntime =
          runtimeGroups.find((item) => {
            if (!item) return false;
            const handle = ensureString(item.groupHandle ?? null);
            const gid = ensureString(item.groupId ?? null);
            if (handle && runtimeHandleCandidates.has(handle)) return true;
            if (gid && runtimeGroupId && gid === runtimeGroupId) return true;
            if (gid && gid === normalizedGroupId) return true;
            return false;
          }) || null;

        if (!matchedRuntime) {
          return null;
        }

        const matchedRuntimeHandle = ensureString(matchedRuntime.groupHandle ?? null);
        const matchedRuntimeGroupId =
          ensureString(matchedRuntime.groupId ?? null) ??
          ensureString(existingEntry?.groupId ?? null) ??
          normalizedGroupId;
        const nextEpoch = (() => {
          const runtimeEpoch = normalizeEpochOrNull(matchedRuntime.epoch ?? null);
          if (runtimeEpoch != null && existingEpoch != null) {
            return Math.max(runtimeEpoch, existingEpoch);
          }
          if (runtimeEpoch != null) return runtimeEpoch;
          if (existingEpoch != null) return existingEpoch;
          if (normalizedEpoch != null) return normalizedEpoch;
          return 1;
        })();
        const hydratedEntry = {
          ...existingEntry,
          groupId: matchedRuntimeGroupId,
          groupHandle:
            matchedRuntimeHandle ??
            ensureString(existingEntry?.groupHandle ?? null) ??
            ensureString(existingEntry?.runtimeHandle ?? null) ??
            matchedRuntimeGroupId,
          runtimeHandle:
            matchedRuntimeHandle ??
            ensureString(existingEntry?.runtimeHandle ?? null) ??
            ensureString(existingEntry?.groupHandle ?? null) ??
            null,
          ciphersuite:
            normalizeSuiteId(matchedRuntime.ciphersuite ?? null) ??
            normalizeSuiteId(existingEntry?.ciphersuite ?? null) ??
            this.preferredCiphersuite ??
            null,
          identityId: existingEntry?.identityId ?? activeIdentityId,
          members: existingMembers.length ? existingMembers : ensureArray([activeIdentityId]),
          epoch: nextEpoch,
          joined: true,
          fetchedAt: Date.now(),
          needsRecovery: false,
          recoveryReason: null,
        };
        const finalKey = hydratedEntry.groupId ?? normalizedGroupId;
        const nextGroups = { ...groups };
        nextGroups[finalKey] = { ...(nextGroups[finalKey] || {}), ...hydratedEntry };
        if (existingStorageKey && existingStorageKey !== finalKey) {
          delete nextGroups[existingStorageKey];
        }
        await this.#saveGroups(nextGroups);
        if (checkpoint) {
          await this.#debugKeyStoreCheckpoint(checkpoint, {
            identityId: activeIdentityId,
            groupId: hydratedEntry.groupId ?? normalizedGroupId,
            welcomeB64: persistedWelcomeB64,
            ciphersuite: hydratedEntry.ciphersuite ?? this.preferredCiphersuite ?? null,
          });
        }
        return this.#finalizeGroupReady(hydratedEntry);
      } catch {
        return null;
      }
    };

    // If this welcome is being processed twice (parallel snapshot refresh + pending sync),
    // avoid consuming the same key package again once the runtime group already exists.
    if (normalizedGroupId && forceRejoin !== true) {
      const alreadyReady = await tryResolveRuntimeReadyGroup({
        checkpoint: 'skip_join_group_runtime_present',
      });
      if (alreadyReady) {
        return alreadyReady;
      }
    }

    let welcomeSuite = normalizeSuiteId(providedWelcomeSuite);
    let welcomeSuiteError = null;
    if (welcomeSuite == null) {
      let decoded = null;
      try {
        decoded = await Promise.resolve(decodeWelcomeCiphersuite(welcomeBytes));
      } catch {
        decoded = null;
      }
      if (decoded && Number.isFinite(decoded.ciphersuite)) {
        welcomeSuite = decoded.ciphersuite;
      } else if (decoded?.error) {
        welcomeSuiteError = decoded.error;
      }
    }

    if (welcomeSuiteError) {
      console.warn('[MLS] Failed to decode welcome ciphersuite before join', {
        groupId: normalizedGroupId,
        error: welcomeSuiteError,
      });
    }

    const supportedSuitesRaw = await getSupportedCiphersuites();
    const supportedSuites = Array.isArray(supportedSuitesRaw) ? supportedSuitesRaw : [];
    const welcomeSuiteLabel = this.#resolveSuiteLabel(welcomeSuite);

    this.#updateRuntimeSuiteMetadata({
      suites: supportedSuites,
      suiteLabels: supportedSuites.map((suite) => this.#resolveSuiteLabel(suite)),
      provider: this.runtimeProvider,
    });

    const telemetryBase = {
      group_id: normalizedGroupId,
      epoch: normalizedEpoch,
      path: welcomeSource || null,
      suite_id: welcomeSuite ?? null,
      suite_label: welcomeSuiteLabel,
      provider: this.runtimeProvider || DEFAULT_MLS_PROVIDER,
    };

    if (
      welcomeSuite != null &&
      supportedSuites.length > 0 &&
      !supportedSuites.includes(welcomeSuite)
    ) {
      const friendlyMessage = `Unsupported MLS ciphersuite (${welcomeSuite}). Please update the app.`;
      const error = new Error(friendlyMessage);
      error.code = 'unsupported_ciphersuite';
      error.ciphersuite = welcomeSuite;
      error.details = {
        supportedSuites,
        supportedSuiteLabels:
          this.runtimeSupportedSuiteLabels?.filter((label) => typeof label === 'string' && label) ||
          [],
        welcomeSource,
        welcomeSuiteLabel,
        welcomeSuiteError: welcomeSuiteError
          ? welcomeSuiteError.message || String(welcomeSuiteError)
          : null,
      };
      console.error('[MLS] joinGroup blocked due to unsupported ciphersuite', {
        groupId: normalizedGroupId,
        suite: welcomeSuite,
        supportedSuites,
      });
      pushMlsTelemetry('mls.join_with_welcome', {
        ...telemetryBase,
        status: 'failure',
        reason: 'unsupported_ciphersuite',
        suite_id: welcomeSuite,
      });
      throw error;
    }

    if (welcomeBytes && welcomeBytes.length > 0 && typeof console?.debug === 'function') {
      const previewBytes = welcomeBytes.slice(0, Math.min(4, welcomeBytes.length));
      const previewHex = Array.from(previewBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join(' ');
      if (previewHex) {
        console.debug('[MLS] welcome head:', previewHex);
      }
    }

    const workerPayload = {
      identityId: activeIdentityId,
      groupId,
      welcomeB64: persistedWelcomeB64,
      ratchetTreeB64: persistedRatchetTreeB64 ?? null,
      // Join-time identity/key-package mutation must stay under session-manager control.
      // The worker should stage the current welcome, not silently recreate the identity.
      allowIdentityRestore: false,
    };

    if (ratchetTreeBytes && ratchetTreeBytes.length) {
      workerPayload.ratchetTreeBytes = ratchetTreeBytes;
    }

    if (typeof globalThis !== 'undefined' && globalThis.__MLS_DEBUG_KEYPACKAGE__ === true) {
      try {
        const suiteHint = welcomeSuite ?? this.preferredCiphersuite ?? null;
        const cachedKeyPackageB64 = (() => {
          try {
            const ls = getLocalStorageSafe();
            if (!ls || !activeIdentityId || suiteHint == null) return null;
            const key = `mls:keypkg:${activeIdentityId}:${suiteHint}`;
            const raw = ls.getItem(key);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return typeof parsed?.key_package_b64 === 'string' ? parsed.key_package_b64 : null;
          } catch {
            return null;
          }
        })();
        const [workerExport, welcomeRef] = await Promise.all([
          workerExportKeyPackage({ identityId: activeIdentityId }).catch(() => null),
          Promise.resolve(
            resolveMatchingWelcomeRefHex(
              persistedWelcomeB64,
              activeIdentityId ? [activeIdentityId] : [],
            ),
          ),
        ]);
        const workerRef = workerExport?.keyPackage
          ? await computeKeyPackageRefHexFromB64(workerExport.keyPackage, suiteHint)
          : null;
        const cachedRef = cachedKeyPackageB64
          ? await computeKeyPackageRefHexFromB64(cachedKeyPackageB64, suiteHint)
          : null;
        console.debug('[MLS DEBUG] key package refs (worker vs cache vs welcome)', {
          identityId: activeIdentityId,
          suite: suiteHint,
          workerRef,
          cachedRef,
          welcomeRef,
        });
      } catch {}
    }

    if (allowJoinBundleRecovery !== false && normalizedGroupId && activeIdentityId) {
      try {
        const localExportForPreflight = await this.exportKeyPackage(activeIdentityId).catch(
          () => null,
        );
        const localKeyPackageB64ForPreflight = localExportForPreflight?.keyPackage || null;
        const localRefForPreflight = localKeyPackageB64ForPreflight
          ? await computeKeyPackageRefHexFromB64(
              localKeyPackageB64ForPreflight,
              welcomeSuite ?? this.preferredCiphersuite ?? null,
            ).catch(() => null)
          : null;
        const expectedWelcomeRefForPreflight = normalizeHexRefValue(
          effectiveProtectedTargetDrift?.targetRef ??
            this.#resolveWelcomeTargetRef({
              welcomeB64: persistedWelcomeB64,
              usedKpRef: null,
            }) ??
            null,
        );
        if (
          localKeyPackageB64ForPreflight &&
          localRefForPreflight &&
          expectedWelcomeRefForPreflight &&
          localRefForPreflight !== expectedWelcomeRefForPreflight
        ) {
          try {
            console.info('[MLS] preflighting welcome ref mismatch before worker join', {
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              localRef: localRefForPreflight,
              expectedWelcomeRef: expectedWelcomeRefForPreflight,
              welcomeSource: welcomeSource || null,
              protectedTargetSource: effectiveProtectedTargetDrift?.source ?? null,
            });
          } catch {}
          const recovered = await this.#recoverJoinAgainstCurrentLocalKeyPackage({
            groupId: normalizedGroupId,
            identityId: activeIdentityId,
            keyPackageB64: localKeyPackageB64ForPreflight,
            ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
            welcomeSource,
            expectedEpoch: normalizedEpoch,
            context: 'join_group_preflight_welcome_ref_mismatch',
            reason: 'preflight_welcome_ref_mismatch',
          }).catch(() => null);
          if (recovered) {
            return recovered;
          }
          return null;
        }
      } catch {}
    }

    await this.#debugKeyStoreCheckpoint('pre_join_group', {
      identityId: activeIdentityId,
      groupId: normalizedGroupId || groupId || null,
      welcomeB64: persistedWelcomeB64,
      ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
    });

    const joinGroupKey =
      normalizedGroupId ||
      (typeof groupId === 'string' && groupId) ||
      (workerPayload.groupId != null ? String(workerPayload.groupId) : null) ||
      'unknown-group';
    let result;
    let joinError = null;
    const runJoinWithTimeout = async (payload, timeoutMs = JOIN_GROUP_WORKER_TIMEOUT_MS) => {
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          const err = new Error('MLS join timeout');
          err.code = 'MLS_JOIN_TIMEOUT';
          try {
            shutdownMlsWorker();
          } catch {}
          reject(err);
        }, timeoutMs);
      });
      try {
        return await Promise.race([workerJoinGroup(payload), timeoutPromise]);
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    await this.withKeyPackageLock(activeIdentityId, async () => {
      try {
        if (forceRejoin !== true) {
          const alreadyReadyInLock = await tryResolveRuntimeReadyGroup({
            checkpoint: 'skip_join_group_runtime_present_locked',
          });
          if (alreadyReadyInLock) {
            result = alreadyReadyInLock;
            joinError = null;
            return;
          }
        }
        try {
          console.info('[MLS JOIN DEBUG] start', {
            groupId: normalizedGroupId || groupId || null,
            identityId: activeIdentityId,
            welcomeBytes: welcomeBytes?.length ?? 0,
            hasRatchetTree: Boolean(persistedRatchetTreeB64),
          });
        } catch {}
        const joinTask = () => runJoinWithTimeout(workerPayload);
        result = await this.#singleFlightWithBackoff(
          this.joinGroupFlights,
          this.joinGroupRetryState,
          `join:${joinGroupKey}`,
          joinTask,
          { baseDelay: 750, maxDelay: 20000, jitter: 0.4 },
        );
        try {
          console.info('[MLS JOIN DEBUG] done', {
            groupId: normalizedGroupId || groupId || null,
            epoch: result?.epoch ?? null,
            members: Array.isArray(result?.members) ? result.members.length : null,
          });
        } catch {}
      } catch (error) {
        joinError = error;
      }
    });

    if (result?.ready === true && !joinError) {
      return result;
    }

    if (
      joinError?.code === 'MLS_JOIN_TIMEOUT' &&
      (workerPayload.ratchetTreeBytes || workerPayload.ratchetTreeB64)
    ) {
      try {
        console.warn('[MLS JOIN] timeout; retrying without ratchet tree', {
          groupId: normalizedGroupId || groupId || null,
        });
      } catch {}
      const fallbackPayload = {
        identityId: workerPayload.identityId,
        groupId: workerPayload.groupId,
        welcomeB64: workerPayload.welcomeB64,
      };
      try {
        result = await runJoinWithTimeout(fallbackPayload, 4000);
        joinError = null;
        try {
          console.info('[MLS JOIN DEBUG] done (no-tree)', {
            groupId: normalizedGroupId || groupId || null,
            epoch: result?.epoch ?? null,
            members: Array.isArray(result?.members) ? result.members.length : null,
          });
        } catch {}
      } catch (fallbackError) {
        joinError = fallbackError;
      }
    }

    if (joinError) {
      let isNoMatchingKey = false;
      try {
        const message = String(joinError?.message || '').toLowerCase();
        isNoMatchingKey =
          message.includes('no matching key package') || message.includes('missing key package');
      } catch {}
      if (isNoMatchingKey && normalizedGroupId && forceRejoin !== true) {
        const alreadyReadyAfterMissingKey = await tryResolveRuntimeReadyGroup({
          checkpoint: 'skip_join_group_missing_key_runtime_present',
        });
        if (alreadyReadyAfterMissingKey) {
          return alreadyReadyAfterMissingKey;
        }
      }
      if (isNoMatchingKey && activeIdentityId) {
        try {
          let exportedForDetails = null;
          let localRefForDetails = null;
          let welcomeRefForDetails = null;
          let persistedExpectedWelcomeRef = null;
          try {
            exportedForDetails = await this.exportKeyPackage(activeIdentityId);
            localRefForDetails = await computeKeyPackageRefHexFromB64(
              exportedForDetails?.keyPackage || null,
              welcomeSuite ?? null,
            );
          } catch {}
          try {
            if (normalizedGroupId) {
              const groups = await this.#loadGroups().catch(() => null);
              const { entry: storedEntry } = this.#resolveStoredGroup(groups, {
                groupId: normalizedGroupId,
                groupHandle:
                  ensureString(groupId) ??
                  ensureString(workerPayload.groupId ?? null) ??
                  normalizedGroupId,
              });
              persistedExpectedWelcomeRef = normalizeHexRefValue(
                storedEntry?.welcomeTargetRefLock ??
                  storedEntry?.welcome_target_ref_lock ??
                  storedEntry?.used_kp_ref ??
                  storedEntry?.usedKpRef ??
                  this.#resolveWelcomeTargetRef({
                    welcomeB64: pickBase64String(
                      storedEntry?.recoveryWelcomeB64,
                      storedEntry?.recovery_welcome_b64,
                      storedEntry?.welcomeB64,
                      storedEntry?.welcome_b64,
                      storedEntry?.welcome,
                      null,
                    ),
                    usedKpRef: storedEntry?.used_kp_ref ?? storedEntry?.usedKpRef ?? null,
                  }) ??
                  null,
              );
            }
          } catch {}
          try {
            welcomeRefForDetails =
              persistedExpectedWelcomeRef ??
              resolveMatchingWelcomeRefHex(persistedWelcomeB64, [localRefForDetails]);
          } catch {}
          const privateKeyReferenceForDetails = activeIdentityId
            ? `keystore:${String(activeIdentityId)}:${String(welcomeSuite ?? 'unknown')}`
            : null;
          const existingJoinErrorDetails =
            joinError?.details && typeof joinError.details === 'object' ? joinError.details : {};
          joinError.details = {
            ...existingJoinErrorDetails,
            publicKeyReference:
              localRefForDetails ?? existingJoinErrorDetails.publicKeyReference ?? null,
            welcomePublicKeyReference:
              welcomeRefForDetails ?? existingJoinErrorDetails.welcomePublicKeyReference ?? null,
            privateKeyReference: privateKeyReferenceForDetails,
            keyReferenceMatch:
              localRefForDetails && welcomeRefForDetails
                ? localRefForDetails === welcomeRefForDetails
                : (existingJoinErrorDetails.keyReferenceMatch ?? null),
          };
          try {
            const workerHealth = await this.#getWorkerKeyPackageHealth({
              identityId: activeIdentityId,
              includeKeyPackage: true,
              expectedRef: welcomeRefForDetails,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
            });
            if (workerHealth) {
              joinError.details = {
                ...(joinError?.details || {}),
                identityHealth: {
                  identityFound: workerHealth.identityFound ?? false,
                  identityHandleAlive: workerHealth.identityHandleAlive ?? false,
                  keyPackageHandleAlive: workerHealth.keyPackageHandleAlive ?? false,
                  keyPackageExportable: workerHealth.keyPackageExportable ?? false,
                  keyPackageProvenance: workerHealth.keyPackageProvenance ?? null,
                  welcomeStageReady: workerHealth.welcomeStageReady ?? null,
                  welcomeStageReason: workerHealth.welcomeStageReason ?? null,
                  privateMaterialReady: workerHealth.privateMaterialReady ?? false,
                  healthCode: workerHealth.healthCode ?? workerHealth.diagnosticCode ?? null,
                },
                keyPackageHealth: {
                  publishedKeyPackageRef:
                    workerHealth.publishedKeyPackageRef ??
                    joinError.details?.publicKeyReference ??
                    null,
                  currentLocalKeyPackageRef:
                    workerHealth.currentLocalKeyPackageRef ??
                    joinError.details?.publicKeyReference ??
                    null,
                  authoritativePublishedKeyPackageRef:
                    workerHealth.authoritativePublishedKeyPackageRef ?? null,
                  expectedKeyPackageRef:
                    workerHealth.expectedKeyPackageRef ??
                    joinError.details?.welcomePublicKeyReference ??
                    null,
                  publishedKeyPackageRefMatchesExpected:
                    workerHealth.publishedKeyPackageRefMatchesExpected ??
                    joinError.details?.keyReferenceMatch ??
                    null,
                  currentLocalKeyPackageRefMatchesExpected:
                    workerHealth.currentLocalKeyPackageRefMatchesExpected ??
                    joinError.details?.keyReferenceMatch ??
                    null,
                  authoritativePublishedKeyPackageRefMatchesExpected:
                    workerHealth.authoritativePublishedKeyPackageRefMatchesExpected ?? null,
                  currentLocalKeyPackageRefMatchesPublished:
                    workerHealth.currentLocalKeyPackageRefMatchesPublished ?? null,
                  keyPackageBytesLength: workerHealth.keyPackageBytesLength ?? 0,
                  keyPackageHandleAlive: workerHealth.keyPackageHandleAlive ?? false,
                  keyPackageExportable: workerHealth.keyPackageExportable ?? false,
                  keyPackageProvenance: workerHealth.keyPackageProvenance ?? null,
                  welcomeStageReady: workerHealth.welcomeStageReady ?? null,
                  welcomeStageReason: workerHealth.welcomeStageReason ?? null,
                  privateMaterialReady: workerHealth.privateMaterialReady ?? false,
                  healthCode: workerHealth.healthCode ?? workerHealth.diagnosticCode ?? null,
                },
              };
            }
          } catch {}
        } catch {}
      }
      try {
        console.warn('[MLS] joinGroup failed', {
          groupId: normalizedGroupId || groupId || null,
          identityId: activeIdentityId,
          code: joinError?.code || null,
          message: joinError?.message || String(joinError || ''),
          details: joinError?.details || null,
          ciphersuite: joinError?.ciphersuite ?? null,
        });
      } catch {}
      await this.#debugKeyStoreCheckpoint('join_failed_before_recovery', {
        identityId: activeIdentityId,
        groupId: normalizedGroupId || groupId || null,
        welcomeB64: persistedWelcomeB64,
        ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
      });
      if (joinError?.code === 'MLS_JOIN_TIMEOUT') {
        try {
          await this.markGroupForRecovery(
            normalizedGroupId || joinGroupKey || null,
            'mls_join_timeout',
          );
        } catch {}
        joinError.groupId = normalizedGroupId || joinGroupKey || null;
        joinError.recoveryReason = joinError.recoveryReason || 'mls_join_timeout';
        joinError.code = 'MLS_GROUP_NEEDS_RECOVERY';
        throw joinError;
      }
      const errorSuite = normalizeSuiteId(joinError?.ciphersuite) ?? welcomeSuite ?? null;
      let reason = 'unknown';
      if (joinError?.code === 'unsupported_ciphersuite') {
        reason = 'unsupported_ciphersuite';
      } else if (joinError?.code === 'ciphersuite_mismatch') {
        reason = 'mismatch';
      } else if (!welcomeSuite && welcomeSuiteError) {
        reason = 'decode_error';
      }

      if (joinError?.code === 'identity_restore_blocked' && normalizedGroupId && activeIdentityId) {
        try {
          console.warn(
            '[MLS] worker blocked implicit identity restore during welcome staging; switching to manager-owned re-enrollment',
            {
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              welcomeSource: welcomeSource || null,
            },
          );
        } catch {}
        try {
          this._protectedWelcomeTargetDriftByIdentity.delete(normalizedActiveIdentityId);
          await this.#clearProtectedWelcomeTargetDrift({
            groupId: normalizedGroupId,
            groupHandle: normalizedGroupId,
            identityId: normalizedActiveIdentityId,
          }).catch(() => null);
        } catch {}
        try {
          await this.#clearPersistedWelcomeState({
            groupId: normalizedGroupId,
            clearRecoveryPayload: true,
            preserveWelcomeTargetLock: false,
          });
        } catch {}
        try {
          await this.publishKeyPackageAndSync(activeIdentityId, {
            forcePublish: true,
            retryOnMissingClientId: false,
            overrideWelcomeTargetLock: true,
          }).catch(() => null);
          const exportedForRecovery = await this.exportKeyPackage(activeIdentityId).catch(
            () => null,
          );
          const recovered = await this.#recoverJoinAgainstCurrentLocalKeyPackage({
            groupId: normalizedGroupId,
            identityId: activeIdentityId,
            keyPackageB64: exportedForRecovery?.keyPackage || null,
            ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
            welcomeSource,
            expectedEpoch: normalizedEpoch,
            context: 'join_group_identity_restore_blocked',
            reason: 'identity_restore_blocked',
          }).catch(() => null);
          if (recovered) {
            return recovered;
          }
        } catch {}
      }

      pushMlsTelemetry('mls.join_with_welcome', {
        ...telemetryBase,
        suite_id: errorSuite,
        status: 'failure',
        reason,
        error_code: joinError?.code || null,
      });

      // Heuristic recovery: if backend references a different key package than our cache,
      // force a republish and re-sync welcomes once, then retry join. Additionally, if the
      // worker reports "No matching key package", give the local store time to persist the
      // freshly published key package and then retry with a refreshed Welcome from snapshot.
      if (isNoMatchingKey && activeIdentityId) {
        let staleBootstrapWelcomeDetected = false;
        if (normalizedGroupId && normalizedEpoch != null) {
          try {
            const groups = await this.#loadGroups().catch(() => null);
            const storedEntry =
              (groups &&
                (groups[normalizedGroupId] ||
                  Object.values(groups).find(
                    (item) =>
                      item?.groupId === normalizedGroupId ||
                      item?.groupHandle === normalizedGroupId,
                  ))) ||
              null;
            const storedEpoch = normalizeEpochOrNull(
              storedEntry?.epoch ?? storedEntry?.current_epoch ?? storedEntry?.currentEpoch ?? null,
            );
            staleBootstrapWelcomeDetected = storedEpoch != null && normalizedEpoch < storedEpoch;
          } catch {}
        }
        if (staleBootstrapWelcomeDetected && normalizedGroupId) {
          try {
            console.info('[MLS] Dropping stale bootstrap welcome before missing-key recovery', {
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              welcomeEpoch: normalizedEpoch ?? null,
            });
          } catch {}
          try {
            await this.#clearPersistedWelcomeState({
              groupId: normalizedGroupId,
              clearRecoveryPayload: true,
              preserveWelcomeTargetLock: false,
            });
          } catch {}
          try {
            const refreshed = await this.syncWelcomes({
              groupId: normalizedGroupId,
              __pipelineBypass: true,
            }).catch(() => null);
            const recovered =
              Array.isArray(refreshed?.joined) && refreshed.joined.length
                ? refreshed.joined.find(
                    (item) =>
                      item?.groupId === normalizedGroupId ||
                      item?.groupHandle === normalizedGroupId,
                  ) || refreshed.joined[0]
                : null;
            if (recovered) {
              return recovered;
            }
          } catch {}
        }
        const reportedLocalRef = normalizeHexRefValue(
          joinError?.details?.publicKeyReference ??
            joinError?.details?.keyPackageHealth?.publishedKeyPackageRef ??
            null,
        );
        const reportedWelcomeRef = normalizeHexRefValue(
          joinError?.details?.welcomePublicKeyReference ??
            joinError?.details?.keyPackageHealth?.expectedKeyPackageRef ??
            null,
        );
        const authoritativePublishedRef = normalizeHexRefValue(
          joinError?.details?.keyPackageHealth?.authoritativePublishedKeyPackageRef ??
            this.identityRecord?.publishedKeyPackageRef ??
            this.identityRecord?.published_key_package_ref ??
            null,
        );
        const normalizedWelcomeSource =
          ensureString(welcomeSource ?? null)
            ?.trim()
            .toLowerCase() || null;
        const authoritativeRecoveryWelcomeSource =
          normalizedWelcomeSource === 'join_group_protected_target_drift_preflight' ||
          normalizedWelcomeSource === 'join_group_preflight_welcome_ref_mismatch' ||
          (normalizedWelcomeSource && normalizedWelcomeSource.startsWith('join_bundle_'));
        // A concrete welcome target ref owns convergence even when the current call
        // came from a generic source like `pending_welcome`. Republish of the drifted
        // local key package in this state only deepens the mismatch.
        const concreteWelcomeTargetAuthoritative =
          authoritativeRecoveryWelcomeSource || Boolean(reportedWelcomeRef);
        const reportedWelcomeRefMismatch =
          Boolean(reportedLocalRef) &&
          Boolean(reportedWelcomeRef) &&
          reportedLocalRef !== reportedWelcomeRef;
        const localPublishedRefDrift =
          Boolean(reportedLocalRef) &&
          Boolean(authoritativePublishedRef) &&
          reportedLocalRef !== authoritativePublishedRef;
        if (localPublishedRefDrift && concreteWelcomeTargetAuthoritative) {
          try {
            console.info(
              '[MLS] concrete welcome target is authoritative; skipping local/published drift republish in same convergence pass',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                localRef: reportedLocalRef,
                publishedRef: authoritativePublishedRef,
                welcomeRef: reportedWelcomeRef,
                welcomeSource: normalizedWelcomeSource,
              },
            );
          } catch {}
        }
        if (localPublishedRefDrift && normalizedGroupId && !concreteWelcomeTargetAuthoritative) {
          try {
            console.warn(
              '[MLS] local key package drift detected; republishing current local package before welcome recovery',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                localRef: reportedLocalRef,
                publishedRef: authoritativePublishedRef,
                welcomeRef: reportedWelcomeRef,
                welcomeSource: welcomeSource || null,
              },
            );
          } catch {}
          try {
            this.#clearPublishedKeyPackageMirror({
              identityId: activeIdentityId,
              clientId:
                this.identityRecord?.clientId ??
                this.identityRecord?.client_id ??
                activeIdentityId ??
                null,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
            });
          } catch {}
          try {
            const localExportAfterDrift = await this.exportKeyPackage(activeIdentityId).catch(
              () => null,
            );
            const driftRecovery = await this.#recoverJoinBundleKeyPackage({
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
              context: 'join_group_local_key_package_drift',
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              preferredKeyPackageB64: localExportAfterDrift?.keyPackage || null,
              error: joinError,
            }).catch(() => null);
            if (driftRecovery?.blockedOnTargetMismatch) {
              try {
                console.info(
                  '[MLS] local key package drift preserved because the welcome target is still locked',
                  {
                    groupId: normalizedGroupId,
                    identityId: activeIdentityId,
                    welcomeSource: welcomeSource || null,
                  },
                );
              } catch {}
              return null;
            }
            const localKeyPackageB64 =
              driftRecovery?.keyPackageB64 ?? localExportAfterDrift?.keyPackage ?? null;
            if (!localKeyPackageB64) {
              return null;
            }
            const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
            const fetched = await this.#fetchJoinBundleWithRecovery({
              groupId: normalizedGroupId,
              identityId: driftRecovery?.identityId || activeIdentityId,
              keyPackageB64: localKeyPackageB64,
              token: __t ? `Bearer ${__t}` : undefined,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
              context: 'join_group_local_key_package_drift',
              preferReissueOnAlreadyMemberNoWelcome: true,
              preferCurrentLocalKeyPackage: true,
            }).catch(() => null);
            const bundle = fetched?.bundle ?? null;
            const bundleWelcome =
              ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
            const bundleTree =
              ensureString(bundle?.ratchet_tree_b64) ||
              ensureString(bundle?.ratchetTreeB64) ||
              null;
            const bundleEpoch = bundle?.epoch != null ? ensureEpoch(bundle.epoch) : normalizedEpoch;
            if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
              const recoveredNoWelcome = await this.#recoverAlreadyMemberNoWelcomeRuntime({
                groupId: normalizedGroupId,
                identityId: fetched?.identityId || activeIdentityId,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                source: welcomeSource || 'joinGroup',
                reason: 'local_key_package_ref_drift_already_member_no_welcome',
              }).catch(() => null);
              if (recoveredNoWelcome) {
                return recoveredNoWelcome;
              }
              return null;
            }
            if (bundleWelcome && bundleEpoch != null) {
              await this.#persistWelcomeTarget({
                groupId: normalizedGroupId,
                identityId: fetched?.identityId || activeIdentityId,
                welcomeB64: bundleWelcome,
                ratchetTreeB64: bundleTree,
                epoch: bundleEpoch,
                usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
                source: 'join_group_local_key_package_drift',
              }).catch(() => null);
              let recovered = await this.joinGroup({
                identityId: fetched?.identityId || activeIdentityId,
                welcomeB64: bundleWelcome,
                ratchetTreeB64: null,
                groupId: normalizedGroupId,
                welcomeSource: 'join_bundle_local_key_package_drift',
                expectedEpoch: bundleEpoch,
                allowJoinBundleRecovery: false,
              }).catch(() => null);
              if (!recovered && bundleTree) {
                recovered = await this.joinGroup({
                  identityId: fetched?.identityId || activeIdentityId,
                  welcomeB64: bundleWelcome,
                  ratchetTreeB64: bundleTree,
                  groupId: normalizedGroupId,
                  welcomeSource: 'join_bundle_local_key_package_drift',
                  expectedEpoch: bundleEpoch,
                  allowJoinBundleRecovery: false,
                }).catch(() => null);
              }
              if (recovered) {
                return recovered;
              }
              return null;
            }
          } catch {}
        }
        if (reportedWelcomeRefMismatch && normalizedGroupId) {
          try {
            const groups = await this.#loadGroups().catch(() => null);
            const { entry: membershipEntry } = this.#resolveStoredGroup(groups, {
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupId,
            });
            const membershipState = this.#resolveEntryMembershipState(membershipEntry, {
              activeIdentityId,
            });
            const unresolvedTargetRef = normalizeHexRefValue(
              membershipEntry?.welcomeTargetRefLock ??
                membershipEntry?.welcome_target_ref_lock ??
                this.#resolveWelcomeTargetRef({
                  welcomeB64: pickBase64String(
                    membershipEntry?.recoveryWelcomeB64,
                    membershipEntry?.recovery_welcome_b64,
                    membershipEntry?.welcomeB64,
                    membershipEntry?.welcome_b64,
                    membershipEntry?.welcome,
                    null,
                  ),
                  usedKpRef: membershipEntry?.used_kp_ref ?? membershipEntry?.usedKpRef ?? null,
                }) ??
                null,
            );
            const staleTargetMatchesFailingWelcome =
              Boolean(unresolvedTargetRef) &&
              Boolean(reportedWelcomeRef) &&
              unresolvedTargetRef === reportedWelcomeRef;
            const shouldPreferRuntimeRepair =
              membershipState.currentIdentityMember &&
              !membershipState.pendingSetup &&
              (!membershipState.welcomeRequired || staleTargetMatchesFailingWelcome);
            if (shouldPreferRuntimeRepair) {
              try {
                console.info(
                  '[MLS] Missing-key mismatch hit an already-member snapshot; preferring runtime repair over welcome reissue',
                  {
                    groupId: normalizedGroupId,
                    identityId: activeIdentityId,
                    localRef: reportedLocalRef,
                    welcomeRef: reportedWelcomeRef,
                    unresolvedTargetRef,
                    welcomeSource: welcomeSource || null,
                  },
                );
              } catch {}
              try {
                await this.#clearPersistedWelcomeState({
                  groupId: normalizedGroupId,
                  clearRecoveryPayload: true,
                  preserveWelcomeTargetLock: false,
                });
              } catch {}
              try {
                const repaired = await this.#recoverAlreadyMemberNoWelcomeRuntime({
                  groupId: normalizedGroupId,
                  identityId: activeIdentityId,
                  ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                  source: welcomeSource || 'joinGroup',
                  reason: 'missing_key_already_member_stale_welcome',
                }).catch(() => null);
                if (repaired) {
                  return repaired;
                }
              } catch {}
              try {
                const followUp = await this.syncWelcomes({
                  groupId: normalizedGroupId,
                  __pipelineBypass: true,
                }).catch(() => null);
                const joined =
                  Array.isArray(followUp?.joined) && followUp.joined.length
                    ? followUp.joined.find(
                        (item) =>
                          item?.groupId === normalizedGroupId ||
                          item?.groupHandle === normalizedGroupId,
                      ) || followUp.joined[0]
                    : null;
                if (joined) {
                  return joined;
                }
              } catch {}
              return null;
            }
          } catch {}
        }
        try {
          if (typeof window !== 'undefined' && window.__MLS_DEBUG_KEYPACKAGE_VERBOSE__ === true) {
            let exported = null;
            let localRef = null;
            try {
              exported = await this.exportKeyPackage(activeIdentityId);
              localRef = await computeKeyPackageRefHexFromB64(
                exported?.keyPackage || null,
                welcomeSuite ?? null,
              );
            } catch {}
            const keyPackageB64 =
              typeof exported?.keyPackage === 'string' ? exported.keyPackage : null;
            const keyPackagePreview = keyPackageB64
              ? `${keyPackageB64.slice(0, 32)}...${keyPackageB64.slice(-16)}`
              : null;
            let welcomeRef = null;
            try {
              welcomeRef = resolveMatchingWelcomeRefHex(persistedWelcomeB64, [localRef]);
            } catch {}
            const privateKeyReference = activeIdentityId
              ? `keystore:${String(activeIdentityId)}:${String(welcomeSuite ?? 'unknown')}`
              : null;
            console.warn('MDBG missing key-package during joinGroup', {
              groupId: normalizedGroupId || null,
              identityId: activeIdentityId || null,
              canonicalIdentityId: canonicalIdentityId || null,
              identityClientId: this.identityClientId || null,
              identityRecordId: this.identityRecord?.identityId || null,
              welcomeSuite: welcomeSuite ?? null,
              localKeyPackageRef: localRef || null,
              publicKeyReference: localRef || null,
              welcomePublicKeyReference: welcomeRef || null,
              privateKeyReference,
              localKeyPackagePreview: keyPackagePreview,
              localKeyPackageLength: keyPackageB64 ? keyPackageB64.length : 0,
            });
          }
        } catch {}
        if (allowJoinBundleRecovery && normalizedGroupId) {
          try {
            const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
            let localKeyPackageB64 = null;
            try {
              const exported = await this.exportKeyPackage(activeIdentityId);
              localKeyPackageB64 = exported?.keyPackage || null;
            } catch {}
            if (localKeyPackageB64) {
              const fetched = await this.#fetchJoinBundleWithRecovery({
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                keyPackageB64: localKeyPackageB64,
                token: __t ? 'Bearer ' + __t : undefined,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                context: 'join_group_missing_keypackage',
              }).catch(() => null);
              const bundle = fetched?.bundle ?? null;
              const bundleWelcome = bundle?.welcome_b64 || bundle?.welcomeB64 || null;
              const bundleTree = bundle?.ratchet_tree_b64 || bundle?.ratchetTreeB64 || null;
              const bundleEpoch = bundle?.epoch != null ? ensureEpoch(bundle.epoch) : null;
              if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
                console.debug(
                  '[MLS] joinGroup missing-key recovery noop: already member, no welcome',
                  {
                    groupId: normalizedGroupId,
                    identityId: fetched?.identityId || activeIdentityId,
                  },
                );
                const recoveredNoWelcome = await this.#recoverAlreadyMemberNoWelcomeRuntime({
                  groupId: normalizedGroupId,
                  identityId: fetched?.identityId || activeIdentityId,
                  ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                  source: welcomeSource || 'joinGroup',
                  reason: 'missing_key_already_member_no_welcome',
                }).catch(() => null);
                if (recoveredNoWelcome) {
                  return recoveredNoWelcome;
                }
                return null;
              }
              if (bundleWelcome && bundleEpoch != null) {
                await this.#persistWelcomeTarget({
                  groupId: normalizedGroupId,
                  identityId: fetched?.identityId || activeIdentityId,
                  welcomeB64: bundleWelcome,
                  ratchetTreeB64: bundleTree,
                  epoch: bundleEpoch,
                  usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
                  source: 'join_group_missing_key_recovery',
                }).catch(() => null);
                let recovered = await this.joinGroup({
                  identityId: fetched?.identityId || activeIdentityId,
                  welcomeB64: bundleWelcome,
                  ratchetTreeB64: null,
                  groupId: normalizedGroupId,
                  welcomeSource: 'join_bundle_missing_key_recovery',
                  expectedEpoch: bundleEpoch,
                  allowJoinBundleRecovery: false,
                }).catch(() => null);
                if (!recovered && bundleTree) {
                  recovered = await this.joinGroup({
                    identityId: fetched?.identityId || activeIdentityId,
                    welcomeB64: bundleWelcome,
                    ratchetTreeB64: bundleTree,
                    groupId: normalizedGroupId,
                    welcomeSource: 'join_bundle_missing_key_recovery',
                    expectedEpoch: bundleEpoch,
                    allowJoinBundleRecovery: false,
                  }).catch(() => null);
                }
                if (recovered) {
                  return recovered;
                }

                // The fetched recovery welcome becomes the new authoritative join
                // context even if that nested retry does not fully converge yet.
                // Subsequent recovery decisions in this call must not keep
                // reasoning against the stale welcome that originally failed.
                persistedWelcomeB64 = bundleWelcome;
                const refreshedWelcomeBytes = decodeBase64ToBytes(bundleWelcome);
                if (refreshedWelcomeBytes?.length) {
                  welcomeBytes = refreshedWelcomeBytes;
                }
                normalizedEpoch = bundleEpoch;
                if (bundleTree) {
                  persistedRatchetTreeB64 = bundleTree;
                  const refreshedRatchetTreeBytes = decodeBase64ToBytes(bundleTree);
                  if (refreshedRatchetTreeBytes?.length) {
                    ratchetTreeBytes = refreshedRatchetTreeBytes;
                  }
                }
                try {
                  console.info(
                    '[MLS] Fresh missing-key recovery welcome became authoritative; deferring further escalation to the next convergence pass',
                    {
                      groupId: normalizedGroupId,
                      identityId: fetched?.identityId || activeIdentityId,
                      epoch: bundleEpoch,
                      welcomeSource: welcomeSource || null,
                    },
                  );
                } catch {}
                return null;
              }
            }
          } catch {}
        }
        if (!allowJoinBundleRecovery && authoritativeRecoveryWelcomeSource) {
          try {
            console.info(
              '[MLS] Fresh recovery welcome remains authoritative after nested missing-key failure; deferring further escalation',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                welcomeSource: normalizedWelcomeSource,
              },
            );
          } catch {}
          return null;
        }
        try {
          console.warn('[MLS] joinGroup failed: missing key package', {
            groupId: normalizedGroupId,
            identityId: activeIdentityId,
            suite: welcomeSuite ?? null,
          });
        } catch {}
        const recoveryKey = normalizedGroupId || joinGroupKey || 'unknown-group';
        this._missingKeyPackageRecovery = this._missingKeyPackageRecovery || new Map();
        this._missingKeyPackageNoRepublishUntil =
          this._missingKeyPackageNoRepublishUntil || new Map();
        const lastRecoveryAt = this._missingKeyPackageRecovery.get(recoveryKey) || 0;
        const nowTs = Date.now();
        const keyRefsMatch = joinError?.details?.keyReferenceMatch === true;
        if (keyRefsMatch) {
          this._missingKeyPackageNoRepublishUntil.set(recoveryKey, nowTs + 120_000);
        }
        const noRepublishUntil = this._missingKeyPackageNoRepublishUntil.get(recoveryKey) || 0;
        const noRepublishWindowActive = nowTs < noRepublishUntil;
        const forceRepublishDisabled = Boolean(
          globalThis && globalThis.__MLS_DISABLE_FORCE_REPUBLISH__ === true,
        );
        if (nowTs - lastRecoveryAt > 30_000) {
          this._missingKeyPackageRecovery.set(recoveryKey, nowTs);
          try {
            await this.#clearPersistedWelcomeState({
              groupId: normalizedGroupId,
              clearRecoveryPayload: true,
              preserveWelcomeTargetLock: true,
            });
          } catch {}
          let localKeyPackageB64ForReissue = null;
          try {
            const exported = await this.exportKeyPackage(activeIdentityId);
            localKeyPackageB64ForReissue = exported?.keyPackage || null;
          } catch {}
          try {
            const pinnedKeyPackage = await this.#resolvePinnedKeyPackageForWelcomeTarget({
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
              preferredKeyPackageB64: localKeyPackageB64ForReissue,
            });
            if (pinnedKeyPackage?.keyPackageB64) {
              localKeyPackageB64ForReissue = pinnedKeyPackage.keyPackageB64;
            }
          } catch {}
          const shouldForceRepublish =
            !forceRepublishDisabled &&
            !keyRefsMatch &&
            !noRepublishWindowActive &&
            !localKeyPackageB64ForReissue;
          if (shouldForceRepublish) {
            try {
              this.#clearKeyPackageCacheForIdentity(activeIdentityId);
            } catch {}
            try {
              await this.publishKeyPackageAndSync(activeIdentityId, { forcePublish: true });
            } catch (forceErr) {
              if (typeof console !== 'undefined' && typeof console.warn === 'function') {
                console.warn('[MLS] Forced key package republish failed', forceErr);
              }
            }
            try {
              const refreshedExport = await this.exportKeyPackage(activeIdentityId);
              localKeyPackageB64ForReissue = refreshedExport?.keyPackage || null;
            } catch {}
          } else {
            try {
              console.info(
                '[MLS] Reissuing welcome for current local key package without rotation',
                {
                  groupId: normalizedGroupId,
                  identityId: activeIdentityId,
                  keyRefsMatch,
                  noRepublishWindowActive,
                  hasLocalKeyPackage: Boolean(localKeyPackageB64ForReissue),
                },
              );
            } catch {}
          }
          try {
            const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
            await this.#reissueWelcomesIfNeeded({
              groupId: normalizedGroupId ?? null,
              identityId: activeIdentityId,
              keyPackageB64: localKeyPackageB64ForReissue,
              token: __t ? 'Bearer ' + __t : undefined,
            }).catch(() => null);
            await this.syncWelcomes({
              groupId: normalizedGroupId ?? null,
              __pipelineBypass: true,
            }).catch(() => null);
          } catch {}
          if (
            !shouldForceRepublish &&
            (forceRepublishDisabled || keyRefsMatch || noRepublishWindowActive)
          ) {
            try {
              console.warn('[MLS] Skipping forced key package republish', {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                disabled: forceRepublishDisabled,
                keyRefsMatch,
                noRepublishWindowActive,
                noRepublishUntil: noRepublishUntil || null,
              });
            } catch {}
          }
        }
        let localRef = null;
        let welcomeRef = null;
        let localExport = null;
        try {
          localExport = await this.exportKeyPackage(activeIdentityId);
          localRef = await computeKeyPackageRefHexFromB64(
            localExport?.keyPackage,
            welcomeSuite ?? null,
          );
          welcomeRef = resolveMatchingWelcomeRefHex(persistedWelcomeB64, [localRef]);
          if (typeof console !== 'undefined' && typeof console.info === 'function') {
            debugLog('[MLS DEBUG] keypackage refs', {
              localRef,
              welcomeRef,
              identityId: activeIdentityId,
              suite: welcomeSuite ?? null,
            });
          }
        } catch {}
        const welcomeRefAlreadyConsumed =
          localRef && welcomeRef && localRef !== welcomeRef
            ? await this.#isTrackedKeyPackageRefConsumed(welcomeRef).catch(() => false)
            : false;
        const matchedWelcomeRef =
          joinError?.details?.keyReferenceMatch === true ||
          (localRef && welcomeRef && localRef === welcomeRef);
        if (matchedWelcomeRef && normalizedGroupId) {
          try {
            const groups = await this.#loadGroups();
            const { entry: membershipEntry } = this.#resolveStoredGroup(groups, {
              groupId: normalizedGroupId,
              groupHandle: normalizedGroupId,
            });
            const membershipState = this.#resolveEntryMembershipState(membershipEntry, {
              activeIdentityId,
            });
            const canBootstrapWithSelfWelcome =
              this.serverManagedWelcomes &&
              membershipState.currentIdentityMember &&
              !membershipState.pendingSetup &&
              !membershipState.welcomeRequired;
            if (canBootstrapWithSelfWelcome) {
              const selfWelcomed = await this.#bootstrapRuntimeWithSelfWelcome({
                conversationId:
                  membershipEntry?.conversationId ?? membershipEntry?.conversation_id ?? null,
                groupId: normalizedGroupId,
                groupHandle:
                  membershipEntry?.groupHandle ??
                  membershipEntry?.group_handle ??
                  normalizedGroupId,
                identityId: activeIdentityId,
                ciphersuite:
                  welcomeSuite ?? membershipEntry?.ciphersuite ?? this.preferredCiphersuite,
                allowServerManaged: true,
                source: welcomeSource || 'joinGroup',
                reason: 'missing_key_package_matched_ref_self_welcome',
              }).catch(() => null);
              if (this.#isGroupReady(selfWelcomed)) {
                return selfWelcomed;
              }
            }
          } catch {}
        }
        const matchedRefHealthLooksUsable =
          joinError?.details?.identityHealth?.privateMaterialReady === true ||
          joinError?.details?.keyPackageHealth?.privateMaterialReady === true;
        const matchedRefHealthIndicatesPublicOnly =
          joinError?.details?.identityHealth?.healthCode === 'public_key_package_only' ||
          joinError?.details?.keyPackageHealth?.healthCode === 'public_key_package_only' ||
          joinError?.details?.identityHealth?.keyPackageProvenance === 'rehydrated_public_only' ||
          joinError?.details?.keyPackageHealth?.keyPackageProvenance === 'rehydrated_public_only' ||
          joinError?.details?.identityHealth?.welcomeStageReady === false ||
          joinError?.details?.keyPackageHealth?.welcomeStageReady === false;
        const matchedRefHealthIndicatesConsumed =
          joinError?.details?.identityHealth?.healthCode === 'key_package_consumed' ||
          joinError?.details?.keyPackageHealth?.healthCode === 'key_package_consumed' ||
          joinError?.details?.identityHealth?.welcomeStageReason === 'key_package_consumed' ||
          joinError?.details?.keyPackageHealth?.welcomeStageReason === 'key_package_consumed';
        const matchedRefHealthyLocalKeyPackage =
          matchedWelcomeRef &&
          matchedRefHealthLooksUsable &&
          !matchedRefHealthIndicatesPublicOnly &&
          !matchedRefHealthIndicatesConsumed;
        if (matchedWelcomeRef && matchedRefHealthIndicatesConsumed && normalizedGroupId) {
          try {
            console.warn(
              '[MLS] matched-ref join failure hit a consumed key package; switching to runtime recovery',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                welcomeSource: welcomeSource || null,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                identityHealth: joinError?.details?.identityHealth ?? null,
                keyPackageHealth: joinError?.details?.keyPackageHealth ?? null,
              },
            );
          } catch {}
          try {
            const recoveredConsumed = await this.#recoverAlreadyMemberNoWelcomeRuntime({
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
              source: welcomeSource || 'joinGroup',
              reason: 'matched_ref_consumed_key_package',
            }).catch(() => null);
            if (recoveredConsumed) {
              return recoveredConsumed;
            }
          } catch {}
        }
        if (matchedRefHealthyLocalKeyPackage) {
          try {
            console.warn(
              '[MLS] matched-ref join failure preserved healthy local key package; skipping identity rehydrate',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                welcomeSource: welcomeSource || null,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                identityHealth: joinError?.details?.identityHealth ?? null,
                keyPackageHealth: joinError?.details?.keyPackageHealth ?? null,
              },
            );
          } catch {}
          try {
            const followUp = await this.syncWelcomes({
              groupId: normalizedGroupId ?? null,
              __pipelineBypass: true,
            });
            const joinedEntries = Array.isArray(followUp?.joined) ? followUp.joined : [];
            if (joinedEntries.length) {
              return joinedEntries[0];
            }
          } catch {}
        }
        const matchedRefShouldRehydrateIdentity =
          matchedWelcomeRef &&
          (matchedRefHealthIndicatesPublicOnly ||
            (joinError?.details?.keyReferenceMatch === true && !matchedRefHealthIndicatesConsumed));
        if (allowMatchedRefIdentityRehydrate && matchedRefShouldRehydrateIdentity) {
          try {
            console.warn(
              '[MLS] Skipping published-identity rehydrate after matched-ref join failure; recovery must converge through a fresh local key package and welcome',
              {
                groupId: normalizedGroupId,
                identityId: activeIdentityId,
                welcomeSource: welcomeSource || null,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                keyReferenceMatch: true,
                identityHealth: joinError?.details?.identityHealth ?? null,
                keyPackageHealth: joinError?.details?.keyPackageHealth ?? null,
              },
            );
          } catch {}
        }
        if (localRef && welcomeRef && localRef !== welcomeRef && welcomeRefAlreadyConsumed) {
          try {
            console.warn(
              '[MLS] stale welcome ref already consumed locally; switching mismatch recovery to runtime repair',
              {
                groupId: normalizedGroupId || null,
                identityId: activeIdentityId || null,
                localRef,
                welcomeRef,
                welcomeSource: welcomeSource || null,
              },
            );
          } catch {}
          try {
            await this.#clearPersistedWelcomeState({
              groupId: normalizedGroupId,
              clearRecoveryPayload: true,
              preserveWelcomeTargetLock: false,
            });
          } catch {}
          try {
            const recoveredConsumedMismatch = await this.#recoverAlreadyMemberNoWelcomeRuntime({
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
              source: welcomeSource || 'joinGroup',
              reason: 'welcome_ref_mismatch_consumed_target',
            }).catch(() => null);
            if (recoveredConsumedMismatch) {
              return recoveredConsumedMismatch;
            }
          } catch {}
          return null;
        }
        if (localRef && welcomeRef && localRef !== welcomeRef) {
          const mismatchKey = normalizedGroupId || joinGroupKey || 'unknown-group';
          this._welcomeRefMismatchClears = this._welcomeRefMismatchClears || new Map();
          const lastClearAt = this._welcomeRefMismatchClears.get(mismatchKey) || 0;
          const nowTs = Date.now();
          if (nowTs - lastClearAt > 30_000) {
            this._welcomeRefMismatchClears.set(mismatchKey, nowTs);
            try {
              await this.#clearPersistedWelcomeState({
                groupId: normalizedGroupId,
                clearRecoveryPayload: true,
                preserveWelcomeTargetLock: true,
              });
            } catch {}
            try {
              const activeId = activeIdentityId || this.identityId || null;
              if (activeId && typeof reissueMlsWelcomes === 'function') {
                const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
                let keyPackageB64 = localExport?.keyPackage || null;
                if (!keyPackageB64) {
                  try {
                    const exported = await this.exportKeyPackage(activeId);
                    keyPackageB64 = exported?.keyPackage || null;
                  } catch {}
                }
                await this.#reissueWelcomesIfNeeded({
                  groupId: normalizedGroupId ?? null,
                  identityId: activeId,
                  keyPackageB64,
                  token: __t ? 'Bearer ' + __t : undefined,
                }).catch(() => null);
              }
            } catch {}
            try {
              const activeId = activeIdentityId || this.identityId || null;
              const currentKeyPackageB64 =
                localExport?.keyPackage ||
                (await this.exportKeyPackage(activeId).then((exported) => exported?.keyPackage)) ||
                null;
              const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
              if (normalizedGroupId && activeId && currentKeyPackageB64) {
                const fetched = await this.#fetchJoinBundleWithRecovery({
                  groupId: normalizedGroupId,
                  identityId: activeId,
                  keyPackageB64: currentKeyPackageB64,
                  token: __t ? `Bearer ${__t}` : undefined,
                  ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                  context: 'join_group_welcome_ref_mismatch',
                  preferReissueOnAlreadyMemberNoWelcome: true,
                  preferCurrentLocalKeyPackage: true,
                }).catch(() => null);
                if (fetched?.blocked) {
                  return null;
                }
                const bundle = fetched?.bundle ?? null;
                const bundleWelcome =
                  ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
                const bundleTree =
                  ensureString(bundle?.ratchet_tree_b64) ||
                  ensureString(bundle?.ratchetTreeB64) ||
                  null;
                const bundleEpoch =
                  bundle?.epoch != null ? ensureEpoch(bundle.epoch) : normalizedEpoch;
                if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
                  const recoveredNoWelcome = await this.#recoverAlreadyMemberNoWelcomeRuntime({
                    groupId: normalizedGroupId,
                    identityId: fetched?.identityId || activeId,
                    ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                    source: welcomeSource || 'joinGroup',
                    reason: 'welcome_ref_mismatch_already_member_no_welcome',
                  }).catch(() => null);
                  if (recoveredNoWelcome) {
                    return recoveredNoWelcome;
                  }
                }
                if (bundleWelcome && bundleEpoch != null) {
                  await this.#persistWelcomeTarget({
                    groupId: normalizedGroupId,
                    identityId: fetched?.identityId || activeId,
                    welcomeB64: bundleWelcome,
                    ratchetTreeB64: bundleTree,
                    epoch: bundleEpoch,
                    usedKpRef: bundle?.used_kp_ref ?? bundle?.usedKpRef ?? null,
                    source: 'join_group_welcome_ref_mismatch',
                  }).catch(() => null);
                  let recovered = await this.joinGroup({
                    identityId: fetched?.identityId || activeId,
                    welcomeB64: bundleWelcome,
                    ratchetTreeB64: null,
                    groupId: normalizedGroupId,
                    welcomeSource: 'join_bundle_welcome_ref_mismatch',
                    expectedEpoch: bundleEpoch,
                    allowJoinBundleRecovery: false,
                  }).catch(() => null);
                  if (!recovered && bundleTree) {
                    recovered = await this.joinGroup({
                      identityId: fetched?.identityId || activeId,
                      welcomeB64: bundleWelcome,
                      ratchetTreeB64: bundleTree,
                      groupId: normalizedGroupId,
                      welcomeSource: 'join_bundle_welcome_ref_mismatch',
                      expectedEpoch: bundleEpoch,
                      allowJoinBundleRecovery: false,
                    }).catch(() => null);
                  }
                  if (recovered) {
                    return recovered;
                  }
                }
              }
            } catch {}
            try {
              await this.syncWelcomes({
                groupId: normalizedGroupId ?? null,
                __pipelineBypass: true,
              });
            } catch {}
            // Once we have realigned recovery toward the current local key package,
            // stop escalating against the stale Welcome in this same invocation.
            return null;
          }
        }
        // If the welcomeRef matches our local ref but we still received a
        // "no matching key package" error, it's likely that the key package
        // was published under a different identity context than the current
        // active identity. Try to locate the identity used for that ref in
        // the local key package cache and retry the join once with it.
        try {
          const localExport = await this.exportKeyPackage(activeIdentityId);
          const localRef = await computeKeyPackageRefHexFromB64(
            localExport?.keyPackage,
            welcomeSuite ?? null,
          );
          const welcomeRef = resolveMatchingWelcomeRefHex(persistedWelcomeB64, [localRef]);
          if (localRef && welcomeRef && localRef === welcomeRef) {
            let matchedIdentityForRef = null;
            for (const [cacheKey, entry] of this.keyPackageStatusCache.entries()) {
              try {
                if (entry && entry.ref && entry.ref === welcomeRef) {
                  // cacheKey shape: `${identityIdOrClientId}:${suiteKey}`
                  const sep = cacheKey.indexOf(':');
                  const idPart = sep >= 0 ? cacheKey.slice(0, sep) : cacheKey;
                  if (idPart && String(idPart).trim()) {
                    matchedIdentityForRef = String(idPart).trim();
                    break;
                  }
                }
              } catch {}
            }
            if (matchedIdentityForRef && matchedIdentityForRef !== activeIdentityId) {
              try {
                const retryWithMatched = await workerJoinGroup({
                  identityId: matchedIdentityForRef,
                  welcomeB64: persistedWelcomeB64,
                  ratchetTreeB64: persistedRatchetTreeB64,
                  groupId: normalizedGroupId ?? undefined,
                  ...(ratchetTreeBytes ? { ratchetTreeBytes } : {}),
                });
                // Emulate normal success path
                const workerSuite = normalizeSuiteId(retryWithMatched?.ciphersuite);
                const workerWelcomeSuite = normalizeSuiteId(retryWithMatched?.welcomeSuite);
                const effectiveSuite = workerSuite ?? workerWelcomeSuite ?? welcomeSuite ?? null;

                const resolvedRatchetTreeB64 =
                  pickBase64String(
                    retryWithMatched?.ratchetTreeB64,
                    retryWithMatched?.ratchet_tree_b64,
                    retryWithMatched?.ratchetTree,
                    retryWithMatched?.ratchet_tree,
                    persistedRatchetTreeB64,
                  ) || null;

                const resolvedRatchetTreeBytes = (() => {
                  const bytesFromWorker =
                    coerceUint8Array(retryWithMatched?.ratchetTreeBytes) ||
                    coerceUint8Array(retryWithMatched?.ratchet_tree_bytes) ||
                    null;
                  return bytesFromWorker && bytesFromWorker.length
                    ? new Uint8Array(bytesFromWorker)
                    : null;
                })();

                const groups = await this.#loadGroups();
                const resolvedHandle =
                  retryWithMatched.groupHandle ??
                  retryWithMatched.group_handle ??
                  normalizedGroupId ??
                  (normalizedGroupId != null ? String(normalizedGroupId) : null);
                const resolvedId =
                  retryWithMatched.groupId ??
                  retryWithMatched.group_id ??
                  retryWithMatched.groupHandle ??
                  resolvedHandle ??
                  normalizedGroupId ??
                  (normalizedGroupId != null ? String(normalizedGroupId) : null);
                const preservedLastServerSeq = resolvePreservedLastServerSeq(groups, {
                  groupId: resolvedId,
                  groupHandle: resolvedHandle,
                });
                const preservedProcessedServerSeqs = resolvePreservedProcessedServerSeqs(groups, {
                  groupId: resolvedId,
                  groupHandle: resolvedHandle,
                  baseCursor: preservedLastServerSeq ?? 0,
                });
                const entry = {
                  groupHandle: resolvedHandle,
                  groupId: resolvedId,
                  ciphersuite: effectiveSuite,
                  ratchetTree: resolvedRatchetTreeB64,
                  ratchetTreeB64: resolvedRatchetTreeB64,
                  ratchetTreeBytes: resolvedRatchetTreeBytes,
                  identityId: matchedIdentityForRef,
                  epoch: ensureEpoch(
                    retryWithMatched.epoch != null ? retryWithMatched.epoch : normalizedEpoch,
                  ),
                  lastServerSeq: preservedLastServerSeq ?? 0,
                  processedServerSeqs: preservedProcessedServerSeqs,
                  processed_server_seqs: preservedProcessedServerSeqs,
                  members: resolvePreservedMembers(groups, {
                    groupId: resolvedId,
                    groupHandle: resolvedHandle,
                    identityId: matchedIdentityForRef,
                  }),
                  welcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
                };

                groups[entry.groupId] = entry;
                await this.#saveGroups(groups);

                pushMlsTelemetry('mls.join_with_welcome', {
                  ...telemetryBase,
                  suite_id: effectiveSuite,
                  status: 'success',
                  reason: 'recovered_identity_mismatch',
                  epoch: entry.epoch,
                });
                return entry;
              } catch (retryWithMatchedError) {
                // fall through to normal recovery path
                void retryWithMatchedError;
              }
            }
          }
        } catch {}
        try {
          await this.#ensureKeyPackageCacheLoaded();
          const debugIdentity = this.#normalizeIdentityId(
            this.identityClientId || activeIdentityId || this.identityId || null,
          );
          const cacheEntry = debugIdentity
            ? this.#getKeyPackageCacheEntry(
                debugIdentity,
                welcomeSuite ?? this.preferredCiphersuite ?? null,
              )
            : null;
          if (typeof console !== 'undefined' && typeof console.debug === 'function') {
            console.debug('[MLS DEBUG] Key package mismatch details', {
              local: {
                identityId: debugIdentity ?? null,
                suite: this.preferredCiphersuite ?? null,
                cache: cacheEntry || null,
              },
              expected: { suite: welcomeSuite ?? null, groupId: normalizedGroupId ?? null },
            });
          }
        } catch {}
        // Guard against ref churn: avoid forcing a republish repeatedly.
        // If we recently published, prefer to let the backend reissue a Welcome
        // that matches the latest ref instead of rotating again mid-join.
        try {
          await this.#ensureKeyPackageCacheLoaded();
        } catch {}
        const recentBackoffMs = 15_000; // short backoff to let reissue catch up
        const cacheIdentity = this.#normalizeIdentityId(
          this.identityClientId || activeIdentityId || this.identityId || null,
        );
        const cacheSuite = welcomeSuite ?? this.preferredCiphersuite ?? null;
        const recentEntry = cacheIdentity
          ? this.#getKeyPackageCacheEntry(cacheIdentity, cacheSuite)
          : null;
        const recentlyPublished = Boolean(
          recentEntry?.lastPublishedAt && nowTs - recentEntry.lastPublishedAt < recentBackoffMs,
        );
        const ttlStale = Boolean(
          recentEntry?.lastPublishedAt && nowTs - recentEntry.lastPublishedAt > KEY_PACKAGE_TTL_MS,
        );
        // One-shot forced republish if TTL expired or we lack a ref, bounded by backoff
        if (
          !keyRefsMatch &&
          !noRepublishWindowActive &&
          !concreteWelcomeTargetAuthoritative &&
          !recentlyPublished &&
          (ttlStale || !recentEntry?.ref)
        ) {
          try {
            await this.publishKeyPackageAndSync(activeIdentityId || this.identityId, {
              forcePublish: true,
            });
          } catch (forceErr) {
            if (typeof console !== 'undefined' && typeof console.warn === 'function') {
              console.warn('[MLS] Forced key package republish failed', forceErr);
            }
          }
        }

        // Attempt to reissue a fresh welcome for this identity/group and then fetch it
        try {
          try {
            const reissueIdentityId = this.#normalizeIdentityId(
              this.identityClientId ||
                this.#getClientIdForSuite(welcomeSuite ?? this.preferredCiphersuite ?? null) ||
                activeIdentityId ||
                (this.identityRecord && this.identityRecord.identityId) ||
                this.identityId ||
                null,
            );
            let keyPackageB64 = null;
            try {
              const exported = await this.exportKeyPackage(reissueIdentityId);
              keyPackageB64 = exported?.keyPackage || null;
            } catch {}
            const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
            await this.#reissueWelcomesIfNeeded({
              groupId: normalizedGroupId,
              identityId: reissueIdentityId,
              keyPackageB64,
              token: __t ? 'Bearer ' + __t : undefined,
            });
            try {
              await sleep(300);
            } catch {}
          } catch (reissueError) {
            // Ignore if endpoint not available; fall back to syncWelcomes
            void reissueError;
          }
          const followUp = await this.syncWelcomes({
            groupId: normalizedGroupId,
            __pipelineBypass: true,
          });
          const joinedEntries = Array.isArray(followUp?.joined) ? followUp.joined : [];
          if (joinedEntries.length) {
            // If syncWelcomes already joined us, return the first entry
            return joinedEntries[0];
          }
        } catch {}
        // Retry join once with potentially refreshed payload; if key refs already match,
        // avoid retrying the same stale Welcome payload.
        if (joinError?.details?.keyReferenceMatch !== true) {
          try {
            const retry = await workerJoinGroup(workerPayload);
            // Emulate normal success path: return the resolved entry below by constructing it
            const workerSuite = normalizeSuiteId(retry?.ciphersuite);
            const workerWelcomeSuite = normalizeSuiteId(retry?.welcomeSuite);
            const effectiveSuite = workerSuite ?? workerWelcomeSuite ?? welcomeSuite ?? null;

            const resolvedRatchetTreeB64 =
              pickBase64String(
                retry?.ratchetTreeB64,
                retry?.ratchet_tree_b64,
                retry?.ratchetTree,
                retry?.ratchet_tree,
                persistedRatchetTreeB64,
              ) || null;

            const resolvedRatchetTreeBytes = (() => {
              const bytesFromWorker =
                coerceUint8Array(retry?.ratchetTreeBytes) ||
                coerceUint8Array(retry?.ratchet_tree_bytes) ||
                null;
              return bytesFromWorker && bytesFromWorker.length
                ? new Uint8Array(bytesFromWorker)
                : null;
            })();

            const groups = await this.#loadGroups();
            const resolvedHandle =
              retry.groupHandle ??
              retry.group_handle ??
              normalizedGroupId ??
              (normalizedGroupId != null ? String(normalizedGroupId) : null);
            const resolvedId =
              retry.groupId ??
              retry.group_id ??
              retry.groupHandle ??
              resolvedHandle ??
              normalizedGroupId ??
              (normalizedGroupId != null ? String(normalizedGroupId) : null);
            const preservedLastServerSeq = resolvePreservedLastServerSeq(groups, {
              groupId: resolvedId,
              groupHandle: resolvedHandle,
            });
            const preservedProcessedServerSeqs = resolvePreservedProcessedServerSeqs(groups, {
              groupId: resolvedId,
              groupHandle: resolvedHandle,
              baseCursor: preservedLastServerSeq ?? 0,
            });
            const entry = {
              groupHandle: resolvedHandle,
              groupId: resolvedId,
              ciphersuite: effectiveSuite,
              ratchetTree: resolvedRatchetTreeB64,
              ratchetTreeB64: resolvedRatchetTreeB64,
              ratchetTreeBytes: resolvedRatchetTreeBytes,
              identityId: activeIdentityId,
              epoch: ensureEpoch(retry.epoch != null ? retry.epoch : normalizedEpoch),
              lastServerSeq: preservedLastServerSeq ?? 0,
              processedServerSeqs: preservedProcessedServerSeqs,
              processed_server_seqs: preservedProcessedServerSeqs,
              members: resolvePreservedMembers(groups, {
                groupId: resolvedId,
                groupHandle: resolvedHandle,
                identityId: activeIdentityId,
              }),
              welcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
            };

            groups[entry.groupId] = entry;
            await this.#saveGroups(groups);

            pushMlsTelemetry('mls.join_with_welcome', {
              ...telemetryBase,
              suite_id: effectiveSuite,
              status: 'success',
              reason: 'recovered_after_republish',
              epoch: entry.epoch,
            });
            return entry;
          } catch (retryError) {
            // proceed to throw original error below
            void retryError;
          }
        }
      }

      // Last resort: if the Welcome still cannot be staged because the key package is
      // missing from the worker keystore, reset the local identity (including stable seed)
      // and try once more with a freshly published key package and synced welcomes.
      try {
        const resetKey = normalizedGroupId || joinGroupKey || 'unknown-group';
        const previousResetAttempts =
          Number(this.identityResetAttemptsByGroup.get(resetKey) || 0) || 0;
        const allowHardIdentityReset = allowJoinBundleRecovery && previousResetAttempts < 2;
        if (isNoMatchingKey && resetKey && allowHardIdentityReset) {
          const nextResetAttempt = previousResetAttempts + 1;
          this.identityResetAttemptsByGroup.set(resetKey, nextResetAttempt);
          const keyReferencesMatch = joinError?.details?.keyReferenceMatch === true;
          const shouldRotateStableIdentity =
            joinError?.details?.keyReferenceMatch === false || previousResetAttempts >= 1;
          try {
            console.warn('[MLS] resetting identity after missing key package', {
              groupId: normalizedGroupId,
              identityId: activeIdentityId,
              welcomeSource: welcomeSource || null,
              resetAttempt: nextResetAttempt,
              keyReferencesMatch,
              rotateStableIdentity: shouldRotateStableIdentity,
            });
          } catch {}
          if (shouldRotateStableIdentity) {
            try {
              await this.#clearStableIdentityState();
            } catch {}
            this._ignoreServerDeviceIdentityOnce = true;
          }
          await this.#resetIdentityState({ clearGroups: true });
          const refreshedIdentity = await this.ensureIdentity({
            ciphersuite: welcomeSuite ?? this.preferredCiphersuite,
          });
          const retryIdentityId = refreshedIdentity?.identityId || activeIdentityId;
          const requireFreshWelcomeAfterReset =
            shouldRotateStableIdentity ||
            (retryIdentityId != null &&
              activeIdentityId != null &&
              String(retryIdentityId) !== String(activeIdentityId));
          let retryWelcomeB64 = persistedWelcomeB64;
          let retryRatchetTreeB64 = persistedRatchetTreeB64;
          let retryExpectedEpoch = normalizedEpoch;
          let fetchedFreshRecoveryWelcome = false;
          try {
            await this.publishKeyPackageAndSync(retryIdentityId, {
              forcePublish: true,
            });
          } catch {}
          try {
            const exported = await this.exportKeyPackage(retryIdentityId);
            const refreshedKeyPackageB64 = exported?.keyPackage || null;
            if (normalizedGroupId && refreshedKeyPackageB64) {
              const __t = typeof getAuthToken === 'function' ? getAuthToken() : null;
              const fetched = await this.#fetchJoinBundleWithRecovery({
                groupId: normalizedGroupId,
                identityId: retryIdentityId,
                keyPackageB64: refreshedKeyPackageB64,
                token: __t ? `Bearer ${__t}` : undefined,
                ciphersuite: welcomeSuite ?? this.preferredCiphersuite ?? null,
                context: 'join_group_hard_reset_recovery',
                preferReissueOnAlreadyMemberNoWelcome: true,
              }).catch(() => null);
              if (fetched?.blocked) {
                return null;
              }
              const bundle = fetched?.bundle ?? null;
              const bundleWelcome =
                ensureString(bundle?.welcome_b64) || ensureString(bundle?.welcomeB64) || null;
              const bundleTree =
                ensureString(bundle?.ratchet_tree_b64) ||
                ensureString(bundle?.ratchetTreeB64) ||
                null;
              const bundleEpoch =
                bundle?.epoch != null ? ensureEpoch(bundle.epoch) : normalizedEpoch;
              if (bundleWelcome) {
                retryWelcomeB64 = bundleWelcome;
                retryRatchetTreeB64 = bundleTree;
                retryExpectedEpoch = bundleEpoch;
                fetchedFreshRecoveryWelcome = true;
              } else if (this.#isAlreadyMemberNoWelcomeBundle(bundle)) {
                const followUp = await this.syncWelcomes({
                  groupId: normalizedGroupId ?? null,
                }).catch(() => null);
                if (Array.isArray(followUp?.joined) && followUp.joined.length) {
                  return followUp.joined[0];
                }
              }
            }
          } catch {}
          const canRetryWithWelcomeAfterReset =
            typeof retryWelcomeB64 === 'string' &&
            retryWelcomeB64 &&
            (!requireFreshWelcomeAfterReset || fetchedFreshRecoveryWelcome);
          if (!canRetryWithWelcomeAfterReset && requireFreshWelcomeAfterReset) {
            try {
              console.warn('[MLS] Skipping stale welcome retry after rotated identity reset', {
                groupId: normalizedGroupId || null,
                previousIdentityId: activeIdentityId || null,
                retryIdentityId: retryIdentityId || null,
                fetchedFreshRecoveryWelcome,
              });
            } catch {}
          } else {
            try {
              const retryAfterReset = await this.joinGroup({
                identityId: retryIdentityId,
                welcomeB64: retryWelcomeB64,
                ratchetTreeB64: retryRatchetTreeB64,
                groupId: normalizedGroupId,
                welcomeSource,
                expectedEpoch: retryExpectedEpoch,
                welcomeSuite,
                allowJoinBundleRecovery: false,
                forceRejoin: true,
              });
              if (retryAfterReset) {
                return retryAfterReset;
              }
            } catch {}
          }
          try {
            const followUp = await this.syncWelcomes({
              groupId: normalizedGroupId ?? null,
              __pipelineBypass: true,
            });
            if (Array.isArray(followUp?.joined) && followUp.joined.length) {
              return followUp.joined[0];
            }
          } catch {}
        } else if (isNoMatchingKey && !allowHardIdentityReset) {
          try {
            console.warn('[MLS] Skipping hard identity reset for non-recovery join source', {
              groupId: normalizedGroupId || null,
              welcomeSource: welcomeSource || null,
              resetAttempts: previousResetAttempts,
            });
          } catch {}
        }
      } catch {}

      if (reason === 'unsupported_ciphersuite') {
        const friendlyMessage = `Unsupported MLS ciphersuite (${errorSuite ?? 'unknown'}). Please update the app.`;
        try {
          console.error('[MLS] joinGroup failed due to unsupported ciphersuite', {
            groupId: normalizedGroupId,
            suite: errorSuite,
            error: joinError,
          });
        } catch {}
        const friendly = new Error(friendlyMessage);
        friendly.code = 'unsupported_ciphersuite';
        friendly.ciphersuite = errorSuite;
        friendly.details = {
          ...(joinError?.details || {}),
          originalMessage: joinError?.message || null,
        };
        throw friendly;
      }

      throw joinError;
    }

    const workerSuite = normalizeSuiteId(result?.ciphersuite);
    const workerWelcomeSuite = normalizeSuiteId(result?.welcomeSuite);
    const effectiveSuite = workerSuite ?? workerWelcomeSuite ?? welcomeSuite ?? null;

    if (welcomeSuite != null && workerSuite != null && workerSuite !== welcomeSuite) {
      const mismatch = new Error('MLS ciphersuite mismatch');
      mismatch.code = 'ciphersuite_mismatch';
      mismatch.ciphersuite = welcomeSuite;
      mismatch.details = {
        welcomeSuite,
        workerSuite,
        workerWelcomeSuite,
        welcomeSource,
      };
      pushMlsTelemetry('mls.join_with_welcome', {
        ...telemetryBase,
        suite_id: welcomeSuite,
        status: 'failure',
        reason: 'mismatch',
      });
      throw mismatch;
    }

    if (welcomeSuite == null && workerWelcomeSuite != null) {
      welcomeSuite = workerWelcomeSuite;
    }

    let resolvedEpoch = ensureEpoch(result.epoch != null ? result.epoch : normalizedEpoch);
    if (!resolvedEpoch && normalizedGroupId) {
      try {
        resolvedEpoch = await getGroupEpoch({ groupId: normalizedGroupId });
      } catch {}
      resolvedEpoch = ensureEpoch(resolvedEpoch);
    }

    const resolvedRatchetTreeB64 =
      pickBase64String(
        result?.ratchetTreeB64,
        result?.ratchet_tree_b64,
        result?.ratchetTree,
        result?.ratchet_tree,
        persistedRatchetTreeB64,
      ) || null;

    const resolvedRatchetTreeBytes = (() => {
      const bytesFromWorker =
        coerceUint8Array(result?.ratchetTreeBytes) ||
        coerceUint8Array(result?.ratchet_tree_bytes) ||
        null;
      if (bytesFromWorker && bytesFromWorker.length) {
        return new Uint8Array(bytesFromWorker);
      }
      if (ratchetTreeBytes && ratchetTreeBytes.length) {
        return new Uint8Array(ratchetTreeBytes);
      }
      return null;
    })();

    const resolvedGroupHandle =
      result.groupHandle ??
      result.group_handle ??
      normalizedGroupId ??
      (groupId != null ? String(groupId) : null);
    const resolvedGroupId =
      result.groupId ??
      result.group_id ??
      result.groupHandle ??
      resolvedGroupHandle ??
      normalizedGroupId ??
      (groupId != null ? String(groupId) : null);
    const groups = await this.#loadGroups();
    const preservedLastServerSeq = resolvePreservedLastServerSeq(groups, {
      groupId: resolvedGroupId,
      groupHandle: resolvedGroupHandle,
    });
    const preservedProcessedServerSeqs = resolvePreservedProcessedServerSeqs(groups, {
      groupId: resolvedGroupId,
      groupHandle: resolvedGroupHandle,
      baseCursor: preservedLastServerSeq ?? 0,
    });
    const preservedMembers = resolvePreservedMembers(groups, {
      groupId: resolvedGroupId,
      groupHandle: resolvedGroupHandle,
      identityId: activeIdentityId,
    });
    let consumedKpRef = null;
    try {
      const exported = await this.exportKeyPackage(activeIdentityId);
      const localRef = await computeKeyPackageRefHexFromB64(
        exported?.keyPackage || null,
        welcomeSuite ?? null,
      );
      consumedKpRef = resolveMatchingWelcomeRefHex(persistedWelcomeB64, [localRef]);
    } catch {
      consumedKpRef = extractWelcomeRefHexFromB64(persistedWelcomeB64);
    }
    const runtimeVerificationEntry = {
      groupId: resolvedGroupId,
      groupHandle: resolvedGroupHandle,
      runtimeHandle: result.groupHandle ?? result.group_handle ?? null,
      identityId: activeIdentityId,
      epoch: resolvedEpoch,
    };
    let matchedRuntimeGroup = null;
    const runtimeVerificationDelaysMs = [0, 75, 200, 400, 800, 1500];
    const probeRuntimeGroupByHandle = async () => {
      const candidateHandles = [
        ensureString(result.groupHandle ?? null),
        ensureString(result.group_handle ?? null),
        ensureString(resolvedGroupHandle ?? null),
      ].filter(Boolean);
      if (!candidateHandles.length) {
        return null;
      }

      const probeContext = ensureString(
        normalizedGroupId ?? resolvedGroupId ?? groupId ?? resolvedGroupHandle ?? null,
      );

      for (const candidateHandle of candidateHandles) {
        try {
          const probe = await awaitWithTimeout(
            () =>
              workerDebugGroupRuntimeState({
                groupHandle: candidateHandle,
                includeExportKey: false,
                label: HISTORY_DERIVED_KEY_LABEL,
                context: probeContext ?? '',
                length: 1,
              }),
            1500,
            null,
          );
          if (!probe?.found) {
            continue;
          }

          const probedEpoch = normalizeEpochOrNull(
            probe?.epoch ??
              probe?.currentEpoch ??
              probe?.snapshot?.epoch ??
              probe?.snapshot?.currentEpoch ??
              null,
          );
          return {
            groupId:
              ensureString(probe?.groupId ?? null) ?? ensureString(resolvedGroupId ?? null) ?? null,
            groupHandle: candidateHandle,
            epoch: probedEpoch ?? resolvedEpoch ?? null,
            ciphersuite:
              normalizeSuiteId(probe?.ciphersuite ?? null) ??
              effectiveSuite ??
              welcomeSuite ??
              null,
            __probedByHandle: true,
          };
        } catch {}
      }

      return null;
    };
    for (const delayMs of runtimeVerificationDelaysMs) {
      if (delayMs > 0) {
        await sleep(delayMs);
      }
      try {
        const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
        matchedRuntimeGroup = this.#resolveRuntimeGroupForEntry(
          runtimeVerificationEntry,
          runtimeGroups,
        );
      } catch {
        matchedRuntimeGroup = null;
      }
      if (!matchedRuntimeGroup) {
        matchedRuntimeGroup = await probeRuntimeGroupByHandle();
      }
      if (matchedRuntimeGroup) {
        break;
      }
    }

    if (!matchedRuntimeGroup) {
      const recoveryEntry = {
        ...(groups[resolvedGroupId] || {}),
        groupHandle: resolvedGroupHandle,
        runtimeHandle:
          ensureString(result.groupHandle ?? result.group_handle ?? null) ??
          ensureString(resolvedGroupHandle ?? null) ??
          null,
        runtimeLive: false,
        runtime_live: false,
        runtimeEpoch: null,
        runtime_epoch: null,
        groupId: resolvedGroupId,
        ciphersuite: effectiveSuite,
        ratchetTree: resolvedRatchetTreeB64,
        ratchetTreeB64: resolvedRatchetTreeB64,
        ratchetTreeBytes: resolvedRatchetTreeBytes,
        identityId: activeIdentityId,
        epoch: resolvedEpoch,
        current_epoch: resolvedEpoch,
        currentEpoch: resolvedEpoch,
        lastServerSeq: preservedLastServerSeq ?? 0,
        processedServerSeqs: preservedProcessedServerSeqs,
        processed_server_seqs: preservedProcessedServerSeqs,
        members: preservedMembers,
        welcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
        recoveryWelcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
        used_kp_ref: consumedKpRef ?? null,
        usedKpRef: consumedKpRef ?? null,
        pending_handshake_queue: [],
        pendingHandshakeQueue: [],
        joined: false,
        fetchedAt: Date.now(),
        needsRecovery: true,
        recoveryReason: 'missing_runtime_group',
        rehydrateBlockedUntil: null,
        rehydrateBackoffReason: null,
      };
      try {
        groups[resolvedGroupId] = recoveryEntry;
        await this.#saveGroups(groups);
        console.warn('[MLS] Persisted recovery welcome after join without runtime', {
          groupId: resolvedGroupId,
          groupHandle: resolvedGroupHandle,
          epoch: resolvedEpoch,
          hasWelcomeB64: Boolean(recoveryEntry.welcomeB64),
          hasRecoveryWelcomeB64: Boolean(recoveryEntry.recoveryWelcomeB64),
          hasRatchetTreeB64: Boolean(recoveryEntry.ratchetTreeB64),
          usedKpRef: recoveryEntry.usedKpRef ?? recoveryEntry.used_kp_ref ?? null,
        });
        console.warn(
          `[MLS] Persisted recovery welcome after join without runtime detail ${JSON.stringify({
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
            epoch: resolvedEpoch,
            hasWelcomeB64: Boolean(recoveryEntry.welcomeB64),
            hasRecoveryWelcomeB64: Boolean(recoveryEntry.recoveryWelcomeB64),
            hasRatchetTreeB64: Boolean(recoveryEntry.ratchetTreeB64),
            usedKpRef: recoveryEntry.usedKpRef ?? recoveryEntry.used_kp_ref ?? null,
          })}`,
        );
      } catch {}
      try {
        await this.markGroupForRecovery(resolvedGroupId, 'missing_runtime_group');
      } catch {}
      const runtimeMissingError = new Error('MLS runtime group missing after join');
      runtimeMissingError.code = 'MLS_GROUP_NEEDS_RECOVERY';
      runtimeMissingError.groupId = resolvedGroupId;
      runtimeMissingError.groupHandle = resolvedGroupHandle;
      runtimeMissingError.recoveryReason = 'missing_runtime_group';
      throw runtimeMissingError;
    }

    const matchedRuntimeHandle = ensureString(matchedRuntimeGroup?.groupHandle ?? null);
    const matchedRuntimeEpoch = normalizeEpochOrNull(matchedRuntimeGroup?.epoch ?? null);
    const effectiveRuntimeEpoch =
      matchedRuntimeEpoch != null && resolvedEpoch != null
        ? Math.max(matchedRuntimeEpoch, resolvedEpoch)
        : matchedRuntimeEpoch != null
          ? matchedRuntimeEpoch
          : resolvedEpoch > 0
            ? resolvedEpoch
            : null;

    const entry = {
      groupHandle: resolvedGroupHandle,
      runtimeHandle:
        matchedRuntimeHandle ??
        result.groupHandle ??
        result.group_handle ??
        resolvedGroupHandle ??
        null,
      groupId: resolvedGroupId,
      ciphersuite: effectiveSuite,
      ratchetTree: resolvedRatchetTreeB64,
      ratchetTreeB64: resolvedRatchetTreeB64,
      ratchetTreeBytes: resolvedRatchetTreeBytes,
      identityId: activeIdentityId,
      epoch: resolvedEpoch,
      lastServerSeq: preservedLastServerSeq ?? 0,
      processedServerSeqs: preservedProcessedServerSeqs,
      processed_server_seqs: preservedProcessedServerSeqs,
      members: preservedMembers,
      welcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
      recoveryWelcomeB64: typeof persistedWelcomeB64 === 'string' ? persistedWelcomeB64 : null,
      used_kp_ref: consumedKpRef ?? null,
      usedKpRef: consumedKpRef ?? null,
      runtimeEpoch: effectiveRuntimeEpoch,
      joined: true,
      fetchedAt: Date.now(),
      needsRecovery: false,
      recoveryReason: null,
    };
    const bootstrapStartedAt = Date.now();
    const bootstrapWindowUntil = bootstrapStartedAt + BOOTSTRAP_CONVERGENCE_WINDOW_MS;

    entry.current_epoch = entry.epoch;
    entry.currentEpoch = entry.epoch;
    entry.pending_handshake_queue = [];
    entry.pendingHandshakeQueue = [];
    entry.bootstrapStartedAt = bootstrapStartedAt;
    entry.bootstrap_started_at = bootstrapStartedAt;
    entry.bootstrapWindowUntil = bootstrapWindowUntil;
    entry.bootstrap_window_until = entry.bootstrapWindowUntil;
    entry.bootstrapReason = 'welcome_join_convergence';
    entry.bootstrap_reason = entry.bootstrapReason;
    entry.group_state_b64 = serializeGroupStateSnapshot(entry);
    entry.serializedGroupState = entry.group_state_b64;

    groups[entry.groupId] = entry;
    await this.#saveGroups(groups);
    if (consumedKpRef) {
      await this.#markKeyPackageRefConsumed(consumedKpRef, {
        groupId: entry.groupId,
        epoch: entry.epoch,
      });
    }

    pushMlsTelemetry('mls.join_with_welcome', {
      ...telemetryBase,
      suite_id: effectiveSuite,
      status: 'success',
      reason: null,
      epoch: entry.epoch,
    });
    if (normalizedActiveIdentityId) {
      this._protectedWelcomeTargetDriftByIdentity.delete(normalizedActiveIdentityId);
      await this.#clearProtectedWelcomeTargetDrift({
        groupId: entry.groupId ?? normalizedGroupId ?? groupId ?? null,
        groupHandle: entry.groupHandle ?? entry.groupId ?? normalizedGroupId ?? groupId ?? null,
        identityId: normalizedActiveIdentityId,
      }).catch(() => null);
    }
    await this.#debugKeyStoreCheckpoint('post_join_group_success', {
      identityId: activeIdentityId,
      groupId: entry.groupId ?? normalizedGroupId ?? groupId ?? null,
      welcomeB64: persistedWelcomeB64,
      ciphersuite: effectiveSuite ?? welcomeSuite ?? this.preferredCiphersuite ?? null,
    });
    return entry;
  }

  async publishKeyPackageAndSync(
    identityId,
    { retryOnMissingClientId = true, forcePublish = false, overrideWelcomeTargetLock = false } = {},
  ) {
    const requestedIdentityId = this.#normalizeIdentityId(
      identityId ??
        this.identityRecord?.identityId ??
        this.identityRecord?.identity_id ??
        this.identityId ??
        null,
    );
    try {
      const unresolvedWelcomeTarget =
        await this.#findUnresolvedWelcomeTargetForIdentity(requestedIdentityId);
      if (requestedIdentityId && unresolvedWelcomeTarget && !overrideWelcomeTargetLock) {
        if (typeof console?.info === 'function') {
          console.info(
            '[MLS] publishKeyPackageAndSync suppressed to preserve unresolved welcome target',
            {
              identityId: requestedIdentityId,
              groupId: unresolvedWelcomeTarget.groupId ?? null,
              groupHandle: unresolvedWelcomeTarget.groupHandle ?? null,
              targetRef: unresolvedWelcomeTarget.targetRef ?? null,
              source: unresolvedWelcomeTarget.source ?? null,
              forcePublish,
              overrideWelcomeTargetLock,
            },
          );
        }
        return {
          skipped: true,
          locked: true,
          reason: 'welcome_target_locked',
          identityId: requestedIdentityId,
          targetRef: unresolvedWelcomeTarget.targetRef ?? null,
          groupId: unresolvedWelcomeTarget.groupId ?? null,
          clientId:
            this.#normalizeIdentityId(this.identityClientId) ??
            this.#normalizeIdentityId(
              this.identityRecord?.clientId ?? this.identityRecord?.client_id ?? null,
            ) ??
            null,
        };
      }
    } catch {}
    // Suppress opportunistic publish while join repair is in-flight, unless explicitly forced.
    try {
      if (!forcePublish && this._suppressPublishUntil && Date.now() < this._suppressPublishUntil) {
        if (typeof console?.debug === 'function') {
          console.debug('[MLS] Suppressing publish during join repair window');
        }
        return { skipped: true, deferred: true };
      }
    } catch {}
    // Also respect per-identity lock
    try {
      if (!forcePublish) {
        const identity = await this.ensureIdentity({});
        const targetIdentityId = identityId || identity?.identityId || this.identityId || null;
        if (targetIdentityId && this.#isKeyPackageLocked(String(targetIdentityId))) {
          if (typeof console?.debug === 'function') {
            console.debug('[MLS] Suppressing publish during join (locked)');
          }
          return { skipped: true, locked: true, identityId: targetIdentityId };
        }
      }
    } catch {}
    // Pre-compute a KeyPackage reference from the exported bytes so we can
    // persist it alongside cache freshness after a successful publish.
    let precomputedRef = null;
    try {
      const identity = await this.ensureIdentity({});
      const targetIdentityId = identityId || identity?.identityId || null;
      if (targetIdentityId) {
        const exported = await this.exportKeyPackage(targetIdentityId);
        if (exported?.keyPackage) {
          precomputedRef = await computeKeyPackageRefHexFromB64(exported.keyPackage);
        }
      }
    } catch {}

    const published = await this.publishKeyPackage(identityId, {
      forcePublish,
      overrideWelcomeTargetLock,
    });
    await this.#debugKeyStoreCheckpoint('post_publish_key_package', {
      identityId: requestedIdentityId ?? published?.identityId ?? this.identityId ?? null,
      ciphersuite: published?.ciphersuite ?? null,
      publishResult: published,
    });

    let normalizedIdentityId = this.#normalizeIdentityId(
      requestedIdentityId ?? published?.identityId ?? this.identityId ?? null,
    );
    if (!normalizedIdentityId) {
      try {
        const ensuredIdentity = await this.ensureIdentity({});
        normalizedIdentityId = this.#normalizeIdentityId(ensuredIdentity?.identityId ?? null);
      } catch {}
    }
    const normalizedClientId = this.#normalizeIdentityId(published?.clientId ?? null);
    const publishedSuite = normalizeSuiteId(published?.ciphersuite ?? null);
    const mintedClientId = normalizedClientId ?? null;
    const canonicalIdentityClientId =
      normalizedIdentityId && isCanonicalMlsClientId(normalizedIdentityId)
        ? normalizedIdentityId
        : null;
    let resolvedClientIdAfterPublish =
      mintedClientId ??
      (publishedSuite != null
        ? this.#normalizeIdentityId(this.#getClientIdForSuite(publishedSuite))
        : null) ??
      canonicalIdentityClientId ??
      null;

    const processedPublish =
      publishedSuite != null &&
      !published?.skipped &&
      !published?.deferred &&
      !published?.alreadyExists;

    if (publishedSuite != null) {
      if (mintedClientId) {
        this.#adoptClientIdForSuite(publishedSuite, mintedClientId);
      } else if (processedPublish) {
        this.#clearClientIdCacheForSuite(publishedSuite);
        this.#clearKeyPackageCacheForSuite(publishedSuite);
      }
    }

    if (mintedClientId) {
      this.identityClientId = mintedClientId;
    }

    if (resolvedClientIdAfterPublish) {
      if (publishedSuite != null) {
        this.#adoptClientIdForSuite(publishedSuite, resolvedClientIdAfterPublish);
        resolvedClientIdAfterPublish =
          this.#normalizeIdentityId(this.#getClientIdForSuite(publishedSuite)) ??
          resolvedClientIdAfterPublish;
      }
      this.identityClientId = resolvedClientIdAfterPublish;
      if (!mintedClientId && normalizedIdentityId) {
        await this.#applyKeyPackageMetadataToIdentity(
          {
            clientId: resolvedClientIdAfterPublish,
            ciphersuite: publishedSuite,
          },
          { identityId: normalizedIdentityId },
        ).catch(() => null);
      }
    }

    const shouldRetryMissingClientId =
      retryOnMissingClientId &&
      !resolvedClientIdAfterPublish &&
      !published?.skipped &&
      !published?.deferred &&
      !published?.alreadyExists;

    if (shouldRetryMissingClientId && normalizedIdentityId) {
      console.warn('[MLS] publishKeyPackageAndSync missing minted client id - retrying publish', {
        identityId: normalizedIdentityId,
        ciphersuite: publishedSuite ?? null,
      });
      return this.publishKeyPackageAndSync(normalizedIdentityId, {
        retryOnMissingClientId: false,
        forcePublish: true,
        overrideWelcomeTargetLock,
      });
    }

    if (published?.deferred) {
      debugLog('[MLS] publishKeyPackageAndSync deferred publish', {
        identityId: published.identityId ?? identityId ?? null,
        ciphersuite: published.ciphersuite ?? null,
      });
      return { ...published, clientId: resolvedClientIdAfterPublish ?? null };
    }

    if (published?.skipped) {
      // If we skipped due to a fresh cache but still don't have a minted client id
      // in memory, force a one-off republish to mint it. This prevents getting stuck
      // in a state where clientId remains null and the server cannot target welcomes.
      const suiteClientId =
        publishedSuite != null
          ? this.#normalizeIdentityId(this.#getClientIdForSuite(publishedSuite))
          : null;
      const effectiveClientId =
        mintedClientId || suiteClientId || canonicalIdentityClientId || null;
      if (effectiveClientId) {
        this.identityClientId = effectiveClientId;
        if (publishedSuite != null) {
          this.#adoptClientIdForSuite(publishedSuite, effectiveClientId);
        }
      }
      if (!effectiveClientId && normalizedIdentityId && !forcePublish) {
        try {
          console.warn(
            '[MLS] publishKeyPackageAndSync skipped but missing client id - forcing republish',
            {
              identityId: normalizedIdentityId,
              ciphersuite: publishedSuite ?? null,
              suiteClientId: suiteClientId ?? null,
            },
          );
          return this.publishKeyPackageAndSync(normalizedIdentityId, {
            retryOnMissingClientId: false,
            forcePublish: true,
            overrideWelcomeTargetLock,
          });
        } catch {}
      }
      // Extra diagnostics: surface the cache freshness we relied on
      const e = published.cacheEntry || null;
      const payload = {
        identityId: published.identityId ?? identityId ?? null,
        clientId: effectiveClientId,
        ciphersuite: published.ciphersuite ?? null,
        cache: e
          ? {
              status: e.status ?? null,
              expiresAt: e.expiresAt ?? null,
              ttlMs: e.ttlMs ?? null,
              fetchedAt: e.fetchedAt ?? null,
              lastPublishedAt: e.lastPublishedAt ?? null,
              deferredUntil: e.deferredUntil ?? null,
            }
          : null,
      };
      const skipReason =
        (typeof published?.reason === 'string' && published.reason.trim()) ||
        (published?.locked ? 'locked' : null) ||
        (published?.deferred ? 'deferred' : null) ||
        (published?.alreadyExists ? 'already_exists' : null) ||
        (e?.status ? `cache_${String(e.status).trim()}` : null) ||
        'unknown';
      const benignSkipReason = skipReason === 'fresh_cache' || skipReason === 'cache_fresh';
      const shouldWarnSkip = !effectiveClientId || !benignSkipReason;
      try {
        if (shouldWarnSkip) {
          console.warn(`[MLS] publishKeyPackageAndSync skipped publish (${skipReason})`, payload);
        } else {
          debugLog(`[MLS] publishKeyPackageAndSync skipped publish (${skipReason})`, payload);
        }
      } catch {}
      return { ...published, clientId: effectiveClientId };
    }

    const authoritativePublishedRefFromPublish = normalizeHexRefValue(
      published?.cacheEntry?.ref ?? precomputedRef ?? null,
    );

    // If we successfully published and have a concrete ref, persist it.
    try {
      const cacheId = mintedClientId || normalizedIdentityId || null;
      if (cacheId && publishedSuite != null && authoritativePublishedRefFromPublish) {
        this.#updateKeyPackageCache(cacheId, publishedSuite, {
          ref: authoritativePublishedRefFromPublish,
        });
      }
    } catch {}

    let workerHealthAfterPublish = null;
    if (publishedSuite != null) {
      try {
        workerHealthAfterPublish = await this.#getWorkerKeyPackageHealth({
          identityId: normalizedIdentityId ?? identityId ?? this.identityId ?? null,
          includeKeyPackage: true,
          expectedRef: authoritativePublishedRefFromPublish,
          ciphersuite: publishedSuite,
        }).catch(() => null);
        await this.#persistKeyPackageLifecycle({
          identityId: normalizedIdentityId ?? identityId ?? this.identityId ?? null,
          clientId:
            mintedClientId ??
            this.#normalizeIdentityId(this.#getClientIdForSuite(publishedSuite)) ??
            null,
          ciphersuite: publishedSuite,
          kpRef:
            workerHealthAfterPublish?.publishedKeyPackageRef ??
            authoritativePublishedRefFromPublish ??
            published?.cacheEntry?.ref ??
            null,
          keyPackageB64: workerHealthAfterPublish?.keyPackageB64 ?? null,
          status: 'unused',
          publishedAt: Date.now(),
          verifiedLocalAt: workerHealthAfterPublish ? Date.now() : null,
          privateMaterialReady: workerHealthAfterPublish?.privateMaterialReady ?? null,
          keyPackageHandleAlive: workerHealthAfterPublish?.keyPackageHandleAlive ?? null,
          keyPackageExportable: workerHealthAfterPublish?.keyPackageExportable ?? null,
          keyPackageHealthCode:
            workerHealthAfterPublish?.healthCode ??
            workerHealthAfterPublish?.diagnosticCode ??
            null,
          keyPackageProvenance: workerHealthAfterPublish?.keyPackageProvenance ?? null,
          welcomeStageReady: workerHealthAfterPublish?.welcomeStageReady ?? null,
          welcomeStageReason: workerHealthAfterPublish?.welcomeStageReason ?? null,
        });
      } catch {}
    }

    const concretePublishedRef = normalizeHexRefValue(
      workerHealthAfterPublish?.publishedKeyPackageRef ??
        authoritativePublishedRefFromPublish ??
        null,
    );

    if (processedPublish && concretePublishedRef) {
      if (
        workerHealthAfterPublish &&
        (workerHealthAfterPublish?.privateMaterialReady !== true ||
          workerHealthAfterPublish?.keyPackageHandleAlive !== true ||
          workerHealthAfterPublish?.welcomeStageReady === false)
      ) {
        try {
          console.warn(
            '[MLS] Deferring post-publish identity rehydrate to preserve concrete published key package ref',
            {
              identityId: normalizedIdentityId ?? identityId ?? this.identityId ?? null,
              ciphersuite: publishedSuite ?? null,
              publishedRef: concretePublishedRef,
              privateMaterialReady: workerHealthAfterPublish?.privateMaterialReady ?? null,
              keyPackageHandleAlive: workerHealthAfterPublish?.keyPackageHandleAlive ?? null,
              welcomeStageReady: workerHealthAfterPublish?.welcomeStageReady ?? null,
              healthCode:
                workerHealthAfterPublish?.healthCode ??
                workerHealthAfterPublish?.diagnosticCode ??
                null,
            },
          );
        } catch {}
      }
    } else if (
      processedPublish &&
      (await this.#shouldRehydratePublishedIdentity({
        identityId: normalizedIdentityId ?? identityId ?? this.identityId ?? null,
        ciphersuite: publishedSuite ?? null,
      }))
    ) {
      try {
        console.warn(
          '[MLS] Skipping post-publish identity rehydrate; convergence must use the live local key package plus a fresh welcome',
          {
            identityId: normalizedIdentityId ?? identityId ?? this.identityId ?? null,
            ciphersuite: publishedSuite ?? null,
          },
        );
      } catch {}
    }

    return { ...published, clientId: resolvedClientIdAfterPublish ?? null };
  }

  async forceRepublishKeyPackage({ ciphersuite } = {}) {
    try {
      const idObj = await this.ensureIdentity({ ciphersuite });
      const identityId = idObj?.identityId || this.identityId || null;
      if (!identityId) return null;
      // Clear freshness so publish won't be skipped
      try {
        await this.#ensureKeyPackageCacheLoaded();
        const suiteKey = String(
          ciphersuite ?? idObj?.ciphersuite ?? this.preferredCiphersuite ?? '',
        );
        const perId = this.keyPackageCache.get(identityId) || new Map();
        const entry = perId.get(suiteKey) || {};
        entry.status = 'stale';
        entry.deferredUntil = null;
        entry.expiresAt = null;
        perId.set(suiteKey, entry);
        this.keyPackageCache.set(identityId, perId);
      } catch {}
      return await this.publishKeyPackageAndSync(identityId, {
        forcePublish: true,
        retryOnMissingClientId: false,
      });
    } catch (error) {
      console.warn('[MLS] forceRepublishKeyPackage failed', error);
      return null;
    }
  }

  async uploadWelcome({
    groupId,
    epoch,
    toUserId,
    toClientId,
    welcomeB64,
    welcomes,
    ratchetTreeB64,
    ciphersuite,
  }) {
    let resolvedRatchetTree = typeof ratchetTreeB64 === 'string' ? ratchetTreeB64 : null;
    const normalizedGroupId = groupId == null ? null : String(groupId);

    let existingEntry = null;
    if (!resolvedRatchetTree && normalizedGroupId) {
      try {
        const groups = await this.#loadGroups();
        existingEntry =
          groups[normalizedGroupId] ||
          Object.values(groups).find((entry) => entry?.groupHandle === normalizedGroupId) ||
          null;
        const existingTreeB64 = pickBase64String(
          existingEntry?.ratchetTreeB64,
          existingEntry?.ratchetTree,
        );
        if (existingTreeB64) {
          resolvedRatchetTree = existingTreeB64;
        }
      } catch (error) {
        console.warn('[MLS] Failed to resolve cached ratchet tree for welcome upload', {
          groupId: normalizedGroupId,
          error,
        });
      }
    }

    if (!resolvedRatchetTree && normalizedGroupId) {
      const handlesToCheck = new Set();
      if (existingEntry?.groupHandle) {
        handlesToCheck.add(String(existingEntry.groupHandle));
      }
      if (existingEntry?.groupId != null) {
        handlesToCheck.add(String(existingEntry.groupId));
      }
      handlesToCheck.add(String(normalizedGroupId));

      try {
        const runtimeGroups = normalizeRuntimeGroups(await workerListGroups());
        const runtimeEntry = runtimeGroups.find((entry) => {
          if (!entry) return false;
          const handle = entry.groupHandle || entry.groupId || null;
          if (!handle) return false;
          return handlesToCheck.has(String(handle));
        });
        const runtimeTreeB64 = pickBase64String(
          runtimeEntry?.ratchetTreeB64,
          runtimeEntry?.ratchetTree,
        );
        if (runtimeTreeB64) {
          resolvedRatchetTree = runtimeTreeB64;
        }
      } catch (error) {
        console.warn('[MLS] Failed to inspect runtime groups for welcome upload', {
          groupId: normalizedGroupId,
          error,
        });
      }
    }

    if (!resolvedRatchetTree && existingEntry) {
      const existingTreeB64 = pickBase64String(
        existingEntry?.ratchetTreeB64,
        existingEntry?.ratchetTree,
      );
      if (existingTreeB64) {
        resolvedRatchetTree = existingTreeB64;
      }
    }

    if (!resolvedRatchetTree && normalizedGroupId) {
      console.warn('[MLS] Uploading welcome without ratchet tree', { groupId: normalizedGroupId });
    }

    const payloadWelcomes = [];
    if (Array.isArray(welcomes) && welcomes.length) {
      welcomes.forEach((entry) => {
        if (!entry) return;
        const welcomeEntry = {
          welcome_b64: entry.welcomeB64 ?? entry.welcome_b64 ?? null,
          to_user_id: entry.toUserId ?? entry.to_user_id ?? null,
          to_client_id: entry.toClientId ?? entry.to_client_id ?? null,
        };
        if (typeof welcomeEntry.welcome_b64 === 'string' && welcomeEntry.welcome_b64.trim()) {
          payloadWelcomes.push(welcomeEntry);
        }
      });
    } else if (welcomeB64) {
      payloadWelcomes.push({
        welcome_b64: welcomeB64,
        to_user_id: toUserId ?? null,
        to_client_id: toClientId ?? null,
      });
    }

    if (!payloadWelcomes.length) {
      return null;
    }

    const uploadGroupId =
      normalizedGroupId ?? (existingEntry?.groupId != null ? String(existingEntry.groupId) : null);
    if (!uploadGroupId) {
      console.warn('[MLS] Unable to upload welcome without group id', {
        groupId,
        normalizedGroupId,
      });
      return null;
    }

    // If server-managed is enabled, proxy recipients so the backend/sidecar mints welcomes
    if (this.serverManagedWelcomes) {
      const targetSuite = Number.isFinite(ciphersuite) ? ciphersuite : this.preferredCiphersuite;
      const normalizedTargets = [];
      if (Array.isArray(welcomes) && welcomes.length) {
        for (const e of welcomes) {
          if (!e) continue;
          const clientId = e.toClientId ?? e.to_client_id ?? null;
          const externalId = e.toUserId ?? e.to_user_id ?? null;
          if (!clientId) continue;
          normalizedTargets.push({ clientId, externalId });
        }
      } else if (toClientId || toUserId) {
        normalizedTargets.push({ clientId: toClientId ?? null, externalId: toUserId ?? null });
      }

      const recipients = [];
      for (const t of normalizedTargets) {
        if (!t?.clientId) continue;
        try {
          const kp = await fetchLatestMlsKeyPackage({
            clientId: String(t.clientId),
            ciphersuite: targetSuite,
          });
          const keyPackageB64 =
            kp?.key_package_b64 ?? kp?.keyPackageB64 ?? kp?.key_package?.key_package_b64 ?? null;
          if (typeof keyPackageB64 === 'string' && keyPackageB64.trim()) {
            recipients.push({
              clientId: String(t.clientId),
              externalId: t.externalId != null ? String(t.externalId) : undefined,
              keyPackageB64,
            });
          }
        } catch (err) {
          // Skip recipients we can't resolve a fresh key package for
          // eslint-disable-next-line no-console
          console.debug('[MLS] Skipping recipient without fresh key package', {
            clientId: t.clientId,
            error: err,
          });
        }
      }

      if (recipients.length > 0) {
        const requestGroupId = String(uploadGroupId);
        const response = await uploadMlsWelcome({
          groupId: requestGroupId,
          payload: { recipients },
        });
        return response ?? { ok: true, uploaded: 0, skipped: false };
      }
      // If no recipients could be resolved, fall through to legacy path below
    }

    const payload = {
      welcomes: payloadWelcomes,
    };

    const normalizedEpoch = epoch == null ? null : ensureEpoch(epoch);
    if (Number.isFinite(normalizedEpoch)) {
      payload.epoch = normalizedEpoch;
    }
    if (resolvedRatchetTree) {
      payload.ratchet_tree_b64 = resolvedRatchetTree;
    }
    if (ciphersuite != null) {
      payload.ciphersuite = ciphersuite;
    }

    const requestGroupId = String(uploadGroupId);

    let response = null;
    try {
      response = await uploadMlsWelcome({
        groupId: requestGroupId,
        payload,
      });
    } catch (error) {
      // In server-managed mode the backend returns 405 for client uploads. Treat as a no-op.
      const status = (error && (error.status || error.code || error.response?.status)) ?? null;
      const body = (error && (error.body || error.response?.data || error.payload)) || {};
      const code = (body && (body.code || body.error)) || null;
      if (status === 405 || code === 'server_managed_mls') {
        try {
          debugLog('[MLS] Welcome upload skipped (server-managed)', {
            groupId: requestGroupId,
            welcomeCount: payloadWelcomes.length,
          });
        } catch {}
        return { ok: true, uploaded: 0, skipped: true };
      }
      console.warn('[MLS] Welcome upload failed', {
        groupId: requestGroupId,
        welcomeCount: payloadWelcomes.length,
        error,
      });
      throw error;
    }

    if (typeof console?.debug === 'function') {
      try {
        console.debug('[MLS] Uploaded MLS welcome(s)', {
          groupId: requestGroupId,
          welcomeCount: payloadWelcomes.length,
        });
      } catch {}
    }

    return response ?? { ok: true, uploaded: payloadWelcomes.length };
  }

  async fetchPendingWelcomes({ since, conversationId, groupId } = {}) {
    const normalizedConversationId = ensureString(conversationId) ?? null;
    const normalizedGroupId = ensureString(groupId) ?? null;
    const explicitSince = since == null ? null : parseCursor(since);
    const groups = (await this.#loadGroups().catch(() => null)) || {};
    const targets = new Map();
    const items = [];
    let aggregatedNextCursor = null;

    const addTarget = (candidateGroupId, candidateConversationId = null) => {
      const normalizedTargetGroupId = ensureString(candidateGroupId);
      if (!normalizedTargetGroupId) return;

      const normalizedTargetConversationId = ensureString(candidateConversationId) ?? null;
      const existing = targets.get(normalizedTargetGroupId) || null;
      targets.set(normalizedTargetGroupId, {
        groupId: normalizedTargetGroupId,
        conversationId: existing?.conversationId ?? normalizedTargetConversationId,
      });
    };

    if (normalizedGroupId) {
      const knownConversation = findKnownConversationByGroupId(normalizedGroupId);
      const knownConversationId = knownConversation
        ? normalizeConversationId(knownConversation)
        : null;
      addTarget(
        normalizedGroupId,
        normalizedConversationId ??
          knownConversationId ??
          ensureString(
            groups?.[normalizedGroupId]?.conversationId ??
              groups?.[normalizedGroupId]?.conversation_id ??
              null,
          ),
      );
    }

    if (!targets.size && normalizedConversationId) {
      const mappedGroupId = ensureString(
        this.conversationGroups.get(normalizedConversationId) ?? null,
      );
      if (mappedGroupId) {
        addTarget(mappedGroupId, normalizedConversationId);
      }

      Object.entries(groups).forEach(([storedKey, entry]) => {
        const storedConversationId = ensureString(
          entry?.conversationId ?? entry?.conversation_id ?? null,
        );
        if (storedConversationId !== normalizedConversationId) return;
        addTarget(ensureString(entry?.groupId ?? storedKey), normalizedConversationId);
      });

      const knownConversation = getKnownMLSConversation(normalizedConversationId);
      addTarget(
        ensureString(
          knownConversation?.mlsGroupId ??
            knownConversation?.mls_group_id ??
            knownConversation?.groupId ??
            null,
        ),
        normalizedConversationId,
      );

      if (!targets.size) {
        try {
          const ensuredGroupId = await this.ensureServerGroup({
            conversationId: normalizedConversationId,
            allowCreate: false,
          });
          addTarget(ensuredGroupId, normalizedConversationId);
        } catch (error) {
          console.warn('[MLS] Failed to resolve group before pending welcome sync', {
            conversationId: normalizedConversationId,
            error,
          });
        }
      }
    }

    if (!targets.size) {
      Object.entries(groups).forEach(([storedKey, entry]) => {
        addTarget(
          ensureString(entry?.groupId ?? storedKey),
          ensureString(entry?.conversationId ?? entry?.conversation_id ?? null),
        );
      });

      for (const [convId, mappedGroupId] of this.conversationGroups.entries()) {
        addTarget(mappedGroupId, convId);
      }
    }

    for (const target of targets.values()) {
      const targetGroupId = ensureString(target?.groupId);
      if (!targetGroupId) continue;

      let response = null;
      try {
        const rememberedCursor = parseCursor(this._pendingWelcomeCursorByGroup.get(targetGroupId));
        const persistedCursor = parseCursor(
          groups?.[targetGroupId]?.lastServerSeq ??
            groups?.[targetGroupId]?.last_server_seq ??
            null,
        );
        const targetSince = explicitSince ?? rememberedCursor ?? persistedCursor ?? 0;
        response = await this.fetchGroupMessages({
          groupId: targetGroupId,
          since: targetSince,
        });
      } catch (error) {
        console.warn('[MLS] Failed to fetch pending MLS items for welcome sync', {
          groupId: targetGroupId,
          conversationId: target?.conversationId ?? null,
          error,
        });
        continue;
      }

      const pendingItems = Array.isArray(response?.items) ? response.items : [];
      const hasNonWelcomePending = pendingItems.some((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        const itemType =
          ensureString(entry?.type ?? entry?.msg_type ?? null)?.toLowerCase() || null;
        const isWelcome =
          itemType === 'welcome' || Boolean(entry?.welcome_b64 || entry?.welcomeB64);
        if (isWelcome) return false;
        return Boolean(entry?.ciphertext_b64 || entry?.envelope_b64 || entry?.ciphertext);
      });
      const nextCursorCandidate = parseCursor(
        response?.nextCursor ?? response?.next_cursor ?? null,
      );
      // This path only consumes welcomes. If server also returned message/commit frames,
      // do not advance the welcome cursor from this response or we can skip control frames
      // before the commit-apply path processes them.
      if (nextCursorCandidate != null && !hasNonWelcomePending) {
        const previousCursor = parseCursor(this._pendingWelcomeCursorByGroup.get(targetGroupId));
        const advancedCursor =
          previousCursor == null
            ? nextCursorCandidate
            : Math.max(previousCursor, nextCursorCandidate);
        this._pendingWelcomeCursorByGroup.set(targetGroupId, advancedCursor);
        aggregatedNextCursor =
          aggregatedNextCursor == null
            ? advancedCursor
            : Math.max(aggregatedNextCursor, advancedCursor);
      }
      const oldestPendingMessageEpoch = (() => {
        let oldest = null;
        for (const entry of pendingItems) {
          if (!entry || typeof entry !== 'object') continue;
          const itemType =
            ensureString(entry?.type ?? entry?.msg_type ?? null)?.toLowerCase() || null;
          const isWelcome =
            itemType === 'welcome' || Boolean(entry?.welcome_b64 || entry?.welcomeB64);
          if (isWelcome) continue;
          const hasCiphertext = Boolean(
            entry?.ciphertext_b64 || entry?.envelope_b64 || entry?.ciphertext,
          );
          if (!hasCiphertext) continue;
          const epochCandidate =
            entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null;
          const numericEpoch = Number(epochCandidate);
          if (!Number.isFinite(numericEpoch)) continue;
          const normalizedEpoch = ensureEpoch(numericEpoch);
          if (oldest == null || normalizedEpoch < oldest) {
            oldest = normalizedEpoch;
          }
        }
        return oldest;
      })();
      let welcomeItems = pendingItems.filter((entry) => {
        if (!entry || typeof entry !== 'object') return false;
        if (entry.type === 'welcome') return true;
        return Boolean(entry.welcome_b64 || entry.welcomeB64);
      });
      if (oldestPendingMessageEpoch != null && welcomeItems.length) {
        const shouldDeferNewerWelcome = await this.#shouldDeferNewerPendingWelcome({
          groups,
          groupId: targetGroupId,
          conversationId: target?.conversationId ?? null,
          oldestPendingMessageEpoch,
        });
        const beforeCount = welcomeItems.length;
        if (shouldDeferNewerWelcome) {
          welcomeItems = welcomeItems.filter((entry) => {
            const epochCandidate =
              entry?.epoch ?? entry?.current_epoch ?? entry?.currentEpoch ?? null;
            const numericEpoch = Number(epochCandidate);
            if (!Number.isFinite(numericEpoch)) return true;
            return ensureEpoch(numericEpoch) <= oldestPendingMessageEpoch;
          });
          if (beforeCount !== welcomeItems.length && typeof console?.info === 'function') {
            console.info('[MLS] Deferring newer welcomes until pending messages are processed', {
              groupId: targetGroupId,
              dropped: beforeCount - welcomeItems.length,
              kept: welcomeItems.length,
              oldestPendingMessageEpoch,
            });
          }
        }
      }
      if (welcomeItems.length) {
        const activeUserId = ensureString(this.userId);
        const activeClientIds = new Set();
        [
          ensureString(this.identityClientId),
          ensureString(this.identityId),
          ensureString(this.identityRecord?.identityId ?? null),
        ]
          .filter(Boolean)
          .forEach((value) => activeClientIds.add(String(value)));
        try {
          if (this.clientIdsBySuite && typeof this.clientIdsBySuite.values === 'function') {
            for (const clientId of this.clientIdsBySuite.values()) {
              const normalized = ensureString(clientId);
              if (normalized) activeClientIds.add(normalized);
            }
          }
        } catch {}

        const beforeCount = welcomeItems.length;
        welcomeItems = welcomeItems.filter((entry) => {
          const targetUserId = ensureString(entry?.to_user_id ?? entry?.toUserId ?? null);
          if (activeUserId && targetUserId && targetUserId !== activeUserId) {
            return false;
          }

          const targetClientId = ensureString(
            entry?.to_client_id ??
              entry?.toClientId ??
              entry?.client_id ??
              entry?.clientId ??
              entry?.identity_id ??
              entry?.identityId ??
              null,
          );
          if (activeClientIds.size && targetClientId && !activeClientIds.has(targetClientId)) {
            return false;
          }
          return true;
        });

        if (beforeCount !== welcomeItems.length && typeof console?.info === 'function') {
          console.info('[MLS] Dropping pending welcomes for another target identity', {
            groupId: targetGroupId,
            dropped: beforeCount - welcomeItems.length,
            kept: welcomeItems.length,
            activeUserId: activeUserId || null,
            activeClientIds: Array.from(activeClientIds),
          });
        }
      }
      if (!welcomeItems.length) {
        const storedGroupMatch = this.#resolveStoredGroup(groups, {
          groupId: targetGroupId,
          groupHandle: ensureString(groups?.[targetGroupId]?.groupHandle ?? null),
        });
        const storedGroupEntry = storedGroupMatch?.entry ?? groups?.[targetGroupId] ?? null;
        const storedGroupHandle = ensureString(
          storedGroupEntry?.groupHandle ?? storedGroupEntry?.group_handle ?? null,
        );
        const storedPendingWelcomes = getPendingWelcomesList(storedGroupEntry);
        const storedBootstrapWelcome = pickBase64String(
          storedGroupEntry?.recoveryWelcomeB64,
          storedGroupEntry?.recovery_welcome_b64,
          storedGroupEntry?.welcomeB64,
          storedGroupEntry?.welcome_b64,
          storedGroupEntry?.welcome,
          null,
        );
        const membershipState = this.#resolveEntryMembershipState(storedGroupEntry, {
          activeIdentityId: ensureString(
            this.identityClientId ??
              this.identityId ??
              storedGroupEntry?.identityId ??
              storedGroupEntry?.identity_id ??
              null,
          ),
        });
        const authoritativeCurrentMemberWithoutWelcome =
          membershipState.currentIdentityMember &&
          !membershipState.pendingSetup &&
          !membershipState.welcomeRequired;
        const shouldPreserveLocalBootstrapWelcome =
          !this.#isGroupReady(storedGroupEntry) &&
          (storedPendingWelcomes.length > 0 ||
            (!authoritativeCurrentMemberWithoutWelcome && Boolean(storedBootstrapWelcome)));

        if (shouldPreserveLocalBootstrapWelcome) {
          this.#markPendingWelcomesForGroup([targetGroupId, storedGroupHandle], true);
          if (typeof console?.info === 'function') {
            console.info('[MLS] Preserving local bootstrap welcome after empty pending response', {
              groupId: targetGroupId,
              groupHandle: storedGroupHandle ?? null,
              conversationId: target?.conversationId ?? null,
              pendingWelcomeCount: storedPendingWelcomes.length,
              hasPersistedWelcome: Boolean(storedBootstrapWelcome),
            });
          }
          continue;
        }

        // No welcome pending for this group/identity in server pending queue.
        // Clear local pending markers so waitForGroupReady does not keep
        // re-triggering self-join/join_bundle on stale state.
        this.#markPendingWelcomesForGroup([targetGroupId], false);
        if (authoritativeCurrentMemberWithoutWelcome) {
          try {
            await this.#clearPersistedWelcomeState({
              conversationId: target?.conversationId ?? null,
              groupId: targetGroupId,
              groupHandle: storedGroupHandle,
              clearRecoveryPayload: true,
            });
          } catch {}
        }
        try {
          await this.#updateGroupEntry(targetGroupId, (prev) => {
            if (!prev) return prev;
            const hadPending =
              (Array.isArray(prev.pending_welcomes) && prev.pending_welcomes.length > 0) ||
              (Array.isArray(prev.pendingWelcomes) && prev.pendingWelcomes.length > 0);
            if (!hadPending) return prev;
            return {
              ...prev,
              pending_welcomes: [],
              pendingWelcomes: [],
              pending_handshake_queue: [],
              pendingHandshakeQueue: [],
            };
          });
        } catch {}
        continue;
      }

      const normalizedWelcomes = normalizePendingWelcomesFromSnapshot(
        {
          group_id: targetGroupId,
          groupId: targetGroupId,
          conversation_id: target?.conversationId ?? null,
          conversationId: target?.conversationId ?? null,
          pending_welcomes: welcomeItems,
          pendingWelcomes: welcomeItems,
        },
        {
          conversationId: target?.conversationId ?? null,
          groupId: targetGroupId,
        },
      );

      normalizedWelcomes.forEach((entry) => {
        const resolvedConversationId = ensureString(
          entry?.conversation_id ?? entry?.conversationId ?? target?.conversationId ?? null,
        );
        items.push({
          ...entry,
          group_id: targetGroupId,
          groupId: targetGroupId,
          conversation_id: resolvedConversationId ?? null,
          conversationId: resolvedConversationId ?? null,
        });
      });
    }

    const dedupedItems = [];
    const seen = new Set();
    items.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const key = ensureString(
        entry.welcome_id ??
          entry.welcomeId ??
          `${entry.group_id ?? entry.groupId}:${entry.to_client_id ?? entry.toClientId}:${entry.used_kp_ref ?? entry.usedKpRef}:${entry.epoch}`,
      );
      if (!key || seen.has(key)) return;
      seen.add(key);
      dedupedItems.push(entry);
    });

    return { items: dedupedItems, snapshots: [], nextCursor: aggregatedNextCursor };
  }

  async acknowledgeWelcomes({
    groupId,
    welcomeIds,
    conversationId,
    conversationIds,
    clientId,
  } = {}) {
    const normalizedGroupId = ensureString(groupId);
    if (!normalizedGroupId) {
      return;
    }

    await this.#ensureAckedWelcomeCacheLoaded();

    let normalizedIds = Array.from(
      new Set(
        (Array.isArray(welcomeIds) ? welcomeIds : [welcomeIds])
          .map((value) => ensureString(value))
          .filter(Boolean),
      ),
    );

    if (this._ackedWelcomeIdsByGroup?.size) {
      const known = this._ackedWelcomeIdsByGroup.get(normalizedGroupId);
      if (known && known.size) {
        normalizedIds = normalizedIds.filter((id) => !known.has(id));
      }
    }

    if (!normalizedIds.length) {
      return;
    }

    const normalizedClientId = ensureString(clientId) || null;
    const ackTraceId = `ack-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    const ackFlightKey = [
      normalizedGroupId,
      normalizedClientId || 'no-client',
      ...normalizedIds.slice().sort(),
    ].join('|');
    if (this._ackFlights.has(ackFlightKey)) {
      if (typeof console?.info === 'function') {
        console.info('MDBG ACK duplicate request skipped (in-flight)', {
          traceId: ackTraceId,
          groupId: normalizedGroupId,
          clientId: normalizedClientId,
          welcomeIds: normalizedIds,
        });
      }
      return;
    }
    this._ackFlights.add(ackFlightKey);

    try {
      const cacheConversationIds = new Set();
      const primaryConversationId = ensureString(conversationId);
      if (primaryConversationId) {
        cacheConversationIds.add(primaryConversationId);
      }
      if (Array.isArray(conversationIds)) {
        conversationIds.forEach((value) => {
          const normalized = ensureString(value);
          if (normalized) {
            cacheConversationIds.add(normalized);
          }
        });
      }

      const knownConversation = findKnownConversationByGroupId(normalizedGroupId);
      const knownConversationId = knownConversation
        ? normalizeConversationId(knownConversation)
        : null;
      if (knownConversationId) {
        cacheConversationIds.add(knownConversationId);
      }

      let ackResult = null;
      try {
        this.#markWelcomeAckPending(normalizedGroupId, normalizedIds);
        ackResult = await acknowledgeMlsWelcomes({
          groupId: normalizedGroupId,
          welcomeIds: normalizedIds,
          clientId: normalizedClientId,
        });
        if (typeof console?.info === 'function') {
          console.info('MDBG ACK response received', {
            traceId: ackTraceId,
            groupId: normalizedGroupId,
            requestedWelcomeIds: normalizedIds,
            ackedCount: Array.isArray(ackResult?.acked) ? ackResult.acked.length : 0,
            skippedCount: Array.isArray(ackResult?.skipped) ? ackResult.skipped.length : 0,
            hasGroupState: Boolean(
              (ackResult?.group_state && typeof ackResult.group_state === 'object') ||
                (ackResult?.groupState && typeof ackResult.groupState === 'object'),
            ),
          });
        }
      } catch (error) {
        try {
          this.#clearPendingWelcomeAck(normalizedGroupId, normalizedIds);
        } catch {}
        const errorCode =
          error?.code || error?.payload?.error?.code || error?.payload?.code || null;
        if (errorCode === 'conversation_not_found' && knownConversationId) {
          try {
            const refreshedGroupId = await this.ensureServerGroup({
              conversationId: knownConversationId,
              allowCreate: false,
            });
            if (refreshedGroupId && refreshedGroupId !== normalizedGroupId) {
              persistKnownConversationGroupId(knownConversationId, refreshedGroupId);
            }
          } catch {}
        }
        console.warn('[MLS] Failed to acknowledge welcomes', {
          groupId: normalizedGroupId,
          welcomeIds: normalizedIds,
          clientId: normalizedClientId,
          error,
        });
        return;
      }

      const ackGroupSnapshot =
        ackResult?.group_state && typeof ackResult.group_state === 'object'
          ? ackResult.group_state
          : ackResult?.groupState && typeof ackResult.groupState === 'object'
            ? ackResult.groupState
            : null;
      const ackGroupSnapshotHasUsableData = (() => {
        if (!ackGroupSnapshot || typeof ackGroupSnapshot !== 'object') {
          return false;
        }
        const snapshotGroupId = ensureString(
          ackGroupSnapshot.group_id ?? ackGroupSnapshot.groupId ?? null,
        );
        const snapshotGroupHandle = ensureString(
          ackGroupSnapshot.group_handle ?? ackGroupSnapshot.groupHandle ?? null,
        );
        const snapshotEpoch = normalizeEpochOrNull(
          ackGroupSnapshot.epoch ??
            ackGroupSnapshot.current_epoch ??
            ackGroupSnapshot.currentEpoch ??
            null,
        );
        const snapshotMembers = Array.isArray(ackGroupSnapshot.members)
          ? ackGroupSnapshot.members
          : [];
        const snapshotRatchetTree = pickBase64String(
          ackGroupSnapshot.ratchet_tree_b64,
          ackGroupSnapshot.ratchetTreeB64,
          ackGroupSnapshot.ratchet_tree,
          ackGroupSnapshot.ratchetTree,
        );
        return Boolean(
          snapshotGroupId ||
            snapshotGroupHandle ||
            snapshotEpoch != null ||
            snapshotMembers.length > 0 ||
            snapshotRatchetTree,
        );
      })();

      if (ackGroupSnapshot && ackGroupSnapshotHasUsableData) {
        try {
          const persisted = await this.#persistServerGroupSnapshot(ackGroupSnapshot, {
            fallbackGroupId: normalizedGroupId,
            // ACK group_state is authoritative post-welcome-consume state for this device.
            // Clear stale recovery flags so ensure/wait flows do not keep forcing join_bundle.
            clearRecovery: true,
            skipFinalize: false,
            source: 'ack:group_state',
            traceId: ackTraceId,
          });
          const persistedPending =
            persisted?.pending_welcomes ?? persisted?.pendingWelcomes ?? null;
          const pendingCount = Array.isArray(persistedPending) ? persistedPending.length : null;
          this.#markPendingWelcomesForGroup(
            [
              persisted?.groupId ?? normalizedGroupId,
              persisted?.groupHandle ??
                ackGroupSnapshot?.group_handle ??
                ackGroupSnapshot?.groupHandle ??
                null,
            ],
            pendingCount != null ? pendingCount > 0 : false,
          );
          if (typeof console?.info === 'function') {
            console.info('MDBG ACK group_state applied to IndexedDB', {
              traceId: ackTraceId,
              groupId: persisted?.groupId ?? normalizedGroupId,
              epoch:
                persisted?.epoch ??
                ackGroupSnapshot?.epoch ??
                ackGroupSnapshot?.current_epoch ??
                null,
              pendingWelcomes: pendingCount,
            });
          }
          try {
            console.info(
              '[MLS] ACK snapshot persisted; runtime convergence remains orchestrator-owned',
              {
                traceId: ackTraceId,
                groupId: persisted?.groupId ?? normalizedGroupId,
              },
            );
          } catch {}
        } catch (error) {
          if (typeof console?.warn === 'function') {
            console.warn('[MLS] Failed to apply group_state from ACK response', {
              groupId: normalizedGroupId,
              error,
            });
          }
        }
      } else if (ackGroupSnapshot && !ackGroupSnapshotHasUsableData) {
        try {
          console.warn('[MLS] Ignoring empty group_state from ACK response', {
            groupId: normalizedGroupId,
            ackTraceId,
          });
        } catch {}
      }

      const ackedIds = Array.isArray(ackResult?.acked) ? ackResult.acked : [];
      const skippedIds = Array.isArray(ackResult?.skipped) ? ackResult.skipped : [];
      const resolvedIds = new Set(
        ackedIds
          .concat(skippedIds)
          .map((value) => ensureString(value))
          .filter(Boolean),
      );
      if (resolvedIds.size) {
        try {
          this.#clearPendingWelcomeAck(normalizedGroupId, Array.from(resolvedIds));
        } catch {}
      }
      if (skippedIds.length) {
        try {
          this._ackResyncThrottle = this._ackResyncThrottle || new Map();
          const lastAttempt = this._ackResyncThrottle.get(normalizedGroupId) || 0;
          if (Date.now() - lastAttempt > 5000) {
            this._ackResyncThrottle.set(normalizedGroupId, Date.now());
            console.info('[MLS] ACK skipped welcomes recorded; waiting for orchestrator resync', {
              groupId: normalizedGroupId,
              skippedIds,
            });
          }
        } catch {}
      }
      if (resolvedIds.size) {
        const groupCache = this._ackedWelcomeIdsByGroup.get(normalizedGroupId) || new Set();
        const now = Date.now();
        resolvedIds.forEach((value) => groupCache.add(value));
        this._ackedWelcomeIdsByGroup.set(normalizedGroupId, groupCache);
        const persistedCache = this._ackedWelcomeTimestampsByGroup || new Map();
        const groupTs = persistedCache.get(normalizedGroupId) || new Map();
        resolvedIds.forEach((value) => groupTs.set(value, now));
        persistedCache.set(normalizedGroupId, groupTs);
        this._ackedWelcomeTimestampsByGroup = persistedCache;
        this.#scheduleAckedWelcomeCachePersist();
        welcomeRecoveryCoordinator.settle({
          userId: this.userId,
          identityId:
            normalizedClientId ??
            this.#normalizeIdentityId(this.identityClientId ?? this.identityId ?? null),
          groupId: normalizedGroupId,
        });
      }

      if (resolvedIds.size) {
        try {
          const welcomeIdsSet = new Set(Array.from(resolvedIds));
          await this.#updateGroupEntry(normalizedGroupId, (prev) => {
            if (!prev || typeof prev !== 'object') {
              return prev;
            }
            const prevPending = Array.isArray(prev.pending_welcomes)
              ? prev.pending_welcomes
              : Array.isArray(prev.pendingWelcomes)
                ? prev.pendingWelcomes
                : [];
            if (!prevPending.length) {
              return prev;
            }
            const filtered = prevPending.filter((entry) => {
              const welcomeId = ensureString(entry?.welcome_id ?? entry?.welcomeId ?? null);
              if (!welcomeId) {
                return false;
              }
              return !welcomeIdsSet.has(welcomeId);
            });
            return {
              ...prev,
              pending_welcomes: filtered,
              pendingWelcomes: filtered,
            };
          });
          this.#markPendingWelcomesForGroup([normalizedGroupId], false);
        } catch {}
      }

      cacheConversationIds.forEach((convId) => {
        try {
          resetConversationMlsGroupCache(convId);
        } catch {}
      });
    } finally {
      this._ackFlights.delete(ackFlightKey);
    }
  }

  async syncWelcomes({ since, conversationId, groupId, __pipelineBypass = false } = {}) {
    const pipelineGroupId = ensureString(groupId);
    if (!__pipelineBypass && pipelineGroupId) {
      return this.#runGroupPipeline(
        {
          groupId: pipelineGroupId,
          stage: 'syncWelcomes',
        },
        () =>
          this.syncWelcomes({
            since,
            conversationId,
            groupId: pipelineGroupId,
            __pipelineBypass: true,
          }),
      );
    }

    const response = await this.fetchPendingWelcomes({ since, conversationId, groupId });
    const responseNextCursor = response?.nextCursor ?? response?.next_cursor ?? null;
    const joined = [];
    const normalizedSnapshots = Array.isArray(response?.snapshots) ? response.snapshots : [];

    const processSnapshot = async ({ snapshot, conversationId: snapshotConversationId }) => {
      if (!snapshot) {
        return;
      }

      const resolvedConversationId =
        ensureString(snapshotConversationId) ??
        ensureString(
          snapshot.conversation_id ?? snapshot.conversationId ?? conversationId ?? null,
        ) ??
        null;

      const resolvedGroupId =
        ensureString(snapshot.group_id ?? snapshot.groupId ?? groupId ?? null) ?? null;
      const resolvedGroupHandle = ensureString(
        snapshot.group_handle ?? snapshot.groupHandle ?? resolvedGroupId ?? null,
      );

      const snapRequestId = `sync-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      try {
        if (resolvedGroupId)
          this._snapshotFetchCorrelation.set(String(resolvedGroupId), snapRequestId);
      } catch {}

      const ingestion = await this.#ingestPendingWelcomesFromSnapshot(snapshot, {
        conversationId: resolvedConversationId,
        groupId: resolvedGroupId,
        groupHandle: resolvedGroupHandle,
        origin: 'snapshot:syncWelcomes',
        requestId: snapRequestId,
      });

      if (Array.isArray(ingestion?.joined) && ingestion.joined.length) {
        joined.push(...ingestion.joined);
      }
    };

    if (normalizedSnapshots.length) {
      for (const entry of normalizedSnapshots) {
        try {
          await processSnapshot(entry || {});
        } catch (error) {
          console.warn('[MLS] Failed to process pending welcome snapshot', error);
        }
      }
      return { joined, nextCursor: responseNextCursor };
    }

    const items = Array.isArray(response?.items) ? response.items : [];
    if (!items.length) {
      return { joined, nextCursor: responseNextCursor };
    }

    try {
      if (typeof console?.info === 'function') {
        debugLog('[MLS DEBUG] using REST pending items path for welcomes', {
          count: items.length,
          origin: 'rest:/api/v1/mls/groups/:id/pending',
        });
      }
    } catch {}

    const fallbackSnapshots = items.reduce((acc, item) => {
      if (!item) {
        return acc;
      }

      const welcomeB64 = item?.welcome_b64 ?? item?.welcomeB64 ?? null;
      if (!welcomeB64) {
        return acc;
      }

      const targetGroupId = ensureString(item?.group_id ?? item?.groupId ?? groupId ?? null);
      if (!targetGroupId) {
        return acc;
      }

      const convId = ensureString(
        item?.conversation_id ?? item?.conversationId ?? conversationId ?? null,
      );

      const fallbackSnapshot = acc.get(targetGroupId) || {
        group_id: targetGroupId,
        groupId: targetGroupId,
        group_handle: item?.group_handle ?? item?.groupHandle ?? null,
        groupHandle: item?.group_handle ?? item?.groupHandle ?? null,
        ratchet_tree_b64: item?.ratchet_tree_b64 ?? item?.ratchetTreeB64 ?? null,
        ratchetTreeB64: item?.ratchet_tree_b64 ?? item?.ratchetTreeB64 ?? null,
        epoch: item?.epoch ?? item?.current_epoch ?? item?.currentEpoch ?? null,
        conversation_id: convId ?? null,
        conversationId: convId ?? null,
        pending_welcomes: [],
        pendingWelcomes: [],
      };

      const normalizedWelcomes = normalizePendingWelcomesFromSnapshot(
        {
          ...fallbackSnapshot,
          pending_welcomes: [item],
          pendingWelcomes: [item],
        },
        {
          conversationId: convId,
          groupId: targetGroupId,
          origin: `rest:/api/v1/mls/groups/${targetGroupId}/pending`,
          requestId: `rest-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
        },
      );

      fallbackSnapshot.pending_welcomes.push(...normalizedWelcomes);
      fallbackSnapshot.pendingWelcomes = fallbackSnapshot.pending_welcomes;

      acc.set(targetGroupId, fallbackSnapshot);
      return acc;
    }, new Map());

    for (const snapshot of fallbackSnapshots.values()) {
      try {
        await processSnapshot({ snapshot, conversationId: snapshot.conversation_id });
      } catch (error) {
        console.warn('[MLS] Failed to process pending welcome fallback snapshot', error);
      }
    }

    return { joined, nextCursor: responseNextCursor };
  }

  async convergeGroupState({ groupId, conversationId = null, reason = 'manual_converge' } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedConversationId = ensureString(conversationId);

    let resolvedGroupId = normalizedGroupId;
    if (!resolvedGroupId && normalizedConversationId) {
      resolvedGroupId = ensureString(this.conversationGroups.get(normalizedConversationId));
    }

    if (
      !resolvedGroupId &&
      normalizedConversationId &&
      typeof this.ensureServerGroup === 'function'
    ) {
      try {
        resolvedGroupId = await this.ensureServerGroup({
          conversationId: normalizedConversationId,
          allowCreate: false,
        });
      } catch {}
    }

    if (!resolvedGroupId) {
      return null;
    }

    try {
      if (typeof this.ensureIdentity === 'function') {
        await this.ensureIdentity({});
      }
    } catch {}
    const attemptDelays = [0, 400];
    let latestInfo = null;

    for (let attemptIndex = 0; attemptIndex < attemptDelays.length; attemptIndex += 1) {
      const delayMs = attemptDelays[attemptIndex];
      if (delayMs > 0) {
        await sleep(delayMs);
      }

      let pendingApply = null;
      try {
        pendingApply = await this.pullAndApplyPendingWelcomeOrCommit({
          groupId: resolvedGroupId,
        });
      } catch (error) {
        console.warn('[MLS] convergeGroupState failed', {
          groupId: resolvedGroupId,
          conversationId: normalizedConversationId,
          reason,
          attempt: attemptIndex + 1,
          error,
        });
      }

      latestInfo = await this.groupInfo({ groupId: resolvedGroupId }).catch(() => null);
      const joinedOnAttempt = Array.isArray(pendingApply?.joined) && pendingApply.joined.length > 0;
      const shouldRetry =
        attemptIndex < attemptDelays.length - 1 &&
        !this.#isGroupReady(latestInfo) &&
        !Boolean(latestInfo?.needsRecovery) &&
        !joinedOnAttempt;
      if (!shouldRetry) {
        break;
      }
    }

    return latestInfo;
  }

  async sendApplicationMessage({
    groupId,
    senderClientId,
    ciphertextB64,
    envelopeB64,
    epoch,
    ciphersuite,
    contentType,
    clientMsgId,
    historyCiphertextB64,
    historyIvB64,
    historyAlg,
    internalReadyFlow = false,
  }) {
    void internalReadyFlow;
    // Runtime convergence is orchestrator-owned. Manager send must not launch its own
    // wait/converge/recover lane even if a caller passes internalReadyFlow=true.

    const encodedEnvelope = envelopeB64 || ciphertextB64;
    if (!encodedEnvelope) {
      throw new Error('envelopeB64 is required to send');
    }
    const normalizedSenderClientId = this.#normalizeIdentityId(senderClientId);
    if (!normalizedSenderClientId) {
      if (typeof console?.error === 'function') {
        try {
          console.error('[MLS] Missing minted client_id for suite; aborting send/probe', {
            groupId,
            identityId: this.identityRecord?.identityId ?? this.identityId ?? null,
          });
        } catch {}
      }
      const missingClientError = new Error('MLS sender_client_id is required');
      missingClientError.code = 'MLS_MISSING_CLIENT_ID';
      throw missingClientError;
    }

    const resolvedIdentityId = this.#normalizeIdentityId(
      this.identityRecord?.identityId ?? this.identityId ?? normalizedSenderClientId ?? null,
    );

    let resolvedEpoch = epoch ?? null;
    let resolvedCiphersuite = ciphersuite ?? null;

    if (
      (resolvedEpoch == null || resolvedCiphersuite == null) &&
      typeof this.groupInfo === 'function'
    ) {
      try {
        const info = await this.groupInfo({ groupId }).catch(() => null);
        if (resolvedEpoch == null && info?.epoch != null) {
          resolvedEpoch = info.epoch;
        }
        if (resolvedCiphersuite == null && info?.ciphersuite != null) {
          resolvedCiphersuite = info.ciphersuite;
        }
      } catch {}
    }

    let response;
    try {
      response = await sendMlsMessage({
        groupId,
        senderClientId: normalizedSenderClientId,
        identityId: resolvedIdentityId,
        envelopeB64: encodedEnvelope,
        ciphertextB64: ciphertextB64 ?? encodedEnvelope,
        epoch: resolvedEpoch,
        ciphersuite: resolvedCiphersuite,
        contentType,
        clientMsgId,
        historyCiphertextB64,
        historyIvB64,
        historyAlg,
      });
    } catch (error) {
      if (typeof console?.error === 'function') {
        let snippet = null;
        if (error?.responseText) {
          snippet = String(error.responseText);
        } else if (error?.payload) {
          try {
            snippet = JSON.stringify(error.payload);
          } catch (stringifyError) {
            try {
              snippet = String(error.payload);
            } catch {}
          }
        }
        if (snippet && snippet.length > 300) {
          snippet = `${snippet.slice(0, 300)}…`;
        }
        try {
          console.error('[MLS] Failed to send group message', {
            groupId,
            status: error?.status ?? null,
            code: error?.code ?? null,
            responseSnippet: snippet,
          });
        } catch {}
      }
      throw error;
    }

    console.log('normalizedServerSeq', response);

    const normalizedServerSeq = response?.server_seq ?? response?.serverSeq ?? null;

    if (groupId && normalizedServerSeq != null) {
      const parsedSeq = ensureEpoch(normalizedServerSeq);
      if (Number.isFinite(parsedSeq)) {
        try {
          await this.#updateGroupEntry(groupId, (prev) => ({
            ...prev,
            ...buildProcessedServerSeqState(prev, parsedSeq),
          }));
        } catch {}
      }
    }

    if (this.events?.emit) {
      try {
        this.events.emit('mls:message-acked', {
          groupId,
          clientMsgId: response?.client_msg_id ?? clientMsgId ?? null,
          serverSeq: normalizedServerSeq,
          payload: response,
        });
      } catch {}
    }

    return response;
  }

  async fetchGroupMessages({ groupId, since } = {}) {
    if (!groupId) {
      throw new Error('groupId is required');
    }

    const normalizedGroupId = String(groupId);
    let resolvedPendingClientId = this.#normalizeIdentityId(this.getActiveMlsClientId()) ?? null;
    if (!resolvedPendingClientId) {
      resolvedPendingClientId =
        this.#normalizeIdentityId(
          this.identityClientId ??
            this.identityRecord?.clientId ??
            this.identityRecord?.client_id ??
            this.identityRecord?.identityId ??
            this.identityRecord?.identity_id ??
            this.identityId ??
            null,
        ) ?? null;
    }

    let existingEntry = null;
    try {
      const groups = (await this.#loadGroups().catch(() => null)) || {};
      existingEntry =
        this.#resolveStoredGroup(groups, { groupId: normalizedGroupId })?.entry ??
        groups[normalizedGroupId] ??
        null;
    } catch {}

    if (!resolvedPendingClientId && existingEntry) {
      resolvedPendingClientId =
        this.#normalizeIdentityId(
          existingEntry?.welcomeTargetIdentityId ??
            existingEntry?.welcome_target_identity_id ??
            existingEntry?.identityId ??
            existingEntry?.identity_id ??
            null,
        ) ?? null;
    }

    const persistedCursor = parseCursor(
      existingEntry?.lastServerSeq ?? existingEntry?.last_server_seq ?? null,
    );
    const normalizedSince = parseCursor(since) ?? persistedCursor ?? 0;
    const fetchKey = `${normalizedGroupId}:${String(normalizedSince)}`;
    const recent = this._pendingFetchRecent?.get(fetchKey) || null;
    if (recent && Date.now() - recent.ts < 800) {
      return {
        items: Array.isArray(recent.response?.items) ? recent.response.items : [],
        nextCursor: recent.response?.nextCursor || null,
        next_cursor: recent.response?.nextCursor || null,
      };
    }

    const existingFlight = this._pendingFetchFlights?.get(fetchKey);
    if (existingFlight) {
      return existingFlight;
    }

    const task = (async () => {
      if (this.events?.emit) {
        try {
          this.events.emit('mls:pending-sync-start', {
            groupId,
            since: normalizedSince,
          });
        } catch {}
      }

      try {
        const response = await fetchMlsMessages({
          groupId: normalizedGroupId,
          since: normalizedSince,
          clientId: resolvedPendingClientId,
        });
        const corrId = `pending-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
        try {
          this._pendingFetchCorrelation.set(String(groupId), corrId);
        } catch {}
        try {
          if (typeof console?.info === 'function') {
            const items = Array.isArray(response?.items) ? response.items : [];
            const welcomeCount = items.filter(
              (it) => it?.type === 'welcome' || it?.welcome_b64 || it?.welcomeB64,
            ).length;
            debugLog('[MLS DEBUG] messages#pending fetched', {
              groupId,
              since: normalizedSince,
              total: items.length,
              welcomes: welcomeCount,
              origin: 'rest:/api/v1/mls/groups/:id/pending',
              requestId: corrId,
            });
          }
        } catch {}
        const normalizedResponse = {
          items: Array.isArray(response?.items) ? response.items : [],
          nextCursor: response?.next_cursor || null,
          next_cursor: response?.next_cursor || null,
        };

        // Do not advance `lastServerSeq` from fetch response alone.
        // Cursor progression must happen only after message processing/decrypt
        // (or explicit stale-drop handling), otherwise commit/application frames
        // can be incorrectly treated as stale before they are actually consumed.

        this._pendingFetchRecent.set(fetchKey, {
          ts: Date.now(),
          response: normalizedResponse,
        });
        if (this._pendingFetchRecent.size > 200) {
          for (const [key, value] of this._pendingFetchRecent.entries()) {
            if (!value || Date.now() - value.ts > 60_000) {
              this._pendingFetchRecent.delete(key);
            }
          }
        }
        if (this.events?.emit) {
          try {
            this.events.emit('mls:pending-sync-complete', {
              groupId,
              since: normalizedSince,
              nextCursor: normalizedResponse.nextCursor,
              count: normalizedResponse.items.length,
            });
          } catch {}
        }
        return normalizedResponse;
      } catch (error) {
        if (this.events?.emit) {
          try {
            this.events.emit('mls:pending-sync-error', {
              groupId,
              since: normalizedSince,
              error,
            });
          } catch {}
        }
        throw error;
      } finally {
        this._pendingFetchFlights.delete(fetchKey);
      }
    })();

    this._pendingFetchFlights.set(fetchKey, task);
    return task;
  }

  async noteServerSequence({
    groupId,
    groupHandle = null,
    serverSeq = null,
    serverMsgId = null,
  } = {}) {
    const normalizedGroupId = ensureString(groupId);
    const normalizedGroupHandle = ensureString(groupHandle);
    const nextCursor = parseCursor(serverSeq);

    if (nextCursor == null) return null;
    if (!normalizedGroupId && !normalizedGroupHandle) return nextCursor;

    try {
      let resolvedGroupId = normalizedGroupId;
      if (!resolvedGroupId && normalizedGroupHandle) {
        const groups = (await this.#loadGroups().catch(() => null)) || {};
        const match = Object.values(groups).find((entry) => {
          if (!entry || typeof entry !== 'object') return false;
          const candidateHandle = ensureString(entry.groupHandle);
          return candidateHandle && candidateHandle === normalizedGroupHandle;
        });
        resolvedGroupId = ensureString(match?.groupId);
      }
      if (!resolvedGroupId) return nextCursor;

      await this.#updateGroupEntry(resolvedGroupId, (prev) => {
        if (isServerSeqAlreadyProcessed(prev, nextCursor)) {
          return prev;
        }
        return {
          ...prev,
          ...buildProcessedServerSeqState(prev, nextCursor),
        };
      });
    } catch {}

    return nextCursor;
  }

  async maybeProbeBootstrap(options = {}) {
    return this.#maybeProbeBootstrap(options);
  }

  // Public: Ingest a minimal server snapshot (e.g., ActionCable refresh event)
  // snapshot: { group_id/groupId, epoch, ratchet_tree_b64/ratchetTreeB64, ciphersuite? }
  async applyServerSnapshot(snapshot, options = {}) {
    try {
      if (!snapshot || typeof snapshot !== 'object') return null;
      const bypassPipeline = options?.__pipelineBypass === true;
      const source = ensureString(options?.source) || 'applyServerSnapshot';
      const traceId = ensureString(options?.traceId) || null;
      const snapshotConversationId = ensureString(
        options?.conversationId ?? options?.conversation_id ?? null,
      );
      const snapshotGroupId = ensureString(snapshot?.group_id ?? snapshot?.groupId ?? null);
      const snapshotGroupHandle = ensureString(
        snapshot?.group_handle ?? snapshot?.groupHandle ?? null,
      );

      if (!bypassPipeline && (snapshotGroupId || snapshotGroupHandle)) {
        return this.#runGroupPipeline(
          {
            groupId: snapshotGroupId,
            groupHandle: snapshotGroupHandle,
            stage: 'applyServerSnapshot',
          },
          () =>
            this.applyServerSnapshot(snapshot, {
              ...options,
              __pipelineBypass: true,
            }),
        );
      }

      let previousEntry = null;
      try {
        const groups = (await this.#loadGroups()) || {};
        const resolved = this.#resolveStoredGroup(groups, {
          groupId: snapshotGroupId,
          groupHandle: snapshotGroupHandle,
        });
        previousEntry = resolved?.entry || null;
      } catch {}
      const persisted = await this.#persistServerGroupSnapshot(snapshot, {
        clearRecovery: false,
        skipFinalize: false,
        fallbackConversationId: snapshotConversationId,
        source,
        traceId,
      });
      const previousEpoch = ensureEpoch(previousEntry?.epoch);
      const nextEpoch = ensureEpoch(
        persisted?.epoch ??
          snapshot?.epoch ??
          snapshot?.current_epoch ??
          snapshot?.currentEpoch ??
          null,
      );
      const snapshotEpochAdvanced =
        Number.isFinite(nextEpoch) &&
        (previousEpoch == null || !Number.isFinite(previousEpoch) || nextEpoch > previousEpoch);
      const previousTreeB64 = pickBase64String(
        previousEntry?.ratchetTreeB64,
        previousEntry?.ratchetTree,
      );
      const nextTreeB64 = pickBase64String(
        snapshot?.ratchet_tree_b64,
        snapshot?.ratchetTreeB64,
        snapshot?.ratchet_tree,
        snapshot?.ratchetTree,
        persisted?.ratchetTreeB64,
        persisted?.ratchetTree,
      );
      const snapshotTreeChanged = Boolean(nextTreeB64) && nextTreeB64 !== previousTreeB64;
      const pendingWelcomes =
        persisted?.pending_welcomes ??
        persisted?.pendingWelcomes ??
        snapshot?.pending_welcomes ??
        [];
      const pendingCount = Array.isArray(pendingWelcomes) ? pendingWelcomes.length : 0;
      const shouldRuntimeSync =
        Boolean(persisted?.needsRecovery) ||
        !this.#isGroupReady(persisted) ||
        pendingCount > 0 ||
        snapshotEpochAdvanced ||
        snapshotTreeChanged;
      if (
        (shouldRuntimeSync || pendingCount > 0) &&
        (persisted?.groupId || snapshotGroupId || snapshotGroupHandle)
      ) {
        const bootstrapGroupId = ensureString(
          persisted?.groupId ?? snapshotGroupId ?? snapshotGroupHandle ?? null,
        );
        if (bootstrapGroupId) {
          await this.#markBootstrapConvergence(bootstrapGroupId, {
            reason:
              pendingCount > 0
                ? 'applyServerSnapshot:pending_welcome_convergence'
                : snapshotEpochAdvanced || snapshotTreeChanged
                  ? 'applyServerSnapshot:snapshot_advance_convergence'
                  : 'applyServerSnapshot:runtime_convergence',
          });
        }
      }
      let synced = null;
      if (shouldRuntimeSync && (persisted?.groupId || persisted?.groupHandle)) {
        const snapshotRuntimeSyncTimedOut = { __snapshotRuntimeSyncTimedOut: true };
        synced = await awaitWithTimeout(
          () =>
            this.#syncRuntimeGroupState({
              groupId: persisted?.groupId ?? null,
              groupHandle: persisted?.groupHandle ?? null,
              conversationId: persisted?.conversationId ?? null,
              source,
              reason:
                snapshotEpochAdvanced || snapshotTreeChanged
                  ? 'apply_server_snapshot_advanced'
                  : 'apply_server_snapshot',
              traceId,
              // Keep snapshot-apply path focused on runtime alignment.
              // Pending/welcome convergence runs immediately after via pullAndApplyPendingWelcomeOrCommit.
              allowSyncWelcomes: false,
              allowJoinBundleRepair: false,
              allowRuntimeRepair: false,
            }),
          SNAPSHOT_APPLY_OBSERVE_TIMEOUT_MS,
          snapshotRuntimeSyncTimedOut,
        ).catch((error) => {
          console.warn('[MLS] runtime sync failed during snapshot apply', {
            groupId: persisted?.groupId ?? snapshotGroupId ?? null,
            source,
            traceId,
            error,
          });
          return null;
        });
        if (synced === snapshotRuntimeSyncTimedOut) {
          synced = null;
          if (typeof console?.info === 'function') {
            console.info(
              '[MLS] applyServerSnapshot runtime observe timed out; leaving convergence to bootstrap owner',
              {
                groupId: persisted?.groupId ?? snapshotGroupId ?? null,
                groupHandle: persisted?.groupHandle ?? snapshotGroupHandle ?? null,
                source,
                traceId,
                timeoutMs: SNAPSHOT_APPLY_OBSERVE_TIMEOUT_MS,
              },
            );
          }
        }
      }

      const resolvedGroupId = ensureString(
        synced?.groupId ?? persisted?.groupId ?? snapshotGroupId ?? null,
      );
      const resolvedGroupHandle = ensureString(
        synced?.groupHandle ?? persisted?.groupHandle ?? snapshotGroupHandle ?? null,
      );
      let finalInfo = synced || persisted || null;

      const shouldConvergePending =
        Boolean(resolvedGroupId) &&
        (snapshotEpochAdvanced ||
          snapshotTreeChanged ||
          Boolean(finalInfo?.needsRecovery) ||
          !this.#isGroupReady(finalInfo) ||
          isRuntimeBehindSnapshot(finalInfo));

      if (shouldConvergePending) {
        try {
          const snapshotPendingConvergeTimedOut = { __snapshotPendingConvergeTimedOut: true };
          const pendingConvergeResult = await awaitWithTimeout(
            () =>
              this.pullAndApplyPendingWelcomeOrCommit({
                groupId: resolvedGroupId,
                since:
                  parseCursor(finalInfo?.lastServerSeq ?? finalInfo?.last_server_seq ?? null) ?? 0,
                __pipelineBypass: true,
                __skipEnsureWait: true,
              }),
            SNAPSHOT_APPLY_OBSERVE_TIMEOUT_MS,
            snapshotPendingConvergeTimedOut,
          );
          if (
            pendingConvergeResult === snapshotPendingConvergeTimedOut &&
            typeof console?.info === 'function'
          ) {
            console.info(
              '[MLS] applyServerSnapshot pending converge timed out; leaving convergence to bootstrap owner',
              {
                groupId: resolvedGroupId,
                groupHandle: resolvedGroupHandle,
                source,
                traceId,
                timeoutMs: SNAPSHOT_APPLY_OBSERVE_TIMEOUT_MS,
              },
            );
          }
        } catch (error) {
          console.warn('[MLS] pending converge failed during snapshot apply', {
            groupId: resolvedGroupId,
            source,
            traceId,
            error,
          });
        }
        finalInfo =
          (await this.groupInfo({
            groupId: resolvedGroupId,
            groupHandle: resolvedGroupHandle,
          }).catch(() => null)) || finalInfo;
      }

      // Snapshot ingest is an input lane, not the final recovery authority.
      // A freshly advanced snapshot can legitimately observe runtime_behind
      // while welcome/pending convergence is still in-flight. Let the explicit
      // ensure/wait bootstrap path own the recovery decision instead of
      // converting this transient state into an immediate fatal recovery.
      return finalInfo || persisted;
    } catch (error) {
      console.warn('[MLS] applyServerSnapshot failed', {
        message: error?.message || String(error),
        code: error?.code || null,
        groupId: error?.groupId || null,
        groupHandle: error?.groupHandle || null,
        recoveryReason: error?.recoveryReason || null,
        causeMessage: error?.cause?.message || null,
        causeCode: error?.cause?.code || null,
        error,
      });
      try {
        console.warn(
          `[MLS] applyServerSnapshot failed detail ${JSON.stringify({
            message: error?.message || String(error),
            code: error?.code || null,
            groupId: error?.groupId || null,
            groupHandle: error?.groupHandle || null,
            recoveryReason: error?.recoveryReason || null,
            causeMessage: error?.cause?.message || null,
            causeCode: error?.cause?.code || null,
          })}`,
        );
      } catch {}
      return null;
    }
  }

  async teardown() {
    this._bumpGeneration('teardown');
    if (this.groupEnsureRetryTimers?.size) {
      for (const timer of this.groupEnsureRetryTimers.values()) {
        clearTimeout(timer);
      }
      this.groupEnsureRetryTimers.clear();
    }
    if (this.groupEnsureRetryBackoff?.size) {
      this.groupEnsureRetryBackoff.clear();
    }
    if (this.bootstrapRecoveryTimers?.size) {
      for (const timer of this.bootstrapRecoveryTimers.values()) {
        clearTimeout(timer);
      }
      this.bootstrapRecoveryTimers.clear();
    }
    if (this.pendingWelcomeCommitTasks?.size) {
      this.pendingWelcomeCommitTasks.clear();
    }
    if (this._pendingWelcomeAckByGroup?.size) {
      this._pendingWelcomeAckByGroup.clear();
    }
    if (this._ackFlights?.size) {
      this._ackFlights.clear();
    }
    if (this._pendingFetchFlights?.size) {
      this._pendingFetchFlights.clear();
    }
    if (this._pendingFetchRecent?.size) {
      this._pendingFetchRecent.clear();
    }
    if (this._pendingApplyFlights?.size) {
      this._pendingApplyFlights.clear();
    }
    if (this._pendingApplyFlightStartedAt?.size) {
      this._pendingApplyFlightStartedAt.clear();
    }
    if (this.runtimeBootstrapTasks?.size) {
      this.runtimeBootstrapTasks.clear();
    }
    if (this.runtimeBootstrapTaskStartedAt?.size) {
      this.runtimeBootstrapTaskStartedAt.clear();
    }
    if (this.runtimeBootstrapCooldownByGroup?.size) {
      this.runtimeBootstrapCooldownByGroup.clear();
    }
    if (this.groupPipelineTails?.size) {
      this.groupPipelineTails.clear();
    }
    if (this._joinBundleFlights?.size) {
      this._joinBundleFlights.clear();
    }
    if (this._joinBundleRecentResponses?.size) {
      this._joinBundleRecentResponses.clear();
    }
    if (this._joinBundleCallDebounceByGroup?.size) {
      this._joinBundleCallDebounceByGroup.clear();
    }
    if (this._joinBundleNoWelcomeCooldownByFlight?.size) {
      this._joinBundleNoWelcomeCooldownByFlight.clear();
    }
    if (this._pendingWelcomeCursorByGroup?.size) {
      this._pendingWelcomeCursorByGroup.clear();
    }
    if (this._waitReadyFlights?.size) {
      this._waitReadyFlights.clear();
    }
    if (this._ackedWelcomeIdsByGroup?.size) {
      this._ackedWelcomeIdsByGroup.clear();
    }
    if (this._ackedWelcomeTimestampsByGroup?.size) {
      this._ackedWelcomeTimestampsByGroup.clear();
    }
    shutdownMlsWorker();
  }
}

export const createSessionManagerMLS = (userId, options = {}) => {
  return new SessionManagerMLS(userId, options);
};

let managerSingleton = null;
let managerUserId = null;

export const resetSessionManagerMLS = async ({ teardown = true } = {}) => {
  const current = managerSingleton;
  try {
    if (current && typeof current._bumpGeneration === 'function') {
      current._bumpGeneration('resetSessionManagerMLS');
    }
  } catch {}
  managerSingleton = null;
  managerUserId = null;

  if (typeof window !== 'undefined') {
    try {
      if (window.sessionManagerMLS === current) {
        window.sessionManagerMLS = null;
      } else if (window.sessionManagerMLS) {
        window.sessionManagerMLS = null;
      }
    } catch {}
  }

  if (teardown && current && typeof current.teardown === 'function') {
    try {
      await current.teardown();
    } catch (error) {
      console.warn('[MLS] Failed to teardown session manager during reset', error);
    }
  }

  return true;
};

export const getSessionManagerMLS = (options = {}) => {
  const {
    userId,
    identityName,
    preferredCiphersuite,
    wantPQ,
    serverManaged = true,
  } = options || {};
  const normalizedUserId =
    userId == null ? null : typeof userId === 'string' ? userId : String(userId);

  const needsNewInstance =
    !managerSingleton ||
    (normalizedUserId != null && managerUserId != null && managerUserId !== normalizedUserId);

  if (needsNewInstance) {
    if (normalizedUserId == null) {
      return managerSingleton;
    }

    managerSingleton = createSessionManagerMLS(normalizedUserId, {
      identityName,
      preferredCiphersuite,
      wantPQ,
      serverManaged,
    });
    managerUserId = normalizedUserId;
  } else if (managerSingleton) {
    if (identityName) {
      managerSingleton.identityName = identityName;
    }
    if (preferredCiphersuite !== undefined) {
      const normalizedPreferred = normalizeSuiteId(preferredCiphersuite);
      managerSingleton.preferredCiphersuiteExplicit = normalizedPreferred != null;
      if (managerSingleton.preferredCiphersuiteExplicit) {
        managerSingleton.preferredCiphersuite = normalizedPreferred;
      } else if (managerSingleton.wantPQ) {
        managerSingleton.preferredCiphersuite = HYBRID_MLS_CIPHERSUITE;
      } else {
        managerSingleton.preferredCiphersuite = null;
      }
    }
    if (wantPQ !== undefined) {
      managerSingleton.setWantPQ(wantPQ);
    }
    if (serverManaged !== undefined) {
      managerSingleton.serverManagedWelcomes = Boolean(serverManaged);
    }
  }

  if (typeof window !== 'undefined' && managerSingleton) {
    window.sessionManagerMLS = managerSingleton;
  }

  return managerSingleton;
};

export default getSessionManagerMLS;

// Convenience helper to apply a server-provided snapshot to the singleton manager
export async function applyMlsServerSnapshot(snapshot, options = {}) {
  const mgr = getSessionManagerMLS({
    userId: options?.userId ?? null,
    identityName: options?.identityName,
    preferredCiphersuite: options?.preferredCiphersuite,
    wantPQ: options?.wantPQ,
  });
  if (!mgr || typeof mgr.applyServerSnapshot !== 'function') return null;
  return mgr.applyServerSnapshot(snapshot, options);
}
